import { CKEditorError, Collection, DomEmitterMixin, EmitterMixin, EventInfo, ObservableMixin, compareArrays, count, diff, env, fastDiff, first, getAncestors, getCode, global, indexOf, insertAt, isArrowKeyCode, isComment, isInsideCombinedSymbol, isInsideEmojiSequence, isInsideSurrogatePair, isIterable, isNode, isText, isValidAttributeName, keyCodes, logWarning, priorities, remove as remove$1, scrollViewportToShowTarget, spliceArray, toArray, toMap, uid } from "@ckeditor/ckeditor5-utils";
import { clone, cloneDeep, debounce, extend, get, isEmpty, isEqual, isEqualWith, isObject, isPlainObject, merge, set } from "es-toolkit/compat";

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
const documentPlaceholders = /* @__PURE__ */ new WeakMap();
let hasDisplayedPlaceholderDeprecationWarning = false;
/**
* A helper that enables a placeholder on the provided view element (also updates its visibility).
* The placeholder is a CSS pseudo–element (with a text content) attached to the element.
*
* To change the placeholder text, change value of the `placeholder` property in the provided `element`.
*
* To disable the placeholder, use {@link module:engine/view/placeholder~disableViewPlaceholder `disableViewPlaceholder()`} helper.
*
* @param options Configuration options of the placeholder.
* @param options.view Editing view instance.
* @param options.element Element that will gain a placeholder. See `options.isDirectHost` to learn more.
* @param options.isDirectHost If set `false`, the placeholder will not be enabled directly
* in the passed `element` but in one of its children (selected automatically, i.e. a first empty child element).
* Useful when attaching placeholders to elements that can host other elements (not just text), for instance,
* editable root elements.
* @param options.text Placeholder text. It's **deprecated** and will be removed soon. Use
* {@link module:engine/view/placeholder~PlaceholderableViewElement#placeholder `options.element.placeholder`} instead.
* @param options.keepOnFocus If set `true`, the placeholder stay visible when the host element is focused.
*/
function enableViewPlaceholder({ view, element, text, isDirectHost = true, keepOnFocus = false }) {
	const doc = view.document;
	if (!documentPlaceholders.has(doc)) {
		documentPlaceholders.set(doc, /* @__PURE__ */ new Map());
		doc.registerPostFixer((writer) => updateDocumentPlaceholders(documentPlaceholders.get(doc), writer));
		doc.on("change:isComposing", () => {
			view.change((writer) => updateDocumentPlaceholders(documentPlaceholders.get(doc), writer));
		}, { priority: "high" });
	}
	if (element.is("editableElement")) element.on("change:placeholder", (evtInfo, evt, text) => setPlaceholder(text));
	if (element.placeholder) setPlaceholder(element.placeholder);
	else if (text) setPlaceholder(text);
	if (text) showViewPlaceholderTextDeprecationWarning();
	function setPlaceholder(text) {
		const config = {
			text,
			isDirectHost,
			keepOnFocus,
			hostElement: isDirectHost ? element : null
		};
		documentPlaceholders.get(doc).set(element, config);
		view.change((writer) => updateDocumentPlaceholders([[element, config]], writer));
	}
}
/**
* Disables the placeholder functionality from a given element.
*
* See {@link module:engine/view/placeholder~enableViewPlaceholder `enableViewPlaceholder()`} to learn more.
*/
function disableViewPlaceholder(view, element) {
	const doc = element.document;
	if (!documentPlaceholders.has(doc)) return;
	view.change((writer) => {
		const placeholders = documentPlaceholders.get(doc);
		const config = placeholders.get(element);
		writer.removeAttribute("data-placeholder", config.hostElement);
		hideViewPlaceholder(writer, config.hostElement);
		placeholders.delete(element);
	});
}
/**
* Shows a placeholder in the provided element by changing related attributes and CSS classes.
*
* **Note**: This helper will not update the placeholder visibility nor manage the
* it in any way in the future. What it does is a one–time state change of an element. Use
* {@link module:engine/view/placeholder~enableViewPlaceholder `enableViewPlaceholder()`} and
* {@link module:engine/view/placeholder~disableViewPlaceholder `disableViewPlaceholder()`} for full
* placeholder functionality.
*
* **Note**: This helper will blindly show the placeholder directly in the root editable element if
* one is passed, which could result in a visual clash if the editable element has some children
* (for instance, an empty paragraph). Use {@link module:engine/view/placeholder~enableViewPlaceholder `enableViewPlaceholder()`}
* in that case or make sure the correct element is passed to the helper.
*
* @returns `true`, if any changes were made to the `element`.
*/
function showViewPlaceholder(writer, element) {
	if (!element.hasClass("ck-placeholder")) {
		writer.addClass("ck-placeholder", element);
		return true;
	}
	return false;
}
/**
* Hides a placeholder in the element by changing related attributes and CSS classes.
*
* **Note**: This helper will not update the placeholder visibility nor manage the
* it in any way in the future. What it does is a one–time state change of an element. Use
* {@link module:engine/view/placeholder~enableViewPlaceholder `enableViewPlaceholder()`} and
* {@link module:engine/view/placeholder~disableViewPlaceholder `disableViewPlaceholder()`} for full
* placeholder functionality.
*
* @returns `true`, if any changes were made to the `element`.
*/
function hideViewPlaceholder(writer, element) {
	if (element.hasClass("ck-placeholder")) {
		writer.removeClass("ck-placeholder", element);
		return true;
	}
	return false;
}
/**
* Checks if a placeholder should be displayed in the element.
*
* **Note**: This helper will blindly check the possibility of showing a placeholder directly in the
* root editable element if one is passed, which may not be the expected result. If an element can
* host other elements (not just text), most likely one of its children should be checked instead
* because it will be the final host for the placeholder. Use
* {@link module:engine/view/placeholder~enableViewPlaceholder `enableViewPlaceholder()`} in that case or make
* sure the correct element is passed to the helper.
*
* @param element Element that holds the placeholder.
* @param keepOnFocus Focusing the element will keep the placeholder visible.
*/
function needsViewPlaceholder(element, keepOnFocus) {
	if (!element.isAttached()) return false;
	if (hasContent(element)) return false;
	const doc = element.document;
	const selectionAnchor = doc.selection.anchor;
	if (doc.isComposing && selectionAnchor && selectionAnchor.parent === element) return false;
	if (keepOnFocus) return true;
	if (!doc.isFocused) return true;
	return !!selectionAnchor && selectionAnchor.parent !== element;
}
/**
* Anything but uiElement(s) counts as content.
*/
function hasContent(element) {
	for (const child of element.getChildren()) if (!child.is("uiElement")) return true;
	return false;
}
/**
* Updates all placeholders associated with a document in a post–fixer callback.
*
* @returns True if any changes were made to the view document.
*/
function updateDocumentPlaceholders(placeholders, writer) {
	const directHostElements = [];
	let wasViewModified = false;
	for (const [element, config] of placeholders) if (config.isDirectHost) {
		directHostElements.push(element);
		if (updatePlaceholder(writer, element, config)) wasViewModified = true;
	}
	for (const [element, config] of placeholders) {
		if (config.isDirectHost) continue;
		const hostElement = getChildPlaceholderHostSubstitute(element);
		if (hostElement !== config.hostElement && config.hostElement) {
			writer.removeAttribute("data-placeholder", config.hostElement);
			hideViewPlaceholder(writer, config.hostElement);
			config.hostElement = null;
			wasViewModified = true;
		}
		if (!hostElement) continue;
		if (directHostElements.includes(hostElement)) continue;
		config.hostElement = hostElement;
		if (updatePlaceholder(writer, element, config)) wasViewModified = true;
	}
	return wasViewModified;
}
/**
* Updates a single placeholder in a post–fixer callback.
*
* @returns True if any changes were made to the view document.
*/
function updatePlaceholder(writer, element, config) {
	const { text, isDirectHost, hostElement } = config;
	let wasViewModified = false;
	if (hostElement.getAttribute("data-placeholder") !== text) {
		writer.setAttribute("data-placeholder", text, hostElement);
		wasViewModified = true;
	}
	if ((isDirectHost || element.childCount == 1) && needsViewPlaceholder(hostElement, config.keepOnFocus)) {
		if (showViewPlaceholder(writer, hostElement)) wasViewModified = true;
	} else if (hideViewPlaceholder(writer, hostElement)) wasViewModified = true;
	return wasViewModified;
}
/**
* Gets a child element capable of displaying a placeholder if a parent element can host more
* than just text (for instance, when it is a root editable element). The child element
* can then be used in other placeholder helpers as a substitute of its parent.
*/
function getChildPlaceholderHostSubstitute(parent) {
	if (parent.childCount) {
		const firstChild = parent.getChild(0);
		if (firstChild.is("element") && !firstChild.is("uiElement") && !firstChild.is("attributeElement")) return firstChild;
	}
	return null;
}
/**
* Displays a deprecation warning message in the console, but only once per page load.
*/
function showViewPlaceholderTextDeprecationWarning() {
	if (!hasDisplayedPlaceholderDeprecationWarning)
 /**
	* The "text" option in the {@link module:engine/view/placeholder~enableViewPlaceholder `enableViewPlaceholder()`}
	* function is deprecated and will be removed soon.
	*
	* See the `#view-element-placeholder` section in the `Update to v39.x` section in the
	* {@glink updating/guides/updating-from-older-versions updating from older versions} guide
	* for more information on how to apply this change.
	*
	* @error enableViewPlaceholder-deprecated-text-option
	*/
	logWarning("enableViewPlaceholder-deprecated-text-option");
	hasDisplayedPlaceholderDeprecationWarning = true;
}

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
var ViewTypeCheckable = class {
	/* istanbul ignore next -- @preserve */
	is() {
		throw new Error("is() method is abstract");
	}
};

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* @module engine/view/node
*/
const ViewNodeBase = /* #__PURE__ */ EmitterMixin(ViewTypeCheckable);
/**
* Abstract view node class.
*
* This is an abstract class. Its constructor should not be used directly.
* Use the {@link module:engine/view/downcastwriter~ViewDowncastWriter} or {@link module:engine/view/upcastwriter~ViewUpcastWriter}
* to create new instances of view nodes.
*/
var ViewNode = class extends ViewNodeBase {
	/**
	* The document instance to which this node belongs.
	*/
	document;
	/**
	* Parent element. Null by default. Set by {@link module:engine/view/element~ViewElement#_insertChild}.
	*/
	parent;
	/**
	* Creates a tree view node.
	*
	* @param document The document instance to which this node belongs.
	*/
	constructor(document) {
		super();
		this.document = document;
		this.parent = null;
	}
	/**
	* Index of the node in the parent element or null if the node has no parent.
	*
	* Accessing this property throws an error if this node's parent element does not contain it.
	* This means that view tree got broken.
	*/
	get index() {
		let pos;
		if (!this.parent) return null;
		if ((pos = this.parent.getChildIndex(this)) == -1)
 /**
		* The node's parent does not contain this node. It means that the document tree is corrupted.
		*
		* @error view-node-not-found-in-parent
		*/
		throw new CKEditorError("view-node-not-found-in-parent", this);
		return pos;
	}
	/**
	* Node's next sibling, or `null` if it is the last child.
	*/
	get nextSibling() {
		const index = this.index;
		return index !== null && this.parent.getChild(index + 1) || null;
	}
	/**
	* Node's previous sibling, or `null` if it is the first child.
	*/
	get previousSibling() {
		const index = this.index;
		return index !== null && this.parent.getChild(index - 1) || null;
	}
	/**
	* Top-most ancestor of the node. If the node has no parent it is the root itself.
	*/
	get root() {
		let root = this;
		while (root.parent) root = root.parent;
		return root;
	}
	/**
	* Returns true if the node is in a tree rooted in the document (is a descendant of one of its roots).
	*/
	isAttached() {
		return this.root.is("rootElement");
	}
	/**
	* Gets a path to the node. The path is an array containing indices of consecutive ancestors of this node,
	* beginning from {@link module:engine/view/node~ViewNode#root root}, down to this node's index.
	*
	* ```ts
	* const abc = downcastWriter.createText( 'abc' );
	* const foo = downcastWriter.createText( 'foo' );
	* const h1 = downcastWriter.createElement( 'h1', null, downcastWriter.createText( 'header' ) );
	* const p = downcastWriter.createElement( 'p', null, [ abc, foo ] );
	* const div = downcastWriter.createElement( 'div', null, [ h1, p ] );
	* foo.getPath(); // Returns [ 1, 3 ]. `foo` is in `p` which is in `div`. `p` starts at offset 1, while `foo` at 3.
	* h1.getPath(); // Returns [ 0 ].
	* div.getPath(); // Returns [].
	* ```
	*
	* @returns The path.
	*/
	getPath() {
		const path = [];
		let node = this;
		while (node.parent) {
			path.unshift(node.index);
			node = node.parent;
		}
		return path;
	}
	/**
	* Returns ancestors array of this node.
	*
	* @param options Options object.
	* @param options.includeSelf When set to `true` this node will be also included in parent's array.
	* @param options.parentFirst When set to `true`, array will be sorted from node's parent to root element,
	* otherwise root element will be the first item in the array.
	* @returns Array with ancestors.
	*/
	getAncestors(options = {}) {
		const ancestors = [];
		let parent = options.includeSelf ? this : this.parent;
		while (parent) {
			ancestors[options.parentFirst ? "push" : "unshift"](parent);
			parent = parent.parent;
		}
		return ancestors;
	}
	/**
	* Returns a {@link module:engine/view/element~ViewElement} or {@link module:engine/view/documentfragment~ViewDocumentFragment}
	* which is a common ancestor of both nodes.
	*
	* @param node The second node.
	* @param options Options object.
	* @param options.includeSelf When set to `true` both nodes will be considered "ancestors" too.
	* Which means that if e.g. node A is inside B, then their common ancestor will be B.
	*/
	getCommonAncestor(node, options = {}) {
		const ancestorsA = this.getAncestors(options);
		const ancestorsB = node.getAncestors(options);
		let i = 0;
		while (ancestorsA[i] == ancestorsB[i] && ancestorsA[i]) i++;
		return i === 0 ? null : ancestorsA[i - 1];
	}
	/**
	* Returns whether this node is before given node. `false` is returned if nodes are in different trees (for example,
	* in different {@link module:engine/view/documentfragment~ViewDocumentFragment}s).
	*
	* @param node Node to compare with.
	*/
	isBefore(node) {
		if (this == node) return false;
		if (this.root !== node.root) return false;
		const thisPath = this.getPath();
		const nodePath = node.getPath();
		const result = compareArrays(thisPath, nodePath);
		switch (result) {
			case "prefix": return true;
			case "extension": return false;
			default: return thisPath[result] < nodePath[result];
		}
	}
	/**
	* Returns whether this node is after given node. `false` is returned if nodes are in different trees (for example,
	* in different {@link module:engine/view/documentfragment~ViewDocumentFragment}s).
	*
	* @param node Node to compare with.
	*/
	isAfter(node) {
		if (this == node) return false;
		if (this.root !== node.root) return false;
		return !this.isBefore(node);
	}
	/**
	* Removes node from parent.
	*
	* @internal
	*/
	_remove() {
		this.parent._removeChildren(this.index);
	}
	/**
	* @internal
	* @param type Type of the change.
	* @param node Changed node.
	* @param data Additional data.
	* @fires change
	*/
	_fireChange(type, node, data) {
		this.fire(`change:${type}`, node, data);
		if (this.parent) this.parent._fireChange(type, node, data);
	}
	/**
	* Converts `ViewNode` to plain object and returns it.
	*
	* @returns `ViewNode` converted to plain object.
	*/
	toJSON() {
		const json = {
			path: this.getPath(),
			type: "Node"
		};
		if (this !== this.root && this.root.is("rootElement")) json.root = this.root.toJSON();
		return json;
	}
};
ViewNode.prototype.is = function(type) {
	return type === "node" || type === "view:node";
};

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* @module engine/view/text
*/
/**
* Tree view text node.
*
* The constructor of this class should not be used directly. To create a new text node instance
* use the {@link module:engine/view/downcastwriter~ViewDowncastWriter#createText `ViewDowncastWriter#createText()`}
* method when working on data downcasted from the model or the
* {@link module:engine/view/upcastwriter~ViewUpcastWriter#createText `ViewUpcastWriter#createText()`}
* method when working on non-semantic views.
*/
var ViewText = class ViewText extends ViewNode {
	/**
	* The text content.
	*
	* Setting the data fires the {@link module:engine/view/node~ViewNode#event:change:text change event}.
	*/
	_textData;
	/**
	* Creates a tree view text node.
	*
	* @see module:engine/view/downcastwriter~ViewDowncastWriter#createText
	* @internal
	* @param document The document instance to which this text node belongs.
	* @param data The text's data.
	*/
	constructor(document, data) {
		super(document);
		this._textData = data;
	}
	/**
	* The text content.
	*/
	get data() {
		return this._textData;
	}
	/**
	* The `_data` property is controlled by a getter and a setter.
	*
	* The getter is required when using the addition assignment operator on protected property:
	*
	* ```ts
	* const foo = downcastWriter.createText( 'foo' );
	* const bar = downcastWriter.createText( 'bar' );
	*
	* foo._data += bar.data;   // executes: `foo._data = foo._data + bar.data`
	* console.log( foo.data ); // prints: 'foobar'
	* ```
	*
	* If the protected getter didn't exist, `foo._data` will return `undefined` and result of the merge will be invalid.
	*
	* The setter sets data and fires the {@link module:engine/view/node~ViewNode#event:change:text change event}.
	*
	* @internal
	*/
	get _data() {
		return this.data;
	}
	set _data(data) {
		this._fireChange("text", this);
		this._textData = data;
	}
	/**
	* Checks if this text node is similar to other text node.
	* Both nodes should have the same data to be considered as similar.
	*
	* @param otherNode Node to check if it is same as this node.
	*/
	isSimilar(otherNode) {
		if (!(otherNode instanceof ViewText)) return false;
		return this === otherNode || this.data === otherNode.data;
	}
	/**
	* Converts `ViewText` instance to plain object and returns it.
	*
	* @returns `ViewText` instance converted to plain object.
	*/
	toJSON() {
		const json = super.toJSON();
		json.type = "Text";
		json.data = this.data;
		return json;
	}
	/**
	* Clones this node.
	*
	* @internal
	* @returns Text node that is a clone of this node.
	*/
	_clone() {
		return new ViewText(this.document, this.data);
	}
};
ViewText.prototype.is = function(type) {
	return type === "$text" || type === "view:$text" || type === "text" || type === "view:text" || type === "node" || type === "view:node";
};

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* @module engine/view/textproxy
*/
/**
* ViewTextProxy is a wrapper for substring of {@link module:engine/view/text~ViewText}. Instance of this class is created by
* {@link module:engine/view/treewalker~ViewTreeWalker} when only a part of {@link module:engine/view/text~ViewText} needs to be returned.
*
* `ViewTextProxy` has an API similar to {@link module:engine/view/text~ViewText Text} and allows to do most of the common tasks performed
* on view nodes.
*
* **Note:** Some `ViewTextProxy` instances may represent whole text node, not just a part of it.
* See {@link module:engine/view/textproxy~ViewTextProxy#isPartial}.
*
* **Note:** `ViewTextProxy` is a readonly interface.
*
* **Note:** `ViewTextProxy` instances are created on the fly basing
* on the current state of parent {@link module:engine/view/text~ViewText}.
* Because of this it is highly unrecommended to store references to `TextProxy instances because they might get
* invalidated due to operations on Document. Also ViewTextProxy is not a {@link module:engine/view/node~ViewNode} so it cannot be
* inserted as a child of {@link module:engine/view/element~ViewElement}.
*
* `ViewTextProxy` instances are created by {@link module:engine/view/treewalker~ViewTreeWalker view tree walker}.
* You should not need to create an instance of this class by your own.
*/
var ViewTextProxy = class extends ViewTypeCheckable {
	/**
	* Reference to the {@link module:engine/view/text~ViewText} element which ViewTextProxy is a substring.
	*/
	textNode;
	/**
	* Text data represented by this text proxy.
	*/
	data;
	/**
	* Offset in the `textNode` where this `ViewTextProxy` instance starts.
	*/
	offsetInText;
	/**
	* Creates a text proxy.
	*
	* @internal
	* @param textNode Text node which part is represented by this text proxy.
	* @param offsetInText Offset in {@link module:engine/view/textproxy~ViewTextProxy#textNode text node}
	* from which the text proxy starts.
	* @param length Text proxy length, that is how many text node's characters, starting from `offsetInText` it represents.
	*/
	constructor(textNode, offsetInText, length) {
		super();
		this.textNode = textNode;
		if (offsetInText < 0 || offsetInText > textNode.data.length)
 /**
		* Given offsetInText value is incorrect.
		*
		* @error view-textproxy-wrong-offsetintext
		*/
		throw new CKEditorError("view-textproxy-wrong-offsetintext", this);
		if (length < 0 || offsetInText + length > textNode.data.length)
 /**
		* Given length value is incorrect.
		*
		* @error view-textproxy-wrong-length
		*/
		throw new CKEditorError("view-textproxy-wrong-length", this);
		this.data = textNode.data.substring(offsetInText, offsetInText + length);
		this.offsetInText = offsetInText;
	}
	/**
	* Offset size of this node.
	*/
	get offsetSize() {
		return this.data.length;
	}
	/**
	* Flag indicating whether `ViewTextProxy` instance covers only part of the original {@link module:engine/view/text~ViewText text node}
	* (`true`) or the whole text node (`false`).
	*
	* This is `false` when text proxy starts at the very beginning of {@link module:engine/view/textproxy~ViewTextProxy#textNode textNode}
	* ({@link module:engine/view/textproxy~ViewTextProxy#offsetInText offsetInText} equals `0`) and text proxy sizes is equal to
	* text node size.
	*/
	get isPartial() {
		return this.data.length !== this.textNode.data.length;
	}
	/**
	* Parent of this text proxy, which is same as parent of text node represented by this text proxy.
	*/
	get parent() {
		return this.textNode.parent;
	}
	/**
	* Root of this text proxy, which is same as root of text node represented by this text proxy.
	*/
	get root() {
		return this.textNode.root;
	}
	/**
	* {@link module:engine/view/document~ViewDocument View document} that owns this text proxy, or `null` if the text proxy is inside
	* {@link module:engine/view/documentfragment~ViewDocumentFragment document fragment}.
	*/
	get document() {
		return this.textNode.document;
	}
	/**
	* Returns ancestors array of this text proxy.
	*
	* @param options Options object.
	* @param options.includeSelf When set to `true`, textNode will be also included in parent's array.
	* @param options.parentFirst When set to `true`, array will be sorted from text proxy parent to
	* root element, otherwise root element will be the first item in the array.
	* @returns Array with ancestors.
	*/
	getAncestors(options = {}) {
		const ancestors = [];
		let parent = options.includeSelf ? this.textNode : this.parent;
		while (parent !== null) {
			ancestors[options.parentFirst ? "push" : "unshift"](parent);
			parent = parent.parent;
		}
		return ancestors;
	}
};
ViewTextProxy.prototype.is = function(type) {
	return type === "$textProxy" || type === "view:$textProxy" || type === "textProxy" || type === "view:textProxy";
};

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* @module engine/conversion/viewconsumable
*/
/**
* Class used for handling consumption of view {@link module:engine/view/element~ViewElement elements},
* {@link module:engine/view/text~ViewText text nodes} and
* {@link module:engine/view/documentfragment~ViewDocumentFragment document fragments}.
* Element's name and its parts (attributes, classes and styles) can be consumed separately. Consuming an element's name
* does not consume its attributes, classes and styles.
* To add items for consumption use {@link module:engine/conversion/viewconsumable~ViewConsumable#add add method}.
* To test items use {@link module:engine/conversion/viewconsumable~ViewConsumable#test test method}.
* To consume items use {@link module:engine/conversion/viewconsumable~ViewConsumable#consume consume method}.
* To revert already consumed items use {@link module:engine/conversion/viewconsumable~ViewConsumable#revert revert method}.
*
* ```ts
* viewConsumable.add( element, { name: true } ); // Adds element's name as ready to be consumed.
* viewConsumable.add( textNode ); // Adds text node for consumption.
* viewConsumable.add( docFragment ); // Adds document fragment for consumption.
* viewConsumable.test( element, { name: true }  ); // Tests if element's name can be consumed.
* viewConsumable.test( textNode ); // Tests if text node can be consumed.
* viewConsumable.test( docFragment ); // Tests if document fragment can be consumed.
* viewConsumable.consume( element, { name: true }  ); // Consume element's name.
* viewConsumable.consume( textNode ); // Consume text node.
* viewConsumable.consume( docFragment ); // Consume document fragment.
* viewConsumable.revert( element, { name: true }  ); // Revert already consumed element's name.
* viewConsumable.revert( textNode ); // Revert already consumed text node.
* viewConsumable.revert( docFragment ); // Revert already consumed document fragment.
* ```
*/
var ViewConsumable = class ViewConsumable {
	/**
	* Map of consumable elements. If {@link module:engine/view/element~ViewElement element} is used as a key,
	* {@link module:engine/conversion/viewconsumable~ViewElementConsumables ViewElementConsumables} instance is stored as value.
	* For {@link module:engine/view/text~ViewText text nodes} and
	* {@link module:engine/view/documentfragment~ViewDocumentFragment document fragments} boolean value is stored as value.
	*/
	_consumables = /* @__PURE__ */ new Map();
	/**
	* Adds view {@link module:engine/view/element~ViewElement element}, {@link module:engine/view/text~ViewText text node} or
	* {@link module:engine/view/documentfragment~ViewDocumentFragment document fragment} as ready to be consumed.
	*
	* ```ts
	* viewConsumable.add( p, { name: true } ); // Adds element's name to consume.
	* viewConsumable.add( p, { attributes: 'name' } ); // Adds element's attribute.
	* viewConsumable.add( p, { classes: 'foobar' } ); // Adds element's class.
	* viewConsumable.add( p, { styles: 'color' } ); // Adds element's style
	* viewConsumable.add( p, { attributes: 'name', styles: 'color' } ); // Adds attribute and style.
	* viewConsumable.add( p, { classes: [ 'baz', 'bar' ] } ); // Multiple consumables can be provided.
	* viewConsumable.add( textNode ); // Adds text node to consume.
	* viewConsumable.add( docFragment ); // Adds document fragment to consume.
	* ```
	*
	* Throws {@link module:utils/ckeditorerror~CKEditorError CKEditorError} `viewconsumable-invalid-attribute` when `class` or `style`
	* attribute is provided - it should be handled separately by providing actual style/class.
	*
	* ```ts
	* viewConsumable.add( p, { attributes: 'style' } ); // This call will throw an exception.
	* viewConsumable.add( p, { styles: 'color' } ); // This is properly handled style.
	* ```
	*
	* @param consumables Used only if first parameter is {@link module:engine/view/element~ViewElement view element} instance.
	* @param consumables.name If set to true element's name will be included.
	* @param consumables.attributes Attribute name or array of attribute names.
	* @param consumables.classes Class name or array of class names.
	* @param consumables.styles Style name or array of style names.
	*/
	add(element, consumables) {
		let elementConsumables;
		if (element.is("$text") || element.is("documentFragment")) {
			this._consumables.set(element, true);
			return;
		}
		if (!this._consumables.has(element)) {
			elementConsumables = new ViewElementConsumables(element);
			this._consumables.set(element, elementConsumables);
		} else elementConsumables = this._consumables.get(element);
		elementConsumables.add(consumables ? normalizeConsumables(consumables) : element._getConsumables());
	}
	/**
	* Tests if {@link module:engine/view/element~ViewElement view element}, {@link module:engine/view/text~ViewText text node} or
	* {@link module:engine/view/documentfragment~ViewDocumentFragment document fragment} can be consumed.
	* It returns `true` when all items included in method's call can be consumed. Returns `false` when
	* first already consumed item is found and `null` when first non-consumable item is found.
	*
	* ```ts
	* viewConsumable.test( p, { name: true } ); // Tests element's name.
	* viewConsumable.test( p, { attributes: 'name' } ); // Tests attribute.
	* viewConsumable.test( p, { classes: 'foobar' } ); // Tests class.
	* viewConsumable.test( p, { styles: 'color' } ); // Tests style.
	* viewConsumable.test( p, { attributes: 'name', styles: 'color' } ); // Tests attribute and style.
	* viewConsumable.test( p, { classes: [ 'baz', 'bar' ] } ); // Multiple consumables can be tested.
	* viewConsumable.test( textNode ); // Tests text node.
	* viewConsumable.test( docFragment ); // Tests document fragment.
	* ```
	*
	* Testing classes and styles as attribute will test if all added classes/styles can be consumed.
	*
	* ```ts
	* viewConsumable.test( p, { attributes: 'class' } ); // Tests if all added classes can be consumed.
	* viewConsumable.test( p, { attributes: 'style' } ); // Tests if all added styles can be consumed.
	* ```
	*
	* @param consumables Used only if first parameter is {@link module:engine/view/element~ViewElement view element} instance.
	* @param consumables.name If set to true element's name will be included.
	* @param consumables.attributes Attribute name or array of attribute names.
	* @param consumables.classes Class name or array of class names.
	* @param consumables.styles Style name or array of style names.
	* @returns Returns `true` when all items included in method's call can be consumed. Returns `false`
	* when first already consumed item is found and `null` when first non-consumable item is found.
	*/
	test(element, consumables) {
		const elementConsumables = this._consumables.get(element);
		if (elementConsumables === void 0) return null;
		if (element.is("$text") || element.is("documentFragment")) return elementConsumables;
		return elementConsumables.test(normalizeConsumables(consumables));
	}
	/**
	* Consumes {@link module:engine/view/element~ViewElement view element}, {@link module:engine/view/text~ViewText text node} or
	* {@link module:engine/view/documentfragment~ViewDocumentFragment document fragment}.
	* It returns `true` when all items included in method's call can be consumed, otherwise returns `false`.
	*
	* ```ts
	* viewConsumable.consume( p, { name: true } ); // Consumes element's name.
	* viewConsumable.consume( p, { attributes: 'name' } ); // Consumes element's attribute.
	* viewConsumable.consume( p, { classes: 'foobar' } ); // Consumes element's class.
	* viewConsumable.consume( p, { styles: 'color' } ); // Consumes element's style.
	* viewConsumable.consume( p, { attributes: 'name', styles: 'color' } ); // Consumes attribute and style.
	* viewConsumable.consume( p, { classes: [ 'baz', 'bar' ] } ); // Multiple consumables can be consumed.
	* viewConsumable.consume( textNode ); // Consumes text node.
	* viewConsumable.consume( docFragment ); // Consumes document fragment.
	* ```
	*
	* Consuming classes and styles as attribute will test if all added classes/styles can be consumed.
	*
	* ```ts
	* viewConsumable.consume( p, { attributes: 'class' } ); // Consume only if all added classes can be consumed.
	* viewConsumable.consume( p, { attributes: 'style' } ); // Consume only if all added styles can be consumed.
	* ```
	*
	* @param consumables Used only if first parameter is {@link module:engine/view/element~ViewElement view element} instance.
	* @param consumables.name If set to true element's name will be included.
	* @param consumables.attributes Attribute name or array of attribute names.
	* @param consumables.classes Class name or array of class names.
	* @param consumables.styles Style name or array of style names.
	* @returns Returns `true` when all items included in method's call can be consumed,
	* otherwise returns `false`.
	*/
	consume(element, consumables) {
		if (element.is("$text") || element.is("documentFragment")) {
			if (!this.test(element, consumables)) return false;
			this._consumables.set(element, false);
			return true;
		}
		const elementConsumables = this._consumables.get(element);
		if (elementConsumables === void 0) return false;
		return elementConsumables.consume(normalizeConsumables(consumables));
	}
	/**
	* Reverts {@link module:engine/view/element~ViewElement view element}, {@link module:engine/view/text~ViewText text node} or
	* {@link module:engine/view/documentfragment~ViewDocumentFragment document fragment} so they can be consumed once again.
	* Method does not revert items that were never previously added for consumption, even if they are included in
	* method's call.
	*
	* ```ts
	* viewConsumable.revert( p, { name: true } ); // Reverts element's name.
	* viewConsumable.revert( p, { attributes: 'name' } ); // Reverts element's attribute.
	* viewConsumable.revert( p, { classes: 'foobar' } ); // Reverts element's class.
	* viewConsumable.revert( p, { styles: 'color' } ); // Reverts element's style.
	* viewConsumable.revert( p, { attributes: 'name', styles: 'color' } ); // Reverts attribute and style.
	* viewConsumable.revert( p, { classes: [ 'baz', 'bar' ] } ); // Multiple names can be reverted.
	* viewConsumable.revert( textNode ); // Reverts text node.
	* viewConsumable.revert( docFragment ); // Reverts document fragment.
	* ```
	*
	* Reverting classes and styles as attribute will revert all classes/styles that were previously added for
	* consumption.
	*
	* ```ts
	* viewConsumable.revert( p, { attributes: 'class' } ); // Reverts all classes added for consumption.
	* viewConsumable.revert( p, { attributes: 'style' } ); // Reverts all styles added for consumption.
	* ```
	*
	* @param consumables Used only if first parameter is {@link module:engine/view/element~ViewElement view element} instance.
	* @param consumables.name If set to true element's name will be included.
	* @param consumables.attributes Attribute name or array of attribute names.
	* @param consumables.classes Class name or array of class names.
	* @param consumables.styles Style name or array of style names.
	*/
	revert(element, consumables) {
		const elementConsumables = this._consumables.get(element);
		if (elementConsumables !== void 0) if (element.is("$text") || element.is("documentFragment")) this._consumables.set(element, true);
		else elementConsumables.revert(normalizeConsumables(consumables));
	}
	/**
	* Creates {@link module:engine/conversion/viewconsumable~ViewConsumable ViewConsumable} instance from
	* {@link module:engine/view/node~ViewNode node} or {@link module:engine/view/documentfragment~ViewDocumentFragment document fragment}.
	* Instance will contain all elements, child nodes, attributes, styles and classes added for consumption.
	*
	* @param from View node or document fragment from which `ViewConsumable` will be created.
	* @param instance If provided, given `ViewConsumable` instance will be used
	* to add all consumables. It will be returned instead of a new instance.
	*/
	static createFrom(from, instance) {
		if (!instance) instance = new ViewConsumable();
		if (from.is("$text")) instance.add(from);
		else if (from.is("element") || from.is("documentFragment")) {
			instance.add(from);
			for (const child of from.getChildren()) ViewConsumable.createFrom(child, instance);
		}
		return instance;
	}
};
/**
* This is a private helper-class for {@link module:engine/conversion/viewconsumable~ViewConsumable}.
* It represents and manipulates consumable parts of a single {@link module:engine/view/element~ViewElement}.
*
* @internal
*/
var ViewElementConsumables = class {
	element;
	/**
	* Flag indicating if name of the element can be consumed.
	*/
	_canConsumeName = null;
	/**
	* A map of element's consumables.
	* * For plain attributes the value is a boolean indicating whether the attribute is available to consume.
	* * For token based attributes (like class list and style) the value is a map of tokens to booleans
	* indicating whether the token is available to consume on the given attribute.
	*/
	_attributes = /* @__PURE__ */ new Map();
	/**
	* Creates ViewElementConsumables instance.
	*
	* @param from View element from which `ViewElementConsumables` is being created.
	*/
	constructor(from) {
		this.element = from;
	}
	/**
	* Adds consumable parts of the {@link module:engine/view/element~ViewElement view element}.
	* Element's name itself can be marked to be consumed (when element's name is consumed its attributes, classes and
	* styles still could be consumed):
	*
	* ```ts
	* consumables.add( { name: true } );
	* ```
	*
	* Attributes classes and styles:
	*
	* ```ts
	* consumables.add( { attributes: [ [ 'title' ], [ 'class', 'foo' ], [ 'style', 'color'] ] } );
	* consumables.add( { attributes: [ [ 'title' ], [ 'name' ], [ 'class', 'foo' ], [ 'class', 'bar' ] ] } );
	* ```
	*
	* Note: This method accepts only {@link module:engine/view/element~ViewNormalizedConsumables}.
	* You can use {@link module:engine/conversion/viewconsumable~normalizeConsumables} helper to convert from
	* {@link module:engine/conversion/viewconsumable~Consumables} to `ViewNormalizedConsumables`.
	*
	* Throws {@link module:utils/ckeditorerror~CKEditorError CKEditorError} `viewconsumable-invalid-attribute` when `class` or `style`
	* attribute is provided - it should be handled separately by providing `style` and `class` in consumables object.
	*
	* @param consumables Object describing which parts of the element can be consumed.
	*/
	add(consumables) {
		if (consumables.name) this._canConsumeName = true;
		for (const [name, token] of consumables.attributes) if (token) {
			let attributeTokens = this._attributes.get(name);
			if (!attributeTokens || typeof attributeTokens == "boolean") {
				attributeTokens = /* @__PURE__ */ new Map();
				this._attributes.set(name, attributeTokens);
			}
			attributeTokens.set(token, true);
		} else if (name == "style" || name == "class")
 /**
		* Class and style attributes should be handled separately in
		* {@link module:engine/conversion/viewconsumable~ViewConsumable#add `ViewConsumable#add()`}.
		*
		* What you have done is trying to use:
		*
		* ```ts
		* consumables.add( { attributes: [ 'class', 'style' ] } );
		* ```
		*
		* While each class and style should be registered separately:
		*
		* ```ts
		* consumables.add( { classes: 'some-class', styles: 'font-weight' } );
		* ```
		*
		* @error viewconsumable-invalid-attribute
		*/
		throw new CKEditorError("viewconsumable-invalid-attribute", this);
		else this._attributes.set(name, true);
	}
	/**
	* Tests if parts of the {@link module:engine/view/element~ViewElement view element} can be consumed.
	*
	* Element's name can be tested:
	*
	* ```ts
	* consumables.test( { name: true } );
	* ```
	*
	* Attributes classes and styles:
	*
	* ```ts
	* consumables.test( { attributes: [ [ 'title' ], [ 'class', 'foo' ], [ 'style', 'color' ] ] } );
	* consumables.test( { attributes: [ [ 'title' ], [ 'name' ], [ 'class', 'foo' ], [ 'class', 'bar' ] ] } );
	* ```
	*
	* @param consumables Object describing which parts of the element should be tested.
	* @returns `true` when all tested items can be consumed, `null` when even one of the items
	* was never marked for consumption and `false` when even one of the items was already consumed.
	*/
	test(consumables) {
		if (consumables.name && !this._canConsumeName) return this._canConsumeName;
		for (const [name, token] of consumables.attributes) {
			const value = this._attributes.get(name);
			if (value === void 0) return null;
			if (value === false) return false;
			if (value === true) continue;
			if (!token) {
				for (const tokenValue of value.values()) if (!tokenValue) return false;
			} else {
				const tokenValue = value.get(token);
				if (tokenValue === void 0) return null;
				if (!tokenValue) return false;
			}
		}
		return true;
	}
	/**
	* Tests if parts of the {@link module:engine/view/element~ViewElement view element} can be consumed and consumes them if available.
	* It returns `true` when all items included in method's call can be consumed, otherwise returns `false`.
	*
	* Element's name can be consumed:
	*
	* ```ts
	* consumables.consume( { name: true } );
	* ```
	*
	* Attributes classes and styles:
	*
	* ```ts
	* consumables.consume( { attributes: [ [ 'title' ], [ 'class', 'foo' ], [ 'style', 'color' ] ] } );
	* consumables.consume( { attributes: [ [ 'title' ], [ 'name' ], [ 'class', 'foo' ], [ 'class', 'bar' ] ] } );
	* ```
	*
	* @param consumables Object describing which parts of the element should be consumed.
	* @returns `true` when all tested items can be consumed and `false` when even one of the items could not be consumed.
	*/
	consume(consumables) {
		if (!this.test(consumables)) return false;
		if (consumables.name) this._canConsumeName = false;
		for (const [name, token] of consumables.attributes) {
			const value = this._attributes.get(name);
			if (typeof value == "boolean") for (const [toConsume] of this.element._getConsumables(name, token).attributes) this._attributes.set(toConsume, false);
			else if (!token) for (const token of value.keys()) value.set(token, false);
			else for (const [, toConsume] of this.element._getConsumables(name, token).attributes) value.set(toConsume, false);
		}
		return true;
	}
	/**
	* Revert already consumed parts of {@link module:engine/view/element~ViewElement view Element}, so they can be consumed once again.
	* Element's name can be reverted:
	*
	* ```ts
	* consumables.revert( { name: true } );
	* ```
	*
	* Attributes classes and styles:
	*
	* ```ts
	* consumables.revert( { attributes: [ [ 'title' ], [ 'class', 'foo' ], [ 'style', 'color' ] ] } );
	* consumables.revert( { attributes: [ [ 'title' ], [ 'name' ], [ 'class', 'foo' ], [ 'class', 'bar' ] ] } );
	* ```
	*
	* @param consumables Object describing which parts of the element should be reverted.
	*/
	revert(consumables) {
		if (consumables.name) this._canConsumeName = true;
		for (const [name, token] of consumables.attributes) {
			const value = this._attributes.get(name);
			if (value === false) {
				this._attributes.set(name, true);
				continue;
			}
			if (value === void 0 || value === true) continue;
			if (!token) for (const token of value.keys()) value.set(token, true);
			else if (value.get(token) === false) value.set(token, true);
		}
	}
};
/**
* Normalizes a {@link module:engine/conversion/viewconsumable~Consumables} or {@link module:engine/view/matcher~Match}
* to a {@link module:engine/view/element~ViewNormalizedConsumables}.
*
* @internal
*/
function normalizeConsumables(consumables) {
	const attributes = [];
	if ("attributes" in consumables && consumables.attributes) normalizeConsumablePart(attributes, consumables.attributes);
	if ("classes" in consumables && consumables.classes) normalizeConsumablePart(attributes, consumables.classes, "class");
	if ("styles" in consumables && consumables.styles) normalizeConsumablePart(attributes, consumables.styles, "style");
	return {
		name: consumables.name || false,
		attributes
	};
}
/**
* Normalizes a list of consumable attributes to a common tuple format.
*/
function normalizeConsumablePart(attributes, items, prefix) {
	if (typeof items == "string") {
		attributes.push(prefix ? [prefix, items] : [items]);
		return;
	}
	for (const item of items) if (Array.isArray(item)) attributes.push(item);
	else attributes.push(prefix ? [prefix, item] : [item]);
}

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* View matcher class.
* Instance of this class can be used to find {@link module:engine/view/element~ViewElement elements} that match given pattern.
*/
var Matcher = class {
	_patterns = [];
	/**
	* Creates new instance of Matcher.
	*
	* @param pattern Match patterns. See {@link module:engine/view/matcher~Matcher#add add method} for more information.
	*/
	constructor(...pattern) {
		this.add(...pattern);
	}
	/**
	* Adds pattern or patterns to matcher instance.
	*
	* ```ts
	* // String.
	* matcher.add( 'div' );
	*
	* // Regular expression.
	* matcher.add( /^\w/ );
	*
	* // Single class.
	* matcher.add( {
	* 	classes: 'foobar'
	* } );
	* ```
	*
	* See {@link module:engine/view/matcher~MatcherPattern} for more examples.
	*
	* Multiple patterns can be added in one call:
	*
	* ```ts
	* matcher.add( 'div', { classes: 'foobar' } );
	* ```
	*
	* @param pattern Object describing pattern details. If string or regular expression
	* is provided it will be used to match element's name. Pattern can be also provided in a form
	* of a function - then this function will be called with each {@link module:engine/view/element~ViewElement element} as a parameter.
	* Function's return value will be stored under `match` key of the object returned from
	* {@link module:engine/view/matcher~Matcher#match match} or {@link module:engine/view/matcher~Matcher#matchAll matchAll} methods.
	*/
	add(...pattern) {
		for (let item of pattern) {
			if (typeof item == "string" || item instanceof RegExp) item = { name: item };
			this._patterns.push(item);
		}
	}
	/**
	* Matches elements for currently stored patterns. Returns match information about first found
	* {@link module:engine/view/element~ViewElement element}, otherwise returns `null`.
	*
	* Example of returned object:
	*
	* ```ts
	* {
	* 	element: <instance of found element>,
	* 	pattern: <pattern used to match found element>,
	* 	match: {
	* 		name: true,
	* 		attributes: [
	* 			[ 'title' ],
	* 			[ 'href' ],
	* 			[ 'class', 'foo' ],
	* 			[ 'style', 'color' ],
	* 			[ 'style', 'position' ]
	* 		]
	* 	}
	* }
	* ```
	*
	* You could use the `match` field from the above returned object as an input for the
	* {@link module:engine/conversion/viewconsumable~ViewConsumable#test `ViewConsumable#test()`} and
	* {@link module:engine/conversion/viewconsumable~ViewConsumable#consume `ViewConsumable#consume()`} methods.
	*
	* @see module:engine/view/matcher~Matcher#add
	* @see module:engine/view/matcher~Matcher#matchAll
	* @param element View element to match against stored patterns.
	* @returns The match information about found element or `null`.
	*/
	match(...element) {
		for (const singleElement of element) for (const pattern of this._patterns) {
			const match = this._isElementMatching(singleElement, pattern);
			if (match) return {
				element: singleElement,
				pattern,
				match
			};
		}
		return null;
	}
	/**
	* Matches elements for currently stored patterns. Returns array of match information with all found
	* {@link module:engine/view/element~ViewElement elements}. If no element is found - returns `null`.
	*
	* @see module:engine/view/matcher~Matcher#add
	* @see module:engine/view/matcher~Matcher#match
	* @param element View element to match against stored patterns.
	* @returns Array with match information about found elements or `null`. For more information
	* see {@link module:engine/view/matcher~Matcher#match match method} description.
	*/
	matchAll(...element) {
		const results = [];
		for (const singleElement of element) for (const pattern of this._patterns) {
			const match = this._isElementMatching(singleElement, pattern);
			if (match) results.push({
				element: singleElement,
				pattern,
				match
			});
		}
		return results.length > 0 ? results : null;
	}
	/**
	* Returns the name of the element to match if there is exactly one pattern added to the matcher instance
	* and it matches element name defined by `string` (not `RegExp`). Otherwise, returns `null`.
	*
	* @returns Element name trying to match.
	*/
	getElementName() {
		if (this._patterns.length !== 1) return null;
		const pattern = this._patterns[0];
		const name = pattern.name;
		return typeof pattern != "function" && name && !(name instanceof RegExp) ? name : null;
	}
	/**
	* Returns match information if {@link module:engine/view/element~ViewElement element} is matching provided pattern.
	* If element cannot be matched to provided pattern - returns `null`.
	*
	* @returns Returns object with match information or null if element is not matching.
	*/
	_isElementMatching(element, pattern) {
		if (typeof pattern == "function") {
			const match = pattern(element);
			if (!match || typeof match != "object") return match;
			return normalizeConsumables(match);
		}
		const match = {};
		if (pattern.name) {
			match.name = matchName(pattern.name, element.name);
			if (!match.name) return null;
		}
		const attributesMatch = [];
		if (pattern.attributes && !matchAttributes(pattern.attributes, element, attributesMatch)) return null;
		if (pattern.classes && !matchClasses(pattern.classes, element, attributesMatch)) return null;
		if (pattern.styles && !matchStyles(pattern.styles, element, attributesMatch)) return null;
		if (attributesMatch.length) match.attributes = attributesMatch;
		return match;
	}
};
/**
* Returns true if the given `item` matches the pattern.
*
* @internal
* @param pattern A pattern representing a key/value we want to match.
* @param item An actual item key/value (e.g. `'src'`, `'background-color'`, `'ck-widget'`) we're testing against pattern.
*/
function isPatternMatched(pattern, item) {
	return pattern === true || pattern === item || pattern instanceof RegExp && !!String(item).match(pattern);
}
/**
* Checks if name can be matched by provided pattern.
*
* @returns Returns `true` if name can be matched, `false` otherwise.
*/
function matchName(pattern, name) {
	if (pattern instanceof RegExp) return !!name.match(pattern);
	return pattern === name;
}
/**
* Bring all the possible pattern forms to an array of tuples where first item is a key, second is a value,
* and third optional is a token value.
*
* Examples:
*
* Boolean pattern value:
*
* ```ts
* true
* ```
*
* to
*
* ```ts
* [ [ true, true ] ]
* ```
*
* Textual pattern value:
*
* ```ts
* 'attribute-name-or-class-or-style'
* ```
*
* to
*
* ```ts
* [ [ 'attribute-name-or-class-or-style', true ] ]
* ```
*
* Regular expression:
*
* ```ts
* /^data-.*$/
* ```
*
* to
*
* ```ts
* [ [ /^data-.*$/, true ] ]
* ```
*
* Objects (plain or with `key` and `value` specified explicitly):
*
* ```ts
* {
* 	src: /^https:.*$/
* }
* ```
*
* or
*
* ```ts
* [ {
* 	key: 'src',
* 	value: /^https:.*$/
* } ]
* ```
*
* to:
*
* ```ts
* [ [ 'src', /^https:.*$/ ] ]
* ```
*
* @returns Returns an array of objects or null if provided patterns were not in an expected form.
*/
function normalizePatterns(patterns, prefix) {
	if (Array.isArray(patterns)) return patterns.map((pattern) => {
		if (typeof pattern !== "object" || pattern instanceof RegExp) return prefix ? [
			prefix,
			pattern,
			true
		] : [pattern, true];
		if (pattern.key === void 0 || pattern.value === void 0) logWarning("matcher-pattern-missing-key-or-value", pattern);
		return prefix ? [
			prefix,
			pattern.key,
			pattern.value
		] : [pattern.key, pattern.value];
	});
	if (typeof patterns !== "object" || patterns instanceof RegExp) return [prefix ? [
		prefix,
		patterns,
		true
	] : [patterns, true]];
	const normalizedPatterns = [];
	for (const key in patterns) if (Object.prototype.hasOwnProperty.call(patterns, key)) normalizedPatterns.push(prefix ? [
		prefix,
		key,
		patterns[key]
	] : [key, patterns[key]]);
	return normalizedPatterns;
}
/**
* Checks if attributes of provided element can be matched against provided patterns.
*
* @param patterns Object with information about attributes to match. Each key of the object will be
* used as attribute name. Value of each key can be a string or regular expression to match against attribute value.
* @param  element Element which attributes will be tested.
* @param match An array to populate with matching tuples.
* @returns Returns array with matched attribute names or `null` if no attributes were matched.
*/
function matchAttributes(patterns, element, match) {
	let excludeAttributes;
	if (typeof patterns === "object" && !(patterns instanceof RegExp) && !Array.isArray(patterns)) {
		if (patterns.style !== void 0) logWarning("matcher-pattern-deprecated-attributes-style-key", patterns);
		if (patterns.class !== void 0) logWarning("matcher-pattern-deprecated-attributes-class-key", patterns);
	} else excludeAttributes = ["class", "style"];
	return element._collectAttributesMatch(normalizePatterns(patterns), match, excludeAttributes);
}
/**
* Checks if classes of provided element can be matched against provided patterns.
*
* @param patterns Array of strings or regular expressions to match against element's classes.
* @param element Element which classes will be tested.
* @param match An array to populate with matching tuples.
* @returns Returns array with matched class names or `null` if no classes were matched.
*/
function matchClasses(patterns, element, match) {
	return element._collectAttributesMatch(normalizePatterns(patterns, "class"), match);
}
/**
* Checks if styles of provided element can be matched against provided patterns.
*
* @param patterns Object with information about styles to match. Each key of the object will be
* used as style name. Value of each key can be a string or regular expression to match against style value.
* @param element Element which styles will be tested.
* @param match An array to populate with matching tuples.
* @returns Returns array with matched style names or `null` if no styles were matched.
*/
function matchStyles(patterns, element, match) {
	return element._collectAttributesMatch(normalizePatterns(patterns, "style"), match);
}
/**
* The key-value matcher pattern is missing key or value. Both must be present.
* Refer the documentation: {@link module:engine/view/matcher~MatcherPattern}.
*
* @param pattern Pattern with missing properties.
* @error matcher-pattern-missing-key-or-value
*/
/**
* The key-value matcher pattern for `attributes` option is using deprecated `style` key.
*
* Use `styles` matcher pattern option instead:
*
* ```ts
* // Instead of:
* const pattern = {
* 	attributes: {
* 		key1: 'value1',
* 		key2: 'value2',
* 		style: /^border.*$/
* 	}
* }
*
* // Use:
* const pattern = {
* 	attributes: {
* 		key1: 'value1',
* 		key2: 'value2'
* 	},
* 	styles: /^border.*$/
* }
* ```
*
* Refer to the `#update-to-ckeditor-5-v2910` section in the `Update to v29.x` section in the
* {@glink updating/guides/updating-from-older-versions updating from older versions} guide
* and {@link module:engine/view/matcher~MatcherPattern} documentation.
*
* @param pattern Pattern with missing properties.
* @error matcher-pattern-deprecated-attributes-style-key
*/
/**
* The key-value matcher pattern for `attributes` option is using deprecated `class` key.
*
* Use `classes` matcher pattern option instead:
*
* ```ts
* // Instead of:
* const pattern = {
* 	attributes: {
* 		key1: 'value1',
* 		key2: 'value2',
* 		class: 'foobar'
* 	}
* }
*
* // Use:
* const pattern = {
* 	attributes: {
* 		key1: 'value1',
* 		key2: 'value2'
* 	},
* 	classes: 'foobar'
* }
* ```
*
* Refer to the `#update-to-ckeditor-5-v2910` paragraph in the Update to v29.x guide in the
* {@glink updating/guides/updating-from-older-versions updating from older versions} guide
* and the {@link module:engine/view/matcher~MatcherPattern} documentation.
*
* @param pattern Pattern with missing properties.
* @error matcher-pattern-deprecated-attributes-class-key
*/

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* @module engine/view/stylesmap
*/
/**
* Styles map. Allows handling (adding, removing, retrieving) a set of style rules (usually, of an element).
*/
var StylesMap = class {
	/**
	* Keeps an internal representation of styles map. Normalized styles are kept as object tree to allow unified modification and
	* value access model using lodash's get, set, unset, etc methods.
	*
	* When no style processor rules are defined it acts as simple key-value storage.
	*/
	_styles;
	/**
	* Cached list of style names for faster access.
	*/
	_cachedStyleNames = null;
	/**
	* Cached list of expanded style names for faster access.
	*/
	_cachedExpandedStyleNames = null;
	/**
	* An instance of the {@link module:engine/view/stylesmap~StylesProcessor}.
	*/
	_styleProcessor;
	/**
	* Creates Styles instance.
	*/
	constructor(styleProcessor) {
		this._styles = {};
		this._styleProcessor = styleProcessor;
	}
	/**
	* Returns true if style map has no styles set.
	*/
	get isEmpty() {
		return !Object.entries(this._styles).length;
	}
	/**
	* Number of styles defined.
	*/
	get size() {
		if (this.isEmpty) return 0;
		return this.getStyleNames().length;
	}
	/**
	* Set styles map to a new value.
	*
	* ```ts
	* styles.setTo( 'border:1px solid blue;margin-top:1px;' );
	* ```
	*/
	setTo(inlineStyle) {
		this.clear();
		const parsedStyles = parseInlineStyles(inlineStyle);
		for (const [key, value] of parsedStyles) this._styleProcessor.toNormalizedForm(key, value, this._styles);
		return this;
	}
	/**
	* Checks if a given style is set.
	*
	* ```ts
	* styles.setTo( 'margin-left:1px;' );
	*
	* styles.has( 'margin-left' );    // -> true
	* styles.has( 'padding' );        // -> false
	* ```
	*
	* **Note**: This check supports normalized style names.
	*
	* ```ts
	* // Enable 'margin' shorthand processing:
	* editor.data.addStyleProcessorRules( addMarginStylesRules );
	*
	* styles.setTo( 'margin:2px;' );
	*
	* styles.has( 'margin' );         // -> true
	* styles.has( 'margin-top' );     // -> true
	* styles.has( 'margin-left' );    // -> true
	*
	* styles.remove( 'margin-top' );
	*
	* styles.has( 'margin' );         // -> false
	* styles.has( 'margin-top' );     // -> false
	* styles.has( 'margin-left' );    // -> true
	* ```
	*
	* @param name Style name.
	*/
	has(name) {
		if (this.isEmpty) return false;
		const propertyDescriptor = this._styleProcessor.getReducedForm(name, this._styles).find(([property]) => property === name);
		return Array.isArray(propertyDescriptor);
	}
	set(nameOrObject, valueOrObject) {
		this._cachedStyleNames = null;
		this._cachedExpandedStyleNames = null;
		if (isObject(nameOrObject)) for (const [key, value] of Object.entries(nameOrObject)) this._styleProcessor.toNormalizedForm(key, value, this._styles);
		else this._styleProcessor.toNormalizedForm(nameOrObject, valueOrObject, this._styles);
	}
	/**
	* Removes given style.
	*
	* ```ts
	* styles.setTo( 'background:#f00;margin-right:2px;' );
	*
	* styles.remove( 'background' );
	*
	* styles.toString();   // -> 'margin-right:2px;'
	* ```
	*
	* ***Note**:* This method uses {@link module:engine/controller/datacontroller~DataController#addStyleProcessorRules
	* enabled style processor rules} to normalize passed values.
	*
	* ```ts
	* // Enable 'margin' shorthand processing:
	* editor.data.addStyleProcessorRules( addMarginStylesRules );
	*
	* styles.setTo( 'margin:1px' );
	*
	* styles.remove( 'margin-top' );
	* styles.remove( 'margin-right' );
	*
	* styles.toString(); // -> 'margin-bottom:1px;margin-left:1px;'
	* ```
	*
	* @param names Style name or an array of names.
	*/
	remove(names) {
		const normalizedStylesToRemove = {};
		for (const name of toArray(names)) {
			const path = toPath(name);
			const pathValue = get(this._styles, path);
			if (pathValue) appendStyleValue(normalizedStylesToRemove, path, pathValue);
			else {
				const value = this.getAsString(name);
				if (value !== void 0) this._styleProcessor.toNormalizedForm(name, value, normalizedStylesToRemove);
			}
		}
		if (Object.keys(normalizedStylesToRemove).length) {
			removeStyles(this._styles, normalizedStylesToRemove);
			this._cachedStyleNames = null;
			this._cachedExpandedStyleNames = null;
		}
	}
	/**
	* Returns a normalized style object or a single value.
	*
	* ```ts
	* // Enable 'margin' shorthand processing:
	* editor.data.addStyleProcessorRules( addMarginStylesRules );
	*
	* const styles = new Styles();
	* styles.setTo( 'margin:1px 2px 3em;' );
	*
	* styles.getNormalized( 'margin' );
	* // will log:
	* // {
	* //     top: '1px',
	* //     right: '2px',
	* //     bottom: '3em',
	* //     left: '2px'     // normalized value from margin shorthand
	* // }
	*
	* styles.getNormalized( 'margin-left' ); // -> '2px'
	* ```
	*
	* **Note**: This method will only return normalized styles if a style processor was defined.
	*
	* @param name Style name.
	*/
	getNormalized(name) {
		return this._styleProcessor.getNormalized(name, this._styles);
	}
	/**
	* Returns a normalized style string. Styles are sorted by name.
	*
	* ```ts
	* styles.set( 'margin' , '1px' );
	* styles.set( 'background', '#f00' );
	*
	* styles.toString(); // -> 'background:#f00;margin:1px;'
	* ```
	*
	* **Note**: This method supports normalized styles if defined.
	*
	* ```ts
	* // Enable 'margin' shorthand processing:
	* editor.data.addStyleProcessorRules( addMarginStylesRules );
	*
	* styles.set( 'margin' , '1px' );
	* styles.set( 'background', '#f00' );
	* styles.remove( 'margin-top' );
	* styles.remove( 'margin-right' );
	*
	* styles.toString(); // -> 'background:#f00;margin-bottom:1px;margin-left:1px;'
	* ```
	*/
	toString() {
		if (this.isEmpty) return "";
		return this.getStylesEntries().map((arr) => arr.join(":")).sort().join(";") + ";";
	}
	/**
	* Returns property as a value string or undefined if property is not set.
	*
	* ```ts
	* // Enable 'margin' shorthand processing:
	* editor.data.addStyleProcessorRules( addMarginStylesRules );
	*
	* const styles = new Styles();
	* styles.setTo( 'margin:1px;' );
	* styles.set( 'margin-bottom', '3em' );
	*
	* styles.getAsString( 'margin' ); // -> 'margin: 1px 1px 3em;'
	* ```
	*
	* Note, however, that all sub-values must be set for the longhand property name to return a value:
	*
	* ```ts
	* const styles = new Styles();
	* styles.setTo( 'margin:1px;' );
	* styles.remove( 'margin-bottom' );
	*
	* styles.getAsString( 'margin' ); // -> undefined
	* ```
	*
	* In the above scenario, it is not possible to return a `margin` value, so `undefined` is returned.
	* Instead, you should use:
	*
	* ```ts
	* const styles = new Styles();
	* styles.setTo( 'margin:1px;' );
	* styles.remove( 'margin-bottom' );
	*
	* for ( const styleName of styles.getStyleNames() ) {
	* 	console.log( styleName, styles.getAsString( styleName ) );
	* }
	* // 'margin-top', '1px'
	* // 'margin-right', '1px'
	* // 'margin-left', '1px'
	* ```
	*
	* In general, it is recommend to iterate over style names like in the example above. This way, you will always get all
	* the currently set style values. So, if all the 4 margin values would be set
	* the for-of loop above would yield only `'margin'`, `'1px'`:
	*
	* ```ts
	* const styles = new Styles();
	* styles.setTo( 'margin:1px;' );
	*
	* for ( const styleName of styles.getStyleNames() ) {
	* 	console.log( styleName, styles.getAsString( styleName ) );
	* }
	* // 'margin', '1px'
	* ```
	*
	* **Note**: To get a normalized version of a longhand property use the {@link #getNormalized `#getNormalized()`} method.
	*/
	getAsString(propertyName) {
		if (this.isEmpty) return;
		if (this._styles[propertyName] && !isObject(this._styles[propertyName])) return this._styles[propertyName];
		const propertyDescriptor = this._styleProcessor.getReducedForm(propertyName, this._styles).find(([property]) => property === propertyName);
		if (Array.isArray(propertyDescriptor)) return propertyDescriptor[1];
	}
	/**
	* Returns all style properties names as they would appear when using {@link #toString `#toString()`}.
	*
	* When `expand` is set to true and there's a shorthand style property set, it will also return all equivalent styles:
	*
	* ```ts
	* stylesMap.setTo( 'margin: 1em' )
	* ```
	*
	* will be expanded to:
	*
	* ```ts
	* [ 'margin', 'margin-top', 'margin-right', 'margin-bottom', 'margin-left' ]
	* ```
	*
	* @param expand Expand shorthand style properties and all return equivalent style representations.
	*/
	getStyleNames(expand = false) {
		if (this.isEmpty) return [];
		if (expand) {
			this._cachedExpandedStyleNames ||= this._styleProcessor.getStyleNames(this._styles);
			return this._cachedExpandedStyleNames;
		}
		this._cachedStyleNames ||= this.getStylesEntries().map(([key]) => key);
		return this._cachedStyleNames;
	}
	/**
	* Alias for {@link #getStyleNames}.
	*/
	keys() {
		return this.getStyleNames();
	}
	/**
	* Removes all styles.
	*/
	clear() {
		this._styles = {};
		this._cachedStyleNames = null;
		this._cachedExpandedStyleNames = null;
	}
	/**
	* Returns `true` if both attributes have the same styles.
	*/
	isSimilar(other) {
		if (this.size !== other.size) return false;
		for (const property of this.getStyleNames()) if (!other.has(property) || other.getAsString(property) !== this.getAsString(property)) return false;
		return true;
	}
	/**
	* Returns normalized styles entries for further processing.
	*/
	getStylesEntries() {
		const parsed = [];
		const keys = Object.keys(this._styles);
		for (const key of keys) parsed.push(...this._styleProcessor.getReducedForm(key, this._styles));
		return parsed;
	}
	/**
	* Clones the attribute value.
	*
	* @internal
	*/
	_clone() {
		const clone = new this.constructor(this._styleProcessor);
		clone.set(this.getNormalized());
		return clone;
	}
	/**
	* Used by the {@link module:engine/view/matcher~Matcher Matcher} to collect matching styles.
	*
	* @internal
	* @param tokenPattern The matched style name pattern.
	* @param valuePattern The matched style value pattern.
	* @returns An array of matching tokens (style names).
	*/
	_getTokensMatch(tokenPattern, valuePattern) {
		const match = [];
		for (const styleName of this.getStyleNames(true)) if (isPatternMatched(tokenPattern, styleName)) {
			if (valuePattern === true) {
				match.push(styleName);
				continue;
			}
			if (isPatternMatched(valuePattern, this.getAsString(styleName))) match.push(styleName);
		}
		return match.length ? match : void 0;
	}
	/**
	* Returns a list of consumables for the attribute. This includes related styles.
	*
	* Could be filtered by the given style name.
	*
	* @internal
	*/
	_getConsumables(name) {
		const result = [];
		if (name) {
			result.push(name);
			for (const relatedName of this._styleProcessor.getRelatedStyles(name)) result.push(relatedName);
		} else for (const name of this.getStyleNames()) {
			for (const relatedName of this._styleProcessor.getRelatedStyles(name)) result.push(relatedName);
			result.push(name);
		}
		return result;
	}
	/**
	* Used by {@link module:engine/view/element~ViewElement#_canMergeAttributesFrom} to verify if the given attribute can be merged without
	* conflicts into the attribute.
	*
	* This method is indirectly used by the {@link module:engine/view/downcastwriter~ViewDowncastWriter} while down-casting
	* an {@link module:engine/view/attributeelement~ViewAttributeElement} to merge it with other ViewAttributeElement.
	*
	* @internal
	*/
	_canMergeFrom(other) {
		for (const key of other.getStyleNames()) if (this.has(key) && this.getAsString(key) !== other.getAsString(key)) return false;
		return true;
	}
	/**
	* Used by {@link module:engine/view/element~ViewElement#_mergeAttributesFrom} to merge a given attribute into the attribute.
	*
	* This method is indirectly used by the {@link module:engine/view/downcastwriter~ViewDowncastWriter} while down-casting
	* an {@link module:engine/view/attributeelement~ViewAttributeElement} to merge it with other ViewAttributeElement.
	*
	* @internal
	*/
	_mergeFrom(other) {
		for (const prop of other.getStyleNames()) if (!this.has(prop)) this.set(prop, other.getAsString(prop));
	}
	/**
	* Used by {@link module:engine/view/element~ViewElement#_canSubtractAttributesOf} to verify if the given attribute can be fully
	* subtracted from the attribute.
	*
	* This method is indirectly used by the {@link module:engine/view/downcastwriter~ViewDowncastWriter} while down-casting
	* an {@link module:engine/view/attributeelement~ViewAttributeElement} to unwrap the ViewAttributeElement.
	*
	* @internal
	*/
	_isMatching(other) {
		for (const key of other.getStyleNames()) if (!this.has(key) || this.getAsString(key) !== other.getAsString(key)) return false;
		return true;
	}
};
/**
* Style processor is responsible for writing and reading a normalized styles object.
*/
var StylesProcessor = class {
	_normalizers;
	_extractors;
	_reducers;
	_consumables;
	/**
	* Creates StylesProcessor instance.
	*
	* @internal
	*/
	constructor() {
		this._normalizers = /* @__PURE__ */ new Map();
		this._extractors = /* @__PURE__ */ new Map();
		this._reducers = /* @__PURE__ */ new Map();
		this._consumables = /* @__PURE__ */ new Map();
	}
	/**
	* Parse style string value to a normalized object and appends it to styles object.
	*
	* ```ts
	* const styles = {};
	*
	* stylesProcessor.toNormalizedForm( 'margin', '1px', styles );
	*
	* // styles will consist: { margin: { top: '1px', right: '1px', bottom: '1px', left: '1px; } }
	* ```
	*
	* **Note**: To define normalizer callbacks use {@link #setNormalizer}.
	*
	* @param name Name of style property.
	* @param propertyValue Value of style property.
	* @param styles Object holding normalized styles.
	*/
	toNormalizedForm(name, propertyValue, styles) {
		if (isObject(propertyValue)) {
			appendStyleValue(styles, toPath(name), propertyValue);
			return;
		}
		if (this._normalizers.has(name)) {
			const { path, value } = this._normalizers.get(name)(propertyValue);
			appendStyleValue(styles, path, value);
		} else appendStyleValue(styles, name, propertyValue);
	}
	/**
	* Returns a normalized version of a style property.
	*
	* ```ts
	* const styles = {
	* 	margin: { top: '1px', right: '1px', bottom: '1px', left: '1px; },
	* 	background: { color: '#f00' }
	* };
	*
	* stylesProcessor.getNormalized( 'background' );
	* // will return: { color: '#f00' }
	*
	* stylesProcessor.getNormalized( 'margin-top' );
	* // will return: '1px'
	* ```
	*
	* **Note**: In some cases extracting single value requires defining an extractor callback {@link #setExtractor}.
	*
	* @param name Name of style property.
	* @param styles Object holding normalized styles.
	*/
	getNormalized(name, styles) {
		if (!name) return merge({}, styles);
		if (styles[name] !== void 0) return styles[name];
		if (this._extractors.has(name)) {
			const extractor = this._extractors.get(name);
			if (typeof extractor === "string") return get(styles, extractor);
			const value = extractor(name, styles);
			if (value) return value;
		}
		return get(styles, toPath(name));
	}
	/**
	* Returns a reduced form of style property form normalized object.
	*
	* For default margin reducer, the below code:
	*
	* ```ts
	* stylesProcessor.getReducedForm( 'margin', {
	* 	margin: { top: '1px', right: '1px', bottom: '2px', left: '1px; }
	* } );
	* ```
	*
	* will return:
	*
	* ```ts
	* [
	* 	[ 'margin', '1px 1px 2px' ]
	* ]
	* ```
	*
	* because it might be represented as a shorthand 'margin' value. However if one of margin long hand values is missing it should return:
	*
	* ```ts
	* [
	* 	[ 'margin-top', '1px' ],
	* 	[ 'margin-right', '1px' ],
	* 	[ 'margin-bottom', '2px' ]
	* 	// the 'left' value is missing - cannot use 'margin' shorthand.
	* ]
	* ```
	*
	* **Note**: To define reducer callbacks use {@link #setReducer}.
	*
	* @param name Name of style property.
	*/
	getReducedForm(name, styles) {
		const normalizedValue = this.getNormalized(name, styles);
		if (normalizedValue === void 0) return [];
		if (this._reducers.has(name)) return this._reducers.get(name)(normalizedValue);
		return [[name, normalizedValue]];
	}
	/**
	* Return all style properties. Also expand shorthand properties (e.g. `margin`, `background`) if respective extractor is available.
	*
	* @param styles Object holding normalized styles.
	*/
	getStyleNames(styles) {
		const styleNamesKeysSet = /* @__PURE__ */ new Set();
		for (const name of this._consumables.keys()) {
			const style = this.getNormalized(name, styles);
			if (style && (typeof style != "object" || Object.keys(style).length)) styleNamesKeysSet.add(name);
		}
		for (const name of Object.keys(styles)) styleNamesKeysSet.add(name);
		return Array.from(styleNamesKeysSet);
	}
	/**
	* Returns related style names.
	*
	* ```ts
	* stylesProcessor.getRelatedStyles( 'margin' );
	* // will return: [ 'margin-top', 'margin-right', 'margin-bottom', 'margin-left' ];
	*
	* stylesProcessor.getRelatedStyles( 'margin-top' );
	* // will return: [ 'margin' ];
	* ```
	*
	* **Note**: To define new style relations load an existing style processor or use
	* {@link module:engine/view/stylesmap~StylesProcessor#setStyleRelation `StylesProcessor.setStyleRelation()`}.
	*/
	getRelatedStyles(name) {
		return this._consumables.get(name) || [];
	}
	/**
	* Adds a normalizer method for a style property.
	*
	* A normalizer returns describing how the value should be normalized.
	*
	* For instance 'margin' style is a shorthand for four margin values:
	*
	* - 'margin-top'
	* - 'margin-right'
	* - 'margin-bottom'
	* - 'margin-left'
	*
	* and can be written in various ways if some values are equal to others. For instance `'margin: 1px 2em;'` is a shorthand for
	* `'margin-top: 1px;margin-right: 2em;margin-bottom: 1px;margin-left: 2em'`.
	*
	* A normalizer should parse various margin notations as a single object:
	*
	* ```ts
	* const styles = {
	* 	margin: {
	* 		top: '1px',
	* 		right: '2em',
	* 		bottom: '1px',
	* 		left: '2em'
	* 	}
	* };
	* ```
	*
	* Thus a normalizer for 'margin' style should return an object defining style path and value to store:
	*
	* ```ts
	* const returnValue = {
	* 	path: 'margin',
	* 	value: {
	* 		top: '1px',
	* 		right: '2em',
	* 		bottom: '1px',
	* 		left: '2em'
	* 	}
	* };
	* ```
	*
	* Additionally to fully support all margin notations there should be also defined 4 normalizers for longhand margin notations. Below
	* is an example for 'margin-top' style property normalizer:
	*
	* ```ts
	* stylesProcessor.setNormalizer( 'margin-top', valueString => {
	* 	return {
	* 		path: 'margin.top',
	* 		value: valueString
	* 	}
	* } );
	* ```
	*/
	setNormalizer(name, callback) {
		this._normalizers.set(name, callback);
	}
	/**
	* Adds a extractor callback for a style property.
	*
	* Most normalized style values are stored as one level objects. It is assumed that `'margin-top'` style will be stored as:
	*
	* ```ts
	* const styles = {
	* 	margin: {
	* 		top: 'value'
	* 	}
	* }
	* ```
	*
	* However, some styles can have conflicting notations and thus it might be harder to extract a style value from shorthand. For instance
	* the 'border-top-style' can be defined using `'border-top:solid'`, `'border-style:solid none none none'` or by `'border:solid'`
	* shorthands. The default border styles processors stores styles as:
	*
	* ```ts
	* const styles = {
	* 	border: {
	* 		style: {
	* 			top: 'solid'
	* 		}
	* 	}
	* }
	* ```
	*
	* as it is better to modify border style independently from other values. On the other part the output of the border might be
	* desired as `border-top`, `border-left`, etc notation.
	*
	* In the above example an extractor should return a side border value that combines style, color and width:
	*
	* ```ts
	* styleProcessor.setExtractor( 'border-top', styles => {
	* 	return {
	* 		color: styles.border.color.top,
	* 		style: styles.border.style.top,
	* 		width: styles.border.width.top
	* 	}
	* } );
	* ```
	*
	* @param callbackOrPath Callback that return a requested value or path string for single values.
	*/
	setExtractor(name, callbackOrPath) {
		this._extractors.set(name, callbackOrPath);
	}
	/**
	* Adds a reducer callback for a style property.
	*
	* Reducer returns a minimal notation for given style name. For longhand properties it is not required to write a reducer as
	* by default the direct value from style path is taken.
	*
	* For shorthand styles a reducer should return minimal style notation either by returning single name-value tuple or multiple tuples
	* if a shorthand cannot be used. For instance for a margin shorthand a reducer might return:
	*
	* ```ts
	* const marginShortHandTuple = [
	* 	[ 'margin', '1px 1px 2px' ]
	* ];
	* ```
	*
	* or a longhand tuples for defined values:
	*
	* ```ts
	* // Considering margin.bottom and margin.left are undefined.
	* const marginLonghandsTuples = [
	* 	[ 'margin-top', '1px' ],
	* 	[ 'margin-right', '1px' ]
	* ];
	* ```
	*
	* A reducer obtains a normalized style value:
	*
	* ```ts
	* // Simplified reducer that always outputs 4 values which are always present:
	* stylesProcessor.setReducer( 'margin', margin => {
	* 	return [
	* 		[ 'margin', `${ margin.top } ${ margin.right } ${ margin.bottom } ${ margin.left }` ]
	* 	]
	* } );
	* ```
	*/
	setReducer(name, callback) {
		this._reducers.set(name, callback);
	}
	/**
	* Defines a style shorthand relation to other style notations.
	*
	* ```ts
	* stylesProcessor.setStyleRelation( 'margin', [
	* 	'margin-top',
	* 	'margin-right',
	* 	'margin-bottom',
	* 	'margin-left'
	* ] );
	* ```
	*
	* This enables expanding of style names for shorthands. For instance, if defined,
	* {@link module:engine/conversion/viewconsumable~ViewConsumable view consumable} items are automatically created
	* for long-hand margin style notation alongside the `'margin'` item.
	*
	* This means that when an element being converted has a style `margin`, a converter for `margin-left` will work just
	* fine since the view consumable will contain a consumable `margin-left` item (thanks to the relation) and
	* `element.getStyle( 'margin-left' )` will work as well assuming that the style processor was correctly configured.
	* However, once `margin-left` is consumed, `margin` will not be consumable anymore.
	*/
	setStyleRelation(shorthandName, styleNames) {
		this._mapStyleNames(shorthandName, styleNames);
		for (const alsoName of styleNames) this._mapStyleNames(alsoName, [shorthandName]);
	}
	/**
	* Set two-way binding of style names.
	*/
	_mapStyleNames(name, styleNames) {
		if (!this._consumables.has(name)) this._consumables.set(name, []);
		this._consumables.get(name).push(...styleNames);
	}
};
/**
* Parses inline styles and puts property - value pairs into styles map.
*
* @param stylesString Styles to parse.
* @returns Map of parsed properties and values.
*/
function parseInlineStyles(stylesString) {
	let quoteType = null;
	let propertyNameStart = 0;
	let propertyValueStart = 0;
	let propertyName = null;
	const stylesMap = /* @__PURE__ */ new Map();
	if (stylesString === "") return stylesMap;
	if (stylesString.charAt(stylesString.length - 1) != ";") stylesString = stylesString + ";";
	for (let i = 0; i < stylesString.length; i++) {
		const char = stylesString.charAt(i);
		if (quoteType === null) switch (char) {
			case ":":
				if (!propertyName) {
					propertyName = stylesString.substr(propertyNameStart, i - propertyNameStart);
					propertyValueStart = i + 1;
				}
				break;
			case "\"":
			case "'":
				quoteType = char;
				break;
			case ";": {
				const propertyValue = stylesString.substr(propertyValueStart, i - propertyValueStart);
				if (propertyName) stylesMap.set(propertyName.trim(), propertyValue.trim());
				propertyName = null;
				propertyNameStart = i + 1;
				break;
			}
		}
		else if (char === quoteType) quoteType = null;
	}
	return stylesMap;
}
/**
* Return lodash compatible path from style name.
*/
function toPath(name) {
	return name.replace("-", ".");
}
/**
* Appends style definition to the styles object.
*/
function appendStyleValue(stylesObject, nameOrPath, valueOrObject) {
	let valueToSet = valueOrObject;
	if (isObject(valueOrObject) && !Array.isArray(valueOrObject)) valueToSet = merge({}, get(stylesObject, nameOrPath), valueOrObject);
	set(stylesObject, nameOrPath, valueToSet);
}
/**
* Modifies the `styles` deeply nested object by removing properties defined in `toRemove`.
*/
function removeStyles(styles, toRemove) {
	for (const key of Object.keys(toRemove)) if (styles[key] !== null && !Array.isArray(styles[key]) && typeof styles[key] == "object" && typeof toRemove[key] == "object") {
		removeStyles(styles[key], toRemove[key]);
		if (!Object.keys(styles[key]).length) delete styles[key];
	} else delete styles[key];
}

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* @module engine/view/tokenlist
*/
/**
* Token list. Allows handling (adding, removing, retrieving) a set of tokens (for example class names).
*/
var ViewTokenList = class {
	/**
	* The set of tokens.
	*/
	_set = /* @__PURE__ */ new Set();
	/**
	* Returns true if token list has no tokens set.
	*/
	get isEmpty() {
		return this._set.size == 0;
	}
	/**
	* Number of tokens.
	*/
	get size() {
		return this._set.size;
	}
	/**
	* Checks if a given token is set.
	*/
	has(name) {
		return this._set.has(name);
	}
	/**
	* Returns all tokens.
	*/
	keys() {
		return Array.from(this._set.keys());
	}
	/**
	* Resets the value to the given one.
	*/
	setTo(value) {
		this.clear();
		for (const token of value.split(/\s+/)) if (token) this._set.add(token);
		return this;
	}
	/**
	* Sets a given token without affecting other tokens.
	*/
	set(tokens) {
		for (const token of toArray(tokens)) if (token) this._set.add(token);
	}
	/**
	* Removes given token.
	*/
	remove(tokens) {
		for (const token of toArray(tokens)) this._set.delete(token);
	}
	/**
	* Removes all tokens.
	*/
	clear() {
		this._set.clear();
	}
	/**
	* Returns a normalized tokens string.
	*/
	toString() {
		return Array.from(this._set).join(" ");
	}
	/**
	* Returns `true` if both attributes have the same tokens.
	*/
	isSimilar(other) {
		if (this.size !== other.size) return false;
		for (const token of this.keys()) if (!other.has(token)) return false;
		return true;
	}
	/**
	* Clones the attribute value.
	*
	* @internal
	*/
	_clone() {
		const clone = new this.constructor();
		clone._set = new Set(this._set);
		return clone;
	}
	/**
	* Used by the {@link module:engine/view/matcher~Matcher Matcher} to collect matching attribute tokens.
	*
	* @internal
	* @param tokenPattern The matched token name pattern.
	* @returns An array of matching tokens.
	*/
	_getTokensMatch(tokenPattern) {
		const match = [];
		if (tokenPattern === true) {
			for (const token of this._set.keys()) match.push(token);
			return match;
		}
		if (typeof tokenPattern == "string") {
			for (const token of tokenPattern.split(/\s+/)) if (this._set.has(token)) match.push(token);
			else return;
			return match;
		}
		for (const token of this._set.keys()) if (token.match(tokenPattern)) match.push(token);
		return match.length ? match : void 0;
	}
	/**
	* Returns a list of consumables for the attribute.
	*
	* Could be filtered by the given token name.
	*
	* @internal
	*/
	_getConsumables(name) {
		return name ? [name] : this.keys();
	}
	/**
	* Used by {@link module:engine/view/element~ViewElement#_canMergeAttributesFrom} to verify if the given attribute
	* can be merged without conflicts into the attribute.
	*
	* This method is indirectly used by the {@link module:engine/view/downcastwriter~ViewDowncastWriter} while downcasting
	* an {@link module:engine/view/attributeelement~ViewAttributeElement} to merge it with other `AttributeElement`.
	*
	* @internal
	*/
	_canMergeFrom() {
		return true;
	}
	/**
	* Used by {@link module:engine/view/element~ViewElement#_mergeAttributesFrom} to merge a given attribute into the attribute.
	*
	* This method is indirectly used by the {@link module:engine/view/downcastwriter~ViewDowncastWriter} while down-casting
	* an {@link module:engine/view/attributeelement~ViewAttributeElement} to merge it with other ViewAttributeElement.
	*
	* @internal
	*/
	_mergeFrom(other) {
		for (const token of other._set.keys()) if (!this._set.has(token)) this._set.add(token);
	}
	/**
	* Used by {@link module:engine/view/element~ViewElement#_canSubtractAttributesOf} to verify if the given attribute
	* can be fully subtracted from the attribute.
	*
	* This method is indirectly used by the {@link module:engine/view/downcastwriter~ViewDowncastWriter} while down-casting
	* an {@link module:engine/view/attributeelement~ViewAttributeElement} to unwrap the ViewAttributeElement.
	*
	* @internal
	*/
	_isMatching(other) {
		for (const name of other._set.keys()) if (!this._set.has(name)) return false;
		return true;
	}
};

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* @module engine/view/element
*/
/**
* View element.
*
* The editing engine does not define a fixed semantics of its elements (it is "DTD-free").
* This is why the type of the {@link module:engine/view/element~ViewElement} need to
* be defined by the feature developer. When creating an element you should use one of the following methods:
*
* * {@link module:engine/view/downcastwriter~ViewDowncastWriter#createContainerElement `downcastWriter#createContainerElement()`}
* in order to create a {@link module:engine/view/containerelement~ViewContainerElement},
* * {@link module:engine/view/downcastwriter~ViewDowncastWriter#createAttributeElement `downcastWriter#createAttributeElement()`}
* in order to create a {@link module:engine/view/attributeelement~ViewAttributeElement},
* * {@link module:engine/view/downcastwriter~ViewDowncastWriter#createEmptyElement `downcastWriter#createEmptyElement()`}
* in order to create a {@link module:engine/view/emptyelement~ViewEmptyElement}.
* * {@link module:engine/view/downcastwriter~ViewDowncastWriter#createUIElement `downcastWriter#createUIElement()`}
* in order to create a {@link module:engine/view/uielement~ViewUIElement}.
* * {@link module:engine/view/downcastwriter~ViewDowncastWriter#createEditableElement `downcastWriter#createEditableElement()`}
* in order to create a {@link module:engine/view/editableelement~ViewEditableElement}.
*
* Note that for view elements which are not created from the model, like elements from mutations, paste or
* {@link module:engine/controller/datacontroller~DataController#set data.set} it is not possible to define the type of the element.
* In such cases the {@link module:engine/view/upcastwriter~ViewUpcastWriter#createElement `UpcastWriter#createElement()`} method
* should be used to create generic view elements.
*/
var ViewElement = class ViewElement extends ViewNode {
	/**
	* Name of the element.
	*/
	name;
	/**
	* A list of attribute names that should be rendered in the editing pipeline even though filtering mechanisms
	* implemented in the {@link module:engine/view/domconverter~ViewDomConverter} (for instance,
	* {@link module:engine/view/domconverter~ViewDomConverter#shouldRenderAttribute}) would filter them out.
	*
	* These attributes can be specified as an option when the element is created by
	* the {@link module:engine/view/downcastwriter~ViewDowncastWriter}. To check whether an unsafe an attribute should
	* be permitted, use the {@link #shouldRenderUnsafeAttribute} method.
	*
	* @internal
	*/
	_unsafeAttributesToRender = [];
	/**
	* Map of attributes, where attributes names are keys and attributes values are values.
	*/
	_attrs;
	/**
	* Array of child nodes.
	*/
	_children;
	/**
	* Map of custom properties.
	* Custom properties can be added to element instance, will be cloned but not rendered into DOM.
	*/
	_customProperties = /* @__PURE__ */ new Map();
	/**
	* Set of classes associated with element instance.
	*
	* Note that this is just an alias for `this._attrs.get( 'class' );`
	*/
	get _classes() {
		return this._attrs.get("class");
	}
	/**
	* Normalized styles.
	*
	* Note that this is just an alias for `this._attrs.get( 'style' );`
	*/
	get _styles() {
		return this._attrs.get("style");
	}
	/**
	* Creates a view element.
	*
	* Attributes can be passed in various formats:
	*
	* ```ts
	* new Element( viewDocument, 'div', { class: 'editor', contentEditable: 'true' } ); // object
	* new Element( viewDocument, 'div', [ [ 'class', 'editor' ], [ 'contentEditable', 'true' ] ] ); // map-like iterator
	* new Element( viewDocument, 'div', mapOfAttributes ); // map
	* ```
	*
	* @internal
	* @param document The document instance to which this element belongs.
	* @param name Node name.
	* @param attrs Collection of attributes.
	* @param children A list of nodes to be inserted into created element.
	*/
	constructor(document, name, attrs, children) {
		super(document);
		this.name = name;
		this._attrs = this._parseAttributes(attrs);
		this._children = [];
		if (children) this._insertChild(0, children);
	}
	/**
	* Number of element's children.
	*/
	get childCount() {
		return this._children.length;
	}
	/**
	* Is `true` if there are no nodes inside this element, `false` otherwise.
	*/
	get isEmpty() {
		return this._children.length === 0;
	}
	/**
	* Gets child at the given index.
	*
	* @param index Index of child.
	* @returns Child node.
	*/
	getChild(index) {
		return this._children[index];
	}
	/**
	* Gets index of the given child node. Returns `-1` if child node is not found.
	*
	* @param node Child node.
	* @returns Index of the child node.
	*/
	getChildIndex(node) {
		return this._children.indexOf(node);
	}
	/**
	* Gets child nodes iterator.
	*
	* @returns Child nodes iterator.
	*/
	getChildren() {
		return this._children[Symbol.iterator]();
	}
	/**
	* Returns an iterator that contains the keys for attributes. Order of inserting attributes is not preserved.
	*
	* @returns Keys for attributes.
	*/
	*getAttributeKeys() {
		if (this._classes) yield "class";
		if (this._styles) yield "style";
		for (const key of this._attrs.keys()) if (key != "class" && key != "style") yield key;
	}
	/**
	* Returns iterator that iterates over this element's attributes.
	*
	* Attributes are returned as arrays containing two items. First one is attribute key and second is attribute value.
	* This format is accepted by native `Map` object and also can be passed in `Node` constructor.
	*/
	*getAttributes() {
		for (const [name, value] of this._attrs.entries()) yield [name, String(value)];
	}
	/**
	* Gets attribute by key. If attribute is not present - returns undefined.
	*
	* @param key Attribute key.
	* @returns Attribute value.
	*/
	getAttribute(key) {
		return this._attrs.has(key) ? String(this._attrs.get(key)) : void 0;
	}
	/**
	* Returns a boolean indicating whether an attribute with the specified key exists in the element.
	*
	* @param key Attribute key.
	* @returns `true` if attribute with the specified key exists in the element, `false` otherwise.
	*/
	hasAttribute(key, token) {
		if (!this._attrs.has(key)) return false;
		if (token !== void 0) if (usesStylesMap(this.name, key) || usesTokenList(this.name, key)) return this._attrs.get(key).has(token);
		else return this._attrs.get(key) === token;
		return true;
	}
	/**
	* Checks if this element is similar to other element.
	* Both elements should have the same name and attributes to be considered as similar. Two similar elements
	* can contain different set of children nodes.
	*/
	isSimilar(otherElement) {
		if (!(otherElement instanceof ViewElement)) return false;
		if (this === otherElement) return true;
		if (this.name != otherElement.name) return false;
		if (this._attrs.size !== otherElement._attrs.size) return false;
		for (const [key, value] of this._attrs) {
			const otherValue = otherElement._attrs.get(key);
			if (otherValue === void 0) return false;
			if (typeof value == "string" || typeof otherValue == "string") {
				if (otherValue !== value) return false;
			} else if (!value.isSimilar(otherValue)) return false;
		}
		return true;
	}
	/**
	* Returns true if class is present.
	* If more then one class is provided - returns true only when all classes are present.
	*
	* ```ts
	* element.hasClass( 'foo' ); // Returns true if 'foo' class is present.
	* element.hasClass( 'foo', 'bar' ); // Returns true if 'foo' and 'bar' classes are both present.
	* ```
	*/
	hasClass(...className) {
		for (const name of className) if (!this._classes || !this._classes.has(name)) return false;
		return true;
	}
	/**
	* Returns iterator that contains all class names.
	*/
	getClassNames() {
		const array = this._classes ? this._classes.keys() : [];
		const iterator = array[Symbol.iterator]();
		return Object.assign(array, { next: iterator.next.bind(iterator) });
	}
	/**
	* Returns style value for the given property name.
	* If the style does not exist `undefined` is returned.
	*
	* **Note**: This method can work with normalized style names if
	* {@link module:engine/controller/datacontroller~DataController#addStyleProcessorRules a particular style processor rule is enabled}.
	* See {@link module:engine/view/stylesmap~StylesMap#getAsString `StylesMap#getAsString()`} for details.
	*
	* For an element with style set to `'margin:1px'`:
	*
	* ```ts
	* // Enable 'margin' shorthand processing:
	* editor.data.addStyleProcessorRules( addMarginStylesRules );
	*
	* const element = view.change( writer => {
	* 	const element = writer.createElement();
	* 	writer.setStyle( 'margin', '1px' );
	* 	writer.setStyle( 'margin-bottom', '3em' );
	*
	* 	return element;
	* } );
	*
	* element.getStyle( 'margin' ); // -> 'margin: 1px 1px 3em;'
	* ```
	*/
	getStyle(property) {
		return this._styles && this._styles.getAsString(property);
	}
	/**
	* Returns a normalized style object or single style value.
	*
	* For an element with style set to: margin:1px 2px 3em;
	*
	* ```ts
	* element.getNormalizedStyle( 'margin' ) );
	* ```
	*
	* will return:
	*
	* ```ts
	* {
	* 	top: '1px',
	* 	right: '2px',
	* 	bottom: '3em',
	* 	left: '2px'    // a normalized value from margin shorthand
	* }
	* ```
	*
	* and reading for single style value:
	*
	* ```ts
	* styles.getNormalizedStyle( 'margin-left' );
	* ```
	*
	* Will return a `2px` string.
	*
	* **Note**: This method will return normalized values only if
	* {@link module:engine/controller/datacontroller~DataController#addStyleProcessorRules a particular style processor rule is enabled}.
	* See {@link module:engine/view/stylesmap~StylesMap#getNormalized `StylesMap#getNormalized()`} for details.
	*
	* @param property Name of CSS property
	*/
	getNormalizedStyle(property) {
		return this._styles && this._styles.getNormalized(property);
	}
	/**
	* Returns an array that contains all style names.
	*
	* @param expand Expand shorthand style properties and return all equivalent style representations.
	*/
	getStyleNames(expand) {
		return this._styles ? this._styles.getStyleNames(expand) : [];
	}
	/**
	* Returns true if style keys are present.
	* If more then one style property is provided - returns true only when all properties are present.
	*
	* ```ts
	* element.hasStyle( 'color' ); // Returns true if 'border-top' style is present.
	* element.hasStyle( 'color', 'border-top' ); // Returns true if 'color' and 'border-top' styles are both present.
	* ```
	*/
	hasStyle(...property) {
		for (const name of property) if (!this._styles || !this._styles.has(name)) return false;
		return true;
	}
	/**
	* Returns ancestor element that match specified pattern.
	* Provided patterns should be compatible with {@link module:engine/view/matcher~Matcher Matcher} as it is used internally.
	*
	* @see module:engine/view/matcher~Matcher
	* @param patterns Patterns used to match correct ancestor. See {@link module:engine/view/matcher~Matcher}.
	* @returns Found element or `null` if no matching ancestor was found.
	*/
	findAncestor(...patterns) {
		const matcher = new Matcher(...patterns);
		let parent = this.parent;
		while (parent && !parent.is("documentFragment")) {
			if (matcher.match(parent)) return parent;
			parent = parent.parent;
		}
		return null;
	}
	/**
	* Returns the custom property value for the given key.
	*/
	getCustomProperty(key) {
		return this._customProperties.get(key);
	}
	/**
	* Returns an iterator which iterates over this element's custom properties.
	* Iterator provides `[ key, value ]` pairs for each stored property.
	*/
	*getCustomProperties() {
		yield* this._customProperties.entries();
	}
	/**
	* Returns identity string based on element's name, styles, classes and other attributes.
	* Two elements that {@link #isSimilar are similar} will have same identity string.
	* It has the following format:
	*
	* ```ts
	* 'name class="class1,class2" style="style1:value1;style2:value2" attr1="val1" attr2="val2"'
	* ```
	*
	* For example:
	*
	* ```ts
	* const element = writer.createContainerElement( 'foo', {
	* 	banana: '10',
	* 	apple: '20',
	* 	style: 'color: red; border-color: white;',
	* 	class: 'baz'
	* } );
	*
	* // returns 'foo class="baz" style="border-color:white;color:red" apple="20" banana="10"'
	* element.getIdentity();
	* ```
	*
	* **Note**: Classes, styles and other attributes are sorted alphabetically.
	*/
	getIdentity() {
		const classes = this._classes ? this._classes.keys().sort().join(",") : "";
		const styles = this._styles && String(this._styles);
		const attributes = Array.from(this._attrs).filter(([key]) => key != "style" && key != "class").map((i) => `${i[0]}="${i[1]}"`).sort().join(" ");
		return this.name + (classes == "" ? "" : ` class="${classes}"`) + (!styles ? "" : ` style="${styles}"`) + (attributes == "" ? "" : ` ${attributes}`);
	}
	/**
	* Decides whether an unsafe attribute is whitelisted and should be rendered in the editing pipeline even though filtering mechanisms
	* like {@link module:engine/view/domconverter~ViewDomConverter#shouldRenderAttribute} say it should not.
	*
	* Unsafe attribute names can be specified when creating an element via {@link module:engine/view/downcastwriter~ViewDowncastWriter}.
	*
	* @param attributeName The name of the attribute to be checked.
	*/
	shouldRenderUnsafeAttribute(attributeName) {
		return this._unsafeAttributesToRender.includes(attributeName);
	}
	/**
	* Converts `ViewElement` to plain object and returns it.
	*
	* @returns `ViewElement` converted to plain object.
	*/
	toJSON() {
		const json = super.toJSON();
		json.name = this.name;
		json.type = "Element";
		if (this._attrs.size) json.attributes = Object.fromEntries(this.getAttributes());
		if (this._children.length > 0) {
			json.children = [];
			for (const node of this._children) json.children.push(node.toJSON());
		}
		return json;
	}
	/**
	* Clones provided element.
	*
	* @internal
	* @param deep If set to `true` clones element and all its children recursively. When set to `false`,
	* element will be cloned without any children.
	* @returns Clone of this element.
	*/
	_clone(deep = false) {
		const childrenClone = [];
		if (deep) for (const child of this.getChildren()) childrenClone.push(child._clone(deep));
		const cloned = new this.constructor(this.document, this.name, this._attrs, childrenClone);
		cloned._customProperties = new Map(this._customProperties);
		cloned.getFillerOffset = this.getFillerOffset;
		cloned._unsafeAttributesToRender = this._unsafeAttributesToRender;
		return cloned;
	}
	/**
	* {@link module:engine/view/element~ViewElement#_insertChild Insert} a child node or a list of child nodes at the end of this node
	* and sets the parent of these nodes to this element.
	*
	* @see module:engine/view/downcastwriter~ViewDowncastWriter#insert
	* @internal
	* @param items Items to be inserted.
	* @fires change
	* @returns Number of appended nodes.
	*/
	_appendChild(items) {
		return this._insertChild(this.childCount, items);
	}
	/**
	* Inserts a child node or a list of child nodes on the given index and sets the parent of these nodes to
	* this element.
	*
	* @internal
	* @see module:engine/view/downcastwriter~ViewDowncastWriter#insert
	* @param index Position where nodes should be inserted.
	* @param items Items to be inserted.
	* @fires change
	* @returns Number of inserted nodes.
	*/
	_insertChild(index, items) {
		this._fireChange("children", this, { index });
		let count = 0;
		const nodes = normalize$3(this.document, items);
		for (const node of nodes) {
			if (node.parent !== null) node._remove();
			node.parent = this;
			node.document = this.document;
			this._children.splice(index, 0, node);
			index++;
			count++;
		}
		return count;
	}
	/**
	* Removes number of child nodes starting at the given index and set the parent of these nodes to `null`.
	*
	* @see module:engine/view/downcastwriter~ViewDowncastWriter#remove
	* @internal
	* @param index Number of the first node to remove.
	* @param howMany Number of nodes to remove.
	* @fires change
	* @returns The array of removed nodes.
	*/
	_removeChildren(index, howMany = 1) {
		this._fireChange("children", this, { index });
		for (let i = index; i < index + howMany; i++) this._children[i].parent = null;
		return this._children.splice(index, howMany);
	}
	/**
	* Adds or overwrite attribute with a specified key and value.
	*
	* @see module:engine/view/downcastwriter~ViewDowncastWriter#setAttribute
	* @internal
	* @param key Attribute key.
	* @param value Attribute value.
	* @param overwrite Whether tokenized attribute should override the attribute value or just add a token.
	* @fires change
	*/
	_setAttribute(key, value, overwrite = true) {
		this._fireChange("attributes", this);
		if (usesStylesMap(this.name, key) || usesTokenList(this.name, key)) {
			let currentValue = this._attrs.get(key);
			if (!currentValue) {
				currentValue = usesStylesMap(this.name, key) ? new StylesMap(this.document.stylesProcessor) : new ViewTokenList();
				this._attrs.set(key, currentValue);
			}
			if (overwrite) currentValue.setTo(String(value));
			else if (usesStylesMap(this.name, key)) if (Array.isArray(value)) currentValue.set(value[0], value[1]);
			else currentValue.set(value);
			else currentValue.set(typeof value == "string" ? value.split(/\s+/) : value);
		} else this._attrs.set(key, String(value));
	}
	/**
	* Removes attribute from the element.
	*
	* @see module:engine/view/downcastwriter~ViewDowncastWriter#removeAttribute
	* @internal
	* @param key Attribute key.
	* @param tokens Attribute value tokens to remove. The whole attribute is removed if not specified.
	* @returns Returns true if an attribute existed and has been removed.
	* @fires change
	*/
	_removeAttribute(key, tokens) {
		this._fireChange("attributes", this);
		if (tokens !== void 0 && (usesStylesMap(this.name, key) || usesTokenList(this.name, key))) {
			const currentValue = this._attrs.get(key);
			if (!currentValue) return false;
			if (usesTokenList(this.name, key) && typeof tokens == "string") tokens = tokens.split(/\s+/);
			currentValue.remove(tokens);
			if (currentValue.isEmpty) return this._attrs.delete(key);
			return false;
		}
		return this._attrs.delete(key);
	}
	/**
	* Adds specified class.
	*
	* ```ts
	* element._addClass( 'foo' ); // Adds 'foo' class.
	* element._addClass( [ 'foo', 'bar' ] ); // Adds 'foo' and 'bar' classes.
	* ```
	*
	* @see module:engine/view/downcastwriter~ViewDowncastWriter#addClass
	* @internal
	* @fires change
	*/
	_addClass(className) {
		this._setAttribute("class", className, false);
	}
	/**
	* Removes specified class.
	*
	* ```ts
	* element._removeClass( 'foo' );  // Removes 'foo' class.
	* element._removeClass( [ 'foo', 'bar' ] ); // Removes both 'foo' and 'bar' classes.
	* ```
	*
	* @see module:engine/view/downcastwriter~ViewDowncastWriter#removeClass
	* @internal
	* @fires change
	*/
	_removeClass(className) {
		this._removeAttribute("class", className);
	}
	_setStyle(property, value) {
		if (typeof property != "string") this._setAttribute("style", property, false);
		else this._setAttribute("style", [property, value], false);
	}
	/**
	* Removes specified style.
	*
	* ```ts
	* element._removeStyle( 'color' );  // Removes 'color' style.
	* element._removeStyle( [ 'color', 'border-top' ] ); // Removes both 'color' and 'border-top' styles.
	* ```
	*
	* **Note**: This method can work with normalized style names if
	* {@link module:engine/controller/datacontroller~DataController#addStyleProcessorRules a particular style processor rule is enabled}.
	* See {@link module:engine/view/stylesmap~StylesMap#remove `StylesMap#remove()`} for details.
	*
	* @see module:engine/view/downcastwriter~ViewDowncastWriter#removeStyle
	* @internal
	* @fires change
	*/
	_removeStyle(property) {
		this._removeAttribute("style", property);
	}
	/**
	* Used by the {@link module:engine/view/matcher~Matcher Matcher} to collect matching attribute tuples
	* (attribute name and optional token).
	*
	* Normalized patterns can be used in following ways:
	* - to match any attribute name with any or no value:
	*
	* ```ts
	* patterns: [
	* 	[ true, true ]
	* ]
	* ```
	*
	* - to match a specific attribute with any value:
	*
	* ```ts
	* patterns: [
	* 	[ 'required', true ]
	* ]
	* ```
	*
	* - to match an attribute name with a RegExp with any value:
	*
	* ```ts
	* patterns: [
	* 	[ /h[1-6]/, true ]
	* ]
	* ```
	*
	* 	- to match a specific attribute with the exact value:
	*
	* ```ts
	* patterns: [
	* 	[ 'rel', 'nofollow' ]
	* ]
	* ```
	*
	* 	- to match a specific attribute with a value matching a RegExp:
	*
	* ```ts
	* patterns: [
	* 	[ 'src', /^https/ ]
	* ]
	* ```
	*
	* 	- to match an attribute name with a RegExp and the exact value:
	*
	* ```ts
	* patterns: [
	* 	[ /^data-property-/, 'foobar' ],
	* ]
	* ```
	*
	* 	- to match an attribute name with a RegExp and match a value with another RegExp:
	*
	* ```ts
	* patterns: [
	* 	[ /^data-property-/, /^foo/ ]
	* ]
	* ```
	*
	* 	- to match a specific style property with the value matching a RegExp:
	*
	* ```ts
	* patterns: [
	* 	[ 'style', 'font-size', /px$/ ]
	* ]
	* ```
	*
	* 	- to match a specific class (class attribute is tokenized so it matches tokens individually):
	*
	* ```ts
	* patterns: [
	* 	[ 'class', 'foo' ]
	* ]
	* ```
	*
	* @internal
	* @param patterns An array of normalized patterns (tuples of 2 or 3 items depending on if tokenized attribute value match is needed).
	* @param match An array to populate with matching tuples.
	* @param exclude Array of attribute names to exclude from match.
	* @returns `true` if element matches all patterns. The matching tuples are pushed to the `match` array.
	*/
	_collectAttributesMatch(patterns, match, exclude) {
		for (const [keyPattern, tokenPattern, valuePattern] of patterns) {
			let hasKey = false;
			let hasValue = false;
			for (const [key, value] of this._attrs) {
				if (exclude && exclude.includes(key) || !isPatternMatched(keyPattern, key)) continue;
				hasKey = true;
				if (typeof value == "string") {
					if (isPatternMatched(tokenPattern, value)) {
						match.push([key]);
						hasValue = true;
					} else if (!(keyPattern instanceof RegExp)) return false;
				} else {
					const tokenMatch = value._getTokensMatch(tokenPattern, valuePattern || true);
					if (tokenMatch) {
						hasValue = true;
						for (const tokenMatchItem of tokenMatch) match.push([key, tokenMatchItem]);
					} else if (!(keyPattern instanceof RegExp)) return false;
				}
			}
			if (!hasKey || !hasValue) return false;
		}
		return true;
	}
	/**
	* Used by the {@link module:engine/conversion/viewconsumable~ViewConsumable} to collect the
	* {@link module:engine/view/element~ViewNormalizedConsumables} for the element.
	*
	* When `key` and `token` parameters are provided the output is filtered for the specified attribute and it's tokens and related tokens.
	*
	* @internal
	* @param key Attribute name.
	* @param token Reference token to collect all related tokens.
	*/
	_getConsumables(key, token) {
		const attributes = [];
		if (key) {
			const value = this._attrs.get(key);
			if (value !== void 0) if (typeof value == "string") attributes.push([key]);
			else for (const prop of value._getConsumables(token)) attributes.push([key, prop]);
		} else for (const [key, value] of this._attrs) if (typeof value == "string") attributes.push([key]);
		else for (const prop of value._getConsumables()) attributes.push([key, prop]);
		return {
			name: !key,
			attributes
		};
	}
	/**
	* Verify if the given element can be merged without conflicts into the element.
	*
	* Note that this method is extended by the {@link module:engine/view/attributeelement~ViewAttributeElement} implementation.
	*
	* This method is used by the {@link module:engine/view/downcastwriter~ViewDowncastWriter} while down-casting
	* an {@link module:engine/view/attributeelement~ViewAttributeElement} to merge it with other ViewAttributeElement.
	*
	* @internal
	* @returns Returns `true` if elements can be merged.
	*/
	_canMergeAttributesFrom(otherElement) {
		if (this.name != otherElement.name) return false;
		for (const [key, otherValue] of otherElement._attrs) {
			const value = this._attrs.get(key);
			if (value === void 0) continue;
			if (typeof value == "string" || typeof otherValue == "string") {
				if (value !== otherValue) return false;
			} else if (!value._canMergeFrom(otherValue)) return false;
		}
		return true;
	}
	/**
	* Merges attributes of a given element into the element.
	* This includes also tokenized attributes like style and class.
	*
	* Note that you should make sure there are no conflicts before merging (see {@link #_canMergeAttributesFrom}).
	*
	* This method is used by the {@link module:engine/view/downcastwriter~ViewDowncastWriter} while down-casting
	* an {@link module:engine/view/attributeelement~ViewAttributeElement} to merge it with other ViewAttributeElement.
	*
	* @internal
	*/
	_mergeAttributesFrom(otherElement) {
		this._fireChange("attributes", this);
		for (const [key, otherValue] of otherElement._attrs) {
			const value = this._attrs.get(key);
			if (value === void 0 || typeof value == "string" || typeof otherValue == "string") this._setAttribute(key, otherValue);
			else value._mergeFrom(otherValue);
		}
	}
	/**
	* Verify if the given element attributes can be fully subtracted from the element.
	*
	* Note that this method is extended by the {@link module:engine/view/attributeelement~ViewAttributeElement} implementation.
	*
	* This method is used by the {@link module:engine/view/downcastwriter~ViewDowncastWriter} while down-casting
	* an {@link module:engine/view/attributeelement~ViewAttributeElement} to unwrap the ViewAttributeElement.
	*
	* @internal
	* @returns Returns `true` if elements attributes can be fully subtracted.
	*/
	_canSubtractAttributesOf(otherElement) {
		if (this.name != otherElement.name) return false;
		for (const [key, otherValue] of otherElement._attrs) {
			const value = this._attrs.get(key);
			if (value === void 0) return false;
			if (typeof value == "string" || typeof otherValue == "string") {
				if (value !== otherValue) return false;
			} else if (!value._isMatching(otherValue)) return false;
		}
		return true;
	}
	/**
	* Removes (subtracts) corresponding attributes of the given element from the element.
	* This includes also tokenized attributes like style and class.
	* All attributes, classes and styles from given element should be present inside the element being unwrapped.
	*
	* Note that you should make sure all attributes could be subtracted before subtracting them (see {@link #_canSubtractAttributesOf}).
	*
	* This method is used by the {@link module:engine/view/downcastwriter~ViewDowncastWriter} while down-casting
	* an {@link module:engine/view/attributeelement~ViewAttributeElement} to unwrap the ViewAttributeElement.
	*
	* @internal
	*/
	_subtractAttributesOf(otherElement) {
		this._fireChange("attributes", this);
		for (const [key, otherValue] of otherElement._attrs) {
			const value = this._attrs.get(key);
			if (typeof value == "string" || typeof otherValue == "string") this._attrs.delete(key);
			else {
				value.remove(otherValue.keys());
				if (value.isEmpty) this._attrs.delete(key);
			}
		}
	}
	/**
	* Sets a custom property. Unlike attributes, custom properties are not rendered to the DOM,
	* so they can be used to add special data to elements.
	*
	* @see module:engine/view/downcastwriter~ViewDowncastWriter#setCustomProperty
	* @internal
	*/
	_setCustomProperty(key, value) {
		this._customProperties.set(key, value);
	}
	/**
	* Removes the custom property stored under the given key.
	*
	* @see module:engine/view/downcastwriter~ViewDowncastWriter#removeCustomProperty
	* @internal
	* @returns Returns true if property was removed.
	*/
	_removeCustomProperty(key) {
		return this._customProperties.delete(key);
	}
	/**
	* Parses attributes provided to the element constructor before they are applied to an element. If attributes are passed
	* as an object (instead of `Iterable`), the object is transformed to the map. Attributes with `null` value are removed.
	* Attributes with non-`String` value are converted to `String`.
	*
	* @param attrs Attributes to parse.
	* @returns Parsed attributes.
	*/
	_parseAttributes(attrs) {
		const attrsMap = toMap(attrs);
		for (const [key, value] of attrsMap) if (value === null) attrsMap.delete(key);
		else if (usesStylesMap(this.name, key)) {
			const newValue = value instanceof StylesMap ? value._clone() : new StylesMap(this.document.stylesProcessor).setTo(String(value));
			attrsMap.set(key, newValue);
		} else if (usesTokenList(this.name, key)) {
			const newValue = value instanceof ViewTokenList ? value._clone() : new ViewTokenList().setTo(String(value));
			attrsMap.set(key, newValue);
		} else if (typeof value != "string") attrsMap.set(key, String(value));
		return attrsMap;
	}
};
ViewElement.prototype.is = function(type, name) {
	if (!name) return type === "element" || type === "view:element" || type === "node" || type === "view:node";
	else return name === this.name && (type === "element" || type === "view:element");
};
/**
* Converts strings to Text and non-iterables to arrays.
*/
function normalize$3(document, nodes) {
	if (typeof nodes == "string") return [new ViewText(document, nodes)];
	if (!isIterable(nodes)) nodes = [nodes];
	const normalizedNodes = [];
	for (const node of nodes) if (typeof node == "string") normalizedNodes.push(new ViewText(document, node));
	else if (node instanceof ViewTextProxy) normalizedNodes.push(new ViewText(document, node.data));
	else normalizedNodes.push(node);
	return normalizedNodes;
}
/**
* Returns `true` if an attribute on a given element should be handled as a TokenList.
*/
function usesTokenList(elementName, key) {
	return key == "class" || elementName == "a" && key == "rel";
}
/**
* Returns `true` if an attribute on a given element should be handled as a StylesMap.
*/
function usesStylesMap(elementName, key) {
	return key == "style";
}

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* @module engine/view/containerelement
*/
/**
* Containers are elements which define document structure. They define boundaries for
* {@link module:engine/view/attributeelement~ViewAttributeElement attributes}.
* They are mostly used for block elements like `<p>` or `<div>`.
*
* Editing engine does not define a fixed HTML DTD. This is why a feature developer needs to choose between various
* types (container element, {@link module:engine/view/attributeelement~ViewAttributeElement attribute element},
* {@link module:engine/view/emptyelement~ViewEmptyElement empty element}, etc) when developing a feature.
*
* The container element should be your default choice when writing a converter, unless:
*
* * this element represents a model text attribute (then use {@link module:engine/view/attributeelement~ViewAttributeElement}),
* * this is an empty element like `<img>` (then use {@link module:engine/view/emptyelement~ViewEmptyElement}),
* * this is a root element,
* * this is a nested editable element (then use  {@link module:engine/view/editableelement~ViewEditableElement}).
*
* To create a new container element instance use the
* {@link module:engine/view/downcastwriter~ViewDowncastWriter#createContainerElement `ViewDowncastWriter#createContainerElement()`}
* method.
*/
var ViewContainerElement = class extends ViewElement {
	/**
	* Creates a container element.
	*
	* @see module:engine/view/downcastwriter~ViewDowncastWriter#createContainerElement
	* @see module:engine/view/element~ViewElement
	* @internal
	* @param document The document instance to which this element belongs.
	* @param name Node name.
	* @param attrs Collection of attributes.
	* @param children A list of nodes to be inserted into created element.
	*/
	constructor(document, name, attrs, children) {
		super(document, name, attrs, children);
		this.getFillerOffset = getViewFillerOffset;
	}
	/**
	* Converts `ViewContainerElement` instance to plain object and returns it.
	*
	* @returns `ViewContainerElement` instance converted to plain object.
	*/
	toJSON() {
		const json = super.toJSON();
		json.type = "ContainerElement";
		return json;
	}
};
ViewContainerElement.prototype.is = function(type, name) {
	if (!name) return type === "containerElement" || type === "view:containerElement" || type === "element" || type === "view:element" || type === "node" || type === "view:node";
	else return name === this.name && (type === "containerElement" || type === "view:containerElement" || type === "element" || type === "view:element");
};
/**
* Returns block {@link module:engine/view/filler filler} offset or `null` if block filler is not needed.
*
* @returns Block filler offset or `null` if block filler is not needed.
*/
function getViewFillerOffset() {
	const children = [...this.getChildren()];
	let lastChild = children[this.childCount - 1];
	if (lastChild && lastChild.is("element", "br")) return this.childCount;
	while (lastChild && lastChild.is("attributeElement")) {
		lastChild = lastChild.getChild(lastChild.childCount - 1);
		if (lastChild && lastChild.is("element", "br")) return this.childCount;
	}
	for (const child of children) if (!child.is("uiElement")) return null;
	return this.childCount;
}

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* @module engine/view/editableelement
*/
const ViewEditableElementBase = /* #__PURE__ */ ObservableMixin(ViewContainerElement);
/**
* Editable element which can be a {@link module:engine/view/rooteditableelement~ViewRootEditableElement root}
* or nested editable area in the editor.
*
* Editable is automatically read-only when its {@link module:engine/view/document~ViewDocument Document} is read-only.
*
* The constructor of this class shouldn't be used directly. To create new `ViewEditableElement` use the
* {@link module:engine/view/downcastwriter~ViewDowncastWriter#createEditableElement `downcastWriter#createEditableElement()`} method.
*/
var ViewEditableElement = class extends ViewEditableElementBase {
	/**
	* Creates an editable element.
	*
	* @see module:engine/view/downcastwriter~ViewDowncastWriter#createEditableElement
	* @internal
	* @param document The document instance to which this element belongs.
	* @param name Node name.
	* @param attributes Collection of attributes.
	* @param children A list of nodes to be inserted into created element.
	*/
	constructor(document, name, attributes, children) {
		super(document, name, attributes, children);
		this.set("isReadOnly", false);
		this.set("isFocused", false);
		this.set("placeholder", void 0);
		this.bind("isReadOnly").to(document);
		this.bind("isFocused").to(document, "isFocused", (isFocused) => isFocused && document.selection.editableElement == this);
		this.listenTo(document.selection, "change", () => {
			this.isFocused = document.isFocused && document.selection.editableElement == this;
		});
	}
	destroy() {
		this.stopListening();
	}
	/**
	* Converts `ViewEditableElement` instance to plain object and returns it.
	*
	* @returns `ViewEditableElement` instance converted to plain object.
	*/
	toJSON() {
		const json = super.toJSON();
		json.type = "EditableElement";
		json.isReadOnly = this.isReadOnly;
		json.isFocused = this.isFocused;
		return json;
	}
};
ViewEditableElement.prototype.is = function(type, name) {
	if (!name) return type === "editableElement" || type === "view:editableElement" || type === "containerElement" || type === "view:containerElement" || type === "element" || type === "view:element" || type === "node" || type === "view:node";
	else return name === this.name && (type === "editableElement" || type === "view:editableElement" || type === "containerElement" || type === "view:containerElement" || type === "element" || type === "view:element");
};

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* @module engine/view/rooteditableelement
*/
const rootNameSymbol = Symbol("rootName");
/**
* Class representing a single root in the data view. A root can be either
* {@link ~ViewRootEditableElement#isReadOnly editable or read-only},
* but in both cases it is called "an editable". Roots can contain other {@link module:engine/view/editableelement~ViewEditableElement
* editable elements} making them "nested editables".
*/
var ViewRootEditableElement = class extends ViewEditableElement {
	/**
	* Creates root editable element.
	*
	* @param document The document instance to which this element belongs.
	* @param name Node name.
	*/
	constructor(document, name) {
		super(document, name);
		this.rootName = "main";
	}
	/**
	* Name of this root inside {@link module:engine/view/document~ViewDocument} that is an owner of this root. If no
	* other name is set, `main` name is used.
	*
	* @readonly
	*/
	get rootName() {
		return this.getCustomProperty(rootNameSymbol);
	}
	set rootName(rootName) {
		this._setCustomProperty(rootNameSymbol, rootName);
	}
	/**
	* Converts `ViewRootEditableElement` instance to string and returns it.
	*
	* @returns `ViewRootEditableElement` instance converted to string.
	*/
	toJSON() {
		return this.rootName;
	}
	/**
	* Overrides old element name and sets new one.
	* This is needed because view roots are created before they are attached to the DOM.
	* The name of the root element is temporary at this stage. It has to be changed when the
	* view root element is attached to the DOM element.
	*
	* @internal
	* @param name The new name of element.
	*/
	set _name(name) {
		this.name = name;
	}
};
ViewRootEditableElement.prototype.is = function(type, name) {
	if (!name) return type === "rootElement" || type === "view:rootElement" || type === "editableElement" || type === "view:editableElement" || type === "containerElement" || type === "view:containerElement" || type === "element" || type === "view:element" || type === "node" || type === "view:node";
	else return name === this.name && (type === "rootElement" || type === "view:rootElement" || type === "editableElement" || type === "view:editableElement" || type === "containerElement" || type === "view:containerElement" || type === "element" || type === "view:element");
};

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* Position iterator class. It allows to iterate forward and backward over the document.
*/
var ViewTreeWalker = class {
	/**
	* Walking direction. Defaults `'forward'`.
	*/
	direction;
	/**
	* Iterator boundaries.
	*
	* When the iterator is walking `'forward'` on the end of boundary or is walking `'backward'`
	* on the start of boundary, then `{ done: true }` is returned.
	*
	* If boundaries are not defined they are set before first and after last child of the root node.
	*/
	boundaries;
	/**
	* Flag indicating whether all characters from {@link module:engine/view/text~ViewText} should be returned as one
	* {@link module:engine/view/text~ViewText} or one by one as {@link module:engine/view/textproxy~ViewTextProxy}.
	*/
	singleCharacters;
	/**
	* Flag indicating whether iterator should enter elements or not. If the iterator is shallow child nodes of any
	* iterated node will not be returned along with `elementEnd` tag.
	*/
	shallow;
	/**
	* Flag indicating whether iterator should ignore `elementEnd` tags. If set to `true`, walker will not
	* return a parent node of the start position. Each {@link module:engine/view/element~ViewElement} will be returned once.
	* When set to `false` each element might be returned twice: for `'elementStart'` and `'elementEnd'`.
	*/
	ignoreElementEnd;
	/**
	* Iterator position. If start position is not defined then position depends on {@link #direction}. If direction is
	* `'forward'` position starts form the beginning, when direction is `'backward'` position starts from the end.
	*/
	_position;
	/**
	* Start boundary parent.
	*/
	_boundaryStartParent;
	/**
	* End boundary parent.
	*/
	_boundaryEndParent;
	/**
	* Creates a range iterator. All parameters are optional, but you have to specify either `boundaries` or `startPosition`.
	*
	* @param options Object with configuration.
	*/
	constructor(options = {}) {
		if (!options.boundaries && !options.startPosition)
 /**
		* Neither boundaries nor starting position have been defined.
		*
		* @error view-tree-walker-no-start-position
		*/
		throw new CKEditorError("view-tree-walker-no-start-position", null);
		if (options.direction && options.direction != "forward" && options.direction != "backward")
 /**
		* Only `backward` and `forward` direction allowed.
		*
		* @error view-tree-walker-unknown-direction
		*/
		throw new CKEditorError("view-tree-walker-unknown-direction", options.startPosition, { direction: options.direction });
		this.boundaries = options.boundaries || null;
		if (options.startPosition) this._position = ViewPosition._createAt(options.startPosition);
		else this._position = ViewPosition._createAt(options.boundaries[options.direction == "backward" ? "end" : "start"]);
		this.direction = options.direction || "forward";
		this.singleCharacters = !!options.singleCharacters;
		this.shallow = !!options.shallow;
		this.ignoreElementEnd = !!options.ignoreElementEnd;
		this._boundaryStartParent = this.boundaries ? this.boundaries.start.parent : null;
		this._boundaryEndParent = this.boundaries ? this.boundaries.end.parent : null;
	}
	/**
	* Iterable interface.
	*/
	[Symbol.iterator]() {
		return this;
	}
	/**
	* Iterator position. If start position is not defined then position depends on {@link #direction}. If direction is
	* `'forward'` position starts form the beginning, when direction is `'backward'` position starts from the end.
	*/
	get position() {
		return this._position;
	}
	/**
	* Moves {@link #position} in the {@link #direction} skipping values as long as the callback function returns `true`.
	*
	* For example:
	*
	* ```ts
	* walker.skip( value => value.type == 'text' ); // <p>{}foo</p> -> <p>foo[]</p>
	* walker.skip( value => true ); // Move the position to the end: <p>{}foo</p> -> <p>foo</p>[]
	* walker.skip( value => false ); // Do not move the position.
	* ```
	*
	* @param skip Callback function. Gets {@link module:engine/view/treewalker~ViewTreeWalkerValue} and should
	* return `true` if the value should be skipped or `false` if not.
	*/
	skip(skip) {
		let nextResult;
		let prevPosition;
		do {
			prevPosition = this.position;
			nextResult = this.next();
		} while (!nextResult.done && skip(nextResult.value));
		if (!nextResult.done) this._position = prevPosition;
	}
	/**
	* Moves tree walker {@link #position} to provided `position`. Tree walker will
	* continue traversing from that position.
	*
	* Note: in contrary to {@link ~ViewTreeWalker#skip}, this method does not iterate over the nodes along the way.
	* It simply sets the current tree walker position to a new one.
	* From the performance standpoint, it is better to use {@link ~ViewTreeWalker#jumpTo} rather than {@link ~ViewTreeWalker#skip}.
	*
	* If the provided position is before the start boundary, the position will be
	* set to the start boundary. If the provided position is after the end boundary,
	* the position will be set to the end boundary.
	* This is done to prevent the treewalker from traversing outside the boundaries.
	*
	* @param position Position to jump to.
	*/
	jumpTo(position) {
		if (this._boundaryStartParent && position.isBefore(this.boundaries.start)) position = this.boundaries.start;
		else if (this._boundaryEndParent && position.isAfter(this.boundaries.end)) position = this.boundaries.end;
		this._position = position.clone();
	}
	/**
	* Gets the next tree walker's value.
	*
	* @returns Object implementing iterator interface, returning
	* information about taken step.
	*/
	next() {
		if (this.direction == "forward") return this._next();
		else return this._previous();
	}
	/**
	* Makes a step forward in view. Moves the {@link #position} to the next position and returns the encountered value.
	*/
	_next() {
		let position = this.position.clone();
		const previousPosition = this.position;
		const parent = position.parent;
		if (parent.parent === null && position.offset === parent.childCount) return {
			done: true,
			value: void 0
		};
		if (parent === this._boundaryEndParent && position.offset == this.boundaries.end.offset) return {
			done: true,
			value: void 0
		};
		let node;
		if (parent && parent.is("view:$text")) {
			if (position.isAtEnd) {
				this._position = ViewPosition._createAfter(parent);
				return this._next();
			}
			node = parent.data[position.offset];
		} else node = parent.getChild(position.offset);
		if (typeof node == "string") {
			let textLength;
			if (this.singleCharacters) textLength = 1;
			else textLength = (parent === this._boundaryEndParent ? this.boundaries.end.offset : parent.data.length) - position.offset;
			const textProxy = new ViewTextProxy(parent, position.offset, textLength);
			position.offset += textLength;
			this._position = position;
			return this._formatReturnValue("text", textProxy, previousPosition, position, textLength);
		}
		if (node && node.is("view:element")) {
			if (!this.shallow) position = new ViewPosition(node, 0);
			else {
				if (this.boundaries && this.boundaries.end.isBefore(position)) return {
					done: true,
					value: void 0
				};
				position.offset++;
			}
			this._position = position;
			return this._formatReturnValue("elementStart", node, previousPosition, position, 1);
		}
		if (node && node.is("view:$text")) {
			if (this.singleCharacters) {
				position = new ViewPosition(node, 0);
				this._position = position;
				return this._next();
			}
			let charactersCount = node.data.length;
			let item;
			if (node == this._boundaryEndParent) {
				charactersCount = this.boundaries.end.offset;
				item = new ViewTextProxy(node, 0, charactersCount);
				position = ViewPosition._createAfter(item);
			} else {
				item = new ViewTextProxy(node, 0, node.data.length);
				position.offset++;
			}
			this._position = position;
			return this._formatReturnValue("text", item, previousPosition, position, charactersCount);
		}
		position = ViewPosition._createAfter(parent);
		this._position = position;
		if (this.ignoreElementEnd) return this._next();
		return this._formatReturnValue("elementEnd", parent, previousPosition, position);
	}
	/**
	* Makes a step backward in view. Moves the {@link #position} to the previous position and returns the encountered value.
	*/
	_previous() {
		let position = this.position.clone();
		const previousPosition = this.position;
		const parent = position.parent;
		if (parent.parent === null && position.offset === 0) return {
			done: true,
			value: void 0
		};
		if (parent == this._boundaryStartParent && position.offset == this.boundaries.start.offset) return {
			done: true,
			value: void 0
		};
		let node;
		if (parent.is("view:$text")) {
			if (position.isAtStart) {
				this._position = ViewPosition._createBefore(parent);
				return this._previous();
			}
			node = parent.data[position.offset - 1];
		} else node = parent.getChild(position.offset - 1);
		if (typeof node == "string") {
			let textLength;
			if (!this.singleCharacters) {
				const startOffset = parent === this._boundaryStartParent ? this.boundaries.start.offset : 0;
				textLength = position.offset - startOffset;
			} else textLength = 1;
			position.offset -= textLength;
			const textProxy = new ViewTextProxy(parent, position.offset, textLength);
			this._position = position;
			return this._formatReturnValue("text", textProxy, previousPosition, position, textLength);
		}
		if (node && node.is("view:element")) {
			if (this.shallow) {
				position.offset--;
				this._position = position;
				return this._formatReturnValue("elementStart", node, previousPosition, position, 1);
			}
			position = new ViewPosition(node, node.childCount);
			this._position = position;
			if (this.ignoreElementEnd) return this._previous();
			return this._formatReturnValue("elementEnd", node, previousPosition, position);
		}
		if (node && node.is("view:$text")) {
			if (this.singleCharacters) {
				position = new ViewPosition(node, node.data.length);
				this._position = position;
				return this._previous();
			}
			let charactersCount = node.data.length;
			let item;
			if (node == this._boundaryStartParent) {
				const offset = this.boundaries.start.offset;
				item = new ViewTextProxy(node, offset, node.data.length - offset);
				charactersCount = item.data.length;
				position = ViewPosition._createBefore(item);
			} else {
				item = new ViewTextProxy(node, 0, node.data.length);
				position.offset--;
			}
			this._position = position;
			return this._formatReturnValue("text", item, previousPosition, position, charactersCount);
		}
		position = ViewPosition._createBefore(parent);
		this._position = position;
		return this._formatReturnValue("elementStart", parent, previousPosition, position, 1);
	}
	/**
	* Format returned data and adjust `previousPosition` and `nextPosition` if
	* reach the bound of the {@link module:engine/view/text~ViewText}.
	*
	* @param type Type of step.
	* @param item Item between old and new position.
	* @param previousPosition Previous position of iterator.
	* @param nextPosition Next position of iterator.
	* @param length Length of the item.
	*/
	_formatReturnValue(type, item, previousPosition, nextPosition, length) {
		if (item.is("view:$textProxy")) {
			if (item.offsetInText + item.data.length == item.textNode.data.length) if (this.direction == "forward" && !(this.boundaries && this.boundaries.end.isEqual(this.position))) {
				nextPosition = ViewPosition._createAfter(item.textNode);
				this._position = nextPosition;
			} else previousPosition = ViewPosition._createAfter(item.textNode);
			if (item.offsetInText === 0) if (this.direction == "backward" && !(this.boundaries && this.boundaries.start.isEqual(this.position))) {
				nextPosition = ViewPosition._createBefore(item.textNode);
				this._position = nextPosition;
			} else previousPosition = ViewPosition._createBefore(item.textNode);
		}
		return {
			done: false,
			value: {
				type,
				item,
				previousPosition,
				nextPosition,
				length
			}
		};
	}
};

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* @module engine/view/position
*/
/**
* Position in the view tree. Position is represented by its parent node and an offset in this parent.
*
* In order to create a new position instance use the `createPosition*()` factory methods available in:
*
* * {@link module:engine/view/view~EditingView}
* * {@link module:engine/view/downcastwriter~ViewDowncastWriter}
* * {@link module:engine/view/upcastwriter~ViewUpcastWriter}
*/
var ViewPosition = class ViewPosition extends ViewTypeCheckable {
	/**
	* Position parent.
	*/
	parent;
	/**
	* Position offset.
	*/
	offset;
	/**
	* Creates a position.
	*
	* @param parent Position parent.
	* @param offset Position offset.
	*/
	constructor(parent, offset) {
		super();
		this.parent = parent;
		this.offset = offset;
	}
	/**
	* Node directly after the position. Equals `null` when there is no node after position or position is located
	* inside text node.
	*/
	get nodeAfter() {
		if (this.parent.is("$text")) return null;
		return this.parent.getChild(this.offset) || null;
	}
	/**
	* Node directly before the position. Equals `null` when there is no node before position or position is located
	* inside text node.
	*/
	get nodeBefore() {
		if (this.parent.is("$text")) return null;
		return this.parent.getChild(this.offset - 1) || null;
	}
	/**
	* Is `true` if position is at the beginning of its {@link module:engine/view/position~ViewPosition#parent parent}, `false` otherwise.
	*/
	get isAtStart() {
		return this.offset === 0;
	}
	/**
	* Is `true` if position is at the end of its {@link module:engine/view/position~ViewPosition#parent parent}, `false` otherwise.
	*/
	get isAtEnd() {
		const endOffset = this.parent.is("$text") ? this.parent.data.length : this.parent.childCount;
		return this.offset === endOffset;
	}
	/**
	* Position's root, that is the root of the position's parent element.
	*/
	get root() {
		return this.parent.root;
	}
	/**
	* {@link module:engine/view/editableelement~ViewEditableElement ViewEditableElement} instance that contains this position, or `null` if
	* position is not inside an editable element.
	*/
	get editableElement() {
		let editable = this.parent;
		while (!(editable instanceof ViewEditableElement)) if (editable.parent) editable = editable.parent;
		else return null;
		return editable;
	}
	/**
	* Returns a new instance of Position with offset incremented by `shift` value.
	*
	* @param shift How position offset should get changed. Accepts negative values.
	* @returns Shifted position.
	*/
	getShiftedBy(shift) {
		const shifted = ViewPosition._createAt(this);
		const offset = shifted.offset + shift;
		shifted.offset = offset < 0 ? 0 : offset;
		return shifted;
	}
	/**
	* Gets the farthest position which matches the callback using
	* {@link module:engine/view/treewalker~ViewTreeWalker TreeWalker}.
	*
	* For example:
	*
	* ```ts
	* getLastMatchingPosition( value => value.type == 'text' ); // <p>{}foo</p> -> <p>foo[]</p>
	* getLastMatchingPosition( value => value.type == 'text', { direction: 'backward' } ); // <p>foo[]</p> -> <p>{}foo</p>
	* getLastMatchingPosition( value => false ); // Do not move the position.
	* ```
	*
	* @param skip Callback function. Gets {@link module:engine/view/treewalker~ViewTreeWalkerValue} and should
	* return `true` if the value should be skipped or `false` if not.
	* @param options Object with configuration options. See {@link module:engine/view/treewalker~ViewTreeWalker}.
	* @returns The position after the last item which matches the `skip` callback test.
	*/
	getLastMatchingPosition(skip, options = {}) {
		options.startPosition = this;
		const treeWalker = new ViewTreeWalker(options);
		treeWalker.skip(skip);
		return treeWalker.position;
	}
	/**
	* Returns ancestors array of this position, that is this position's parent and it's ancestors.
	*
	* @returns Array with ancestors.
	*/
	getAncestors() {
		if (this.parent.is("documentFragment")) return [this.parent];
		else return this.parent.getAncestors({ includeSelf: true });
	}
	/**
	* Returns a {@link module:engine/view/node~ViewNode} or {@link module:engine/view/documentfragment~ViewDocumentFragment}
	* which is a common ancestor of both positions.
	*/
	getCommonAncestor(position) {
		const ancestorsA = this.getAncestors();
		const ancestorsB = position.getAncestors();
		let i = 0;
		while (ancestorsA[i] == ancestorsB[i] && ancestorsA[i]) i++;
		return i === 0 ? null : ancestorsA[i - 1];
	}
	/**
	* Checks whether this position equals given position.
	*
	* @param otherPosition Position to compare with.
	* @returns True if positions are same.
	*/
	isEqual(otherPosition) {
		return this.parent == otherPosition.parent && this.offset == otherPosition.offset;
	}
	/**
	* Checks whether this position is located before given position. When method returns `false` it does not mean that
	* this position is after give one. Two positions may be located inside separate roots and in that situation this
	* method will still return `false`.
	*
	* @see module:engine/view/position~ViewPosition#isAfter
	* @see module:engine/view/position~ViewPosition#compareWith
	* @param otherPosition Position to compare with.
	* @returns Returns `true` if this position is before given position.
	*/
	isBefore(otherPosition) {
		return this.compareWith(otherPosition) == "before";
	}
	/**
	* Checks whether this position is located after given position. When method returns `false` it does not mean that
	* this position is before give one. Two positions may be located inside separate roots and in that situation this
	* method will still return `false`.
	*
	* @see module:engine/view/position~ViewPosition#isBefore
	* @see module:engine/view/position~ViewPosition#compareWith
	* @param otherPosition Position to compare with.
	* @returns Returns `true` if this position is after given position.
	*/
	isAfter(otherPosition) {
		return this.compareWith(otherPosition) == "after";
	}
	/**
	* Checks whether this position is before, after or in same position that other position. Two positions may be also
	* different when they are located in separate roots.
	*
	* @param otherPosition Position to compare with.
	*/
	compareWith(otherPosition) {
		if (this.root !== otherPosition.root) return "different";
		if (this.isEqual(otherPosition)) return "same";
		const thisPath = this.parent.is("node") ? this.parent.getPath() : [];
		const otherPath = otherPosition.parent.is("node") ? otherPosition.parent.getPath() : [];
		thisPath.push(this.offset);
		otherPath.push(otherPosition.offset);
		const result = compareArrays(thisPath, otherPath);
		switch (result) {
			case "prefix": return "before";
			case "extension": return "after";
			default: return thisPath[result] < otherPath[result] ? "before" : "after";
		}
	}
	/**
	* Creates a {@link module:engine/view/treewalker~ViewTreeWalker TreeWalker} instance with this positions as a start position.
	*
	* @param options Object with configuration options. See {@link module:engine/view/treewalker~ViewTreeWalker}
	*/
	getWalker(options = {}) {
		options.startPosition = this;
		return new ViewTreeWalker(options);
	}
	/**
	* Clones this position.
	*/
	clone() {
		return new ViewPosition(this.parent, this.offset);
	}
	/**
	* Converts `ViewPosition` instance to plain object and returns it.
	*
	* @returns `ViewPosition` instance converted to plain object.
	*/
	toJSON() {
		return {
			parent: this.parent.toJSON(),
			offset: this.offset
		};
	}
	/**
	* Creates position at the given location. The location can be specified as:
	*
	* * a {@link module:engine/view/position~ViewPosition position},
	* * parent element and offset (offset defaults to `0`),
	* * parent element and `'end'` (sets position at the end of that element),
	* * {@link module:engine/view/item~ViewItem view item} and `'before'` or `'after'` (sets position before or after given view item).
	*
	* This method is a shortcut to other constructors such as:
	*
	* * {@link module:engine/view/position~ViewPosition._createBefore},
	* * {@link module:engine/view/position~ViewPosition._createAfter}.
	*
	* @internal
	* @param offset Offset or one of the flags. Used only when first parameter is a {@link module:engine/view/item~ViewItem view item}.
	*/
	static _createAt(itemOrPosition, offset) {
		if (itemOrPosition instanceof ViewPosition) return new this(itemOrPosition.parent, itemOrPosition.offset);
		else {
			const node = itemOrPosition;
			if (offset == "end") offset = node.is("$text") ? node.data.length : node.childCount;
			else if (offset == "before") return this._createBefore(node);
			else if (offset == "after") return this._createAfter(node);
			else if (offset !== 0 && !offset)
 /**
			* {@link module:engine/view/view~EditingView#createPositionAt `View#createPositionAt()`}
			* requires the offset to be specified when the first parameter is a view item.
			*
			* @error view-createpositionat-offset-required
			*/
			throw new CKEditorError("view-createpositionat-offset-required", node);
			return new ViewPosition(node, offset);
		}
	}
	/**
	* Creates a new position after given view item.
	*
	* @internal
	* @param item View item after which the position should be located.
	*/
	static _createAfter(item) {
		if (item.is("$textProxy")) return new ViewPosition(item.textNode, item.offsetInText + item.data.length);
		if (!item.parent)
 /**
		* You cannot make a position after a root.
		*
		* @error view-position-after-root
		* @param {module:engine/view/node~ViewNode} root A root item.
		*/
		throw new CKEditorError("view-position-after-root", item, { root: item });
		return new ViewPosition(item.parent, item.index + 1);
	}
	/**
	* Creates a new position before given view item.
	*
	* @internal
	* @param item View item before which the position should be located.
	*/
	static _createBefore(item) {
		if (item.is("$textProxy")) return new ViewPosition(item.textNode, item.offsetInText);
		if (!item.parent)
 /**
		* You cannot make a position before a root.
		*
		* @error view-position-before-root
		* @param {module:engine/view/node~ViewNode} root A root item.
		*/
		throw new CKEditorError("view-position-before-root", item, { root: item });
		return new ViewPosition(item.parent, item.index);
	}
};
ViewPosition.prototype.is = function(type) {
	return type === "position" || type === "view:position";
};

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* @module engine/view/range
*/
/**
* Range in the view tree. A range is represented by its start and end {@link module:engine/view/position~ViewPosition positions}.
*
* In order to create a new position instance use the `createPosition*()` factory methods available in:
*
* * {@link module:engine/view/view~EditingView}
* * {@link module:engine/view/downcastwriter~ViewDowncastWriter}
* * {@link module:engine/view/upcastwriter~ViewUpcastWriter}
*/
var ViewRange = class ViewRange extends ViewTypeCheckable {
	/**
	* Start position.
	*/
	start;
	/**
	* End position.
	*/
	end;
	/**
	* Creates a range spanning from `start` position to `end` position.
	*
	* **Note:** Constructor creates it's own {@link module:engine/view/position~ViewPosition} instances basing on passed values.
	*
	* @param start Start position.
	* @param end End position. If not set, range will be collapsed at the `start` position.
	*/
	constructor(start, end = null) {
		super();
		this.start = start.clone();
		this.end = end ? end.clone() : start.clone();
	}
	/**
	* Iterable interface.
	*
	* Iterates over all {@link module:engine/view/item~ViewItem view items} that are in this range and returns
	* them together with additional information like length or {@link module:engine/view/position~ViewPosition positions},
	* grouped as {@link module:engine/view/treewalker~ViewTreeWalkerValue}.
	*
	* This iterator uses {@link module:engine/view/treewalker~ViewTreeWalker TreeWalker} with `boundaries` set to this range and
	* `ignoreElementEnd` option
	* set to `true`.
	*/
	*[Symbol.iterator]() {
		yield* new ViewTreeWalker({
			boundaries: this,
			ignoreElementEnd: true
		});
	}
	/**
	* Returns whether the range is collapsed, that is it start and end positions are equal.
	*/
	get isCollapsed() {
		return this.start.isEqual(this.end);
	}
	/**
	* Returns whether this range is flat, that is if {@link module:engine/view/range~ViewRange#start start} position and
	* {@link module:engine/view/range~ViewRange#end end} position are in the same
	* {@link module:engine/view/position~ViewPosition#parent parent}.
	*/
	get isFlat() {
		return this.start.parent === this.end.parent;
	}
	/**
	* Range root element.
	*/
	get root() {
		return this.start.root;
	}
	/**
	* Creates a maximal range that has the same content as this range but is expanded in both ways (at the beginning
	* and at the end).
	*
	* For example:
	*
	* ```html
	* <p>Foo</p><p><b>{Bar}</b></p> -> <p>Foo</p>[<p><b>Bar</b>]</p>
	* <p><b>foo</b>{bar}<span></span></p> -> <p><b>foo[</b>bar<span></span>]</p>
	* ```
	*
	* Note that in the sample above:
	*
	* - `<p>` have type of {@link module:engine/view/containerelement~ViewContainerElement},
	* - `<b>` have type of {@link module:engine/view/attributeelement~ViewAttributeElement},
	* - `<span>` have type of {@link module:engine/view/uielement~ViewUIElement}.
	*
	* @returns Enlarged range.
	*/
	getEnlarged() {
		let start = this.start.getLastMatchingPosition(enlargeTrimSkip, { direction: "backward" });
		let end = this.end.getLastMatchingPosition(enlargeTrimSkip);
		if (start.parent.is("$text") && start.isAtStart) start = ViewPosition._createBefore(start.parent);
		if (end.parent.is("$text") && end.isAtEnd) end = ViewPosition._createAfter(end.parent);
		return new ViewRange(start, end);
	}
	/**
	* Creates a minimum range that has the same content as this range but is trimmed in both ways (at the beginning
	* and at the end).
	*
	* For example:
	*
	* ```html
	* <p>Foo</p>[<p><b>Bar</b>]</p> -> <p>Foo</p><p><b>{Bar}</b></p>
	* <p><b>foo[</b>bar<span></span>]</p> -> <p><b>foo</b>{bar}<span></span></p>
	* ```
	*
	* Note that in the sample above:
	*
	* - `<p>` have type of {@link module:engine/view/containerelement~ViewContainerElement},
	* - `<b>` have type of {@link module:engine/view/attributeelement~ViewAttributeElement},
	* - `<span>` have type of {@link module:engine/view/uielement~ViewUIElement}.
	*
	* @returns Shrunk range.
	*/
	getTrimmed() {
		let start = this.start.getLastMatchingPosition(enlargeTrimSkip);
		if (start.isAfter(this.end) || start.isEqual(this.end)) return new ViewRange(start, start);
		let end = this.end.getLastMatchingPosition(enlargeTrimSkip, { direction: "backward" });
		const nodeAfterStart = start.nodeAfter;
		const nodeBeforeEnd = end.nodeBefore;
		if (nodeAfterStart && nodeAfterStart.is("$text")) start = new ViewPosition(nodeAfterStart, 0);
		if (nodeBeforeEnd && nodeBeforeEnd.is("$text")) end = new ViewPosition(nodeBeforeEnd, nodeBeforeEnd.data.length);
		return new ViewRange(start, end);
	}
	/**
	* Two ranges are equal if their start and end positions are equal.
	*
	* @param otherRange Range to compare with.
	* @returns `true` if ranges are equal, `false` otherwise
	*/
	isEqual(otherRange) {
		return this == otherRange || this.start.isEqual(otherRange.start) && this.end.isEqual(otherRange.end);
	}
	/**
	* Checks whether this range contains given {@link module:engine/view/position~ViewPosition position}.
	*
	* @param position Position to check.
	* @returns `true` if given {@link module:engine/view/position~ViewPosition position} is contained in this range, `false` otherwise.
	*/
	containsPosition(position) {
		return position.isAfter(this.start) && position.isBefore(this.end);
	}
	/**
	* Checks whether this range contains given {@link module:engine/view/range~ViewRange range}.
	*
	* @param otherRange Range to check.
	* @param loose Whether the check is loose or strict. If the check is strict (`false`), compared range cannot
	* start or end at the same position as this range boundaries. If the check is loose (`true`), compared range can start, end or
	* even be equal to this range. Note that collapsed ranges are always compared in strict mode.
	* @returns `true` if given {@link module:engine/view/range~ViewRange range} boundaries are contained by this range, `false`
	* otherwise.
	*/
	containsRange(otherRange, loose = false) {
		if (otherRange.isCollapsed) loose = false;
		const containsStart = this.containsPosition(otherRange.start) || loose && this.start.isEqual(otherRange.start);
		const containsEnd = this.containsPosition(otherRange.end) || loose && this.end.isEqual(otherRange.end);
		return containsStart && containsEnd;
	}
	/**
	* Computes which part(s) of this {@link module:engine/view/range~ViewRange range} is not a part of given
	* {@link module:engine/view/range~ViewRange range}.
	* Returned array contains zero, one or two {@link module:engine/view/range~ViewRange ranges}.
	*
	* Examples:
	*
	* ```ts
	* let foo = downcastWriter.createText( 'foo' );
	* let img = downcastWriter.createContainerElement( 'img' );
	* let bar = downcastWriter.createText( 'bar' );
	* let p = downcastWriter.createContainerElement( 'p', null, [ foo, img, bar ] );
	*
	* let range = view.createRange( view.createPositionAt( foo, 2 ), view.createPositionAt( bar, 1 ); // "o", img, "b" are in range.
	* let otherRange = view.createRange( // "oo", img, "ba" are in range.
	* 	view.createPositionAt( foo, 1 ),
	* 	view.createPositionAt( bar, 2 )
	* );
	* let transformed = range.getDifference( otherRange );
	* // transformed array has no ranges because `otherRange` contains `range`
	*
	* otherRange = view.createRange( view.createPositionAt( foo, 1 ), view.createPositionAt( p, 2 ); // "oo", img are in range.
	* transformed = range.getDifference( otherRange );
	* // transformed array has one range: from ( p, 2 ) to ( bar, 1 )
	*
	* otherRange = view.createRange( view.createPositionAt( p, 1 ), view.createPositionAt( p, 2 ) ); // img is in range.
	* transformed = range.getDifference( otherRange );
	* // transformed array has two ranges: from ( foo, 1 ) to ( p, 1 ) and from ( p, 2 ) to ( bar, 1 )
	* ```
	*
	* @param otherRange Range to differentiate against.
	* @returns The difference between ranges.
	*/
	getDifference(otherRange) {
		const ranges = [];
		if (this.isIntersecting(otherRange)) {
			if (this.containsPosition(otherRange.start)) ranges.push(new ViewRange(this.start, otherRange.start));
			if (this.containsPosition(otherRange.end)) ranges.push(new ViewRange(otherRange.end, this.end));
		} else ranges.push(this.clone());
		return ranges;
	}
	/**
	* Returns an intersection of this {@link module:engine/view/range~ViewRange range}
	* and given {@link module:engine/view/range~ViewRange range}.
	* Intersection is a common part of both of those ranges. If ranges has no common part, returns `null`.
	*
	* Examples:
	*
	* ```ts
	* let foo = downcastWriter.createText( 'foo' );
	* let img = downcastWriter.createContainerElement( 'img' );
	* let bar = downcastWriter.createText( 'bar' );
	* let p = downcastWriter.createContainerElement( 'p', null, [ foo, img, bar ] );
	*
	* let range = view.createRange( view.createPositionAt( foo, 2 ), view.createPositionAt( bar, 1 ); // "o", img, "b" are in range.
	* let otherRange = view.createRange( view.createPositionAt( foo, 1 ), view.createPositionAt( p, 2 ); // "oo", img are in range.
	* let transformed = range.getIntersection( otherRange ); // range from ( foo, 1 ) to ( p, 2 ).
	*
	* otherRange = view.createRange( view.createPositionAt( bar, 1 ), view.createPositionAt( bar, 3 ); "ar" is in range.
	* transformed = range.getIntersection( otherRange ); // null - no common part.
	* ```
	*
	* @param otherRange Range to check for intersection.
	* @returns A common part of given ranges or `null` if ranges have no common part.
	*/
	getIntersection(otherRange) {
		if (this.isIntersecting(otherRange)) {
			let commonRangeStart = this.start;
			let commonRangeEnd = this.end;
			if (this.containsPosition(otherRange.start)) commonRangeStart = otherRange.start;
			if (this.containsPosition(otherRange.end)) commonRangeEnd = otherRange.end;
			return new ViewRange(commonRangeStart, commonRangeEnd);
		}
		return null;
	}
	/**
	* Creates a {@link module:engine/view/treewalker~ViewTreeWalker TreeWalker} instance with this range as a boundary.
	*
	* @param options Object with configuration options. See {@link module:engine/view/treewalker~ViewTreeWalker}.
	*/
	getWalker(options = {}) {
		options.boundaries = this;
		return new ViewTreeWalker(options);
	}
	/**
	* Returns a {@link module:engine/view/node~ViewNode} or {@link module:engine/view/documentfragment~ViewDocumentFragment}
	* which is a common ancestor of range's both ends (in which the entire range is contained).
	*/
	getCommonAncestor() {
		return this.start.getCommonAncestor(this.end);
	}
	/**
	* Returns an {@link module:engine/view/element~ViewElement Element} contained by the range.
	* The element will be returned when it is the **only** node within the range and **fully–contained**
	* at the same time.
	*/
	getContainedElement() {
		if (this.isCollapsed) return null;
		let nodeAfterStart = this.start.nodeAfter;
		let nodeBeforeEnd = this.end.nodeBefore;
		if (this.start.parent.is("$text") && this.start.isAtEnd && this.start.parent.nextSibling) nodeAfterStart = this.start.parent.nextSibling;
		if (this.end.parent.is("$text") && this.end.isAtStart && this.end.parent.previousSibling) nodeBeforeEnd = this.end.parent.previousSibling;
		if (nodeAfterStart && nodeAfterStart.is("element") && nodeAfterStart === nodeBeforeEnd) return nodeAfterStart;
		return null;
	}
	/**
	* Clones this range.
	*/
	clone() {
		return new ViewRange(this.start, this.end);
	}
	/**
	* Returns an iterator that iterates over all {@link module:engine/view/item~ViewItem view items} that are in this range and returns
	* them.
	*
	* This method uses {@link module:engine/view/treewalker~ViewTreeWalker} with `boundaries` set to this range
	* and `ignoreElementEnd` option set to `true`. However it returns only {@link module:engine/view/item~ViewItem items},
	* not {@link module:engine/view/treewalker~ViewTreeWalkerValue}.
	*
	* You may specify additional options for the tree walker. See {@link module:engine/view/treewalker~ViewTreeWalker} for
	* a full list of available options.
	*
	* @param options Object with configuration options. See {@link module:engine/view/treewalker~ViewTreeWalker}.
	*/
	*getItems(options = {}) {
		options.boundaries = this;
		options.ignoreElementEnd = true;
		const treeWalker = new ViewTreeWalker(options);
		for (const value of treeWalker) yield value.item;
	}
	/**
	* Returns an iterator that iterates over all {@link module:engine/view/position~ViewPosition positions} that are boundaries or
	* contained in this range.
	*
	* This method uses {@link module:engine/view/treewalker~ViewTreeWalker} with `boundaries` set to this range. However it returns only
	* {@link module:engine/view/position~ViewPosition positions}, not {@link module:engine/view/treewalker~ViewTreeWalkerValue}.
	*
	* You may specify additional options for the tree walker. See {@link module:engine/view/treewalker~ViewTreeWalker} for
	* a full list of available options.
	*
	* @param options Object with configuration options. See {@link module:engine/view/treewalker~ViewTreeWalker}.
	*/
	*getPositions(options = {}) {
		options.boundaries = this;
		const treeWalker = new ViewTreeWalker(options);
		yield treeWalker.position;
		for (const value of treeWalker) yield value.nextPosition;
	}
	/**
	* Checks and returns whether this range intersects with the given range.
	*
	* @param otherRange Range to compare with.
	* @returns True if ranges intersect.
	*/
	isIntersecting(otherRange) {
		return this.start.isBefore(otherRange.end) && this.end.isAfter(otherRange.start);
	}
	/**
	* Converts `ViewRange` instance to plain object and returns it.
	*
	* @returns `ViewRange` instance converted to plain object.
	*/
	toJSON() {
		return {
			start: this.start.toJSON(),
			end: this.end.toJSON()
		};
	}
	/**
	* Creates a range from the given parents and offsets.
	*
	* @internal
	* @param startElement Start position parent element.
	* @param startOffset Start position offset.
	* @param endElement End position parent element.
	* @param endOffset End position offset.
	* @returns Created range.
	*/
	static _createFromParentsAndOffsets(startElement, startOffset, endElement, endOffset) {
		return new this(new ViewPosition(startElement, startOffset), new ViewPosition(endElement, endOffset));
	}
	/**
	* Creates a new range, spreading from specified {@link module:engine/view/position~ViewPosition position} to a position moved by
	* given `shift`. If `shift` is a negative value, shifted position is treated as the beginning of the range.
	*
	* @internal
	* @param position Beginning of the range.
	* @param shift How long the range should be.
	*/
	static _createFromPositionAndShift(position, shift) {
		const start = position;
		const end = position.getShiftedBy(shift);
		return shift > 0 ? new this(start, end) : new this(end, start);
	}
	/**
	* Creates a range inside an {@link module:engine/view/element~ViewElement element} which starts before the first child of
	* that element and ends after the last child of that element.
	*
	* @internal
	* @param element Element which is a parent for the range.
	*/
	static _createIn(element) {
		return this._createFromParentsAndOffsets(element, 0, element, element.childCount);
	}
	/**
	* Creates a range that starts before given {@link module:engine/view/item~ViewItem view item} and ends after it.
	*
	* @internal
	*/
	static _createOn(item) {
		const size = item.is("$textProxy") ? item.offsetSize : 1;
		return this._createFromPositionAndShift(ViewPosition._createBefore(item), size);
	}
};
ViewRange.prototype.is = function(type) {
	return type === "range" || type === "view:range";
};
/**
* Function used by getEnlarged and getTrimmed methods.
*/
function enlargeTrimSkip(value) {
	if (value.item.is("attributeElement") || value.item.is("uiElement")) return true;
	return false;
}

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* @module engine/view/selection
*/
const ViewSelectionBase = /* #__PURE__ */ EmitterMixin(ViewTypeCheckable);
/**
* Class representing an arbirtary selection in the view.
* See also {@link module:engine/view/documentselection~ViewDocumentSelection}.
*
* New selection instances can be created via the constructor or one these methods:
*
* * {@link module:engine/view/view~EditingView#createSelection `View#createSelection()`},
* * {@link module:engine/view/upcastwriter~ViewUpcastWriter#createSelection `UpcastWriter#createSelection()`}.
*
* A selection can consist of {@link module:engine/view/range~ViewRange ranges} that can be set by using
* the {@link module:engine/view/selection~ViewSelection#setTo `Selection#setTo()`} method.
*/
var ViewSelection = class ViewSelection extends ViewSelectionBase {
	/**
	* Stores all ranges that are selected.
	*/
	_ranges;
	/**
	* Specifies whether the last added range was added as a backward or forward range.
	*/
	_lastRangeBackward;
	/**
	* Specifies whether selection instance is fake.
	*/
	_isFake;
	/**
	* Fake selection's label.
	*/
	_fakeSelectionLabel;
	/**
	* Creates new selection instance.
	*
	* **Note**: The selection constructor is available as a factory method:
	*
	* * {@link module:engine/view/view~EditingView#createSelection `View#createSelection()`},
	* * {@link module:engine/view/upcastwriter~ViewUpcastWriter#createSelection `UpcastWriter#createSelection()`}.
	*
	* ```ts
	* // Creates empty selection without ranges.
	* const selection = writer.createSelection();
	*
	* // Creates selection at the given range.
	* const range = writer.createRange( start, end );
	* const selection = writer.createSelection( range );
	*
	* // Creates selection at the given ranges
	* const ranges = [ writer.createRange( start1, end2 ), writer.createRange( star2, end2 ) ];
	* const selection = writer.createSelection( ranges );
	*
	* // Creates selection from the other selection.
	* const otherSelection = writer.createSelection();
	* const selection = writer.createSelection( otherSelection );
	*
	* // Creates selection from the document selection.
	* const selection = writer.createSelection( editor.editing.view.document.selection );
	*
	* // Creates selection at the given position.
	* const position = writer.createPositionFromPath( root, path );
	* const selection = writer.createSelection( position );
	*
	* // Creates collapsed selection at the position of given item and offset.
	* const paragraph = writer.createContainerElement( 'paragraph' );
	* const selection = writer.createSelection( paragraph, offset );
	*
	* // Creates a range inside an {@link module:engine/view/element~ViewElement element} which starts before the
	* // first child of that element and ends after the last child of that element.
	* const selection = writer.createSelection( paragraph, 'in' );
	*
	* // Creates a range on an {@link module:engine/view/item~ViewItem item} which starts before the item and ends
	* // just after the item.
	* const selection = writer.createSelection( paragraph, 'on' );
	* ```
	*
	* `Selection`'s constructor allow passing additional options (`backward`, `fake` and `label`) as the last argument.
	*
	* ```ts
	* // Creates backward selection.
	* const selection = writer.createSelection( range, { backward: true } );
	* ```
	*
	* Fake selection does not render as browser native selection over selected elements and is hidden to the user.
	* This way, no native selection UI artifacts are displayed to the user and selection over elements can be
	* represented in other way, for example by applying proper CSS class.
	*
	* Additionally fake's selection label can be provided. It will be used to describe fake selection in DOM
	* (and be  properly handled by screen readers).
	*
	* ```ts
	* // Creates fake selection with label.
	* const selection = writer.createSelection( range, { fake: true, label: 'foo' } );
	* ```
	*
	* @internal
	*/
	constructor(...args) {
		super();
		this._ranges = [];
		this._lastRangeBackward = false;
		this._isFake = false;
		this._fakeSelectionLabel = "";
		if (args.length) this.setTo(...args);
	}
	/**
	* Returns true if selection instance is marked as `fake`.
	*
	* @see #setTo
	*/
	get isFake() {
		return this._isFake;
	}
	/**
	* Returns fake selection label.
	*
	* @see #setTo
	*/
	get fakeSelectionLabel() {
		return this._fakeSelectionLabel;
	}
	/**
	* Selection anchor. Anchor may be described as a position where the selection starts. Together with
	* {@link #focus focus} they define the direction of selection, which is important
	* when expanding/shrinking selection. Anchor is always the start or end of the most recent added range.
	* It may be a bit unintuitive when there are multiple ranges in selection.
	*
	* @see #focus
	*/
	get anchor() {
		if (!this._ranges.length) return null;
		const range = this._ranges[this._ranges.length - 1];
		return (this._lastRangeBackward ? range.end : range.start).clone();
	}
	/**
	* Selection focus. Focus is a position where the selection ends.
	*
	* @see #anchor
	*/
	get focus() {
		if (!this._ranges.length) return null;
		const range = this._ranges[this._ranges.length - 1];
		return (this._lastRangeBackward ? range.start : range.end).clone();
	}
	/**
	* Returns whether the selection is collapsed. Selection is collapsed when there is exactly one range which is
	* collapsed.
	*/
	get isCollapsed() {
		return this.rangeCount === 1 && this._ranges[0].isCollapsed;
	}
	/**
	* Returns number of ranges in selection.
	*/
	get rangeCount() {
		return this._ranges.length;
	}
	/**
	* Specifies whether the {@link #focus} precedes {@link #anchor}.
	*/
	get isBackward() {
		return !this.isCollapsed && this._lastRangeBackward;
	}
	/**
	* {@link module:engine/view/editableelement~ViewEditableElement ViewEditableElement} instance that contains this selection, or `null`
	* if the selection is not inside an editable element.
	*/
	get editableElement() {
		if (this.anchor) return this.anchor.editableElement;
		return null;
	}
	/**
	* Returns an iterable that contains copies of all ranges added to the selection.
	*/
	*getRanges() {
		for (const range of this._ranges) yield range.clone();
	}
	/**
	* Returns copy of the first range in the selection. First range is the one which
	* {@link module:engine/view/range~ViewRange#start start} position
	* {@link module:engine/view/position~ViewPosition#isBefore is before} start
	* position of all other ranges (not to confuse with the first range added to the selection).
	* Returns `null` if no ranges are added to selection.
	*/
	getFirstRange() {
		let first = null;
		for (const range of this._ranges) if (!first || range.start.isBefore(first.start)) first = range;
		return first ? first.clone() : null;
	}
	/**
	* Returns copy of the last range in the selection. Last range is the one which {@link module:engine/view/range~ViewRange#end end}
	* position {@link module:engine/view/position~ViewPosition#isAfter is after} end position of all other ranges (not to confuse
	* with the last range added to the selection). Returns `null` if no ranges are added to selection.
	*/
	getLastRange() {
		let last = null;
		for (const range of this._ranges) if (!last || range.end.isAfter(last.end)) last = range;
		return last ? last.clone() : null;
	}
	/**
	* Returns copy of the first position in the selection. First position is the position that
	* {@link module:engine/view/position~ViewPosition#isBefore is before} any other position in the selection ranges.
	* Returns `null` if no ranges are added to selection.
	*/
	getFirstPosition() {
		const firstRange = this.getFirstRange();
		return firstRange ? firstRange.start.clone() : null;
	}
	/**
	* Returns copy of the last position in the selection. Last position is the position that
	* {@link module:engine/view/position~ViewPosition#isAfter is after} any other position in the selection ranges.
	* Returns `null` if no ranges are added to selection.
	*/
	getLastPosition() {
		const lastRange = this.getLastRange();
		return lastRange ? lastRange.end.clone() : null;
	}
	/**
	* Checks whether, this selection is equal to given selection. Selections are equal if they have same directions,
	* same number of ranges and all ranges from one selection equal to a range from other selection.
	*
	* @param otherSelection Selection to compare with.
	* @returns `true` if selections are equal, `false` otherwise.
	*/
	isEqual(otherSelection) {
		if (this.isFake != otherSelection.isFake) return false;
		if (this.isFake && this.fakeSelectionLabel != otherSelection.fakeSelectionLabel) return false;
		if (this.rangeCount != otherSelection.rangeCount) return false;
		else if (this.rangeCount === 0) return true;
		if (!this.anchor.isEqual(otherSelection.anchor) || !this.focus.isEqual(otherSelection.focus)) return false;
		for (const thisRange of this._ranges) {
			let found = false;
			for (const otherRange of otherSelection._ranges) if (thisRange.isEqual(otherRange)) {
				found = true;
				break;
			}
			if (!found) return false;
		}
		return true;
	}
	/**
	* Checks whether this selection is similar to given selection. Selections are similar if they have same directions, same
	* number of ranges, and all {@link module:engine/view/range~ViewRange#getTrimmed trimmed} ranges from one selection are
	* equal to any trimmed range from other selection.
	*
	* @param otherSelection Selection to compare with.
	* @returns `true` if selections are similar, `false` otherwise.
	*/
	isSimilar(otherSelection) {
		if (this.isBackward != otherSelection.isBackward) return false;
		const numOfRangesA = count(this.getRanges());
		if (numOfRangesA != count(otherSelection.getRanges())) return false;
		if (numOfRangesA == 0) return true;
		for (let rangeA of this.getRanges()) {
			rangeA = rangeA.getTrimmed();
			let found = false;
			for (let rangeB of otherSelection.getRanges()) {
				rangeB = rangeB.getTrimmed();
				if (rangeA.start.isEqual(rangeB.start) && rangeA.end.isEqual(rangeB.end)) {
					found = true;
					break;
				}
			}
			if (!found) return false;
		}
		return true;
	}
	/**
	* Returns the selected element. {@link module:engine/view/element~ViewElement Element} is considered as selected if there is only
	* one range in the selection, and that range contains exactly one element.
	* Returns `null` if there is no selected element.
	*/
	getSelectedElement() {
		if (this.rangeCount !== 1) return null;
		return this.getFirstRange().getContainedElement();
	}
	/**
	* Sets this selection's ranges and direction to the specified location based on the given
	* {@link module:engine/view/selection~ViewSelectable selectable}.
	*
	* ```ts
	* // Sets selection to the given range.
	* const range = writer.createRange( start, end );
	* selection.setTo( range );
	*
	* // Sets selection to given ranges.
	* const ranges = [ writer.createRange( start1, end2 ), writer.createRange( star2, end2 ) ];
	* selection.setTo( range );
	*
	* // Sets selection to the other selection.
	* const otherSelection = writer.createSelection();
	* selection.setTo( otherSelection );
	*
	* // Sets selection to contents of ViewDocumentSelection.
	* selection.setTo( editor.editing.view.document.selection );
	*
	* // Sets collapsed selection at the given position.
	* const position = writer.createPositionAt( root, path );
	* selection.setTo( position );
	*
	* // Sets collapsed selection at the position of given item and offset.
	* selection.setTo( paragraph, offset );
	* ```
	*
	* Creates a range inside an {@link module:engine/view/element~ViewElement element} which starts before the first child of
	* that element and ends after the last child of that element.
	*
	* ```ts
	* selection.setTo( paragraph, 'in' );
	* ```
	*
	* Creates a range on an {@link module:engine/view/item~ViewItem item} which starts before the item and ends just after the item.
	*
	* ```ts
	* selection.setTo( paragraph, 'on' );
	*
	* // Clears selection. Removes all ranges.
	* selection.setTo( null );
	* ```
	*
	* `Selection#setTo()` method allow passing additional options (`backward`, `fake` and `label`) as the last argument.
	*
	* ```ts
	* // Sets selection as backward.
	* selection.setTo( range, { backward: true } );
	* ```
	*
	* Fake selection does not render as browser native selection over selected elements and is hidden to the user.
	* This way, no native selection UI artifacts are displayed to the user and selection over elements can be
	* represented in other way, for example by applying proper CSS class.
	*
	* Additionally fake's selection label can be provided. It will be used to describe fake selection in DOM
	* (and be  properly handled by screen readers).
	*
	* ```ts
	* // Creates fake selection with label.
	* selection.setTo( range, { fake: true, label: 'foo' } );
	* ```
	*
	* @fires change
	*/
	setTo(...args) {
		let [selectable, placeOrOffset, options] = args;
		if (typeof placeOrOffset == "object") {
			options = placeOrOffset;
			placeOrOffset = void 0;
		}
		if (selectable === null) {
			this._setRanges([]);
			this._setFakeOptions(options);
		} else if (selectable instanceof ViewSelection || selectable instanceof ViewDocumentSelection) {
			this._setRanges(selectable.getRanges(), selectable.isBackward);
			this._setFakeOptions({
				fake: selectable.isFake,
				label: selectable.fakeSelectionLabel
			});
		} else if (selectable instanceof ViewRange) {
			this._setRanges([selectable], options && options.backward);
			this._setFakeOptions(options);
		} else if (selectable instanceof ViewPosition) {
			this._setRanges([new ViewRange(selectable)]);
			this._setFakeOptions(options);
		} else if (selectable instanceof ViewNode) {
			const backward = !!options && !!options.backward;
			let range;
			if (placeOrOffset === void 0)
 /**
			* selection.setTo requires the second parameter when the first parameter is a node.
			*
			* @error view-selection-setto-required-second-parameter
			*/
			throw new CKEditorError("view-selection-setto-required-second-parameter", this);
			else if (placeOrOffset == "in") range = ViewRange._createIn(selectable);
			else if (placeOrOffset == "on") range = ViewRange._createOn(selectable);
			else range = new ViewRange(ViewPosition._createAt(selectable, placeOrOffset));
			this._setRanges([range], backward);
			this._setFakeOptions(options);
		} else if (isIterable(selectable)) {
			this._setRanges(selectable, options && options.backward);
			this._setFakeOptions(options);
		} else
 /**
		* Cannot set selection to given place.
		*
		* @error view-selection-setto-not-selectable
		*/
		throw new CKEditorError("view-selection-setto-not-selectable", this);
		this.fire("change");
	}
	/**
	* Moves {@link #focus} to the specified location.
	*
	* The location can be specified in the same form as
	* {@link module:engine/view/view~EditingView#createPositionAt view.createPositionAt()}
	* parameters.
	*
	* @fires change
	* @param offset Offset or one of the flags. Used only when first parameter is a {@link module:engine/view/item~ViewItem view item}.
	*/
	setFocus(itemOrPosition, offset) {
		if (this.anchor === null)
 /**
		* Cannot set selection focus if there are no ranges in selection.
		*
		* @error view-selection-setfocus-no-ranges
		*/
		throw new CKEditorError("view-selection-setfocus-no-ranges", this);
		const newFocus = ViewPosition._createAt(itemOrPosition, offset);
		if (newFocus.compareWith(this.focus) == "same") return;
		const anchor = this.anchor;
		this._ranges.pop();
		if (newFocus.compareWith(anchor) == "before") this._addRange(new ViewRange(newFocus, anchor), true);
		else this._addRange(new ViewRange(anchor, newFocus));
		this.fire("change");
	}
	/**
	* Converts `ViewSelection` instance to plain object and returns it.
	*
	* @returns `ViewSelection` instance converted to plain object.
	*/
	toJSON() {
		const json = { ranges: Array.from(this.getRanges()).map((range) => range.toJSON()) };
		if (this.isBackward) json.isBackward = true;
		if (this.isFake) json.isFake = true;
		return json;
	}
	/**
	* Replaces all ranges that were added to the selection with given array of ranges. Last range of the array
	* is treated like the last added range and is used to set {@link #anchor anchor} and {@link #focus focus}.
	* Accepts a flag describing in which way the selection is made.
	*
	* @param newRanges Iterable object of ranges to set.
	* @param isLastBackward Flag describing if last added range was selected forward - from start to end
	* (`false`) or backward - from end to start (`true`). Defaults to `false`.
	*/
	_setRanges(newRanges, isLastBackward = false) {
		newRanges = Array.from(newRanges);
		this._ranges = [];
		for (const range of newRanges) this._addRange(range);
		this._lastRangeBackward = !!isLastBackward;
	}
	/**
	* Sets this selection instance to be marked as `fake`. A fake selection does not render as browser native selection
	* over selected elements and is hidden to the user. This way, no native selection UI artifacts are displayed to
	* the user and selection over elements can be represented in other way, for example by applying proper CSS class.
	*
	* Additionally fake's selection label can be provided. It will be used to describe fake selection in DOM (and be
	* properly handled by screen readers).
	*/
	_setFakeOptions(options = {}) {
		this._isFake = !!options.fake;
		this._fakeSelectionLabel = options.fake ? options.label || "" : "";
	}
	/**
	* Adds a range to the selection. Added range is copied. This means that passed range is not saved in the
	* selection instance and you can safely operate on it.
	*
	* Accepts a flag describing in which way the selection is made - passed range might be selected from
	* {@link module:engine/view/range~ViewRange#start start} to {@link module:engine/view/range~ViewRange#end end}
	* or from {@link module:engine/view/range~ViewRange#end end} to {@link module:engine/view/range~ViewRange#start start}.
	* The flag is used to set {@link #anchor anchor} and {@link #focus focus} properties.
	*
	* Throws {@link module:utils/ckeditorerror~CKEditorError CKEditorError} `view-selection-range-intersects` if added range intersects
	* with ranges already stored in Selection instance.
	*/
	_addRange(range, isBackward = false) {
		if (!(range instanceof ViewRange))
 /**
		* Selection range set to an object that is not an instance of {@link module:engine/view/range~ViewRange}.
		*
		* @error view-selection-add-range-not-range
		*/
		throw new CKEditorError("view-selection-add-range-not-range", this);
		this._pushRange(range);
		this._lastRangeBackward = !!isBackward;
	}
	/**
	* Adds range to selection - creates copy of given range so it can be safely used and modified.
	*
	* Throws {@link module:utils/ckeditorerror~CKEditorError CKEditorError} `view-selection-range-intersects` if added range intersects
	* with ranges already stored in selection instance.
	*/
	_pushRange(range) {
		for (const storedRange of this._ranges) if (range.isIntersecting(storedRange))
 /**
		* Trying to add a range that intersects with another range from selection.
		*
		* @error view-selection-range-intersects
		* @param {module:engine/view/range~ViewRange} addedRange Range that was added to the selection.
		* @param {module:engine/view/range~ViewRange} intersectingRange Range from selection that intersects with `addedRange`.
		*/
		throw new CKEditorError("view-selection-range-intersects", this, {
			addedRange: range,
			intersectingRange: storedRange
		});
		this._ranges.push(new ViewRange(range.start, range.end));
	}
};
ViewSelection.prototype.is = function(type) {
	return type === "selection" || type === "view:selection";
};

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* @module engine/view/documentselection
*/
const ViewDocumentSelectionBase = /* #__PURE__ */ EmitterMixin(ViewTypeCheckable);
/**
* Class representing the document selection in the view.
*
* Its instance is available in {@link module:engine/view/document~ViewDocument#selection `Document#selection`}.
*
* It is similar to {@link module:engine/view/selection~ViewSelection} but
* it has a read-only API and can be modified only by the writer available in
* the {@link module:engine/view/view~EditingView#change `View#change()`} block
* (so via {@link module:engine/view/downcastwriter~ViewDowncastWriter#setSelection `ViewDowncastWriter#setSelection()`}).
*/
var ViewDocumentSelection = class extends ViewDocumentSelectionBase {
	/**
	* Selection is used internally (`ViewDocumentSelection` is a proxy to that selection).
	*/
	_selection;
	constructor(...args) {
		super();
		this._selection = new ViewSelection();
		this._selection.delegate("change").to(this);
		if (args.length) this._selection.setTo(...args);
	}
	/**
	* Returns true if selection instance is marked as `fake`.
	*
	* @see #_setTo
	*/
	get isFake() {
		return this._selection.isFake;
	}
	/**
	* Returns fake selection label.
	*
	* @see #_setTo
	*/
	get fakeSelectionLabel() {
		return this._selection.fakeSelectionLabel;
	}
	/**
	* Selection anchor. Anchor may be described as a position where the selection starts. Together with
	* {@link #focus focus} they define the direction of selection, which is important
	* when expanding/shrinking selection. Anchor is always the start or end of the most recent added range.
	* It may be a bit unintuitive when there are multiple ranges in selection.
	*
	* @see #focus
	*/
	get anchor() {
		return this._selection.anchor;
	}
	/**
	* Selection focus. Focus is a position where the selection ends.
	*
	* @see #anchor
	*/
	get focus() {
		return this._selection.focus;
	}
	/**
	* Returns whether the selection is collapsed. Selection is collapsed when there is exactly one range which is
	* collapsed.
	*/
	get isCollapsed() {
		return this._selection.isCollapsed;
	}
	/**
	* Returns number of ranges in selection.
	*/
	get rangeCount() {
		return this._selection.rangeCount;
	}
	/**
	* Specifies whether the {@link #focus} precedes {@link #anchor}.
	*/
	get isBackward() {
		return this._selection.isBackward;
	}
	/**
	* {@link module:engine/view/editableelement~ViewEditableElement ViewEditableElement} instance that contains this selection, or `null`
	* if the selection is not inside an editable element.
	*/
	get editableElement() {
		return this._selection.editableElement;
	}
	/**
	* Used for the compatibility with the {@link module:engine/view/selection~ViewSelection#isEqual} method.
	*
	* @internal
	*/
	get _ranges() {
		return this._selection._ranges;
	}
	/**
	* Returns an iterable that contains copies of all ranges added to the selection.
	*/
	*getRanges() {
		yield* this._selection.getRanges();
	}
	/**
	* Returns copy of the first range in the selection. First range is the one which
	* {@link module:engine/view/range~ViewRange#start start} position
	* {@link module:engine/view/position~ViewPosition#isBefore is before} start
	* position of all other ranges (not to confuse with the first range added to the selection).
	* Returns `null` if no ranges are added to selection.
	*/
	getFirstRange() {
		return this._selection.getFirstRange();
	}
	/**
	* Returns copy of the last range in the selection. Last range is the one which {@link module:engine/view/range~ViewRange#end end}
	* position {@link module:engine/view/position~ViewPosition#isAfter is after} end position of all other ranges (not to confuse
	* with the last range added to the selection). Returns `null` if no ranges are added to selection.
	*/
	getLastRange() {
		return this._selection.getLastRange();
	}
	/**
	* Returns copy of the first position in the selection. First position is the position that
	* {@link module:engine/view/position~ViewPosition#isBefore is before} any other position in the selection ranges.
	* Returns `null` if no ranges are added to selection.
	*/
	getFirstPosition() {
		return this._selection.getFirstPosition();
	}
	/**
	* Returns copy of the last position in the selection. Last position is the position that
	* {@link module:engine/view/position~ViewPosition#isAfter is after} any other position in the selection ranges.
	* Returns `null` if no ranges are added to selection.
	*/
	getLastPosition() {
		return this._selection.getLastPosition();
	}
	/**
	* Returns the selected element. {@link module:engine/view/element~ViewElement Element} is considered as selected if there is only
	* one range in the selection, and that range contains exactly one element.
	* Returns `null` if there is no selected element.
	*/
	getSelectedElement() {
		return this._selection.getSelectedElement();
	}
	/**
	* Checks whether, this selection is equal to given selection. Selections are equal if they have same directions,
	* same number of ranges and all ranges from one selection equal to a range from other selection.
	*
	* @param otherSelection Selection to compare with.
	* @returns `true` if selections are equal, `false` otherwise.
	*/
	isEqual(otherSelection) {
		return this._selection.isEqual(otherSelection);
	}
	/**
	* Checks whether this selection is similar to given selection. Selections are similar if they have same directions, same
	* number of ranges, and all {@link module:engine/view/range~ViewRange#getTrimmed trimmed} ranges from one selection are
	* equal to any trimmed range from other selection.
	*
	* @param otherSelection Selection to compare with.
	* @returns `true` if selections are similar, `false` otherwise.
	*/
	isSimilar(otherSelection) {
		return this._selection.isSimilar(otherSelection);
	}
	/**
	* Converts `ViewDocumentSelection` instance to plain object and returns it.
	*
	* @returns `ViewDocumentSelection` instance converted to plain object.
	*/
	toJSON() {
		return this._selection.toJSON();
	}
	/**
	* Sets this selection's ranges and direction to the specified location based on the given
	* {@link module:engine/view/selection~ViewSelectable selectable}.
	*
	* ```ts
	* // Sets selection to the given range.
	* const range = writer.createRange( start, end );
	* documentSelection._setTo( range );
	*
	* // Sets selection to given ranges.
	* const ranges = [ writer.createRange( start1, end2 ), writer.createRange( start2, end2 ) ];
	* documentSelection._setTo( range );
	*
	* // Sets selection to the other selection.
	* const otherSelection = writer.createSelection();
	* documentSelection._setTo( otherSelection );
	*
	* // Sets collapsed selection at the given position.
	* const position = writer.createPositionAt( root, offset );
	* documentSelection._setTo( position );
	*
	* // Sets collapsed selection at the position of given item and offset.
	* documentSelection._setTo( paragraph, offset );
	* ```
	*
	* Creates a range inside an {@link module:engine/view/element~ViewElement element} which starts before the first child of
	* that element and ends after the last child of that element.
	*
	* ```ts
	* documentSelection._setTo( paragraph, 'in' );
	* ```
	*
	* Creates a range on an {@link module:engine/view/item~ViewItem item} which starts before the item and ends just after the item.
	*
	* ```ts
	* documentSelection._setTo( paragraph, 'on' );
	*
	* // Clears selection. Removes all ranges.
	* documentSelection._setTo( null );
	* ```
	*
	* `Selection#_setTo()` method allow passing additional options (`backward`, `fake` and `label`) as the last argument.
	*
	* ```ts
	* // Sets selection as backward.
	* documentSelection._setTo( range, { backward: true } );
	* ```
	*
	* Fake selection does not render as browser native selection over selected elements and is hidden to the user.
	* This way, no native selection UI artifacts are displayed to the user and selection over elements can be
	* represented in other way, for example by applying proper CSS class.
	*
	* Additionally fake's selection label can be provided. It will be used to des cribe fake selection in DOM
	* (and be  properly handled by screen readers).
	*
	* ```ts
	* // Creates fake selection with label.
	* documentSelection._setTo( range, { fake: true, label: 'foo' } );
	* ```
	*
	* @internal
	* @fires change
	*/
	_setTo(...args) {
		this._selection.setTo(...args);
	}
	/**
	* Moves {@link #focus} to the specified location.
	*
	* The location can be specified in the same form as
	* {@link module:engine/view/view~EditingView#createPositionAt view.createPositionAt()}
	* parameters.
	*
	* @internal
	* @fires change
	* @param offset Offset or one of the flags. Used only when first parameter is a {@link module:engine/view/item~ViewItem view item}.
	*/
	_setFocus(itemOrPosition, offset) {
		this._selection.setFocus(itemOrPosition, offset);
	}
};
ViewDocumentSelection.prototype.is = function(type) {
	return type === "selection" || type == "documentSelection" || type == "view:selection" || type == "view:documentSelection";
};

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* @module engine/view/observer/bubblingeventinfo
*/
/**
* The event object passed to bubbling event callbacks. It is used to provide information about the event as well as a tool to
* manipulate it.
*/
var BubblingEventInfo = class extends EventInfo {
	/**
	* The view range that the bubbling should start from.
	*/
	startRange;
	/**
	* The current event phase.
	*/
	_eventPhase;
	/**
	* The current bubbling target.
	*/
	_currentTarget;
	/**
	* @param source The emitter.
	* @param name The event name.
	* @param startRange The view range that the bubbling should start from.
	*/
	constructor(source, name, startRange) {
		super(source, name);
		this.startRange = startRange;
		this._eventPhase = "none";
		this._currentTarget = null;
	}
	/**
	* The current event phase.
	*/
	get eventPhase() {
		return this._eventPhase;
	}
	/**
	* The current bubbling target.
	*/
	get currentTarget() {
		return this._currentTarget;
	}
};

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* @module engine/view/observer/bubblingemittermixin
*/
const bubblingEmitterSymbol = Symbol("bubblingEmitter");
const callbackMapSymbol = Symbol("bubblingCallbacks");
const contextsSymbol = Symbol("bubblingContexts");
/**
* Bubbling emitter mixin for the view document as described in the {@link ~BubblingEmitter} interface.
*
* This function creates a class that inherits from the provided `base` and implements `Emitter` interface.
* The base class must implement {@link module:utils/emittermixin~Emitter} interface.
*
* ```ts
* class BaseClass extends EmitterMixin() {
* 	// ...
* }
*
* class MyClass extends BubblingEmitterMixin( BaseClass ) {
* 	// This class derives from `BaseClass` and implements the `BubblingEmitter` interface.
* }
* ```
*/
function BubblingEmitterMixin(base) {
	class Mixin extends base {
		fire(eventOrInfo, ...eventArgs) {
			try {
				const eventInfo = eventOrInfo instanceof EventInfo ? eventOrInfo : new EventInfo(this, eventOrInfo);
				const bubblingEmitter = getBubblingEmitter(this);
				const customContexts = getCustomContexts(this);
				updateEventInfo(eventInfo, "capturing", this);
				if (fireListenerFor(bubblingEmitter, "$capture", eventInfo, ...eventArgs)) return eventInfo.return;
				const startRange = eventInfo.startRange || this.selection.getFirstRange();
				const selectedElement = startRange ? startRange.getContainedElement() : null;
				const isCustomContext = selectedElement ? hasMatchingCustomContext(customContexts, selectedElement) : false;
				let node = selectedElement || getDeeperRangeParent(startRange);
				updateEventInfo(eventInfo, "atTarget", node);
				if (!isCustomContext) {
					if (fireListenerFor(bubblingEmitter, "$text", eventInfo, ...eventArgs)) return eventInfo.return;
					updateEventInfo(eventInfo, "bubbling", node);
				}
				while (node) {
					if (node.is("element") && fireListenerFor(bubblingEmitter, node, eventInfo, ...eventArgs)) return eventInfo.return;
					node = node.parent;
					updateEventInfo(eventInfo, "bubbling", node);
				}
				updateEventInfo(eventInfo, "bubbling", this);
				fireListenerFor(bubblingEmitter, "$document", eventInfo, ...eventArgs);
				return eventInfo.return;
			} catch (err) {
				CKEditorError.rethrowUnexpectedError(err, this);
			}
		}
		_addEventListener(event, callback, options) {
			const contexts = toArray(options.context || "$document");
			const bubblingEmitter = getBubblingEmitter(this);
			const callbacksMap = getCallbackMap(this);
			for (const context of contexts) if (typeof context == "function") getCustomContexts(this).add(context);
			const wrappedCallback = wrapCallback(this, contexts, callback);
			callbacksMap.set(callback, wrappedCallback);
			this.listenTo(bubblingEmitter, event, wrappedCallback, options);
		}
		_removeEventListener(event, callback) {
			const bubblingEmitter = getBubblingEmitter(this);
			const callbacksMap = getCallbackMap(this);
			const wrappedCallback = callbacksMap.get(callback);
			if (wrappedCallback) {
				callbacksMap.delete(callback);
				this.stopListening(bubblingEmitter, event, wrappedCallback);
			}
		}
	}
	return Mixin;
}
/**
* Update the event info bubbling fields.
*
* @param eventInfo The event info object to update.
* @param eventPhase The current event phase.
* @param currentTarget The current bubbling target.
*/
function updateEventInfo(eventInfo, eventPhase, currentTarget) {
	if (eventInfo instanceof BubblingEventInfo) {
		eventInfo._eventPhase = eventPhase;
		eventInfo._currentTarget = currentTarget;
	}
}
/**
* Fires the listener for the specified context. Returns `true` if event was stopped.
*
* @param eventInfo The `EventInfo` object.
* @param eventArgs Additional arguments to be passed to the callbacks.
* @returns True if event stop was called.
*/
function fireListenerFor(emitter, currentTarget, eventInfo, ...eventArgs) {
	emitter.fire(eventInfo, {
		currentTarget,
		eventArgs
	});
	if (eventInfo.stop.called) return true;
	return false;
}
/**
* Returns an event callback wrapped with context check condition.
*/
function wrapCallback(emitter, contexts, callback) {
	return function(event, data) {
		const { currentTarget, eventArgs } = data;
		if (typeof currentTarget == "string") {
			if (contexts.includes(currentTarget)) callback.call(emitter, event, ...eventArgs);
			return;
		}
		if (currentTarget.is("rootElement") && contexts.includes("$root")) {
			callback.call(emitter, event, ...eventArgs);
			return;
		}
		if (contexts.includes(currentTarget.name)) {
			callback.call(emitter, event, ...eventArgs);
			return;
		}
		for (const context of contexts) if (typeof context == "function" && context(currentTarget)) {
			callback.call(emitter, event, ...eventArgs);
			return;
		}
	};
}
/**
* Returns bubbling emitter for the source (emitter).
*/
function getBubblingEmitter(source) {
	if (!source[bubblingEmitterSymbol]) source[bubblingEmitterSymbol] = new (EmitterMixin())();
	return source[bubblingEmitterSymbol];
}
/**
* Returns map of callbacks (original to wrapped one).
*/
function getCallbackMap(source) {
	if (!source[callbackMapSymbol]) source[callbackMapSymbol] = /* @__PURE__ */ new Map();
	return source[callbackMapSymbol];
}
/**
* Returns the set of registered custom contexts.
*/
function getCustomContexts(source) {
	if (!source[contextsSymbol]) source[contextsSymbol] = /* @__PURE__ */ new Set();
	return source[contextsSymbol];
}
/**
* Returns true if any of custom context match the given element.
*/
function hasMatchingCustomContext(customContexts, element) {
	for (const context of customContexts) if (context(element)) return true;
	return false;
}
/**
* Returns the deeper parent element for the range.
*/
function getDeeperRangeParent(range) {
	if (!range) return null;
	const startParent = range.start.parent;
	const endParent = range.end.parent;
	const startPath = startParent.getPath();
	const endPath = endParent.getPath();
	return startPath.length > endPath.length ? startParent : endParent;
}

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* @module engine/view/document
*/
const ViewDocumentBase = /* #__PURE__ */ BubblingEmitterMixin(/* #__PURE__ */ ObservableMixin());
/**
* Document class creates an abstract layer over the content editable area, contains a tree of view elements and
* {@link module:engine/view/documentselection~ViewDocumentSelection view selection} associated with this document.
*/
var ViewDocument = class extends ViewDocumentBase {
	/**
	* Selection done on this document.
	*/
	selection;
	/**
	* Roots of the view tree. Collection of the {@link module:engine/view/element~ViewElement view elements}.
	*
	* View roots are created as a result of binding between {@link module:engine/view/document~ViewDocument#roots} and
	* {@link module:engine/model/document~ModelDocument#roots} and this is handled by
	* {@link module:engine/controller/editingcontroller~EditingController}, so to create view root we need to create
	* model root using {@link module:engine/model/document~ModelDocument#createRoot}.
	*/
	roots;
	/**
	* The styles processor instance used by this document when normalizing styles.
	*/
	stylesProcessor;
	/**
	* Post-fixer callbacks registered to the view document.
	*/
	_postFixers = /* @__PURE__ */ new Set();
	/**
	* Creates a Document instance.
	*
	* @param stylesProcessor The styles processor instance.
	*/
	constructor(stylesProcessor) {
		super();
		this.selection = new ViewDocumentSelection();
		this.roots = new Collection({ idProperty: "rootName" });
		this.stylesProcessor = stylesProcessor;
		this.set("isReadOnly", false);
		this.set("isFocused", false);
		this.set("isSelecting", false);
		this.set("isComposing", false);
	}
	/**
	* Gets a {@link module:engine/view/document~ViewDocument#roots view root element} with the specified name. If the name is not
	* specific "main" root is returned.
	*
	* @param name Name of the root.
	* @returns The view root element with the specified name or null when there is no root of given name.
	*/
	getRoot(name = "main") {
		return this.roots.get(name);
	}
	/**
	* Returns an array with all roots added to the document.
	*/
	getRoots() {
		return Array.from(this.roots);
	}
	/**
	* Allows registering post-fixer callbacks. A post-fixers mechanism allows to update the view tree just before it is rendered
	* to the DOM.
	*
	* Post-fixers are executed right after all changes from the outermost change block were applied but
	* before the {@link module:engine/view/view~EditingView#event:render render event} is fired. If a post-fixer callback made
	* a change, it should return `true`. When this happens, all post-fixers are fired again to check if something else should
	* not be fixed in the new document tree state.
	*
	* View post-fixers are useful when you want to apply some fixes whenever the view structure changes. Keep in mind that
	* changes executed in a view post-fixer should not break model-view mapping.
	*
	* The types of changes which should be safe:
	*
	* * adding or removing attribute from elements,
	* * changes inside of {@link module:engine/view/uielement~ViewUIElement UI elements},
	* * {@link module:engine/controller/editingcontroller~EditingController#reconvertItem marking some of the model elements to be
	* re-converted}.
	*
	* Try to avoid changes which touch view structure:
	*
	* * you should not add or remove nor wrap or unwrap any view elements,
	* * you should not change the editor data model in a view post-fixer.
	*
	* As a parameter, a post-fixer callback receives a {@link module:engine/view/downcastwriter~ViewDowncastWriter downcast writer}.
	*
	* Typically, a post-fixer will look like this:
	*
	* ```ts
	* editor.editing.view.document.registerPostFixer( writer => {
	* 	if ( checkSomeCondition() ) {
	* 		writer.doSomething();
	*
	* 		// Let other post-fixers know that something changed.
	* 		return true;
	* 	}
	*
	* 	return false;
	* } );
	* ```
	*
	* Note that nothing happens right after you register a post-fixer (e.g. execute such a code in the console).
	* That is because adding a post-fixer does not execute it.
	* The post-fixer will be executed as soon as any change in the document needs to cause its rendering.
	* If you want to re-render the editor's view after registering the post-fixer then you should do it manually by calling
	* {@link module:engine/view/view~EditingView#forceRender `view.forceRender()`}.
	*
	* If you need to register a callback which is executed when DOM elements are already updated,
	* use {@link module:engine/view/view~EditingView#event:render render event}.
	*/
	registerPostFixer(postFixer) {
		this._postFixers.add(postFixer);
	}
	/**
	* Destroys this instance. Makes sure that all observers are destroyed and listeners removed.
	*/
	destroy() {
		this.roots.forEach((root) => root.destroy());
		this.stopListening();
	}
	/**
	* Performs post-fixer loops. Executes post-fixer callbacks as long as none of them has done any changes to the model.
	*
	* @internal
	*/
	_callPostFixers(writer) {
		let wasFixed = false;
		do
			for (const callback of this._postFixers) {
				wasFixed = callback(writer);
				if (wasFixed) break;
			}
		while (wasFixed);
	}
};

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* @module engine/view/attributeelement
*/
const DEFAULT_PRIORITY = 10;
/**
* Attribute elements are used to represent formatting elements in the view (think – `<b>`, `<span style="font-size: 2em">`, etc.).
* Most often they are created when downcasting model text attributes.
*
* Editing engine does not define a fixed HTML DTD. This is why a feature developer needs to choose between various
* types (container element, {@link module:engine/view/attributeelement~ViewAttributeElement attribute element},
* {@link module:engine/view/emptyelement~ViewEmptyElement empty element}, etc) when developing a feature.
*
* To create a new attribute element instance use the
* {@link module:engine/view/downcastwriter~ViewDowncastWriter#createAttributeElement `ViewDowncastWriter#createAttributeElement()`} method.
*/
var ViewAttributeElement = class extends ViewElement {
	static DEFAULT_PRIORITY = DEFAULT_PRIORITY;
	/**
	* Element priority. Decides in what order elements are wrapped by {@link module:engine/view/downcastwriter~ViewDowncastWriter}.
	*
	* @internal
	* @readonly
	*/
	_priority = DEFAULT_PRIORITY;
	/**
	* Element identifier. If set, it is used by {@link module:engine/view/element~ViewElement#isSimilar},
	* and then two elements are considered similar if, and only if they have the same `_id`.
	*
	* @internal
	* @readonly
	*/
	_id = null;
	/**
	* Keeps all the attribute elements that have the same {@link module:engine/view/attributeelement~ViewAttributeElement#id ids}
	* and still exist in the view tree.
	*
	* This property is managed by {@link module:engine/view/downcastwriter~ViewDowncastWriter}.
	*/
	_clonesGroup = null;
	/**
	* Creates an attribute element.
	*
	* @see module:engine/view/downcastwriter~ViewDowncastWriter#createAttributeElement
	* @see module:engine/view/element~ViewElement
	* @protected
	* @param document The document instance to which this element belongs.
	* @param name Node name.
	* @param attrs Collection of attributes.
	* @param children A list of nodes to be inserted into created element.
	*/
	constructor(document, name, attrs, children) {
		super(document, name, attrs, children);
		this.getFillerOffset = getFillerOffset$3;
	}
	/**
	* Element priority. Decides in what order elements are wrapped by {@link module:engine/view/downcastwriter~ViewDowncastWriter}.
	*/
	get priority() {
		return this._priority;
	}
	/**
	* Element identifier. If set, it is used by {@link module:engine/view/element~ViewElement#isSimilar},
	* and then two elements are considered similar if, and only if they have the same `id`.
	*/
	get id() {
		return this._id;
	}
	/**
	* Returns all {@link module:engine/view/attributeelement~ViewAttributeElement attribute elements} that has the
	* same {@link module:engine/view/attributeelement~ViewAttributeElement#id id} and are in the view tree (were not removed).
	*
	* Note: If this element has been removed from the tree, returned set will not include it.
	*
	* Throws {@link module:utils/ckeditorerror~CKEditorError attribute-element-get-elements-with-same-id-no-id}
	* if this element has no `id`.
	*
	* @returns Set containing all the attribute elements
	* with the same `id` that were added and not removed from the view tree.
	*/
	getElementsWithSameId() {
		if (this.id === null)
 /**
		* Cannot get elements with the same id for an attribute element without id.
		*
		* @error attribute-element-get-elements-with-same-id-no-id
		*/
		throw new CKEditorError("attribute-element-get-elements-with-same-id-no-id", this);
		return new Set(this._clonesGroup);
	}
	/**
	* Checks if this element is similar to other element.
	*
	* If none of elements has set {@link module:engine/view/attributeelement~ViewAttributeElement#id}, then both elements
	* should have the same name, attributes and priority to be considered as similar. Two similar elements can contain
	* different set of children nodes.
	*
	* If at least one element has {@link module:engine/view/attributeelement~ViewAttributeElement#id} set, then both
	* elements have to have the same {@link module:engine/view/attributeelement~ViewAttributeElement#id} value to be
	* considered similar.
	*
	* Similarity is important for {@link module:engine/view/downcastwriter~ViewDowncastWriter}. For example:
	*
	* * two following similar elements can be merged together into one, longer element,
	* * {@link module:engine/view/downcastwriter~ViewDowncastWriter#unwrap} checks similarity of passed element and processed element to
	* decide whether processed element should be unwrapped,
	* * etc.
	*/
	isSimilar(otherElement) {
		if (this.id !== null || otherElement.id !== null) return this.id === otherElement.id;
		return super.isSimilar(otherElement) && this.priority == otherElement.priority;
	}
	/**
	* Converts `ViewAttributeElement` instance to plain object and returns it.
	*
	* @returns `ViewAttributeElement` instance converted to plain object.
	*/
	toJSON() {
		const json = super.toJSON();
		json.type = "AttributeElement";
		return json;
	}
	/**
	* Clones provided element with priority.
	*
	* @internal
	* @param deep If set to `true` clones element and all its children recursively. When set to `false`,
	* element will be cloned without any children.
	* @returns Clone of this element.
	*/
	_clone(deep = false) {
		const cloned = super._clone(deep);
		cloned._priority = this._priority;
		cloned._id = this._id;
		return cloned;
	}
	/**
	* Used by {@link module:engine/view/element~ViewElement#_mergeAttributesFrom} to verify if the given element can be merged without
	* conflicts into this element.
	*
	* @internal
	*/
	_canMergeAttributesFrom(otherElement) {
		if (this.id !== null || otherElement.id !== null || this.priority !== otherElement.priority) return false;
		return super._canMergeAttributesFrom(otherElement);
	}
	/**
	* Used by {@link module:engine/view/element~ViewElement#_subtractAttributesOf} to verify if the given element attributes
	* can be fully subtracted from this element.
	*
	* @internal
	*/
	_canSubtractAttributesOf(otherElement) {
		if (this.id !== null || otherElement.id !== null || this.priority !== otherElement.priority) return false;
		return super._canSubtractAttributesOf(otherElement);
	}
};
ViewAttributeElement.prototype.is = function(type, name) {
	if (!name) return type === "attributeElement" || type === "view:attributeElement" || type === "element" || type === "view:element" || type === "node" || type === "view:node";
	else return name === this.name && (type === "attributeElement" || type === "view:attributeElement" || type === "element" || type === "view:element");
};
/**
* Returns block {@link module:engine/view/filler~Filler filler} offset or `null` if block filler is not needed.
*
* @returns Block filler offset or `null` if block filler is not needed.
*/
function getFillerOffset$3() {
	if (nonUiChildrenCount(this)) return null;
	let element = this.parent;
	while (element && element.is("attributeElement")) {
		if (nonUiChildrenCount(element) > 1) return null;
		element = element.parent;
	}
	if (!element || nonUiChildrenCount(element) > 1) return null;
	return this.childCount;
}
/**
* Returns total count of children that are not {@link module:engine/view/uielement~ViewUIElement UIElements}.
*/
function nonUiChildrenCount(element) {
	return Array.from(element.getChildren()).filter((element) => !element.is("uiElement")).length;
}

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* @module engine/view/emptyelement
*/
/**
* Empty element class. It is used to represent elements that cannot contain any child nodes (for example `<img>` elements).
*
* To create a new empty element use the
* {@link module:engine/view/downcastwriter~ViewDowncastWriter#createEmptyElement `downcastWriter#createEmptyElement()`} method.
*/
var ViewEmptyElement = class extends ViewElement {
	/**
	* Creates new instance of ViewEmptyElement.
	*
	* Throws {@link module:utils/ckeditorerror~CKEditorError CKEditorError} `view-emptyelement-cannot-add` when third parameter is passed,
	* to inform that usage of ViewEmptyElement is incorrect (adding child nodes to ViewEmptyElement is forbidden).
	*
	* @see module:engine/view/downcastwriter~ViewDowncastWriter#createEmptyElement
	* @internal
	* @param document The document instance to which this element belongs.
	* @param name Node name.
	* @param attributes Collection of attributes.
	* @param children A list of nodes to be inserted into created element.
	*/
	constructor(document, name, attributes, children) {
		super(document, name, attributes, children);
		this.getFillerOffset = getFillerOffset$2;
	}
	/**
	* Converts `ViewEmptyElement` instance to plain object and returns it.
	*
	* @returns `ViewEmptyElement` instance converted to plain object.
	*/
	toJSON() {
		const json = super.toJSON();
		json.type = "EmptyElement";
		return json;
	}
	/**
	* Overrides {@link module:engine/view/element~ViewElement#_insertChild} method.
	* Throws {@link module:utils/ckeditorerror~CKEditorError CKEditorError} `view-emptyelement-cannot-add` to prevent
	* adding any child nodes to ViewEmptyElement.
	*
	* @internal
	*/
	_insertChild(index, items) {
		if (items && (items instanceof ViewNode || Array.from(items).length > 0))
 /**
		* Cannot add children to {@link module:engine/view/emptyelement~ViewEmptyElement}.
		*
		* @error view-emptyelement-cannot-add
		*/
		throw new CKEditorError("view-emptyelement-cannot-add", [this, items]);
		return 0;
	}
};
ViewEmptyElement.prototype.is = function(type, name) {
	if (!name) return type === "emptyElement" || type === "view:emptyElement" || type === "element" || type === "view:element" || type === "node" || type === "view:node";
	else return name === this.name && (type === "emptyElement" || type === "view:emptyElement" || type === "element" || type === "view:element");
};
/**
* Returns `null` because block filler is not needed for ViewEmptyElements.
*/
function getFillerOffset$2() {
	return null;
}

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* @module engine/view/uielement
*/
/**
* UI element class. It should be used to represent editing UI which needs to be injected into the editing view
* If possible, you should keep your UI outside the editing view. However, if that is not possible,
* UI elements can be used.
*
* How a UI element is rendered is in your control (you pass a callback to
* {@link module:engine/view/downcastwriter~ViewDowncastWriter#createUIElement `downcastWriter#createUIElement()`}).
* The editor will ignore your UI element – the selection cannot be placed in it, it is skipped (invisible) when
* the user modifies the selection by using arrow keys and the editor does not listen to any mutations which
* happen inside your UI elements.
*
* The limitation is that you cannot convert a model element to a UI element. UI elements need to be
* created for {@link module:engine/model/markercollection~Marker markers} or as additinal elements
* inside normal {@link module:engine/view/containerelement~ViewContainerElement container elements}.
*
* To create a new UI element use the
* {@link module:engine/view/downcastwriter~ViewDowncastWriter#createUIElement `downcastWriter#createUIElement()`} method.
*/
var ViewUIElement = class extends ViewElement {
	/**
	* Creates new instance of UIElement.
	*
	* Throws {@link module:utils/ckeditorerror~CKEditorError CKEditorError} `view-uielement-cannot-add` when third parameter is passed,
	* to inform that usage of UIElement is incorrect (adding child nodes to UIElement is forbidden).
	*
	* @see module:engine/view/downcastwriter~ViewDowncastWriter#createUIElement
	* @internal
	* @param document The document instance to which this element belongs.
	* @param name Node name.
	* @param attrs Collection of attributes.
	* @param children A list of nodes to be inserted into created element.
	*/
	constructor(document, name, attrs, children) {
		super(document, name, attrs, children);
		this.getFillerOffset = getFillerOffset$1;
	}
	/**
	* Overrides {@link module:engine/view/element~ViewElement#_insertChild} method.
	* Throws {@link module:utils/ckeditorerror~CKEditorError CKEditorError} `view-uielement-cannot-add` to prevent adding any child nodes
	* to UIElement.
	*
	* @internal
	*/
	_insertChild(index, items) {
		if (items && (items instanceof ViewNode || Array.from(items).length > 0))
 /**
		* Cannot add children to {@link module:engine/view/uielement~ViewUIElement}.
		*
		* @error view-uielement-cannot-add
		*/
		throw new CKEditorError("view-uielement-cannot-add", [this, items]);
		return 0;
	}
	/**
	* Renders this {@link module:engine/view/uielement~ViewUIElement} to DOM. This method is called by
	* {@link module:engine/view/domconverter~ViewDomConverter}.
	* Do not use inheritance to create custom rendering method, replace `render()` method instead:
	*
	* ```ts
	* const myUIElement = downcastWriter.createUIElement( 'span' );
	* myUIElement.render = function( domDocument, domConverter ) {
	* 	const domElement = this.toDomElement( domDocument );
	*
	* 	domConverter.setContentOf( domElement, '<b>this is ui element</b>' );
	*
	* 	return domElement;
	* };
	* ```
	*
	* If changes in your UI element should trigger some editor UI update you should call
	* the {@link module:ui/editorui/editorui~EditorUI#update `editor.ui.update()`} method
	* after rendering your UI element.
	*
	* @param domConverter Instance of the ViewDomConverter used to optimize the output.
	*/
	render(domDocument, domConverter) {
		return this.toDomElement(domDocument);
	}
	/**
	* Creates DOM element based on this view UIElement.
	* Note that each time this method is called new DOM element is created.
	*/
	toDomElement(domDocument) {
		const domElement = domDocument.createElement(this.name);
		for (const key of this.getAttributeKeys()) domElement.setAttribute(key, this.getAttribute(key));
		return domElement;
	}
	/**
	* Converts `ViewUIElement` instance to plain object and returns it.
	*
	* @returns `ViewUIElement` instance converted to plain object.
	*/
	toJSON() {
		const json = super.toJSON();
		json.type = "UIElement";
		return json;
	}
};
ViewUIElement.prototype.is = function(type, name) {
	if (!name) return type === "uiElement" || type === "view:uiElement" || type === "element" || type === "view:element" || type === "node" || type === "view:node";
	else return name === this.name && (type === "uiElement" || type === "view:uiElement" || type === "element" || type === "view:element");
};
/**
* This function injects UI element handling to the given {@link module:engine/view/document~ViewDocument document}.
*
* A callback is added to {@link module:engine/view/document~ViewDocument#event:keydown document keydown event}.
* The callback handles the situation when right arrow key is pressed and selection is collapsed before a UI element.
* Without this handler, it would be impossible to "jump over" UI element using right arrow key.
*
* @param view View controller to which the quirks handling will be injected.
* @internal
*/
function injectUiElementHandling(view) {
	view.document.on("arrowKey", (evt, data) => jumpOverUiElement(evt, data, view.domConverter), { priority: "low" });
}
/**
* Returns `null` because block filler is not needed for UIElements.
*/
function getFillerOffset$1() {
	return null;
}
/**
* Selection cannot be placed in a `UIElement`. Whenever it is placed there, it is moved before it. This
* causes a situation when it is impossible to jump over `UIElement` using right arrow key, because the selection
* ends up in ui element (in DOM) and is moved back to the left. This handler fixes this situation.
*/
function jumpOverUiElement(evt, data, domConverter) {
	if (data.keyCode == keyCodes.arrowright) {
		const domSelection = data.domTarget.ownerDocument.defaultView.getSelection();
		const domSelectionCollapsed = domSelection.rangeCount == 1 && domSelection.getRangeAt(0).collapsed;
		if (domSelectionCollapsed || data.shiftKey) {
			const domParent = domSelection.focusNode;
			const domOffset = domSelection.focusOffset;
			const viewPosition = domConverter.domPositionToView(domParent, domOffset);
			if (viewPosition === null) return;
			let jumpedOverAnyUiElement = false;
			const nextViewPosition = viewPosition.getLastMatchingPosition((value) => {
				if (value.item.is("uiElement")) jumpedOverAnyUiElement = true;
				if (value.item.is("uiElement") || value.item.is("attributeElement")) return true;
				return false;
			});
			if (jumpedOverAnyUiElement) {
				const newDomPosition = domConverter.viewPositionToDom(nextViewPosition);
				if (domSelectionCollapsed) domSelection.collapse(newDomPosition.parent, newDomPosition.offset);
				else domSelection.extend(newDomPosition.parent, newDomPosition.offset);
			}
		}
	}
}

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* @module engine/view/rawelement
*/
/**
* The raw element class.
*
* The raw elements work as data containers ("wrappers", "sandboxes") but their children are not managed or
* even recognized by the editor. This encapsulation allows integrations to maintain custom DOM structures
* in the editor content without, for instance, worrying about compatibility with other editor features.
* Raw elements are a perfect tool for integration with external frameworks and data sources.
*
* Unlike {@link module:engine/view/uielement~ViewUIElement UI elements}, raw elements act like real editor
* content (similar to {@link module:engine/view/containerelement~ViewContainerElement} or
* {@link module:engine/view/emptyelement~ViewEmptyElement}), they are considered by the editor selection and
* {@link module:widget/utils~toWidget they can work as widgets}.
*
* To create a new raw element, use the
* {@link module:engine/view/downcastwriter~ViewDowncastWriter#createRawElement `downcastWriter#createRawElement()`} method.
*/
var ViewRawElement = class extends ViewElement {
	/**
	* Creates a new instance of a raw element.
	*
	* Throws the `view-rawelement-cannot-add` {@link module:utils/ckeditorerror~CKEditorError CKEditorError} when the `children`
	* parameter is passed to inform that the usage of `ViewRawElement` is incorrect (adding child nodes to `ViewRawElement` is forbidden).
	*
	* @see module:engine/view/downcastwriter~ViewDowncastWriter#createRawElement
	* @internal
	* @param document The document instance to which this element belongs.
	* @param name Node name.
	* @param attrs Collection of attributes.
	* @param children A list of nodes to be inserted into created element.
	*/
	constructor(document, name, attrs, children) {
		super(document, name, attrs, children);
		this.getFillerOffset = getFillerOffset;
	}
	/**
	* Converts `ViewRawElement` instance to plain object and returns it.
	*
	* @returns `ViewRawElement` instance converted to plain object.
	*/
	toJSON() {
		const json = super.toJSON();
		json.type = "RawElement";
		return json;
	}
	/**
	* Overrides the {@link module:engine/view/element~ViewElement#_insertChild} method.
	* Throws the `view-rawelement-cannot-add` {@link module:utils/ckeditorerror~CKEditorError CKEditorError} to prevent
	* adding any child nodes to a raw element.
	*
	* @internal
	*/
	_insertChild(index, items) {
		if (items && (items instanceof ViewNode || Array.from(items).length > 0))
 /**
		* Cannot add children to a {@link module:engine/view/rawelement~ViewRawElement} instance.
		*
		* @error view-rawelement-cannot-add
		*/
		throw new CKEditorError("view-rawelement-cannot-add", [this, items]);
		return 0;
	}
	/**
	* This allows rendering the children of a {@link module:engine/view/rawelement~ViewRawElement} on the DOM level.
	* This method is called by the {@link module:engine/view/domconverter~ViewDomConverter} with the raw DOM element
	* passed as an argument, leaving the number and shape of the children up to the integrator.
	*
	* This method **must be defined** for the raw element to work:
	*
	* ```ts
	* const myRawElement = downcastWriter.createRawElement( 'div' );
	*
	* myRawElement.render = function( domElement, domConverter ) {
	* 	domConverter.setContentOf( domElement, '<b>This is the raw content of myRawElement.</b>' );
	* };
	* ```
	*
	* @param domElement The native DOM element representing the raw view element.
	* @param domConverter Instance of the ViewDomConverter used to optimize the output.
	*/
	render(domElement, domConverter) {}
};
ViewRawElement.prototype.is = function(type, name) {
	if (!name) return type === "rawElement" || type === "view:rawElement" || type === this.name || type === "view:" + this.name || type === "element" || type === "view:element" || type === "node" || type === "view:node";
	else return name === this.name && (type === "rawElement" || type === "view:rawElement" || type === "element" || type === "view:element");
};
/**
* Returns `null` because block filler is not needed for raw elements.
*/
function getFillerOffset() {
	return null;
}

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* @module engine/view/documentfragment
*/
const ViewDocumentFragmentBase = /* #__PURE__ */ EmitterMixin(ViewTypeCheckable);
/**
* Document fragment.
*
* To create a new document fragment instance use the
* {@link module:engine/view/upcastwriter~ViewUpcastWriter#createDocumentFragment `ViewUpcastWriter#createDocumentFragment()`}
* method.
*/
var ViewDocumentFragment = class extends ViewDocumentFragmentBase {
	/**
	* The document to which this document fragment belongs.
	*/
	document;
	/**
	* Array of child nodes.
	*/
	_children = [];
	/**
	* Map of custom properties.
	* Custom properties can be added to document fragment instance.
	*/
	_customProperties = /* @__PURE__ */ new Map();
	/**
	* Creates new DocumentFragment instance.
	*
	* @internal
	* @param document The document to which this document fragment belongs.
	* @param children A list of nodes to be inserted into the created document fragment.
	*/
	constructor(document, children) {
		super();
		this.document = document;
		if (children) this._insertChild(0, children);
	}
	/**
	* Iterable interface.
	*
	* Iterates over nodes added to this document fragment.
	*/
	[Symbol.iterator]() {
		return this._children[Symbol.iterator]();
	}
	/**
	* Number of child nodes in this document fragment.
	*/
	get childCount() {
		return this._children.length;
	}
	/**
	* Is `true` if there are no nodes inside this document fragment, `false` otherwise.
	*/
	get isEmpty() {
		return this.childCount === 0;
	}
	/**
	* Artificial root of `DocumentFragment`. Returns itself. Added for compatibility reasons.
	*/
	get root() {
		return this;
	}
	/**
	* Artificial parent of `DocumentFragment`. Returns `null`. Added for compatibility reasons.
	*/
	get parent() {
		return null;
	}
	/**
	* Artificial element name. Returns `undefined`. Added for compatibility reasons.
	*/
	get name() {}
	/**
	* Artificial element getFillerOffset. Returns `undefined`. Added for compatibility reasons.
	*/
	get getFillerOffset() {}
	/**
	* Returns the custom property value for the given key.
	*/
	getCustomProperty(key) {
		return this._customProperties.get(key);
	}
	/**
	* Returns an iterator which iterates over this document fragment's custom properties.
	* Iterator provides `[ key, value ]` pairs for each stored property.
	*/
	*getCustomProperties() {
		yield* this._customProperties.entries();
	}
	/**
	* Converts `ViewDocumentFragment` instance to plain object and returns it.
	* Takes care of converting all of this document fragment's children.
	*
	* @returns `ViewDocumentFragment` instance converted to plain object.
	*/
	toJSON() {
		const json = [];
		for (const node of this._children) json.push(node.toJSON());
		return json;
	}
	/**
	* {@link module:engine/view/documentfragment~ViewDocumentFragment#_insertChild Insert} a child node or a list of child nodes at the end
	* and sets the parent of these nodes to this fragment.
	*
	* @internal
	* @param items Items to be inserted.
	* @returns Number of appended nodes.
	*/
	_appendChild(items) {
		return this._insertChild(this.childCount, items);
	}
	/**
	* Gets child at the given index.
	*
	* @param index Index of child.
	* @returns Child node.
	*/
	getChild(index) {
		return this._children[index];
	}
	/**
	* Gets index of the given child node. Returns `-1` if child node is not found.
	*
	* @param node Child node.
	* @returns Index of the child node.
	*/
	getChildIndex(node) {
		return this._children.indexOf(node);
	}
	/**
	* Gets child nodes iterator.
	*
	* @returns Child nodes iterator.
	*/
	getChildren() {
		return this._children[Symbol.iterator]();
	}
	/**
	* Inserts a child node or a list of child nodes on the given index and sets the parent of these nodes to
	* this fragment.
	*
	* @internal
	* @param index Position where nodes should be inserted.
	* @param items Items to be inserted.
	* @returns Number of inserted nodes.
	*/
	_insertChild(index, items) {
		this._fireChange("children", this, { index });
		let count = 0;
		const nodes = normalize$2(this.document, items);
		for (const node of nodes) {
			if (node.parent !== null) node._remove();
			node.parent = this;
			this._children.splice(index, 0, node);
			index++;
			count++;
		}
		return count;
	}
	/**
	* Removes number of child nodes starting at the given index and set the parent of these nodes to `null`.
	*
	* @internal
	* @param index Number of the first node to remove.
	* @param howMany Number of nodes to remove.
	* @returns The array of removed nodes.
	*/
	_removeChildren(index, howMany = 1) {
		this._fireChange("children", this, { index });
		for (let i = index; i < index + howMany; i++) this._children[i].parent = null;
		return this._children.splice(index, howMany);
	}
	/**
	* @internal
	* @param type Type of the change.
	* @param node Changed node.
	* @param data Additional data.
	* @fires module:engine/view/node~ViewNode#event:change
	*/
	_fireChange(type, node, data) {
		this.fire(`change:${type}`, node, data);
	}
	/**
	* Sets a custom property. They can be used to add special data to elements.
	*
	* @see module:engine/view/downcastwriter~ViewDowncastWriter#setCustomProperty
	* @internal
	*/
	_setCustomProperty(key, value) {
		this._customProperties.set(key, value);
	}
	/**
	* Removes the custom property stored under the given key.
	*
	* @see module:engine/view/downcastwriter~ViewDowncastWriter#removeCustomProperty
	* @internal
	* @returns Returns true if property was removed.
	*/
	_removeCustomProperty(key) {
		return this._customProperties.delete(key);
	}
};
ViewDocumentFragment.prototype.is = function(type) {
	return type === "documentFragment" || type === "view:documentFragment";
};
/**
* Converts strings to Text and non-iterables to arrays.
*/
function normalize$2(document, nodes) {
	if (typeof nodes == "string") return [new ViewText(document, nodes)];
	if (!isIterable(nodes)) nodes = [nodes];
	return Array.from(nodes).map((node) => {
		if (typeof node == "string") return new ViewText(document, node);
		if (node instanceof ViewTextProxy) return new ViewText(document, node.data);
		return node;
	});
}

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* @module engine/view/downcastwriter
*/
/**
* View downcast writer.
*
* It provides a set of methods used to manipulate view nodes.
*
* Do not create an instance of this writer manually. To modify a view structure, use
* the {@link module:engine/view/view~EditingView#change `View#change()`} block.
*
* The `ViewDowncastWriter` is designed to work with semantic views which are the views that were/are being downcasted from the model.
* To work with ordinary views (e.g. parsed from a pasted content) use the
* {@link module:engine/view/upcastwriter~ViewUpcastWriter upcast writer}.
*
* Read more about changing the view in the {@glink framework/architecture/editing-engine#changing-the-view Changing the view}
* section of the {@glink framework/architecture/editing-engine Editing engine architecture} guide.
*/
var ViewDowncastWriter = class {
	/**
	* The view document instance in which this writer operates.
	*/
	document;
	/**
	* Holds references to the attribute groups that share the same {@link module:engine/view/attributeelement~ViewAttributeElement#id id}.
	* The keys are `id`s, the values are `Set`s holding {@link module:engine/view/attributeelement~ViewAttributeElement}s.
	*/
	_cloneGroups = /* @__PURE__ */ new Map();
	/**
	* The slot factory used by the `elementToStructure` downcast helper.
	*/
	_slotFactory = null;
	/**
	* @param document The view document instance.
	*/
	constructor(document) {
		this.document = document;
	}
	setSelection(...args) {
		this.document.selection._setTo(...args);
	}
	/**
	* Moves {@link module:engine/view/documentselection~ViewDocumentSelection#focus selection's focus} to the specified location.
	*
	* The location can be specified in the same form as
	* {@link module:engine/view/view~EditingView#createPositionAt view.createPositionAt()}
	* parameters.
	*
	* @param itemOrPosition
	* @param offset Offset or one of the flags. Used only when the first parameter is a {@link module:engine/view/item~ViewItem view item}.
	*/
	setSelectionFocus(itemOrPosition, offset) {
		this.document.selection._setFocus(itemOrPosition, offset);
	}
	/**
	* Creates a new {@link module:engine/view/documentfragment~ViewDocumentFragment} instance.
	*
	* @param children A list of nodes to be inserted into the created document fragment.
	* @returns The created document fragment.
	*/
	createDocumentFragment(children) {
		return new ViewDocumentFragment(this.document, children);
	}
	/**
	* Creates a new {@link module:engine/view/text~ViewText text node}.
	*
	* ```ts
	* writer.createText( 'foo' );
	* ```
	*
	* @param data The text's data.
	* @returns The created text node.
	*/
	createText(data) {
		return new ViewText(this.document, data);
	}
	/**
	* Creates a new {@link module:engine/view/attributeelement~ViewAttributeElement}.
	*
	* ```ts
	* writer.createAttributeElement( 'strong' );
	* writer.createAttributeElement( 'a', { href: 'foo.bar' } );
	*
	* // Make `<a>` element contain other attributes element so the `<a>` element is not broken.
	* writer.createAttributeElement( 'a', { href: 'foo.bar' }, { priority: 5 } );
	*
	* // Set `id` of a marker element so it is not joined or merged with "normal" elements.
	* writer.createAttributeElement( 'span', { class: 'my-marker' }, { id: 'marker:my' } );
	* ```
	*
	* @param name Name of the element.
	* @param attributes Element's attributes.
	* @param options Element's options.
	* @param options.priority Element's {@link module:engine/view/attributeelement~ViewAttributeElement#priority priority}.
	* @param options.id Element's {@link module:engine/view/attributeelement~ViewAttributeElement#id id}.
	* @param options.renderUnsafeAttributes A list of attribute names that should be rendered in the editing
	* pipeline even though they would normally be filtered out by unsafe attribute detection mechanisms.
	* @returns Created element.
	*/
	createAttributeElement(name, attributes, options = {}) {
		const attributeElement = new ViewAttributeElement(this.document, name, attributes);
		if (typeof options.priority === "number") attributeElement._priority = options.priority;
		if (options.id) attributeElement._id = options.id;
		if (options.renderUnsafeAttributes) attributeElement._unsafeAttributesToRender.push(...options.renderUnsafeAttributes);
		return attributeElement;
	}
	createContainerElement(name, attributes, childrenOrOptions = {}, options = {}) {
		let children = void 0;
		if (isContainerOptions(childrenOrOptions)) options = childrenOrOptions;
		else children = childrenOrOptions;
		const containerElement = new ViewContainerElement(this.document, name, attributes, children);
		if (options.renderUnsafeAttributes) containerElement._unsafeAttributesToRender.push(...options.renderUnsafeAttributes);
		return containerElement;
	}
	/**
	* Creates a new {@link module:engine/view/editableelement~ViewEditableElement}.
	*
	* ```ts
	* writer.createEditableElement( 'div' );
	* writer.createEditableElement( 'div', { id: 'foo-1234' } );
	* ```
	*
	* Note: The editable element is to be used in the editing pipeline. Usually, together with
	* {@link module:widget/utils~toWidgetEditable `toWidgetEditable()`}.
	*
	* @param name Name of the element.
	* @param attributes Elements attributes.
	* @param options Element's options.
	* @param options.renderUnsafeAttributes A list of attribute names that should be rendered in the editing
	* pipeline even though they would normally be filtered out by unsafe attribute detection mechanisms.
	* @returns Created element.
	*/
	createEditableElement(name, attributes, options = {}) {
		const editableElement = new ViewEditableElement(this.document, name, attributes);
		if (options.renderUnsafeAttributes) editableElement._unsafeAttributesToRender.push(...options.renderUnsafeAttributes);
		return editableElement;
	}
	/**
	* Creates a new {@link module:engine/view/emptyelement~ViewEmptyElement}.
	*
	* ```ts
	* writer.createEmptyElement( 'img' );
	* writer.createEmptyElement( 'img', { id: 'foo-1234' } );
	* ```
	*
	* @param name Name of the element.
	* @param attributes Elements attributes.
	* @param options Element's options.
	* @param options.renderUnsafeAttributes A list of attribute names that should be rendered in the editing
	* pipeline even though they would normally be filtered out by unsafe attribute detection mechanisms.
	* @returns Created element.
	*/
	createEmptyElement(name, attributes, options = {}) {
		const emptyElement = new ViewEmptyElement(this.document, name, attributes);
		if (options.renderUnsafeAttributes) emptyElement._unsafeAttributesToRender.push(...options.renderUnsafeAttributes);
		return emptyElement;
	}
	/**
	* Creates a new {@link module:engine/view/uielement~ViewUIElement}.
	*
	* ```ts
	* writer.createUIElement( 'span' );
	* writer.createUIElement( 'span', { id: 'foo-1234' } );
	* ```
	*
	* A custom render function can be provided as the third parameter:
	*
	* ```ts
	* writer.createUIElement( 'span', null, function( domDocument ) {
	* 	const domElement = this.toDomElement( domDocument );
	* 	domElement.innerHTML = '<b>this is ui element</b>';
	*
	* 	return domElement;
	* } );
	* ```
	*
	* Unlike {@link #createRawElement raw elements}, UI elements are by no means editor content, for instance,
	* they are ignored by the editor selection system.
	*
	* You should not use UI elements as data containers. Check out {@link #createRawElement} instead.
	*
	* @param name The name of the element.
	* @param attributes Element attributes.
	* @param renderFunction A custom render function.
	* @returns The created element.
	*/
	createUIElement(name, attributes, renderFunction) {
		const uiElement = new ViewUIElement(this.document, name, attributes);
		if (renderFunction) uiElement.render = renderFunction;
		return uiElement;
	}
	/**
	* Creates a new {@link module:engine/view/rawelement~ViewRawElement}.
	*
	* ```ts
	* writer.createRawElement( 'span', { id: 'foo-1234' }, function( domElement ) {
	* 	domElement.innerHTML = '<b>This is the raw content of the raw element.</b>';
	* } );
	* ```
	*
	* Raw elements work as data containers ("wrappers", "sandboxes") but their children are not managed or
	* even recognized by the editor. This encapsulation allows integrations to maintain custom DOM structures
	* in the editor content without, for instance, worrying about compatibility with other editor features.
	* Raw elements are a perfect tool for integration with external frameworks and data sources.
	*
	* Unlike {@link #createUIElement UI elements}, raw elements act like "real" editor content (similar to
	* {@link module:engine/view/containerelement~ViewContainerElement} or {@link module:engine/view/emptyelement~ViewEmptyElement}),
	* and they are considered by the editor selection.
	*
	* You should not use raw elements to render the UI in the editor content. Check out {@link #createUIElement `#createUIElement()`}
	* instead.
	*
	* @param name The name of the element.
	* @param attributes Element attributes.
	* @param renderFunction A custom render function.
	* @param options Element's options.
	* @param options.renderUnsafeAttributes A list of attribute names that should be rendered in the editing
	* pipeline even though they would normally be filtered out by unsafe attribute detection mechanisms.
	* @returns The created element.
	*/
	createRawElement(name, attributes, renderFunction, options = {}) {
		const rawElement = new ViewRawElement(this.document, name, attributes);
		if (renderFunction) rawElement.render = renderFunction;
		if (options.renderUnsafeAttributes) rawElement._unsafeAttributesToRender.push(...options.renderUnsafeAttributes);
		return rawElement;
	}
	setAttribute(key, value, elementOrOverwrite, element) {
		if (element !== void 0) element._setAttribute(key, value, elementOrOverwrite);
		else elementOrOverwrite._setAttribute(key, value);
	}
	removeAttribute(key, elementOrTokens, element) {
		if (element !== void 0) element._removeAttribute(key, elementOrTokens);
		else elementOrTokens._removeAttribute(key);
	}
	/**
	* Adds specified class to the element.
	*
	* ```ts
	* writer.addClass( 'foo', linkElement );
	* writer.addClass( [ 'foo', 'bar' ], linkElement );
	* ```
	*/
	addClass(className, element) {
		element._addClass(className);
	}
	/**
	* Removes specified class from the element.
	*
	* ```ts
	* writer.removeClass( 'foo', linkElement );
	* writer.removeClass( [ 'foo', 'bar' ], linkElement );
	* ```
	*/
	removeClass(className, element) {
		element._removeClass(className);
	}
	setStyle(property, value, element) {
		if (isPlainObject(property) && element === void 0) value._setStyle(property);
		else element._setStyle(property, value);
	}
	/**
	* Removes specified style from the element.
	*
	* ```ts
	* writer.removeStyle( 'color', element ); // Removes 'color' style.
	* writer.removeStyle( [ 'color', 'border-top' ], element ); // Removes both 'color' and 'border-top' styles.
	* ```
	*
	* **Note**: This method can work with normalized style names if
	* {@link module:engine/controller/datacontroller~DataController#addStyleProcessorRules a particular style processor rule is enabled}.
	* See {@link module:engine/view/stylesmap~StylesMap#remove `StylesMap#remove()`} for details.
	*/
	removeStyle(property, element) {
		element._removeStyle(property);
	}
	/**
	* Sets a custom property on element. Unlike attributes, custom properties are not rendered to the DOM,
	* so they can be used to add special data to elements.
	*/
	setCustomProperty(key, value, element) {
		element._setCustomProperty(key, value);
	}
	/**
	* Removes a custom property stored under the given key.
	*
	* @returns Returns true if property was removed.
	*/
	removeCustomProperty(key, element) {
		return element._removeCustomProperty(key);
	}
	/**
	* Breaks attribute elements at the provided position or at the boundaries of a provided range. It breaks attribute elements
	* up to their first ancestor that is a container element.
	*
	* In following examples `<p>` is a container, `<b>` and `<u>` are attribute elements:
	*
	* ```html
	* <p>foo<b><u>bar{}</u></b></p> -> <p>foo<b><u>bar</u></b>[]</p>
	* <p>foo<b><u>{}bar</u></b></p> -> <p>foo{}<b><u>bar</u></b></p>
	* <p>foo<b><u>b{}ar</u></b></p> -> <p>foo<b><u>b</u></b>[]<b><u>ar</u></b></p>
	* <p><b>fo{o</b><u>ba}r</u></p> -> <p><b>fo</b><b>o</b><u>ba</u><u>r</u></b></p>
	* ```
	*
	* **Note:** {@link module:engine/view/documentfragment~ViewDocumentFragment DocumentFragment} is treated like a container.
	*
	* **Note:** The difference between {@link module:engine/view/downcastwriter~ViewDowncastWriter#breakAttributes breakAttributes()} and
	* {@link module:engine/view/downcastwriter~ViewDowncastWriter#breakContainer breakContainer()} is that `breakAttributes()` breaks all
	* {@link module:engine/view/attributeelement~ViewAttributeElement attribute elements} that are ancestors of a given `position`,
	* up to the first encountered {@link module:engine/view/containerelement~ViewContainerElement container element}.
	* `breakContainer()` assumes that a given `position` is directly in the container element and breaks that container element.
	*
	* Throws the `view-writer-invalid-range-container` {@link module:utils/ckeditorerror~CKEditorError CKEditorError}
	* when the {@link module:engine/view/range~ViewRange#start start}
	* and {@link module:engine/view/range~ViewRange#end end} positions of a passed range are not placed inside same parent container.
	*
	* Throws the `view-writer-cannot-break-empty-element` {@link module:utils/ckeditorerror~CKEditorError CKEditorError}
	* when trying to break attributes inside an {@link module:engine/view/emptyelement~ViewEmptyElement ViewEmptyElement}.
	*
	* Throws the `view-writer-cannot-break-ui-element` {@link module:utils/ckeditorerror~CKEditorError CKEditorError}
	* when trying to break attributes inside a {@link module:engine/view/uielement~ViewUIElement UIElement}.
	*
	* @see module:engine/view/attributeelement~ViewAttributeElement
	* @see module:engine/view/containerelement~ViewContainerElement
	* @see module:engine/view/downcastwriter~ViewDowncastWriter#breakContainer
	* @param positionOrRange The position where to break attribute elements.
	* @returns The new position or range, after breaking the attribute elements.
	*/
	breakAttributes(positionOrRange) {
		if (positionOrRange instanceof ViewPosition) return this._breakAttributes(positionOrRange);
		else return this._breakAttributesRange(positionOrRange);
	}
	/**
	* Breaks a {@link module:engine/view/containerelement~ViewContainerElement container view element} into two, at the given position.
	* The position has to be directly inside the container element and cannot be in the root. It does not break the conrainer view element
	* if the position is at the beginning or at the end of its parent element.
	*
	* ```html
	* <p>foo^bar</p> -> <p>foo</p><p>bar</p>
	* <div><p>foo</p>^<p>bar</p></div> -> <div><p>foo</p></div><div><p>bar</p></div>
	* <p>^foobar</p> -> ^<p>foobar</p>
	* <p>foobar^</p> -> <p>foobar</p>^
	* ```
	*
	* **Note:** The difference between {@link module:engine/view/downcastwriter~ViewDowncastWriter#breakAttributes breakAttributes()} and
	* {@link module:engine/view/downcastwriter~ViewDowncastWriter#breakContainer breakContainer()} is that `breakAttributes()` breaks all
	* {@link module:engine/view/attributeelement~ViewAttributeElement attribute elements} that are ancestors of a given `position`,
	* up to the first encountered {@link module:engine/view/containerelement~ViewContainerElement container element}.
	* `breakContainer()` assumes that the given `position` is directly in the container element and breaks that container element.
	*
	* @see module:engine/view/attributeelement~ViewAttributeElement
	* @see module:engine/view/containerelement~ViewContainerElement
	* @see module:engine/view/downcastwriter~ViewDowncastWriter#breakAttributes
	* @param position The position where to break the element.
	* @returns The position between broken elements. If an element has not been broken,
	* the returned position is placed either before or after it.
	*/
	breakContainer(position) {
		const element = position.parent;
		if (!element.is("containerElement"))
 /**
		* Trying to break an element which is not a container element.
		*
		* @error view-writer-break-non-container-element
		*/
		throw new CKEditorError("view-writer-break-non-container-element", this.document);
		if (!element.parent)
 /**
		* Trying to break root element.
		*
		* @error view-writer-break-root
		*/
		throw new CKEditorError("view-writer-break-root", this.document);
		if (position.isAtStart) return ViewPosition._createBefore(element);
		else if (!position.isAtEnd) {
			const newElement = element._clone(false);
			this.insert(ViewPosition._createAfter(element), newElement);
			const sourceRange = new ViewRange(position, ViewPosition._createAt(element, "end"));
			const targetPosition = new ViewPosition(newElement, 0);
			this.move(sourceRange, targetPosition);
		}
		return ViewPosition._createAfter(element);
	}
	/**
	* Merges {@link module:engine/view/attributeelement~ViewAttributeElement attribute elements}. It also merges text nodes if needed.
	* Only {@link module:engine/view/attributeelement~ViewAttributeElement#isSimilar similar} attribute elements can be merged.
	*
	* In following examples `<p>` is a container and `<b>` is an attribute element:
	*
	* ```html
	* <p>foo[]bar</p> -> <p>foo{}bar</p>
	* <p><b>foo</b>[]<b>bar</b></p> -> <p><b>foo{}bar</b></p>
	* <p><b foo="bar">a</b>[]<b foo="baz">b</b></p> -> <p><b foo="bar">a</b>[]<b foo="baz">b</b></p>
	* ```
	*
	* It will also take care about empty attributes when merging:
	*
	* ```html
	* <p><b>[]</b></p> -> <p>[]</p>
	* <p><b>foo</b><i>[]</i><b>bar</b></p> -> <p><b>foo{}bar</b></p>
	* ```
	*
	* **Note:** Difference between {@link module:engine/view/downcastwriter~ViewDowncastWriter#mergeAttributes mergeAttributes} and
	* {@link module:engine/view/downcastwriter~ViewDowncastWriter#mergeContainers mergeContainers} is that `mergeAttributes` merges two
	* {@link module:engine/view/attributeelement~ViewAttributeElement attribute elements} or
	* {@link module:engine/view/text~ViewText text nodes} while `mergeContainer` merges two
	* {@link module:engine/view/containerelement~ViewContainerElement container elements}.
	*
	* @see module:engine/view/attributeelement~ViewAttributeElement
	* @see module:engine/view/containerelement~ViewContainerElement
	* @see module:engine/view/downcastwriter~ViewDowncastWriter#mergeContainers
	* @param position Merge position.
	* @returns Position after merge.
	*/
	mergeAttributes(position) {
		const positionOffset = position.offset;
		const positionParent = position.parent;
		if (positionParent.is("$text")) return position;
		if (positionParent.is("attributeElement") && positionParent.childCount === 0) {
			const parent = positionParent.parent;
			const offset = positionParent.index;
			positionParent._remove();
			this._removeFromClonedElementsGroup(positionParent);
			return this.mergeAttributes(new ViewPosition(parent, offset));
		}
		const nodeBefore = positionParent.getChild(positionOffset - 1);
		const nodeAfter = positionParent.getChild(positionOffset);
		if (!nodeBefore || !nodeAfter) return position;
		if (nodeBefore.is("$text") && nodeAfter.is("$text")) return mergeTextNodes(nodeBefore, nodeAfter);
		else if (nodeBefore.is("attributeElement") && nodeAfter.is("attributeElement") && nodeBefore.isSimilar(nodeAfter)) {
			const count = nodeBefore.childCount;
			nodeBefore._appendChild(nodeAfter.getChildren());
			nodeAfter._remove();
			this._removeFromClonedElementsGroup(nodeAfter);
			return this.mergeAttributes(new ViewPosition(nodeBefore, count));
		}
		return position;
	}
	/**
	* Merges two {@link module:engine/view/containerelement~ViewContainerElement container elements} that are
	* before and after given position. Precisely, the element after the position is removed and it's contents are
	* moved to element before the position.
	*
	* ```html
	* <p>foo</p>^<p>bar</p> -> <p>foo^bar</p>
	* <div>foo</div>^<p>bar</p> -> <div>foo^bar</div>
	* ```
	*
	* **Note:** Difference between {@link module:engine/view/downcastwriter~ViewDowncastWriter#mergeAttributes mergeAttributes} and
	* {@link module:engine/view/downcastwriter~ViewDowncastWriter#mergeContainers mergeContainers} is that `mergeAttributes` merges two
	* {@link module:engine/view/attributeelement~ViewAttributeElement attribute elements} or
	* {@link module:engine/view/text~ViewText text nodes} while `mergeContainer` merges two
	* {@link module:engine/view/containerelement~ViewContainerElement container elements}.
	*
	* @see module:engine/view/attributeelement~ViewAttributeElement
	* @see module:engine/view/containerelement~ViewContainerElement
	* @see module:engine/view/downcastwriter~ViewDowncastWriter#mergeAttributes
	* @param position Merge position.
	* @returns Position after merge.
	*/
	mergeContainers(position) {
		const prev = position.nodeBefore;
		const next = position.nodeAfter;
		if (!prev || !next || !prev.is("containerElement") || !next.is("containerElement"))
 /**
		* Element before and after given position cannot be merged.
		*
		* @error view-writer-merge-containers-invalid-position
		*/
		throw new CKEditorError("view-writer-merge-containers-invalid-position", this.document);
		const lastChild = prev.getChild(prev.childCount - 1);
		const newPosition = lastChild instanceof ViewText ? ViewPosition._createAt(lastChild, "end") : ViewPosition._createAt(prev, "end");
		this.move(ViewRange._createIn(next), ViewPosition._createAt(prev, "end"));
		this.remove(ViewRange._createOn(next));
		return newPosition;
	}
	/**
	* Inserts a node or nodes at specified position. Takes care about breaking attributes before insertion
	* and merging them afterwards.
	*
	* Throws {@link module:utils/ckeditorerror~CKEditorError CKEditorError} `view-writer-insert-invalid-node` when nodes to insert
	* contains instances that are not {@link module:engine/view/text~ViewText Texts},
	* {@link module:engine/view/attributeelement~ViewAttributeElement ViewAttributeElements},
	* {@link module:engine/view/containerelement~ViewContainerElement ViewContainerElements},
	* {@link module:engine/view/emptyelement~ViewEmptyElement ViewEmptyElements},
	* {@link module:engine/view/rawelement~ViewRawElement RawElements} or
	* {@link module:engine/view/uielement~ViewUIElement UIElements}.
	*
	* @param position Insertion position.
	* @param nodes Node or nodes to insert.
	* @returns Range around inserted nodes.
	*/
	insert(position, nodes) {
		nodes = isIterable(nodes) ? [...nodes] : [nodes];
		validateNodesToInsert(nodes, this.document);
		const nodeGroups = nodes.reduce((groups, node) => {
			const lastGroup = groups[groups.length - 1];
			const breakAttributes = !node.is("uiElement");
			if (!lastGroup || lastGroup.breakAttributes != breakAttributes) groups.push({
				breakAttributes,
				nodes: [node]
			});
			else lastGroup.nodes.push(node);
			return groups;
		}, []);
		let start = null;
		let end = position;
		for (const { nodes, breakAttributes } of nodeGroups) {
			const range = this._insertNodes(end, nodes, breakAttributes);
			if (!start) start = range.start;
			end = range.end;
		}
		if (!start) return new ViewRange(position);
		return new ViewRange(start, end);
	}
	/**
	* Removes provided range from the container.
	*
	* Throws {@link module:utils/ckeditorerror~CKEditorError CKEditorError} `view-writer-invalid-range-container` when
	* {@link module:engine/view/range~ViewRange#start start} and {@link module:engine/view/range~ViewRange#end end}
	* positions are not placed inside same parent container.
	*
	* @param rangeOrItem Range to remove from container
	* or an {@link module:engine/view/item~ViewItem item} to remove. If range is provided, after removing, it will be updated
	* to a collapsed range showing the new position.
	* @returns Document fragment containing removed nodes.
	*/
	remove(rangeOrItem) {
		const range = rangeOrItem instanceof ViewRange ? rangeOrItem : ViewRange._createOn(rangeOrItem);
		validateRangeContainer(range, this.document);
		if (range.isCollapsed) return new ViewDocumentFragment(this.document);
		const { start: breakStart, end: breakEnd } = this._breakAttributesRange(range, true);
		const parentContainer = breakStart.parent;
		const count = breakEnd.offset - breakStart.offset;
		const removed = parentContainer._removeChildren(breakStart.offset, count);
		for (const node of removed) this._removeFromClonedElementsGroup(node);
		const mergePosition = this.mergeAttributes(breakStart);
		range.start = mergePosition;
		range.end = mergePosition.clone();
		return new ViewDocumentFragment(this.document, removed);
	}
	/**
	* Removes matching elements from given range.
	*
	* Throws {@link module:utils/ckeditorerror~CKEditorError CKEditorError} `view-writer-invalid-range-container` when
	* {@link module:engine/view/range~ViewRange#start start} and {@link module:engine/view/range~ViewRange#end end}
	* positions are not placed inside same parent container.
	*
	* @param range Range to clear.
	* @param element Element to remove.
	*/
	clear(range, element) {
		validateRangeContainer(range, this.document);
		const walker = range.getWalker({
			direction: "backward",
			ignoreElementEnd: true
		});
		for (const current of walker) {
			const item = current.item;
			let rangeToRemove;
			if (item.is("element") && element.isSimilar(item)) rangeToRemove = ViewRange._createOn(item);
			else if (!current.nextPosition.isAfter(range.start) && item.is("$textProxy")) {
				const parentElement = item.getAncestors().find((ancestor) => {
					return ancestor.is("element") && element.isSimilar(ancestor);
				});
				if (parentElement) rangeToRemove = ViewRange._createIn(parentElement);
			}
			if (rangeToRemove) {
				if (rangeToRemove.end.isAfter(range.end)) rangeToRemove.end = range.end;
				if (rangeToRemove.start.isBefore(range.start)) rangeToRemove.start = range.start;
				this.remove(rangeToRemove);
			}
		}
	}
	/**
	* Moves nodes from provided range to target position.
	*
	* Throws {@link module:utils/ckeditorerror~CKEditorError CKEditorError} `view-writer-invalid-range-container` when
	* {@link module:engine/view/range~ViewRange#start start} and {@link module:engine/view/range~ViewRange#end end}
	* positions are not placed inside same parent container.
	*
	* @param sourceRange Range containing nodes to move.
	* @param targetPosition Position to insert.
	* @returns Range in target container. Inserted nodes are placed between
	* {@link module:engine/view/range~ViewRange#start start} and {@link module:engine/view/range~ViewRange#end end} positions.
	*/
	move(sourceRange, targetPosition) {
		let nodes;
		if (targetPosition.isAfter(sourceRange.end)) {
			targetPosition = this._breakAttributes(targetPosition, true);
			const parent = targetPosition.parent;
			const countBefore = parent.childCount;
			sourceRange = this._breakAttributesRange(sourceRange, true);
			nodes = this.remove(sourceRange);
			targetPosition.offset += parent.childCount - countBefore;
		} else nodes = this.remove(sourceRange);
		return this.insert(targetPosition, nodes);
	}
	/**
	* Wraps elements within range with provided {@link module:engine/view/attributeelement~ViewAttributeElement ViewAttributeElement}.
	* If a collapsed range is provided, it will be wrapped only if it is equal to view selection.
	*
	* If a collapsed range was passed and is same as selection, the selection
	* will be moved to the inside of the wrapped attribute element.
	*
	* Throws {@link module:utils/ckeditorerror~CKEditorError} `view-writer-invalid-range-container`
	* when {@link module:engine/view/range~ViewRange#start}
	* and {@link module:engine/view/range~ViewRange#end} positions are not placed inside same parent container.
	*
	* Throws {@link module:utils/ckeditorerror~CKEditorError} `view-writer-wrap-invalid-attribute` when passed attribute element is not
	* an instance of {@link module:engine/view/attributeelement~ViewAttributeElement ViewAttributeElement}.
	*
	* Throws {@link module:utils/ckeditorerror~CKEditorError} `view-writer-wrap-nonselection-collapsed-range` when passed range
	* is collapsed and different than view selection.
	*
	* @param range Range to wrap.
	* @param attribute Attribute element to use as wrapper.
	* @returns range Range after wrapping, spanning over wrapping attribute element.
	*/
	wrap(range, attribute) {
		if (!(attribute instanceof ViewAttributeElement)) throw new CKEditorError("view-writer-wrap-invalid-attribute", this.document);
		validateRangeContainer(range, this.document);
		if (!range.isCollapsed) return this._wrapRange(range, attribute);
		else {
			let position = range.start;
			if (position.parent.is("element") && !_hasNonUiChildren(position.parent)) position = position.getLastMatchingPosition((value) => value.item.is("uiElement"));
			position = this._wrapPosition(position, attribute);
			const viewSelection = this.document.selection;
			if (viewSelection.isCollapsed && viewSelection.getFirstPosition().isEqual(range.start)) this.setSelection(position);
			return new ViewRange(position);
		}
	}
	/**
	* Unwraps nodes within provided range from attribute element.
	*
	* Throws {@link module:utils/ckeditorerror~CKEditorError CKEditorError} `view-writer-invalid-range-container` when
	* {@link module:engine/view/range~ViewRange#start start} and {@link module:engine/view/range~ViewRange#end end}
	* positions are not placed inside same parent container.
	*/
	unwrap(range, attribute) {
		if (!(attribute instanceof ViewAttributeElement))
 /**
		* The `attribute` passed to {@link module:engine/view/downcastwriter~ViewDowncastWriter#unwrap `ViewDowncastWriter#unwrap()`}
		* must be an instance of {@link module:engine/view/attributeelement~ViewAttributeElement `AttributeElement`}.
		*
		* @error view-writer-unwrap-invalid-attribute
		*/
		throw new CKEditorError("view-writer-unwrap-invalid-attribute", this.document);
		validateRangeContainer(range, this.document);
		if (range.isCollapsed) return range;
		const { start: breakStart, end: breakEnd } = this._breakAttributesRange(range, true);
		const parentContainer = breakStart.parent;
		const newRange = this._unwrapChildren(parentContainer, breakStart.offset, breakEnd.offset, attribute);
		const start = this.mergeAttributes(newRange.start);
		if (!start.isEqual(newRange.start)) newRange.end.offset--;
		return new ViewRange(start, this.mergeAttributes(newRange.end));
	}
	/**
	* Renames element by creating a copy of renamed element but with changed name and then moving contents of the
	* old element to the new one. Keep in mind that this will invalidate all {@link module:engine/view/position~ViewPosition positions}
	* which has renamed element as {@link module:engine/view/position~ViewPosition#parent a parent}.
	*
	* New element has to be created because `Element#tagName` property in DOM is readonly.
	*
	* Since this function creates a new element and removes the given one, the new element is returned to keep reference.
	*
	* @param newName New name for element.
	* @param viewElement Element to be renamed.
	* @returns Element created due to rename.
	*/
	rename(newName, viewElement) {
		const newElement = new ViewContainerElement(this.document, newName, viewElement.getAttributes());
		this.insert(ViewPosition._createAfter(viewElement), newElement);
		this.move(ViewRange._createIn(viewElement), ViewPosition._createAt(newElement, 0));
		this.remove(ViewRange._createOn(viewElement));
		return newElement;
	}
	/**
	* Cleans up memory by removing obsolete cloned elements group from the writer.
	*
	* Should be used whenever all {@link module:engine/view/attributeelement~ViewAttributeElement attribute elements}
	* with the same {@link module:engine/view/attributeelement~ViewAttributeElement#id id} are going to be removed from the view and
	* the group will no longer be needed.
	*
	* Cloned elements group are not removed automatically in case if the group is still needed after all its elements
	* were removed from the view.
	*
	* Keep in mind that group names are equal to the `id` property of the attribute element.
	*
	* @param groupName Name of the group to clear.
	*/
	clearClonedElementsGroup(groupName) {
		this._cloneGroups.delete(groupName);
	}
	/**
	* Creates position at the given location. The location can be specified as:
	*
	* * a {@link module:engine/view/position~ViewPosition position},
	* * parent element and offset (offset defaults to `0`),
	* * parent element and `'end'` (sets position at the end of that element),
	* * {@link module:engine/view/item~ViewItem view item} and `'before'` or `'after'` (sets position before or after given view item).
	*
	* This method is a shortcut to other constructors such as:
	*
	* * {@link #createPositionBefore},
	* * {@link #createPositionAfter},
	*
	* @param offset Offset or one of the flags. Used only when the first parameter is a {@link module:engine/view/item~ViewItem view item}.
	*/
	createPositionAt(itemOrPosition, offset) {
		return ViewPosition._createAt(itemOrPosition, offset);
	}
	/**
	* Creates a new position after given view item.
	*
	* @param item View item after which the position should be located.
	*/
	createPositionAfter(item) {
		return ViewPosition._createAfter(item);
	}
	/**
	* Creates a new position before given view item.
	*
	* @param item View item before which the position should be located.
	*/
	createPositionBefore(item) {
		return ViewPosition._createBefore(item);
	}
	/**
	* Creates a range spanning from `start` position to `end` position.
	*
	* **Note:** This factory method creates its own {@link module:engine/view/position~ViewPosition} instances basing on passed values.
	*
	* @param start Start position.
	* @param end End position. If not set, range will be collapsed at `start` position.
	*/
	createRange(start, end) {
		return new ViewRange(start, end);
	}
	/**
	* Creates a range that starts before given {@link module:engine/view/item~ViewItem view item} and ends after it.
	*/
	createRangeOn(item) {
		return ViewRange._createOn(item);
	}
	/**
	* Creates a range inside an {@link module:engine/view/element~ViewElement element} which starts before the first child of
	* that element and ends after the last child of that element.
	*
	* @param element Element which is a parent for the range.
	*/
	createRangeIn(element) {
		return ViewRange._createIn(element);
	}
	createSelection(...args) {
		return new ViewSelection(...args);
	}
	/**
	* Creates placeholders for child elements of the {@link module:engine/conversion/downcasthelpers~DowncastHelpers#elementToStructure
	* `elementToStructure()`} conversion helper.
	*
	* ```ts
	* const viewSlot = conversionApi.writer.createSlot();
	* const viewPosition = conversionApi.writer.createPositionAt( viewElement, 0 );
	*
	* conversionApi.writer.insert( viewPosition, viewSlot );
	* ```
	*
	* It could be filtered down to a specific subset of children (only `<foo>` model elements in this case):
	*
	* ```ts
	* const viewSlot = conversionApi.writer.createSlot( node => node.is( 'element', 'foo' ) );
	* const viewPosition = conversionApi.writer.createPositionAt( viewElement, 0 );
	*
	* conversionApi.writer.insert( viewPosition, viewSlot );
	* ```
	*
	* While providing a filtered slot, make sure to provide slots for all child nodes. A single node cannot be downcasted into
	* multiple slots.
	*
	* **Note**: You should not change the order of nodes. View elements should be in the same order as model nodes.
	*
	* @param modeOrFilter The filter for child nodes.
	* @returns The slot element to be placed in to the view structure while processing
	* {@link module:engine/conversion/downcasthelpers~DowncastHelpers#elementToStructure `elementToStructure()`}.
	*/
	createSlot(modeOrFilter = "children") {
		if (!this._slotFactory)
 /**
		* The `createSlot()` method is only allowed inside the `elementToStructure` downcast helper callback.
		*
		* @error view-writer-invalid-create-slot-context
		*/
		throw new CKEditorError("view-writer-invalid-create-slot-context", this.document);
		return this._slotFactory(this, modeOrFilter);
	}
	/**
	* Registers a slot factory.
	*
	* @internal
	* @param slotFactory The slot factory.
	*/
	_registerSlotFactory(slotFactory) {
		this._slotFactory = slotFactory;
	}
	/**
	* Clears the registered slot factory.
	*
	* @internal
	*/
	_clearSlotFactory() {
		this._slotFactory = null;
	}
	/**
	* Inserts a node or nodes at the specified position. Takes care of breaking attributes before insertion
	* and merging them afterwards if requested by the breakAttributes param.
	*
	* @param position Insertion position.
	* @param nodes Node or nodes to insert.
	* @param breakAttributes Whether attributes should be broken.
	* @returns Range around inserted nodes.
	*/
	_insertNodes(position, nodes, breakAttributes) {
		let parentElement;
		if (breakAttributes) parentElement = getParentContainer(position);
		else parentElement = position.parent.is("$text") ? position.parent.parent : position.parent;
		if (!parentElement)
 /**
		* Position's parent container cannot be found.
		*
		* @error view-writer-invalid-position-container
		*/
		throw new CKEditorError("view-writer-invalid-position-container", this.document);
		let insertionPosition;
		if (breakAttributes) insertionPosition = this._breakAttributes(position, true);
		else insertionPosition = position.parent.is("$text") ? breakTextNode(position) : position;
		const length = parentElement._insertChild(insertionPosition.offset, nodes);
		for (const node of nodes) this._addToClonedElementsGroup(node);
		const endPosition = insertionPosition.getShiftedBy(length);
		const start = this.mergeAttributes(insertionPosition);
		if (!start.isEqual(insertionPosition)) endPosition.offset--;
		return new ViewRange(start, this.mergeAttributes(endPosition));
	}
	/**
	* Wraps children with provided `wrapElement`. Only children contained in `parent` element between
	* `startOffset` and `endOffset` will be wrapped.
	*/
	_wrapChildren(parent, startOffset, endOffset, wrapElement) {
		let i = startOffset;
		const wrapPositions = [];
		while (i < endOffset) {
			const child = parent.getChild(i);
			const isText = child.is("$text");
			const isAttribute = child.is("attributeElement");
			if (isAttribute && child._canMergeAttributesFrom(wrapElement)) {
				child._mergeAttributesFrom(wrapElement);
				wrapPositions.push(new ViewPosition(parent, i));
			} else if (isText || !isAttribute || shouldABeOutsideB(wrapElement, child)) {
				const newAttribute = wrapElement._clone();
				child._remove();
				newAttribute._appendChild(child);
				parent._insertChild(i, newAttribute);
				this._addToClonedElementsGroup(newAttribute);
				wrapPositions.push(new ViewPosition(parent, i));
			} else this._wrapChildren(child, 0, child.childCount, wrapElement);
			i++;
		}
		let offsetChange = 0;
		for (const position of wrapPositions) {
			position.offset -= offsetChange;
			if (position.offset == startOffset) continue;
			if (!this.mergeAttributes(position).isEqual(position)) {
				offsetChange++;
				endOffset--;
			}
		}
		return ViewRange._createFromParentsAndOffsets(parent, startOffset, parent, endOffset);
	}
	/**
	* Unwraps children from provided `unwrapElement`. Only children contained in `parent` element between
	* `startOffset` and `endOffset` will be unwrapped.
	*/
	_unwrapChildren(parent, startOffset, endOffset, unwrapElement) {
		let i = startOffset;
		const unwrapPositions = [];
		while (i < endOffset) {
			const child = parent.getChild(i);
			if (!child.is("attributeElement")) {
				i++;
				continue;
			}
			if (child.isSimilar(unwrapElement)) {
				const unwrapped = child.getChildren();
				const count = child.childCount;
				child._remove();
				parent._insertChild(i, unwrapped);
				this._removeFromClonedElementsGroup(child);
				unwrapPositions.push(new ViewPosition(parent, i), new ViewPosition(parent, i + count));
				i += count;
				endOffset += count - 1;
				continue;
			}
			if (child._canSubtractAttributesOf(unwrapElement)) {
				child._subtractAttributesOf(unwrapElement);
				unwrapPositions.push(new ViewPosition(parent, i), new ViewPosition(parent, i + 1));
				i++;
				continue;
			}
			this._unwrapChildren(child, 0, child.childCount, unwrapElement);
			i++;
		}
		let offsetChange = 0;
		for (const position of unwrapPositions) {
			position.offset -= offsetChange;
			if (position.offset == startOffset || position.offset == endOffset) continue;
			if (!this.mergeAttributes(position).isEqual(position)) {
				offsetChange++;
				endOffset--;
			}
		}
		return ViewRange._createFromParentsAndOffsets(parent, startOffset, parent, endOffset);
	}
	/**
	* Helper function for `view.writer.wrap`. Wraps range with provided attribute element.
	* This method will also merge newly added attribute element with its siblings whenever possible.
	*
	* Throws {@link module:utils/ckeditorerror~CKEditorError} `view-writer-wrap-invalid-attribute` when passed attribute element is not
	* an instance of {@link module:engine/view/attributeelement~ViewAttributeElement ViewAttributeElement}.
	*
	* @returns New range after wrapping, spanning over wrapping attribute element.
	*/
	_wrapRange(range, attribute) {
		const { start: breakStart, end: breakEnd } = this._breakAttributesRange(range, true);
		const parentContainer = breakStart.parent;
		const newRange = this._wrapChildren(parentContainer, breakStart.offset, breakEnd.offset, attribute);
		const start = this.mergeAttributes(newRange.start);
		if (!start.isEqual(newRange.start)) newRange.end.offset--;
		return new ViewRange(start, this.mergeAttributes(newRange.end));
	}
	/**
	* Helper function for {@link #wrap}. Wraps position with provided attribute element.
	* This method will also merge newly added attribute element with its siblings whenever possible.
	*
	* Throws {@link module:utils/ckeditorerror~CKEditorError} `view-writer-wrap-invalid-attribute` when passed attribute element is not
	* an instance of {@link module:engine/view/attributeelement~ViewAttributeElement ViewAttributeElement}.
	*
	* @returns New position after wrapping.
	*/
	_wrapPosition(position, attribute) {
		if (attribute.isSimilar(position.parent)) return movePositionToTextNode(position.clone());
		if (position.parent.is("$text")) position = breakTextNode(position);
		const fakeElement = this.createAttributeElement("_wrapPosition-fake-element");
		fakeElement._priority = Number.POSITIVE_INFINITY;
		fakeElement.isSimilar = () => false;
		position.parent._insertChild(position.offset, fakeElement);
		const wrapRange = new ViewRange(position, position.getShiftedBy(1));
		this.wrap(wrapRange, attribute);
		const newPosition = new ViewPosition(fakeElement.parent, fakeElement.index);
		fakeElement._remove();
		const nodeBefore = newPosition.nodeBefore;
		const nodeAfter = newPosition.nodeAfter;
		if (nodeBefore && nodeBefore.is("view:$text") && nodeAfter && nodeAfter.is("view:$text")) return mergeTextNodes(nodeBefore, nodeAfter);
		return movePositionToTextNode(newPosition);
	}
	/**
	* Helper function used by other `ViewDowncastWriter` methods. Breaks attribute elements at the boundaries of given range.
	*
	* @param range Range which `start` and `end` positions will be used to break attributes.
	* @param forceSplitText If set to `true`, will break text nodes even if they are directly in container element.
	* This behavior will result in incorrect view state, but is needed by other view writing methods which then fixes view state.
	* @returns New range with located at break positions.
	*/
	_breakAttributesRange(range, forceSplitText = false) {
		const rangeStart = range.start;
		const rangeEnd = range.end;
		validateRangeContainer(range, this.document);
		if (range.isCollapsed) {
			const position = this._breakAttributes(range.start, forceSplitText);
			return new ViewRange(position, position);
		}
		const breakEnd = this._breakAttributes(rangeEnd, forceSplitText);
		const count = breakEnd.parent.childCount;
		const breakStart = this._breakAttributes(rangeStart, forceSplitText);
		breakEnd.offset += breakEnd.parent.childCount - count;
		return new ViewRange(breakStart, breakEnd);
	}
	/**
	* Helper function used by other `ViewDowncastWriter` methods. Breaks attribute elements at given position.
	*
	* Throws {@link module:utils/ckeditorerror~CKEditorError CKEditorError} `view-writer-cannot-break-empty-element` when break position
	* is placed inside {@link module:engine/view/emptyelement~ViewEmptyElement ViewEmptyElement}.
	*
	* Throws {@link module:utils/ckeditorerror~CKEditorError CKEditorError} `view-writer-cannot-break-ui-element` when break position
	* is placed inside {@link module:engine/view/uielement~ViewUIElement UIElement}.
	*
	* @param position Position where to break attributes.
	* @param forceSplitText If set to `true`, will break text nodes even if they are directly in container element.
	* This behavior will result in incorrect view state, but is needed by other view writing methods which then fixes view state.
	* @returns New position after breaking the attributes.
	*/
	_breakAttributes(position, forceSplitText = false) {
		const positionOffset = position.offset;
		const positionParent = position.parent;
		if (position.parent.is("emptyElement"))
 /**
		* Cannot break an `EmptyElement` instance.
		*
		* This error is thrown if
		* {@link module:engine/view/downcastwriter~ViewDowncastWriter#breakAttributes `ViewDowncastWriter#breakAttributes()`}
		* was executed in an incorrect position.
		*
		* @error view-writer-cannot-break-empty-element
		*/
		throw new CKEditorError("view-writer-cannot-break-empty-element", this.document);
		if (position.parent.is("uiElement"))
 /**
		* Cannot break a `UIElement` instance.
		*
		* This error is thrown if
		* {@link module:engine/view/downcastwriter~ViewDowncastWriter#breakAttributes `ViewDowncastWriter#breakAttributes()`}
		* was executed in an incorrect position.
		*
		* @error view-writer-cannot-break-ui-element
		*/
		throw new CKEditorError("view-writer-cannot-break-ui-element", this.document);
		if (position.parent.is("rawElement"))
 /**
		* Cannot break a `RawElement` instance.
		*
		* This error is thrown if
		* {@link module:engine/view/downcastwriter~ViewDowncastWriter#breakAttributes `ViewDowncastWriter#breakAttributes()`}
		* was executed in an incorrect position.
		*
		* @error view-writer-cannot-break-raw-element
		*/
		throw new CKEditorError("view-writer-cannot-break-raw-element", this.document);
		if (!forceSplitText && positionParent.is("$text") && isContainerOrFragment(positionParent.parent)) return position.clone();
		if (isContainerOrFragment(positionParent)) return position.clone();
		if (positionParent.is("$text")) return this._breakAttributes(breakTextNode(position), forceSplitText);
		if (positionOffset == positionParent.childCount) {
			const newPosition = new ViewPosition(positionParent.parent, positionParent.index + 1);
			return this._breakAttributes(newPosition, forceSplitText);
		} else if (positionOffset === 0) {
			const newPosition = new ViewPosition(positionParent.parent, positionParent.index);
			return this._breakAttributes(newPosition, forceSplitText);
		} else {
			const offsetAfter = positionParent.index + 1;
			const clonedNode = positionParent._clone();
			positionParent.parent._insertChild(offsetAfter, clonedNode);
			this._addToClonedElementsGroup(clonedNode);
			const count = positionParent.childCount - positionOffset;
			const nodesToMove = positionParent._removeChildren(positionOffset, count);
			clonedNode._appendChild(nodesToMove);
			const newPosition = new ViewPosition(positionParent.parent, offsetAfter);
			return this._breakAttributes(newPosition, forceSplitText);
		}
	}
	/**
	* Stores the information that an {@link module:engine/view/attributeelement~ViewAttributeElement attribute element} was
	* added to the tree. Saves the reference to the group in the given element and updates the group, so other elements
	* from the group now keep a reference to the given attribute element.
	*
	* The clones group can be obtained using {@link module:engine/view/attributeelement~ViewAttributeElement#getElementsWithSameId}.
	*
	* Does nothing if added element has no {@link module:engine/view/attributeelement~ViewAttributeElement#id id}.
	*
	* @param element Attribute element to save.
	*/
	_addToClonedElementsGroup(element) {
		if (!element.root.is("rootElement")) return;
		if (element.is("element")) for (const child of element.getChildren()) this._addToClonedElementsGroup(child);
		const id = element.id;
		if (!id) return;
		let group = this._cloneGroups.get(id);
		if (!group) {
			group = /* @__PURE__ */ new Set();
			this._cloneGroups.set(id, group);
		}
		group.add(element);
		element._clonesGroup = group;
	}
	/**
	* Removes all the information about the given {@link module:engine/view/attributeelement~ViewAttributeElement attribute element}
	* from its clones group.
	*
	* Keep in mind, that the element will still keep a reference to the group (but the group will not keep a reference to it).
	* This allows to reference the whole group even if the element was already removed from the tree.
	*
	* Does nothing if the element has no {@link module:engine/view/attributeelement~ViewAttributeElement#id id}.
	*
	* @param element Attribute element to remove.
	*/
	_removeFromClonedElementsGroup(element) {
		if (element.is("element")) for (const child of element.getChildren()) this._removeFromClonedElementsGroup(child);
		const id = element.id;
		if (!id) return;
		const group = this._cloneGroups.get(id);
		if (!group) return;
		group.delete(element);
	}
};
function _hasNonUiChildren(parent) {
	return Array.from(parent.getChildren()).some((child) => !child.is("uiElement"));
}
/**
* The `attribute` passed to {@link module:engine/view/downcastwriter~ViewDowncastWriter#wrap `ViewDowncastWriter#wrap()`}
* must be an instance of {@link module:engine/view/attributeelement~ViewAttributeElement `AttributeElement`}.
*
* @error view-writer-wrap-invalid-attribute
*/
/**
* Returns first parent container of specified {@link module:engine/view/position~ViewPosition Position}.
* Position's parent node is checked as first, then next parents are checked.
* Note that {@link module:engine/view/documentfragment~ViewDocumentFragment DocumentFragment} is treated like a container.
*
* @param position Position used as a start point to locate parent container.
* @returns Parent container element or `undefined` if container is not found.
*/
function getParentContainer(position) {
	let parent = position.parent;
	while (!isContainerOrFragment(parent)) {
		if (!parent) return;
		parent = parent.parent;
	}
	return parent;
}
/**
* Checks if first {@link module:engine/view/attributeelement~ViewAttributeElement ViewAttributeElement} provided to the function
* can be wrapped outside second element. It is done by comparing elements'
* {@link module:engine/view/attributeelement~ViewAttributeElement#priority priorities}, if both have same priority
* {@link module:engine/view/element~ViewElement#getIdentity identities} are compared.
*/
function shouldABeOutsideB(a, b) {
	if (a.priority < b.priority) return true;
	else if (a.priority > b.priority) return false;
	return a.getIdentity() < b.getIdentity();
}
/**
* Returns new position that is moved to near text node. Returns same position if there is no text node before of after
* specified position.
*
* ```html
* <p>foo[]</p>  ->  <p>foo{}</p>
* <p>[]foo</p>  ->  <p>{}foo</p>
* ```
*
* @returns Position located inside text node or same position if there is no text nodes
* before or after position location.
*/
function movePositionToTextNode(position) {
	const nodeBefore = position.nodeBefore;
	if (nodeBefore && nodeBefore.is("$text")) return new ViewPosition(nodeBefore, nodeBefore.data.length);
	const nodeAfter = position.nodeAfter;
	if (nodeAfter && nodeAfter.is("$text")) return new ViewPosition(nodeAfter, 0);
	return position;
}
/**
* Breaks text node into two text nodes when possible.
*
* ```html
* <p>foo{}bar</p> -> <p>foo[]bar</p>
* <p>{}foobar</p> -> <p>[]foobar</p>
* <p>foobar{}</p> -> <p>foobar[]</p>
* ```
*
* @param position Position that need to be placed inside text node.
* @returns New position after breaking text node.
*/
function breakTextNode(position) {
	if (position.offset == position.parent.data.length) return new ViewPosition(position.parent.parent, position.parent.index + 1);
	if (position.offset === 0) return new ViewPosition(position.parent.parent, position.parent.index);
	const textToMove = position.parent.data.slice(position.offset);
	position.parent._data = position.parent.data.slice(0, position.offset);
	position.parent.parent._insertChild(position.parent.index + 1, new ViewText(position.root.document, textToMove));
	return new ViewPosition(position.parent.parent, position.parent.index + 1);
}
/**
* Merges two text nodes into first node. Removes second node and returns merge position.
*
* @param t1 First text node to merge. Data from second text node will be moved at the end of this text node.
* @param t2 Second text node to merge. This node will be removed after merging.
* @returns Position after merging text nodes.
*/
function mergeTextNodes(t1, t2) {
	const nodeBeforeLength = t1.data.length;
	t1._data += t2.data;
	t2._remove();
	return new ViewPosition(t1, nodeBeforeLength);
}
const validNodesToInsert = [
	ViewText,
	ViewAttributeElement,
	ViewContainerElement,
	ViewEmptyElement,
	ViewRawElement,
	ViewUIElement
];
/**
* Checks if provided nodes are valid to insert.
*
* Throws {@link module:utils/ckeditorerror~CKEditorError CKEditorError} `view-writer-insert-invalid-node` when nodes to insert
* contains instances that are not supported ones (see error description for valid ones.
*/
function validateNodesToInsert(nodes, errorContext) {
	for (const node of nodes) {
		if (!validNodesToInsert.some((validNode) => node instanceof validNode))
 /**
		* One of the nodes to be inserted is of an invalid type.
		*
		* Nodes to be inserted with {@link module:engine/view/downcastwriter~ViewDowncastWriter#insert `ViewDowncastWriter#insert()`}
		* should be of the following types:
		*
		* * {@link module:engine/view/attributeelement~ViewAttributeElement ViewAttributeElement},
		* * {@link module:engine/view/containerelement~ViewContainerElement ViewContainerElement},
		* * {@link module:engine/view/emptyelement~ViewEmptyElement ViewEmptyElement},
		* * {@link module:engine/view/uielement~ViewUIElement UIElement},
		* * {@link module:engine/view/rawelement~ViewRawElement RawElement},
		* * {@link module:engine/view/text~ViewText Text}.
		*
		* @error view-writer-insert-invalid-node-type
		*/
		throw new CKEditorError("view-writer-insert-invalid-node-type", errorContext);
		if (!node.is("$text")) validateNodesToInsert(node.getChildren(), errorContext);
	}
}
/**
* Checks if node is ViewContainerElement or DocumentFragment, because in most cases they should be treated the same way.
*
* @returns Returns `true` if node is instance of ViewContainerElement or DocumentFragment.
*/
function isContainerOrFragment(node) {
	return node && (node.is("containerElement") || node.is("documentFragment"));
}
/**
* Checks if {@link module:engine/view/range~ViewRange#start range start} and {@link module:engine/view/range~ViewRange#end range end}
* are placed inside same {@link module:engine/view/containerelement~ViewContainerElement container element}.
* Throws {@link module:utils/ckeditorerror~CKEditorError CKEditorError} `view-writer-invalid-range-container` when validation fails.
*/
function validateRangeContainer(range, errorContext) {
	const startContainer = getParentContainer(range.start);
	const endContainer = getParentContainer(range.end);
	if (!startContainer || !endContainer || startContainer !== endContainer)
 /**
	* The container of the given range is invalid.
	*
	* This may happen if {@link module:engine/view/range~ViewRange#start range start} and
	* {@link module:engine/view/range~ViewRange#end range end} positions are not placed inside the same container element or
	* a parent container for these positions cannot be found.
	*
	* Methods like {@link module:engine/view/downcastwriter~ViewDowncastWriter#wrap `ViewDowncastWriter#remove()`},
	* {@link module:engine/view/downcastwriter~ViewDowncastWriter#wrap `ViewDowncastWriter#clean()`},
	* {@link module:engine/view/downcastwriter~ViewDowncastWriter#wrap `ViewDowncastWriter#wrap()`},
	* {@link module:engine/view/downcastwriter~ViewDowncastWriter#wrap `ViewDowncastWriter#unwrap()`} need to be called
	* on a range that has its start and end positions located in the same container element. Both positions can be
	* nested within other elements (e.g. an attribute element) but the closest container ancestor must be the same.
	*
	* @error view-writer-invalid-range-container
	*/
	throw new CKEditorError("view-writer-invalid-range-container", errorContext);
}
/**
* Checks if the provided argument is a plain object that can be used as options for container element.
*/
function isContainerOptions(childrenOrOptions) {
	return isPlainObject(childrenOrOptions);
}

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* Set of utilities related to handling block and inline fillers.
*
* Browsers do not allow to put caret in elements which does not have height. Because of it, we need to fill all
* empty elements which should be selectable with elements or characters called "fillers". Unfortunately there is no one
* universal filler, this is why two types are uses:
*
* * Block filler is an element which fill block elements, like `<p>`. CKEditor uses `<br>` as a block filler during the editing,
* as browsers do natively. So instead of an empty `<p>` there will be `<p><br></p>`. The advantage of block filler is that
* it is transparent for the selection, so when the caret is before the `<br>` and user presses right arrow he will be
* moved to the next paragraph, not after the `<br>`. The disadvantage is that it breaks a block, so it cannot be used
* in the middle of a line of text. The {@link module:engine/view/filler~BR_FILLER `<br>` filler} can be replaced with any other
* character in the data output, for instance {@link module:engine/view/filler~NBSP_FILLER non-breaking space} or
* {@link module:engine/view/filler~MARKED_NBSP_FILLER marked non-breaking space}.
*
* * Inline filler is a filler which does not break a line of text, so it can be used inside the text, for instance in the empty
* `<b>` surrendered by text: `foo<b></b>bar`, if we want to put the caret there. CKEditor uses a sequence of the zero-width
* spaces as an {@link module:engine/view/filler~INLINE_FILLER inline filler} having the predetermined
* {@link module:engine/view/filler~INLINE_FILLER_LENGTH length}. A sequence is used, instead of a single character to
* avoid treating random zero-width spaces as the inline filler. Disadvantage of the inline filler is that it is not
* transparent for the selection. The arrow key moves the caret between zero-width spaces characters, so the additional
* code is needed to handle the caret.
*
* Both inline and block fillers are handled by the {@link module:engine/view/renderer~ViewRenderer renderer} and are not present in the
* view.
*
* @module engine/view/filler
*/
/**
* Non-breaking space filler creator. This function creates the `&nbsp;` text node.
* It defines how the filler is created.
*
* @see module:engine/view/filler~MARKED_NBSP_FILLER
* @see module:engine/view/filler~BR_FILLER
* @internal
*/
const NBSP_FILLER = (domDocument) => domDocument.createTextNode("\xA0");
/**
* Marked non-breaking space filler creator. This function creates the `<span data-cke-filler="true">&nbsp;</span>` element.
* It defines how the filler is created.
*
* @see module:engine/view/filler~NBSP_FILLER
* @see module:engine/view/filler~BR_FILLER
* @internal
*/
const MARKED_NBSP_FILLER = (domDocument) => {
	const span = domDocument.createElement("span");
	span.dataset.ckeFiller = "true";
	span.innerText = "\xA0";
	return span;
};
/**
* `<br>` filler creator. This function creates the `<br data-cke-filler="true">` element.
* It defines how the filler is created.
*
* @see module:engine/view/filler~NBSP_FILLER
* @see module:engine/view/filler~MARKED_NBSP_FILLER
* @internal
*/
const BR_FILLER = (domDocument) => {
	const fillerBr = domDocument.createElement("br");
	fillerBr.dataset.ckeFiller = "true";
	return fillerBr;
};
/**
* Length of the {@link module:engine/view/filler~INLINE_FILLER INLINE_FILLER}.
*
* @internal
*/
const INLINE_FILLER_LENGTH = 7;
/**
* Inline filler which is a sequence of the word joiners.
*
* @internal
*/
const INLINE_FILLER = "⁠".repeat(7);
/**
* Checks if the node is a text node which starts with the {@link module:engine/view/filler~INLINE_FILLER inline filler}.
*
* ```ts
* startsWithFiller( document.createTextNode( INLINE_FILLER ) ); // true
* startsWithFiller( document.createTextNode( INLINE_FILLER + 'foo' ) ); // true
* startsWithFiller( document.createTextNode( 'foo' ) ); // false
* startsWithFiller( document.createElement( 'p' ) ); // false
* ```
*
* @param domNode DOM node.
* @returns True if the text node starts with the {@link module:engine/view/filler~INLINE_FILLER inline filler}.
* @internal
*/
function startsWithFiller(domNode) {
	if (typeof domNode == "string") return domNode.substr(0, 7) === INLINE_FILLER;
	return isText(domNode) && domNode.data.substr(0, 7) === INLINE_FILLER;
}
/**
* Checks if the text node contains only the {@link module:engine/view/filler~INLINE_FILLER inline filler}.
*
* ```ts
* isInlineFiller( document.createTextNode( INLINE_FILLER ) ); // true
* isInlineFiller( document.createTextNode( INLINE_FILLER + 'foo' ) ); // false
* ```
*
* @param domText DOM text node.
* @returns True if the text node contains only the {@link module:engine/view/filler~INLINE_FILLER inline filler}.
* @internal
*/
function isInlineFiller(domText) {
	return domText.data.length == 7 && startsWithFiller(domText);
}
/**
* Get string data from the text node, removing an {@link module:engine/view/filler~INLINE_FILLER inline filler} from it,
* if text node contains it.
*
* ```ts
* getDataWithoutFiller( document.createTextNode( INLINE_FILLER + 'foo' ) ) == 'foo' // true
* getDataWithoutFiller( document.createTextNode( 'foo' ) ) == 'foo' // true
* ```
*
* @param domText DOM text node, possible with inline filler.
* @returns Data without filler.
* @internal
*/
function getDataWithoutFiller(domText) {
	const data = typeof domText == "string" ? domText : domText.data;
	if (startsWithFiller(domText)) return data.slice(7);
	return data;
}
/**
* Assign key observer which move cursor from the end of the inline filler to the beginning of it when
* the left arrow is pressed, so the filler does not break navigation.
*
* @param view View controller instance we should inject quirks handling on.
* @internal
*/
function injectQuirksHandling(view) {
	view.document.on("arrowKey", jumpOverInlineFiller, { priority: "low" });
}
/**
* Move cursor from the end of the inline filler to the beginning of it when, so the filler does not break navigation.
*/
function jumpOverInlineFiller(evt, data) {
	if (data.keyCode == keyCodes.arrowleft) {
		const domSelection = data.domTarget.ownerDocument.defaultView.getSelection();
		if (domSelection.rangeCount == 1 && domSelection.getRangeAt(0).collapsed) {
			const domParent = domSelection.getRangeAt(0).startContainer;
			const domOffset = domSelection.getRangeAt(0).startOffset;
			if (startsWithFiller(domParent) && domOffset <= 7) domSelection.collapse(domParent, 0);
		}
	}
}

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* @module engine/view/renderer
*/
const ViewRendererBase = /* #__PURE__ */ ObservableMixin();
/**
* Renderer is responsible for updating the DOM structure and the DOM selection based on
* the {@link module:engine/view/renderer~ViewRenderer#markToSync information about updated view nodes}.
* In other words, it renders the view to the DOM.
*
* Its main responsibility is to make only the necessary, minimal changes to the DOM. However, unlike in many
* virtual DOM implementations, the primary reason for doing minimal changes is not the performance but ensuring
* that native editing features such as text composition, autocompletion, spell checking, selection's x-index are
* affected as little as possible.
*
* Renderer uses {@link module:engine/view/domconverter~ViewDomConverter} to transform view nodes and positions
* to and from the DOM.
*/
var ViewRenderer = class extends ViewRendererBase {
	/**
	* Set of DOM Documents instances.
	*/
	domDocuments = /* @__PURE__ */ new Set();
	/**
	* Converter instance.
	*/
	domConverter;
	/**
	* Set of nodes which attributes changed and may need to be rendered.
	*/
	markedAttributes = /* @__PURE__ */ new Set();
	/**
	* Set of elements which child lists changed and may need to be rendered.
	*/
	markedChildren = /* @__PURE__ */ new Set();
	/**
	* Set of text nodes which text data changed and may need to be rendered.
	*/
	markedTexts = /* @__PURE__ */ new Set();
	/**
	* View selection. Renderer updates DOM selection based on the view selection.
	*/
	selection;
	/**
	* The text node in which the inline filler was rendered.
	*/
	_inlineFiller = null;
	/**
	* DOM element containing fake selection.
	*/
	_fakeSelectionContainer = null;
	/**
	* Creates a renderer instance.
	*
	* @param domConverter Converter instance.
	* @param selection View selection.
	*/
	constructor(domConverter, selection) {
		super();
		this.domConverter = domConverter;
		this.selection = selection;
		this.set("isFocused", false);
		this.set("isSelecting", false);
		this.set("isComposing", false);
		if (env.isBlink && !env.isAndroid) this.on("change:isSelecting", () => {
			if (!this.isSelecting) this.render();
		});
	}
	/**
	* Marks a view node to be updated in the DOM by {@link #render `render()`}.
	*
	* Note that only view nodes whose parents have corresponding DOM elements need to be marked to be synchronized.
	*
	* @see #markedAttributes
	* @see #markedChildren
	* @see #markedTexts
	*
	* @param type Type of the change.
	* @param node ViewNode to be marked.
	*/
	markToSync(type, node) {
		if (type === "text") {
			if (this.domConverter.mapViewToDom(node.parent)) this.markedTexts.add(node);
		} else {
			if (!this.domConverter.mapViewToDom(node)) return;
			if (type === "attributes") this.markedAttributes.add(node);
			else if (type === "children") this.markedChildren.add(node);
			else
 /**
			* Unknown type passed to Renderer.markToSync.
			*
			* @error view-renderer-unknown-type
			*/
			throw new CKEditorError("view-renderer-unknown-type", this);
		}
	}
	/**
	* Renders all buffered changes ({@link #markedAttributes}, {@link #markedChildren} and {@link #markedTexts}) and
	* the current view selection (if needed) to the DOM by applying a minimal set of changes to it.
	*
	* Renderer tries not to break the text composition (e.g. IME) and x-index of the selection,
	* so it does as little as it is needed to update the DOM.
	*
	* Renderer also handles {@link module:engine/view/filler fillers}. Especially, it checks if the inline filler is needed
	* at the selection position and adds or removes it. To prevent breaking text composition inline filler will not be
	* removed as long as the selection is in the text node which needed it at first.
	*/
	render() {
		if (this.isComposing && !env.isAndroid) return;
		let inlineFillerPosition = null;
		const isInlineFillerRenderingPossible = env.isBlink && !env.isAndroid ? !this.isSelecting : true;
		for (const element of this.markedChildren) this._updateChildrenMappings(element);
		if (isInlineFillerRenderingPossible) {
			if (this._inlineFiller && !this._isSelectionInInlineFiller()) this._removeInlineFiller();
			if (this._inlineFiller) inlineFillerPosition = this._getInlineFillerPosition();
			else if (this._needsInlineFillerAtSelection()) {
				inlineFillerPosition = this.selection.getFirstPosition();
				this.markedChildren.add(inlineFillerPosition.parent);
			}
		} else if (this._inlineFiller && this._inlineFiller.parentNode) {
			inlineFillerPosition = this.domConverter.domPositionToView(this._inlineFiller);
			if (inlineFillerPosition && inlineFillerPosition.parent.is("$text")) inlineFillerPosition = ViewPosition._createBefore(inlineFillerPosition.parent);
		}
		for (const element of this.markedAttributes) this._updateAttrs(element);
		for (const element of this.markedChildren) this._updateChildren(element, { inlineFillerPosition });
		for (const node of this.markedTexts) if (!this.markedChildren.has(node.parent) && this.domConverter.mapViewToDom(node.parent)) this._updateText(node, { inlineFillerPosition });
		if (isInlineFillerRenderingPossible) if (inlineFillerPosition) {
			const fillerDomPosition = this.domConverter.viewPositionToDom(inlineFillerPosition);
			const domDocument = fillerDomPosition.parent.ownerDocument;
			if (!startsWithFiller(fillerDomPosition.parent)) this._inlineFiller = addInlineFiller(domDocument, fillerDomPosition.parent, fillerDomPosition.offset);
			else this._inlineFiller = fillerDomPosition.parent;
		} else this._inlineFiller = null;
		this._updateFocus();
		this._updateSelection();
		this.domConverter._clearTemporaryCustomProperties();
		this.markedTexts.clear();
		this.markedAttributes.clear();
		this.markedChildren.clear();
	}
	/**
	* Updates mappings of view element's children.
	*
	* Children that were replaced in the view structure by similar elements (same tag name) are treated as 'replaced'.
	* This means that their mappings can be updated so the new view elements are mapped to the existing DOM elements.
	* Thanks to that these elements do not need to be re-rendered completely.
	*
	* @param viewElement The view element whose children mappings will be updated.
	*/
	_updateChildrenMappings(viewElement) {
		const domElement = this.domConverter.mapViewToDom(viewElement);
		if (!domElement) return;
		const actualDomChildren = Array.from(domElement.childNodes);
		const expectedDomChildren = Array.from(this.domConverter.viewChildrenToDom(viewElement, { withChildren: false }));
		const diff = this._diffNodeLists(actualDomChildren, expectedDomChildren);
		const actions = this._findUpdateActions(diff, actualDomChildren, expectedDomChildren, areSimilarElements);
		if (actions.indexOf("update") !== -1) {
			const counter = {
				equal: 0,
				insert: 0,
				delete: 0
			};
			for (const action of actions) if (action === "update") {
				const insertIndex = counter.equal + counter.insert;
				const deleteIndex = counter.equal + counter.delete;
				const viewChild = viewElement.getChild(insertIndex);
				if (viewChild && !viewChild.is("uiElement") && !viewChild.is("rawElement")) this._updateElementMappings(viewChild, actualDomChildren[deleteIndex]);
				remove$1(expectedDomChildren[insertIndex]);
				counter.equal++;
			} else counter[action]++;
		}
	}
	/**
	* Updates mappings of a given view element.
	*
	* @param viewElement The view element whose mappings will be updated.
	* @param domElement The DOM element representing the given view element.
	*/
	_updateElementMappings(viewElement, domElement) {
		this.domConverter.unbindDomElement(domElement);
		this.domConverter.bindElements(domElement, viewElement);
		this.markedChildren.add(viewElement);
		this.markedAttributes.add(viewElement);
	}
	/**
	* Gets the position of the inline filler based on the current selection.
	* Here, we assume that we know that the filler is needed and
	* {@link #_isSelectionInInlineFiller is at the selection position}, and, since it is needed,
	* it is somewhere at the selection position.
	*
	* Note: The filler position cannot be restored based on the filler's DOM text node, because
	* when this method is called (before rendering), the bindings will often be broken. View-to-DOM
	* bindings are only dependable after rendering.
	*/
	_getInlineFillerPosition() {
		const firstPos = this.selection.getFirstPosition();
		if (firstPos.parent.is("$text")) return ViewPosition._createBefore(firstPos.parent);
		else return firstPos;
	}
	/**
	* Returns `true` if the selection has not left the inline filler's text node.
	* If it is `true`, it means that the filler had been added for a reason and the selection did not
	* leave the filler's text node. For example, the user can be in the middle of a composition so it should not be touched.
	*
	* @returns `true` if the inline filler and selection are in the same place.
	*/
	_isSelectionInInlineFiller() {
		if (this.selection.rangeCount != 1 || !this.selection.isCollapsed) return false;
		const selectionPosition = this.selection.getFirstPosition();
		const position = this.domConverter.viewPositionToDom(selectionPosition);
		if (position && isText(position.parent) && startsWithFiller(position.parent)) return true;
		return false;
	}
	/**
	* Removes the inline filler.
	*/
	_removeInlineFiller() {
		const domFillerNode = this._inlineFiller;
		if (!startsWithFiller(domFillerNode))
 /**
		* The inline filler node was lost. Most likely, something overwrote the filler text node
		* in the DOM.
		*
		* @error view-renderer-filler-was-lost
		*/
		throw new CKEditorError("view-renderer-filler-was-lost", this);
		if (isInlineFiller(domFillerNode)) domFillerNode.remove();
		else domFillerNode.data = domFillerNode.data.substr(7);
		this._inlineFiller = null;
	}
	/**
	* Checks if the inline {@link module:engine/view/filler filler} should be added.
	*
	* @returns `true` if the inline filler should be added.
	*/
	_needsInlineFillerAtSelection() {
		if (this.selection.rangeCount != 1 || !this.selection.isCollapsed) return false;
		const selectionPosition = this.selection.getFirstPosition();
		const selectionParent = selectionPosition.parent;
		const selectionOffset = selectionPosition.offset;
		if (!this.domConverter.mapViewToDom(selectionParent.root)) return false;
		if (!selectionParent.is("element")) return false;
		if (!isEditable(selectionParent)) return false;
		const nodeBefore = selectionPosition.nodeBefore;
		const nodeAfter = selectionPosition.nodeAfter;
		if (nodeBefore instanceof ViewText || nodeAfter instanceof ViewText) return false;
		if (selectionOffset === selectionParent.getFillerOffset() && (!nodeBefore || !nodeBefore.is("element", "br"))) return false;
		if (env.isAndroid && (nodeBefore || nodeAfter)) return false;
		return true;
	}
	/**
	* Checks if text needs to be updated and possibly updates it.
	*
	* @param viewText View text to update.
	* @param options.inlineFillerPosition The position where the inline filler should be rendered.
	*/
	_updateText(viewText, options) {
		const domText = this.domConverter.findCorrespondingDomText(viewText);
		let expectedText = this.domConverter.viewToDom(viewText).data;
		const filler = options.inlineFillerPosition;
		if (filler && filler.parent == viewText.parent && filler.offset == viewText.index) expectedText = INLINE_FILLER + expectedText;
		this._updateTextNode(domText, expectedText);
	}
	/**
	* Checks if attribute list needs to be updated and possibly updates it.
	*
	* @param viewElement The view element to update.
	*/
	_updateAttrs(viewElement) {
		const domElement = this.domConverter.mapViewToDom(viewElement);
		if (!domElement) return;
		for (const domAttr of Array.from(domElement.attributes)) {
			const key = domAttr.name;
			if (!viewElement.hasAttribute(key)) this.domConverter.removeDomElementAttribute(domElement, key);
		}
		for (const key of viewElement.getAttributeKeys()) this.domConverter.setDomElementAttribute(domElement, key, viewElement.getAttribute(key), viewElement);
	}
	/**
	* Checks if elements child list needs to be updated and possibly updates it.
	*
	* Note that on Android, to reduce the risk of composition breaks, it tries to update data of an existing
	* child text nodes instead of replacing them completely.
	*
	* @param viewElement View element to update.
	* @param options.inlineFillerPosition The position where the inline filler should be rendered.
	*/
	_updateChildren(viewElement, options) {
		const domElement = this.domConverter.mapViewToDom(viewElement);
		if (!domElement) return;
		if (env.isAndroid) {
			let previousDomNode = null;
			for (const domNode of Array.from(domElement.childNodes)) {
				if (previousDomNode && isText(previousDomNode) && isText(domNode)) {
					domElement.normalize();
					break;
				}
				previousDomNode = domNode;
			}
		}
		const inlineFillerPosition = options.inlineFillerPosition;
		const actualDomChildren = domElement.childNodes;
		const expectedDomChildren = Array.from(this.domConverter.viewChildrenToDom(viewElement, { bind: true }));
		if (inlineFillerPosition && inlineFillerPosition.parent === viewElement) addInlineFiller(domElement.ownerDocument, expectedDomChildren, inlineFillerPosition.offset);
		const diff = this._diffNodeLists(actualDomChildren, expectedDomChildren);
		const actions = this._findUpdateActions(diff, actualDomChildren, expectedDomChildren, areTextNodes);
		let i = 0;
		const nodesToUnbind = /* @__PURE__ */ new Set();
		for (const action of actions) if (action === "delete") {
			nodesToUnbind.add(actualDomChildren[i]);
			remove$1(actualDomChildren[i]);
		} else if (action === "equal" || action === "update") i++;
		i = 0;
		for (const action of actions) if (action === "insert") {
			insertAt(domElement, i, expectedDomChildren[i]);
			i++;
		} else if (action === "update") {
			this._updateTextNode(actualDomChildren[i], expectedDomChildren[i].data);
			i++;
		} else if (action === "equal") {
			this._markDescendantTextToSync(this.domConverter.domToView(expectedDomChildren[i]));
			i++;
		}
		for (const node of nodesToUnbind) if (!node.parentNode) this.domConverter.unbindDomElement(node);
	}
	/**
	* Shorthand for diffing two arrays or node lists of DOM nodes.
	*
	* @param actualDomChildren Actual DOM children
	* @param expectedDomChildren Expected DOM children.
	* @returns The list of actions based on the {@link module:utils/diff~diff} function.
	*/
	_diffNodeLists(actualDomChildren, expectedDomChildren) {
		actualDomChildren = filterOutFakeSelectionContainer(actualDomChildren, this._fakeSelectionContainer);
		return diff(actualDomChildren, expectedDomChildren, sameNodes$1.bind(null, this.domConverter));
	}
	/**
	* Finds DOM nodes that were replaced with the similar nodes (same tag name) in the view. All nodes are compared
	* within one `insert`/`delete` action group, for example:
	*
	* ```
	* Actual DOM:		<p><b>Foo</b>Bar<i>Baz</i><b>Bax</b></p>
	* Expected DOM:	<p>Bar<b>123</b><i>Baz</i><b>456</b></p>
	* Input actions:	[ insert, insert, delete, delete, equal, insert, delete ]
	* Output actions:	[ insert, replace, delete, equal, replace ]
	* ```
	*
	* @param actions Actions array which is a result of the {@link module:utils/diff~diff} function.
	* @param actualDom Actual DOM children
	* @param expectedDom Expected DOM children.
	* @param comparator A comparator function that should return `true` if the given node should be reused
	* (either by the update of a text node data or an element children list for similar elements).
	* @returns Actions array modified with the `update` actions.
	*/
	_findUpdateActions(actions, actualDom, expectedDom, comparator) {
		if (actions.indexOf("insert") === -1 || actions.indexOf("delete") === -1) return actions;
		let newActions = [];
		let actualSlice = [];
		let expectedSlice = [];
		const counter = {
			equal: 0,
			insert: 0,
			delete: 0
		};
		for (const action of actions) {
			if (action === "insert") expectedSlice.push(expectedDom[counter.equal + counter.insert]);
			else if (action === "delete") actualSlice.push(actualDom[counter.equal + counter.delete]);
			else {
				newActions = newActions.concat(diff(actualSlice, expectedSlice, comparator).map((action) => action === "equal" ? "update" : action));
				newActions.push("equal");
				actualSlice = [];
				expectedSlice = [];
			}
			counter[action]++;
		}
		return newActions.concat(diff(actualSlice, expectedSlice, comparator).map((action) => action === "equal" ? "update" : action));
	}
	/**
	* Checks if text needs to be updated and possibly updates it by removing and inserting only parts
	* of the data from the existing text node to reduce impact on the IME composition.
	*
	* @param domText DOM text node to update.
	* @param expectedText The expected data of a text node.
	*/
	_updateTextNode(domText, expectedText) {
		const actualText = domText.data;
		if (actualText == expectedText) return;
		if (env.isAndroid && this.isComposing && actualText.replace(/\u00A0/g, " ") == expectedText.replace(/\u00A0/g, " ")) return;
		this._updateTextNodeInternal(domText, expectedText);
	}
	/**
	* Part of the `_updateTextNode` method extracted for easier testing.
	*/
	_updateTextNodeInternal(domText, expectedText) {
		const actions = fastDiff(domText.data, expectedText);
		for (const action of actions) if (action.type === "insert") domText.insertData(action.index, action.values.join(""));
		else domText.deleteData(action.index, action.howMany);
	}
	/**
	* Marks text nodes to be synchronized.
	*
	* If a text node is passed, it will be marked. If an element is passed, all descendant text nodes inside it will be marked.
	*
	* @param viewNode View node to sync.
	*/
	_markDescendantTextToSync(viewNode) {
		if (!viewNode) return;
		if (viewNode.is("$text")) this.markedTexts.add(viewNode);
		else if (viewNode.is("element")) for (const child of viewNode.getChildren()) this._markDescendantTextToSync(child);
	}
	/**
	* Checks if the selection needs to be updated and possibly updates it.
	*/
	_updateSelection() {
		if (env.isBlink && !env.isAndroid && this.isSelecting && !this.markedChildren.size) return;
		if (this.selection.rangeCount === 0) {
			this._removeDomSelection();
			this._removeFakeSelection();
			return;
		}
		const domEditable = this.domConverter.mapViewToDom(this.selection.editableElement);
		if (!this.isFocused || !domEditable) {
			if (!this.selection.isFake && this._fakeSelectionContainer && this._fakeSelectionContainer.isConnected) this._removeFakeSelection();
			return;
		}
		if (this.selection.isFake) this._updateFakeSelection(domEditable);
		else if (this._fakeSelectionContainer && this._fakeSelectionContainer.isConnected) {
			this._removeFakeSelection();
			this._updateDomSelection(domEditable);
		} else if (!(this.isComposing && env.isAndroid)) this._updateDomSelection(domEditable);
	}
	/**
	* Updates the fake selection.
	*
	* @param domEditable A valid DOM editable where the fake selection container should be added.
	*/
	_updateFakeSelection(domEditable) {
		const domDocument = domEditable.ownerDocument;
		if (!this._fakeSelectionContainer) this._fakeSelectionContainer = createFakeSelectionContainer(domDocument);
		const container = this._fakeSelectionContainer;
		this.domConverter.bindFakeSelection(container, this.selection);
		if (!this._fakeSelectionNeedsUpdate(domEditable)) return;
		if (!container.parentElement || container.parentElement != domEditable) domEditable.appendChild(container);
		container.textContent = this.selection.fakeSelectionLabel || "\xA0";
		const domSelection = domDocument.getSelection();
		const domRange = domDocument.createRange();
		domSelection.removeAllRanges();
		domRange.selectNodeContents(container);
		domSelection.addRange(domRange);
	}
	/**
	* Updates the DOM selection.
	*
	* @param domEditable A valid DOM editable where the DOM selection should be rendered.
	*/
	_updateDomSelection(domEditable) {
		const domSelection = domEditable.ownerDocument.defaultView.getSelection();
		if (!this._domSelectionNeedsUpdate(domSelection)) return;
		const anchor = this.domConverter.viewPositionToDom(this.selection.anchor);
		const focus = this.domConverter.viewPositionToDom(this.selection.focus);
		domSelection.setBaseAndExtent(anchor.parent, anchor.offset, focus.parent, focus.offset);
		if (env.isGecko) fixGeckoSelectionAfterBr(focus, domSelection);
	}
	/**
	* Checks whether a given DOM selection needs to be updated.
	*
	* @param domSelection The DOM selection to check.
	*/
	_domSelectionNeedsUpdate(domSelection) {
		if (!this.domConverter.isDomSelectionCorrect(domSelection)) return true;
		const oldViewSelection = domSelection && this.domConverter.domSelectionToView(domSelection);
		if (oldViewSelection && this.selection.isEqual(oldViewSelection)) return false;
		if (!this.selection.isCollapsed && this.selection.isSimilar(oldViewSelection)) return false;
		return true;
	}
	/**
	* Checks whether the fake selection needs to be updated.
	*
	* @param domEditable A valid DOM editable where a new fake selection container should be added.
	*/
	_fakeSelectionNeedsUpdate(domEditable) {
		const container = this._fakeSelectionContainer;
		const domSelection = domEditable.ownerDocument.getSelection();
		if (!container || container.parentElement !== domEditable) return true;
		if (domSelection.anchorNode !== container && !container.contains(domSelection.anchorNode)) return true;
		return container.textContent !== this.selection.fakeSelectionLabel;
	}
	/**
	* Removes the DOM selection.
	*/
	_removeDomSelection() {
		for (const doc of this.domDocuments) {
			const domSelection = doc.getSelection();
			if (domSelection.rangeCount) {
				const activeDomElement = doc.activeElement;
				const viewElement = this.domConverter.mapDomToView(activeDomElement);
				if (activeDomElement && viewElement) domSelection.removeAllRanges();
			}
		}
	}
	/**
	* Removes the fake selection.
	*/
	_removeFakeSelection() {
		const container = this._fakeSelectionContainer;
		if (container) container.remove();
	}
	/**
	* Checks if focus needs to be updated and possibly updates it.
	*/
	_updateFocus() {
		if (this.isFocused) {
			const editable = this.selection.editableElement;
			if (editable) this.domConverter.focus(editable);
		}
	}
};
/**
* Checks if provided element is editable.
*/
function isEditable(element) {
	if (element.getAttribute("contenteditable") == "false") return false;
	const parent = element.findAncestor((element) => element.hasAttribute("contenteditable"));
	return !parent || parent.getAttribute("contenteditable") == "true";
}
/**
* Adds inline filler at a given position.
*
* The position can be given as an array of DOM nodes and an offset in that array,
* or a DOM parent element and an offset in that element.
*
* @returns The DOM text node that contains an inline filler.
*/
function addInlineFiller(domDocument, domParentOrArray, offset) {
	const childNodes = domParentOrArray instanceof Array ? domParentOrArray : domParentOrArray.childNodes;
	const nodeAfterFiller = childNodes[offset];
	if (isText(nodeAfterFiller)) {
		nodeAfterFiller.data = INLINE_FILLER + nodeAfterFiller.data;
		return nodeAfterFiller;
	} else {
		const fillerNode = domDocument.createTextNode(INLINE_FILLER);
		if (Array.isArray(domParentOrArray)) childNodes.splice(offset, 0, fillerNode);
		else insertAt(domParentOrArray, offset, fillerNode);
		return fillerNode;
	}
}
/**
* Whether two DOM nodes should be considered as similar.
* Nodes are considered similar if they have the same tag name.
*/
function areSimilarElements(node1, node2) {
	return isNode(node1) && isNode(node2) && !isText(node1) && !isText(node2) && !isComment(node1) && !isComment(node2) && node1.tagName.toLowerCase() === node2.tagName.toLowerCase();
}
/**
* Whether two DOM nodes are text nodes.
*/
function areTextNodes(node1, node2) {
	return isNode(node1) && isNode(node2) && isText(node1) && isText(node2);
}
/**
* Whether two dom nodes should be considered as the same.
* Two nodes which are considered the same are:
*
* * Text nodes with the same text.
* * Element nodes represented by the same object.
* * Two block filler elements.
*
* @param blockFillerMode Block filler mode, see {@link module:engine/view/domconverter~ViewDomConverter#blockFillerMode}.
*/
function sameNodes$1(domConverter, actualDomChild, expectedDomChild) {
	if (actualDomChild === expectedDomChild) return true;
	else if (isText(actualDomChild) && isText(expectedDomChild)) return actualDomChild.data === expectedDomChild.data;
	else if (domConverter.isBlockFiller(actualDomChild) && domConverter.isBlockFiller(expectedDomChild)) return true;
	return false;
}
/**
* The following is a Firefox–specific hack (https://github.com/ckeditor/ckeditor5-engine/issues/1439).
* When the native DOM selection is at the end of the block and preceded by <br /> e.g.
*
* ```html
* <p>foo<br/>[]</p>
* ```
*
* which happens a lot when using the soft line break, the browser fails to (visually) move the
* caret to the new line. A quick fix is as simple as force–refreshing the selection with the same range.
*/
function fixGeckoSelectionAfterBr(focus, domSelection) {
	let parent = focus.parent;
	let offset = focus.offset;
	if (isText(parent) && isInlineFiller(parent)) {
		offset = indexOf(parent) + 1;
		parent = parent.parentNode;
	}
	if (parent.nodeType != Node.ELEMENT_NODE || offset != parent.childNodes.length - 1) return;
	const childAtOffset = parent.childNodes[offset];
	if (childAtOffset && childAtOffset.tagName == "BR") domSelection.addRange(domSelection.getRangeAt(0));
}
function filterOutFakeSelectionContainer(domChildList, fakeSelectionContainer) {
	const childList = Array.from(domChildList);
	if (childList.length == 0 || !fakeSelectionContainer) return childList;
	if (childList[childList.length - 1] == fakeSelectionContainer) childList.pop();
	return childList;
}
/**
* Creates a fake selection container for a given document.
*/
function createFakeSelectionContainer(domDocument) {
	const container = domDocument.createElement("div");
	container.className = "ck-fake-selection-container";
	Object.assign(container.style, {
		position: "fixed",
		top: 0,
		left: "-9999px",
		width: "42px"
	});
	container.textContent = "\xA0";
	return container;
}

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* @module engine/view/domconverter
*/
const BR_FILLER_REF = BR_FILLER(global.document);
const NBSP_FILLER_REF = NBSP_FILLER(global.document);
const MARKED_NBSP_FILLER_REF = MARKED_NBSP_FILLER(global.document);
const UNSAFE_ATTRIBUTE_NAME_PREFIX = "data-ck-unsafe-attribute-";
const UNSAFE_ELEMENT_REPLACEMENT_ATTRIBUTE = "data-ck-unsafe-element";
/**
* `ViewDomConverter` is a set of tools to do transformations between DOM nodes and view nodes. It also handles
* {@link module:engine/view/domconverter~ViewDomConverter#bindElements bindings} between these nodes.
*
* An instance of the DOM converter is available under
* {@link module:engine/view/view~EditingView#domConverter `editor.editing.view.domConverter`}.
*
* The DOM converter does not check which nodes should be rendered (use {@link module:engine/view/renderer~ViewRenderer}), does not keep the
* state of a tree nor keeps the synchronization between the tree view and
* the DOM tree (use {@link module:engine/view/document~ViewDocument}).
*
* The DOM converter keeps DOM elements to view element bindings, so when the converter gets destroyed, the bindings are lost.
* Two converters will keep separate binding maps, so one tree view can be bound with two DOM trees.
*/
var ViewDomConverter = class {
	document;
	/**
	* Whether to leave the View-to-DOM conversion result unchanged or improve editing experience by filtering out interactive data.
	*/
	renderingMode;
	/**
	* The mode of a block filler used by the DOM converter.
	*/
	blockFillerMode;
	/**
	* Elements which are considered pre-formatted elements.
	*/
	preElements;
	/**
	* Elements which are considered block elements (and hence should be filled with a
	* {@link #isBlockFiller block filler}).
	*
	* Whether an element is considered a block element also affects handling of trailing whitespaces.
	*
	* You can extend this array if you introduce support for block elements which are not yet recognized here.
	*/
	blockElements;
	/**
	* A list of elements that exist inline (in text) but their inner structure cannot be edited because
	* of the way they are rendered by the browser. They are mostly HTML form elements but there are other
	* elements such as `<img>` or `<iframe>` that also have non-editable children or no children whatsoever.
	*
	* Whether an element is considered an inline object has an impact on white space rendering (trimming)
	* around (and inside of it). In short, white spaces in text nodes next to inline objects are not trimmed.
	*
	* You can extend this array if you introduce support for inline object elements which are not yet recognized here.
	*/
	inlineObjectElements;
	/**
	* A list of elements which may affect the editing experience. To avoid this, those elements are replaced with
	* `<span data-ck-unsafe-element="[element name]"></span>` while rendering in the editing mode.
	*/
	unsafeElements;
	/**
	* The DOM Document used by `ViewDomConverter` to create DOM nodes.
	*/
	_domDocument;
	/**
	* The DOM-to-view mapping.
	*/
	_domToViewMapping = /* @__PURE__ */ new WeakMap();
	/**
	* The view-to-DOM mapping.
	*/
	_viewToDomMapping = /* @__PURE__ */ new WeakMap();
	/**
	* Holds the mapping between fake selection containers and corresponding view selections.
	*/
	_fakeSelectionMapping = /* @__PURE__ */ new WeakMap();
	/**
	* Matcher for view elements whose content should be treated as raw data
	* and not processed during the conversion from DOM nodes to view elements.
	*/
	_rawContentElementMatcher = new Matcher();
	/**
	* Matcher for inline object view elements. This is an extension of a simple {@link #inlineObjectElements} array of element names.
	*/
	_inlineObjectElementMatcher = new Matcher();
	/**
	* Set of elements with temporary custom properties that require clearing after render.
	*/
	_elementsWithTemporaryCustomProperties = /* @__PURE__ */ new Set();
	/**
	* Creates a DOM converter.
	*
	* @param document The view document instance.
	* @param options An object with configuration options.
	* @param options.blockFillerMode The type of the block filler to use.
	* Default value depends on the options.renderingMode:
	*  'nbsp' when options.renderingMode == 'data',
	*  'br' when options.renderingMode == 'editing'.
	* @param options.renderingMode Whether to leave the View-to-DOM conversion result unchanged
	* or improve editing experience by filtering out interactive data.
	*/
	constructor(document, { blockFillerMode, renderingMode = "editing" } = {}) {
		this.document = document;
		this.renderingMode = renderingMode;
		this.blockFillerMode = blockFillerMode || (renderingMode === "editing" ? "br" : "nbsp");
		this.preElements = ["pre", "textarea"];
		this.blockElements = [
			"address",
			"article",
			"aside",
			"blockquote",
			"caption",
			"center",
			"dd",
			"details",
			"dir",
			"div",
			"dl",
			"dt",
			"fieldset",
			"figcaption",
			"figure",
			"footer",
			"form",
			"h1",
			"h2",
			"h3",
			"h4",
			"h5",
			"h6",
			"header",
			"hgroup",
			"legend",
			"li",
			"main",
			"menu",
			"nav",
			"ol",
			"p",
			"pre",
			"section",
			"summary",
			"table",
			"tbody",
			"td",
			"tfoot",
			"th",
			"thead",
			"tr",
			"ul"
		];
		this.inlineObjectElements = [
			"object",
			"iframe",
			"input",
			"button",
			"textarea",
			"select",
			"option",
			"video",
			"embed",
			"audio",
			"img",
			"canvas"
		];
		this.unsafeElements = ["script", "style"];
		this._domDocument = this.renderingMode === "editing" ? global.document : global.document.implementation.createHTMLDocument("");
	}
	/**
	* The DOM Document used by `ViewDomConverter` to create DOM nodes.
	*/
	get domDocument() {
		return this._domDocument;
	}
	/**
	* Binds a given DOM element that represents fake selection to a **position** of a
	* {@link module:engine/view/documentselection~ViewDocumentSelection document selection}.
	* Document selection copy is stored and can be retrieved by the
	* {@link module:engine/view/domconverter~ViewDomConverter#fakeSelectionToView} method.
	*/
	bindFakeSelection(domElement, viewDocumentSelection) {
		this._fakeSelectionMapping.set(domElement, new ViewSelection(viewDocumentSelection));
	}
	/**
	* Returns a {@link module:engine/view/selection~ViewSelection view selection} instance corresponding to a given
	* DOM element that represents fake selection. Returns `undefined` if binding to the given DOM element does not exist.
	*/
	fakeSelectionToView(domElement) {
		return this._fakeSelectionMapping.get(domElement);
	}
	/**
	* Binds DOM and view elements, so it will be possible to get corresponding elements using
	* {@link module:engine/view/domconverter~ViewDomConverter#mapDomToView} and
	* {@link module:engine/view/domconverter~ViewDomConverter#mapViewToDom}.
	*
	* @param domElement The DOM element to bind.
	* @param viewElement The view element to bind.
	*/
	bindElements(domElement, viewElement) {
		this._domToViewMapping.set(domElement, viewElement);
		this._viewToDomMapping.set(viewElement, domElement);
	}
	/**
	* Unbinds a given DOM element from the view element it was bound to. Unbinding is deep, meaning that all children of
	* the DOM element will be unbound too.
	*
	* @param domElement The DOM element to unbind.
	*/
	unbindDomElement(domElement) {
		const viewElement = this._domToViewMapping.get(domElement);
		if (viewElement) {
			this._domToViewMapping.delete(domElement);
			this._viewToDomMapping.delete(viewElement);
			for (const child of domElement.children) this.unbindDomElement(child);
		}
	}
	/**
	* Binds DOM and view document fragments, so it will be possible to get corresponding document fragments using
	* {@link module:engine/view/domconverter~ViewDomConverter#mapDomToView} and
	* {@link module:engine/view/domconverter~ViewDomConverter#mapViewToDom}.
	*
	* @param domFragment The DOM document fragment to bind.
	* @param viewFragment The view document fragment to bind.
	*/
	bindDocumentFragments(domFragment, viewFragment) {
		this._domToViewMapping.set(domFragment, viewFragment);
		this._viewToDomMapping.set(viewFragment, domFragment);
	}
	/**
	* Decides whether a given pair of attribute key and value should be passed further down the pipeline.
	*
	* @param elementName Element name in lower case.
	*/
	shouldRenderAttribute(attributeKey, attributeValue, elementName) {
		if (this.renderingMode === "data") return true;
		attributeKey = attributeKey.toLowerCase();
		if (attributeKey.startsWith("on")) return false;
		if (attributeKey === "srcdoc") return false;
		if (elementName === "img" && (attributeKey === "src" || attributeKey === "srcset")) return true;
		if (elementName === "source" && attributeKey === "srcset") return true;
		if (attributeValue.replace(/\s+/g, "").match(/^(javascript:|data:(image\/svg|text\/x?html))/i)) return false;
		return true;
	}
	/**
	* Set `domElement`'s content using provided `html` argument. Apply necessary filtering for the editing pipeline.
	*
	* @param domElement DOM element that should have `html` set as its content.
	* @param html Textual representation of the HTML that will be set on `domElement`.
	*/
	setContentOf(domElement, html) {
		if (this.renderingMode === "data") {
			domElement.innerHTML = html;
			return;
		}
		const document = new DOMParser().parseFromString(html, "text/html");
		const fragment = document.createDocumentFragment();
		const bodyChildNodes = document.body.childNodes;
		while (bodyChildNodes.length > 0) fragment.appendChild(bodyChildNodes[0]);
		const treeWalker = document.createTreeWalker(fragment, NodeFilter.SHOW_ELEMENT);
		const nodes = [];
		let currentNode;
		while (currentNode = treeWalker.nextNode()) nodes.push(currentNode);
		for (const currentNode of nodes) {
			for (const attributeName of currentNode.getAttributeNames()) this.setDomElementAttribute(currentNode, attributeName, currentNode.getAttribute(attributeName));
			const elementName = currentNode.tagName.toLowerCase();
			if (this._shouldRenameElement(elementName)) {
				_logUnsafeElement(elementName);
				currentNode.replaceWith(this._createReplacementDomElement(elementName, currentNode));
			}
		}
		while (domElement.firstChild) domElement.firstChild.remove();
		domElement.append(fragment);
	}
	/**
	* Converts the view to the DOM. For all text nodes, not bound elements and document fragments new items will
	* be created. For bound elements and document fragments the method will return corresponding items.
	*
	* @param viewNode View node or document fragment to transform.
	* @param options Conversion options.
	* @param options.bind Determines whether new elements will be bound.
	* @param options.withChildren If `false`, node's and document fragment's children will not be converted.
	* @returns Converted node or DocumentFragment.
	*/
	viewToDom(viewNode, options = {}) {
		if (viewNode.is("$text")) {
			const textData = this._processDataFromViewText(viewNode);
			return this._domDocument.createTextNode(textData);
		} else {
			const viewElementOrFragment = viewNode;
			if (this.mapViewToDom(viewElementOrFragment)) if (viewElementOrFragment.getCustomProperty("editingPipeline:doNotReuseOnce")) this._elementsWithTemporaryCustomProperties.add(viewElementOrFragment);
			else return this.mapViewToDom(viewElementOrFragment);
			let domElement;
			if (viewElementOrFragment.is("documentFragment")) {
				domElement = this._domDocument.createDocumentFragment();
				if (options.bind) this.bindDocumentFragments(domElement, viewElementOrFragment);
			} else if (viewElementOrFragment.is("uiElement")) {
				if (viewElementOrFragment.name === "$comment") domElement = this._domDocument.createComment(viewElementOrFragment.getCustomProperty("$rawContent"));
				else domElement = viewElementOrFragment.render(this._domDocument, this);
				if (options.bind) this.bindElements(domElement, viewElementOrFragment);
				return domElement;
			} else {
				if (this._shouldRenameElement(viewElementOrFragment.name)) {
					_logUnsafeElement(viewElementOrFragment.name);
					domElement = this._createReplacementDomElement(viewElementOrFragment.name);
				} else if (viewElementOrFragment.hasAttribute("xmlns")) domElement = this._domDocument.createElementNS(viewElementOrFragment.getAttribute("xmlns"), viewElementOrFragment.name);
				else domElement = this._domDocument.createElement(viewElementOrFragment.name);
				if (viewElementOrFragment.is("rawElement")) viewElementOrFragment.render(domElement, this);
				if (options.bind) this.bindElements(domElement, viewElementOrFragment);
				for (const key of viewElementOrFragment.getAttributeKeys()) this.setDomElementAttribute(domElement, key, viewElementOrFragment.getAttribute(key), viewElementOrFragment);
			}
			if (options.withChildren !== false) for (const child of this.viewChildrenToDom(viewElementOrFragment, options)) if (domElement instanceof HTMLTemplateElement) domElement.content.appendChild(child);
			else domElement.appendChild(child);
			return domElement;
		}
	}
	/**
	* Sets the attribute on a DOM element.
	*
	* **Note**: To remove the attribute, use {@link #removeDomElementAttribute}.
	*
	* @param domElement The DOM element the attribute should be set on.
	* @param key The name of the attribute.
	* @param value The value of the attribute.
	* @param relatedViewElement The view element related to the `domElement` (if there is any).
	* It helps decide whether the attribute set is unsafe. For instance, view elements created via the
	* {@link module:engine/view/downcastwriter~ViewDowncastWriter} methods can allow certain attributes
	* that would normally be filtered out.
	*/
	setDomElementAttribute(domElement, key, value, relatedViewElement) {
		const shouldRenderAttribute = this.shouldRenderAttribute(key, value, domElement.tagName.toLowerCase()) || relatedViewElement && relatedViewElement.shouldRenderUnsafeAttribute(key);
		if (!shouldRenderAttribute) logWarning("domconverter-unsafe-attribute-detected", {
			domElement,
			key,
			value
		});
		if (!isValidAttributeName(key)) {
			/**
			* Invalid attribute name was ignored during rendering.
			*
			* @error domconverter-invalid-attribute-detected
			*/
			logWarning("domconverter-invalid-attribute-detected", {
				domElement,
				key,
				value
			});
			return;
		}
		if (domElement.hasAttribute(key) && !shouldRenderAttribute) domElement.removeAttribute(key);
		else if (domElement.hasAttribute(UNSAFE_ATTRIBUTE_NAME_PREFIX + key) && shouldRenderAttribute) domElement.removeAttribute(UNSAFE_ATTRIBUTE_NAME_PREFIX + key);
		domElement.setAttribute(shouldRenderAttribute ? key : UNSAFE_ATTRIBUTE_NAME_PREFIX + key, value);
	}
	/**
	* Removes an attribute from a DOM element.
	*
	* **Note**: To set the attribute, use {@link #setDomElementAttribute}.
	*
	* @param domElement The DOM element the attribute should be removed from.
	* @param key The name of the attribute.
	*/
	removeDomElementAttribute(domElement, key) {
		if (key == UNSAFE_ELEMENT_REPLACEMENT_ATTRIBUTE) return;
		domElement.removeAttribute(key);
		domElement.removeAttribute(UNSAFE_ATTRIBUTE_NAME_PREFIX + key);
	}
	/**
	* Converts children of the view element to DOM using the
	* {@link module:engine/view/domconverter~ViewDomConverter#viewToDom} method.
	* Additionally, this method adds block {@link module:engine/view/filler filler} to the list of children, if needed.
	*
	* @param viewElement Parent view element.
	* @param options See {@link module:engine/view/domconverter~ViewDomConverter#viewToDom} options parameter.
	* @returns DOM nodes.
	*/
	*viewChildrenToDom(viewElement, options = {}) {
		const fillerPositionOffset = viewElement.getFillerOffset && viewElement.getFillerOffset();
		let offset = 0;
		for (const childView of viewElement.getChildren()) {
			if (fillerPositionOffset === offset) yield this._getBlockFiller();
			const transparentRendering = childView.is("element") && !!childView.getCustomProperty("dataPipeline:transparentRendering") && !first(childView.getAttributes());
			if (transparentRendering && this.renderingMode == "data") if (childView.is("rawElement")) {
				const tempElement = this._domDocument.createElement(childView.name);
				childView.render(tempElement, this);
				yield* [...tempElement.childNodes];
			} else yield* this.viewChildrenToDom(childView, options);
			else {
				if (transparentRendering)
 /**
				* The `dataPipeline:transparentRendering` flag is supported only in the data pipeline.
				*
				* @error domconverter-transparent-rendering-unsupported-in-editing-pipeline
				*/
				logWarning("domconverter-transparent-rendering-unsupported-in-editing-pipeline", { viewElement: childView });
				yield this.viewToDom(childView, options);
			}
			offset++;
		}
		if (fillerPositionOffset === offset) yield this._getBlockFiller();
	}
	/**
	* Converts view {@link module:engine/view/range~ViewRange} to DOM range.
	* Inline and block {@link module:engine/view/filler fillers} are handled during the conversion.
	*
	* @param viewRange View range.
	* @returns DOM range.
	*/
	viewRangeToDom(viewRange) {
		const domStart = this.viewPositionToDom(viewRange.start);
		const domEnd = this.viewPositionToDom(viewRange.end);
		const domRange = this._domDocument.createRange();
		domRange.setStart(domStart.parent, domStart.offset);
		domRange.setEnd(domEnd.parent, domEnd.offset);
		return domRange;
	}
	/**
	* Converts view {@link module:engine/view/position~ViewPosition} to DOM parent and offset.
	*
	* Inline and block {@link module:engine/view/filler fillers} are handled during the conversion.
	* If the converted position is directly before inline filler it is moved inside the filler.
	*
	* @param viewPosition View position.
	* @returns DOM position or `null` if view position could not be converted to DOM.
	* DOM position has two properties:
	* * `parent` - DOM position parent.
	* * `offset` - DOM position offset.
	*/
	viewPositionToDom(viewPosition) {
		const viewParent = viewPosition.parent;
		if (viewParent.is("$text")) {
			const domParent = this.findCorrespondingDomText(viewParent);
			if (!domParent) return null;
			let offset = viewPosition.offset;
			if (startsWithFiller(domParent)) offset += 7;
			if (domParent.data && offset > domParent.data.length) offset = domParent.data.length;
			return {
				parent: domParent,
				offset
			};
		} else {
			let domParent, domBefore, domAfter;
			if (viewPosition.offset === 0) {
				domParent = this.mapViewToDom(viewParent);
				if (!domParent) return null;
				domAfter = domParent.childNodes[0];
			} else {
				const nodeBefore = viewPosition.nodeBefore;
				domBefore = nodeBefore.is("$text") ? this.findCorrespondingDomText(nodeBefore) : this.mapViewToDom(nodeBefore);
				if (!domBefore) return null;
				domParent = domBefore.parentNode;
				domAfter = domBefore.nextSibling;
			}
			if (isText(domAfter) && startsWithFiller(domAfter)) return {
				parent: domAfter,
				offset: 7
			};
			const offset = domBefore ? indexOf(domBefore) + 1 : 0;
			return {
				parent: domParent,
				offset
			};
		}
	}
	/**
	* Converts DOM to view. For all text nodes, not bound elements and document fragments new items will
	* be created. For bound elements and document fragments function will return corresponding items. For
	* {@link module:engine/view/filler fillers} `null` will be returned.
	* For all DOM elements rendered by {@link module:engine/view/uielement~ViewUIElement} that UIElement will be returned.
	*
	* @param domNode DOM node or document fragment to transform.
	* @param options Conversion options.
	* @param options.bind Determines whether new elements will be bound. False by default.
	* @param options.withChildren If `true`, node's and document fragment's children will be converted too. True by default.
	* @param options.keepOriginalCase If `false`, node's tag name will be converted to lower case. False by default.
	* @param options.skipComments If `false`, comment nodes will be converted to `$comment`
	* {@link module:engine/view/uielement~ViewUIElement view UI elements}. False by default.
	* @returns Converted node or document fragment or `null` if DOM node is a {@link module:engine/view/filler filler}
	* or the given node is an empty text node.
	*/
	domToView(domNode, options = {}) {
		const inlineNodes = [];
		const generator = this._domToView(domNode, options, inlineNodes);
		const node = generator.next().value;
		if (!node) return null;
		generator.next();
		this._processDomInlineNodes(null, inlineNodes, options);
		if (this.blockFillerMode == "br" && isViewBrFiller(node)) return null;
		if (node.is("$text") && node.data.length == 0) return null;
		return node;
	}
	/**
	* Converts children of the DOM element to view nodes using
	* the {@link module:engine/view/domconverter~ViewDomConverter#domToView} method.
	* Additionally this method omits block {@link module:engine/view/filler filler}, if it exists in the DOM parent.
	*
	* @param domElement Parent DOM element.
	* @param options See {@link module:engine/view/domconverter~ViewDomConverter#domToView} options parameter.
	* @param inlineNodes An array that will be populated with inline nodes. It's used internally for whitespace processing.
	* @returns View nodes.
	*/
	*domChildrenToView(domElement, options = {}, inlineNodes = []) {
		let childNodes = [];
		if (domElement instanceof HTMLTemplateElement) childNodes = [...domElement.content.childNodes];
		else childNodes = [...domElement.childNodes];
		for (let i = 0; i < childNodes.length; i++) {
			const domChild = childNodes[i];
			const generator = this._domToView(domChild, options, inlineNodes);
			const viewChild = generator.next().value;
			if (viewChild !== null) {
				if (this._isBlockViewElement(viewChild)) this._processDomInlineNodes(domElement, inlineNodes, options);
				if (!(this.blockFillerMode == "br" && isViewBrFiller(viewChild))) yield viewChild;
				generator.next();
			}
		}
		this._processDomInlineNodes(domElement, inlineNodes, options);
	}
	/**
	* Converts DOM selection to view {@link module:engine/view/selection~ViewSelection}.
	* Ranges which cannot be converted will be omitted.
	*
	* @param domSelection DOM selection.
	* @returns View selection.
	*/
	domSelectionToView(domSelection) {
		if (isGeckoRestrictedDomSelection(domSelection)) return new ViewSelection([]);
		if (domSelection.rangeCount === 1) {
			let container = domSelection.getRangeAt(0).startContainer;
			if (isText(container)) container = container.parentNode;
			const viewSelection = this.fakeSelectionToView(container);
			if (viewSelection) return viewSelection;
		}
		const isBackward = this.isDomSelectionBackward(domSelection);
		const viewRanges = [];
		for (let i = 0; i < domSelection.rangeCount; i++) {
			const domRange = domSelection.getRangeAt(i);
			const viewRange = this.domRangeToView(domRange);
			if (viewRange) viewRanges.push(viewRange);
		}
		return new ViewSelection(viewRanges, { backward: isBackward });
	}
	/**
	* Converts DOM Range to view {@link module:engine/view/range~ViewRange}.
	* If the start or end position cannot be converted `null` is returned.
	*
	* @param domRange DOM range.
	* @returns View range.
	*/
	domRangeToView(domRange) {
		const viewStart = this.domPositionToView(domRange.startContainer, domRange.startOffset);
		const viewEnd = this.domPositionToView(domRange.endContainer, domRange.endOffset);
		if (viewStart && viewEnd) return new ViewRange(viewStart, viewEnd);
		return null;
	}
	/**
	* Converts DOM parent and offset to view {@link module:engine/view/position~ViewPosition}.
	*
	* If the position is inside a {@link module:engine/view/filler filler} which has no corresponding view node,
	* position of the filler will be converted and returned.
	*
	* If the position is inside DOM element rendered by {@link module:engine/view/uielement~ViewUIElement}
	* that position will be converted to view position before that UIElement.
	*
	* If structures are too different and it is not possible to find corresponding position then `null` will be returned.
	*
	* @param domParent DOM position parent.
	* @param domOffset DOM position offset. You can skip it when converting the inline filler node.
	* @returns View position.
	*/
	domPositionToView(domParent, domOffset = 0) {
		if (this.isBlockFiller(domParent)) return this.domPositionToView(domParent.parentNode, indexOf(domParent));
		const viewElement = this.mapDomToView(domParent);
		if (viewElement && (viewElement.is("uiElement") || viewElement.is("rawElement"))) return ViewPosition._createBefore(viewElement);
		if (isText(domParent)) {
			if (isInlineFiller(domParent)) return this.domPositionToView(domParent.parentNode, indexOf(domParent));
			const viewParent = this.findCorrespondingViewText(domParent);
			let offset = domOffset;
			if (!viewParent) return null;
			if (startsWithFiller(domParent)) {
				offset -= 7;
				offset = offset < 0 ? 0 : offset;
			}
			return new ViewPosition(viewParent, offset);
		} else {
			if (domOffset === 0) {
				const viewParent = this.mapDomToView(domParent);
				if (viewParent) return new ViewPosition(viewParent, 0);
			} else {
				const domBefore = domParent.childNodes[domOffset - 1];
				if (isText(domBefore) && isInlineFiller(domBefore) || domBefore && this.isBlockFiller(domBefore)) return this.domPositionToView(domBefore.parentNode, indexOf(domBefore));
				const viewBefore = isText(domBefore) ? this.findCorrespondingViewText(domBefore) : this.mapDomToView(domBefore);
				if (viewBefore && viewBefore.parent) return new ViewPosition(viewBefore.parent, viewBefore.index + 1);
			}
			return null;
		}
	}
	/**
	* Returns corresponding view {@link module:engine/view/element~ViewElement Element} or
	* {@link module:engine/view/documentfragment~ViewDocumentFragment} for provided DOM element or
	* document fragment. If there is no view item {@link module:engine/view/domconverter~ViewDomConverter#bindElements bound}
	* to the given DOM - `undefined` is returned.
	*
	* For all DOM elements rendered by a {@link module:engine/view/uielement~ViewUIElement} or
	* a {@link module:engine/view/rawelement~ViewRawElement}, the parent `UIElement` or `RawElement` will be returned.
	*
	* @param domElementOrDocumentFragment DOM element or document fragment.
	* @returns Corresponding view element, document fragment or `undefined` if no element was bound.
	*/
	mapDomToView(domElementOrDocumentFragment) {
		return this.getHostViewElement(domElementOrDocumentFragment) || this._domToViewMapping.get(domElementOrDocumentFragment);
	}
	/**
	* Finds corresponding text node. Text nodes are not {@link module:engine/view/domconverter~ViewDomConverter#bindElements bound},
	* corresponding text node is returned based on the sibling or parent.
	*
	* If the directly previous sibling is a {@link module:engine/view/domconverter~ViewDomConverter#bindElements bound} element, it is used
	* to find the corresponding text node.
	*
	* If this is a first child in the parent and the parent is a
	* {@link module:engine/view/domconverter~ViewDomConverter#bindElements bound}
	* element, it is used to find the corresponding text node.
	*
	* For all text nodes rendered by a {@link module:engine/view/uielement~ViewUIElement} or
	* a {@link module:engine/view/rawelement~ViewRawElement}, the parent `UIElement` or `RawElement` will be returned.
	*
	* Otherwise `null` is returned.
	*
	* Note that for the block or inline {@link module:engine/view/filler filler} this method returns `null`.
	*
	* @param domText DOM text node.
	* @returns Corresponding view text node or `null`, if it was not possible to find a corresponding node.
	*/
	findCorrespondingViewText(domText) {
		if (isInlineFiller(domText)) return null;
		const hostElement = this.getHostViewElement(domText);
		if (hostElement) return hostElement;
		const previousSibling = domText.previousSibling;
		if (previousSibling) {
			if (!this.isElement(previousSibling)) return null;
			const viewElement = this.mapDomToView(previousSibling);
			if (viewElement) {
				const nextSibling = viewElement.nextSibling;
				if (nextSibling instanceof ViewText) return nextSibling;
				else return null;
			}
		} else {
			const viewElement = this.mapDomToView(domText.parentNode);
			if (viewElement) {
				const firstChild = viewElement.getChild(0);
				if (firstChild instanceof ViewText) return firstChild;
				else return null;
			}
		}
		return null;
	}
	mapViewToDom(documentFragmentOrElement) {
		return this._viewToDomMapping.get(documentFragmentOrElement);
	}
	/**
	* Finds corresponding text node. Text nodes are not {@link module:engine/view/domconverter~ViewDomConverter#bindElements bound},
	* corresponding text node is returned based on the sibling or parent.
	*
	* If the directly previous sibling is a {@link module:engine/view/domconverter~ViewDomConverter#bindElements bound} element, it is used
	* to find the corresponding text node.
	*
	* If this is a first child in the parent and the parent is a
	* {@link module:engine/view/domconverter~ViewDomConverter#bindElements bound}
	* element, it is used to find the corresponding text node.
	*
	* Otherwise `null` is returned.
	*
	* @param viewText View text node.
	* @returns Corresponding DOM text node or `null`, if it was not possible to find a corresponding node.
	*/
	findCorrespondingDomText(viewText) {
		const previousSibling = viewText.previousSibling;
		if (previousSibling && this.mapViewToDom(previousSibling)) return this.mapViewToDom(previousSibling).nextSibling;
		if (!previousSibling && viewText.parent && this.mapViewToDom(viewText.parent)) return this.mapViewToDom(viewText.parent).childNodes[0];
		return null;
	}
	/**
	* Focuses DOM editable that is corresponding to provided {@link module:engine/view/editableelement~ViewEditableElement}.
	*/
	focus(viewEditable) {
		const domEditable = this.mapViewToDom(viewEditable);
		if (!domEditable || domEditable.ownerDocument.activeElement === domEditable) return;
		const { scrollX, scrollY } = global.window;
		const scrollPositions = [];
		forEachDomElementAncestor(domEditable, (node) => {
			const { scrollLeft, scrollTop } = node;
			scrollPositions.push([scrollLeft, scrollTop]);
		});
		domEditable.focus({ preventScroll: true });
		forEachDomElementAncestor(domEditable, (node) => {
			const [scrollLeft, scrollTop] = scrollPositions.shift();
			node.scrollLeft = scrollLeft;
			node.scrollTop = scrollTop;
		});
		global.window.scrollTo(scrollX, scrollY);
	}
	/**
	* Remove DOM selection from blurred editable, so it won't interfere with clicking on dropdowns (especially on iOS).
	*
	* @internal
	*/
	_clearDomSelection() {
		const domEditable = this.mapViewToDom(this.document.selection.editableElement);
		if (!domEditable) return;
		const domSelection = domEditable.ownerDocument.defaultView.getSelection();
		const newViewSelection = this.domSelectionToView(domSelection);
		if (newViewSelection && newViewSelection.rangeCount > 0) domSelection.removeAllRanges();
	}
	/**
	* Returns `true` when `node.nodeType` equals `Node.ELEMENT_NODE`.
	*
	* @param node Node to check.
	*/
	isElement(node) {
		return node && node.nodeType == Node.ELEMENT_NODE;
	}
	/**
	* Returns `true` when `node.nodeType` equals `Node.DOCUMENT_FRAGMENT_NODE`.
	*
	* @param node Node to check.
	*/
	isDocumentFragment(node) {
		return node && node.nodeType == Node.DOCUMENT_FRAGMENT_NODE;
	}
	/**
	* Checks if the node is an instance of the block filler for this DOM converter.
	*
	* ```ts
	* const converter = new ViewDomConverter( viewDocument, { blockFillerMode: 'br' } );
	*
	* converter.isBlockFiller( BR_FILLER( document ) ); // true
	* converter.isBlockFiller( NBSP_FILLER( document ) ); // false
	* ```
	*
	* **Note:**: For the `'nbsp'` mode the method also checks context of a node so it cannot be a detached node.
	*
	* **Note:** A special case in the `'nbsp'` mode exists where the `<br>` in `<p><br></p>` is treated as a block filler.
	*
	* @param domNode DOM node to check.
	* @returns True if a node is considered a block filler for given mode.
	*/
	isBlockFiller(domNode) {
		if (this.blockFillerMode == "br") return domNode.isEqualNode(BR_FILLER_REF);
		if (isOnlyBrInBlock(domNode, this.blockElements)) return true;
		return domNode.isEqualNode(MARKED_NBSP_FILLER_REF) || isNbspBlockFiller(domNode, this.blockElements);
	}
	/**
	* Returns `true` if given selection is a backward selection, that is, if it's `focus` is before `anchor`.
	*
	* @param selection Selection instance to check.
	*/
	isDomSelectionBackward(selection) {
		if (selection.isCollapsed) return false;
		const range = this._domDocument.createRange();
		try {
			range.setStart(selection.anchorNode, selection.anchorOffset);
			range.setEnd(selection.focusNode, selection.focusOffset);
		} catch {
			return false;
		}
		const backward = range.collapsed;
		range.detach();
		return backward;
	}
	/**
	* Returns a parent {@link module:engine/view/uielement~ViewUIElement} or {@link module:engine/view/rawelement~ViewRawElement}
	* that hosts the provided DOM node. Returns `null` if there is no such parent.
	*/
	getHostViewElement(domNode) {
		const ancestors = getAncestors(domNode);
		ancestors.pop();
		while (ancestors.length) {
			const domNode = ancestors.pop();
			const viewNode = this._domToViewMapping.get(domNode);
			if (viewNode && (viewNode.is("uiElement") || viewNode.is("rawElement"))) return viewNode;
		}
		return null;
	}
	/**
	* Checks if the given selection's boundaries are at correct places.
	*
	* The following places are considered as incorrect for selection boundaries:
	*
	* * before or in the middle of an inline filler sequence,
	* * inside a DOM element which represents {@link module:engine/view/uielement~ViewUIElement a view UI element},
	* * inside a DOM element which represents {@link module:engine/view/rawelement~ViewRawElement a view raw element}.
	*
	* @param domSelection The DOM selection object to be checked.
	* @returns `true` if the given selection is at a correct place, `false` otherwise.
	*/
	isDomSelectionCorrect(domSelection) {
		return this._isDomSelectionPositionCorrect(domSelection.anchorNode, domSelection.anchorOffset) && this._isDomSelectionPositionCorrect(domSelection.focusNode, domSelection.focusOffset);
	}
	/**
	* Registers a {@link module:engine/view/matcher~MatcherPattern} for view elements whose content should be treated as raw data
	* and not processed during the conversion from DOM nodes to view elements.
	*
	* This is affecting how {@link module:engine/view/domconverter~ViewDomConverter#domToView} and
	* {@link module:engine/view/domconverter~ViewDomConverter#domChildrenToView} process DOM nodes.
	*
	* The raw data can be later accessed by a
	* {@link module:engine/view/element~ViewElement#getCustomProperty custom property of a view element} called `"$rawContent"`.
	*
	* @param pattern Pattern matching a view element whose content should
	* be treated as raw data.
	*/
	registerRawContentMatcher(pattern) {
		this._rawContentElementMatcher.add(pattern);
	}
	/**
	* Registers a {@link module:engine/view/matcher~MatcherPattern} for inline object view elements.
	*
	* This is affecting how {@link module:engine/view/domconverter~ViewDomConverter#domToView} and
	* {@link module:engine/view/domconverter~ViewDomConverter#domChildrenToView} process DOM nodes.
	*
	* This is an extension of a simple {@link #inlineObjectElements} array of element names.
	*
	* @param pattern Pattern matching a view element which should be treated as an inline object.
	*/
	registerInlineObjectMatcher(pattern) {
		this._inlineObjectElementMatcher.add(pattern);
	}
	/**
	* Clear temporary custom properties.
	*
	* @internal
	*/
	_clearTemporaryCustomProperties() {
		for (const element of this._elementsWithTemporaryCustomProperties) element._removeCustomProperty("editingPipeline:doNotReuseOnce");
		this._elementsWithTemporaryCustomProperties.clear();
	}
	/**
	* Returns the block {@link module:engine/view/filler filler} node based on the current {@link #blockFillerMode} setting.
	*/
	_getBlockFiller() {
		switch (this.blockFillerMode) {
			case "nbsp": return NBSP_FILLER(this._domDocument);
			case "markedNbsp": return MARKED_NBSP_FILLER(this._domDocument);
			case "br": return BR_FILLER(this._domDocument);
		}
	}
	/**
	* Checks if the given DOM position is a correct place for selection boundary. See {@link #isDomSelectionCorrect}.
	*
	* @param domParent Position parent.
	* @param offset Position offset.
	* @returns `true` if given position is at a correct place for selection boundary, `false` otherwise.
	*/
	_isDomSelectionPositionCorrect(domParent, offset) {
		if (isText(domParent) && startsWithFiller(domParent) && offset < 7) return false;
		if (this.isElement(domParent) && startsWithFiller(domParent.childNodes[offset])) return false;
		const viewParent = this.mapDomToView(domParent);
		if (viewParent && (viewParent.is("uiElement") || viewParent.is("rawElement"))) return false;
		return true;
	}
	/**
	* Internal generator for {@link #domToView}. Also used by {@link #domChildrenToView}.
	* Separates DOM nodes conversion from whitespaces processing.
	*
	* @param domNode DOM node or document fragment to transform.
	* @param inlineNodes An array of recently encountered inline nodes truncated to the block element boundaries.
	* Used later to process whitespaces.
	*/
	*_domToView(domNode, options, inlineNodes) {
		if (this.blockFillerMode != "br" && isOnlyBrInBlock(domNode, this.blockElements)) return null;
		const hostElement = this.getHostViewElement(domNode);
		if (hostElement) return hostElement;
		if (isComment(domNode) && options.skipComments) return null;
		if (isText(domNode)) if (isInlineFiller(domNode)) return null;
		else {
			const textData = domNode.data;
			if (textData === "") return null;
			const textNode = new ViewText(this.document, textData);
			inlineNodes.push(textNode);
			return textNode;
		}
		else {
			let viewElement = this.mapDomToView(domNode);
			if (viewElement) {
				if (this._isInlineObjectElement(viewElement)) inlineNodes.push(viewElement);
				return viewElement;
			}
			if (this.isDocumentFragment(domNode)) {
				viewElement = new ViewDocumentFragment(this.document);
				if (options.bind) this.bindDocumentFragments(domNode, viewElement);
			} else {
				viewElement = this._createViewElement(domNode, options);
				if (options.bind) this.bindElements(domNode, viewElement);
				const attrs = domNode.attributes;
				if (attrs) for (let l = attrs.length, i = 0; i < l; i++) viewElement._setAttribute(attrs[i].name, attrs[i].value);
				if (this._isViewElementWithRawContent(viewElement, options)) {
					viewElement._setCustomProperty("$rawContent", domNode.innerHTML);
					if (!this._isBlockViewElement(viewElement)) inlineNodes.push(viewElement);
					return viewElement;
				}
				if (isComment(domNode)) {
					viewElement._setCustomProperty("$rawContent", domNode.data);
					return viewElement;
				}
			}
			yield viewElement;
			const nestedInlineNodes = [];
			if (options.withChildren !== false) for (const child of this.domChildrenToView(domNode, options, nestedInlineNodes)) viewElement._appendChild(child);
			if (this._isInlineObjectElement(viewElement)) {
				inlineNodes.push(viewElement);
				this._processDomInlineNodes(null, nestedInlineNodes, options);
			} else for (const inlineNode of nestedInlineNodes) inlineNodes.push(inlineNode);
		}
	}
	/**
	* Internal helper that walks the list of inline view nodes already generated from DOM nodes
	* and handles whitespaces and NBSPs.
	*
	* @param domParent The DOM parent of the given inline nodes. This should be a document fragment or
	* a block element to whitespace processing start cleaning.
	* @param inlineNodes An array of recently encountered inline nodes truncated to the block element boundaries.
	*/
	_processDomInlineNodes(domParent, inlineNodes, options) {
		if (!inlineNodes.length) return;
		if (domParent && !this.isDocumentFragment(domParent) && !this._isBlockDomElement(domParent)) return;
		let prevNodeEndsWithSpace = false;
		for (let i = 0; i < inlineNodes.length; i++) {
			const node = inlineNodes[i];
			if (!node.is("$text")) {
				prevNodeEndsWithSpace = false;
				continue;
			}
			let data;
			let nodeEndsWithSpace = false;
			if (this._isPreFormatted(node)) data = getDataWithoutFiller(node.data);
			else {
				data = node.data.replace(/[ \n\t\r]{1,}/g, " ");
				nodeEndsWithSpace = /[^\S\u00A0]/.test(data.charAt(data.length - 1));
				const prevNode = i > 0 ? inlineNodes[i - 1] : null;
				const nextNode = i + 1 < inlineNodes.length ? inlineNodes[i + 1] : null;
				const shouldLeftTrim = !prevNode || prevNode.is("element") && prevNode.name == "br" || prevNodeEndsWithSpace;
				const shouldRightTrim = nextNode ? false : !startsWithFiller(node.data);
				if (options.withChildren !== false) {
					if (shouldLeftTrim) data = data.replace(/^ /, "");
					if (shouldRightTrim) data = data.replace(/ $/, "");
				}
				data = getDataWithoutFiller(data);
				if (this.blockFillerMode != "br" && node.parent) {
					if (isViewMarkedNbspFiller(node.parent, data)) {
						data = "";
						if (node.parent.parent) {
							node.parent.parent._setCustomProperty("$hasBlockFiller", true);
							node.parent._remove();
						}
					} else if (isViewNbspFiller(node.parent, data, this.blockElements)) {
						data = "";
						node.parent._setCustomProperty("$hasBlockFiller", true);
					}
				}
				data = data.replace(/ \u00A0/g, "  ");
				const isNextNodeInlineObjectElement = nextNode && nextNode.is("element") && nextNode.name != "br";
				const isNextNodeStartingWithSpace = nextNode && nextNode.is("$text") && nextNode.data.charAt(0) == " ";
				if (/[ \u00A0]\u00A0$/.test(data) || !nextNode || isNextNodeInlineObjectElement || isNextNodeStartingWithSpace) data = data.replace(/\u00A0$/, " ");
				if (shouldLeftTrim || prevNode && prevNode.is("element") && prevNode.name != "br") data = data.replace(/^\u00A0/, " ");
			}
			if (data.length == 0 && node.parent) {
				node._remove();
				inlineNodes.splice(i, 1);
				i--;
			} else {
				node._data = data;
				prevNodeEndsWithSpace = nodeEndsWithSpace;
			}
		}
		inlineNodes.length = 0;
	}
	/**
	* Takes text data from a given {@link module:engine/view/text~ViewText#data} and processes it so
	* it is correctly displayed in the DOM.
	*
	* Following changes are done:
	*
	* * a space at the beginning is changed to `&nbsp;` if this is the first text node in its container
	* element or if a previous text node ends with a space character,
	* * space at the end of the text node is changed to `&nbsp;` if there are two spaces at the end of a node or if next node
	* starts with a space or if it is the last text node in its container,
	* * remaining spaces are replaced to a chain of spaces and `&nbsp;` (e.g. `'x   x'` becomes `'x &nbsp; x'`).
	*
	* Content of {@link #preElements} is not processed.
	*
	* @param node View text node to process.
	* @returns Processed text data.
	*/
	_processDataFromViewText(node) {
		let data = node.data;
		if (this._isPreFormatted(node)) return data;
		if (data.charAt(0) == " ") {
			const prevNode = this._getTouchingInlineViewNode(node, false);
			if (prevNode && prevNode.is("$textProxy") && this._nodeEndsWithSpace(prevNode) || !prevNode) data = "\xA0" + data.substr(1);
		}
		if (data.charAt(data.length - 1) == " ") {
			const nextNode = this._getTouchingInlineViewNode(node, true);
			const nextStartsWithSpace = nextNode && nextNode.is("$textProxy") && nextNode.data.charAt(0) == " ";
			if (data.charAt(data.length - 2) == " " || !nextNode || nextStartsWithSpace) data = data.substr(0, data.length - 1) + "\xA0";
		}
		return data.replace(/ {2}/g, " \xA0");
	}
	/**
	* Checks whether given node ends with a space character after changing appropriate space characters to `&nbsp;`s.
	*
	* @param  node Node to check.
	* @returns `true` if given `node` ends with space, `false` otherwise.
	*/
	_nodeEndsWithSpace(node) {
		if (this._isPreFormatted(node)) return false;
		const data = this._processDataFromViewText(node);
		return data.charAt(data.length - 1) == " ";
	}
	/**
	* Checks whether given text contains preformatted white space. This is the case if
	* * any of node ancestors has a name which is in `preElements` array, or
	* * the closest ancestor that has the `white-space` CSS property sets it to a value that preserves spaces
	*
	* @param node Node to check
	* @returns `true` if given node contains preformatted white space, `false` otherwise.
	*/
	_isPreFormatted(node) {
		if (_hasViewParentOfType(node, this.preElements)) return true;
		for (const ancestor of node.getAncestors({ parentFirst: true })) {
			if (!ancestor.is("element") || !ancestor.hasStyle("white-space") || ancestor.getStyle("white-space") === "inherit") continue;
			return [
				"pre",
				"pre-wrap",
				"break-spaces"
			].includes(ancestor.getStyle("white-space"));
		}
		return false;
	}
	/**
	* Helper function. For given {@link module:engine/view/text~ViewText view text node}, it finds previous or next sibling
	* that is contained in the same container element. If there is no such sibling, `null` is returned.
	*
	* @param node Reference node.
	* @returns Touching text node, an inline object
	* or `null` if there is no next or previous touching text node.
	*/
	_getTouchingInlineViewNode(node, getNext) {
		const treeWalker = new ViewTreeWalker({
			startPosition: getNext ? ViewPosition._createAfter(node) : ViewPosition._createBefore(node),
			direction: getNext ? "forward" : "backward"
		});
		for (const { item } of treeWalker) if (item.is("$textProxy")) return item;
		else if (item.is("element") && item.getCustomProperty("dataPipeline:transparentRendering")) continue;
		else if (item.is("element", "br")) return null;
		else if (this._isInlineObjectElement(item)) return item;
		else if (item.is("containerElement") || this._isBlockViewElement(item)) return null;
		return null;
	}
	/**
	* Returns `true` if a DOM node belongs to {@link #blockElements}. `false` otherwise.
	*/
	_isBlockDomElement(node) {
		return this.isElement(node) && this.blockElements.includes(node.tagName.toLowerCase());
	}
	/**
	* Returns `true` if a view node belongs to {@link #blockElements}. `false` otherwise.
	*/
	_isBlockViewElement(node) {
		return node.is("element") && this.blockElements.includes(node.name);
	}
	/**
	* Returns `true` if a DOM node belongs to {@link #inlineObjectElements}. `false` otherwise.
	*/
	_isInlineObjectElement(node) {
		if (!node.is("element")) return false;
		return node.name == "br" || this.inlineObjectElements.includes(node.name) || !!this._inlineObjectElementMatcher.match(node);
	}
	/**
	* Creates view element basing on the node type.
	*
	* @param node DOM node to check.
	* @param options Conversion options. See {@link module:engine/view/domconverter~ViewDomConverter#domToView} options parameter.
	*/
	_createViewElement(node, options) {
		if (isComment(node)) return new ViewUIElement(this.document, "$comment");
		const viewName = options.keepOriginalCase ? node.tagName : node.tagName.toLowerCase();
		return new ViewElement(this.document, viewName);
	}
	/**
	* Checks if view element's content should be treated as a raw data.
	*
	* @param viewElement View element to check.
	* @param options Conversion options. See {@link module:engine/view/domconverter~ViewDomConverter#domToView} options parameter.
	*/
	_isViewElementWithRawContent(viewElement, options) {
		return options.withChildren !== false && viewElement.is("element") && !!this._rawContentElementMatcher.match(viewElement);
	}
	/**
	* Checks whether a given element name should be renamed in a current rendering mode.
	*
	* @param elementName The name of view element.
	*/
	_shouldRenameElement(elementName) {
		const name = elementName.toLowerCase();
		return this.renderingMode === "editing" && this.unsafeElements.includes(name);
	}
	/**
	* Return a <span> element with a special attribute holding the name of the original element.
	* Optionally, copy all the attributes of the original element if that element is provided.
	*
	* @param elementName The name of view element.
	* @param originalDomElement The original DOM element to copy attributes and content from.
	*/
	_createReplacementDomElement(elementName, originalDomElement) {
		const newDomElement = this._domDocument.createElement("span");
		newDomElement.setAttribute(UNSAFE_ELEMENT_REPLACEMENT_ATTRIBUTE, elementName);
		if (originalDomElement) {
			while (originalDomElement.firstChild) newDomElement.appendChild(originalDomElement.firstChild);
			for (const attributeName of originalDomElement.getAttributeNames()) newDomElement.setAttribute(attributeName, originalDomElement.getAttribute(attributeName));
		}
		return newDomElement;
	}
};
/**
* Helper function.
* Used to check if given native `Element` or `Text` node has parent with tag name from `types` array.
*
* @returns`true` if such parent exists or `false` if it does not.
*/
function _hasViewParentOfType(node, types) {
	return node.getAncestors().some((parent) => parent.is("element") && types.includes(parent.name));
}
/**
* A helper that executes given callback for each DOM node's ancestor, starting from the given node
* and ending in document#documentElement.
*
* @param callback A callback to be executed for each ancestor.
*/
function forEachDomElementAncestor(element, callback) {
	let node = element;
	while (node) {
		callback(node);
		node = node.parentElement;
	}
}
/**
* Checks if given DOM node is a nbsp block filler.
*
* A &nbsp; is a block filler only if it is a single child of a block element.
*
* @param domNode DOM node.
*/
function isNbspBlockFiller(domNode, blockElements) {
	return domNode.isEqualNode(NBSP_FILLER_REF) && hasBlockParent(domNode, blockElements) && domNode.parentNode.childNodes.length === 1;
}
/**
* Checks if domNode has block parent.
*
* @param domNode DOM node.
*/
function hasBlockParent(domNode, blockElements) {
	const parent = domNode.parentNode;
	return !!parent && !!parent.tagName && blockElements.includes(parent.tagName.toLowerCase());
}
/**
* Checks if given view node is a nbsp block filler.
*
* A &nbsp; is a block filler only if it is a single child of a block element.
*/
function isViewNbspFiller(parent, data, blockElements) {
	return data == "\xA0" && parent && parent.is("element") && parent.childCount == 1 && blockElements.includes(parent.name);
}
/**
* Checks if given view node is a marked-nbsp block filler.
*
* A &nbsp; is a block filler only if it is wrapped in `<span data-cke-filler="true">` element.
*/
function isViewMarkedNbspFiller(parent, data) {
	return data == "\xA0" && parent && parent.is("element", "span") && parent.childCount == 1 && parent.hasAttribute("data-cke-filler");
}
/**
* Checks if given view node is a br block filler.
*
* A <br> is a block filler only if it has data-cke-filler attribute set.
*/
function isViewBrFiller(node) {
	return node.is("element", "br") && node.hasAttribute("data-cke-filler");
}
/**
* Special case for `<p><br></p>` in which `<br>` should be treated as filler even when we are not in the 'br' mode.
*/
function isOnlyBrInBlock(domNode, blockElements) {
	return domNode.tagName === "BR" && hasBlockParent(domNode, blockElements) && domNode.parentNode.childNodes.length === 1;
}
/**
* Log to console the information about element that was replaced.
* Check UNSAFE_ELEMENTS for all recognized unsafe elements.
*
* @param elementName The name of the view element.
*/
function _logUnsafeElement(elementName) {
	if (elementName === "script") logWarning("domconverter-unsafe-script-element-detected");
	if (elementName === "style") logWarning("domconverter-unsafe-style-element-detected");
}
/**
* In certain cases, Firefox mysteriously assigns so called "restricted objects" to native DOM Range properties.
* Any attempt at accessing restricted object's properties causes errors.
* See: https://github.com/ckeditor/ckeditor5/issues/9635.
*/
function isGeckoRestrictedDomSelection(domSelection) {
	if (!env.isGecko) return false;
	if (!domSelection.rangeCount) return false;
	const container = domSelection.getRangeAt(0).startContainer;
	try {
		Object.prototype.toString.call(container);
	} catch {
		return true;
	}
	return false;
}
/**
* While rendering the editor content, the {@link module:engine/view/domconverter~ViewDomConverter} detected a `<script>` element that may
* disrupt the editing experience. To avoid this, the `<script>` element was replaced with `<span data-ck-unsafe-element="script"></span>`.
*
* @error domconverter-unsafe-script-element-detected
*/
/**
* While rendering the editor content, the
* {@link module:engine/view/domconverter~ViewDomConverter} detected a `<style>` element that may affect
* the editing experience. To avoid this, the `<style>` element was replaced with `<span data-ck-unsafe-element="style"></span>`.
*
* @error domconverter-unsafe-style-element-detected
*/
/**
* The {@link module:engine/view/domconverter~ViewDomConverter} detected an interactive attribute in the
* {@glink framework/architecture/editing-engine#editing-pipeline editing pipeline}. For the best
* editing experience, the attribute was renamed to `data-ck-unsafe-attribute-[original attribute name]`.
*
* If you are the author of the plugin that generated this attribute and you want it to be preserved
* in the editing pipeline, you can configure this when creating the element
* using {@link module:engine/view/downcastwriter~ViewDowncastWriter} during the
* {@glink framework/architecture/editing-engine#conversion model–view conversion}. Methods such as
* {@link module:engine/view/downcastwriter~ViewDowncastWriter#createContainerElement},
* {@link module:engine/view/downcastwriter~ViewDowncastWriter#createAttributeElement}, or
* {@link module:engine/view/downcastwriter~ViewDowncastWriter#createEmptyElement}
* accept an option that will disable filtering of specific attributes:
*
* ```ts
* const paragraph = writer.createContainerElement( 'p',
* 	{
* 		class: 'clickable-paragraph',
* 		onclick: 'alert( "Paragraph clicked!" )'
* 	},
* 	{
* 		// Make sure the "onclick" attribute will pass through.
* 		renderUnsafeAttributes: [ 'onclick' ]
* 	}
* );
* ```
*
* @error domconverter-unsafe-attribute-detected
* @param {HTMLElement} domElement The DOM element the attribute was set on.
* @param {string} key The original name of the attribute
* @param {string} value The value of the original attribute
*/

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* @module engine/view/observer/observer
*/
const ObserverBase = /* #__PURE__ */ DomEmitterMixin();
/**
* Abstract base observer class. Observers are classes which listen to DOM events, do the preliminary
* processing and fire events on the {@link module:engine/view/document~ViewDocument} objects.
* Observers can also add features to the view, for instance by updating its status or marking elements
* which need a refresh on DOM events.
*/
var Observer = class extends ObserverBase {
	/**
	* An instance of the view controller.
	*/
	view;
	/**
	* A reference to the {@link module:engine/view/document~ViewDocument} object.
	*/
	document;
	/**
	* The state of the observer. If it is disabled, no events will be fired.
	*/
	_isEnabled = false;
	/**
	* Creates an instance of the observer.
	*/
	constructor(view) {
		super();
		this.view = view;
		this.document = view.document;
	}
	/**
	* The state of the observer. If it is disabled, no events will be fired.
	*/
	get isEnabled() {
		return this._isEnabled;
	}
	/**
	* Enables the observer. This method is called when the observer is registered to the
	* {@link module:engine/view/view~EditingView} and after {@link module:engine/view/view~EditingView#forceRender rendering}
	* (all observers are {@link #disable disabled} before rendering).
	*
	* A typical use case for disabling observers is that mutation observers need to be disabled for the rendering.
	* However, a child class may not need to be disabled, so it can implement an empty method.
	*
	* @see module:engine/view/observer/observer~Observer#disable
	*/
	enable() {
		this._isEnabled = true;
	}
	/**
	* Disables the observer. This method is called before
	* {@link module:engine/view/view~EditingView#forceRender rendering} to prevent firing events during rendering.
	*
	* @see module:engine/view/observer/observer~Observer#enable
	*/
	disable() {
		this._isEnabled = false;
	}
	/**
	* Disables and destroys the observer, among others removes event listeners created by the observer.
	*/
	destroy() {
		this.disable();
		this.stopListening();
	}
	/**
	* Checks whether a given DOM event should be ignored (should not be turned into a synthetic view document event).
	*
	* Currently, an event will be ignored only if its target or any of its ancestors has the `data-cke-ignore-events` attribute.
	* This attribute can be used inside the structures generated by
	* {@link module:engine/view/downcastwriter~ViewDowncastWriter#createUIElement `ViewDowncastWriter#createUIElement()`} to ignore events
	* fired within a UI that should be excluded from CKEditor 5's realms.
	*
	* @param domTarget The DOM event target to check (usually an element, sometimes a text node and
	* potentially sometimes a document, too).
	* @returns Whether this event should be ignored by the observer.
	*/
	checkShouldIgnoreEventFromTarget(domTarget) {
		if (domTarget && domTarget.nodeType === 3) domTarget = domTarget.parentNode;
		if (!domTarget || domTarget.nodeType !== 1) return false;
		return domTarget.matches("[data-cke-ignore-events], [data-cke-ignore-events] *");
	}
};

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* @module engine/view/observer/domeventdata
*/
/**
* Information about a DOM event in context of the {@link module:engine/view/document~ViewDocument}.
* It wraps the native event, which usually should not be used as the wrapper contains
* additional data (like key code for keyboard events).
*
* @typeParam TEvent The type of DOM Event that this class represents.
*/
var ViewDocumentDomEventData = class {
	/**
	* Instance of the view controller.
	*/
	view;
	/**
	* The instance of the document.
	*/
	document;
	/**
	* The DOM event.
	*/
	domEvent;
	/**
	* The DOM target.
	*/
	domTarget;
	/**
	* @param view The instance of the view controller.
	* @param domEvent The DOM event.
	* @param additionalData Additional properties that the instance should contain.
	*/
	constructor(view, domEvent, additionalData) {
		this.view = view;
		this.document = view.document;
		this.domEvent = domEvent;
		this.domTarget = domEvent.target;
		extend(this, additionalData);
	}
	/**
	* The tree view element representing the target.
	*/
	get target() {
		return this.view.domConverter.mapDomToView(this.domTarget);
	}
	/**
	* Prevents the native's event default action.
	*/
	preventDefault() {
		this.domEvent.preventDefault();
	}
	/**
	* Stops native event propagation.
	*/
	stopPropagation() {
		this.domEvent.stopPropagation();
	}
};

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* @module engine/view/observer/domeventobserver
*/
/**
* Base class for DOM event observers. This class handles
* {@link module:engine/view/observer/observer~Observer#observe adding} listeners to DOM elements,
* {@link module:engine/view/observer/observer~Observer#disable disabling} and
* {@link module:engine/view/observer/observer~Observer#enable re-enabling} events.
* Child class needs to define
* {@link module:engine/view/observer/domeventobserver~DomEventObserver#domEventType DOM event type} and
* {@link module:engine/view/observer/domeventobserver~DomEventObserver#onDomEvent callback}.
*
* For instance:
*
* ```ts
* class ClickObserver extends DomEventObserver<'click'> {
* 	// It can also be defined as a normal property in the constructor.
* 	get domEventType(): 'click' {
* 		return 'click';
* 	}
*
* 	onDomEvent( domEvent: MouseEvent ): void {
* 		this.fire( 'click', domEvent );
* 	}
* }
* ```
*
* @typeParam EventType DOM Event type name or an union of those.
* @typeParam AdditionalData Additional data passed along with the event.
*/
var DomEventObserver = class extends Observer {
	/**
	* If set to `true` DOM events will be listened on the capturing phase.
	* Default value is `false`.
	*/
	useCapture = false;
	/**
	* If set to `true`, indicates that the function specified by listener will never call `preventDefault()`.
	* Default value is `false`.
	*/
	usePassive = false;
	/**
	* @inheritDoc
	*/
	observe(domElement) {
		(typeof this.domEventType == "string" ? [this.domEventType] : this.domEventType).forEach((type) => {
			this.listenTo(domElement, type, (eventInfo, domEvent) => {
				if (this.isEnabled && !this.checkShouldIgnoreEventFromTarget(domEvent.target)) this.onDomEvent(domEvent);
			}, {
				useCapture: this.useCapture,
				usePassive: this.usePassive
			});
		});
	}
	/**
	* @inheritDoc
	*/
	stopObserving(domElement) {
		this.stopListening(domElement);
	}
	/**
	* Calls `Document#fire()` if observer {@link #isEnabled is enabled}.
	*
	* @see module:utils/emittermixin~Emitter#fire
	* @param eventType The event type (name).
	* @param domEvent The DOM event.
	* @param additionalData The additional data which should extend the
	* {@link module:engine/view/observer/domeventdata~ViewDocumentDomEventData event data} object.
	*/
	fire(eventType, domEvent, additionalData) {
		if (this.isEnabled) this.document.fire(eventType, new ViewDocumentDomEventData(this.view, domEvent, additionalData));
	}
};

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* @module engine/view/observer/keyobserver
*/
/**
* Observer for events connected with pressing keyboard keys.
*
* Note that this observer is attached by the {@link module:engine/view/view~EditingView} and is available by default.
*/
var KeyObserver = class extends DomEventObserver {
	/**
	* @inheritDoc
	*/
	domEventType = ["keydown", "keyup"];
	/**
	* @inheritDoc
	*/
	onDomEvent(domEvt) {
		const data = {
			keyCode: domEvt.keyCode,
			altKey: domEvt.altKey,
			ctrlKey: domEvt.ctrlKey,
			shiftKey: domEvt.shiftKey,
			metaKey: domEvt.metaKey,
			get keystroke() {
				return getCode(this);
			}
		};
		this.fire(domEvt.type, domEvt, data);
	}
};

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* @module engine/view/observer/fakeselectionobserver
*/
/**
* Fake selection observer class. If view selection is fake it is placed in dummy DOM container. This observer listens
* on {@link module:engine/view/document~ViewDocument#event:keydown keydown} events and handles moving
* fake view selection to the correct place if arrow keys are pressed.
* Fires {@link module:engine/view/document~ViewDocument#event:selectionChange selectionChange event} simulating natural behaviour of
* {@link module:engine/view/observer/selectionobserver~SelectionObserver SelectionObserver}.
*/
var FakeSelectionObserver = class extends Observer {
	/**
	* Fires debounced event `selectionChangeDone`. It uses `es-toolkit#debounce` method to delay function call.
	*/
	_fireSelectionChangeDoneDebounced;
	/**
	* Creates new FakeSelectionObserver instance.
	*/
	constructor(view) {
		super(view);
		this._fireSelectionChangeDoneDebounced = debounce((data) => {
			this.document.fire("selectionChangeDone", data);
		}, 200);
	}
	/**
	* @inheritDoc
	*/
	observe() {
		const document = this.document;
		document.on("arrowKey", (eventInfo, data) => {
			if (document.selection.isFake && this.isEnabled) data.preventDefault();
		}, { context: "$capture" });
		document.on("arrowKey", (eventInfo, data) => {
			if (document.selection.isFake && this.isEnabled) this._handleSelectionMove(data.keyCode);
		}, { priority: "lowest" });
	}
	/**
	* @inheritDoc
	*/
	stopObserving() {}
	/**
	* @inheritDoc
	*/
	destroy() {
		super.destroy();
		this._fireSelectionChangeDoneDebounced.cancel();
	}
	/**
	* Handles collapsing view selection according to given key code. If left or up key is provided - new selection will be
	* collapsed to left. If right or down key is pressed - new selection will be collapsed to right.
	*
	* This method fires {@link module:engine/view/document~ViewDocument#event:selectionChange} and
	* {@link module:engine/view/document~ViewDocument#event:selectionChangeDone} events imitating behaviour of
	* {@link module:engine/view/observer/selectionobserver~SelectionObserver}.
	*/
	_handleSelectionMove(keyCode) {
		const selection = this.document.selection;
		const newSelection = new ViewSelection(selection.getRanges(), {
			backward: selection.isBackward,
			fake: false
		});
		if (keyCode == keyCodes.arrowleft || keyCode == keyCodes.arrowup) newSelection.setTo(newSelection.getFirstPosition());
		if (keyCode == keyCodes.arrowright || keyCode == keyCodes.arrowdown) newSelection.setTo(newSelection.getLastPosition());
		const data = {
			oldSelection: selection,
			newSelection,
			domSelection: null
		};
		this.document.fire("selectionChange", data);
		this._fireSelectionChangeDoneDebounced(data);
	}
};

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* @module engine/view/observer/mutationobserver
*/
/**
* Mutation observer's role is to watch for any DOM changes inside the editor that weren't
* done by the editor's {@link module:engine/view/renderer~ViewRenderer} itself and reverting these changes.
*
* It does this by observing all mutations in the DOM, marking related view elements as changed and calling
* {@link module:engine/view/renderer~ViewRenderer#render}. Because all mutated nodes are marked as
* "to be rendered" and the {@link module:engine/view/renderer~ViewRenderer#render `render()`} method is called,
* all changes are reverted in the DOM (the DOM is synced with the editor's view structure).
*
* Note that this observer is attached by the {@link module:engine/view/view~EditingView} and is available by default.
*/
var MutationObserver = class extends Observer {
	/**
	* Reference to the {@link module:engine/view/view~EditingView#domConverter}.
	*/
	domConverter;
	/**
	* Native mutation observer config.
	*/
	_config;
	/**
	* Observed DOM elements.
	*/
	_domElements;
	/**
	* Native mutation observer.
	*/
	_mutationObserver;
	/**
	* @inheritDoc
	*/
	constructor(view) {
		super(view);
		this._config = {
			childList: true,
			characterData: true,
			subtree: true
		};
		this.domConverter = view.domConverter;
		this._domElements = /* @__PURE__ */ new Set();
		this._mutationObserver = new window.MutationObserver(this._onMutations.bind(this));
	}
	/**
	* Synchronously handles mutations and empties the queue.
	*/
	flush() {
		this._onMutations(this._mutationObserver.takeRecords());
	}
	/**
	* @inheritDoc
	*/
	observe(domElement) {
		this._domElements.add(domElement);
		if (this.isEnabled) this._mutationObserver.observe(domElement, this._config);
	}
	/**
	* @inheritDoc
	*/
	stopObserving(domElement) {
		this._domElements.delete(domElement);
		if (this.isEnabled) {
			this._mutationObserver.disconnect();
			for (const domElement of this._domElements) this._mutationObserver.observe(domElement, this._config);
		}
	}
	/**
	* @inheritDoc
	*/
	enable() {
		super.enable();
		for (const domElement of this._domElements) this._mutationObserver.observe(domElement, this._config);
	}
	/**
	* @inheritDoc
	*/
	disable() {
		super.disable();
		this._mutationObserver.disconnect();
	}
	/**
	* @inheritDoc
	*/
	destroy() {
		super.destroy();
		this._mutationObserver.disconnect();
	}
	/**
	* Handles mutations. Mark view elements to sync and call render.
	*
	* @param domMutations Array of native mutations.
	*/
	_onMutations(domMutations) {
		if (domMutations.length === 0) return;
		const domConverter = this.domConverter;
		const mutatedTextNodes = /* @__PURE__ */ new Set();
		const elementsWithMutatedChildren = /* @__PURE__ */ new Set();
		for (const mutation of domMutations) {
			const element = domConverter.mapDomToView(mutation.target);
			if (!element) continue;
			if (element.is("uiElement") || element.is("rawElement")) continue;
			if (mutation.type === "childList" && !this._isBogusBrMutation(mutation)) elementsWithMutatedChildren.add(element);
		}
		for (const mutation of domMutations) {
			const element = domConverter.mapDomToView(mutation.target);
			if (element && (element.is("uiElement") || element.is("rawElement"))) continue;
			if (mutation.type === "characterData") {
				const text = domConverter.findCorrespondingViewText(mutation.target);
				if (text && !elementsWithMutatedChildren.has(text.parent)) mutatedTextNodes.add(text);
				else if (!text && startsWithFiller(mutation.target)) elementsWithMutatedChildren.add(domConverter.mapDomToView(mutation.target.parentNode));
			}
		}
		const mutations = [];
		for (const textNode of mutatedTextNodes) mutations.push({
			type: "text",
			node: textNode
		});
		for (const viewElement of elementsWithMutatedChildren) {
			const domElement = domConverter.mapViewToDom(viewElement);
			if (!isEqualWith(Array.from(viewElement.getChildren()), Array.from(domConverter.domChildrenToView(domElement, { withChildren: false })), sameNodes)) mutations.push({
				type: "children",
				node: viewElement
			});
		}
		if (mutations.length) this.document.fire("mutations", { mutations });
	}
	/**
	* Checks if mutation was generated by the browser inserting bogus br on the end of the block element.
	* Such mutations are generated while pressing space or performing native spellchecker correction
	* on the end of the block element in Firefox browser.
	*
	* @param mutation Native mutation object.
	*/
	_isBogusBrMutation(mutation) {
		let addedNode = null;
		if (mutation.nextSibling === null && mutation.removedNodes.length === 0 && mutation.addedNodes.length == 1) addedNode = this.domConverter.domToView(mutation.addedNodes[0], { withChildren: false });
		return addedNode && addedNode.is("element", "br");
	}
};
function sameNodes(child1, child2) {
	if (Array.isArray(child1)) return;
	if (child1 === child2) return true;
	else if (child1.is("$text") && child2.is("$text")) return child1.data === child2.data;
	return false;
}

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* @module engine/view/observer/focusobserver
*/
/**
* {@link module:engine/view/document~ViewDocument#event:focus Focus}
* and {@link module:engine/view/document~ViewDocument#event:blur blur} events observer.
* Focus observer handle also {@link module:engine/view/rooteditableelement~ViewRootEditableElement#isFocused isFocused} property of the
* {@link module:engine/view/rooteditableelement~ViewRootEditableElement root elements}.
*
* Note that this observer is attached by the {@link module:engine/view/view~EditingView} and is available by default.
*/
var FocusObserver = class extends DomEventObserver {
	/**
	* Identifier of the timeout currently used by focus listener to delay rendering execution.
	*/
	_renderTimeoutId = null;
	/**
	* Set to `true` if the document is in the process of setting the focus.
	*
	* The flag is used to indicate that setting the focus is in progress.
	*/
	_isFocusChanging = false;
	/**
	* @inheritDoc
	*/
	domEventType = ["focus", "blur"];
	/**
	* @inheritDoc
	*/
	constructor(view) {
		super(view);
		this.useCapture = true;
		const document = this.document;
		document.on("focus", () => this._handleFocus());
		document.on("blur", (evt, data) => this._handleBlur(data));
		document.on("beforeinput", () => {
			if (!document.isFocused) this._handleFocus();
		}, { priority: "highest" });
	}
	/**
	* Finishes setting the document focus state.
	*/
	flush() {
		if (this._isFocusChanging) {
			this._isFocusChanging = false;
			this.document.isFocused = true;
		}
	}
	/**
	* @inheritDoc
	*/
	onDomEvent(domEvent) {
		this.fire(domEvent.type, domEvent);
	}
	/**
	* @inheritDoc
	*/
	destroy() {
		this._clearTimeout();
		super.destroy();
	}
	/**
	* The `focus` event handler.
	*/
	_handleFocus() {
		this._clearTimeout();
		this._isFocusChanging = true;
		this._renderTimeoutId = setTimeout(() => {
			this._renderTimeoutId = null;
			this.flush();
			this.view.change(() => {});
		}, 50);
	}
	/**
	* The `blur` event handler.
	*/
	_handleBlur(data) {
		const selectedEditable = this.document.selection.editableElement;
		if (selectedEditable === null || selectedEditable === data.target) {
			this.document.isFocused = false;
			this._isFocusChanging = false;
			this.view.change(() => {});
		}
	}
	/**
	* Clears timeout.
	*/
	_clearTimeout() {
		if (this._renderTimeoutId) {
			clearTimeout(this._renderTimeoutId);
			this._renderTimeoutId = null;
		}
	}
};

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* @module engine/view/observer/selectionobserver
*/
/**
* Selection observer class observes selection changes in the document. If a selection changes on the document this
* observer checks if the DOM selection is different from the {@link module:engine/view/document~ViewDocument#selection view selection}.
* The selection observer fires {@link module:engine/view/document~ViewDocument#event:selectionChange} event only if
* a selection change was the only change in the document and the DOM selection is different from the view selection.
*
* This observer also manages the {@link module:engine/view/document~ViewDocument#isSelecting} property of the view document.
*
* Note that this observer is attached by the {@link module:engine/view/view~EditingView} and is available by default.
*/
var SelectionObserver = class extends Observer {
	/**
	* Instance of the mutation observer. Selection observer calls
	* {@link module:engine/view/observer/mutationobserver~MutationObserver#flush} to ensure that the mutations will be handled
	* before the {@link module:engine/view/document~ViewDocument#event:selectionChange} event is fired.
	*/
	mutationObserver;
	/**
	* Instance of the focus observer. Focus observer calls
	* {@link module:engine/view/observer/focusobserver~FocusObserver#flush} to mark the latest focus change as complete.
	*/
	focusObserver;
	/**
	* Reference to the view {@link module:engine/view/documentselection~ViewDocumentSelection} object used to compare
	* new selection with it.
	*/
	selection;
	/**
	* Reference to the {@link module:engine/view/view~EditingView#domConverter}.
	*/
	domConverter;
	/**
	* A set of documents which have added `selectionchange` listener to avoid adding a listener twice to the same
	* document.
	*/
	_documents = /* @__PURE__ */ new WeakSet();
	/**
	* Fires debounced event `selectionChangeDone`. It uses `es-toolkit#debounce` method to delay function call.
	*/
	_fireSelectionChangeDoneDebounced;
	/**
	* When called, starts clearing the {@link #_loopbackCounter} counter in time intervals. When the number of selection
	* changes exceeds a certain limit within the interval of time, the observer will not fire `selectionChange` but warn about
	* possible infinite selection loop.
	*/
	_clearInfiniteLoopInterval;
	/**
	* Unlocks the `isSelecting` state of the view document in case the selection observer did not record this fact
	* correctly (for whatever reason). It is a safeguard (paranoid check), that returns document to the normal state
	* after a certain period of time (debounced, postponed by each selectionchange event).
	*/
	_documentIsSelectingInactivityTimeoutDebounced;
	/**
	* Private property to check if the code does not enter infinite loop.
	*/
	_loopbackCounter = 0;
	/**
	* A set of DOM documents that have a pending selection change.
	* Pending selection change is recorded while selection change event is detected on non focused editable.
	*/
	_pendingSelectionChange = /* @__PURE__ */ new Set();
	constructor(view) {
		super(view);
		this.mutationObserver = view.getObserver(MutationObserver);
		this.focusObserver = view.getObserver(FocusObserver);
		this.selection = this.document.selection;
		this.domConverter = view.domConverter;
		this._fireSelectionChangeDoneDebounced = debounce((data) => {
			this.document.fire("selectionChangeDone", data);
		}, 200);
		this._clearInfiniteLoopInterval = setInterval(() => this._clearInfiniteLoop(), 1e3);
		this._documentIsSelectingInactivityTimeoutDebounced = debounce(() => this.document.isSelecting = false, 5e3);
		this.view.document.on("change:isFocused", (evt, name, isFocused) => {
			if (isFocused && this._pendingSelectionChange.size) {
				for (const domDocument of Array.from(this._pendingSelectionChange)) this._handleSelectionChange(domDocument);
				this._pendingSelectionChange.clear();
			}
		});
	}
	/**
	* @inheritDoc
	*/
	observe(domElement) {
		const domDocument = domElement.ownerDocument;
		const startDocumentIsSelecting = () => {
			this.document.isSelecting = true;
			this._documentIsSelectingInactivityTimeoutDebounced();
		};
		const endDocumentIsSelecting = () => {
			if (!this.document.isSelecting) return;
			this._handleSelectionChange(domDocument);
			this.document.isSelecting = false;
			this._documentIsSelectingInactivityTimeoutDebounced.cancel();
		};
		this.listenTo(domElement, "selectstart", startDocumentIsSelecting, { priority: "highest" });
		this.listenTo(domElement, "keydown", endDocumentIsSelecting, {
			priority: "highest",
			useCapture: true
		});
		this.listenTo(domElement, "keyup", endDocumentIsSelecting, {
			priority: "highest",
			useCapture: true
		});
		if (this._documents.has(domDocument)) return;
		this.listenTo(domDocument, "mouseup", endDocumentIsSelecting, {
			priority: "highest",
			useCapture: true
		});
		this.listenTo(domDocument, "selectionchange", () => {
			if (this.document.isComposing && !env.isAndroid) return;
			this._handleSelectionChange(domDocument);
			this._documentIsSelectingInactivityTimeoutDebounced();
		});
		this.listenTo(this.view.document, "compositionstart", () => {
			this._handleSelectionChange(domDocument);
		}, { priority: "lowest" });
		this._documents.add(domDocument);
	}
	/**
	* @inheritDoc
	*/
	stopObserving(domElement) {
		this.stopListening(domElement);
	}
	/**
	* @inheritDoc
	*/
	destroy() {
		super.destroy();
		clearInterval(this._clearInfiniteLoopInterval);
		this._fireSelectionChangeDoneDebounced.cancel();
		this._documentIsSelectingInactivityTimeoutDebounced.cancel();
	}
	/* v8 ignore next 6 -- @preserve */
	_reportInfiniteLoop() {}
	/**
	* Selection change listener. {@link module:engine/view/observer/mutationobserver~MutationObserver#flush Flush} mutations, check if
	* a selection changes and fires {@link module:engine/view/document~ViewDocument#event:selectionChange} event on every change
	* and {@link module:engine/view/document~ViewDocument#event:selectionChangeDone} when a selection stop changing.
	*
	* @param domDocument DOM document.
	*/
	_handleSelectionChange(domDocument) {
		if (!this.isEnabled) return;
		const domSelection = domDocument.defaultView.getSelection();
		if (this.checkShouldIgnoreEventFromTarget(domSelection.anchorNode)) return;
		this.mutationObserver.flush();
		const newViewSelection = this.domConverter.domSelectionToView(domSelection);
		if (newViewSelection.rangeCount == 0) {
			this.view.hasDomSelection = false;
			return;
		}
		this.view.hasDomSelection = true;
		this.focusObserver.flush();
		if (!this.view.document.isFocused && !this.view.document.isReadOnly) {
			this._pendingSelectionChange.add(domDocument);
			return;
		}
		this._pendingSelectionChange.delete(domDocument);
		if (this.selection.isEqual(newViewSelection) && this.domConverter.isDomSelectionCorrect(domSelection)) return;
		if (++this._loopbackCounter > 60) {
			this._reportInfiniteLoop();
			return;
		}
		if (!isSelectionWithinRootElements(newViewSelection)) this.view.forceRender();
		else if (this.selection.isSimilar(newViewSelection)) this.view.forceRender();
		else {
			const data = {
				oldSelection: this.selection,
				newSelection: newViewSelection,
				domSelection
			};
			this.document.fire("selectionChange", data);
			this._fireSelectionChangeDoneDebounced(data);
		}
	}
	/**
	* Clears `SelectionObserver` internal properties connected with preventing infinite loop.
	*/
	_clearInfiniteLoop() {
		this._loopbackCounter = 0;
	}
};
/**
* Checks if all roots of the selection's range boundaries are root elements.
* Returns true if the selection is fully contained within root elements (not orphaned).
*
* @param selection The view selection to check.
* @returns True if all range boundaries are within root elements, false otherwise.
*/
function isSelectionWithinRootElements(selection) {
	return Array.from(selection.getRanges()).flatMap((range) => [range.start.root, range.end.root]).every((root) => root && root.is("rootElement"));
}

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* @module engine/view/observer/compositionobserver
*/
/**
* {@link module:engine/view/document~ViewDocument#event:compositionstart Compositionstart},
* {@link module:engine/view/document~ViewDocument#event:compositionupdate compositionupdate} and
* {@link module:engine/view/document~ViewDocument#event:compositionend compositionend} events observer.
*
* Note that this observer is attached by the {@link module:engine/view/view~EditingView} and is available by default.
*/
var CompositionObserver = class extends DomEventObserver {
	/**
	* @inheritDoc
	*/
	domEventType = [
		"compositionstart",
		"compositionupdate",
		"compositionend"
	];
	/**
	* @inheritDoc
	*/
	constructor(view) {
		super(view);
		const document = this.document;
		document.on("compositionstart", () => {
			document.isComposing = true;
		});
		document.on("compositionend", () => {
			document.isComposing = false;
		});
	}
	/**
	* @inheritDoc
	*/
	onDomEvent(domEvent) {
		this.fire(domEvent.type, domEvent, { data: domEvent.data });
	}
};

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* A facade over the native [`DataTransfer`](https://developer.mozilla.org/en-US/docs/Web/API/DataTransfer) object.
*/
var ViewDataTransfer = class {
	/**
	* The array of files created from the native `DataTransfer#files` or `DataTransfer#items`.
	*/
	_files;
	/**
	* The native DataTransfer object.
	*/
	_native;
	/**
	* @param nativeDataTransfer The native [`DataTransfer`](https://developer.mozilla.org/en-US/docs/Web/API/DataTransfer) object.
	* @param options.cacheFiles Whether `files` list should be initialized in the constructor.
	*/
	constructor(nativeDataTransfer, options = {}) {
		this._files = options.cacheFiles ? getFiles(nativeDataTransfer) : null;
		this._native = nativeDataTransfer;
	}
	/**
	* The array of files created from the native `DataTransfer#files` or `DataTransfer#items`.
	*/
	get files() {
		if (!this._files) this._files = getFiles(this._native);
		return this._files;
	}
	/**
	* Returns an array of available native content types.
	*/
	get types() {
		return this._native.types;
	}
	/**
	* Gets the data from the data transfer by its MIME type.
	*
	* ```ts
	* dataTransfer.getData( 'text/plain' );
	* ```
	*
	* @param type The MIME type. E.g. `text/html` or `text/plain`.
	*/
	getData(type) {
		return this._native.getData(type);
	}
	/**
	* Sets the data in the data transfer.
	*
	* @param type The MIME type. E.g. `text/html` or `text/plain`.
	*/
	setData(type, data) {
		this._native.setData(type, data);
	}
	/**
	* The effect that is allowed for a drag operation.
	*/
	set effectAllowed(value) {
		this._native.effectAllowed = value;
	}
	get effectAllowed() {
		return this._native.effectAllowed;
	}
	/**
	* The actual drop effect.
	*/
	set dropEffect(value) {
		this._native.dropEffect = value;
	}
	get dropEffect() {
		return this._native.dropEffect;
	}
	/**
	* Set a preview image of the dragged content.
	*/
	setDragImage(image, x, y) {
		this._native.setDragImage(image, x, y);
	}
	/**
	* Whether the dragging operation was canceled.
	*/
	get isCanceled() {
		return this._native.dropEffect == "none" || !!this._native.mozUserCancelled;
	}
};
function getFiles(nativeDataTransfer) {
	const files = Array.from(nativeDataTransfer.files || []);
	const items = Array.from(nativeDataTransfer.items || []);
	if (files.length) return files;
	return items.filter((item) => item.kind === "file").map((item) => item.getAsFile());
}

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* @module engine/view/observer/inputobserver
*/
/**
* Observer for events connected with data input.
*
* **Note**: This observer is attached by {@link module:engine/view/view~EditingView} and available by default in all
* editor instances.
*/
var InputObserver = class extends DomEventObserver {
	/**
	* @inheritDoc
	*/
	domEventType = "beforeinput";
	/**
	* @inheritDoc
	*/
	onDomEvent(domEvent) {
		const domTargetRanges = domEvent.getTargetRanges();
		const view = this.view;
		const viewDocument = view.document;
		let dataTransfer = null;
		let data = null;
		let targetRanges = [];
		if (domEvent.dataTransfer) dataTransfer = new ViewDataTransfer(domEvent.dataTransfer);
		if (domEvent.data !== null) data = domEvent.data;
		else if (dataTransfer) data = dataTransfer.getData("text/plain");
		if (viewDocument.selection.isFake) {
			targetRanges = Array.from(viewDocument.selection.getRanges());
			domEvent.preventDefault();
		} else if (domTargetRanges.length) targetRanges = domTargetRanges.map((domRange) => {
			let viewStart = view.domConverter.domPositionToView(domRange.startContainer, domRange.startOffset);
			const viewEnd = view.domConverter.domPositionToView(domRange.endContainer, domRange.endOffset);
			if (viewStart && startsWithFiller(domRange.startContainer) && domRange.startOffset < 7) {
				domEvent.preventDefault();
				let count = 7 - domRange.startOffset;
				viewStart = viewStart.getLastMatchingPosition((value) => {
					if (value.item.is("attributeElement") || value.item.is("uiElement")) return true;
					if (value.item.is("$textProxy") && count--) return true;
					return false;
				}, {
					direction: "backward",
					singleCharacters: true
				});
			}
			if (isFollowedByInlineFiller(domRange.endContainer, domRange.endOffset)) domEvent.preventDefault();
			if (viewStart) return view.createRange(viewStart, viewEnd);
			else if (viewEnd) return view.createRange(viewEnd);
			/* v8 ignore stop */
		}).filter((range) => !!range);
		else if (env.isAndroid) {
			const domSelection = domEvent.target.ownerDocument.defaultView.getSelection();
			targetRanges = Array.from(view.domConverter.domSelectionToView(domSelection).getRanges());
		}
		if (env.isAndroid && domEvent.inputType == "insertCompositionText" && data && data.endsWith("\n")) {
			this.fire(domEvent.type, domEvent, {
				inputType: "insertParagraph",
				targetRanges: [view.createRange(targetRanges[0].end)]
			});
			return;
		}
		if (["insertText", "insertReplacementText"].includes(domEvent.inputType) && data && data.includes("\n")) {
			const parts = data.split(/\n{1,2}/g);
			let partTargetRanges = targetRanges;
			domEvent.preventDefault();
			for (let i = 0; i < parts.length; i++) {
				const dataPart = parts[i];
				if (dataPart != "") {
					this.fire(domEvent.type, domEvent, {
						data: dataPart,
						dataTransfer,
						targetRanges: partTargetRanges,
						inputType: domEvent.inputType,
						isComposing: domEvent.isComposing
					});
					partTargetRanges = [viewDocument.selection.getFirstRange()];
				}
				if (i + 1 < parts.length) {
					this.fire(domEvent.type, domEvent, {
						inputType: "insertParagraph",
						targetRanges: partTargetRanges
					});
					partTargetRanges = [viewDocument.selection.getFirstRange()];
				}
			}
			return;
		}
		this.fire(domEvent.type, domEvent, {
			data,
			dataTransfer,
			targetRanges,
			inputType: domEvent.inputType,
			isComposing: domEvent.isComposing
		});
	}
};
/**
* Returns `true` if there is an inline filler just after the position in DOM.
* It walks up the DOM tree if the offset is at the end of the node.
*/
function isFollowedByInlineFiller(node, offset) {
	while (node.parentNode) {
		if (isText(node)) {
			if (offset != node.data.length) return false;
		} else if (offset != node.childNodes.length) return false;
		offset = indexOf(node) + 1;
		node = node.parentNode;
		if (offset < node.childNodes.length && startsWithFiller(node.childNodes[offset])) return true;
	}
	return false;
}

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* @module engine/view/observer/arrowkeysobserver
*/
/**
* Arrow keys observer introduces the {@link module:engine/view/document~ViewDocument#event:arrowKey `Document#arrowKey`} event.
*
* Note that this observer is attached by the {@link module:engine/view/view~EditingView} and is available by default.
*/
var ArrowKeysObserver = class extends Observer {
	/**
	* @inheritDoc
	*/
	constructor(view) {
		super(view);
		this.document.on("keydown", (event, data) => {
			if (this.isEnabled && isArrowKeyCode(data.keyCode)) {
				const eventInfo = new BubblingEventInfo(this.document, "arrowKey", this.document.selection.getFirstRange());
				this.document.fire(eventInfo, data);
				if (eventInfo.stop.called) event.stop();
			}
		});
	}
	/**
	* @inheritDoc
	*/
	observe() {}
	/**
	* @inheritDoc
	*/
	stopObserving() {}
};

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* Tab observer introduces the {@link module:engine/view/document~ViewDocument#event:tab `Document#tab`} event.
*
* Note that because {@link module:engine/view/observer/tabobserver~TabObserver} is attached by the
* {@link module:engine/view/view~EditingView}, this event is available by default.
*/
var TabObserver = class extends Observer {
	/**
	* @inheritDoc
	*/
	constructor(view) {
		super(view);
		const doc = this.document;
		doc.on("keydown", (evt, data) => {
			if (!this.isEnabled || data.keyCode != keyCodes.tab || data.ctrlKey) return;
			const event = new BubblingEventInfo(doc, "tab", doc.selection.getFirstRange());
			doc.fire(event, data);
			if (event.stop.called) evt.stop();
		});
	}
	/**
	* @inheritDoc
	*/
	observe() {}
	/**
	* @inheritDoc
	*/
	stopObserving() {}
};

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* @module engine/view/view
*/
const EditingViewBase = /* #__PURE__ */ ObservableMixin();
/**
* Editor's view controller class. Its main responsibility is DOM - View management for editing purposes, to provide
* abstraction over the DOM structure and events and hide all browsers quirks.
*
* View controller renders view document to DOM whenever view structure changes. To determine when view can be rendered,
* all changes need to be done using the {@link module:engine/view/view~EditingView#change} method, using
* {@link module:engine/view/downcastwriter~ViewDowncastWriter}:
*
* ```ts
* view.change( writer => {
* 	writer.insert( position, writer.createText( 'foo' ) );
* } );
* ```
*
* View controller also register {@link module:engine/view/observer/observer~Observer observers} which observes changes
* on DOM and fire events on the {@link module:engine/view/document~ViewDocument Document}.
* Note that the following observers are added by the class constructor and are always available:
*
* * {@link module:engine/view/observer/selectionobserver~SelectionObserver},
* * {@link module:engine/view/observer/focusobserver~FocusObserver},
* * {@link module:engine/view/observer/keyobserver~KeyObserver},
* * {@link module:engine/view/observer/fakeselectionobserver~FakeSelectionObserver}.
* * {@link module:engine/view/observer/compositionobserver~CompositionObserver}.
* * {@link module:engine/view/observer/inputobserver~InputObserver}.
* * {@link module:engine/view/observer/arrowkeysobserver~ArrowKeysObserver}.
* * {@link module:engine/view/observer/tabobserver~TabObserver}.
*
* This class also {@link module:engine/view/view~EditingView#attachDomRoot binds the DOM and the view elements}.
*
* If you do not need full a DOM - view management, and only want to transform a tree of view elements to a tree of DOM
* elements you do not need this controller. You can use the {@link module:engine/view/domconverter~ViewDomConverter ViewDomConverter}
* instead.
*/
var EditingView = class extends EditingViewBase {
	/**
	* Instance of the {@link module:engine/view/document~ViewDocument} associated with this view controller.
	*/
	document;
	/**
	* Instance of the {@link module:engine/view/domconverter~ViewDomConverter domConverter} used by
	* {@link module:engine/view/view~EditingView#_renderer renderer}
	* and {@link module:engine/view/observer/observer~Observer observers}.
	*/
	domConverter;
	/**
	* Roots of the DOM tree. Map on the `HTMLElement`s with roots names as keys.
	*/
	domRoots = /* @__PURE__ */ new Map();
	/**
	* Instance of the {@link module:engine/view/renderer~ViewRenderer renderer}.
	*/
	_renderer;
	/**
	* A DOM root attributes cache. It saves the initial values of DOM root attributes before the DOM element
	* is {@link module:engine/view/view~EditingView#attachDomRoot attached} to the view so later on, when
	* the view is destroyed ({@link module:engine/view/view~EditingView#detachDomRoot}), they can be easily restored.
	* This way, the DOM element can go back to the (clean) state as if the editing view never used it.
	*/
	_initialDomRootAttributes = /* @__PURE__ */ new WeakMap();
	/**
	* Map of registered {@link module:engine/view/observer/observer~Observer observers}.
	*/
	_observers = /* @__PURE__ */ new Map();
	/**
	* ViewDowncastWriter instance used in {@link #change change method} callbacks.
	*/
	_writer;
	/**
	* Is set to `true` when {@link #change view changes} are currently in progress.
	*/
	_ongoingChange = false;
	/**
	* Used to prevent calling {@link #forceRender} and {@link #change} during rendering view to the DOM.
	*/
	_postFixersInProgress = false;
	/**
	* Internal flag to temporary disable rendering. See the usage in the {@link #_disableRendering}.
	*/
	_renderingDisabled = false;
	/**
	* Internal flag that disables rendering when there are no changes since the last rendering.
	* It stores information about changed selection and changed elements from attached document roots.
	*/
	_hasChangedSinceTheLastRendering = false;
	/**
	* @param stylesProcessor The styles processor instance.
	*/
	constructor(stylesProcessor) {
		super();
		this.document = new ViewDocument(stylesProcessor);
		this.domConverter = new ViewDomConverter(this.document);
		this.set("isRenderingInProgress", false);
		this.set("hasDomSelection", false);
		this._renderer = new ViewRenderer(this.domConverter, this.document.selection);
		this._renderer.bind("isFocused", "isSelecting", "isComposing").to(this.document, "isFocused", "isSelecting", "isComposing");
		this._writer = new ViewDowncastWriter(this.document);
		this.addObserver(MutationObserver);
		this.addObserver(FocusObserver);
		this.addObserver(SelectionObserver);
		this.addObserver(KeyObserver);
		this.addObserver(FakeSelectionObserver);
		this.addObserver(CompositionObserver);
		this.addObserver(ArrowKeysObserver);
		this.addObserver(InputObserver);
		this.addObserver(TabObserver);
		injectQuirksHandling(this);
		injectUiElementHandling(this);
		this.on("render", () => {
			this._render();
			this.document.fire("layoutChanged");
			this._hasChangedSinceTheLastRendering = false;
		});
		this.listenTo(this.document.selection, "change", () => {
			this._hasChangedSinceTheLastRendering = true;
		});
		this.listenTo(this.document, "change:isFocused", () => {
			this._hasChangedSinceTheLastRendering = true;
		});
		if (env.isiOS) this.listenTo(this.document, "blur", (evt, data) => {
			if (!this.domConverter.mapDomToView(data.domEvent.relatedTarget)) this.domConverter._clearDomSelection();
		});
		this.listenTo(this.document, "mutations", (evt, { mutations }) => {
			mutations.forEach((mutation) => this._renderer.markToSync(mutation.type, mutation.node));
		}, { priority: "low" });
		this.listenTo(this.document, "mutations", () => {
			this.forceRender();
		}, { priority: "lowest" });
	}
	/**
	* Attaches a DOM root element to the view element and enable all observers on that element.
	* Also {@link module:engine/view/renderer~ViewRenderer#markToSync mark element} to be synchronized
	* with the view what means that all child nodes will be removed and replaced with content of the view root.
	*
	* This method also will change view element name as the same as tag name of given dom root.
	* Name is always transformed to lower case.
	*
	* **Note:** Use {@link #detachDomRoot `detachDomRoot()`} to revert this action.
	*
	* @param domRoot DOM root element.
	* @param name Name of the root.
	*/
	attachDomRoot(domRoot, name = "main") {
		const viewRoot = this.document.getRoot(name);
		viewRoot._name = domRoot.tagName.toLowerCase();
		const initialDomRootAttributes = {};
		for (const { name, value } of Array.from(domRoot.attributes)) {
			initialDomRootAttributes[name] = value;
			if (name === "class") this._writer.addClass(value.split(" "), viewRoot);
			else if (!viewRoot.hasAttribute(name)) this._writer.setAttribute(name, value, viewRoot);
		}
		this._initialDomRootAttributes.set(domRoot, initialDomRootAttributes);
		const updateContenteditableAttribute = () => {
			this._writer.setAttribute("contenteditable", (!viewRoot.isReadOnly).toString(), viewRoot);
			if (viewRoot.isReadOnly) this._writer.addClass("ck-read-only", viewRoot);
			else this._writer.removeClass("ck-read-only", viewRoot);
		};
		updateContenteditableAttribute();
		this.domRoots.set(name, domRoot);
		this.domConverter.bindElements(domRoot, viewRoot);
		this._renderer.markToSync("children", viewRoot);
		this._renderer.markToSync("attributes", viewRoot);
		this._renderer.domDocuments.add(domRoot.ownerDocument);
		viewRoot.on("change:children", (evt, node) => this._renderer.markToSync("children", node));
		viewRoot.on("change:attributes", (evt, node) => this._renderer.markToSync("attributes", node));
		viewRoot.on("change:text", (evt, node) => this._renderer.markToSync("text", node));
		viewRoot.on("change:isReadOnly", () => this.change(updateContenteditableAttribute));
		viewRoot.on("change", () => {
			this._hasChangedSinceTheLastRendering = true;
		});
		for (const observer of this._observers.values()) observer.observe(domRoot, name);
	}
	/**
	* Detaches a DOM root element from the view element and restores its attributes to the state before
	* {@link #attachDomRoot `attachDomRoot()`}.
	*
	* @param name Name of the root to detach.
	*/
	detachDomRoot(name) {
		const domRoot = this.domRoots.get(name);
		Array.from(domRoot.attributes).forEach(({ name }) => domRoot.removeAttribute(name));
		const initialDomRootAttributes = this._initialDomRootAttributes.get(domRoot);
		for (const attribute in initialDomRootAttributes) domRoot.setAttribute(attribute, initialDomRootAttributes[attribute]);
		this.domRoots.delete(name);
		this.domConverter.unbindDomElement(domRoot);
		for (const observer of this._observers.values()) observer.stopObserving(domRoot);
	}
	/**
	* Gets DOM root element.
	*
	* @param name  Name of the root.
	* @returns DOM root element instance.
	*/
	getDomRoot(name = "main") {
		return this.domRoots.get(name);
	}
	/**
	* Creates observer of the given type if not yet created, {@link module:engine/view/observer/observer~Observer#enable enables} it
	* and {@link module:engine/view/observer/observer~Observer#observe attaches} to all existing and future
	* {@link #domRoots DOM roots}.
	*
	* Note: Observers are recognized by their constructor (classes). A single observer will be instantiated and used only
	* when registered for the first time. This means that features and other components can register a single observer
	* multiple times without caring whether it has been already added or not.
	*
	* @param ObserverConstructor The constructor of an observer to add.
	* Should create an instance inheriting from {@link module:engine/view/observer/observer~Observer}.
	* @returns Added observer instance.
	*/
	addObserver(ObserverConstructor) {
		let observer = this._observers.get(ObserverConstructor);
		if (observer) return observer;
		observer = new ObserverConstructor(this);
		this._observers.set(ObserverConstructor, observer);
		for (const [name, domElement] of this.domRoots) observer.observe(domElement, name);
		observer.enable();
		return observer;
	}
	/**
	* Returns observer of the given type or `undefined` if such observer has not been added yet.
	*
	* @param ObserverConstructor The constructor of an observer to get.
	* @returns Observer instance or undefined.
	*/
	getObserver(ObserverConstructor) {
		return this._observers.get(ObserverConstructor);
	}
	/**
	* Disables all added observers.
	*/
	disableObservers() {
		for (const observer of this._observers.values()) observer.disable();
	}
	/**
	* Enables all added observers.
	*/
	enableObservers() {
		for (const observer of this._observers.values()) observer.enable();
	}
	/**
	* Scrolls the page viewport and {@link #domRoots} with their ancestors to reveal the
	* caret, **if not already visible to the user**.
	*
	* **Note**: Calling this method fires the {@link module:engine/view/view~ViewScrollToTheSelectionEvent} event that
	* allows custom behaviors.
	*
	* @param options Additional configuration of the scrolling behavior.
	* @param options.viewportOffset A distance between the DOM selection and the viewport boundary to be maintained
	* while scrolling to the selection (default is 20px). Setting this value to `0` will reveal the selection precisely at
	* the viewport boundary.
	* @param options.ancestorOffset A distance between the DOM selection and scrollable DOM root ancestor(s) to be maintained
	* while scrolling to the selection (default is 20px). Setting this value to `0` will reveal the selection precisely at
	* the scrollable ancestor(s) boundary.
	* @param options.alignToTop When set `true`, the DOM selection will be aligned to the top of the viewport if not already visible
	* (see `forceScroll` to learn more).
	* @param options.forceScroll When set `true`, the DOM selection will be aligned to the top of the viewport and scrollable ancestors
	* whether it is already visible or not. This option will only work when `alignToTop` is `true`.
	*/
	scrollToTheSelection({ alignToTop, forceScroll, viewportOffset = 20, ancestorOffset = 20 } = {}) {
		const range = this.document.selection.getFirstRange();
		if (!range) return;
		const originalArgs = cloneDeep({
			alignToTop,
			forceScroll,
			viewportOffset,
			ancestorOffset
		});
		if (typeof viewportOffset === "number") viewportOffset = {
			top: viewportOffset,
			bottom: viewportOffset,
			left: viewportOffset,
			right: viewportOffset
		};
		const options = {
			target: this.domConverter.viewRangeToDom(range),
			viewportOffset,
			ancestorOffset,
			alignToTop,
			forceScroll
		};
		this.fire("scrollToTheSelection", options, originalArgs);
		scrollViewportToShowTarget(options);
	}
	/**
	* It will focus DOM element representing {@link module:engine/view/editableelement~ViewEditableElement ViewEditableElement}
	* that is currently having selection inside.
	*/
	focus() {
		if (!this.document.isFocused) {
			const editable = this.document.selection.editableElement;
			if (editable) {
				this.domConverter.focus(editable);
				this.forceRender();
			}
		}
	}
	/**
	* The `change()` method is the primary way of changing the view. You should use it to modify any node in the view tree.
	* It makes sure that after all changes are made the view is rendered to the DOM (assuming that the view will be changed
	* inside the callback). It prevents situations when the DOM is updated when the view state is not yet correct. It allows
	* to nest calls one inside another and still performs a single rendering after all those changes are made.
	* It also returns the return value of its callback.
	*
	* ```ts
	* const text = view.change( writer => {
	* 	const newText = writer.createText( 'foo' );
	* 	writer.insert( position1, newText );
	*
	* 	view.change( writer => {
	* 		writer.insert( position2, writer.createText( 'bar' ) );
	* 	} );
	*
	* 	writer.remove( range );
	*
	* 	return newText;
	* } );
	* ```
	*
	* When the outermost change block is done and rendering to the DOM is over the
	* {@link module:engine/view/view~EditingView#event:render `View#render`} event is fired.
	*
	* This method throws a `applying-view-changes-on-rendering` error when
	* the change block is used after rendering to the DOM has started.
	*
	* @param callback Callback function which may modify the view.
	* @returns Value returned by the callback.
	*/
	change(callback) {
		if (this.isRenderingInProgress || this._postFixersInProgress)
 /**
		* Thrown when there is an attempt to make changes to the view tree when it is in incorrect state. This may
		* cause some unexpected behaviour and inconsistency between the DOM and the view.
		* This may be caused by:
		*
		* * calling {@link module:engine/view/view~EditingView#change} or {@link module:engine/view/view~EditingView#forceRender}
		* during rendering process,
		* * calling {@link module:engine/view/view~EditingView#change} or {@link module:engine/view/view~EditingView#forceRender}
		* inside of {@link module:engine/view/document~ViewDocument#registerPostFixer post-fixer function}.
		*
		* @error cannot-change-view-tree
		*/
		throw new CKEditorError("cannot-change-view-tree", this);
		try {
			if (this._ongoingChange) return callback(this._writer);
			this._ongoingChange = true;
			const callbackResult = callback(this._writer);
			this._ongoingChange = false;
			if (!this._renderingDisabled && this._hasChangedSinceTheLastRendering) {
				this._postFixersInProgress = true;
				this.document._callPostFixers(this._writer);
				this._postFixersInProgress = false;
				this.fire("render");
			}
			return callbackResult;
		} catch (err) {
			CKEditorError.rethrowUnexpectedError(err, this);
		}
	}
	/**
	* Forces rendering {@link module:engine/view/document~ViewDocument view document} to DOM. If any view changes are
	* currently in progress, rendering will start after all {@link #change change blocks} are processed.
	*
	* Note that this method is dedicated for special cases. All view changes should be wrapped in the {@link #change}
	* block and the view will automatically check whether it needs to render DOM or not.
	*
	* Throws {@link module:utils/ckeditorerror~CKEditorError CKEditorError} `applying-view-changes-on-rendering` when
	* trying to re-render when rendering to DOM has already started.
	*/
	forceRender() {
		this._hasChangedSinceTheLastRendering = true;
		this.getObserver(FocusObserver).flush();
		this.change(() => {});
	}
	/**
	* Destroys this instance. Makes sure that all observers are destroyed and listeners removed.
	*/
	destroy() {
		for (const observer of this._observers.values()) observer.destroy();
		this.document.destroy();
		this.stopListening();
	}
	/**
	* Creates position at the given location. The location can be specified as:
	*
	* * a {@link module:engine/view/position~ViewPosition position},
	* * parent element and offset (offset defaults to `0`),
	* * parent element and `'end'` (sets position at the end of that element),
	* * {@link module:engine/view/item~ViewItem view item} and `'before'` or `'after'` (sets position before or after given view item).
	*
	* This method is a shortcut to other constructors such as:
	*
	* * {@link #createPositionBefore},
	* * {@link #createPositionAfter},
	*
	* @param offset Offset or one of the flags. Used only when first parameter is a {@link module:engine/view/item~ViewItem view item}.
	*/
	createPositionAt(itemOrPosition, offset) {
		return ViewPosition._createAt(itemOrPosition, offset);
	}
	/**
	* Creates a new position after given view item.
	*
	* @param item View item after which the position should be located.
	*/
	createPositionAfter(item) {
		return ViewPosition._createAfter(item);
	}
	/**
	* Creates a new position before given view item.
	*
	* @param item View item before which the position should be located.
	*/
	createPositionBefore(item) {
		return ViewPosition._createBefore(item);
	}
	/**
	* Creates a range spanning from `start` position to `end` position.
	*
	* **Note:** This factory method creates it's own {@link module:engine/view/position~ViewPosition} instances basing on passed values.
	*
	* @param start Start position.
	* @param end End position. If not set, range will be collapsed at `start` position.
	*/
	createRange(start, end) {
		return new ViewRange(start, end);
	}
	/**
	* Creates a range that starts before given {@link module:engine/view/item~ViewItem view item} and ends after it.
	*/
	createRangeOn(item) {
		return ViewRange._createOn(item);
	}
	/**
	* Creates a range inside an {@link module:engine/view/element~ViewElement element} which starts before the first child of
	* that element and ends after the last child of that element.
	*
	* @param element Element which is a parent for the range.
	*/
	createRangeIn(element) {
		return ViewRange._createIn(element);
	}
	createSelection(...args) {
		return new ViewSelection(...args);
	}
	/**
	* Disables or enables rendering. If the flag is set to `true` then the rendering will be disabled.
	* If the flag is set to `false` and if there was some change in the meantime, then the rendering action will be performed.
	*
	* @internal
	* @param flag A flag indicates whether the rendering should be disabled.
	*/
	_disableRendering(flag) {
		this._renderingDisabled = flag;
		if (flag == false) this.change(() => {});
	}
	/**
	* Renders all changes. In order to avoid triggering the observers (e.g. selection) all observers are disabled
	* before rendering and re-enabled after that.
	*/
	_render() {
		this.isRenderingInProgress = true;
		this.disableObservers();
		this._renderer.render();
		this.enableObservers();
		this.isRenderingInProgress = false;
	}
};

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
var ModelTypeCheckable = class {
	/* istanbul ignore next -- @preserve */
	is() {
		throw new Error("is() method is abstract");
	}
};

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* @module engine/model/textproxy
*/
/**
* `ModelTextProxy` represents a part of {@link module:engine/model/text~ModelText text node}.
*
* Since {@link module:engine/model/position~ModelPosition positions} can be placed between characters of a text node,
* {@link module:engine/model/range~ModelRange ranges} may contain only parts of text nodes.
* When {@link module:engine/model/range~ModelRange#getItems getting items}
* contained in such range, we need to represent a part of that text node, since returning the whole text node would be incorrect.
* `ModelTextProxy` solves this issue.
*
* `ModelTextProxy` has an API similar to {@link module:engine/model/text~ModelText Text} and allows to do
* most of the common tasks performed on model nodes.
*
* **Note:** Some `ModelTextProxy` instances may represent whole text node, not just a part of it.
* See {@link module:engine/model/textproxy~ModelTextProxy#isPartial}.
*
* **Note:** `ModelTextProxy` is not an instance of {@link module:engine/model/node~ModelNode node}. Keep this in mind when using it as a
* parameter of methods.
*
* **Note:** `ModelTextProxy` is a readonly interface. If you want to perform changes on model data represented by a `ModelTextProxy`
* use {@link module:engine/model/writer~ModelWriter model writer API}.
*
* **Note:** `ModelTextProxy` instances are created on the fly, basing on the current state of model. Because of this, it is
* highly unrecommended to store references to `ModelTextProxy` instances. `ModelTextProxy` instances are not refreshed when
* model changes, so they might get invalidated. Instead, consider creating {@link module:engine/model/liveposition~ModelLivePosition live
* position}.
*
* `ModelTextProxy` instances are created by {@link module:engine/model/treewalker~ModelTreeWalker model tree walker}.
* You should not need to create an instance of this class by your own.
*/
var ModelTextProxy = class extends ModelTypeCheckable {
	/**
	* Text node which part is represented by this text proxy.
	*/
	textNode;
	/**
	* Text data represented by this text proxy.
	*/
	data;
	/**
	* Offset in {@link module:engine/model/textproxy~ModelTextProxy#textNode text node} from which the text proxy starts.
	*/
	offsetInText;
	/**
	* Creates a text proxy.
	*
	* @internal
	* @param textNode Text node which part is represented by this text proxy.
	* @param offsetInText Offset in {@link module:engine/model/textproxy~ModelTextProxy#textNode text node} from which the text proxy
	* starts.
	* @param length Text proxy length, that is how many text node's characters, starting from `offsetInText` it represents.
	*/
	constructor(textNode, offsetInText, length) {
		super();
		this.textNode = textNode;
		if (offsetInText < 0 || offsetInText > textNode.offsetSize)
 /**
		* Given `offsetInText` value is incorrect.
		*
		* @error model-textproxy-wrong-offsetintext
		*/
		throw new CKEditorError("model-textproxy-wrong-offsetintext", this);
		if (length < 0 || offsetInText + length > textNode.offsetSize)
 /**
		* Given `length` value is incorrect.
		*
		* @error model-textproxy-wrong-length
		*/
		throw new CKEditorError("model-textproxy-wrong-length", this);
		this.data = textNode.data.substring(offsetInText, offsetInText + length);
		this.offsetInText = offsetInText;
	}
	/**
	* Offset at which this text proxy starts in it's parent.
	*
	* @see module:engine/model/node~ModelNode#startOffset
	*/
	get startOffset() {
		return this.textNode.startOffset !== null ? this.textNode.startOffset + this.offsetInText : null;
	}
	/**
	* Offset size of this text proxy. Equal to the number of characters represented by the text proxy.
	*
	* @see module:engine/model/node~ModelNode#offsetSize
	*/
	get offsetSize() {
		return this.data.length;
	}
	/**
	* Offset at which this text proxy ends in it's parent.
	*
	* @see module:engine/model/node~ModelNode#endOffset
	*/
	get endOffset() {
		return this.startOffset !== null ? this.startOffset + this.offsetSize : null;
	}
	/**
	* Flag indicating whether `ModelTextProxy` instance covers only part of the original
	* {@link module:engine/model/text~ModelText text node} (`true`) or the whole text node (`false`).
	*
	* This is `false` when text proxy starts at the very beginning of
	* {@link module:engine/model/textproxy~ModelTextProxy#textNode textNode}
	* ({@link module:engine/model/textproxy~ModelTextProxy#offsetInText offsetInText} equals `0`) and text proxy sizes is equal to
	* text node size.
	*/
	get isPartial() {
		return this.offsetSize !== this.textNode.offsetSize;
	}
	/**
	* Parent of this text proxy, which is same as parent of text node represented by this text proxy.
	*/
	get parent() {
		return this.textNode.parent;
	}
	/**
	* Root of this text proxy, which is same as root of text node represented by this text proxy.
	*/
	get root() {
		return this.textNode.root;
	}
	/**
	* Gets path to this text proxy.
	*
	* @see module:engine/model/node~ModelNode#getPath
	*/
	getPath() {
		const path = this.textNode.getPath();
		if (path.length > 0) path[path.length - 1] += this.offsetInText;
		return path;
	}
	/**
	* Returns ancestors array of this text proxy.
	*
	* @param options Options object.
	* @param options.includeSelf When set to `true` this text proxy will be also included in parent's array.
	* @param options.parentFirst When set to `true`, array will be sorted from text proxy parent to root element,
	* otherwise root element will be the first item in the array.
	* @returns Array with ancestors.
	*/
	getAncestors(options = {}) {
		const ancestors = [];
		let parent = options.includeSelf ? this : this.parent;
		while (parent) {
			ancestors[options.parentFirst ? "push" : "unshift"](parent);
			parent = parent.parent;
		}
		return ancestors;
	}
	/**
	* Checks if this text proxy has an attribute for given key.
	*
	* @param key Key of attribute to check.
	* @returns `true` if attribute with given key is set on text proxy, `false` otherwise.
	*/
	hasAttribute(key) {
		return this.textNode.hasAttribute(key);
	}
	/**
	* Gets an attribute value for given key or `undefined` if that attribute is not set on text proxy.
	*
	* @param key Key of attribute to look for.
	* @returns Attribute value or `undefined`.
	*/
	getAttribute(key) {
		return this.textNode.getAttribute(key);
	}
	/**
	* Returns iterator that iterates over this node's attributes. Attributes are returned as arrays containing two
	* items. First one is attribute key and second is attribute value.
	*
	* This format is accepted by native `Map` object and also can be passed in `Node` constructor.
	*/
	getAttributes() {
		return this.textNode.getAttributes();
	}
	/**
	* Returns iterator that iterates over this node's attribute keys.
	*/
	getAttributeKeys() {
		return this.textNode.getAttributeKeys();
	}
};
ModelTextProxy.prototype.is = function(type) {
	return type === "$textProxy" || type === "model:$textProxy" || type === "textProxy" || type === "model:textProxy";
};

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* Position iterator class. It allows to iterate forward and backward over the document.
*/
var ModelTreeWalker = class {
	/**
	* Walking direction. Defaults `'forward'`.
	*/
	direction;
	/**
	* Iterator boundaries.
	*
	* When the iterator is walking `'forward'` on the end of boundary or is walking `'backward'`
	* on the start of boundary, then `{ done: true }` is returned.
	*
	* If boundaries are not defined they are set before first and after last child of the root node.
	*/
	boundaries;
	/**
	* Flag indicating whether all consecutive characters with the same attributes should be
	* returned as one {@link module:engine/model/textproxy~ModelTextProxy} (`true`) or one by one (`false`).
	*/
	singleCharacters;
	/**
	* Flag indicating whether iterator should enter elements or not. If the iterator is shallow child nodes of any
	* iterated node will not be returned along with `elementEnd` tag.
	*/
	shallow;
	/**
	* Flag indicating whether iterator should ignore `elementEnd` tags. If the option is true walker will not
	* return a parent node of the start position. If this option is `true` each {@link module:engine/model/element~ModelElement} will
	* be returned once, while if the option is `false` they might be returned twice:
	* for `'elementStart'` and `'elementEnd'`.
	*/
	ignoreElementEnd;
	/**
	* Iterator position. This is always static position, even if the initial position was a
	* {@link module:engine/model/liveposition~ModelLivePosition live position}. If start position is not defined then position depends
	* on {@link #direction}. If direction is `'forward'` position starts form the beginning, when direction
	* is `'backward'` position starts from the end.
	*/
	_position;
	/**
	* Start boundary cached for optimization purposes.
	*/
	_boundaryStartParent;
	/**
	* End boundary cached for optimization purposes.
	*/
	_boundaryEndParent;
	/**
	* Parent of the most recently visited node. Cached for optimization purposes.
	*/
	_visitedParent;
	/**
	* Creates a range iterator. All parameters are optional, but you have to specify either `boundaries` or `startPosition`.
	*
	* @param options Object with configuration.
	*/
	constructor(options) {
		if (!options || !options.boundaries && !options.startPosition)
 /**
		* Neither boundaries nor starting position of a `TreeWalker` have been defined.
		*
		* @error model-tree-walker-no-start-position
		*/
		throw new CKEditorError("model-tree-walker-no-start-position", null);
		const direction = options.direction || "forward";
		if (direction != "forward" && direction != "backward")
 /**
		* Only `backward` and `forward` direction allowed.
		*
		* @error model-tree-walker-unknown-direction
		*/
		throw new CKEditorError("model-tree-walker-unknown-direction", options, { direction });
		this.direction = direction;
		this.boundaries = options.boundaries || null;
		if (options.startPosition) this._position = options.startPosition.clone();
		else this._position = ModelPosition._createAt(this.boundaries[this.direction == "backward" ? "end" : "start"]);
		this.position.stickiness = "toNone";
		this.singleCharacters = !!options.singleCharacters;
		this.shallow = !!options.shallow;
		this.ignoreElementEnd = !!options.ignoreElementEnd;
		this._boundaryStartParent = this.boundaries ? this.boundaries.start.parent : null;
		this._boundaryEndParent = this.boundaries ? this.boundaries.end.parent : null;
		this._visitedParent = this.position.parent;
	}
	/**
	* Iterable interface.
	*
	* @returns {Iterable.<module:engine/model/treewalker~ModelTreeWalkerValue>}
	*/
	[Symbol.iterator]() {
		return this;
	}
	/**
	* Iterator position. This is always static position, even if the initial position was a
	* {@link module:engine/model/liveposition~ModelLivePosition live position}. If start position is not defined then position depends
	* on {@link #direction}. If direction is `'forward'` position starts form the beginning, when direction
	* is `'backward'` position starts from the end.
	*/
	get position() {
		return this._position;
	}
	/**
	* Moves {@link #position} in the {@link #direction} skipping values as long as the callback function returns `true`.
	*
	* For example:
	*
	* ```ts
	* walker.skip( value => value.type == 'text' ); // <paragraph>[]foo</paragraph> -> <paragraph>foo[]</paragraph>
	* walker.skip( () => true ); // Move the position to the end: <paragraph>[]foo</paragraph> -> <paragraph>foo</paragraph>[]
	* walker.skip( () => false ); // Do not move the position.
	* ```
	*
	* @param skip Callback function. Gets {@link module:engine/model/treewalker~ModelTreeWalkerValue} and should
	* return `true` if the value should be skipped or `false` if not.
	*/
	skip(skip) {
		let done, value, prevPosition, prevVisitedParent;
		do {
			prevPosition = this.position;
			prevVisitedParent = this._visitedParent;
			({done, value} = this.next());
		} while (!done && skip(value));
		if (!done) {
			this._position = prevPosition;
			this._visitedParent = prevVisitedParent;
		}
	}
	/**
	* Moves tree walker {@link #position} to provided `position`. Tree walker will
	* continue traversing from that position.
	*
	* Note: in contrary to {@link ~ModelTreeWalker#skip}, this method does not iterate over the nodes along the way.
	* It simply sets the current tree walker position to a new one.
	* From the performance standpoint, it is better to use {@link ~ModelTreeWalker#jumpTo} rather than {@link ~ModelTreeWalker#skip}.
	*
	* If the provided position is before the start boundary, the position will be
	* set to the start boundary. If the provided position is after the end boundary,
	* the position will be set to the end boundary.
	* This is done to prevent the treewalker from traversing outside the boundaries.
	*
	* @param position Position to jump to.
	*/
	jumpTo(position) {
		if (this._boundaryStartParent && position.isBefore(this.boundaries.start)) position = this.boundaries.start;
		else if (this._boundaryEndParent && position.isAfter(this.boundaries.end)) position = this.boundaries.end;
		this._position = position.clone();
		this._visitedParent = position.parent;
	}
	/**
	* Gets the next tree walker's value.
	*/
	next() {
		if (this.direction == "forward") return this._next();
		else return this._previous();
	}
	/**
	* Makes a step forward in model. Moves the {@link #position} to the next position and returns the encountered value.
	*/
	_next() {
		const previousPosition = this.position;
		const position = this.position.clone();
		const parent = this._visitedParent;
		if (parent.parent === null && position.offset === parent.maxOffset) return {
			done: true,
			value: void 0
		};
		if (parent === this._boundaryEndParent && position.offset == this.boundaries.end.offset) return {
			done: true,
			value: void 0
		};
		const textNodeAtPosition = getTextNodeAtPosition(position, parent);
		const node = textNodeAtPosition || getNodeAfterPosition(position, parent, textNodeAtPosition);
		if (node && node.is("model:element")) {
			if (!this.shallow) {
				position.path.push(0);
				this._visitedParent = node;
			} else {
				if (this.boundaries && this.boundaries.end.isBefore(position)) return {
					done: true,
					value: void 0
				};
				position.offset++;
			}
			this._position = position;
			return formatReturnValue("elementStart", node, previousPosition, position, 1);
		}
		if (node && node.is("model:$text")) {
			let charactersCount;
			if (this.singleCharacters) charactersCount = 1;
			else {
				let offset = node.endOffset;
				if (this._boundaryEndParent == parent && this.boundaries.end.offset < offset) offset = this.boundaries.end.offset;
				charactersCount = offset - position.offset;
			}
			const item = new ModelTextProxy(node, position.offset - node.startOffset, charactersCount);
			position.offset += charactersCount;
			this._position = position;
			return formatReturnValue("text", item, previousPosition, position, charactersCount);
		}
		position.path.pop();
		position.offset++;
		this._position = position;
		this._visitedParent = parent.parent;
		if (this.ignoreElementEnd) return this._next();
		return formatReturnValue("elementEnd", parent, previousPosition, position);
	}
	/**
	* Makes a step backward in model. Moves the {@link #position} to the previous position and returns the encountered value.
	*/
	_previous() {
		const previousPosition = this.position;
		const position = this.position.clone();
		const parent = this._visitedParent;
		if (parent.parent === null && position.offset === 0) return {
			done: true,
			value: void 0
		};
		if (parent == this._boundaryStartParent && position.offset == this.boundaries.start.offset) return {
			done: true,
			value: void 0
		};
		const positionParent = position.parent;
		const textNodeAtPosition = getTextNodeAtPosition(position, positionParent);
		const node = textNodeAtPosition || getNodeBeforePosition(position, positionParent, textNodeAtPosition);
		if (node && node.is("model:element")) {
			position.offset--;
			if (this.shallow) {
				this._position = position;
				return formatReturnValue("elementStart", node, previousPosition, position, 1);
			}
			position.path.push(node.maxOffset);
			this._position = position;
			this._visitedParent = node;
			if (this.ignoreElementEnd) return this._previous();
			return formatReturnValue("elementEnd", node, previousPosition, position);
		}
		if (node && node.is("model:$text")) {
			let charactersCount;
			if (this.singleCharacters) charactersCount = 1;
			else {
				let offset = node.startOffset;
				if (this._boundaryStartParent == parent && this.boundaries.start.offset > offset) offset = this.boundaries.start.offset;
				charactersCount = position.offset - offset;
			}
			const item = new ModelTextProxy(node, position.offset - node.startOffset - charactersCount, charactersCount);
			position.offset -= charactersCount;
			this._position = position;
			return formatReturnValue("text", item, previousPosition, position, charactersCount);
		}
		position.path.pop();
		this._position = position;
		this._visitedParent = parent.parent;
		return formatReturnValue("elementStart", parent, previousPosition, position, 1);
	}
};
function formatReturnValue(type, item, previousPosition, nextPosition, length) {
	return {
		done: false,
		value: {
			type,
			item,
			previousPosition,
			nextPosition,
			length
		}
	};
}

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* @module engine/model/position
*/
/**
* Represents a position in the model tree.
*
* A position is represented by its {@link module:engine/model/position~ModelPosition#root} and
* a {@link module:engine/model/position~ModelPosition#path} in that root.
*
* You can create position instances via its constructor or the `createPosition*()` factory methods of
* {@link module:engine/model/model~Model} and {@link module:engine/model/writer~ModelWriter}.
*
* **Note:** Position is based on offsets, not indexes. This means that a position between two text nodes
* `foo` and `bar` has offset `3`, not `1`. See {@link module:engine/model/position~ModelPosition#path} for more information.
*
* Since a position in the model is represented by a {@link module:engine/model/position~ModelPosition#root position root} and
* {@link module:engine/model/position~ModelPosition#path position path} it is possible to create positions placed in non-existing places.
* This requirement is important for operational transformation algorithms.
*
* Also, {@link module:engine/model/operation/operation~Operation operations}
* kept in the {@link module:engine/model/document~ModelDocument#history document history}
* are storing positions (and ranges) which were correct when those operations were applied, but may not be correct
* after the document has changed.
*
* When changes are applied to the model, it may also happen that {@link module:engine/model/position~ModelPosition#parent position parent}
* will change even if position path has not changed. Keep in mind, that if a position leads to non-existing element,
* {@link module:engine/model/position~ModelPosition#parent} and some other properties and methods will throw errors.
*
* In most cases, position with wrong path is caused by an error in code, but it is sometimes needed, as described above.
*/
var ModelPosition = class ModelPosition extends ModelTypeCheckable {
	/**
	* Root of the position path.
	*/
	root;
	/**
	* Position of the node in the tree. **Path contains offsets, not indexes.**
	*
	* Position can be placed before, after or in a {@link module:engine/model/node~ModelNode node} if that node has
	* {@link module:engine/model/node~ModelNode#offsetSize} greater than `1`. Items in position path are
	* {@link module:engine/model/node~ModelNode#startOffset starting offsets} of position ancestors, starting from direct root children,
	* down to the position offset in it's parent.
	*
	* ```
	* ROOT
	*  |- P            before: [ 0 ]         after: [ 1 ]
	*  |- UL           before: [ 1 ]         after: [ 2 ]
	*     |- LI        before: [ 1, 0 ]      after: [ 1, 1 ]
	*     |  |- foo    before: [ 1, 0, 0 ]   after: [ 1, 0, 3 ]
	*     |- LI        before: [ 1, 1 ]      after: [ 1, 2 ]
	*        |- bar    before: [ 1, 1, 0 ]   after: [ 1, 1, 3 ]
	* ```
	*
	* `foo` and `bar` are representing {@link module:engine/model/text~ModelText text nodes}. Since text nodes has offset size
	* greater than `1` you can place position offset between their start and end:
	*
	* ```
	* ROOT
	*  |- P
	*  |- UL
	*     |- LI
	*     |  |- f^o|o  ^ has path: [ 1, 0, 1 ]   | has path: [ 1, 0, 2 ]
	*     |- LI
	*        |- b^a|r  ^ has path: [ 1, 1, 1 ]   | has path: [ 1, 1, 2 ]
	* ```
	*/
	path;
	/**
	* Position stickiness. See {@link module:engine/model/position~ModelPositionStickiness}.
	*/
	stickiness;
	/**
	* Creates a position.
	*
	* @param root Root of the position.
	* @param path Position path. See {@link module:engine/model/position~ModelPosition#path}.
	* @param stickiness Position stickiness. See {@link module:engine/model/position~ModelPositionStickiness}.
	*/
	constructor(root, path, stickiness = "toNone") {
		super();
		if (!root.is("element") && !root.is("documentFragment"))
 /**
		* Position root is invalid.
		*
		* Positions can only be anchored in elements or document fragments.
		*
		* @error model-position-root-invalid
		*/
		throw new CKEditorError("model-position-root-invalid", root);
		if (!Array.isArray(path) || path.length === 0)
 /**
		* Position path must be an array with at least one item.
		*
		* @error model-position-path-incorrect-format
		* @param {Array.<number>} path A path to the position.
		*/
		throw new CKEditorError("model-position-path-incorrect-format", root, { path });
		if (root.is("rootElement")) path = path.slice();
		else {
			path = [...root.getPath(), ...path];
			root = root.root;
		}
		this.root = root;
		this.path = path;
		this.stickiness = stickiness;
	}
	/**
	* Offset at which this position is located in its {@link module:engine/model/position~ModelPosition#parent parent}. It is equal
	* to the last item in position {@link module:engine/model/position~ModelPosition#path path}.
	*
	* @type {Number}
	*/
	get offset() {
		return this.path[this.path.length - 1];
	}
	set offset(newOffset) {
		this.path[this.path.length - 1] = newOffset;
	}
	/**
	* Parent element of this position.
	*
	* Keep in mind that `parent` value is calculated when the property is accessed.
	* If {@link module:engine/model/position~ModelPosition#path position path}
	* leads to a non-existing element, `parent` property will throw error.
	*
	* Also it is a good idea to cache `parent` property if it is used frequently in an algorithm (i.e. in a long loop).
	*/
	get parent() {
		let parent = this.root;
		for (let i = 0; i < this.path.length - 1; i++) {
			parent = parent.getChildAtOffset(this.path[i]);
			if (!parent)
 /**
			* The position's path is incorrect. This means that a position does not point to
			* a correct place in the tree and hence, some of its methods and getters cannot work correctly.
			*
			* **Note**: Unlike DOM and view positions, in the model, the
			* {@link module:engine/model/position~ModelPosition#parent position's parent} is always an element or a document fragment.
			* The last offset in the
			* {@link module:engine/model/position~ModelPosition#path position's path} is the point in this element
			* where this position points.
			*
			* Read more about model positions and offsets in
			* the {@glink framework/architecture/editing-engine#indexes-and-offsets Editing engine architecture} guide.
			*
			* @error model-position-path-incorrect
			* @param {module:engine/model/position~ModelPosition} position The incorrect position.
			*/
			throw new CKEditorError("model-position-path-incorrect", this, { position: this });
		}
		if (parent.is("$text")) throw new CKEditorError("model-position-path-incorrect", this, { position: this });
		return parent;
	}
	/**
	* Position {@link module:engine/model/position~ModelPosition#offset offset} converted to an index in position's parent node. It is
	* equal to the {@link module:engine/model/node~ModelNode#index index} of a node after this position. If position is placed
	* in text node, position index is equal to the index of that text node.
	*/
	get index() {
		return this.parent.offsetToIndex(this.offset);
	}
	/**
	* Returns {@link module:engine/model/text~ModelText text node} instance in which this position is placed or `null` if this
	* position is not in a text node.
	*/
	get textNode() {
		return getTextNodeAtPosition(this, this.parent);
	}
	/**
	* Node directly after this position. Returns `null` if this position is at the end of its parent, or if it is in a text node.
	*/
	get nodeAfter() {
		const parent = this.parent;
		return getNodeAfterPosition(this, parent, getTextNodeAtPosition(this, parent));
	}
	/**
	* Node directly before this position. Returns `null` if this position is at the start of its parent, or if it is in a text node.
	*/
	get nodeBefore() {
		const parent = this.parent;
		return getNodeBeforePosition(this, parent, getTextNodeAtPosition(this, parent));
	}
	/**
	* Is `true` if position is at the beginning of its {@link module:engine/model/position~ModelPosition#parent parent}, `false` otherwise.
	*/
	get isAtStart() {
		return this.offset === 0;
	}
	/**
	* Is `true` if position is at the end of its {@link module:engine/model/position~ModelPosition#parent parent}, `false` otherwise.
	*/
	get isAtEnd() {
		return this.offset == this.parent.maxOffset;
	}
	/**
	* Checks whether the position is valid in current model tree, that is whether it points to an existing place in the model.
	*/
	isValid() {
		if (this.offset < 0) return false;
		let parent = this.root;
		for (let i = 0; i < this.path.length - 1; i++) {
			parent = parent.getChildAtOffset(this.path[i]);
			if (!parent) return false;
		}
		return this.offset <= parent.maxOffset;
	}
	/**
	* Checks whether this position is before or after given position.
	*
	* This method is safe to use it on non-existing positions (for example during operational transformation).
	*/
	compareWith(otherPosition) {
		if (this.root != otherPosition.root) return "different";
		const result = compareArrays(this.path, otherPosition.path);
		switch (result) {
			case "same": return "same";
			case "prefix": return "before";
			case "extension": return "after";
			default: return this.path[result] < otherPosition.path[result] ? "before" : "after";
		}
	}
	/**
	* Gets the farthest position which matches the callback using
	* {@link module:engine/model/treewalker~ModelTreeWalker TreeWalker}.
	*
	* For example:
	*
	* ```ts
	* getLastMatchingPosition( value => value.type == 'text' );
	* // <paragraph>[]foo</paragraph> -> <paragraph>foo[]</paragraph>
	*
	* getLastMatchingPosition( value => value.type == 'text', { direction: 'backward' } );
	* // <paragraph>foo[]</paragraph> -> <paragraph>[]foo</paragraph>
	*
	* getLastMatchingPosition( value => false );
	* // Do not move the position.
	* ```
	*
	* @param skip Callback function. Gets {@link module:engine/model/treewalker~ModelTreeWalkerValue} and should
	* return `true` if the value should be skipped or `false` if not.
	* @param options Object with configuration options. See {@link module:engine/model/treewalker~ModelTreeWalker}.
	*
	* @returns The position after the last item which matches the `skip` callback test.
	*/
	getLastMatchingPosition(skip, options = {}) {
		options.startPosition = this;
		const treeWalker = new ModelTreeWalker(options);
		treeWalker.skip(skip);
		return treeWalker.position;
	}
	/**
	* Returns a path to this position's parent. Parent path is equal to position
	* {@link module:engine/model/position~ModelPosition#path path} but without the last item.
	*
	* This method is safe to use it on non-existing positions (for example during operational transformation).
	*
	* @returns Path to the parent.
	*/
	getParentPath() {
		return this.path.slice(0, -1);
	}
	/**
	* Returns ancestors array of this position, that is this position's parent and its ancestors.
	*
	* @returns Array with ancestors.
	*/
	getAncestors() {
		const parent = this.parent;
		if (parent.is("documentFragment")) return [parent];
		else return parent.getAncestors({ includeSelf: true });
	}
	/**
	* Returns the parent element of the given name. Returns null if the position is not inside the desired parent.
	*
	* @param parentName The name of the parent element to find.
	*/
	findAncestor(parentName) {
		const parent = this.parent;
		if (parent.is("element")) return parent.findAncestor(parentName, { includeSelf: true });
		return null;
	}
	/**
	* Returns the slice of two position {@link #path paths} which is identical. The {@link #root roots}
	* of these two paths must be identical.
	*
	* This method is safe to use it on non-existing positions (for example during operational transformation).
	*
	* @param position The second position.
	* @returns The common path.
	*/
	getCommonPath(position) {
		if (this.root != position.root) return [];
		const cmp = compareArrays(this.path, position.path);
		const diffAt = typeof cmp == "string" ? Math.min(this.path.length, position.path.length) : cmp;
		return this.path.slice(0, diffAt);
	}
	/**
	* Returns an {@link module:engine/model/element~ModelElement} or {@link module:engine/model/documentfragment~ModelDocumentFragment}
	* which is a common ancestor of both positions. The {@link #root roots} of these two positions must be identical.
	*
	* @param position The second position.
	*/
	getCommonAncestor(position) {
		const ancestorsA = this.getAncestors();
		const ancestorsB = position.getAncestors();
		let i = 0;
		while (ancestorsA[i] == ancestorsB[i] && ancestorsA[i]) i++;
		return i === 0 ? null : ancestorsA[i - 1];
	}
	/**
	* Returns a new instance of `Position`, that has same {@link #parent parent} but it's offset
	* is shifted by `shift` value (can be a negative value).
	*
	* This method is safe to use it on non-existing positions (for example during operational transformation).
	*
	* @param shift Offset shift. Can be a negative value.
	* @returns Shifted position.
	*/
	getShiftedBy(shift) {
		const shifted = this.clone();
		const offset = shifted.offset + shift;
		shifted.offset = offset < 0 ? 0 : offset;
		return shifted;
	}
	/**
	* Checks whether this position is after given position.
	*
	* This method is safe to use it on non-existing positions (for example during operational transformation).
	*
	* @see module:engine/model/position~ModelPosition#isBefore
	* @param  otherPosition Position to compare with.
	* @returns True if this position is after given position.
	*/
	isAfter(otherPosition) {
		return this.compareWith(otherPosition) == "after";
	}
	/**
	* Checks whether this position is before given position.
	*
	* **Note:** watch out when using negation of the value returned by this method, because the negation will also
	* be `true` if positions are in different roots and you might not expect this. You should probably use
	* `a.isAfter( b ) || a.isEqual( b )` or `!a.isBefore( p ) && a.root == b.root` in most scenarios. If your
	* condition uses multiple `isAfter` and `isBefore` checks, build them so they do not use negated values, i.e.:
	*
	* ```ts
	* if ( a.isBefore( b ) && c.isAfter( d ) ) {
	* 	// do A.
	* } else {
	* 	// do B.
	* }
	* ```
	*
	* or, if you have only one if-branch:
	*
	* ```ts
	* if ( !( a.isBefore( b ) && c.isAfter( d ) ) {
	* 	// do B.
	* }
	* ```
	*
	* rather than:
	*
	* ```ts
	* if ( !a.isBefore( b ) || && !c.isAfter( d ) ) {
	* 	// do B.
	* } else {
	* 	// do A.
	* }
	* ```
	*
	* This method is safe to use it on non-existing positions (for example during operational transformation).
	*
	* @param otherPosition Position to compare with.
	* @returns True if this position is before given position.
	*/
	isBefore(otherPosition) {
		return this.compareWith(otherPosition) == "before";
	}
	/**
	* Checks whether this position is equal to given position.
	*
	* This method is safe to use it on non-existing positions (for example during operational transformation).
	*
	* @param otherPosition Position to compare with.
	* @returns True if positions are same.
	*/
	isEqual(otherPosition) {
		return this.compareWith(otherPosition) == "same";
	}
	/**
	* Checks whether this position is touching given position. Positions touch when there are no text nodes
	* or empty nodes in a range between them. Technically, those positions are not equal but in many cases
	* they are very similar or even indistinguishable.
	*
	* @param otherPosition Position to compare with.
	* @returns True if positions touch.
	*/
	isTouching(otherPosition) {
		if (this.root !== otherPosition.root) return false;
		const commonLevel = Math.min(this.path.length, otherPosition.path.length);
		for (let level = 0; level < commonLevel; level++) {
			const diff = this.path[level] - otherPosition.path[level];
			if (diff < -1 || diff > 1) return false;
			else if (diff === 1) return checkTouchingBranch(otherPosition, this, level);
			else if (diff === -1) return checkTouchingBranch(this, otherPosition, level);
		}
		if (this.path.length === otherPosition.path.length) return true;
		else if (this.path.length > otherPosition.path.length) return checkOnlyZeroes(this.path, commonLevel);
		else return checkOnlyZeroes(otherPosition.path, commonLevel);
	}
	/**
	* Checks if two positions are in the same parent.
	*
	* This method is safe to use it on non-existing positions (for example during operational transformation).
	*
	* @param position Position to compare with.
	* @returns `true` if positions have the same parent, `false` otherwise.
	*/
	hasSameParentAs(position) {
		if (this.root !== position.root) return false;
		return compareArrays(this.getParentPath(), position.getParentPath()) == "same";
	}
	/**
	* Returns a copy of this position that is transformed by given `operation`.
	*
	* The new position's parameters are updated accordingly to the effect of the `operation`.
	*
	* For example, if `n` nodes are inserted before the position, the returned position {@link ~ModelPosition#offset} will be
	* increased by `n`. If the position was in a merged element, it will be accordingly moved to the new element, etc.
	*
	* This method is safe to use it on non-existing positions (for example during operational transformation).
	*
	* @param operation Operation to transform by.
	* @returns Transformed position.
	*/
	getTransformedByOperation(operation) {
		let result;
		switch (operation.type) {
			case "insert":
				result = this._getTransformedByInsertOperation(operation);
				break;
			case "move":
			case "remove":
			case "reinsert":
				result = this._getTransformedByMoveOperation(operation);
				break;
			case "split":
				result = this._getTransformedBySplitOperation(operation);
				break;
			case "merge":
				result = this._getTransformedByMergeOperation(operation);
				break;
			default:
				result = ModelPosition._createAt(this);
				break;
		}
		return result;
	}
	/**
	* Returns a copy of this position transformed by an insert operation.
	*
	* @internal
	*/
	_getTransformedByInsertOperation(operation) {
		return this._getTransformedByInsertion(operation.position, operation.howMany);
	}
	/**
	* Returns a copy of this position transformed by a move operation.
	*
	* @internal
	*/
	_getTransformedByMoveOperation(operation) {
		return this._getTransformedByMove(operation.sourcePosition, operation.targetPosition, operation.howMany);
	}
	/**
	* Returns a copy of this position transformed by a split operation.
	*
	* @internal
	*/
	_getTransformedBySplitOperation(operation) {
		const movedRange = operation.movedRange;
		if (movedRange.containsPosition(this) || movedRange.start.isEqual(this) && this.stickiness == "toNext") return this._getCombined(operation.splitPosition, operation.moveTargetPosition);
		else if (operation.graveyardPosition) return this._getTransformedByMove(operation.graveyardPosition, operation.insertionPosition, 1);
		else return this._getTransformedByInsertion(operation.insertionPosition, 1);
	}
	/**
	* Returns a copy of this position transformed by merge operation.
	*
	* @internal
	*/
	_getTransformedByMergeOperation(operation) {
		const movedRange = operation.movedRange;
		const isContained = movedRange.containsPosition(this) || movedRange.start.isEqual(this);
		let pos;
		if (isContained) {
			pos = this._getCombined(operation.sourcePosition, operation.targetPosition);
			if (operation.sourcePosition.isBefore(operation.targetPosition)) pos = pos._getTransformedByDeletion(operation.deletionPosition, 1);
		} else if (this.isEqual(operation.deletionPosition)) pos = ModelPosition._createAt(operation.deletionPosition);
		else pos = this._getTransformedByMove(operation.deletionPosition, operation.graveyardPosition, 1);
		return pos;
	}
	/**
	* Returns a copy of this position that is updated by removing `howMany` nodes starting from `deletePosition`.
	* It may happen that this position is in a removed node. If that is the case, `null` is returned instead.
	*
	* @internal
	* @param deletePosition Position before the first removed node.
	* @param howMany How many nodes are removed.
	* @returns Transformed position or `null`.
	*/
	_getTransformedByDeletion(deletePosition, howMany) {
		const transformed = ModelPosition._createAt(this);
		if (this.root != deletePosition.root) return transformed;
		if (compareArrays(deletePosition.getParentPath(), this.getParentPath()) == "same") {
			if (deletePosition.offset < this.offset) if (deletePosition.offset + howMany > this.offset) return null;
			else transformed.offset -= howMany;
		} else if (compareArrays(deletePosition.getParentPath(), this.getParentPath()) == "prefix") {
			const i = deletePosition.path.length - 1;
			if (deletePosition.offset <= this.path[i]) if (deletePosition.offset + howMany > this.path[i]) return null;
			else transformed.path[i] -= howMany;
		}
		return transformed;
	}
	/**
	* Returns a copy of this position that is updated by inserting `howMany` nodes at `insertPosition`.
	*
	* @internal
	* @param insertPosition Position where nodes are inserted.
	* @param howMany How many nodes are inserted.
	* @returns Transformed position.
	*/
	_getTransformedByInsertion(insertPosition, howMany) {
		const transformed = ModelPosition._createAt(this);
		if (this.root != insertPosition.root) return transformed;
		if (compareArrays(insertPosition.getParentPath(), this.getParentPath()) == "same") {
			if (insertPosition.offset < this.offset || insertPosition.offset == this.offset && this.stickiness != "toPrevious") transformed.offset += howMany;
		} else if (compareArrays(insertPosition.getParentPath(), this.getParentPath()) == "prefix") {
			const i = insertPosition.path.length - 1;
			if (insertPosition.offset <= this.path[i]) transformed.path[i] += howMany;
		}
		return transformed;
	}
	/**
	* Returns a copy of this position that is updated by moving `howMany` nodes from `sourcePosition` to `targetPosition`.
	*
	* @internal
	* @param sourcePosition Position before the first element to move.
	* @param targetPosition Position where moved elements will be inserted.
	* @param howMany How many consecutive nodes to move, starting from `sourcePosition`.
	* @returns Transformed position.
	*/
	_getTransformedByMove(sourcePosition, targetPosition, howMany) {
		targetPosition = targetPosition._getTransformedByDeletion(sourcePosition, howMany);
		if (sourcePosition.isEqual(targetPosition)) return ModelPosition._createAt(this);
		const transformed = this._getTransformedByDeletion(sourcePosition, howMany);
		if (transformed === null || sourcePosition.isEqual(this) && this.stickiness == "toNext" || sourcePosition.getShiftedBy(howMany).isEqual(this) && this.stickiness == "toPrevious") return this._getCombined(sourcePosition, targetPosition);
		else return transformed._getTransformedByInsertion(targetPosition, howMany);
	}
	/**
	* Returns a new position that is a combination of this position and given positions.
	*
	* The combined position is a copy of this position transformed by moving a range starting at `source` position
	* to the `target` position. It is expected that this position is inside the moved range.
	*
	* Example:
	*
	* ```ts
	* let original = model.createPositionFromPath( root, [ 2, 3, 1 ] );
	* let source = model.createPositionFromPath( root, [ 2, 2 ] );
	* let target = model.createPositionFromPath( otherRoot, [ 1, 1, 3 ] );
	* original._getCombined( source, target ); // path is [ 1, 1, 4, 1 ], root is `otherRoot`
	* ```
	*
	* Explanation:
	*
	* We have a position `[ 2, 3, 1 ]` and move some nodes from `[ 2, 2 ]` to `[ 1, 1, 3 ]`. The original position
	* was inside moved nodes and now should point to the new place. The moved nodes will be after
	* positions `[ 1, 1, 3 ]`, `[ 1, 1, 4 ]`, `[ 1, 1, 5 ]`. Since our position was in the second moved node,
	* the transformed position will be in a sub-tree of a node at `[ 1, 1, 4 ]`. Looking at original path, we
	* took care of `[ 2, 3 ]` part of it. Now we have to add the rest of the original path to the transformed path.
	* Finally, the transformed position will point to `[ 1, 1, 4, 1 ]`.
	*
	* @internal
	* @param source Beginning of the moved range.
	* @param target Position where the range is moved.
	* @returns Combined position.
	*/
	_getCombined(source, target) {
		const i = source.path.length - 1;
		const combined = ModelPosition._createAt(target);
		combined.stickiness = this.stickiness;
		combined.offset = combined.offset + this.path[i] - source.offset;
		combined.path = [...combined.path, ...this.path.slice(i + 1)];
		return combined;
	}
	/**
	* @inheritDoc
	*/
	toJSON() {
		return {
			root: this.root.toJSON(),
			path: Array.from(this.path),
			stickiness: this.stickiness
		};
	}
	/**
	* Returns a new position that is equal to current position.
	*/
	clone() {
		return new this.constructor(this.root, this.path, this.stickiness);
	}
	/**
	* Creates position at the given location. The location can be specified as:
	*
	* * a {@link module:engine/model/position~ModelPosition position},
	* * parent element and offset (offset defaults to `0`),
	* * parent element and `'end'` (sets position at the end of that element),
	* * {@link module:engine/model/item~ModelItem model item} and `'before'` or `'after'` (sets position before or after given model item).
	*
	* This method is a shortcut to other factory methods such as:
	*
	* * {@link module:engine/model/position~ModelPosition._createBefore},
	* * {@link module:engine/model/position~ModelPosition._createAfter}.
	*
	* @internal
	* @param offset Offset or one of the flags. Used only when the first parameter
	* is a {@link module:engine/model/item~ModelItem model item}.
	* @param stickiness Position stickiness. Used only when the first parameter is a {@link module:engine/model/item~ModelItem model item}.
	*/
	static _createAt(itemOrPosition, offset, stickiness = "toNone") {
		if (itemOrPosition.is("model:position")) return new ModelPosition(itemOrPosition.root, itemOrPosition.path, itemOrPosition.stickiness);
		else {
			const node = itemOrPosition;
			if (offset == "end") offset = node.maxOffset;
			else if (offset == "before") return this._createBefore(node, stickiness);
			else if (offset == "after") return this._createAfter(node, stickiness);
			else if (offset !== 0 && !offset)
 /**
			* {@link module:engine/model/model~Model#createPositionAt `Model#createPositionAt()`}
			* requires the offset to be specified when the first parameter is a model item.
			*
			* @error model-createpositionat-offset-required
			*/
			throw new CKEditorError("model-createpositionat-offset-required", [this, itemOrPosition]);
			if (!node.is("element") && !node.is("documentFragment"))
 /**
			* Position parent have to be a model element or model document fragment.
			*
			* @error model-position-parent-incorrect
			*/
			throw new CKEditorError("model-position-parent-incorrect", [this, itemOrPosition]);
			const path = node.getPath();
			path.push(offset);
			return new this(node.root, path, stickiness);
		}
	}
	/**
	* Creates a new position, after given {@link module:engine/model/item~ModelItem model item}.
	*
	* @internal
	* @param item Item after which the position should be placed.
	* @param stickiness Position stickiness.
	*/
	static _createAfter(item, stickiness) {
		if (!item.parent)
 /**
		* You cannot make a position after a root element.
		*
		* @error model-position-after-root
		* @param {module:engine/model/rootelement~ModelRootElement} root The root element..
		*/
		throw new CKEditorError("model-position-after-root", [this, item], { root: item });
		return this._createAt(item.parent, item.endOffset, stickiness);
	}
	/**
	* Creates a new position, before the given {@link module:engine/model/item~ModelItem model item}.
	*
	* @internal
	* @param item Item before which the position should be placed.
	* @param stickiness Position stickiness.
	*/
	static _createBefore(item, stickiness) {
		if (!item.parent)
 /**
		* You cannot make a position before a root element.
		*
		* @error model-position-before-root
		* @param {module:engine/model/rootelement~ModelRootElement} root The root element..
		*/
		throw new CKEditorError("model-position-before-root", item, { root: item });
		return this._createAt(item.parent, item.startOffset, stickiness);
	}
	/**
	* Creates a `Position` instance from given plain object (i.e. parsed JSON string).
	*
	* @param json Plain object to be converted to `Position`.
	* @param doc Document object that will be position owner.
	* @returns `Position` instance created using given plain object.
	*/
	static fromJSON(json, doc) {
		if (json.root === "$graveyard") {
			const pos = new ModelPosition(doc.graveyard, json.path);
			pos.stickiness = json.stickiness;
			return pos;
		}
		if (!doc.getRoot(json.root))
 /**
		* Cannot create position for document. Root with specified name does not exist.
		*
		* @error model-position-fromjson-no-root
		* @param {string} rootName The root name.
		*/
		throw new CKEditorError("model-position-fromjson-no-root", doc, { rootName: json.root });
		return new ModelPosition(doc.getRoot(json.root), json.path, json.stickiness);
	}
};
ModelPosition.prototype.is = function(type) {
	return type === "position" || type === "model:position";
};
/**
* Returns a text node at the given position.
*
* This is a helper function optimized to reuse the position parent instance for performance reasons.
*
* Normally, you should use {@link module:engine/model/position~ModelPosition#textNode `Position#textNode`}.
* If you start hitting performance issues with {@link module:engine/model/position~ModelPosition#parent `Position#parent`}
* check if your algorithm does not access it multiple times (which can happen directly or indirectly via other position properties).
*
* See https://github.com/ckeditor/ckeditor5/issues/6579.
*
* See also:
*
* * {@link module:engine/model/position~getNodeAfterPosition}
* * {@link module:engine/model/position~getNodeBeforePosition}
*
* @param position
* @param positionParent The parent of the given position.
* @internal
*/
function getTextNodeAtPosition(position, positionParent) {
	const node = positionParent.getChildAtOffset(position.offset);
	if (node && node.is("$text") && node.startOffset < position.offset) return node;
	return null;
}
/**
* Returns the node after the given position.
*
* This is a helper function optimized to reuse the position parent instance and the calculation of the text node at the
* specific position for performance reasons.
*
* Normally, you should use {@link module:engine/model/position~ModelPosition#nodeAfter `Position#nodeAfter`}.
* If you start hitting performance issues with {@link module:engine/model/position~ModelPosition#parent `Position#parent`} and/or
* {@link module:engine/model/position~ModelPosition#textNode `Position#textNode`}
* check if your algorithm does not access those properties multiple times
* (which can happen directly or indirectly via other position properties).
*
* See https://github.com/ckeditor/ckeditor5/issues/6579 and https://github.com/ckeditor/ckeditor5/issues/6582.
*
* See also:
*
* * {@link module:engine/model/position~getTextNodeAtPosition}
* * {@link module:engine/model/position~getNodeBeforePosition}
*
* @param position Position to check.
* @param positionParent The parent of the given position.
* @param textNode Text node at the given position.
* @internal
*/
function getNodeAfterPosition(position, positionParent, textNode) {
	if (textNode !== null) return null;
	return positionParent.getChildAtOffset(position.offset);
}
/**
* Returns the node before the given position.
*
* Refer to {@link module:engine/model/position~getNodeBeforePosition} for documentation on when to use this util method.
*
* See also:
*
* * {@link module:engine/model/position~getTextNodeAtPosition}
* * {@link module:engine/model/position~getNodeAfterPosition}
*
* @param position Position to check.
* @param positionParent The parent of the given position.
* @param textNode Text node at the given position.
* @internal
*/
function getNodeBeforePosition(position, positionParent, textNode) {
	if (textNode !== null) return null;
	return positionParent.getChild(positionParent.offsetToIndex(position.offset) - 1);
}
/**
* This is a helper function for `Position#isTouching()`.
*
* It checks whether to given positions are touching, considering that they have the same root and paths
* until given level, and at given level they differ by 1 (so they are branching at `level` point).
*
* The exact requirements for touching positions are described in `Position#isTouching()` and also
* in the body of this function.
*
* @param left Position "on the left" (it is before `right`).
* @param right Position "on the right" (it is after `left`).
* @param level Level on which the positions are different.
*/
function checkTouchingBranch(left, right, level) {
	if (level + 1 === left.path.length) return false;
	if (!checkOnlyZeroes(right.path, level + 1)) return false;
	if (!checkOnlyMaxOffset(left, level + 1)) return false;
	return true;
}
/**
* Checks whether for given array, starting from given index until the end of the array, all items are `0`s.
*
* This is a helper function for `Position#isTouching()`.
*/
function checkOnlyZeroes(arr, idx) {
	while (idx < arr.length) {
		if (arr[idx] !== 0) return false;
		idx++;
	}
	return true;
}
/**
* Checks whether for given position, starting from given path level, whether the position is at the end of
* its parent and whether each element on the path to the position is also at at the end of its parent.
*
* This is a helper function for `Position#isTouching()`.
*/
function checkOnlyMaxOffset(pos, level) {
	let parent = pos.parent;
	let idx = pos.path.length - 1;
	let add = 0;
	while (idx >= level) {
		if (pos.path[idx] + add !== parent.maxOffset) return false;
		add = 1;
		idx--;
		parent = parent.parent;
	}
	return true;
}

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* @module engine/model/range
*/
/**
* Represents a range in the model tree.
*
* A range is defined by its {@link module:engine/model/range~ModelRange#start} and {@link module:engine/model/range~ModelRange#end}
* positions.
*
* You can create range instances via its constructor or the `createRange*()` factory methods of
* {@link module:engine/model/model~Model} and {@link module:engine/model/writer~ModelWriter}.
*/
var ModelRange = class ModelRange extends ModelTypeCheckable {
	/**
	* Start position.
	*/
	start;
	/**
	* End position.
	*/
	end;
	/**
	* Creates a range spanning from `start` position to `end` position.
	*
	* @param start The start position.
	* @param end The end position. If not set, the range will be collapsed at the `start` position.
	*/
	constructor(start, end) {
		super();
		this.start = ModelPosition._createAt(start);
		this.end = end ? ModelPosition._createAt(end) : ModelPosition._createAt(start);
		this.start.stickiness = this.isCollapsed ? "toNone" : "toNext";
		this.end.stickiness = this.isCollapsed ? "toNone" : "toPrevious";
	}
	/**
	* Iterable interface.
	*
	* Iterates over all {@link module:engine/model/item~ModelItem items} that are in this range and returns
	* them together with additional information like length or {@link module:engine/model/position~ModelPosition positions},
	* grouped as {@link module:engine/model/treewalker~ModelTreeWalkerValue}.
	* It iterates over all {@link module:engine/model/textproxy~ModelTextProxy text contents} that are inside the range
	* and all the {@link module:engine/model/element~ModelElement}s that are entered into when iterating over this range.
	*
	* This iterator uses {@link module:engine/model/treewalker~ModelTreeWalker} with `boundaries` set to this range
	* and `ignoreElementEnd` option set to `true`.
	*/
	*[Symbol.iterator]() {
		yield* new ModelTreeWalker({
			boundaries: this,
			ignoreElementEnd: true
		});
	}
	/**
	* Describes whether the range is collapsed, that is if {@link #start} and
	* {@link #end} positions are equal.
	*/
	get isCollapsed() {
		return this.start.isEqual(this.end);
	}
	/**
	* Describes whether this range is flat, that is if {@link #start} position and
	* {@link #end} position are in the same {@link module:engine/model/position~ModelPosition#parent}.
	*/
	get isFlat() {
		return compareArrays(this.start.getParentPath(), this.end.getParentPath()) == "same";
	}
	/**
	* Range root element.
	*/
	get root() {
		return this.start.root;
	}
	/**
	* Checks whether this range contains given {@link module:engine/model/position~ModelPosition position}.
	*
	* @param position Position to check.
	* @returns `true` if given {@link module:engine/model/position~ModelPosition position} is contained
	* in this range,`false` otherwise.
	*/
	containsPosition(position) {
		return position.isAfter(this.start) && position.isBefore(this.end);
	}
	/**
	* Checks whether this range contains given {@link module:engine/model/range~ModelRange range}.
	*
	* @param otherRange Range to check.
	* @param loose Whether the check is loose or strict. If the check is strict (`false`), compared range cannot
	* start or end at the same position as this range boundaries. If the check is loose (`true`), compared range can start, end or
	* even be equal to this range. Note that collapsed ranges are always compared in strict mode.
	* @returns {Boolean} `true` if given {@link module:engine/model/range~ModelRange range} boundaries are contained by this range,
	* `false` otherwise.
	*/
	containsRange(otherRange, loose = false) {
		if (otherRange.isCollapsed) loose = false;
		const containsStart = this.containsPosition(otherRange.start) || loose && this.start.isEqual(otherRange.start);
		const containsEnd = this.containsPosition(otherRange.end) || loose && this.end.isEqual(otherRange.end);
		return containsStart && containsEnd;
	}
	/**
	* Checks whether given {@link module:engine/model/item~ModelItem} is inside this range.
	*/
	containsItem(item) {
		const pos = ModelPosition._createBefore(item);
		return this.containsPosition(pos) || this.start.isEqual(pos);
	}
	/**
	* Two ranges are equal if their {@link #start} and {@link #end} positions are equal.
	*
	* @param otherRange Range to compare with.
	* @returns `true` if ranges are equal, `false` otherwise.
	*/
	isEqual(otherRange) {
		return this.start.isEqual(otherRange.start) && this.end.isEqual(otherRange.end);
	}
	/**
	* Checks and returns whether this range intersects with given range.
	*
	* @param otherRange Range to compare with.
	* @returns `true` if ranges intersect, `false` otherwise.
	*/
	isIntersecting(otherRange) {
		return this.start.isBefore(otherRange.end) && this.end.isAfter(otherRange.start);
	}
	/**
	* Computes which part(s) of this {@link module:engine/model/range~ModelRange range} is not a part of given
	* {@link module:engine/model/range~ModelRange range}.
	* Returned array contains zero, one or two {@link module:engine/model/range~ModelRange ranges}.
	*
	* Examples:
	*
	* ```ts
	* let range = model.createRange(
	* 	model.createPositionFromPath( root, [ 2, 7 ] ),
	* 	model.createPositionFromPath( root, [ 4, 0, 1 ] )
	* );
	* let otherRange = model.createRange( model.createPositionFromPath( root, [ 1 ] ), model.createPositionFromPath( root, [ 5 ] ) );
	* let transformed = range.getDifference( otherRange );
	* // transformed array has no ranges because `otherRange` contains `range`
	*
	* otherRange = model.createRange( model.createPositionFromPath( root, [ 1 ] ), model.createPositionFromPath( root, [ 3 ] ) );
	* transformed = range.getDifference( otherRange );
	* // transformed array has one range: from [ 3 ] to [ 4, 0, 1 ]
	*
	* otherRange = model.createRange( model.createPositionFromPath( root, [ 3 ] ), model.createPositionFromPath( root, [ 4 ] ) );
	* transformed = range.getDifference( otherRange );
	* // transformed array has two ranges: from [ 2, 7 ] to [ 3 ] and from [ 4 ] to [ 4, 0, 1 ]
	* ```
	*
	* @param otherRange Range to differentiate against.
	* @returns The difference between ranges.
	*/
	getDifference(otherRange) {
		const ranges = [];
		if (this.isIntersecting(otherRange)) {
			if (this.containsPosition(otherRange.start)) ranges.push(new ModelRange(this.start, otherRange.start));
			if (this.containsPosition(otherRange.end)) ranges.push(new ModelRange(otherRange.end, this.end));
		} else ranges.push(new ModelRange(this.start, this.end));
		return ranges;
	}
	/**
	* Returns an intersection of this {@link module:engine/model/range~ModelRange range} and given
	* {@link module:engine/model/range~ModelRange range}.
	* Intersection is a common part of both of those ranges. If ranges has no common part, returns `null`.
	*
	* Examples:
	*
	* ```ts
	* let range = model.createRange(
	* 	model.createPositionFromPath( root, [ 2, 7 ] ),
	* 	model.createPositionFromPath( root, [ 4, 0, 1 ] )
	* );
	* let otherRange = model.createRange( model.createPositionFromPath( root, [ 1 ] ), model.createPositionFromPath( root, [ 2 ] ) );
	* let transformed = range.getIntersection( otherRange ); // null - ranges have no common part
	*
	* otherRange = model.createRange( model.createPositionFromPath( root, [ 3 ] ), model.createPositionFromPath( root, [ 5 ] ) );
	* transformed = range.getIntersection( otherRange ); // range from [ 3 ] to [ 4, 0, 1 ]
	* ```
	*
	* @param otherRange Range to check for intersection.
	* @returns A common part of given ranges or `null` if ranges have no common part.
	*/
	getIntersection(otherRange) {
		if (this.isIntersecting(otherRange)) {
			let commonRangeStart = this.start;
			let commonRangeEnd = this.end;
			if (this.containsPosition(otherRange.start)) commonRangeStart = otherRange.start;
			if (this.containsPosition(otherRange.end)) commonRangeEnd = otherRange.end;
			return new ModelRange(commonRangeStart, commonRangeEnd);
		}
		return null;
	}
	/**
	* Returns a range created by joining this {@link module:engine/model/range~ModelRange range} with the given
	* {@link module:engine/model/range~ModelRange range}.
	* If ranges have no common part, returns `null`.
	*
	* Examples:
	*
	* ```ts
	* let range = model.createRange(
	* 	model.createPositionFromPath( root, [ 2, 7 ] ),
	* 	model.createPositionFromPath( root, [ 4, 0, 1 ] )
	* );
	* let otherRange = model.createRange(
	* 	model.createPositionFromPath( root, [ 1 ] ),
	* 	model.createPositionFromPath( root, [ 2 ] )
	* );
	* let transformed = range.getJoined( otherRange ); // null - ranges have no common part
	*
	* otherRange = model.createRange(
	* 	model.createPositionFromPath( root, [ 3 ] ),
	* 	model.createPositionFromPath( root, [ 5 ] )
	* );
	* transformed = range.getJoined( otherRange ); // range from [ 2, 7 ] to [ 5 ]
	* ```
	*
	* @param otherRange Range to be joined.
	* @param loose Whether the intersection check is loose or strict. If the check is strict (`false`),
	* ranges are tested for intersection or whether start/end positions are equal. If the check is loose (`true`),
	* compared range is also checked if it's {@link module:engine/model/position~ModelPosition#isTouching touching} current range.
	* @returns A sum of given ranges or `null` if ranges have no common part.
	*/
	getJoined(otherRange, loose = false) {
		let shouldJoin = this.isIntersecting(otherRange);
		if (!shouldJoin) if (this.start.isBefore(otherRange.start)) shouldJoin = loose ? this.end.isTouching(otherRange.start) : this.end.isEqual(otherRange.start);
		else shouldJoin = loose ? otherRange.end.isTouching(this.start) : otherRange.end.isEqual(this.start);
		if (!shouldJoin) return null;
		let startPosition = this.start;
		let endPosition = this.end;
		if (otherRange.start.isBefore(startPosition)) startPosition = otherRange.start;
		if (otherRange.end.isAfter(endPosition)) endPosition = otherRange.end;
		return new ModelRange(startPosition, endPosition);
	}
	/**
	* Computes and returns the smallest set of {@link #isFlat flat} ranges, that covers this range in whole.
	*
	* See an example of a model structure (`[` and `]` are range boundaries):
	*
	* ```
	* root                                                            root
	*  |- element DIV                         DIV             P2              P3             DIV
	*  |   |- element H                   H        P1        f o o           b a r       H         P4
	*  |   |   |- "fir[st"             fir[st     lorem                               se]cond     ipsum
	*  |   |- element P1
	*  |   |   |- "lorem"                                              ||
	*  |- element P2                                                   ||
	*  |   |- "foo"                                                    VV
	*  |- element P3
	*  |   |- "bar"                                                   root
	*  |- element DIV                         DIV             [P2             P3]             DIV
	*  |   |- element H                   H       [P1]       f o o           b a r        H         P4
	*  |   |   |- "se]cond"            fir[st]    lorem                               [se]cond     ipsum
	*  |   |- element P4
	*  |   |   |- "ipsum"
	* ```
	*
	* As it can be seen, letters contained in the range are: `stloremfoobarse`, spread across different parents.
	* We are looking for minimal set of flat ranges that contains the same nodes.
	*
	* Minimal flat ranges for above range `( [ 0, 0, 3 ], [ 3, 0, 2 ] )` will be:
	*
	* ```
	* ( [ 0, 0, 3 ], [ 0, 0, 5 ] ) = "st"
	* ( [ 0, 1 ], [ 0, 2 ] ) = element P1 ("lorem")
	* ( [ 1 ], [ 3 ] ) = element P2, element P3 ("foobar")
	* ( [ 3, 0, 0 ], [ 3, 0, 2 ] ) = "se"
	* ```
	*
	* **Note:** if an {@link module:engine/model/element~ModelElement element} is not wholly contained in this range, it won't be returned
	* in any of the returned flat ranges. See in the example how `H` elements at the beginning and at the end of the range
	* were omitted. Only their parts that were wholly in the range were returned.
	*
	* **Note:** this method is not returning flat ranges that contain no nodes.
	*
	* @returns Array of flat ranges covering this range.
	*/
	getMinimalFlatRanges() {
		const ranges = [];
		const diffAt = this.start.getCommonPath(this.end).length;
		const pos = ModelPosition._createAt(this.start);
		let posParent = pos.parent;
		while (pos.path.length > diffAt + 1) {
			const howMany = posParent.maxOffset - pos.offset;
			if (howMany !== 0) ranges.push(new ModelRange(pos, pos.getShiftedBy(howMany)));
			pos.path = pos.path.slice(0, -1);
			pos.offset++;
			posParent = posParent.parent;
		}
		while (pos.path.length <= this.end.path.length) {
			const offset = this.end.path[pos.path.length - 1];
			const howMany = offset - pos.offset;
			if (howMany !== 0) ranges.push(new ModelRange(pos, pos.getShiftedBy(howMany)));
			pos.offset = offset;
			pos.path.push(0);
		}
		return ranges;
	}
	/**
	* Creates a {@link module:engine/model/treewalker~ModelTreeWalker TreeWalker} instance with this range as a boundary.
	*
	* For example, to iterate over all items in the entire document root:
	*
	* ```ts
	* // Create a range spanning over the entire root content:
	* const range = editor.model.createRangeIn( editor.model.document.getRoot() );
	*
	* // Iterate over all items in this range:
	* for ( const value of range.getWalker() ) {
	* 	console.log( value.item );
	* }
	* ```
	*
	* @param options Object with configuration options. See {@link module:engine/model/treewalker~ModelTreeWalker}.
	*/
	getWalker(options = {}) {
		options.boundaries = this;
		return new ModelTreeWalker(options);
	}
	/**
	* Returns an iterator that iterates over all {@link module:engine/model/item~ModelItem items} that are in this range and returns
	* them.
	*
	* This method uses {@link module:engine/model/treewalker~ModelTreeWalker} with `boundaries` set to this range and
	* `ignoreElementEnd` option set to `true`. However it returns only {@link module:engine/model/item~ModelItem model items},
	* not {@link module:engine/model/treewalker~ModelTreeWalkerValue}.
	*
	* You may specify additional options for the tree walker. See {@link module:engine/model/treewalker~ModelTreeWalker} for
	* a full list of available options.
	*
	* @param options Object with configuration options. See {@link module:engine/model/treewalker~ModelTreeWalker}.
	*/
	*getItems(options = {}) {
		options.boundaries = this;
		options.ignoreElementEnd = true;
		const treeWalker = new ModelTreeWalker(options);
		for (const value of treeWalker) yield value.item;
	}
	/**
	* Returns an iterator that iterates over all {@link module:engine/model/position~ModelPosition positions} that are boundaries or
	* contained in this range.
	*
	* This method uses {@link module:engine/model/treewalker~ModelTreeWalker} with `boundaries` set to this range. However it returns only
	* {@link module:engine/model/position~ModelPosition positions}, not {@link module:engine/model/treewalker~ModelTreeWalkerValue}.
	*
	* You may specify additional options for the tree walker. See {@link module:engine/model/treewalker~ModelTreeWalker} for
	* a full list of available options.
	*
	* @param options Object with configuration options. See {@link module:engine/model/treewalker~ModelTreeWalker}.
	*/
	*getPositions(options = {}) {
		options.boundaries = this;
		const treeWalker = new ModelTreeWalker(options);
		yield treeWalker.position;
		for (const value of treeWalker) yield value.nextPosition;
	}
	/**
	* Returns a range that is a result of transforming this range by given `operation`.
	*
	* **Note:** transformation may break one range into multiple ranges (for example, when a part of the range is
	* moved to a different part of document tree). For this reason, an array is returned by this method and it
	* may contain one or more `Range` instances.
	*
	* @param operation Operation to transform range by.
	* @returns Range which is the result of transformation.
	*/
	getTransformedByOperation(operation) {
		switch (operation.type) {
			case "insert": return this._getTransformedByInsertOperation(operation);
			case "move":
			case "remove":
			case "reinsert": return this._getTransformedByMoveOperation(operation);
			case "split": return [this._getTransformedBySplitOperation(operation)];
			case "merge": return [this._getTransformedByMergeOperation(operation)];
		}
		return [new ModelRange(this.start, this.end)];
	}
	/**
	* Returns a range that is a result of transforming this range by multiple `operations`.
	*
	* @see module:engine/model/range~ModelRange#getTransformedByOperation
	* @param operations Operations to transform the range by.
	* @returns Range which is the result of transformation.
	*/
	getTransformedByOperations(operations) {
		const ranges = [new ModelRange(this.start, this.end)];
		for (const operation of operations) for (let i = 0; i < ranges.length; i++) {
			const result = ranges[i].getTransformedByOperation(operation);
			ranges.splice(i, 1, ...result);
			i += result.length - 1;
		}
		for (let i = 0; i < ranges.length; i++) {
			const range = ranges[i];
			for (let j = i + 1; j < ranges.length; j++) {
				const next = ranges[j];
				if (range.containsRange(next) || next.containsRange(range) || range.isEqual(next)) ranges.splice(j, 1);
			}
		}
		return ranges;
	}
	/**
	* Returns an {@link module:engine/model/element~ModelElement} or {@link module:engine/model/documentfragment~ModelDocumentFragment}
	* which is a common ancestor of the range's both ends (in which the entire range is contained).
	*/
	getCommonAncestor() {
		return this.start.getCommonAncestor(this.end);
	}
	/**
	* Returns an {@link module:engine/model/element~ModelElement Element} contained by the range.
	* The element will be returned when it is the **only** node within the range and **fully–contained**
	* at the same time.
	*/
	getContainedElement() {
		if (this.isCollapsed) return null;
		const nodeAfterStart = this.start.nodeAfter;
		const nodeBeforeEnd = this.end.nodeBefore;
		if (nodeAfterStart && nodeAfterStart.is("element") && nodeAfterStart === nodeBeforeEnd) return nodeAfterStart;
		return null;
	}
	/**
	* Converts `Range` to plain object and returns it.
	*
	* @returns `Range` converted to plain object.
	*/
	toJSON() {
		return {
			start: this.start.toJSON(),
			end: this.end.toJSON()
		};
	}
	/**
	* Returns a new range that is equal to current range.
	*/
	clone() {
		return new this.constructor(this.start, this.end);
	}
	/**
	* Returns a result of transforming a copy of this range by insert operation.
	*
	* One or more ranges may be returned as a result of this transformation.
	*
	* @internal
	*/
	_getTransformedByInsertOperation(operation, spread = false) {
		return this._getTransformedByInsertion(operation.position, operation.howMany, spread);
	}
	/**
	* Returns a result of transforming a copy of this range by move operation.
	*
	* One or more ranges may be returned as a result of this transformation.
	*
	* @internal
	*/
	_getTransformedByMoveOperation(operation, spread = false) {
		const sourcePosition = operation.sourcePosition;
		const howMany = operation.howMany;
		const targetPosition = operation.targetPosition;
		return this._getTransformedByMove(sourcePosition, targetPosition, howMany, spread);
	}
	/**
	* Returns a result of transforming a copy of this range by split operation.
	*
	* Always one range is returned. The transformation is done in a way to not break the range.
	*
	* @internal
	*/
	_getTransformedBySplitOperation(operation) {
		const start = this.start._getTransformedBySplitOperation(operation);
		let end = this.end._getTransformedBySplitOperation(operation);
		if (this.end.isEqual(operation.insertionPosition)) end = this.end.getShiftedBy(1);
		if (start.root != end.root) end = this.end.getShiftedBy(-1);
		return new ModelRange(start, end);
	}
	/**
	* Returns a result of transforming a copy of this range by merge operation.
	*
	* Always one range is returned. The transformation is done in a way to not break the range.
	*
	* @internal
	*/
	_getTransformedByMergeOperation(operation) {
		if (this.start.isEqual(operation.targetPosition) && this.end.isEqual(operation.deletionPosition)) return new ModelRange(this.start);
		let start = this.start._getTransformedByMergeOperation(operation);
		let end = this.end._getTransformedByMergeOperation(operation);
		if (start.root != end.root) end = this.end.getShiftedBy(-1);
		if (start.isAfter(end)) {
			if (operation.sourcePosition.isBefore(operation.targetPosition)) {
				start = ModelPosition._createAt(end);
				start.offset = 0;
			} else {
				if (!operation.deletionPosition.isEqual(start)) end = operation.deletionPosition;
				start = operation.targetPosition;
			}
			return new ModelRange(start, end);
		}
		return new ModelRange(start, end);
	}
	/**
	* Returns an array containing one or two {@link module:engine/model/range~ModelRange ranges} that are a result of transforming this
	* {@link module:engine/model/range~ModelRange range} by inserting `howMany` nodes at `insertPosition`. Two
	* {@link module:engine/model/range~ModelRange ranges} are returned if the insertion was inside this
	* {@link module:engine/model/range~ModelRange range} and `spread` is set to `true`.
	*
	* Examples:
	*
	* ```ts
	* let range = model.createRange(
	* 	model.createPositionFromPath( root, [ 2, 7 ] ),
	* 	model.createPositionFromPath( root, [ 4, 0, 1 ] )
	* );
	* let transformed = range._getTransformedByInsertion( model.createPositionFromPath( root, [ 1 ] ), 2 );
	* // transformed array has one range from [ 4, 7 ] to [ 6, 0, 1 ]
	*
	* transformed = range._getTransformedByInsertion( model.createPositionFromPath( root, [ 4, 0, 0 ] ), 4 );
	* // transformed array has one range from [ 2, 7 ] to [ 4, 0, 5 ]
	*
	* transformed = range._getTransformedByInsertion( model.createPositionFromPath( root, [ 3, 2 ] ), 4 );
	* // transformed array has one range, which is equal to original range
	*
	* transformed = range._getTransformedByInsertion( model.createPositionFromPath( root, [ 3, 2 ] ), 4, true );
	* // transformed array has two ranges: from [ 2, 7 ] to [ 3, 2 ] and from [ 3, 6 ] to [ 4, 0, 1 ]
	* ```
	*
	* @internal
	* @param insertPosition Position where nodes are inserted.
	* @param howMany How many nodes are inserted.
	* @param spread Flag indicating whether this range should be spread if insertion
	* was inside the range. Defaults to `false`.
	* @returns Result of the transformation.
	*/
	_getTransformedByInsertion(insertPosition, howMany, spread = false) {
		if (spread && this.containsPosition(insertPosition)) return [new ModelRange(this.start, insertPosition), new ModelRange(insertPosition.getShiftedBy(howMany), this.end._getTransformedByInsertion(insertPosition, howMany))];
		else {
			const range = new ModelRange(this.start, this.end);
			range.start = range.start._getTransformedByInsertion(insertPosition, howMany);
			range.end = range.end._getTransformedByInsertion(insertPosition, howMany);
			return [range];
		}
	}
	/**
	* Returns an array containing {@link module:engine/model/range~ModelRange ranges} that are a result of transforming this
	* {@link module:engine/model/range~ModelRange range} by moving `howMany` nodes from `sourcePosition` to `targetPosition`.
	*
	* @internal
	* @param sourcePosition Position from which nodes are moved.
	* @param targetPosition Position to where nodes are moved.
	* @param howMany How many nodes are moved.
	* @param spread Whether the range should be spread if the move points inside the range.
	* @returns  Result of the transformation.
	*/
	_getTransformedByMove(sourcePosition, targetPosition, howMany, spread = false) {
		if (this.isCollapsed) return [new ModelRange(this.start._getTransformedByMove(sourcePosition, targetPosition, howMany))];
		const moveRange = ModelRange._createFromPositionAndShift(sourcePosition, howMany);
		const insertPosition = targetPosition._getTransformedByDeletion(sourcePosition, howMany);
		if (this.containsPosition(targetPosition) && !spread) {
			if (moveRange.containsPosition(this.start) || moveRange.containsPosition(this.end)) return [new ModelRange(this.start._getTransformedByMove(sourcePosition, targetPosition, howMany), this.end._getTransformedByMove(sourcePosition, targetPosition, howMany))];
		}
		let result;
		const differenceSet = this.getDifference(moveRange);
		let difference = null;
		const common = this.getIntersection(moveRange);
		if (differenceSet.length == 1) difference = new ModelRange(differenceSet[0].start._getTransformedByDeletion(sourcePosition, howMany), differenceSet[0].end._getTransformedByDeletion(sourcePosition, howMany));
		else if (differenceSet.length == 2) difference = new ModelRange(this.start, this.end._getTransformedByDeletion(sourcePosition, howMany));
		if (difference) result = difference._getTransformedByInsertion(insertPosition, howMany, common !== null || spread);
		else result = [];
		if (common) {
			const transformedCommon = new ModelRange(common.start._getCombined(moveRange.start, insertPosition), common.end._getCombined(moveRange.start, insertPosition));
			if (result.length == 2) result.splice(1, 0, transformedCommon);
			else result.push(transformedCommon);
		}
		return result;
	}
	/**
	* Returns a copy of this range that is transformed by deletion of `howMany` nodes from `deletePosition`.
	*
	* If the deleted range is intersecting with the transformed range, the transformed range will be shrank.
	*
	* If the deleted range contains transformed range, `null` will be returned.
	*
	* @internal
	* @param deletePosition Position from which nodes are removed.
	* @param howMany How many nodes are removed.
	* @returns Result of the transformation.
	*/
	_getTransformedByDeletion(deletePosition, howMany) {
		let newStart = this.start._getTransformedByDeletion(deletePosition, howMany);
		let newEnd = this.end._getTransformedByDeletion(deletePosition, howMany);
		if (newStart == null && newEnd == null) return null;
		if (newStart == null) newStart = deletePosition;
		if (newEnd == null) newEnd = deletePosition;
		return new ModelRange(newStart, newEnd);
	}
	/**
	* Creates a new range, spreading from specified {@link module:engine/model/position~ModelPosition position} to a position moved by
	* given `shift`. If `shift` is a negative value, shifted position is treated as the beginning of the range.
	*
	* @internal
	* @param position Beginning of the range.
	* @param shift How long the range should be.
	*/
	static _createFromPositionAndShift(position, shift) {
		const start = position;
		const end = position.getShiftedBy(shift);
		return shift > 0 ? new this(start, end) : new this(end, start);
	}
	/**
	* Creates a range inside an {@link module:engine/model/element~ModelElement element} which starts before the first child of
	* that element and ends after the last child of that element.
	*
	* @internal
	* @param element Element which is a parent for the range.
	*/
	static _createIn(element) {
		return new this(ModelPosition._createAt(element, 0), ModelPosition._createAt(element, element.maxOffset));
	}
	/**
	* Creates a range that starts before given {@link module:engine/model/item~ModelItem model item} and ends after it.
	*
	* @internal
	*/
	static _createOn(item) {
		return this._createFromPositionAndShift(ModelPosition._createBefore(item), item.offsetSize);
	}
	/**
	* Combines all ranges from the passed array into a one range. At least one range has to be passed.
	* Passed ranges must not have common parts.
	*
	* The first range from the array is a reference range. If other ranges start or end on the exactly same position where
	* the reference range, they get combined into one range.
	*
	* ```
	* [  ][]  [    ][ ][             ][ ][]  [  ]  // Passed ranges, shown sorted
	* [    ]                                       // The result of the function if the first range was a reference range.
	*         [                           ]        // The result of the function if the third-to-seventh range was a reference range.
	*                                        [  ]  // The result of the function if the last range was a reference range.
	* ```
	*
	* @internal
	* @param ranges Ranges to combine.
	* @returns Combined range.
	*/
	static _createFromRanges(ranges) {
		if (ranges.length === 0)
 /**
		* At least one range has to be passed to
		* {@link module:engine/model/range~ModelRange._createFromRanges `Range._createFromRanges()`}.
		*
		* @error range-create-from-ranges-empty-array
		*/
		throw new CKEditorError("range-create-from-ranges-empty-array", null);
		else if (ranges.length == 1) return ranges[0].clone();
		const ref = ranges[0];
		ranges.sort((a, b) => {
			return a.start.isAfter(b.start) ? 1 : -1;
		});
		const refIndex = ranges.indexOf(ref);
		const result = new this(ref.start, ref.end);
		for (let i = refIndex - 1; i >= 0; i--) if (ranges[i].end.isEqual(result.start)) result.start = ModelPosition._createAt(ranges[i].start);
		else break;
		for (let i = refIndex + 1; i < ranges.length; i++) if (ranges[i].start.isEqual(result.end)) result.end = ModelPosition._createAt(ranges[i].end);
		else break;
		return result;
	}
	/**
	* Creates a `Range` instance from given plain object (i.e. parsed JSON string).
	*
	* @param json Plain object to be converted to `Range`.
	* @param doc Document object that will be range owner.
	* @returns `Range` instance created using given plain object.
	*/
	static fromJSON(json, doc) {
		return new this(ModelPosition.fromJSON(json.start, doc), ModelPosition.fromJSON(json.end, doc));
	}
};
ModelRange.prototype.is = function(type) {
	return type === "range" || type === "model:range";
};

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* @module engine/conversion/mapper
*/
const MapperBase = /* #__PURE__ */ EmitterMixin();
const MapperCacheBase = /* #__PURE__ */ EmitterMixin();
/**
* Maps elements, positions and markers between the {@link module:engine/view/document~ViewDocument view} and
* the {@link module:engine/model/model model}.
*
* The instance of the Mapper used for the editing pipeline is available in
* {@link module:engine/controller/editingcontroller~EditingController#mapper `editor.editing.mapper`}.
*
* Mapper uses bound elements to find corresponding elements and positions, so, to get proper results,
* all model elements should be {@link module:engine/conversion/mapper~Mapper#bindElements bound}.
*
* To map the complex model to/from view relations, you may provide custom callbacks for the
* {@link module:engine/conversion/mapper~Mapper#event:modelToViewPosition modelToViewPosition event} and
* {@link module:engine/conversion/mapper~Mapper#event:viewToModelPosition viewToModelPosition event} that are fired whenever
* a position mapping request occurs.
* Those events are fired by the {@link module:engine/conversion/mapper~Mapper#toViewPosition toViewPosition}
* and {@link module:engine/conversion/mapper~Mapper#toModelPosition toModelPosition} methods. `Mapper` adds its own default callbacks
* with `'lowest'` priority. To override default `Mapper` mapping, add custom callback with higher priority and
* stop the event.
*/
var Mapper = class extends MapperBase {
	/**
	* Model element to view element mapping.
	*/
	_modelToViewMapping = /* @__PURE__ */ new WeakMap();
	/**
	* View element to model element mapping.
	*/
	_viewToModelMapping = /* @__PURE__ */ new WeakMap();
	/**
	* A map containing callbacks between view element names and functions evaluating length of view elements
	* in model.
	*/
	_viewToModelLengthCallbacks = /* @__PURE__ */ new Map();
	/**
	* Model marker name to view elements mapping.
	*
	* Keys are `String`s while values are `Set`s with {@link module:engine/view/element~ViewElement view elements}.
	* One marker (name) can be mapped to multiple elements.
	*/
	_markerNameToElements = /* @__PURE__ */ new Map();
	/**
	* View element to model marker names mapping.
	*
	* This is reverse to {@link ~Mapper#_markerNameToElements} map.
	*/
	_elementToMarkerNames = /* @__PURE__ */ new Map();
	/**
	* The map of removed view elements with their current root (used for deferred unbinding).
	*/
	_deferredBindingRemovals = /* @__PURE__ */ new Map();
	/**
	* Stores marker names of markers which have changed due to unbinding a view element (so it is assumed that the view element
	* has been removed, moved or renamed).
	*/
	_unboundMarkerNames = /* @__PURE__ */ new Set();
	/**
	* Manages dynamic cache for the `Mapper` to improve the performance.
	*/
	_cache = new MapperCache();
	/**
	* Creates an instance of the mapper.
	*/
	constructor() {
		super();
		this.on("modelToViewPosition", (evt, data) => {
			if (data.viewPosition) return;
			const viewContainer = this._modelToViewMapping.get(data.modelPosition.parent);
			if (!viewContainer)
 /**
			* A model position could not be mapped to the view because the parent of the model position
			* does not have a mapped view element (might have not been converted yet or it has no converter).
			*
			* Make sure that the model element is correctly converted to the view.
			*
			* @error mapping-model-position-view-parent-not-found
			*/
			throw new CKEditorError("mapping-model-position-view-parent-not-found", this, { modelPosition: data.modelPosition });
			data.viewPosition = this.findPositionIn(viewContainer, data.modelPosition.offset);
		}, { priority: "low" });
		this.on("viewToModelPosition", (evt, data) => {
			if (data.modelPosition) return;
			const viewBlock = this.findMappedViewAncestor(data.viewPosition);
			const modelParent = this._viewToModelMapping.get(viewBlock);
			const modelOffset = this._toModelOffset(data.viewPosition.parent, data.viewPosition.offset, viewBlock);
			data.modelPosition = ModelPosition._createAt(modelParent, modelOffset);
		}, { priority: "low" });
	}
	/**
	* Marks model and view elements as corresponding. Corresponding elements can be retrieved by using
	* the {@link module:engine/conversion/mapper~Mapper#toModelElement toModelElement} and
	* {@link module:engine/conversion/mapper~Mapper#toViewElement toViewElement} methods.
	* The information that elements are bound is also used to translate positions.
	*
	* @param modelElement Model element.
	* @param viewElement View element.
	*/
	bindElements(modelElement, viewElement) {
		this._modelToViewMapping.set(modelElement, viewElement);
		this._viewToModelMapping.set(viewElement, modelElement);
	}
	/**
	* Unbinds the given {@link module:engine/view/element~ViewElement view element} from the map.
	*
	* **Note:** view-to-model binding will be removed, if it existed. However, corresponding model-to-view binding
	* will be removed only if model element is still bound to the passed `viewElement`.
	*
	* This behavior allows for re-binding model element to another view element without fear of losing the new binding
	* when the previously bound view element is unbound.
	*
	* @param viewElement View element to unbind.
	* @param options The options object.
	* @param options.defer Controls whether the binding should be removed immediately or deferred until a
	* {@link #flushDeferredBindings `flushDeferredBindings()`} call.
	*/
	unbindViewElement(viewElement, options = {}) {
		const modelElement = this.toModelElement(viewElement);
		if (this._elementToMarkerNames.has(viewElement)) for (const markerName of this._elementToMarkerNames.get(viewElement)) this._unboundMarkerNames.add(markerName);
		if (options.defer) this._deferredBindingRemovals.set(viewElement, viewElement.root);
		else {
			if (this._viewToModelMapping.delete(viewElement)) this._cache.stopTracking(viewElement);
			if (this._modelToViewMapping.get(modelElement) == viewElement) this._modelToViewMapping.delete(modelElement);
		}
	}
	/**
	* Unbinds the given {@link module:engine/model/element~ModelElement model element} from the map.
	*
	* **Note:** the model-to-view binding will be removed, if it existed. However, the corresponding view-to-model binding
	* will be removed only if the view element is still bound to the passed `modelElement`.
	*
	* This behavior lets for re-binding view element to another model element without fear of losing the new binding
	* when the previously bound model element is unbound.
	*
	* @param modelElement Model element to unbind.
	*/
	unbindModelElement(modelElement) {
		const viewElement = this.toViewElement(modelElement);
		this._modelToViewMapping.delete(modelElement);
		if (this._viewToModelMapping.get(viewElement) == modelElement) {
			/* v8 ignore next 4 -- @preserve */
			if (this._viewToModelMapping.delete(viewElement)) this._cache.stopTracking(viewElement);
		}
	}
	/**
	* Binds the given marker name with the given {@link module:engine/view/element~ViewElement view element}. The element
	* will be added to the current set of elements bound with the given marker name.
	*
	* @param element Element to bind.
	* @param name Marker name.
	*/
	bindElementToMarker(element, name) {
		const elements = this._markerNameToElements.get(name) || /* @__PURE__ */ new Set();
		elements.add(element);
		const names = this._elementToMarkerNames.get(element) || /* @__PURE__ */ new Set();
		names.add(name);
		this._markerNameToElements.set(name, elements);
		this._elementToMarkerNames.set(element, names);
	}
	/**
	* Unbinds an element from given marker name.
	*
	* @param element Element to unbind.
	* @param name Marker name.
	*/
	unbindElementFromMarkerName(element, name) {
		const nameToElements = this._markerNameToElements.get(name);
		if (nameToElements) {
			nameToElements.delete(element);
			if (nameToElements.size == 0) this._markerNameToElements.delete(name);
		}
		const elementToNames = this._elementToMarkerNames.get(element);
		if (elementToNames) {
			elementToNames.delete(name);
			if (elementToNames.size == 0) this._elementToMarkerNames.delete(element);
		}
	}
	/**
	* Returns all marker names of markers which have changed due to unbinding a view element (so it is assumed that the view element
	* has been removed, moved or renamed) since the last flush. After returning, the marker names list is cleared.
	*/
	flushUnboundMarkerNames() {
		const markerNames = Array.from(this._unboundMarkerNames);
		this._unboundMarkerNames.clear();
		return markerNames;
	}
	/**
	* Unbinds all deferred binding removals of view elements that in the meantime were not re-attached to some root or document fragment.
	*
	* See: {@link #unbindViewElement `unbindViewElement()`}.
	*/
	flushDeferredBindings() {
		for (const [viewElement, root] of this._deferredBindingRemovals) if (viewElement.root == root) this.unbindViewElement(viewElement);
		this._deferredBindingRemovals = /* @__PURE__ */ new Map();
	}
	/**
	* Removes all model to view and view to model bindings.
	*/
	clearBindings() {
		this._modelToViewMapping = /* @__PURE__ */ new WeakMap();
		this._viewToModelMapping = /* @__PURE__ */ new WeakMap();
		this._markerNameToElements = /* @__PURE__ */ new Map();
		this._elementToMarkerNames = /* @__PURE__ */ new Map();
		this._unboundMarkerNames = /* @__PURE__ */ new Set();
		this._deferredBindingRemovals = /* @__PURE__ */ new Map();
	}
	toModelElement(viewElement) {
		return this._viewToModelMapping.get(viewElement);
	}
	toViewElement(modelElement) {
		return this._modelToViewMapping.get(modelElement);
	}
	/**
	* Gets the corresponding model range.
	*
	* @param viewRange View range.
	* @returns Corresponding model range.
	*/
	toModelRange(viewRange) {
		return new ModelRange(this.toModelPosition(viewRange.start), this.toModelPosition(viewRange.end));
	}
	/**
	* Gets the corresponding view range.
	*
	* @param modelRange Model range.
	* @returns Corresponding view range.
	*/
	toViewRange(modelRange) {
		return new ViewRange(this.toViewPosition(modelRange.start), this.toViewPosition(modelRange.end));
	}
	/**
	* Gets the corresponding model position.
	*
	* @fires viewToModelPosition
	* @param viewPosition View position.
	* @returns Corresponding model position.
	*/
	toModelPosition(viewPosition) {
		const data = {
			viewPosition,
			mapper: this
		};
		this.fire("viewToModelPosition", data);
		return data.modelPosition;
	}
	/**
	* Gets the corresponding view position.
	*
	* @fires modelToViewPosition
	* @param modelPosition Model position.
	* @param options Additional options for position mapping process.
	* @param options.isPhantom Should be set to `true` if the model position to map is pointing to a place
	* in model tree which no longer exists. For example, it could be an end of a removed model range.
	* @returns Corresponding view position.
	*/
	toViewPosition(modelPosition, options = {}) {
		const data = {
			modelPosition,
			mapper: this,
			isPhantom: options.isPhantom
		};
		this.fire("modelToViewPosition", data);
		return data.viewPosition;
	}
	/**
	* Gets all view elements bound to the given marker name.
	*
	* @param name Marker name.
	* @returns View elements bound with the given marker name or `null`
	* if no elements are bound to the given marker name.
	*/
	markerNameToElements(name) {
		const boundElements = this._markerNameToElements.get(name);
		if (!boundElements) return null;
		const elements = /* @__PURE__ */ new Set();
		for (const element of boundElements) if (element.is("attributeElement")) for (const clone of element.getElementsWithSameId()) elements.add(clone);
		else elements.add(element);
		return elements;
	}
	/**
	* **This method is deprecated and will be removed in one of the future CKEditor 5 releases.**
	*
	* **Using this method will turn off `Mapper` caching system and may degrade performance when operating on bigger documents.**
	*
	* Registers a callback that evaluates the length in the model of a view element with the given name.
	*
	* The callback is fired with one argument, which is a view element instance. The callback is expected to return
	* a number representing the length of the view element in the model.
	*
	* ```ts
	* // List item in view may contain nested list, which have other list items. In model though,
	* // the lists are represented by flat structure. Because of those differences, length of list view element
	* // may be greater than one. In the callback it's checked how many nested list items are in evaluated list item.
	*
	* function getViewListItemLength( element ) {
	* 	let length = 1;
	*
	* 	for ( let child of element.getChildren() ) {
	* 		if ( child.name == 'ul' || child.name == 'ol' ) {
	* 			for ( let item of child.getChildren() ) {
	* 				length += getViewListItemLength( item );
	* 			}
	* 		}
	* 	}
	*
	* 	return length;
	* }
	*
	* mapper.registerViewToModelLength( 'li', getViewListItemLength );
	* ```
	*
	* @param viewElementName Name of view element for which callback is registered.
	* @param lengthCallback Function return a length of view element instance in model.
	* @deprecated
	*/
	registerViewToModelLength(viewElementName, lengthCallback) {
		this._viewToModelLengthCallbacks.set(viewElementName, lengthCallback);
	}
	/**
	* For the given `viewPosition`, finds and returns the closest ancestor of this position that has a mapping to
	* the model.
	*
	* @param viewPosition Position for which a mapped ancestor should be found.
	*/
	findMappedViewAncestor(viewPosition) {
		let parent = viewPosition.parent;
		while (!this._viewToModelMapping.has(parent)) parent = parent.parent;
		return parent;
	}
	/**
	* Calculates model offset based on the view position and the block element.
	*
	* Example:
	*
	* ```html
	* <p>foo<b>ba|r</b></p> // _toModelOffset( b, 2, p ) -> 5
	* ```
	*
	* Is a sum of:
	*
	* ```html
	* <p>foo|<b>bar</b></p> // _toModelOffset( p, 3, p ) -> 3
	* <p>foo<b>ba|r</b></p> // _toModelOffset( b, 2, b ) -> 2
	* ```
	*
	* @param viewParent Position parent.
	* @param viewOffset Position offset.
	* @param viewBlock Block used as a base to calculate offset.
	* @returns Offset in the model.
	*/
	_toModelOffset(viewParent, viewOffset, viewBlock) {
		if (viewBlock != viewParent) return this._toModelOffset(viewParent.parent, viewParent.index, viewBlock) + this._toModelOffset(viewParent, viewOffset, viewParent);
		if (viewParent.is("$text")) return viewOffset;
		let modelOffset = 0;
		for (let i = 0; i < viewOffset; i++) modelOffset += this.getModelLength(viewParent.getChild(i));
		return modelOffset;
	}
	/**
	* Gets the length of the view element in the model.
	*
	* The length is calculated as follows:
	* * if a {@link #registerViewToModelLength length mapping callback} is provided for the given `viewNode`, it is used to
	* evaluate the model length (`viewNode` is used as first and only parameter passed to the callback),
	* * length of a {@link module:engine/view/text~ViewText text node} is equal to the length of its
	* {@link module:engine/view/text~ViewText#data data},
	* * length of a {@link module:engine/view/uielement~ViewUIElement ui element} is equal to 0,
	* * length of a mapped {@link module:engine/view/element~ViewElement element} is equal to 1,
	* * length of a non-mapped {@link module:engine/view/element~ViewElement element} is equal to the length of its children.
	*
	* Examples:
	*
	* ```
	* foo                          -> 3 // Text length is equal to its data length.
	* <p>foo</p>                   -> 1 // Length of an element which is mapped is by default equal to 1.
	* <b>foo</b>                   -> 3 // Length of an element which is not mapped is a length of its children.
	* <div><p>x</p><p>y</p></div>  -> 2 // Assuming that <div> is not mapped and <p> are mapped.
	* ```
	*
	* @param viewNode View node.
	* @returns Length of the node in the tree model.
	*/
	getModelLength(viewNode) {
		const stack = [viewNode];
		let len = 0;
		while (stack.length > 0) {
			const node = stack.pop();
			const callback = node.name && this._viewToModelLengthCallbacks.size > 0 && this._viewToModelLengthCallbacks.get(node.name);
			if (callback) len += callback(node);
			else if (this._viewToModelMapping.has(node)) len += 1;
			else if (node.is("$text")) len += node.data.length;
			else if (node.is("uiElement")) continue;
			else for (const child of node.getChildren()) stack.push(child);
		}
		return len;
	}
	/**
	* Finds the position in a view element or view document fragment node (or in its children) with the expected model offset.
	*
	* If the passed `viewContainer` is bound to model, `Mapper` will use caching mechanism to improve performance.
	*
	* @param viewContainer Tree view element in which we are looking for the position.
	* @param modelOffset Expected offset.
	* @returns Found position.
	*/
	findPositionIn(viewContainer, modelOffset) {
		if (modelOffset === 0) return this._moveViewPositionToTextNode(new ViewPosition(viewContainer, 0));
		if (this._viewToModelLengthCallbacks.size == 0 && this._viewToModelMapping.has(viewContainer)) {
			const cacheItem = this._cache.getClosest(viewContainer, modelOffset);
			return this._findPositionStartingFrom(cacheItem.viewPosition, cacheItem.modelOffset, modelOffset, viewContainer, true);
		} else return this._findPositionStartingFrom(new ViewPosition(viewContainer, 0), 0, modelOffset, viewContainer, false);
	}
	/**
	* Performs most of the logic for `Mapper#findPositionIn()`.
	*
	* It allows to start looking for the requested model offset from a given starting position, to enable caching. Using the cache,
	* you can set the starting point and skip all the calculations that were already previously done.
	*
	* This method uses recursion to find positions inside deep structures. Example:
	*
	* ```
	* <p>fo<b>bar</b>bom</p>  -> target offset: 4
	* <p>|fo<b>bar</b>bom</p> -> target offset: 4, traversed offset: 0
	* <p>fo|<b>bar</b>bom</p> -> target offset: 4, traversed offset: 2
	* <p>fo<b>bar</b>|bom</p> -> target offset: 4, traversed offset: 5 -> we are too far, look recursively in <b>.
	*
	* <p>fo<b>|bar</b>bom</p> -> target offset: 4, traversed offset: 2
	* <p>fo<b>bar|</b>bom</p> -> target offset: 4, traversed offset: 5 -> we are too far, look inside "bar".
	*
	* <p>fo<b>ba|r</b>bom</p> -> target offset: 4, traversed offset: 2 -> position is inside text node at offset 4-2 = 2.
	* ```
	*
	* @param startViewPosition View position to start looking from.
	* @param startModelOffset Model offset related to `startViewPosition`.
	* @param targetModelOffset Target model offset to find.
	* @param viewContainer Mapped ancestor of `startViewPosition`. `startModelOffset` is the offset inside a model element or model
	* document fragment mapped to `viewContainer`.
	* @param useCache Whether `Mapper` should cache positions while traversing the view tree looking for `expectedModelOffset`.
	* @returns View position mapped to `targetModelOffset`.
	*/
	_findPositionStartingFrom(startViewPosition, startModelOffset, targetModelOffset, viewContainer, useCache) {
		let viewParent = startViewPosition.parent;
		let viewOffset = startViewPosition.offset;
		if (viewParent.is("$text")) return new ViewPosition(viewParent, targetModelOffset - startModelOffset);
		let viewNode;
		let traversedModelOffset = startModelOffset;
		let lastLength = 0;
		while (traversedModelOffset < targetModelOffset) {
			viewNode = viewParent.getChild(viewOffset);
			if (!viewNode) if (viewParent == viewContainer)
 /**
			* A model position could not be mapped to the view because specified model offset was too big and could not be
			* found inside the mapped view element or view document fragment.
			*
			* @error mapping-model-offset-not-found
			*/
			throw new CKEditorError("mapping-model-offset-not-found", this, {
				modelOffset: targetModelOffset,
				viewContainer
			});
			else {
				viewOffset = viewParent.parent.getChildIndex(viewParent) + 1;
				viewParent = viewParent.parent;
				/* v8 ignore next 3 -- @preserve */
				if (useCache) this._cache.save(viewParent, viewOffset, viewContainer, traversedModelOffset);
				continue;
			}
			if (useCache) lastLength = this._getModelLengthAndCache(viewNode, viewContainer, traversedModelOffset);
			else lastLength = this.getModelLength(viewNode);
			traversedModelOffset += lastLength;
			viewOffset++;
		}
		let viewPosition = new ViewPosition(viewParent, viewOffset);
		if (useCache) while (viewPosition.isAtEnd && viewPosition.parent !== viewContainer && viewPosition.parent.parent) {
			const cacheViewParent = viewPosition.parent.parent;
			const cacheViewOffset = cacheViewParent.getChildIndex(viewPosition.parent) + 1;
			this._cache.save(cacheViewParent, cacheViewOffset, viewContainer, traversedModelOffset);
			viewPosition = new ViewPosition(cacheViewParent, cacheViewOffset);
		}
		if (traversedModelOffset == targetModelOffset) return this._moveViewPositionToTextNode(viewPosition);
		else return this._findPositionStartingFrom(new ViewPosition(viewNode, 0), traversedModelOffset - lastLength, targetModelOffset, viewContainer, useCache);
	}
	/**
	* Gets the length of the view element in the model and updates cache values after each view item it visits.
	*
	* See also {@link #getModelLength}.
	*
	* @param viewNode View node.
	* @param viewContainer Ancestor of `viewNode` that is a mapped view element.
	* @param modelOffset Model offset at which the `viewNode` starts.
	* @returns Length of the node in the tree model.
	*/
	_getModelLengthAndCache(viewNode, viewContainer, modelOffset) {
		let len = 0;
		if (this._viewToModelMapping.has(viewNode)) len = 1;
		else if (viewNode.is("$text")) len = viewNode.data.length;
		else if (!viewNode.is("uiElement")) for (const child of viewNode.getChildren()) len += this._getModelLengthAndCache(child, viewContainer, modelOffset + len);
		this._cache.save(viewNode.parent, viewNode.index + 1, viewContainer, modelOffset + len);
		return len;
	}
	/**
	* Because we prefer positions in the text nodes over positions next to text nodes, if the view position was next to a text node,
	* it moves it into the text node instead.
	*
	* ```
	* <p>[]<b>foo</b></p> -> <p>[]<b>foo</b></p> // do not touch if position is not directly next to text
	* <p>foo[]<b>foo</b></p> -> <p>foo{}<b>foo</b></p> // move to text node
	* <p><b>[]foo</b></p> -> <p><b>{}foo</b></p> // move to text node
	* ```
	*
	* @param viewPosition Position potentially next to the text node.
	* @returns Position in the text node if possible.
	*/
	_moveViewPositionToTextNode(viewPosition) {
		const nodeBefore = viewPosition.nodeBefore;
		const nodeAfter = viewPosition.nodeAfter;
		if (nodeBefore && nodeBefore.is("view:$text")) return new ViewPosition(nodeBefore, nodeBefore.data.length);
		else if (nodeAfter && nodeAfter.is("view:$text")) return new ViewPosition(nodeAfter, 0);
		return viewPosition;
	}
};
/**
* Cache mechanism for {@link module:engine/conversion/mapper~Mapper Mapper}.
*
* `MapperCache` improves performance for model-to-view position mapping, which is the main `Mapper` task. Asking for a mapping is much
* more frequent than actually performing changes, and even if the change happens, we can still partially keep the cache. This makes
* caching a useful strategy for `Mapper`.
*
* `MapperCache` will store some data for view elements or view document fragments that are mapped by the `Mapper`. These view items
* are "tracked" by the `MapperCache`. For such view items, we will keep entries of model offsets inside their mapped counterpart. For
* the cached model offsets, we will keep a view position that is inside the tracked item. This allows us to either get the mapping
* instantly, or at least in less steps than when calculating it from the beginning.
*
* Important problem related to caching is invalidating the cache. The cache must be invalidated each time the tracked view item changes.
* Additionally, we should invalidate as small part of the cache as possible. Since all the logic is encapsulated inside `MapperCache`,
* the `MapperCache` listens to view items {@link module:engine/view/node~ViewNodeChangeEvent `change` event} and reacts to it.
* Then, it invalidates just the part of the cache that is "after" the changed part of the view.
*
* As mentioned, `MapperCache` currently is used only for model-to-view position mapping as it was much bigger problem than view-to-model
* mapping. However, it should be possible to use it also for view-to-model.
*
* The main assumptions regarding `MapperCache` are:
*
* * it is an internal tool, used by `Mapper`, transparent to the outside (no additional effort when developing a plugin or a converter),
* * it stores all the necessary data internally, which makes it easier to disable or debug,
* * it is optimized for initial downcast process (long insertions), which is crucial for editor init and data save,
* * it does not save all possible positions mapping for memory considerations, although it is a possible improvement, which may increase
*   performance, as well as simplify some parts of the `MapperCache` logic.
*
* @internal
*/
var MapperCache = class extends MapperCacheBase {
	/**
	* For every view element or document fragment tracked by `MapperCache`, it holds currently cached data, or more precisely,
	* model offset to view position mappings. See also `MappingCache` and `CacheItem`.
	*
	* If an item is tracked by `MapperCache` it has an entry in this structure, so this structure can be used to check which items
	* are tracked by `MapperCache`. When an item is no longer tracked, it is removed from this structure.
	*
	* Although `MappingCache` and `CacheItem` structures allows for caching any model offsets and view positions, we only cache
	* values for model offsets that are after a view node. So, in essence, positions inside text nodes are not cached. However, it takes
	* from one to at most a few steps, to get from a cached position to a position that is inside a view text node.
	*
	* Additionally, only one item per `modelOffset` is cached. There can be several view positions that map to the same `modelOffset`.
	* Only the first save for `modelOffset` is stored.
	*/
	_cachedMapping = /* @__PURE__ */ new WeakMap();
	/**
	* When `MapperCache` {@link ~MapperCache#save saves} view position -> model offset mapping, a `CacheItem` is inserted into certain
	* `MappingCache#cacheList` at some index. Additionally, we store that index with the view node that is before the cached view position.
	*
	* This allows to quickly get a cache list item related to certain view node, and hence, for fast cache invalidation.
	*
	* For example, consider view: `<p>Some <strong>bold</strong> text.</p>`, where `<p>` is a view element tracked by `MapperCache`.
	* If all `<p>` children were visited by `MapperCache`, then `<p>` cache list would have four items, related to following model offsets:
	* `0`, `5`, `9`, `15`. Then, view node `"Some "` would have index `1`, `<strong>` index `2`, and `" text." index `3`.
	*
	* Note that the index related with a node is always greater than `0`. The first item in cache list is always for model offset `0`
	* (and view offset `0`), and it is not related to any node.
	*/
	_nodeToCacheListIndex = /* @__PURE__ */ new WeakMap();
	/**
	* Callback fired whenever there is a direct or indirect children change in tracked view element or tracked view document fragment.
	*
	* This is specified as a property to make it easier to set as an event callback and to later turn off that event.
	*/
	_invalidateOnChildrenChangeCallback = (evt, viewNode, data) => {
		this._clearCacheInsideParent(viewNode, data.index);
	};
	/**
	* Callback fired whenever a view text node directly or indirectly inside a tracked view element or tracked view document fragment
	* changes its text data.
	*
	* This is specified as a property to make it easier to set as an event callback and to later turn off that event.
	*/
	_invalidateOnTextChangeCallback = (evt, viewNode) => {
		this._clearCacheAfter(viewNode);
	};
	/**
	* Saves cache for given view position mapping <-> model offset mapping. The view position should be after a node (i.e. it cannot
	* be the first position inside its parent, or in other words, `viewOffset` must be greater than `0`).
	*
	* Note, that if `modelOffset` for given `viewContainer` was already saved, the stored view position (i.e. parent+offset) will not
	* be overwritten. However, it is important to still save it, as we still store additional data related to cached view positions.
	*
	* @param viewParent View position parent.
	* @param viewOffset View position offset. Must be greater than `0`.
	* @param viewContainer Tracked view position ascendant (it may be the direct parent of the view position).
	* @param modelOffset Model offset in the model element or document fragment which is mapped to `viewContainer`.
	*/
	save(viewParent, viewOffset, viewContainer, modelOffset) {
		const cache = this._cachedMapping.get(viewContainer);
		const cacheItem = cache.cacheMap.get(modelOffset);
		if (cacheItem) {
			const viewChild = viewParent.getChild(viewOffset - 1);
			const index = cacheItem.viewPosition.nodeBefore ? this._nodeToCacheListIndex.get(cacheItem.viewPosition.nodeBefore) : 0;
			this._nodeToCacheListIndex.set(viewChild, index);
			return;
		}
		const newCacheItem = {
			viewPosition: new ViewPosition(viewParent, viewOffset),
			modelOffset
		};
		cache.maxModelOffset = modelOffset > cache.maxModelOffset ? modelOffset : cache.maxModelOffset;
		cache.cacheMap.set(modelOffset, newCacheItem);
		let i = cache.cacheList.length - 1;
		while (i >= 0 && cache.cacheList[i].modelOffset > modelOffset) i--;
		cache.cacheList.splice(i + 1, 0, newCacheItem);
		if (viewOffset > 0) {
			const viewChild = viewParent.getChild(viewOffset - 1);
			this._nodeToCacheListIndex.set(viewChild, i + 1);
		}
	}
	/**
	* For given `modelOffset` inside a model element mapped to given `viewContainer`, it returns the closest saved cache item
	* (view position and related model offset) to the requested one.
	*
	* It can be exactly the requested mapping, or it can be mapping that is the closest starting point to look for the requested mapping.
	*
	* `viewContainer` must be a view element or document fragment that is mapped by the {@link ~Mapper Mapper}.
	*
	* If `viewContainer` is not yet tracked by the `MapperCache`, it will be automatically tracked after calling this method.
	*
	* Note: this method will automatically "hoist" cached positions, i.e. it will return a position that is closest to the tracked element.
	*
	* For example, if `<p>` is tracked element, and `^` is cached position:
	*
	* ```
	* <p>This is <strong>some <em>heavily <u>formatted</u>^</em></strong> text.</p>
	* ```
	*
	* If this position would be returned, instead, a position directly in `<p>` would be returned:
	*
	* ```
	* <p>This is <strong>some <em>heavily <u>formatted</u></em></strong>^ text.</p>
	* ```
	*
	* Note, that `modelOffset` for both positions is the same.
	*
	* @param viewContainer Tracked view element or document fragment, which cache will be used.
	* @param modelOffset Model offset in a model element or document fragment, which is mapped to `viewContainer`.
	*/
	getClosest(viewContainer, modelOffset) {
		const cache = this._cachedMapping.get(viewContainer);
		let result;
		if (cache) if (modelOffset > cache.maxModelOffset) result = cache.cacheList[cache.cacheList.length - 1];
		else {
			const cacheItem = cache.cacheMap.get(modelOffset);
			if (cacheItem) result = cacheItem;
			else result = this._findInCacheList(cache.cacheList, modelOffset);
		}
		else result = this.startTracking(viewContainer);
		return {
			modelOffset: result.modelOffset,
			viewPosition: result.viewPosition.clone()
		};
	}
	/**
	* Starts tracking given `viewContainer`, which must be mapped to a model element or model document fragment.
	*
	* Note, that this method is automatically called by
	* {@link module:engine/conversion/mapper~MapperCache#getClosest `MapperCache#getClosest()`} and there is no need to call it manually.
	*
	* This method initializes the cache for `viewContainer` and adds callbacks for
	* {@link module:engine/view/node~ViewNodeChangeEvent `change` event} fired by `viewContainer`. `MapperCache` listens to `change` event
	* on the tracked elements to invalidate the stored cache.
	*/
	startTracking(viewContainer) {
		const initialCacheItem = {
			viewPosition: new ViewPosition(viewContainer, 0),
			modelOffset: 0
		};
		const initialCache = {
			maxModelOffset: 0,
			cacheList: [initialCacheItem],
			cacheMap: new Map([[0, initialCacheItem]])
		};
		this._cachedMapping.set(viewContainer, initialCache);
		viewContainer.on("change:children", this._invalidateOnChildrenChangeCallback);
		viewContainer.on("change:text", this._invalidateOnTextChangeCallback);
		return initialCacheItem;
	}
	/**
	* Stops tracking given `viewContainer`.
	*
	* It removes the cached data and stops listening to {@link module:engine/view/node~ViewNodeChangeEvent `change` event} on the
	* `viewContainer`.
	*/
	stopTracking(viewContainer) {
		viewContainer.off("change:children", this._invalidateOnChildrenChangeCallback);
		viewContainer.off("change:text", this._invalidateOnTextChangeCallback);
		this._cachedMapping.delete(viewContainer);
	}
	/**
	* Invalidates cache inside `viewParent`, starting from given `index` in that parent.
	*
	* This method may clear a bit more cache than just what was saved after given `index`, but it is guaranteed that at least it
	* will invalidate everything after `index`.
	*/
	_clearCacheInsideParent(viewParent, index) {
		if (index == 0) if (this._cachedMapping.has(viewParent)) this._clearCacheAll(viewParent);
		else this._clearCacheInsideParent(viewParent.parent, viewParent.index);
		else {
			const lastValidNode = viewParent.getChild(index - 1);
			this._clearCacheAfter(lastValidNode);
		}
	}
	/**
	* Clears all the cache for given tracked `viewContainer`.
	*/
	_clearCacheAll(viewContainer) {
		const cache = this._cachedMapping.get(viewContainer);
		if (cache.maxModelOffset > 0) {
			cache.maxModelOffset = 0;
			cache.cacheList.length = 1;
			cache.cacheMap.clear();
			cache.cacheMap.set(0, cache.cacheList[0]);
		}
	}
	/**
	* Clears all the stored cache that is after given `viewNode`. The `viewNode` can be any node that is inside a tracked view element
	* or view document fragment.
	*
	* In reality, this function may clear a bit more cache than just "starting after" `viewNode`, but it is guaranteed that at least
	* all cache after `viewNode` is invalidated.
	*/
	_clearCacheAfter(viewNode) {
		const cacheListIndex = this._nodeToCacheListIndex.get(viewNode);
		if (cacheListIndex === void 0) {
			const viewParent = viewNode.parent;
			if (!this._cachedMapping.has(viewParent)) this._clearCacheInsideParent(viewParent.parent, viewParent.index);
			return;
		}
		let viewContainer = viewNode.parent;
		while (!this._cachedMapping.has(viewContainer)) viewContainer = viewContainer.parent;
		this._clearCacheFromCacheIndex(viewContainer, cacheListIndex);
	}
	/**
	* Clears all the cache in the cache list related to given `viewContainer`, starting from `index` (inclusive).
	*/
	_clearCacheFromCacheIndex(viewContainer, index) {
		if (index === 0) index = 1;
		const cache = this._cachedMapping.get(viewContainer);
		const cacheItem = cache.cacheList[index - 1];
		if (!cacheItem) return;
		cache.maxModelOffset = cacheItem.modelOffset;
		const clearedItems = cache.cacheList.splice(index);
		for (const item of clearedItems) {
			cache.cacheMap.delete(item.modelOffset);
			const viewNode = item.viewPosition.nodeBefore;
			this._nodeToCacheListIndex.delete(viewNode);
		}
	}
	/**
	* Finds a cache item in the given cache list, which `modelOffset` is closest (but smaller or equal) to given `offset`.
	*
	* Since `cacheList` is a sorted array, this uses binary search to retrieve the item quickly.
	*/
	_findInCacheList(cacheList, offset) {
		let start = 0;
		let end = cacheList.length - 1;
		let index = end - start >> 1;
		let item = cacheList[index];
		while (start < end) {
			if (item.modelOffset < offset) start = index + 1;
			else end = index - 1;
			index = start + (end - start >> 1);
			item = cacheList[index];
		}
		return item.modelOffset <= offset ? item : cacheList[index - 1];
	}
};

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* @module engine/conversion/modelconsumable
*/
/**
* Manages a list of consumable values for the {@link module:engine/model/item~ModelItem model items}.
*
* Consumables are various aspects of the model. A model item can be broken down into separate, single properties that might be
* taken into consideration when converting that item.
*
* `ModelConsumable` is used by {@link module:engine/conversion/downcastdispatcher~DowncastDispatcher} while analyzing the changed
* parts of {@link module:engine/model/document~ModelDocument the document}. The added / changed / removed model items are broken down
* into singular properties (the item itself and its attributes). All those parts are saved in `ModelConsumable`. Then,
* during conversion, when the given part of a model item is converted (i.e. the view element has been inserted into the view,
* but without attributes), the consumable value is removed from `ModelConsumable`.
*
* For model items, `ModelConsumable` stores consumable values of one of following types: `insert`, `addattribute:<attributeKey>`,
* `changeattributes:<attributeKey>`, `removeattributes:<attributeKey>`.
*
* In most cases, it is enough to let th {@link module:engine/conversion/downcastdispatcher~DowncastDispatcher}
* gather consumable values, so there is no need to use
* the {@link module:engine/conversion/modelconsumable~ModelConsumable#add add method} directly.
* However, it is important to understand how consumable values can be
* {@link module:engine/conversion/modelconsumable~ModelConsumable#consume consumed}.
* See {@link module:engine/conversion/downcasthelpers default downcast converters} for more information.
*
* Keep in mind that one conversion event may have multiple callbacks (converters) attached to it. Each of those is
* able to convert one or more parts of the model. However, when one of those callbacks actually converts
* something, the others should not, because they would duplicate the results. Using `ModelConsumable` helps to avoid
* this situation, because callbacks should only convert these values that were not yet consumed from `ModelConsumable`.
*
* Consuming multiple values in a single callback:
*
* ```ts
* // Converter for custom `imageBlock` element that might have a `caption` element inside which changes
* // how the image is displayed in the view:
* //
* // Model:
* //
* // [imageBlock]
* //   └─ [caption]
* //       └─ foo
* //
* // View:
* //
* // <figure>
* //   ├─ <img />
* //   └─ <caption>
* //       └─ foo
* modelConversionDispatcher.on( 'insert:imageBlock', ( evt, data, conversionApi ) => {
* 	// First, consume the `imageBlock` element.
* 	conversionApi.consumable.consume( data.item, 'insert' );
*
* 	// Just create normal image element for the view.
* 	// Maybe it will be "decorated" later.
* 	const viewImage = new ViewElement( 'img' );
* 	const insertPosition = conversionApi.mapper.toViewPosition( data.range.start );
* 	const viewWriter = conversionApi.writer;
*
* 	// Check if the `imageBlock` element has children.
* 	if ( data.item.childCount > 0 ) {
* 		const modelCaption = data.item.getChild( 0 );
*
* 		// `modelCaption` insertion change is consumed from consumable values.
* 		// It will not be converted by other converters, but it's children (probably some text) will be.
* 		// Through mapping, converters for text will know where to insert contents of `modelCaption`.
* 		if ( conversionApi.consumable.consume( modelCaption, 'insert' ) ) {
* 			const viewCaption = new ViewElement( 'figcaption' );
*
* 			const viewImageHolder = new ViewElement( 'figure', null, [ viewImage, viewCaption ] );
*
* 			conversionApi.mapper.bindElements( modelCaption, viewCaption );
* 			conversionApi.mapper.bindElements( data.item, viewImageHolder );
* 			viewWriter.insert( insertPosition, viewImageHolder );
* 		}
* 	} else {
* 		conversionApi.mapper.bindElements( data.item, viewImage );
* 		viewWriter.insert( insertPosition, viewImage );
* 	}
*
* 	evt.stop();
* } );
* ```
*/
var ModelConsumable = class {
	/**
	* Contains list of consumable values.
	*/
	_consumable = /* @__PURE__ */ new Map();
	/**
	* For each {@link module:engine/model/textproxy~ModelTextProxy} added to `ModelConsumable`, this registry holds a parent
	* of that `ModelTextProxy` and the start and end indices of that `ModelTextProxy`. This allows identification of the `ModelTextProxy`
	* instances that point to the same part of the model but are different instances. Each distinct `ModelTextProxy`
	* is given a unique `Symbol` which is then registered as consumable. This process is transparent for the `ModelConsumable`
	* API user because whenever `ModelTextProxy` is added, tested, consumed or reverted, the internal mechanisms of
	* `ModelConsumable` translate `ModelTextProxy` to that unique `Symbol`.
	*/
	_textProxyRegistry = /* @__PURE__ */ new Map();
	/**
	* Adds a consumable value to the consumables list and links it with a given model item.
	*
	* ```ts
	* modelConsumable.add( modelElement, 'insert' ); // Add `modelElement` insertion change to consumable values.
	* modelConsumable.add( modelElement, 'addAttribute:bold' ); // Add `bold` attribute insertion on `modelElement` change.
	* modelConsumable.add( modelElement, 'removeAttribute:bold' ); // Add `bold` attribute removal on `modelElement` change.
	* modelConsumable.add( modelSelection, 'selection' ); // Add `modelSelection` to consumable values.
	* modelConsumable.add( modelRange, 'range' ); // Add `modelRange` to consumable values.
	* ```
	*
	* @param item Model item, range or selection that has the consumable.
	* @param type Consumable type. Will be normalized to a proper form, that is either `<word>` or `<part>:<part>`.
	* Second colon and everything after will be cut. Passing event name is a safe and good practice.
	*/
	add(item, type) {
		type = _normalizeConsumableType(type);
		if (item instanceof ModelTextProxy) item = this._getSymbolForTextProxy(item);
		if (!this._consumable.has(item)) this._consumable.set(item, /* @__PURE__ */ new Map());
		this._consumable.get(item).set(type, true);
	}
	/**
	* Removes a given consumable value from a given model item.
	*
	* ```ts
	* modelConsumable.consume( modelElement, 'insert' ); // Remove `modelElement` insertion change from consumable values.
	* modelConsumable.consume( modelElement, 'addAttribute:bold' ); // Remove `bold` attribute insertion on `modelElement` change.
	* modelConsumable.consume( modelElement, 'removeAttribute:bold' ); // Remove `bold` attribute removal on `modelElement` change.
	* modelConsumable.consume( modelSelection, 'selection' ); // Remove `modelSelection` from consumable values.
	* modelConsumable.consume( modelRange, 'range' ); // Remove 'modelRange' from consumable values.
	* ```
	*
	* @param item Model item, range or selection from which consumable will be consumed.
	* @param type Consumable type. Will be normalized to a proper form, that is either `<word>` or `<part>:<part>`.
	* Second colon and everything after will be cut. Passing event name is a safe and good practice.
	* @returns `true` if consumable value was available and was consumed, `false` otherwise.
	*/
	consume(item, type) {
		type = _normalizeConsumableType(type);
		if (item instanceof ModelTextProxy) item = this._getSymbolForTextProxy(item);
		if (this.test(item, type)) {
			this._consumable.get(item).set(type, false);
			return true;
		} else return false;
	}
	/**
	* Tests whether there is a consumable value of a given type connected with a given model item.
	*
	* ```ts
	* modelConsumable.test( modelElement, 'insert' ); // Check for `modelElement` insertion change.
	* modelConsumable.test( modelElement, 'addAttribute:bold' ); // Check for `bold` attribute insertion on `modelElement` change.
	* modelConsumable.test( modelElement, 'removeAttribute:bold' ); // Check for `bold` attribute removal on `modelElement` change.
	* modelConsumable.test( modelSelection, 'selection' ); // Check if `modelSelection` is consumable.
	* modelConsumable.test( modelRange, 'range' ); // Check if `modelRange` is consumable.
	* ```
	*
	* @param item Model item, range or selection to be tested.
	* @param type Consumable type. Will be normalized to a proper form, that is either `<word>` or `<part>:<part>`.
	* Second colon and everything after will be cut. Passing event name is a safe and good practice.
	* @returns `null` if such consumable was never added, `false` if the consumable values was
	* already consumed or `true` if it was added and not consumed yet.
	*/
	test(item, type) {
		type = _normalizeConsumableType(type);
		if (item instanceof ModelTextProxy) item = this._getSymbolForTextProxy(item);
		const itemConsumables = this._consumable.get(item);
		if (itemConsumables === void 0) return null;
		const value = itemConsumables.get(type);
		if (value === void 0) return null;
		return value;
	}
	/**
	* Reverts consuming of a consumable value.
	*
	* ```ts
	* modelConsumable.revert( modelElement, 'insert' ); // Revert consuming `modelElement` insertion change.
	* modelConsumable.revert( modelElement, 'addAttribute:bold' ); // Revert consuming `bold` attribute insert from `modelElement`.
	* modelConsumable.revert( modelElement, 'removeAttribute:bold' ); // Revert consuming `bold` attribute remove from `modelElement`.
	* modelConsumable.revert( modelSelection, 'selection' ); // Revert consuming `modelSelection`.
	* modelConsumable.revert( modelRange, 'range' ); // Revert consuming `modelRange`.
	* ```
	*
	* @param item Model item, range or selection to be reverted.
	* @param type Consumable type.
	* @returns `true` if consumable has been reversed, `false` otherwise. `null` if the consumable has
	* never been added.
	*/
	revert(item, type) {
		type = _normalizeConsumableType(type);
		if (item instanceof ModelTextProxy) item = this._getSymbolForTextProxy(item);
		const test = this.test(item, type);
		if (test === false) {
			this._consumable.get(item).set(type, true);
			return true;
		} else if (test === true) return false;
		return null;
	}
	/**
	* Verifies if all events from the specified group were consumed.
	*
	* @param eventGroup The events group to verify.
	*/
	verifyAllConsumed(eventGroup) {
		const items = [];
		for (const [item, consumables] of this._consumable) for (const [event, canConsume] of consumables) {
			const eventPrefix = event.split(":")[0];
			if (canConsume && eventGroup == eventPrefix) items.push({
				event,
				item: item.name || item.description
			});
		}
		if (items.length)
 /**
		* Some of the {@link module:engine/model/item~ModelItem model items} were not consumed while downcasting the model to view.
		*
		* This might be the effect of:
		*
		* * A missing converter for some model elements. Make sure that you registered downcast converters for all model elements.
		* * A custom converter that does not consume converted items. Make sure that you
		* {@link module:engine/conversion/modelconsumable~ModelConsumable#consume consumed} all model elements that you converted
		* from the model to the view.
		* * A custom converter that called `event.stop()`. When providing a custom converter, keep in mind that you should not stop
		* the event. If you stop it then the default converter at the `lowest` priority will not trigger the conversion of this node's
		* attributes and child nodes.
		*
		* @error conversion-model-consumable-not-consumed
		* @param {Array.<module:engine/model/item~ModelItem>} items Items that were not consumed.
		*/
		throw new CKEditorError("conversion-model-consumable-not-consumed", null, { items });
	}
	/**
	* Gets a unique symbol for the passed {@link module:engine/model/textproxy~ModelTextProxy} instance.
	* All `ModelTextProxy` instances that have same parent, same start index and same end index will get the same symbol.
	*
	* Used internally to correctly consume `ModelTextProxy` instances.
	*
	* @internal
	* @param textProxy `ModelTextProxy` instance to get a symbol for.
	* @returns Symbol representing all equal instances of `ModelTextProxy`.
	*/
	_getSymbolForTextProxy(textProxy) {
		let symbol = null;
		const startMap = this._textProxyRegistry.get(textProxy.startOffset);
		if (startMap) {
			const endMap = startMap.get(textProxy.endOffset);
			if (endMap) symbol = endMap.get(textProxy.parent);
		}
		if (!symbol) symbol = this._addSymbolForTextProxy(textProxy);
		return symbol;
	}
	/**
	* Adds a symbol for the given {@link module:engine/model/textproxy~ModelTextProxy} instance.
	*
	* Used internally to correctly consume `ModelTextProxy` instances.
	*
	* @param textProxy Text proxy instance.
	* @returns Symbol generated for given `ModelTextProxy`.
	*/
	_addSymbolForTextProxy(textProxy) {
		const start = textProxy.startOffset;
		const end = textProxy.endOffset;
		const parent = textProxy.parent;
		const symbol = Symbol("$textProxy:" + textProxy.data);
		let startMap;
		let endMap;
		startMap = this._textProxyRegistry.get(start);
		if (!startMap) {
			startMap = /* @__PURE__ */ new Map();
			this._textProxyRegistry.set(start, startMap);
		}
		endMap = startMap.get(end);
		if (!endMap) {
			endMap = /* @__PURE__ */ new Map();
			startMap.set(end, endMap);
		}
		endMap.set(parent, symbol);
		return symbol;
	}
};
/**
* Returns a normalized consumable type name from the given string. A normalized consumable type name is a string that has
* at most one colon, for example: `insert` or `addMarker:highlight`. If a string to normalize has more "parts" (more colons),
* the further parts are dropped, for example: `addattribute:bold:$text` -> `addattributes:bold`.
*
* @param type Consumable type.
* @returns Normalized consumable type.
*/
function _normalizeConsumableType(type) {
	const parts = type.split(":");
	if (parts[0] == "insert") return parts[0];
	if (parts[0] == "addMarker" || parts[0] == "removeMarker") return type;
	return parts.length > 1 ? parts[0] + ":" + parts[1] : parts[0];
}

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* Sorts markers so the downcast result is deterministic regardless of the order
* markers were added to the marker collection.
*
* The sort key is the marker's range, ordered "right-to-left" through the document so that
* a marker's opening boundary is processed *after* any markers nested inside it. This way
* the outer marker wraps the inner ones at conversion time.
*
* Cases (positions shown as `0123456789`, sort result top-to-bottom):
*
* 1. Non-overlapping ranges — sorted by position, last range first:
*
*        a: [--]               →   c, b, a
*        b:     [--]
*        c:        [--]
*
* 2. Adjacent ranges (end === start) — treated as non-overlapping:
*
*        first:  [---]         →   third, second, first
*        second:    [---]
*        third:        [---]
*
* 3. Nested ranges (same start, different ends) — inner first, outer last:
*
*        shorter: [-]          →   shorter, longer
*        longer:  [---]
*
* 4. Partially overlapping ranges — sorted by start position:
*
*        earlier: [---]        →   later, earlier
*        later:     [---]
*
* 5. Identical ranges — fall back to reverse name comparison:
*
*        alpha:   [---]        →   charlie, bravo, alpha
*        bravo:   [---]
*        charlie: [---]
*
* @internal
*/
function compareMarkersForDowncast([name1, range1], [name2, range2]) {
	if (range1.end.compareWith(range2.start) !== "after") return 1;
	else if (range1.start.compareWith(range2.end) !== "before") return -1;
	else switch (range1.start.compareWith(range2.start)) {
		case "before": return 1;
		case "after": return -1;
		default: switch (range1.end.compareWith(range2.end)) {
			case "before": return -1;
			case "after": return 1;
			default: return name2.localeCompare(name1);
		}
	}
}

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* @module engine/conversion/downcastdispatcher
*/
const DowncastDispatcherBase = /* #__PURE__ */ EmitterMixin();
/**
* The downcast dispatcher is a central point of downcasting (conversion from the model to the view), which is a process of reacting
* to changes in the model and firing a set of events. The callbacks listening to these events are called converters. The
* converters' role is to convert the model changes to changes in view (for example, adding view nodes or
* changing attributes on view elements).
*
* During the conversion process, downcast dispatcher fires events basing on the state of the model and prepares
* data for these events. It is important to understand that the events are connected with the changes done on the model,
* for example: "a node has been inserted" or "an attribute has changed". This is in contrary to upcasting (a view-to-model conversion)
* where you convert the view state (view nodes) to a model tree.
*
* The events are prepared basing on a diff created by the {@link module:engine/model/differ~Differ Differ}, which buffers them
* and then passes to the downcast dispatcher as a diff between the old model state and the new model state.
*
* Note that because the changes are converted, there is a need to have a mapping between the model structure and the view structure.
* To map positions and elements during the downcast (a model-to-view conversion), use {@link module:engine/conversion/mapper~Mapper}.
*
* Downcast dispatcher fires the following events for model tree changes:
*
* * {@link module:engine/conversion/downcastdispatcher~DowncastDispatcher#event:insert `insert`} &ndash;
* If a range of nodes was inserted to the model tree.
* * {@link module:engine/conversion/downcastdispatcher~DowncastDispatcher#event:remove `remove`} &ndash;
* If a range of nodes was removed from the model tree.
* * {@link module:engine/conversion/downcastdispatcher~DowncastDispatcher#event:attribute `attribute`} &ndash;
* If an attribute was added, changed or removed from a model node.
*
* For {@link module:engine/conversion/downcastdispatcher~DowncastDispatcher#event:insert `insert`}
* and {@link module:engine/conversion/downcastdispatcher~DowncastDispatcher#event:attribute `attribute`},
* the downcast dispatcher generates {@link module:engine/conversion/modelconsumable~ModelConsumable consumables}.
* These are used to have control over which changes have already been consumed. It is useful when some converters
* overwrite others or convert multiple changes (for example, it converts an insertion of an element and also converts that
* element's attributes during the insertion).
*
* Additionally, downcast dispatcher fires events for {@link module:engine/model/markercollection~Marker marker} changes:
*
* * {@link module:engine/conversion/downcastdispatcher~DowncastDispatcher#event:addMarker `addMarker`} &ndash; If a marker was added.
* * {@link module:engine/conversion/downcastdispatcher~DowncastDispatcher#event:removeMarker `removeMarker`} &ndash; If a marker was
* removed.
*
* Note that changing a marker is done through removing the marker from the old range and adding it to the new range,
* so both of these events are fired.
*
* Finally, a downcast dispatcher also handles firing events for the {@link module:engine/model/selection model selection}
* conversion:
*
* * {@link module:engine/conversion/downcastdispatcher~DowncastDispatcher#event:selection `selection`}
* &ndash; Converts the selection from the model to the view.
* * {@link module:engine/conversion/downcastdispatcher~DowncastDispatcher#event:attribute `attribute`}
* &ndash; Fired for every selection attribute.
* * {@link module:engine/conversion/downcastdispatcher~DowncastDispatcher#event:addMarker `addMarker`}
* &ndash; Fired for every marker that contains a selection.
*
* Unlike the model tree and the markers, the events for selection are not fired for changes but for a selection state.
*
* When providing custom listeners for a downcast dispatcher, remember to check whether a given change has not been
* {@link module:engine/conversion/modelconsumable~ModelConsumable#consume consumed} yet.
*
* When providing custom listeners for a downcast dispatcher, keep in mind that you **should not** stop the event. If you stop it,
* then the default converter at the `lowest` priority will not trigger the conversion of this node's attributes and child nodes.
*
* When providing custom listeners for a downcast dispatcher, remember to use the provided
* {@link module:engine/view/downcastwriter~ViewDowncastWriter view downcast writer} to apply changes to the view document.
*
* You can read more about conversion in the following guide:
*
* * {@glink framework/deep-dive/conversion/downcast Downcast conversion}
*
* An example of a custom converter for the downcast dispatcher:
*
* ```ts
* // You will convert inserting a "paragraph" model element into the model.
* downcastDispatcher.on( 'insert:paragraph', ( evt, data, conversionApi ) => {
* 	// Remember to check whether the change has not been consumed yet and consume it.
* 	if ( !conversionApi.consumable.consume( data.item, 'insert' ) ) {
* 		return;
* 	}
*
* 	// Translate the position in the model to a position in the view.
* 	const viewPosition = conversionApi.mapper.toViewPosition( data.range.start );
*
* 	// Create a <p> element that will be inserted into the view at the `viewPosition`.
* 	const viewElement = conversionApi.writer.createContainerElement( 'p' );
*
* 	// Bind the newly created view element to the model element so positions will map accordingly in the future.
* 	conversionApi.mapper.bindElements( data.item, viewElement );
*
* 	// Add the newly created view element to the view.
* 	conversionApi.writer.insert( viewPosition, viewElement );
* } );
* ```
*/
var DowncastDispatcher = class extends DowncastDispatcherBase {
	/**
	* A template for an interface passed by the dispatcher to the event callbacks.
	*
	* @internal
	*/
	_conversionApi;
	/**
	* A map of already fired events for a given `ModelConsumable`.
	*/
	_firedEventsMap;
	/**
	* Creates a downcast dispatcher instance.
	*
	* @see module:engine/conversion/downcastdispatcher~DowncastConversionApi
	*
	* @param conversionApi Additional properties for an interface that will be passed to events fired
	* by the downcast dispatcher.
	*/
	constructor(conversionApi) {
		super();
		this._conversionApi = {
			dispatcher: this,
			...conversionApi
		};
		this._firedEventsMap = /* @__PURE__ */ new WeakMap();
	}
	/**
	* Converts changes buffered in the given {@link module:engine/model/differ~Differ model differ}
	* and fires conversion events based on it.
	*
	* @fires insert
	* @fires remove
	* @fires attribute
	* @fires addMarker
	* @fires removeMarker
	* @fires reduceChanges
	* @param differ The differ object with buffered changes.
	* @param markers Markers related to the model fragment to convert.
	* @param writer The view writer that should be used to modify the view document.
	*/
	convertChanges(differ, markers, writer) {
		const refreshedItems = differ.getRefreshedItems();
		const conversionApi = this._createConversionApi(writer, refreshedItems);
		for (const change of differ.getMarkersToRemove()) this._convertMarkerRemove(change.name, change.range, conversionApi);
		const changes = this._reduceChanges(differ.getChanges(), refreshedItems);
		for (const entry of changes) if (entry.type === "insert") this._convertInsert(ModelRange._createFromPositionAndShift(entry.position, entry.length), conversionApi);
		else if (entry.type === "reinsert") this._convertReinsert(ModelRange._createFromPositionAndShift(entry.position, entry.length), conversionApi);
		else if (entry.type === "remove") this._convertRemove(entry.position, entry.length, entry.name, conversionApi);
		else this._convertAttribute(entry.range, entry.attributeKey, entry.attributeOldValue, entry.attributeNewValue, conversionApi);
		conversionApi.mapper.flushDeferredBindings();
		for (const markerName of conversionApi.mapper.flushUnboundMarkerNames()) {
			const markerRange = markers.get(markerName).getRange();
			this._convertMarkerRemove(markerName, markerRange, conversionApi);
			this._convertMarkerAdd(markerName, markerRange, conversionApi);
		}
		const markersToAdd = differ.getMarkersToAdd().sort((a, b) => compareMarkersForDowncast([a.name, a.range], [b.name, b.range]));
		for (const change of markersToAdd) this._convertMarkerAdd(change.name, change.range, conversionApi);
		conversionApi.consumable.verifyAllConsumed("insert");
	}
	/**
	* Starts a conversion of a model range and the provided markers.
	*
	* @fires insert
	* @fires attribute
	* @fires addMarker
	* @param range The inserted range.
	* @param markers The map of markers that should be down-casted.
	* @param writer The view writer that should be used to modify the view document.
	* @param options Optional options object passed to `convertionApi.options`.
	*/
	convert(range, markers, writer, options = {}) {
		const conversionApi = this._createConversionApi(writer, void 0, options);
		this._convertInsert(range, conversionApi);
		for (const [name, range] of Array.from(markers).sort(compareMarkersForDowncast)) this._convertMarkerAdd(name, range, conversionApi);
		conversionApi.consumable.verifyAllConsumed("insert");
	}
	/**
	* Starts the model selection conversion.
	*
	* Fires events for a given {@link module:engine/model/selection~ModelSelection selection} to start the selection conversion.
	*
	* @fires selection
	* @fires addMarker
	* @fires attribute
	* @param selection The selection to convert.
	* @param markers Markers connected with the converted model.
	* @param writer View writer that should be used to modify the view document.
	*/
	convertSelection(selection, markers, writer) {
		const conversionApi = this._createConversionApi(writer);
		this.fire("cleanSelection", { selection }, conversionApi);
		const modelRoot = selection.getFirstPosition().root;
		if (!conversionApi.mapper.toViewElement(modelRoot)) return;
		const markersAtSelection = Array.from(markers.getMarkersAtPosition(selection.getFirstPosition()));
		this._addConsumablesForSelection(conversionApi.consumable, selection, markersAtSelection);
		this.fire("selection", { selection }, conversionApi);
		if (!selection.isCollapsed) return;
		for (const marker of markersAtSelection) if (conversionApi.consumable.test(selection, "addMarker:" + marker.name)) {
			const markerRange = marker.getRange();
			if (!shouldMarkerChangeBeConverted(selection.getFirstPosition(), marker, conversionApi.mapper)) continue;
			const data = {
				item: selection,
				markerName: marker.name,
				markerRange
			};
			this.fire(`addMarker:${marker.name}`, data, conversionApi);
		}
		for (const key of selection.getAttributeKeys()) if (conversionApi.consumable.test(selection, "attribute:" + key)) {
			const data = {
				item: selection,
				range: selection.getFirstRange(),
				attributeKey: key,
				attributeOldValue: null,
				attributeNewValue: selection.getAttribute(key)
			};
			this.fire(`attribute:${key}:$text`, data, conversionApi);
		}
	}
	/**
	* Fires insertion conversion of a range of nodes.
	*
	* For each node in the range, {@link #event:insert `insert` event is fired}. For each attribute on each node,
	* {@link #event:attribute `attribute` event is fired}.
	*
	* @fires insert
	* @fires attribute
	* @param range The inserted range.
	* @param conversionApi The conversion API object.
	* @param options.doNotAddConsumables Whether the ModelConsumable should not get populated
	* for items in the provided range.
	*/
	_convertInsert(range, conversionApi, options = {}) {
		if (!options.doNotAddConsumables) this._addConsumablesForInsert(conversionApi.consumable, range);
		for (const data of range.getWalker({ shallow: true })) this._testAndFire("insert", walkerValueToEventData(data), conversionApi);
	}
	/**
	* Fires conversion of a single node removal. Fires {@link #event:remove remove event} with provided data.
	*
	* @param position Position from which node was removed.
	* @param length Offset size of removed node.
	* @param name Name of removed node.
	* @param conversionApi The conversion API object.
	*/
	_convertRemove(position, length, name, conversionApi) {
		this.fire(`remove:${name}`, {
			position,
			length
		}, conversionApi);
	}
	/**
	* Starts a conversion of an attribute change on a given `range`.
	*
	* For each node in the given `range`, {@link #event:attribute attribute event} is fired with the passed data.
	*
	* @fires attribute
	* @param range Changed range.
	* @param key Key of the attribute that has changed.
	* @param oldValue Attribute value before the change or `null` if the attribute has not been set before.
	* @param newValue New attribute value or `null` if the attribute has been removed.
	* @param conversionApi The conversion API object.
	*/
	_convertAttribute(range, key, oldValue, newValue, conversionApi) {
		this._addConsumablesForRange(conversionApi.consumable, range, `attribute:${key}`);
		for (const value of range) {
			const data = {
				item: value.item,
				range: ModelRange._createFromPositionAndShift(value.previousPosition, value.length),
				attributeKey: key,
				attributeOldValue: oldValue,
				attributeNewValue: newValue
			};
			this._testAndFire(`attribute:${key}`, data, conversionApi);
		}
	}
	/**
	* Fires re-insertion conversion (with a `reconversion` flag passed to `remove` and `insert` events)
	* of a range of elements (only elements on the range depth, without children).
	*
	* For each node in the range on its depth (without children), {@link #event:insert `insert` event} is fired.
	* For each attribute on each node, {@link #event:attribute `attribute` event} is fired.
	*
	* @fires insert
	* @fires attribute
	* @param range The range to reinsert.
	* @param conversionApi The conversion API object.
	*/
	_convertReinsert(range, conversionApi) {
		const walkerValues = Array.from(range.getWalker({ shallow: true }));
		this._addConsumablesForInsert(conversionApi.consumable, walkerValues);
		for (const data of walkerValues.map(walkerValueToEventData)) {
			this.fire(`remove:${data.item.is("element") ? data.item.name : "$text"}`, {
				position: data.range.start,
				length: data.item.offsetSize,
				reconversion: true
			}, conversionApi);
			this._testAndFire("insert", {
				...data,
				reconversion: true
			}, conversionApi);
		}
	}
	/**
	* Converts the added marker. Fires the {@link #event:addMarker `addMarker`} event for each item
	* in the marker's range. If the range is collapsed, a single event is dispatched. See the event description for more details.
	*
	* @fires addMarker
	* @param markerName Marker name.
	* @param markerRange The marker range.
	* @param conversionApi The conversion API object.
	*/
	_convertMarkerAdd(markerName, markerRange, conversionApi) {
		if (markerRange.root.rootName == "$graveyard") return;
		const eventName = `addMarker:${markerName}`;
		conversionApi.consumable.add(markerRange, eventName);
		this.fire(eventName, {
			markerName,
			markerRange
		}, conversionApi);
		if (!conversionApi.consumable.consume(markerRange, eventName)) return;
		this._addConsumablesForRange(conversionApi.consumable, markerRange, eventName);
		for (const item of markerRange.getItems()) {
			if (!conversionApi.consumable.test(item, eventName)) continue;
			const data = {
				item,
				range: ModelRange._createOn(item),
				markerName,
				markerRange
			};
			this.fire(eventName, data, conversionApi);
		}
	}
	/**
	* Fires the conversion of the marker removal. Fires the {@link #event:removeMarker `removeMarker`} event with the provided data.
	*
	* @fires removeMarker
	* @param markerName Marker name.
	* @param markerRange The marker range.
	* @param conversionApi The conversion API object.
	*/
	_convertMarkerRemove(markerName, markerRange, conversionApi) {
		if (markerRange.root.rootName == "$graveyard") return;
		this.fire(`removeMarker:${markerName}`, {
			markerName,
			markerRange
		}, conversionApi);
	}
	/**
	* Fires the reduction of changes buffered in the {@link module:engine/model/differ~Differ `Differ`}.
	*
	* Features can replace selected {@link module:engine/model/differ~DifferItem `DifferItem`}s with `reinsert` entries to trigger
	* reconversion. The {@link module:engine/conversion/downcasthelpers~DowncastHelpers#elementToStructure
	* `DowncastHelpers.elementToStructure()`} is using this event to trigger reconversion.
	*
	* @fires reduceChanges
	*/
	_reduceChanges(changes, refreshedItems) {
		const data = {
			changes,
			refreshedItems
		};
		this.fire("reduceChanges", data);
		return data.changes;
	}
	/**
	* Populates provided {@link module:engine/conversion/modelconsumable~ModelConsumable} with values to consume from a given range,
	* assuming that the range has just been inserted to the model.
	*
	* @param consumable The consumable.
	* @param walkerValues The walker values for the inserted range.
	* @returns The values to consume.
	*/
	_addConsumablesForInsert(consumable, walkerValues) {
		for (const value of walkerValues) {
			const item = value.item;
			if (consumable.test(item, "insert") === null) {
				consumable.add(item, "insert");
				for (const key of item.getAttributeKeys()) consumable.add(item, "attribute:" + key);
			}
		}
		return consumable;
	}
	/**
	* Populates provided {@link module:engine/conversion/modelconsumable~ModelConsumable} with values to consume for a given range.
	*
	* @param consumable The consumable.
	* @param range The affected range.
	* @param type Consumable type.
	* @returns The values to consume.
	*/
	_addConsumablesForRange(consumable, range, type) {
		for (const item of range.getItems()) consumable.add(item, type);
		return consumable;
	}
	/**
	* Populates provided {@link module:engine/conversion/modelconsumable~ModelConsumable} with selection consumable values.
	*
	* @param consumable The consumable.
	* @param selection The selection to create the consumable from.
	* @param markers Markers that contain the selection.
	* @returns The values to consume.
	*/
	_addConsumablesForSelection(consumable, selection, markers) {
		consumable.add(selection, "selection");
		for (const marker of markers) consumable.add(selection, "addMarker:" + marker.name);
		for (const key of selection.getAttributeKeys()) consumable.add(selection, "attribute:" + key);
		return consumable;
	}
	/**
	* Tests whether given event wasn't already fired and if so, fires it.
	*
	* @fires insert
	* @fires attribute
	* @param type Event type.
	* @param data Event data.
	* @param conversionApi The conversion API object.
	*/
	_testAndFire(type, data, conversionApi) {
		const eventName = getEventName(type, data);
		const itemKey = data.item.is("$textProxy") ? conversionApi.consumable._getSymbolForTextProxy(data.item) : data.item;
		const eventsFiredForConversion = this._firedEventsMap.get(conversionApi);
		const eventsFiredForItem = eventsFiredForConversion.get(itemKey);
		if (!eventsFiredForItem) eventsFiredForConversion.set(itemKey, new Set([eventName]));
		else if (!eventsFiredForItem.has(eventName)) eventsFiredForItem.add(eventName);
		else return;
		this.fire(eventName, data, conversionApi);
	}
	/**
	* Fires not already fired events for setting attributes on just inserted item.
	*
	* @param item The model item to convert attributes for.
	* @param conversionApi The conversion API object.
	*/
	_testAndFireAddAttributes(item, conversionApi) {
		const data = {
			item,
			range: ModelRange._createOn(item)
		};
		for (const key of data.item.getAttributeKeys()) {
			data.attributeKey = key;
			data.attributeOldValue = null;
			data.attributeNewValue = data.item.getAttribute(key);
			this._testAndFire(`attribute:${key}`, data, conversionApi);
		}
	}
	/**
	* Builds an instance of the {@link module:engine/conversion/downcastdispatcher~DowncastConversionApi} from a template and a given
	* {@link module:engine/view/downcastwriter~ViewDowncastWriter `ViewDowncastWriter`} and options object.
	*
	* @param writer View writer that should be used to modify the view document.
	* @param refreshedItems A set of model elements that should not reuse their
	* previous view representations.
	* @param options Optional options passed to `convertionApi.options`.
	* @return The conversion API object.
	*/
	_createConversionApi(writer, refreshedItems = /* @__PURE__ */ new Set(), options = {}) {
		const conversionApi = {
			...this._conversionApi,
			consumable: new ModelConsumable(),
			writer,
			options,
			convertItem: (item) => this._convertInsert(ModelRange._createOn(item), conversionApi),
			convertChildren: (element) => this._convertInsert(ModelRange._createIn(element), conversionApi, { doNotAddConsumables: true }),
			convertAttributes: (item) => this._testAndFireAddAttributes(item, conversionApi),
			canReuseView: (viewElement) => !refreshedItems.has(conversionApi.mapper.toModelElement(viewElement))
		};
		this._firedEventsMap.set(conversionApi, /* @__PURE__ */ new Map());
		return conversionApi;
	}
};
/**
* Helper function, checks whether change of `marker` at `modelPosition` should be converted. Marker changes are not
* converted if they happen inside an element with custom conversion method.
*/
function shouldMarkerChangeBeConverted(modelPosition, marker, mapper) {
	const range = marker.getRange();
	const ancestors = Array.from(modelPosition.getAncestors());
	ancestors.shift();
	ancestors.reverse();
	return !ancestors.some((element) => {
		if (range.containsItem(element)) return !!mapper.toViewElement(element).getCustomProperty("addHighlight");
	});
}
function getEventName(type, data) {
	return `${type}:${data.item.is("element") ? data.item.name : "$text"}`;
}
function walkerValueToEventData(value) {
	return {
		item: value.item,
		range: ModelRange._createFromPositionAndShift(value.previousPosition, value.length)
	};
}

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* @module engine/model/node
*/
/**
* Model node. Most basic structure of model tree.
*
* This is an abstract class that is a base for other classes representing different nodes in model.
*
* **Note:** If a node is detached from the model tree, you can manipulate it using it's API.
* However, it is **very important** that nodes already attached to model tree should be only changed through
* {@link module:engine/model/writer~ModelWriter Writer API}.
*
* Changes done by `Node` methods, like {@link module:engine/model/element~ModelElement#_insertChild _insertChild} or
* {@link module:engine/model/node~ModelNode#_setAttribute _setAttribute}
* do not generate {@link module:engine/model/operation/operation~Operation operations}
* which are essential for correct editor work if you modify nodes in {@link module:engine/model/document~ModelDocument document} root.
*
* The flow of working on `Node` (and classes that inherits from it) is as such:
* 1. You can create a `Node` instance, modify it using it's API.
* 2. Add `Node` to the model using `Batch` API.
* 3. Change `Node` that was already added to the model using `Batch` API.
*
* Similarly, you cannot use `Batch` API on a node that has not been added to the model tree, with the exception
* of {@link module:engine/model/writer~ModelWriter#insert inserting} that node to the model tree.
*
* Be aware that using {@link module:engine/model/writer~ModelWriter#remove remove from Batch API} does not allow to use `Node` API because
* the information about `Node` is still kept in model document.
*
* In case of {@link module:engine/model/element~ModelElement element node}, adding and removing children also counts as changing a node and
* follows same rules.
*/
var ModelNode = class extends ModelTypeCheckable {
	/**
	* Parent of this node. It could be {@link module:engine/model/element~ModelElement}
	* or {@link module:engine/model/documentfragment~ModelDocumentFragment}.
	* Equals to `null` if the node has no parent.
	*/
	parent = null;
	/**
	* Attributes set on this node.
	*/
	_attrs;
	/**
	* Index of this node in its parent or `null` if the node has no parent.
	*
	* @internal
	*/
	_index = null;
	/**
	* Offset at which this node starts in its parent or `null` if the node has no parent.
	*
	* @internal
	*/
	_startOffset = null;
	/**
	* Creates a model node.
	*
	* This is an abstract class, so this constructor should not be used directly.
	*
	* @param attrs Node's attributes. See {@link module:utils/tomap~toMap} for a list of accepted values.
	*/
	constructor(attrs) {
		super();
		this._attrs = toMap(attrs);
	}
	/**
	* {@link module:engine/model/document~ModelDocument Document} that owns this root element.
	*/
	get document() {
		return null;
	}
	/**
	* Index of this node in its parent or `null` if the node has no parent.
	*/
	get index() {
		return this._index;
	}
	/**
	* Offset at which this node starts in its parent. It is equal to the sum of {@link #offsetSize offsetSize}
	* of all its previous siblings. Equals to `null` if node has no parent.
	*/
	get startOffset() {
		return this._startOffset;
	}
	/**
	* Offset size of this node.
	*
	* Represents how much "offset space" is occupied by the node in its parent. It is important for
	* {@link module:engine/model/position~ModelPosition position}. When node has `offsetSize` greater
	* than `1`, position can be placed between that node start and end. `offsetSize` greater than `1` is for
	* nodes that represents more than one entity, i.e. a {@link module:engine/model/text~ModelText text node}.
	*/
	get offsetSize() {
		return 1;
	}
	/**
	* Offset at which this node ends in its parent. It is equal to the sum of this node's
	* {@link module:engine/model/node~ModelNode#startOffset start offset} and {@link #offsetSize offset size}.
	* Equals to `null` if the node has no parent.
	*/
	get endOffset() {
		if (this.startOffset === null) return null;
		return this.startOffset + this.offsetSize;
	}
	/**
	* Node's next sibling or `null` if the node is a last child of it's parent or if the node has no parent.
	*/
	get nextSibling() {
		const index = this.index;
		return index !== null && this.parent.getChild(index + 1) || null;
	}
	/**
	* Node's previous sibling or `null` if the node is a first child of it's parent or if the node has no parent.
	*/
	get previousSibling() {
		const index = this.index;
		return index !== null && this.parent.getChild(index - 1) || null;
	}
	/**
	* The top-most ancestor of the node. If node has no parent it is the root itself. If the node is a part
	* of {@link module:engine/model/documentfragment~ModelDocumentFragment}, it's `root` is equal to that `DocumentFragment`.
	*/
	get root() {
		let root = this;
		while (root.parent) root = root.parent;
		return root;
	}
	/**
	* Returns `true` if the node is inside a document root that is attached to the document.
	*/
	isAttached() {
		return this.parent === null ? false : this.root.isAttached();
	}
	/**
	* Gets path to the node. The path is an array containing starting offsets of consecutive ancestors of this node,
	* beginning from {@link module:engine/model/node~ModelNode#root root}, down to this node's starting offset. The path can be used to
	* create {@link module:engine/model/position~ModelPosition Position} instance.
	*
	* ```ts
	* const abc = new Text( 'abc' );
	* const foo = new Text( 'foo' );
	* const h1 = new ModelElement( 'h1', null, new Text( 'header' ) );
	* const p = new ModelElement( 'p', null, [ abc, foo ] );
	* const div = new ModelElement( 'div', null, [ h1, p ] );
	* foo.getPath(); // Returns [ 1, 3 ]. `foo` is in `p` which is in `div`. `p` starts at offset 1, while `foo` at 3.
	* h1.getPath(); // Returns [ 0 ].
	* div.getPath(); // Returns [].
	* ```
	*/
	getPath() {
		const path = [];
		let node = this;
		while (node.parent) {
			path.unshift(node.startOffset);
			node = node.parent;
		}
		return path;
	}
	/**
	* Returns ancestors array of this node.
	*
	* @param options Options object.
	* @param options.includeSelf When set to `true` this node will be also included in parent's array.
	* @param options.parentFirst When set to `true`, array will be sorted from node's parent to root element,
	* otherwise root element will be the first item in the array.
	* @returns Array with ancestors.
	*/
	getAncestors(options = {}) {
		const ancestors = [];
		let parent = options.includeSelf ? this : this.parent;
		while (parent) {
			ancestors[options.parentFirst ? "push" : "unshift"](parent);
			parent = parent.parent;
		}
		return ancestors;
	}
	/**
	* Returns a {@link module:engine/model/element~ModelElement} or {@link module:engine/model/documentfragment~ModelDocumentFragment}
	* which is a common ancestor of both nodes.
	*
	* @param node The second node.
	* @param options Options object.
	* @param options.includeSelf When set to `true` both nodes will be considered "ancestors" too.
	* Which means that if e.g. node A is inside B, then their common ancestor will be B.
	*/
	getCommonAncestor(node, options = {}) {
		const ancestorsA = this.getAncestors(options);
		const ancestorsB = node.getAncestors(options);
		let i = 0;
		while (ancestorsA[i] == ancestorsB[i] && ancestorsA[i]) i++;
		return i === 0 ? null : ancestorsA[i - 1];
	}
	/**
	* Returns whether this node is before given node. `false` is returned if nodes are in different trees (for example,
	* in different {@link module:engine/model/documentfragment~ModelDocumentFragment}s).
	*
	* @param node Node to compare with.
	*/
	isBefore(node) {
		if (this == node) return false;
		if (this.root !== node.root) return false;
		const thisPath = this.getPath();
		const nodePath = node.getPath();
		const result = compareArrays(thisPath, nodePath);
		switch (result) {
			case "prefix": return true;
			case "extension": return false;
			default: return thisPath[result] < nodePath[result];
		}
	}
	/**
	* Returns whether this node is after given node. `false` is returned if nodes are in different trees (for example,
	* in different {@link module:engine/model/documentfragment~ModelDocumentFragment}s).
	*
	* @param node Node to compare with.
	*/
	isAfter(node) {
		if (this == node) return false;
		if (this.root !== node.root) return false;
		return !this.isBefore(node);
	}
	/**
	* Checks if the node has an attribute with given key.
	*
	* @param key Key of attribute to check.
	* @returns `true` if attribute with given key is set on node, `false` otherwise.
	*/
	hasAttribute(key) {
		return this._attrs.has(key);
	}
	/**
	* Gets an attribute value for given key or `undefined` if that attribute is not set on node.
	*
	* @param key Key of attribute to look for.
	* @returns Attribute value or `undefined`.
	*/
	getAttribute(key) {
		return this._attrs.get(key);
	}
	/**
	* Returns iterator that iterates over this node's attributes.
	*
	* Attributes are returned as arrays containing two items. First one is attribute key and second is attribute value.
	* This format is accepted by native `Map` object and also can be passed in `Node` constructor.
	*/
	getAttributes() {
		return this._attrs.entries();
	}
	/**
	* Returns iterator that iterates over this node's attribute keys.
	*/
	getAttributeKeys() {
		return this._attrs.keys();
	}
	/**
	* Converts `Node` to plain object and returns it.
	*
	* @returns `Node` converted to plain object.
	*/
	toJSON() {
		const json = {};
		if (this._attrs.size) json.attributes = Array.from(this._attrs).reduce((result, attr) => {
			result[attr[0]] = attr[1];
			return result;
		}, {});
		return json;
	}
	/**
	* Creates a copy of this node, that is a node with exactly same attributes, and returns it.
	*
	* @internal
	* @returns Node with same attributes as this node.
	*/
	_clone(_deep) {
		return new this.constructor(this._attrs);
	}
	/**
	* Removes this node from its parent.
	*
	* @internal
	* @see module:engine/model/writer~ModelWriter#remove
	*/
	_remove() {
		this.parent._removeChildren(this.index);
	}
	/**
	* Sets attribute on the node. If attribute with the same key already is set, it's value is overwritten.
	*
	* @see module:engine/model/writer~ModelWriter#setAttribute
	* @internal
	* @param key Key of attribute to set.
	* @param value Attribute value.
	*/
	_setAttribute(key, value) {
		this._attrs.set(key, value);
	}
	/**
	* Removes all attributes from the node and sets given attributes.
	*
	* @see module:engine/model/writer~ModelWriter#setAttributes
	* @internal
	* @param attrs Attributes to set. See {@link module:utils/tomap~toMap} for a list of accepted values.
	*/
	_setAttributesTo(attrs) {
		this._attrs = toMap(attrs);
	}
	/**
	* Removes an attribute with given key from the node.
	*
	* @see module:engine/model/writer~ModelWriter#removeAttribute
	* @internal
	* @param key Key of attribute to remove.
	* @returns `true` if the attribute was set on the element, `false` otherwise.
	*/
	_removeAttribute(key) {
		return this._attrs.delete(key);
	}
	/**
	* Removes all attributes from the node.
	*
	* @see module:engine/model/writer~ModelWriter#clearAttributes
	* @internal
	*/
	_clearAttributes() {
		this._attrs.clear();
	}
};
ModelNode.prototype.is = function(type) {
	return type === "node" || type === "model:node";
};

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* @module engine/model/selection
*/
const ModelSelectionBase = /* #__PURE__ */ EmitterMixin(ModelTypeCheckable);
/**
* Selection is a set of {@link module:engine/model/range~ModelRange ranges}. It has a direction specified by its
* {@link module:engine/model/selection~ModelSelection#anchor anchor} and {@link module:engine/model/selection~ModelSelection#focus focus}
* (it can be {@link module:engine/model/selection~ModelSelection#isBackward forward or backward}).
* Additionally, selection may have its own attributes (think – whether text typed in in this selection
* should have those attributes – e.g. whether you type a bolded text).
*/
var ModelSelection = class ModelSelection extends ModelSelectionBase {
	/**
	* Specifies whether the last added range was added as a backward or forward range.
	*/
	_lastRangeBackward = false;
	/**
	* List of attributes set on current selection.
	*/
	_attrs = /* @__PURE__ */ new Map();
	/** @internal */
	_ranges = [];
	/**
	* Creates a new selection instance based on the given {@link module:engine/model/selection~ModelSelectable selectable}
	* or creates an empty selection if no arguments were passed.
	*
	* ```ts
	* // Creates empty selection without ranges.
	* const selection = writer.createSelection();
	*
	* // Creates selection at the given range.
	* const range = writer.createRange( start, end );
	* const selection = writer.createSelection( range );
	*
	* // Creates selection at the given ranges
	* const ranges = [ writer.createRange( start1, end2 ), writer.createRange( star2, end2 ) ];
	* const selection = writer.createSelection( ranges );
	*
	* // Creates selection from the other selection.
	* // Note: It doesn't copy selection attributes.
	* const otherSelection = writer.createSelection();
	* const selection = writer.createSelection( otherSelection );
	*
	* // Creates selection from the given document selection.
	* // Note: It doesn't copy selection attributes.
	* const documentSelection = model.document.selection;
	* const selection = writer.createSelection( documentSelection );
	*
	* // Creates selection at the given position.
	* const position = writer.createPositionFromPath( root, path );
	* const selection = writer.createSelection( position );
	*
	* // Creates selection at the given offset in the given element.
	* const paragraph = writer.createElement( 'paragraph' );
	* const selection = writer.createSelection( paragraph, offset );
	*
	* // Creates a range inside an {@link module:engine/model/element~ModelElement element} which starts before the
	* // first child of that element and ends after the last child of that element.
	* const selection = writer.createSelection( paragraph, 'in' );
	*
	* // Creates a range on an {@link module:engine/model/item~ModelItem item} which starts before the item and ends
	* // just after the item.
	* const selection = writer.createSelection( paragraph, 'on' );
	* ```
	*
	* Selection's constructor allow passing additional options (`'backward'`) as the last argument.
	*
	* ```ts
	* // Creates backward selection.
	* const selection = writer.createSelection( range, { backward: true } );
	* ```
	*
	* @internal
	*/
	constructor(...args) {
		super();
		if (args.length) this.setTo(...args);
	}
	/**
	* Selection anchor. Anchor is the position from which the selection was started. If a user is making a selection
	* by dragging the mouse, the anchor is where the user pressed the mouse button (the beginning of the selection).
	*
	* Anchor and {@link #focus} define the direction of the selection, which is important
	* when expanding/shrinking selection. The focus moves, while the anchor should remain in the same place.
	*
	* Anchor is always set to the {@link module:engine/model/range~ModelRange#start start} or
	* {@link module:engine/model/range~ModelRange#end end} position of the last of selection's ranges. Whether it is
	* the `start` or `end` depends on the specified `options.backward`. See the {@link #setTo `setTo()`} method.
	*
	* May be set to `null` if there are no ranges in the selection.
	*
	* @see #focus
	*/
	get anchor() {
		if (this._ranges.length > 0) {
			const range = this._ranges[this._ranges.length - 1];
			return this._lastRangeBackward ? range.end : range.start;
		}
		return null;
	}
	/**
	* Selection focus. Focus is the position where the selection ends. If a user is making a selection
	* by dragging the mouse, the focus is where the mouse cursor is.
	*
	* May be set to `null` if there are no ranges in the selection.
	*
	* @see #anchor
	*/
	get focus() {
		if (this._ranges.length > 0) {
			const range = this._ranges[this._ranges.length - 1];
			return this._lastRangeBackward ? range.start : range.end;
		}
		return null;
	}
	/**
	* Whether the selection is collapsed. Selection is collapsed when there is exactly one range in it
	* and it is collapsed.
	*/
	get isCollapsed() {
		if (this._ranges.length === 1) return this._ranges[0].isCollapsed;
		else return false;
	}
	/**
	* Returns the number of ranges in the selection.
	*/
	get rangeCount() {
		return this._ranges.length;
	}
	/**
	* Specifies whether the selection's {@link #focus} precedes the selection's {@link #anchor}.
	*/
	get isBackward() {
		return !this.isCollapsed && this._lastRangeBackward;
	}
	/**
	* Checks whether this selection is equal to the given selection. Selections are equal if they have the same directions,
	* the same number of ranges and all ranges from one selection equal to ranges from the another selection.
	*
	* @param otherSelection Selection to compare with.
	* @returns `true` if selections are equal, `false` otherwise.
	*/
	isEqual(otherSelection) {
		if (this.rangeCount != otherSelection.rangeCount) return false;
		else if (this.rangeCount === 0) return true;
		if (!this.anchor.isEqual(otherSelection.anchor) || !this.focus.isEqual(otherSelection.focus)) return false;
		for (const thisRange of this._ranges) {
			let found = false;
			for (const otherRange of otherSelection._ranges) if (thisRange.isEqual(otherRange)) {
				found = true;
				break;
			}
			if (!found) return false;
		}
		return true;
	}
	/**
	* Returns an iterable object that iterates over copies of selection ranges.
	*/
	*getRanges() {
		for (const range of this._ranges) yield new ModelRange(range.start, range.end);
	}
	/**
	* Returns a copy of the first range in the selection.
	* First range is the one which {@link module:engine/model/range~ModelRange#start start} position
	* {@link module:engine/model/position~ModelPosition#isBefore is before} start position of all other ranges
	* (not to confuse with the first range added to the selection).
	*
	* Returns `null` if there are no ranges in selection.
	*/
	getFirstRange() {
		let first = null;
		for (const range of this._ranges) if (!first || range.start.isBefore(first.start)) first = range;
		return first ? new ModelRange(first.start, first.end) : null;
	}
	/**
	* Returns a copy of the last range in the selection.
	* Last range is the one which {@link module:engine/model/range~ModelRange#end end} position
	* {@link module:engine/model/position~ModelPosition#isAfter is after} end position of all other
	* ranges (not to confuse with the range most recently added to the selection).
	*
	* Returns `null` if there are no ranges in selection.
	*/
	getLastRange() {
		let last = null;
		for (const range of this._ranges) if (!last || range.end.isAfter(last.end)) last = range;
		return last ? new ModelRange(last.start, last.end) : null;
	}
	/**
	* Returns the first position in the selection.
	* First position is the position that {@link module:engine/model/position~ModelPosition#isBefore is before}
	* any other position in the selection.
	*
	* Returns `null` if there are no ranges in selection.
	*/
	getFirstPosition() {
		const first = this.getFirstRange();
		return first ? first.start.clone() : null;
	}
	/**
	* Returns the last position in the selection.
	* Last position is the position that {@link module:engine/model/position~ModelPosition#isAfter is after}
	* any other position in the selection.
	*
	* Returns `null` if there are no ranges in selection.
	*/
	getLastPosition() {
		const lastRange = this.getLastRange();
		return lastRange ? lastRange.end.clone() : null;
	}
	/**
	* Sets this selection's ranges and direction to the specified location based on the given
	* {@link module:engine/model/selection~ModelSelectable selectable}.
	*
	* ```ts
	* // Removes all selection's ranges.
	* selection.setTo( null );
	*
	* // Sets selection to the given range.
	* const range = writer.createRange( start, end );
	* selection.setTo( range );
	*
	* // Sets selection to given ranges.
	* const ranges = [ writer.createRange( start1, end2 ), writer.createRange( star2, end2 ) ];
	* selection.setTo( ranges );
	*
	* // Sets selection to other selection.
	* // Note: It doesn't copy selection attributes.
	* const otherSelection = writer.createSelection();
	* selection.setTo( otherSelection );
	*
	* // Sets selection to the given document selection.
	* // Note: It doesn't copy selection attributes.
	* const documentSelection = new ModelDocumentSelection( doc );
	* selection.setTo( documentSelection );
	*
	* // Sets collapsed selection at the given position.
	* const position = writer.createPositionFromPath( root, path );
	* selection.setTo( position );
	*
	* // Sets collapsed selection at the position of the given node and an offset.
	* selection.setTo( paragraph, offset );
	* ```
	*
	* Creates a range inside an {@link module:engine/model/element~ModelElement element} which starts before the first child of
	* that element and ends after the last child of that element.
	*
	* ```ts
	* selection.setTo( paragraph, 'in' );
	* ```
	*
	* Creates a range on an {@link module:engine/model/item~ModelItem item} which starts before the item and ends just after the item.
	*
	* ```ts
	* selection.setTo( paragraph, 'on' );
	* ```
	*
	* `Selection#setTo()` method allows passing additional options (`backward`) as the last argument.
	*
	* ```ts
	* // Sets backward selection.
	* const selection = writer.createSelection( range, { backward: true } );
	* ```
	*/
	setTo(...args) {
		let [selectable, placeOrOffset, options] = args;
		if (typeof placeOrOffset == "object") {
			options = placeOrOffset;
			placeOrOffset = void 0;
		}
		if (selectable === null) this._setRanges([]);
		else if (selectable instanceof ModelSelection) this._setRanges(selectable.getRanges(), selectable.isBackward);
		else if (selectable && typeof selectable.getRanges == "function") this._setRanges(selectable.getRanges(), selectable.isBackward);
		else if (selectable instanceof ModelRange) this._setRanges([selectable], !!options && !!options.backward);
		else if (selectable instanceof ModelPosition) this._setRanges([new ModelRange(selectable)]);
		else if (selectable instanceof ModelNode) {
			const backward = !!options && !!options.backward;
			let range;
			if (placeOrOffset == "in") range = ModelRange._createIn(selectable);
			else if (placeOrOffset == "on") range = ModelRange._createOn(selectable);
			else if (placeOrOffset !== void 0) range = new ModelRange(ModelPosition._createAt(selectable, placeOrOffset));
			else
 /**
			* selection.setTo requires the second parameter when the first parameter is a node.
			*
			* @error model-selection-setto-required-second-parameter
			*/
			throw new CKEditorError("model-selection-setto-required-second-parameter", [this, selectable]);
			this._setRanges([range], backward);
		} else if (isIterable(selectable)) this._setRanges(selectable, options && !!options.backward);
		else
 /**
		* Cannot set the selection to the given place.
		*
		* Invalid parameters were specified when setting the selection. Common issues:
		*
		* * A {@link module:engine/model/textproxy~ModelTextProxy} instance was passed instead of
		* a real {@link module:engine/model/text~ModelText}.
		* * View nodes were passed instead of model nodes.
		* * `null`/`undefined` was passed.
		*
		* @error model-selection-setto-not-selectable
		*/
		throw new CKEditorError("model-selection-setto-not-selectable", [this, selectable]);
	}
	/**
	* Replaces all ranges that were added to the selection with given array of ranges. Last range of the array
	* is treated like the last added range and is used to set {@link module:engine/model/selection~ModelSelection#anchor} and
	* {@link module:engine/model/selection~ModelSelection#focus}. Accepts a flag describing in which direction the selection is made.
	*
	* @fires change:range
	* @param newRanges Ranges to set.
	* @param isLastBackward Flag describing if last added range was selected forward - from start to end (`false`)
	* or backward - from end to start (`true`).
	*/
	_setRanges(newRanges, isLastBackward = false) {
		const ranges = Array.from(newRanges);
		const anyNewRange = ranges.some((newRange) => {
			if (!(newRange instanceof ModelRange))
 /**
			* Selection range set to an object that is not an instance of {@link module:engine/model/range~ModelRange}.
			*
			* Only {@link module:engine/model/range~ModelRange} instances can be used to set a selection.
			* Common mistakes leading to this error are:
			*
			* * using DOM `Range` object,
			* * incorrect CKEditor 5 installation with multiple `ckeditor5-engine` packages having different versions.
			*
			* @error model-selection-set-ranges-not-range
			*/
			throw new CKEditorError("model-selection-set-ranges-not-range", [this, newRanges]);
			return this._ranges.every((oldRange) => {
				return !oldRange.isEqual(newRange);
			});
		});
		if (ranges.length === this._ranges.length && !anyNewRange) return;
		this._replaceAllRanges(ranges);
		this._lastRangeBackward = !!isLastBackward;
		this.fire("change:range", { directChange: true });
	}
	/**
	* Moves {@link module:engine/model/selection~ModelSelection#focus} to the specified location.
	*
	* The location can be specified in the same form as
	* {@link module:engine/model/writer~ModelWriter#createPositionAt writer.createPositionAt()} parameters.
	*
	* @fires change:range
	* @param offset Offset or one of the flags. Used only when first parameter is a {@link module:engine/model/item~ModelItem model item}.
	*/
	setFocus(itemOrPosition, offset) {
		if (this.anchor === null)
 /**
		* Cannot set selection focus if there are no ranges in selection.
		*
		* @error model-selection-setfocus-no-ranges
		*/
		throw new CKEditorError("model-selection-setfocus-no-ranges", [this, itemOrPosition]);
		const newFocus = ModelPosition._createAt(itemOrPosition, offset);
		if (newFocus.compareWith(this.focus) == "same") return;
		const anchor = this.anchor;
		if (this._ranges.length) this._popRange();
		if (newFocus.compareWith(anchor) == "before") {
			this._pushRange(new ModelRange(newFocus, anchor));
			this._lastRangeBackward = true;
		} else {
			this._pushRange(new ModelRange(anchor, newFocus));
			this._lastRangeBackward = false;
		}
		this.fire("change:range", { directChange: true });
	}
	/**
	* Gets an attribute value for given key or `undefined` if that attribute is not set on the selection.
	*
	* @param key Key of attribute to look for.
	* @returns Attribute value or `undefined`.
	*/
	getAttribute(key) {
		return this._attrs.get(key);
	}
	/**
	* Returns iterable that iterates over this selection's attributes.
	*
	* Attributes are returned as arrays containing two items. First one is attribute key and second is attribute value.
	* This format is accepted by native `Map` object and also can be passed in `Node` constructor.
	*/
	getAttributes() {
		return this._attrs.entries();
	}
	/**
	* Returns iterable that iterates over this selection's attribute keys.
	*/
	getAttributeKeys() {
		return this._attrs.keys();
	}
	/**
	* Checks if the selection has an attribute for given key.
	*
	* @param key Key of attribute to check.
	* @returns `true` if attribute with given key is set on selection, `false` otherwise.
	*/
	hasAttribute(key) {
		return this._attrs.has(key);
	}
	/**
	* Removes an attribute with given key from the selection.
	*
	* If given attribute was set on the selection, fires the {@link #event:change:range} event with
	* removed attribute key.
	*
	* @fires change:attribute
	* @param key Key of attribute to remove.
	*/
	removeAttribute(key) {
		if (this.hasAttribute(key)) {
			this._attrs.delete(key);
			this.fire("change:attribute", {
				attributeKeys: [key],
				directChange: true
			});
		}
	}
	/**
	* Sets attribute on the selection. If attribute with the same key already is set, it's value is overwritten.
	*
	* If the attribute value has changed, fires the {@link #event:change:range} event with
	* the attribute key.
	*
	* @fires change:attribute
	* @param key Key of attribute to set.
	* @param value Attribute value.
	*/
	setAttribute(key, value) {
		if (this.getAttribute(key) !== value) {
			this._attrs.set(key, value);
			this.fire("change:attribute", {
				attributeKeys: [key],
				directChange: true
			});
		}
	}
	/**
	* Returns the selected element. {@link module:engine/model/element~ModelElement Element} is considered as selected if there is only
	* one range in the selection, and that range contains exactly one element.
	* Returns `null` if there is no selected element.
	*/
	getSelectedElement() {
		if (this.rangeCount !== 1) return null;
		return this.getFirstRange().getContainedElement();
	}
	/**
	* Gets elements of type {@link module:engine/model/schema~ModelSchema#isBlock "block"} touched by the selection.
	*
	* This method's result can be used for example to apply block styling to all blocks covered by this selection.
	*
	* **Note:** `getSelectedBlocks()` returns blocks that are nested in other non-block elements
	* but will not return blocks nested in other blocks.
	*
	* In this case the function will return exactly all 3 paragraphs (note: `<blockQuote>` is not a block itself):
	*
	* ```xml
	* <paragraph>[a</paragraph>
	* <blockQuote>
	* 	<paragraph>b</paragraph>
	* </blockQuote>
	* <paragraph>c]d</paragraph>
	* ```
	*
	* In this case the paragraph will also be returned, despite the collapsed selection:
	*
	* ```xml
	* <paragraph>[]a</paragraph>
	* ```
	*
	* In such a scenario, however, only blocks A, B & E will be returned as blocks C & D are nested in block B:
	*
	* ```xml
	* [<blockA></blockA>
	* <blockB>
	* 	<blockC></blockC>
	* 	<blockD></blockD>
	* </blockB>
	* <blockE></blockE>]
	* ```
	*
	* If the selection is inside a block all the inner blocks (A & B) are returned:
	*
	* ```xml
	* <block>
	* 	<blockA>[a</blockA>
	* 	<blockB>b]</blockB>
	* </block>
	* ```
	*
	* **Special case**: Selection ignores first and/or last blocks if nothing (from user perspective) is selected in them.
	*
	* ```xml
	* // Selection ends and the beginning of the last block.
	* <paragraph>[a</paragraph>
	* <paragraph>b</paragraph>
	* <paragraph>]c</paragraph> // This block will not be returned
	*
	* // Selection begins at the end of the first block.
	* <paragraph>a[</paragraph> // This block will not be returned
	* <paragraph>b</paragraph>
	* <paragraph>c]</paragraph>
	*
	* // Selection begings at the end of the first block and ends at the beginning of the last block.
	* <paragraph>a[</paragraph> // This block will not be returned
	* <paragraph>b</paragraph>
	* <paragraph>]c</paragraph> // This block will not be returned
	* ```
	*/
	*getSelectedBlocks() {
		const visited = /* @__PURE__ */ new WeakSet();
		for (const range of this.getRanges()) {
			const startBlock = getParentBlock$1(range.start, visited);
			if (isStartBlockSelected(startBlock, range)) yield startBlock;
			const treewalker = range.getWalker();
			for (const value of treewalker) {
				const block = value.item;
				if (value.type == "elementEnd" && isUnvisitedTopBlock(block, visited, range)) yield block;
				else if (value.type == "elementStart" && block.is("model:element") && block.root.document.model.schema.isBlock(block)) treewalker.jumpTo(ModelPosition._createAt(block, "end"));
			}
			const endBlock = getParentBlock$1(range.end, visited);
			if (isEndBlockSelected(endBlock, range)) yield endBlock;
		}
	}
	/**
	* Checks whether the selection contains the entire content of the given element. This means that selection must start
	* at a position {@link module:engine/model/position~ModelPosition#isTouching touching} the element's start and ends at position
	* touching the element's end.
	*
	* By default, this method will check whether the entire content of the selection's current root is selected.
	* Useful to check if e.g. the user has just pressed <kbd>Ctrl</kbd> + <kbd>A</kbd>.
	*/
	containsEntireContent(element = this.anchor.root) {
		const limitStartPosition = ModelPosition._createAt(element, 0);
		const limitEndPosition = ModelPosition._createAt(element, "end");
		return limitStartPosition.isTouching(this.getFirstPosition()) && limitEndPosition.isTouching(this.getLastPosition());
	}
	/**
	* Converts `Selection` to plain object and returns it.
	*
	* @returns `Selection` converted to plain object.
	*/
	toJSON() {
		const json = { ranges: Array.from(this.getRanges()).map((range) => range.toJSON()) };
		const attributes = Object.fromEntries(this.getAttributes());
		if (Object.keys(attributes).length) json.attributes = attributes;
		if (this.isBackward) json.isBackward = true;
		return json;
	}
	/**
	* Adds given range to internal {@link #_ranges ranges array}. Throws an error
	* if given range is intersecting with any range that is already stored in this selection.
	*/
	_pushRange(range) {
		this._checkRange(range);
		this._ranges.push(new ModelRange(range.start, range.end));
	}
	/**
	* Checks if given range intersects with ranges that are already in the selection. Throws an error if it does.
	*/
	_checkRange(range) {
		for (let i = 0; i < this._ranges.length; i++) if (range.isIntersecting(this._ranges[i]))
 /**
		* Trying to add a range that intersects with another range in the selection.
		*
		* @error model-selection-range-intersects
		* @param {module:engine/model/range~ModelRange} addedRange Range that was added to the selection.
		* @param {module:engine/model/range~ModelRange} intersectingRange Range in the selection that intersects with `addedRange`.
		*/
		throw new CKEditorError("model-selection-range-intersects", [this, range], {
			addedRange: range,
			intersectingRange: this._ranges[i]
		});
	}
	/**
	* Replaces all the ranges by the given ones.
	* Uses {@link #_popRange _popRange} and {@link #_pushRange _pushRange} to ensure proper ranges removal and addition.
	*/
	_replaceAllRanges(ranges) {
		this._removeAllRanges();
		for (const range of ranges) this._pushRange(range);
	}
	/**
	* Deletes ranges from internal range array. Uses {@link #_popRange _popRange} to
	* ensure proper ranges removal.
	*/
	_removeAllRanges() {
		while (this._ranges.length > 0) this._popRange();
	}
	/**
	* Removes most recently added range from the selection.
	*/
	_popRange() {
		this._ranges.pop();
	}
};
ModelSelection.prototype.is = function(type) {
	return type === "selection" || type === "model:selection";
};
/**
* Checks whether the given element extends $block in the schema and has a parent (is not a root).
* Marks it as already visited.
*/
function isUnvisitedBlock(element, visited) {
	if (visited.has(element)) return false;
	visited.add(element);
	return element.root.document.model.schema.isBlock(element) && !!element.parent;
}
/**
* Checks if the given element is a $block was not previously visited and is a top block in a range.
*/
function isUnvisitedTopBlock(element, visited, range) {
	return isUnvisitedBlock(element, visited) && isTopBlockInRange(element, range);
}
/**
* Finds the lowest element in position's ancestors which is a block.
* It will search until first ancestor that is a limit element.
* Marks all ancestors as already visited to not include any of them later on.
*/
function getParentBlock$1(position, visited) {
	const schema = position.parent.root.document.model.schema;
	const ancestors = position.parent.getAncestors({
		parentFirst: true,
		includeSelf: true
	});
	let hasParentLimit = false;
	const block = ancestors.find((element) => {
		if (hasParentLimit) return false;
		hasParentLimit = schema.isLimit(element);
		return !hasParentLimit && isUnvisitedBlock(element, visited);
	});
	ancestors.forEach((element) => visited.add(element));
	return block;
}
/**
* Checks if the blocks is not nested in other block inside a range.
*/
function isTopBlockInRange(block, range) {
	const parentBlock = findAncestorBlock(block);
	if (!parentBlock) return true;
	return !range.containsRange(ModelRange._createOn(parentBlock), true);
}
/**
* If a selection starts at the end of a block, that block is not returned as from the user's perspective this block wasn't selected.
* See [#11585](https://github.com/ckeditor/ckeditor5/issues/11585) for more details.
*
* ```xml
* <paragraph>a[</paragraph> // This block will not be returned
* <paragraph>b</paragraph>
* <paragraph>c]</paragraph>
* ```
*
* Collapsed selection is not affected by it:
*
* ```xml
* <paragraph>a[]</paragraph> // This block will be returned
* ```
*/
function isStartBlockSelected(startBlock, range) {
	if (!startBlock) return false;
	if (range.isCollapsed || startBlock.isEmpty) return true;
	if (range.start.isTouching(ModelPosition._createAt(startBlock, startBlock.maxOffset))) return false;
	return isTopBlockInRange(startBlock, range);
}
/**
* If a selection ends at the beginning of a block, that block is not returned as from the user's perspective this block wasn't selected.
* See [#984](https://github.com/ckeditor/ckeditor5-engine/issues/984) for more details.
*
* ```xml
* <paragraph>[a</paragraph>
* <paragraph>b</paragraph>
* <paragraph>]c</paragraph> // this block will not be returned
* ```
*
* Collapsed selection is not affected by it:
*
* ```xml
* <paragraph>[]a</paragraph> // this block will be returned
* ```
*/
function isEndBlockSelected(endBlock, range) {
	if (!endBlock) return false;
	if (range.isCollapsed || endBlock.isEmpty) return true;
	if (range.end.isTouching(ModelPosition._createAt(endBlock, 0))) return false;
	return isTopBlockInRange(endBlock, range);
}
/**
* Returns first ancestor block of a node.
*/
function findAncestorBlock(node) {
	const schema = node.root.document.model.schema;
	let parent = node.parent;
	while (parent) {
		if (schema.isBlock(parent)) return parent;
		parent = parent.parent;
	}
}

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* @module engine/model/liverange
*/
const ModelLiveRangeBase = /* #__PURE__ */ EmitterMixin(ModelRange);
/**
* `ModelLiveRange` is a type of {@link module:engine/model/range~ModelRange Range}
* that updates itself as {@link module:engine/model/document~ModelDocument document}
* is changed through operations. It may be used as a bookmark.
*
* **Note:** Be very careful when dealing with `ModelLiveRange`. Each `ModelLiveRange` instance bind events that might
* have to be unbound. Use {@link module:engine/model/liverange~ModelLiveRange#detach detach} whenever you don't need
* `ModelLiveRange` anymore.
*/
var ModelLiveRange = class ModelLiveRange extends ModelLiveRangeBase {
	/**
	* Creates a live range.
	*
	* @see module:engine/model/range~ModelRange
	*/
	constructor(start, end) {
		super(start, end);
		bindWithDocument$1.call(this);
	}
	/**
	* Unbinds all events previously bound by `ModelLiveRange`. Use it whenever you don't need `ModelLiveRange` instance
	* anymore (i.e. when leaving scope in which it was declared or before re-assigning variable that was
	* referring to it).
	*/
	detach() {
		this.stopListening();
	}
	/**
	* Creates a {@link module:engine/model/range~ModelRange range instance} that is equal to this live range.
	*/
	toRange() {
		return new ModelRange(this.start, this.end);
	}
	/**
	* Creates a `ModelLiveRange` instance that is equal to the given range.
	*/
	static fromRange(range) {
		return new ModelLiveRange(range.start, range.end);
	}
};
ModelLiveRange.prototype.is = function(type) {
	return type === "liveRange" || type === "model:liveRange" || type == "range" || type === "model:range";
};
/**
* Binds this `ModelLiveRange` to the {@link module:engine/model/document~ModelDocument document}
* that owns this range's {@link module:engine/model/range~ModelRange#root root}.
*/
function bindWithDocument$1() {
	this.listenTo(this.root.document.model, "applyOperation", (event, args) => {
		const operation = args[0];
		if (!operation.isDocumentOperation) return;
		transform$2.call(this, operation);
	}, { priority: "low" });
}
/**
* Updates this range accordingly to the updates applied to the model. Bases on change events.
*/
function transform$2(operation) {
	const ranges = this.getTransformedByOperation(operation);
	const result = ModelRange._createFromRanges(ranges);
	const boundariesChanged = !result.isEqual(this);
	const contentChanged = doesOperationChangeRangeContent(this, operation);
	let deletionPosition = null;
	if (boundariesChanged) {
		if (result.root.rootName == "$graveyard") if (operation.type == "remove") deletionPosition = operation.sourcePosition;
		else deletionPosition = operation.deletionPosition;
		const oldRange = this.toRange();
		this.start = result.start;
		this.end = result.end;
		this.fire("change:range", oldRange, { deletionPosition });
	} else if (contentChanged) this.fire("change:content", this.toRange(), { deletionPosition });
}
/**
* Checks whether given operation changes something inside the range (even if it does not change boundaries).
*/
function doesOperationChangeRangeContent(range, operation) {
	switch (operation.type) {
		case "insert": return range.containsPosition(operation.position);
		case "move":
		case "remove":
		case "reinsert":
		case "merge": return range.containsPosition(operation.sourcePosition) || range.start.isEqual(operation.sourcePosition) || range.containsPosition(operation.targetPosition);
		case "split": return range.containsPosition(operation.splitPosition) || range.containsPosition(operation.insertionPosition);
	}
	return false;
}

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* @module engine/model/text
*/
/**
* Model text node. Type of {@link module:engine/model/node~ModelNode node} that
* contains {@link module:engine/model/text~ModelText#data text data}.
*
* **Important:** see {@link module:engine/model/node~ModelNode} to read about restrictions using `Text` and `Node` API.
*
* **Note:** keep in mind that `Text` instances might indirectly got removed from model tree when model is changed.
* This happens when {@link module:engine/model/writer~ModelWriter model writer} is used to change model and the text node is merged with
* another text node. Then, both text nodes are removed and a new text node is inserted into the model. Because of
* this behavior, keeping references to `Text` is not recommended. Instead, consider creating
* {@link module:engine/model/liveposition~ModelLivePosition live position} placed before the text node.
*/
var ModelText = class ModelText extends ModelNode {
	/**
	* Text data contained in this text node.
	*
	* @internal
	*/
	_data;
	/**
	* Creates a text node.
	*
	* **Note:** Constructor of this class shouldn't be used directly in the code.
	* Use the {@link module:engine/model/writer~ModelWriter#createText} method instead.
	*
	* @internal
	* @param data Node's text.
	* @param attrs Node's attributes. See {@link module:utils/tomap~toMap} for a list of accepted values.
	*/
	constructor(data, attrs) {
		super(attrs);
		this._data = data || "";
	}
	/**
	* @inheritDoc
	*/
	get offsetSize() {
		return this.data.length;
	}
	/**
	* Returns a text data contained in the node.
	*/
	get data() {
		return this._data;
	}
	/**
	* Converts `Text` instance to plain object and returns it.
	*
	* @returns`Text` instance converted to plain object.
	*/
	toJSON() {
		const json = super.toJSON();
		json.data = this.data;
		return json;
	}
	/**
	* Creates a copy of this text node and returns it. Created text node has same text data and attributes as original text node.
	*
	* @internal
	* @returns `Text` instance created using given plain object.
	*/
	_clone() {
		return new ModelText(this.data, this.getAttributes());
	}
	/**
	* Creates a `Text` instance from given plain object (i.e. parsed JSON string).
	*
	* @param json Plain object to be converted to `Text`.
	* @returns `Text` instance created using given plain object.
	*/
	static fromJSON(json) {
		return new ModelText(json.data, json.attributes);
	}
};
ModelText.prototype.is = function(type) {
	return type === "$text" || type === "model:$text" || type === "text" || type === "model:text" || type === "node" || type === "model:node";
};

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* @module engine/model/documentselection
*/
const storePrefix = "selection:";
const ModelDocumentSelectionBase = /* #__PURE__ */ EmitterMixin(ModelTypeCheckable);
/**
* `ModelDocumentSelection` is a special selection which is used as the
* {@link module:engine/model/document~ModelDocument#selection document's selection}.
* There can be only one instance of `ModelDocumentSelection` per document.
*
* Document selection can only be changed by using the {@link module:engine/model/writer~ModelWriter} instance
* inside the {@link module:engine/model/model~Model#change `change()`} block, as it provides a secure way to modify model.
*
* `ModelDocumentSelection` is automatically updated upon changes in the {@link module:engine/model/document~ModelDocument document}
* to always contain valid ranges. Its attributes are inherited from the text unless set explicitly.
*
* Differences between {@link module:engine/model/selection~ModelSelection} and `ModelDocumentSelection` are:
* * there is always a range in `ModelDocumentSelection` - even if no ranges were added there is a "default range"
* present in the selection,
* * ranges added to this selection updates automatically when the document changes,
* * attributes of `ModelDocumentSelection` are updated automatically according to selection ranges.
*
* Since `ModelDocumentSelection` uses {@link module:engine/model/liverange~ModelLiveRange live ranges}
* and is updated when {@link module:engine/model/document~ModelDocument document}
* changes, it cannot be set on {@link module:engine/model/node~ModelNode nodes}
* that are inside {@link module:engine/model/documentfragment~ModelDocumentFragment document fragment}.
* If you need to represent a selection in document fragment,
* use {@link module:engine/model/selection~ModelSelection Selection class} instead.
*/
var ModelDocumentSelection = class extends ModelDocumentSelectionBase {
	/**
	* Selection used internally by that class (`ModelDocumentSelection` is a proxy to that selection).
	*/
	_selection;
	/**
	* Creates an empty live selection for given {@link module:engine/model/document~ModelDocument}.
	*
	* @param doc Document which owns this selection.
	*/
	constructor(doc) {
		super();
		this._selection = new LiveSelection(doc);
		this._selection.delegate("change:range").to(this);
		this._selection.delegate("change:attribute").to(this);
		this._selection.delegate("change:marker").to(this);
	}
	/**
	* Describes whether the selection is collapsed. Selection is collapsed when there is exactly one range which is
	* collapsed.
	*/
	get isCollapsed() {
		return this._selection.isCollapsed;
	}
	/**
	* Selection anchor. Anchor may be described as a position where the most recent part of the selection starts.
	* Together with {@link #focus} they define the direction of selection, which is important
	* when expanding/shrinking selection. Anchor is always {@link module:engine/model/range~ModelRange#start start} or
	* {@link module:engine/model/range~ModelRange#end end} position of the most recently added range.
	*
	* Is set to `null` if there are no ranges in selection.
	*
	* @see #focus
	*/
	get anchor() {
		return this._selection.anchor;
	}
	/**
	* Selection focus. Focus is a position where the selection ends.
	*
	* Is set to `null` if there are no ranges in selection.
	*
	* @see #anchor
	*/
	get focus() {
		return this._selection.focus;
	}
	/**
	* Number of ranges in selection.
	*/
	get rangeCount() {
		return this._selection.rangeCount;
	}
	/**
	* Describes whether `Documentselection` has own range(s) set, or if it is defaulted to
	* {@link module:engine/model/document~ModelDocument#_getDefaultRange document's default range}.
	*/
	get hasOwnRange() {
		return this._selection.hasOwnRange;
	}
	/**
	* Specifies whether the {@link #focus}
	* precedes {@link #anchor}.
	*
	* @readonly
	* @type {Boolean}
	*/
	get isBackward() {
		return this._selection.isBackward;
	}
	/**
	* Describes whether the gravity is overridden (using {@link module:engine/model/writer~ModelWriter#overrideSelectionGravity}) or not.
	*
	* Note that the gravity remains overridden as long as will not be restored the same number of times as it was overridden.
	*/
	get isGravityOverridden() {
		return this._selection.isGravityOverridden;
	}
	/**
	* A collection of selection {@link module:engine/model/markercollection~Marker markers}.
	* Marker is a selection marker when selection range is inside the marker range.
	*
	* **Note**: Only markers from {@link ~ModelDocumentSelection#observeMarkers observed markers groups} are collected.
	*/
	get markers() {
		return this._selection.markers;
	}
	/**
	* Used for the compatibility with the {@link module:engine/model/selection~ModelSelection#isEqual} method.
	*
	* @internal
	*/
	get _ranges() {
		return this._selection._ranges;
	}
	/**
	* Returns an iterable that iterates over copies of selection ranges.
	*/
	getRanges() {
		return this._selection.getRanges();
	}
	/**
	* Returns the first position in the selection.
	* First position is the position that {@link module:engine/model/position~ModelPosition#isBefore is before}
	* any other position in the selection.
	*
	* Returns `null` if there are no ranges in selection.
	*/
	getFirstPosition() {
		return this._selection.getFirstPosition();
	}
	/**
	* Returns the last position in the selection.
	* Last position is the position that {@link module:engine/model/position~ModelPosition#isAfter is after}
	* any other position in the selection.
	*
	* Returns `null` if there are no ranges in selection.
	*/
	getLastPosition() {
		return this._selection.getLastPosition();
	}
	/**
	* Returns a copy of the first range in the selection.
	* First range is the one which {@link module:engine/model/range~ModelRange#start start} position
	* {@link module:engine/model/position~ModelPosition#isBefore is before} start position of all other ranges
	* (not to confuse with the first range added to the selection).
	*
	* Returns `null` if there are no ranges in selection.
	*/
	getFirstRange() {
		return this._selection.getFirstRange();
	}
	/**
	* Returns a copy of the last range in the selection.
	* Last range is the one which {@link module:engine/model/range~ModelRange#end end} position
	* {@link module:engine/model/position~ModelPosition#isAfter is after} end position of all
	* other ranges (not to confuse with the range most recently added to the selection).
	*
	* Returns `null` if there are no ranges in selection.
	*/
	getLastRange() {
		return this._selection.getLastRange();
	}
	/**
	* Gets elements of type {@link module:engine/model/schema~ModelSchema#isBlock "block"} touched by the selection.
	*
	* This method's result can be used for example to apply block styling to all blocks covered by this selection.
	*
	* **Note:** `getSelectedBlocks()` returns blocks that are nested in other non-block elements
	* but will not return blocks nested in other blocks.
	*
	* In this case the function will return exactly all 3 paragraphs (note: `<blockQuote>` is not a block itself):
	*
	* ```
	* <paragraph>[a</paragraph>
	* <blockQuote>
	* 	<paragraph>b</paragraph>
	* </blockQuote>
	* <paragraph>c]d</paragraph>
	* ```
	*
	* In this case the paragraph will also be returned, despite the collapsed selection:
	*
	* ```
	* <paragraph>[]a</paragraph>
	* ```
	*
	* In such a scenario, however, only blocks A, B & E will be returned as blocks C & D are nested in block B:
	*
	* ```
	* [<blockA></blockA>
	* <blockB>
	* 	<blockC></blockC>
	* 	<blockD></blockD>
	* </blockB>
	* <blockE></blockE>]
	* ```
	*
	* If the selection is inside a block all the inner blocks (A & B) are returned:
	*
	* ```
	* <block>
	* 	<blockA>[a</blockA>
	* 	<blockB>b]</blockB>
	* </block>
	* ```
	*
	* **Special case**: If a selection ends at the beginning of a block, that block is not returned as from user perspective
	* this block wasn't selected. See [#984](https://github.com/ckeditor/ckeditor5-engine/issues/984) for more details.
	*
	* ```
	* <paragraph>[a</paragraph>
	* <paragraph>b</paragraph>
	* <paragraph>]c</paragraph> // this block will not be returned
	* ```
	*/
	getSelectedBlocks() {
		return this._selection.getSelectedBlocks();
	}
	/**
	* Returns the selected element. {@link module:engine/model/element~ModelElement Element} is considered as selected if there is only
	* one range in the selection, and that range contains exactly one element.
	* Returns `null` if there is no selected element.
	*/
	getSelectedElement() {
		return this._selection.getSelectedElement();
	}
	/**
	* Checks whether the selection contains the entire content of the given element. This means that selection must start
	* at a position {@link module:engine/model/position~ModelPosition#isTouching touching} the element's start and ends at position
	* touching the element's end.
	*
	* By default, this method will check whether the entire content of the selection's current root is selected.
	* Useful to check if e.g. the user has just pressed <kbd>Ctrl</kbd> + <kbd>A</kbd>.
	*/
	containsEntireContent(element) {
		return this._selection.containsEntireContent(element);
	}
	/**
	* Unbinds all events previously bound by document selection.
	*/
	destroy() {
		this._selection.destroy();
	}
	/**
	* Returns iterable that iterates over this selection's attribute keys.
	*/
	getAttributeKeys() {
		return this._selection.getAttributeKeys();
	}
	/**
	* Returns iterable that iterates over this selection's attributes.
	*
	* Attributes are returned as arrays containing two items. First one is attribute key and second is attribute value.
	* This format is accepted by native `Map` object and also can be passed in `Node` constructor.
	*/
	getAttributes() {
		return this._selection.getAttributes();
	}
	/**
	* Gets an attribute value for given key or `undefined` if that attribute is not set on the selection.
	*
	* @param key Key of attribute to look for.
	* @returns Attribute value or `undefined`.
	*/
	getAttribute(key) {
		return this._selection.getAttribute(key);
	}
	/**
	* Checks if the selection has an attribute for given key.
	*
	* @param key Key of attribute to check.
	* @returns `true` if attribute with given key is set on selection, `false` otherwise.
	*/
	hasAttribute(key) {
		return this._selection.hasAttribute(key);
	}
	/**
	* Refreshes selection attributes and markers according to the current position in the model.
	*/
	refresh() {
		this._selection.updateMarkers();
		this._selection._updateAttributes(false);
	}
	/**
	* Registers a marker group prefix or a marker name to be collected in the
	* {@link ~ModelDocumentSelection#markers selection markers collection}.
	*
	* See also {@link module:engine/model/markercollection~MarkerCollection#getMarkersGroup `MarkerCollection#getMarkersGroup()`}.
	*
	* @param prefixOrName The marker group prefix or marker name.
	*/
	observeMarkers(prefixOrName) {
		this._selection.observeMarkers(prefixOrName);
	}
	/**
	* Converts `DocumentSelection` to plain object and returns it.
	*
	* @returns `DocumentSelection` converted to plain object.
	*/
	toJSON() {
		return this._selection.toJSON();
	}
	/**
	* Moves {@link module:engine/model/documentselection~ModelDocumentSelection#focus} to the specified location.
	* Should be used only within the {@link module:engine/model/writer~ModelWriter#setSelectionFocus} method.
	*
	* The location can be specified in the same form as
	* {@link module:engine/model/writer~ModelWriter#createPositionAt writer.createPositionAt()} parameters.
	*
	* @see module:engine/model/writer~ModelWriter#setSelectionFocus
	* @internal
	* @param offset Offset or one of the flags. Used only when
	* first parameter is a {@link module:engine/model/item~ModelItem model item}.
	*/
	_setFocus(itemOrPosition, offset) {
		this._selection.setFocus(itemOrPosition, offset);
	}
	/**
	* Sets this selection's ranges and direction to the specified location based on the given
	* {@link module:engine/model/selection~ModelSelectable selectable}.
	* Should be used only within the {@link module:engine/model/writer~ModelWriter#setSelection} method.
	*
	* @see module:engine/model/writer~ModelWriter#setSelection
	* @internal
	*/
	_setTo(...args) {
		this._selection.setTo(...args);
	}
	/**
	* Sets attribute on the selection. If attribute with the same key already is set, it's value is overwritten.
	* Should be used only within the {@link module:engine/model/writer~ModelWriter#setSelectionAttribute} method.
	*
	* @see module:engine/model/writer~ModelWriter#setSelectionAttribute
	* @internal
	* @param key Key of the attribute to set.
	* @param value Attribute value.
	*/
	_setAttribute(key, value) {
		this._selection.setAttribute(key, value);
	}
	/**
	* Removes an attribute with given key from the selection.
	* If the given attribute was set on the selection, fires the {@link module:engine/model/selection~ModelSelection#event:change:range}
	* event with removed attribute key.
	* Should be used only within the {@link module:engine/model/writer~ModelWriter#removeSelectionAttribute} method.
	*
	* @see module:engine/model/writer~ModelWriter#removeSelectionAttribute
	* @internal
	* @param key Key of the attribute to remove.
	*/
	_removeAttribute(key) {
		this._selection.removeAttribute(key);
	}
	/**
	* Returns an iterable that iterates through all selection attributes stored in current selection's parent.
	*
	* @internal
	*/
	_getStoredAttributes() {
		return this._selection.getStoredAttributes();
	}
	/**
	* Temporarily changes the gravity of the selection from the left to the right.
	*
	* The gravity defines from which direction the selection inherits its attributes. If it's the default left
	* gravity, the selection (after being moved by the the user) inherits attributes from its left hand side.
	* This method allows to temporarily override this behavior by forcing the gravity to the right.
	*
	* It returns an unique identifier which is required to restore the gravity. It guarantees the symmetry
	* of the process.
	*
	* @see module:engine/model/writer~ModelWriter#overrideSelectionGravity
	* @internal
	* @returns The unique id which allows restoring the gravity.
	*/
	_overrideGravity() {
		return this._selection.overrideGravity();
	}
	/**
	* Restores the {@link ~ModelDocumentSelection#_overrideGravity overridden gravity}.
	*
	* Restoring the gravity is only possible using the unique identifier returned by
	* {@link ~ModelDocumentSelection#_overrideGravity}. Note that the gravity remains overridden as long as won't be restored
	* the same number of times it was overridden.
	*
	* @see module:engine/model/writer~ModelWriter#restoreSelectionGravity
	* @internal
	* @param uid The unique id returned by {@link #_overrideGravity}.
	*/
	_restoreGravity(uid) {
		this._selection.restoreGravity(uid);
	}
	/**
	* Generates and returns an attribute key for selection attributes store, basing on original attribute key.
	*
	* @internal
	* @param key Attribute key to convert.
	* @returns Converted attribute key, applicable for selection store.
	*/
	static _getStoreAttributeKey(key) {
		return storePrefix + key;
	}
	/**
	* Checks whether the given attribute key is an attribute stored on an element.
	*
	* @internal
	*/
	static _isStoreAttributeKey(key) {
		return key.startsWith(storePrefix);
	}
};
ModelDocumentSelection.prototype.is = function(type) {
	return type === "selection" || type == "model:selection" || type == "documentSelection" || type == "model:documentSelection";
};
/**
* `LiveSelection` is used internally by {@link module:engine/model/documentselection~ModelDocumentSelection}
* and shouldn't be used directly.
*
* `LiveSelection` is automatically updated upon changes in the {@link module:engine/model/document~ModelDocument document}
* to always contain valid ranges. Its attributes are inherited from the text unless set explicitly.
*
* Differences between {@link module:engine/model/selection~ModelSelection} and `LiveSelection` are:
* * there is always a range in `LiveSelection` - even if no ranges were added there is a "default range"
* present in the selection,
* * ranges added to this selection updates automatically when the document changes,
* * attributes of `LiveSelection` are updated automatically according to selection ranges.
*/
var LiveSelection = class extends ModelSelection {
	/**
	* List of selection markers.
	* Marker is a selection marker when selection range is inside the marker range.
	*/
	markers = new Collection({ idProperty: "name" });
	/**
	* Document which owns this selection.
	*/
	_model;
	/**
	* Document which owns this selection.
	*/
	_document;
	/**
	* Keeps mapping of attribute name to priority with which the attribute got modified (added/changed/removed)
	* last time. Possible values of priority are: `'low'` and `'normal'`.
	*
	* Priorities are used by internal `LiveSelection` mechanisms. All attributes set using `LiveSelection`
	* attributes API are set with `'normal'` priority.
	*/
	_attributePriority = /* @__PURE__ */ new Map();
	/**
	* Position to which the selection should be set if the last selection range was moved to the graveyard.
	*/
	_selectionRestorePosition = null;
	/**
	* Flag that informs whether the selection ranges have changed. It is changed on true when `LiveRange#change:range` event is fired.
	*/
	_hasChangedRange = false;
	/**
	* Each overriding gravity adds an UID to the set and each removal removes it.
	* Gravity is overridden when there's at least one UID in the set.
	* Gravity is restored when the set is empty.
	* This is to prevent conflicts when gravity is overridden by more than one feature at the same time.
	*/
	_overriddenGravityRegister = /* @__PURE__ */ new Set();
	/**
	* Prefixes of marker names that should affect `LiveSelection#markers` collection.
	*/
	_observedMarkers = /* @__PURE__ */ new Set();
	/**
	* Creates an empty live selection for given {@link module:engine/model/document~ModelDocument}.
	*
	* @param doc Document which owns this selection.
	*/
	constructor(doc) {
		super();
		this._model = doc.model;
		this._document = doc;
		this.listenTo(this._model, "applyOperation", (evt, args) => {
			const operation = args[0];
			if (!operation.isDocumentOperation || operation.type == "marker" || operation.type == "rename" || operation.type == "noop") return;
			if (this._ranges.length == 0 && this._selectionRestorePosition) this._fixGraveyardSelection(this._selectionRestorePosition);
			this._selectionRestorePosition = null;
			if (this._hasChangedRange) {
				this._hasChangedRange = false;
				this.fire("change:range", { directChange: false });
			}
		}, { priority: "lowest" });
		this.on("change:range", () => {
			this._validateSelectionRanges(this.getRanges());
		});
		this.listenTo(this._model.markers, "update", (evt, marker, oldRange, newRange) => {
			this._updateMarker(marker, newRange);
		});
		this.listenTo(this._document, "change", (evt, batch) => {
			clearAttributesStoredInElement(this._model, batch);
		});
	}
	get isCollapsed() {
		return this._ranges.length === 0 ? this._document._getDefaultRange().isCollapsed : super.isCollapsed;
	}
	get anchor() {
		return super.anchor || this._document._getDefaultRange().start;
	}
	get focus() {
		return super.focus || this._document._getDefaultRange().end;
	}
	get rangeCount() {
		return this._ranges.length ? this._ranges.length : 1;
	}
	/**
	* Describes whether `LiveSelection` has own range(s) set, or if it is defaulted to
	* {@link module:engine/model/document~ModelDocument#_getDefaultRange document's default range}.
	*/
	get hasOwnRange() {
		return this._ranges.length > 0;
	}
	/**
	* When set to `true` then selection attributes on node before the caret won't be taken
	* into consideration while updating selection attributes.
	*/
	get isGravityOverridden() {
		return !!this._overriddenGravityRegister.size;
	}
	/**
	* Unbinds all events previously bound by live selection.
	*/
	destroy() {
		for (let i = 0; i < this._ranges.length; i++) this._ranges[i].detach();
		this.stopListening();
	}
	*getRanges() {
		if (this._ranges.length) yield* super.getRanges();
		else yield this._document._getDefaultRange();
	}
	getFirstRange() {
		return super.getFirstRange() || this._document._getDefaultRange();
	}
	getLastRange() {
		return super.getLastRange() || this._document._getDefaultRange();
	}
	setTo(...args) {
		super.setTo(...args);
		this._updateAttributes(true);
		this.updateMarkers();
	}
	setFocus(itemOrPosition, offset) {
		super.setFocus(itemOrPosition, offset);
		this._updateAttributes(true);
		this.updateMarkers();
	}
	setAttribute(key, value) {
		if (this._setAttribute(key, value)) {
			const attributeKeys = [key];
			this.fire("change:attribute", {
				attributeKeys,
				directChange: true
			});
		}
	}
	removeAttribute(key) {
		if (this._removeAttribute(key)) {
			const attributeKeys = [key];
			this.fire("change:attribute", {
				attributeKeys,
				directChange: true
			});
		}
	}
	overrideGravity() {
		const overrideUid = uid();
		this._overriddenGravityRegister.add(overrideUid);
		if (this._overriddenGravityRegister.size === 1) this._updateAttributes(true);
		return overrideUid;
	}
	restoreGravity(uid) {
		if (!this._overriddenGravityRegister.has(uid))
 /**
		* Restoring gravity for an unknown UID is not possible. Make sure you are using a correct
		* UID obtained from the {@link module:engine/model/writer~ModelWriter#overrideSelectionGravity} to restore.
		*
		* @error document-selection-gravity-wrong-restore
		* @param {string} uid The unique identifier returned by
		* {@link module:engine/model/documentselection~ModelDocumentSelection#_overrideGravity}.
		*/
		throw new CKEditorError("document-selection-gravity-wrong-restore", this, { uid });
		this._overriddenGravityRegister.delete(uid);
		if (!this.isGravityOverridden) this._updateAttributes(true);
	}
	observeMarkers(prefixOrName) {
		this._observedMarkers.add(prefixOrName);
		this.updateMarkers();
	}
	_replaceAllRanges(ranges) {
		this._validateSelectionRanges(ranges);
		super._replaceAllRanges(ranges);
	}
	_popRange() {
		this._ranges.pop().detach();
	}
	_pushRange(range) {
		const liveRange = this._prepareRange(range);
		if (liveRange) this._ranges.push(liveRange);
	}
	/**
	* Converts `LiveSelection` to plain object and returns it.
	*
	* @returns `LiveSelection` converted to plain object.
	*/
	toJSON() {
		const json = super.toJSON();
		if (this.markers.length) json.markers = this.markers.map((marker) => marker.toJSON());
		return json;
	}
	_validateSelectionRanges(ranges) {
		for (const range of ranges) if (!this._document._validateSelectionRange(range))
 /**
		* Range from {@link module:engine/model/documentselection~ModelDocumentSelection document selection}
		* starts or ends at incorrect position.
		*
		* @error document-selection-wrong-position
		* @param {module:engine/model/range~ModelRange} range The invalid range.
		*/
		throw new CKEditorError("document-selection-wrong-position", this, { range });
	}
	/**
	* Prepares given range to be added to selection. Checks if it is correct,
	* converts it to {@link module:engine/model/liverange~ModelLiveRange ModelLiveRange}
	* and sets listeners listening to the range's change event.
	*/
	_prepareRange(range) {
		this._checkRange(range);
		if (range.root == this._document.graveyard) return;
		const liveRange = ModelLiveRange.fromRange(range);
		liveRange.on("change:range", (evt, oldRange, data) => {
			this._hasChangedRange = true;
			if (liveRange.root == this._document.graveyard) {
				this._selectionRestorePosition = data.deletionPosition;
				const index = this._ranges.indexOf(liveRange);
				this._ranges.splice(index, 1);
				liveRange.detach();
			}
		});
		return liveRange;
	}
	updateMarkers() {
		if (!this._observedMarkers.size) return;
		const markers = [];
		let changed = false;
		for (const marker of this._model.markers) {
			const markerGroup = marker.name.split(":", 1)[0];
			if (!this._observedMarkers.has(markerGroup)) continue;
			const markerRange = marker.getRange();
			for (const selectionRange of this.getRanges()) if (markerRange.containsRange(selectionRange, !selectionRange.isCollapsed)) markers.push(marker);
		}
		const oldMarkers = Array.from(this.markers);
		for (const marker of markers) if (!this.markers.has(marker)) {
			this.markers.add(marker);
			changed = true;
		}
		for (const marker of Array.from(this.markers)) if (!markers.includes(marker)) {
			this.markers.remove(marker);
			changed = true;
		}
		if (changed) this.fire("change:marker", {
			oldMarkers,
			directChange: false
		});
	}
	_updateMarker(marker, markerRange) {
		const markerGroup = marker.name.split(":", 1)[0];
		if (!this._observedMarkers.has(markerGroup)) return;
		let changed = false;
		const oldMarkers = Array.from(this.markers);
		const hasMarker = this.markers.has(marker);
		if (!markerRange) {
			if (hasMarker) {
				this.markers.remove(marker);
				changed = true;
			}
		} else {
			let contained = false;
			for (const selectionRange of this.getRanges()) if (markerRange.containsRange(selectionRange, !selectionRange.isCollapsed)) {
				contained = true;
				break;
			}
			if (contained && !hasMarker) {
				this.markers.add(marker);
				changed = true;
			} else if (!contained && hasMarker) {
				this.markers.remove(marker);
				changed = true;
			}
		}
		if (changed) this.fire("change:marker", {
			oldMarkers,
			directChange: false
		});
	}
	/**
	* Updates this selection attributes according to its ranges and the {@link module:engine/model/document~ModelDocument model document}.
	*/
	_updateAttributes(clearAll) {
		const newAttributes = toMap(this._getSurroundingAttributes());
		const oldAttributes = toMap(this.getAttributes());
		if (clearAll) {
			this._attributePriority = /* @__PURE__ */ new Map();
			this._attrs = /* @__PURE__ */ new Map();
		} else for (const [key, priority] of this._attributePriority) if (priority == "low") {
			this._attrs.delete(key);
			this._attributePriority.delete(key);
		}
		this._setAttributesTo(newAttributes);
		const changed = [];
		for (const [newKey, newValue] of this.getAttributes()) if (!oldAttributes.has(newKey) || oldAttributes.get(newKey) !== newValue) changed.push(newKey);
		for (const [oldKey] of oldAttributes) if (!this.hasAttribute(oldKey)) changed.push(oldKey);
		if (changed.length > 0) this.fire("change:attribute", {
			attributeKeys: changed,
			directChange: false
		});
	}
	/**
	* Internal method for setting `LiveSelection` attribute. Supports attribute priorities (through `directChange`
	* parameter).
	*/
	_setAttribute(key, value, directChange = true) {
		const priority = directChange ? "normal" : "low";
		if (priority == "low" && this._attributePriority.get(key) == "normal") return false;
		if (super.getAttribute(key) === value) return false;
		this._attrs.set(key, value);
		this._attributePriority.set(key, priority);
		return true;
	}
	/**
	* Internal method for removing `LiveSelection` attribute. Supports attribute priorities (through `directChange`
	* parameter).
	*
	* NOTE: Even if attribute is not present in the selection but is provided to this method, it's priority will
	* be changed according to `directChange` parameter.
	*/
	_removeAttribute(key, directChange = true) {
		const priority = directChange ? "normal" : "low";
		if (priority == "low" && this._attributePriority.get(key) == "normal") return false;
		this._attributePriority.set(key, priority);
		if (!super.hasAttribute(key)) return false;
		this._attrs.delete(key);
		return true;
	}
	/**
	* Internal method for setting multiple `LiveSelection` attributes. Supports attribute priorities (through
	* `directChange` parameter).
	*/
	_setAttributesTo(attrs) {
		const changed = /* @__PURE__ */ new Set();
		for (const [oldKey, oldValue] of this.getAttributes()) {
			if (attrs.get(oldKey) === oldValue) continue;
			this._removeAttribute(oldKey, false);
		}
		for (const [key, value] of attrs) if (this._setAttribute(key, value, false)) changed.add(key);
		return changed;
	}
	/**
	* Returns an iterable that iterates through all selection attributes stored in current selection's parent.
	*/
	*getStoredAttributes() {
		const selectionParent = this.getFirstPosition().parent;
		if (this.isCollapsed && selectionParent.isEmpty) {
			for (const key of selectionParent.getAttributeKeys()) if (key.startsWith(storePrefix)) yield [key.substr(10), selectionParent.getAttribute(key)];
		}
	}
	/**
	* Checks model text nodes that are closest to the selection's first position and returns attributes of first
	* found element. If there are no text nodes in selection's first position parent, it returns selection
	* attributes stored in that parent.
	*/
	_getSurroundingAttributes() {
		const position = this.getFirstPosition();
		const schema = this._model.schema;
		if (position.root.rootName == "$graveyard") return null;
		let attrs = null;
		if (!this.isCollapsed) {
			const range = this.getFirstRange();
			for (const value of range) {
				if (value.item.is("element") && schema.isObject(value.item)) {
					attrs = getTextAttributes(value.item, schema);
					break;
				}
				if (value.type == "text") {
					attrs = value.item.getAttributes();
					break;
				}
			}
		} else {
			const nodeBefore = position.textNode ? position.textNode : position.nodeBefore;
			const nodeAfter = position.textNode ? position.textNode : position.nodeAfter;
			if (!this.isGravityOverridden) attrs = getTextAttributes(nodeBefore, schema);
			if (!attrs) attrs = getTextAttributes(nodeAfter, schema);
			if (!this.isGravityOverridden && !attrs) attrs = getTextAttributes(nodeBefore, schema, "backward");
			if (!attrs) attrs = getTextAttributes(nodeAfter, schema, "forward");
			if (!attrs) attrs = this.getStoredAttributes();
		}
		return attrs;
	}
	/**
	* Fixes the selection after all its ranges got removed.
	* @param deletionPosition Position where the deletion happened.
	*/
	_fixGraveyardSelection(deletionPosition) {
		const selectionRange = this._model.schema.getNearestSelectionRange(deletionPosition);
		if (selectionRange) this._pushRange(selectionRange);
	}
};
/**
* Helper function for {@link module:engine/model/liveselection~LiveSelection#_updateAttributes}.
*
* It checks if the passed model node is a text node and, if so, returns its attributes.
* If not, it checks if item is an inline element and does the same. Otherwise, it returns `null`.
*/
function getTextAttributes(startNode, schema, scan = "self") {
	if (!startNode) return null;
	for (const node of siblingNodes(startNode, scan)) {
		if (!node) return null;
		if (node instanceof ModelText) return node.getAttributes();
		if (!schema.isInline(node)) continue;
		const isObject = schema.isObject(node);
		const attributes = [];
		for (const [key, value] of node.getAttributes()) if (schema.checkAttribute("$text", key) && (!isObject || schema.getAttributeProperties(key).copyFromObject !== false)) attributes.push([key, value]);
		return attributes;
	}
	return null;
}
/**
* Returns sibling nodes from the given start node.
*/
function* siblingNodes(startNode, scan) {
	if (scan == "self") yield startNode;
	else {
		let node = startNode;
		while (node) {
			node = scan == "backward" ? node.previousSibling : node.nextSibling;
			yield node;
		}
	}
	return null;
}
/**
* Removes selection attributes from element which is not empty anymore.
*/
function clearAttributesStoredInElement(model, batch) {
	const differ = model.document.differ;
	for (const entry of differ.getChanges()) {
		if (entry.type != "insert") continue;
		const changeParent = entry.position.parent;
		if (entry.length === changeParent.maxOffset) model.enqueueChange(batch, (writer) => {
			const storedAttributes = Array.from(changeParent.getAttributeKeys()).filter((key) => key.startsWith(storePrefix));
			for (const key of storedAttributes) writer.removeAttribute(key, changeParent);
		});
	}
}

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* @module engine/model/nodelist
*/
/**
* Provides an interface to operate on a list of {@link module:engine/model/node~ModelNode nodes}. `NodeList` is used internally
* in classes like {@link module:engine/model/element~ModelElement Element}
* or {@link module:engine/model/documentfragment~ModelDocumentFragment ModelDocumentFragment}.
*/
var ModelNodeList = class {
	/**
	* Nodes contained in this node list.
	*/
	_nodes = [];
	/**
	* This array maps numbers (offsets) to node that is placed at that offset.
	*
	* This array is similar to `_nodes` with the difference that one node may occupy multiple consecutive items in the array.
	*
	* This array is needed to quickly retrieve a node that is placed at given offset.
	*/
	_offsetToNode = [];
	/**
	* Creates a node list.
	*
	* @internal
	* @param nodes Nodes contained in this node list.
	*/
	constructor(nodes) {
		if (nodes) this._insertNodes(0, nodes);
	}
	/**
	* Iterable interface.
	*
	* Iterates over all nodes contained inside this node list.
	*/
	[Symbol.iterator]() {
		return this._nodes[Symbol.iterator]();
	}
	/**
	* Number of nodes contained inside this node list.
	*/
	get length() {
		return this._nodes.length;
	}
	/**
	* Sum of {@link module:engine/model/node~ModelNode#offsetSize offset sizes} of all nodes contained inside this node list.
	*/
	get maxOffset() {
		return this._offsetToNode.length;
	}
	/**
	* Gets the node at the given index. Returns `null` if incorrect index was passed.
	*/
	getNode(index) {
		return this._nodes[index] || null;
	}
	/**
	* Gets the node at the given offset. Returns `null` if incorrect offset was passed.
	*/
	getNodeAtOffset(offset) {
		return this._offsetToNode[offset] || null;
	}
	/**
	* Returns an index of the given node or `null` if given node does not have a parent.
	*
	* This is an alias to {@link module:engine/model/node~ModelNode#index}.
	*/
	getNodeIndex(node) {
		return node.index;
	}
	/**
	* Returns the offset at which given node is placed in its parent or `null` if given node does not have a parent.
	*
	* This is an alias to {@link module:engine/model/node~ModelNode#startOffset}.
	*/
	getNodeStartOffset(node) {
		return node.startOffset;
	}
	/**
	* Converts index to offset in node list.
	*
	* Throws {@link module:utils/ckeditorerror~CKEditorError CKEditorError} `model-nodelist-index-out-of-bounds` if given index is less
	* than `0` or more than {@link #length}.
	*/
	indexToOffset(index) {
		if (index == this._nodes.length) return this.maxOffset;
		const node = this._nodes[index];
		if (!node)
 /**
		* Given index cannot be found in the node list.
		*
		* @error model-nodelist-index-out-of-bounds
		*/
		throw new CKEditorError("model-nodelist-index-out-of-bounds", this);
		return this.getNodeStartOffset(node);
	}
	/**
	* Converts offset in node list to index.
	*
	* Throws {@link module:utils/ckeditorerror~CKEditorError CKEditorError} `model-nodelist-offset-out-of-bounds` if given offset is less
	* than `0` or more than {@link #maxOffset}.
	*/
	offsetToIndex(offset) {
		if (offset == this._offsetToNode.length) return this._nodes.length;
		const node = this._offsetToNode[offset];
		if (!node)
 /**
		* Given offset cannot be found in the node list.
		*
		* @error model-nodelist-offset-out-of-bounds
		* @param {number} offset The offset value.
		* @param {module:engine/model/nodelist~ModelNodeList} nodeList Stringified node list.
		*/
		throw new CKEditorError("model-nodelist-offset-out-of-bounds", this, {
			offset,
			nodeList: this
		});
		return this.getNodeIndex(node);
	}
	/**
	* Inserts given nodes at given index.
	*
	* @internal
	* @param index Index at which nodes should be inserted.
	* @param nodes Nodes to be inserted.
	*/
	_insertNodes(index, nodes) {
		const nodesArray = [];
		for (const node of nodes) {
			if (!(node instanceof ModelNode))
 /**
			* Trying to insert an object which is not a Node instance.
			*
			* @error model-nodelist-insertnodes-not-node
			*/
			throw new CKEditorError("model-nodelist-insertnodes-not-node", this);
			nodesArray.push(node);
		}
		let offset = this.indexToOffset(index);
		spliceArray(this._nodes, nodesArray, index);
		spliceArray(this._offsetToNode, makeOffsetsArray(nodesArray), offset);
		for (let i = index; i < this._nodes.length; i++) {
			this._nodes[i]._index = i;
			this._nodes[i]._startOffset = offset;
			offset += this._nodes[i].offsetSize;
		}
	}
	/**
	* Removes one or more nodes starting at the given index.
	*
	* @internal
	* @param indexStart Index of the first node to remove.
	* @param howMany Number of nodes to remove.
	* @returns Array containing removed nodes.
	*/
	_removeNodes(indexStart, howMany = 1) {
		if (howMany == 0) return [];
		let offset = this.indexToOffset(indexStart);
		const nodes = this._nodes.splice(indexStart, howMany);
		const lastNode = nodes[nodes.length - 1];
		const removedOffsetSum = lastNode.startOffset + lastNode.offsetSize - offset;
		this._offsetToNode.splice(offset, removedOffsetSum);
		for (const node of nodes) {
			node._index = null;
			node._startOffset = null;
		}
		for (let i = indexStart; i < this._nodes.length; i++) {
			this._nodes[i]._index = i;
			this._nodes[i]._startOffset = offset;
			offset += this._nodes[i].offsetSize;
		}
		return nodes;
	}
	/**
	* Removes children nodes provided as an array. These nodes do not need to be direct siblings.
	*
	* This method is faster than removing nodes one by one, as it recalculates offsets only once.
	*
	* @internal
	* @param nodes Array of nodes.
	*/
	_removeNodesArray(nodes) {
		if (nodes.length == 0) return;
		for (const node of nodes) {
			node._index = null;
			node._startOffset = null;
		}
		this._nodes = this._nodes.filter((node) => node.index !== null);
		this._offsetToNode = this._offsetToNode.filter((node) => node.index !== null);
		let offset = 0;
		for (let i = 0; i < this._nodes.length; i++) {
			this._nodes[i]._index = i;
			this._nodes[i]._startOffset = offset;
			offset += this._nodes[i].offsetSize;
		}
	}
	/**
	* Converts `NodeList` instance to an array containing nodes that were inserted in the node list. Nodes
	* are also converted to their plain object representation.
	*
	* @returns `NodeList` instance converted to `Array`.
	*/
	toJSON() {
		return this._nodes.map((node) => node.toJSON());
	}
};
/**
* Creates an array of nodes in the format as in {@link module:engine/model/nodelist~ModelNodeList#_offsetToNode}, i.e. one node will
* occupy multiple items if its offset size is greater than one.
*/
function makeOffsetsArray(nodes) {
	const offsets = [];
	let index = 0;
	for (const node of nodes) for (let i = 0; i < node.offsetSize; i++) offsets[index++] = node;
	return offsets;
}

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* @module engine/model/element
*/
/**
* Model element. Type of {@link module:engine/model/node~ModelNode node} that has a
* {@link module:engine/model/element~ModelElement#name name} and {@link module:engine/model/element~ModelElement#getChildren child nodes}.
*
* **Important**: see {@link module:engine/model/node~ModelNode} to read about restrictions using `Element` and `Node` API.
*/
var ModelElement = class ModelElement extends ModelNode {
	/**
	* Element name.
	*/
	name;
	/**
	* List of children nodes.
	*/
	_children = new ModelNodeList();
	/**
	* Creates a model element.
	*
	* **Note:** Constructor of this class shouldn't be used directly in the code.
	* Use the {@link module:engine/model/writer~ModelWriter#createElement} method instead.
	*
	* @internal
	* @param name Element's name.
	* @param attrs Element's attributes. See {@link module:utils/tomap~toMap} for a list of accepted values.
	* @param children One or more nodes to be inserted as children of created element.
	*/
	constructor(name, attrs, children) {
		super(attrs);
		this.name = name;
		if (children) this._insertChild(0, children);
	}
	/**
	* Number of this element's children.
	*/
	get childCount() {
		return this._children.length;
	}
	/**
	* Sum of {@link module:engine/model/node~ModelNode#offsetSize offset sizes} of all of this element's children.
	*/
	get maxOffset() {
		return this._children.maxOffset;
	}
	/**
	* Is `true` if there are no nodes inside this element, `false` otherwise.
	*/
	get isEmpty() {
		return this.childCount === 0;
	}
	/**
	* Gets the child at the given index. Returns `null` if incorrect index was passed.
	*
	* @param index Index in this element.
	* @returns Child node.
	*/
	getChild(index) {
		return this._children.getNode(index);
	}
	/**
	* Gets the child at the given offset. Returns `null` if incorrect index was passed.
	*
	* @param offset Offset in this element.
	* @returns Child node.
	*/
	getChildAtOffset(offset) {
		return this._children.getNodeAtOffset(offset);
	}
	/**
	* Returns an iterator that iterates over all of this element's children.
	*/
	getChildren() {
		return this._children[Symbol.iterator]();
	}
	/**
	* Returns an index of the given child node. Returns `null` if given node is not a child of this element.
	*
	* @param node Child node to look for.
	* @returns Child node's index in this element.
	*/
	getChildIndex(node) {
		return this._children.getNodeIndex(node);
	}
	/**
	* Returns the starting offset of given child. Starting offset is equal to the sum of
	* {@link module:engine/model/node~ModelNode#offsetSize offset sizes} of all node's siblings that are before it. Returns `null` if
	* given node is not a child of this element.
	*
	* @param node Child node to look for.
	* @returns Child node's starting offset.
	*/
	getChildStartOffset(node) {
		return this._children.getNodeStartOffset(node);
	}
	/**
	* Returns index of a node that occupies given offset. If given offset is too low, returns `0`. If given offset is
	* too high, returns {@link module:engine/model/element~ModelElement#getChildIndex index after last child}.
	*
	* ```ts
	* const textNode = new Text( 'foo' );
	* const pElement = new Element( 'p' );
	* const divElement = new Element( [ textNode, pElement ] );
	* divElement.offsetToIndex( -1 ); // Returns 0, because offset is too low.
	* divElement.offsetToIndex( 0 ); // Returns 0, because offset 0 is taken by `textNode` which is at index 0.
	* divElement.offsetToIndex( 1 ); // Returns 0, because `textNode` has `offsetSize` equal to 3, so it occupies offset 1 too.
	* divElement.offsetToIndex( 2 ); // Returns 0.
	* divElement.offsetToIndex( 3 ); // Returns 1.
	* divElement.offsetToIndex( 4 ); // Returns 2. There are no nodes at offset 4, so last available index is returned.
	* ```
	*/
	offsetToIndex(offset) {
		return this._children.offsetToIndex(offset);
	}
	/**
	* Returns a descendant node by its path relative to this element.
	*
	* ```ts
	* // <this>a<b>c</b></this>
	* this.getNodeByPath( [ 0 ] );     // -> "a"
	* this.getNodeByPath( [ 1 ] );     // -> <b>
	* this.getNodeByPath( [ 1, 0 ] );  // -> "c"
	* ```
	*
	* @param relativePath Path of the node to find, relative to this element.
	*/
	getNodeByPath(relativePath) {
		let node = this;
		for (const offset of relativePath) node = node.getChildAtOffset(offset);
		return node;
	}
	/**
	* Returns the parent element of the given name. Returns null if the element is not inside the desired parent.
	*
	* @param parentName The name of the parent element to find.
	* @param options Options object.
	* @param options.includeSelf When set to `true` this node will be also included while searching.
	*/
	findAncestor(parentName, options = {}) {
		let parent = options.includeSelf ? this : this.parent;
		while (parent) {
			if (parent.name === parentName) return parent;
			parent = parent.parent;
		}
		return null;
	}
	/**
	* Converts `Element` instance to plain object and returns it. Takes care of converting all of this element's children.
	*
	* @returns `Element` instance converted to plain object.
	*/
	toJSON() {
		const json = super.toJSON();
		json.name = this.name;
		if (this._children.length > 0) {
			json.children = [];
			for (const node of this._children) json.children.push(node.toJSON());
		}
		return json;
	}
	/**
	* Creates a copy of this element and returns it. Created element has the same name and attributes as the original element.
	* If clone is deep, the original element's children are also cloned. If not, then empty element is returned.
	*
	* @internal
	* @param deep If set to `true` clones element and all its children recursively. When set to `false`,
	* element will be cloned without any child.
	*/
	_clone(deep = false) {
		const children = deep ? cloneNodes(this._children) : void 0;
		return new ModelElement(this.name, this.getAttributes(), children);
	}
	/**
	* {@link module:engine/model/element~ModelElement#_insertChild Inserts} one or more nodes at the end of this element.
	*
	* @see module:engine/model/writer~ModelWriter#append
	* @internal
	* @param nodes Nodes to be inserted.
	*/
	_appendChild(nodes) {
		this._insertChild(this.childCount, nodes);
	}
	/**
	* Inserts one or more nodes at the given index and sets {@link module:engine/model/node~ModelNode#parent parent} of these nodes
	* to this element.
	*
	* @see module:engine/model/writer~ModelWriter#insert
	* @internal
	* @param index Index at which nodes should be inserted.
	* @param items Items to be inserted.
	*/
	_insertChild(index, items) {
		const nodes = normalize$1(items);
		for (const node of nodes) {
			if (node.parent !== null) node._remove();
			node.parent = this;
		}
		this._children._insertNodes(index, nodes);
	}
	/**
	* Removes one or more nodes starting at the given index and sets
	* {@link module:engine/model/node~ModelNode#parent parent} of these nodes to `null`.
	*
	* @see module:engine/model/writer~ModelWriter#remove
	* @internal
	* @param index Index of the first node to remove.
	* @param howMany Number of nodes to remove.
	* @returns Array containing removed nodes.
	*/
	_removeChildren(index, howMany = 1) {
		const nodes = this._children._removeNodes(index, howMany);
		for (const node of nodes) node.parent = null;
		return nodes;
	}
	/**
	* Removes children nodes provided as an array and sets
	* the {@link module:engine/model/node~ModelNode#parent parent} of these nodes to `null`.
	*
	* These nodes do not need to be direct siblings.
	*
	* This method is faster than removing nodes one by one, as it recalculates offsets only once.
	*
	* @internal
	* @param nodes Array of nodes.
	*/
	_removeChildrenArray(nodes) {
		this._children._removeNodesArray(nodes);
		for (const node of nodes) node.parent = null;
	}
	/**
	* Creates an `Element` instance from given plain object (i.e. parsed JSON string).
	* Converts `Element` children to proper nodes.
	*
	* @param json Plain object to be converted to `Element`.
	* @returns `Element` instance created using given plain object.
	*/
	static fromJSON(json) {
		let children;
		if (json.children) {
			children = [];
			for (const child of json.children) if (child.name) children.push(ModelElement.fromJSON(child));
			else children.push(ModelText.fromJSON(child));
		}
		return new ModelElement(json.name, json.attributes, children);
	}
};
ModelElement.prototype.is = function(type, name) {
	if (!name) return type === "element" || type === "model:element" || type === "node" || type === "model:node";
	return name === this.name && (type === "element" || type === "model:element");
};
/**
* Converts strings to Text and non-iterables to arrays.
*/
function normalize$1(nodes) {
	if (typeof nodes == "string") return [new ModelText(nodes)];
	if (!isIterable(nodes)) nodes = [nodes];
	const normalizedNodes = [];
	for (const node of nodes) if (typeof node == "string") normalizedNodes.push(new ModelText(node));
	else if (node instanceof ModelTextProxy) normalizedNodes.push(new ModelText(node.data, node.getAttributes()));
	else normalizedNodes.push(node);
	return normalizedNodes;
}
function cloneNodes(nodes) {
	const clonedNodes = [];
	for (const node of nodes) clonedNodes.push(node._clone(true));
	return clonedNodes;
}

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* @module engine/conversion/conversionhelpers
*/
/**
* Base class for conversion helpers.
*/
var ConversionHelpers = class {
	_dispatchers;
	/**
	* Creates a conversion helpers instance.
	*/
	constructor(dispatchers) {
		this._dispatchers = dispatchers;
	}
	/**
	* Registers a conversion helper.
	*
	* **Note**: See full usage example in the `{@link module:engine/conversion/conversion~Conversion#for conversion.for()}`
	* method description.
	*
	* @param conversionHelper The function to be called on event.
	*/
	add(conversionHelper) {
		for (const dispatcher of this._dispatchers) conversionHelper(dispatcher);
		return this;
	}
};

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* Contains downcast (model-to-view) converters for {@link module:engine/conversion/downcastdispatcher~DowncastDispatcher}.
*
* @module engine/conversion/downcasthelpers
*/
/**
* Downcast conversion helper functions.
*
* Learn more about {@glink framework/deep-dive/conversion/downcast downcast helpers}.
*
* @extends module:engine/conversion/conversionhelpers~ConversionHelpers
*/
var DowncastHelpers = class extends ConversionHelpers {
	/**
	* Model element to view element conversion helper.
	*
	* This conversion results in creating a view element. For example, model `<paragraph>Foo</paragraph>` becomes `<p>Foo</p>` in the view.
	*
	* ```ts
	* editor.conversion.for( 'downcast' ).elementToElement( {
	* 	model: 'paragraph',
	* 	view: 'p'
	* } );
	*
	* editor.conversion.for( 'downcast' ).elementToElement( {
	* 	model: 'paragraph',
	* 	view: 'div',
	* 	converterPriority: 'high'
	* } );
	*
	* editor.conversion.for( 'downcast' ).elementToElement( {
	* 	model: 'fancyParagraph',
	* 	view: {
	* 		name: 'p',
	* 		classes: 'fancy'
	* 	}
	* } );
	*
	* editor.conversion.for( 'downcast' ).elementToElement( {
	* 	model: 'heading',
	* 	view: ( modelElement, conversionApi ) => {
	* 		const { writer } = conversionApi;
	*
	* 		return writer.createContainerElement( 'h' + modelElement.getAttribute( 'level' ) );
	* 	}
	* } );
	* ```
	*
	* The element-to-element conversion supports the reconversion mechanism. It can be enabled by using either the `attributes` or
	* the `children` props on a model description. You will find a couple examples below.
	*
	* In order to reconvert an element if any of its direct children have been added or removed, use the `children` property on a `model`
	* description. For example, this model:
	*
	* ```xml
	* <box>
	* 	<paragraph>Some text.</paragraph>
	* </box>
	* ```
	*
	* will be converted into this structure in the view:
	*
	* ```html
	* <div class="box" data-type="single">
	* 	<p>Some text.</p>
	* </div>
	* ```
	*
	* But if more items were inserted in the model:
	*
	* ```xml
	* <box>
	* 	<paragraph>Some text.</paragraph>
	* 	<paragraph>Other item.</paragraph>
	* </box>
	* ```
	*
	* it will be converted into this structure in the view (note the element `data-type` change):
	*
	* ```html
	* <div class="box" data-type="multiple">
	* 	<p>Some text.</p>
	* 	<p>Other item.</p>
	* </div>
	* ```
	*
	* Such a converter would look like this (note that the `paragraph` elements are converted separately):
	*
	* ```ts
	* editor.conversion.for( 'downcast' ).elementToElement( {
	* 	model: {
	* 		name: 'box',
	* 		children: true
	* 	},
	* 	view: ( modelElement, conversionApi ) => {
	* 		const { writer } = conversionApi;
	*
	* 		return writer.createContainerElement( 'div', {
	* 			class: 'box',
	* 			'data-type': modelElement.childCount == 1 ? 'single' : 'multiple'
	* 		} );
	* 	}
	* } );
	* ```
	*
	* In order to reconvert element if any of its attributes have been updated, use the `attributes` property on a `model`
	* description. For example, this model:
	*
	* ```xml
	* <heading level="2">Some text.</heading>
	* ```
	*
	* will be converted into this structure in the view:
	*
	* ```html
	* <h2>Some text.</h2>
	* ```
	*
	* But if the `heading` element's `level` attribute has been updated to `3` for example, then
	* it will be converted into this structure in the view:
	*
	* ```html
	* <h3>Some text.</h3>
	* ```
	*
	* Such a converter would look as follows:
	*
	* ```ts
	* editor.conversion.for( 'downcast' ).elementToElement( {
	* 	model: {
	* 		name: 'heading',
	* 		attributes: 'level'
	* 	},
	* 	view: ( modelElement, conversionApi ) => {
	* 		const { writer } = conversionApi;
	*
	* 		return writer.createContainerElement( 'h' + modelElement.getAttribute( 'level' ) );
	* 	}
	* } );
	* ```
	*
	* See {@link module:engine/conversion/conversion~Conversion#for `conversion.for()`} to learn how to add a converter
	* to the conversion process.
	*
	* You can read more about the element-to-element conversion in the
	* {@glink framework/deep-dive/conversion/downcast downcast conversion} guide.
	*
	* @param config Conversion configuration.
	* @param config.model The description or a name of the model element to convert.
	* @param config.view A view element definition or a function that takes the model element and
	* {@link module:engine/conversion/downcastdispatcher~DowncastConversionApi downcast conversion API}
	* as parameters and returns a view container element.
	* @param config.converterPriority Converter priority.
	*/
	elementToElement(config) {
		return this.add(downcastElementToElement(config));
	}
	/**
	* The model element to view structure (several elements) conversion helper.
	*
	* This conversion results in creating a view structure with one or more slots defined for the child nodes.
	* For example, a model `<table>` may become this structure in the view:
	*
	* ```html
	* <figure class="table">
	* 	<table>
	* 		<tbody>${ slot for table rows }</tbody>
	* 	</table>
	* </figure>
	* ```
	*
	* The children of the model's `<table>` element will be inserted into the `<tbody>` element.
	* If the `elementToElement()` helper was used, the children would be inserted into the `<figure>`.
	*
	* Imagine a table feature where for this model structure:
	*
	* ```xml
	* <table headingRows="1">
	* 	<tableRow> ... table cells 1 ... </tableRow>
	* 	<tableRow> ... table cells 2 ... </tableRow>
	* 	<tableRow> ... table cells 3 ... </tableRow>
	* 	<caption>Caption text</caption>
	* </table>
	* ```
	*
	* we want to generate this view structure:
	*
	* ```html
	* <figure class="table">
	* 	<table>
	* 		<thead>
	* 			<tr> ... table cells 1 ... </tr>
	* 		</thead>
	* 		<tbody>
	* 			<tr> ... table cells 2 ... </tr>
	* 			<tr> ... table cells 3 ... </tr>
	* 		</tbody>
	* 	</table>
	* 	<figcaption>Caption text</figcaption>
	* </figure>
	* ```
	*
	* The converter has to take the `headingRows` attribute into consideration when allocating the `<tableRow>` elements
	* into the `<tbody>` and `<thead>` elements. Hence, we need two slots and need to define proper filter callbacks for them.
	*
	* Additionally, all elements other than `<tableRow>` should be placed outside the `<table>` tag.
	* In the example above, this will handle the table caption.
	*
	* Such a converter would look like this:
	*
	* ```ts
	* editor.conversion.for( 'downcast' ).elementToStructure( {
	* 	model: {
	* 		name: 'table',
	* 		attributes: [ 'headingRows' ]
	* 	},
	* 	view: ( modelElement, conversionApi ) => {
	* 		const { writer } = conversionApi;
	*
	* 		const figureElement = writer.createContainerElement( 'figure', { class: 'table' } );
	* 		const tableElement = writer.createContainerElement( 'table' );
	*
	* 		writer.insert( writer.createPositionAt( figureElement, 0 ), tableElement );
	*
	* 		const headingRows = modelElement.getAttribute( 'headingRows' ) || 0;
	*
	* 		if ( headingRows > 0 ) {
	* 			const tableHead = writer.createContainerElement( 'thead' );
	*
	* 			const headSlot = writer.createSlot( node => node.is( 'element', 'tableRow' ) && node.index < headingRows );
	*
	* 			writer.insert( writer.createPositionAt( tableElement, 'end' ), tableHead );
	* 			writer.insert( writer.createPositionAt( tableHead, 0 ), headSlot );
	* 		}
	*
	* 		if ( headingRows < tableUtils.getRows( table ) ) {
	* 			const tableBody = writer.createContainerElement( 'tbody' );
	*
	* 			const bodySlot = writer.createSlot( node => node.is( 'element', 'tableRow' ) && node.index >= headingRows );
	*
	* 			writer.insert( writer.createPositionAt( tableElement, 'end' ), tableBody );
	* 			writer.insert( writer.createPositionAt( tableBody, 0 ), bodySlot );
	* 		}
	*
	* 		const restSlot = writer.createSlot( node => !node.is( 'element', 'tableRow' ) );
	*
	* 		writer.insert( writer.createPositionAt( figureElement, 'end' ), restSlot );
	*
	* 		return figureElement;
	* 	}
	* } );
	* ```
	*
	* Note: The children of a model element that's being converted must be allocated in the same order in the view
	* in which they are placed in the model.
	*
	* See {@link module:engine/conversion/conversion~Conversion#for `conversion.for()`} to learn how to add a converter
	* to the conversion process.
	*
	* @param config Conversion configuration.
	* @param config.model The description or a name of the model element to convert.
	* @param config.view A function that takes the model element and
	* {@link module:engine/conversion/downcastdispatcher~DowncastConversionApi downcast conversion API} as parameters
	* and returns a view container element with slots for model child nodes to be converted into.
	* @param config.converterPriority Converter priority.
	*/
	elementToStructure(config) {
		return this.add(downcastElementToStructure(config));
	}
	/**
	* Model attribute to view element conversion helper.
	*
	* This conversion results in wrapping view nodes with a view attribute element. For example, a model text node with
	* `"Foo"` as data and the `bold` attribute becomes `<strong>Foo</strong>` in the view.
	*
	* ```ts
	* editor.conversion.for( 'downcast' ).attributeToElement( {
	* 	model: 'bold',
	* 	view: 'strong'
	* } );
	*
	* editor.conversion.for( 'downcast' ).attributeToElement( {
	* 	model: 'bold',
	* 	view: 'b',
	* 	converterPriority: 'high'
	* } );
	*
	* editor.conversion.for( 'downcast' ).attributeToElement( {
	* 	model: 'invert',
	* 	view: {
	* 		name: 'span',
	* 		classes: [ 'font-light', 'bg-dark' ]
	* 	}
	* } );
	*
	* editor.conversion.for( 'downcast' ).attributeToElement( {
	* 	model: {
	* 		key: 'fontSize',
	* 		values: [ 'big', 'small' ]
	* 	},
	* 	view: {
	* 		big: {
	* 			name: 'span',
	* 			styles: {
	* 				'font-size': '1.2em'
	* 			}
	* 		},
	* 		small: {
	* 			name: 'span',
	* 			styles: {
	* 				'font-size': '0.8em'
	* 			}
	* 		}
	* 	}
	* } );
	*
	* editor.conversion.for( 'downcast' ).attributeToElement( {
	* 	model: 'bold',
	* 	view: ( modelAttributeValue, conversionApi ) => {
	* 		const { writer } = conversionApi;
	*
	* 		return writer.createAttributeElement( 'span', {
	* 			style: 'font-weight:' + modelAttributeValue
	* 		} );
	* 	}
	* } );
	*
	* editor.conversion.for( 'downcast' ).attributeToElement( {
	* 	model: {
	* 		key: 'color',
	* 		name: '$text'
	* 	},
	* 	view: ( modelAttributeValue, conversionApi ) => {
	* 		const { writer } = conversionApi;
	*
	* 		return writer.createAttributeElement( 'span', {
	* 			style: 'color:' + modelAttributeValue
	* 		} );
	* 	}
	* } );
	* ```
	*
	* See {@link module:engine/conversion/conversion~Conversion#for `conversion.for()`} to learn how to add a converter
	* to the conversion process.
	*
	* @param config Conversion configuration.
	* @param config.model The key of the attribute to convert from or a `{ key, values }` object. `values` is an array
	* of `String`s with possible values if the model attribute is an enumerable.
	* @param config.view A view element definition or a function
	* that takes the model attribute value and
	* {@link module:engine/conversion/downcastdispatcher~DowncastConversionApi downcast conversion API} as parameters and returns a view
	* attribute element. If `config.model.values` is given, `config.view` should be an object assigning values from `config.model.values`
	* to view element definitions or functions.
	* @param config.converterPriority Converter priority.
	*/
	attributeToElement(config) {
		return this.add(downcastAttributeToElement(config));
	}
	/**
	* Model attribute to view attribute conversion helper.
	*
	* This conversion results in adding an attribute to a view node, basing on an attribute from a model node. For example,
	* `<imageInline src='foo.jpg'></imageInline>` is converted to `<img src='foo.jpg'></img>`.
	*
	* ```ts
	* editor.conversion.for( 'downcast' ).attributeToAttribute( {
	* 	model: 'source',
	* 	view: 'src'
	* } );
	*
	* editor.conversion.for( 'downcast' ).attributeToAttribute( {
	* 	model: 'source',
	* 	view: 'href',
	* 	converterPriority: 'high'
	* } );
	*
	* editor.conversion.for( 'downcast' ).attributeToAttribute( {
	* 	model: {
	* 		name: 'imageInline',
	* 		key: 'source'
	* 	},
	* 	view: 'src'
	* } );
	*
	* editor.conversion.for( 'downcast' ).attributeToAttribute( {
	* 	model: {
	* 		name: 'styled',
	* 		values: [ 'dark', 'light' ]
	* 	},
	* 	view: {
	* 		dark: {
	* 			key: 'class',
	* 			value: [ 'styled', 'styled-dark' ]
	* 		},
	* 		light: {
	* 			key: 'class',
	* 			value: [ 'styled', 'styled-light' ]
	* 		}
	* 	}
	* } );
	*
	* editor.conversion.for( 'downcast' ).attributeToAttribute( {
	* 	model: 'styled',
	* 	view: modelAttributeValue => ( {
	* 		key: 'class',
	* 		value: 'styled-' + modelAttributeValue
	* 	} )
	* } );
	* ```
	*
	* **Note**: Downcasting to a style property requires providing `value` as an object:
	*
	* ```ts
	* editor.conversion.for( 'downcast' ).attributeToAttribute( {
	* 	model: 'lineHeight',
	* 	view: modelAttributeValue => ( {
	* 		key: 'style',
	* 		value: {
	* 			'line-height': modelAttributeValue,
	* 			'border-bottom': '1px dotted #ba2'
	* 		}
	* 	} )
	* } );
	* ```
	*
	* See {@link module:engine/conversion/conversion~Conversion#for `conversion.for()`} to learn how to add a converter
	* to the conversion process.
	*
	* @param config Conversion configuration.
	* @param config.model The key of the attribute to convert from or a `{ key, values, [ name ] }` object describing
	* the attribute key, possible values and, optionally, an element name to convert from.
	* @param config.view A view attribute key, or a `{ key, value }` object or a function that takes the model attribute value and
	* {@link module:engine/conversion/downcastdispatcher~DowncastConversionApi downcast conversion API}
	* as parameters and returns a `{ key, value }` object. If the `key` is `'class'`, the `value` can be a `String` or an
	* array of `String`s. If the `key` is `'style'`, the `value` is an object with key-value pairs. In other cases, `value` is a `String`.
	* If `config.model.values` is set, `config.view` should be an object assigning values from `config.model.values` to
	* `{ key, value }` objects or a functions.
	* @param config.converterPriority Converter priority.
	*/
	attributeToAttribute(config) {
		return this.add(downcastAttributeToAttribute(config));
	}
	/**
	* Model marker to view element conversion helper.
	*
	* **Note**: This method should be used mainly for editing the downcast and it is recommended
	* to use the {@link #markerToData `#markerToData()`} helper instead.
	*
	* This helper may produce invalid HTML code (e.g. a span between table cells).
	* It should only be used when you are sure that the produced HTML will be semantically correct.
	*
	* This conversion results in creating a view element on the boundaries of the converted marker. If the converted marker
	* is collapsed, only one element is created. For example, a model marker set like this: `<paragraph>F[oo b]ar</paragraph>`
	* becomes `<p>F<span data-marker="search"></span>oo b<span data-marker="search"></span>ar</p>` in the view.
	*
	* ```ts
	* editor.conversion.for( 'editingDowncast' ).markerToElement( {
	* 	model: 'search',
	* 	view: 'marker-search'
	* } );
	*
	* editor.conversion.for( 'editingDowncast' ).markerToElement( {
	* 	model: 'search',
	* 	view: 'search-result',
	* 	converterPriority: 'high'
	* } );
	*
	* editor.conversion.for( 'editingDowncast' ).markerToElement( {
	* 	model: 'search',
	* 	view: {
	* 		name: 'span',
	* 		attributes: {
	* 			'data-marker': 'search'
	* 		}
	* 	}
	* } );
	*
	* editor.conversion.for( 'editingDowncast' ).markerToElement( {
	* 	model: 'search',
	* 	view: ( markerData, conversionApi ) => {
	* 		const { writer } = conversionApi;
	*
	* 		return writer.createUIElement( 'span', {
	* 			'data-marker': 'search',
	* 			'data-start': markerData.isOpening
	* 		} );
	* 	}
	* } );
	* ```
	*
	* If a function is passed as the `config.view` parameter, it will be used to generate both boundary elements. The function
	* receives the `data` object and {@link module:engine/conversion/downcastdispatcher~DowncastConversionApi downcast conversion API}
	* as a parameters and should return an instance of the
	* {@link module:engine/view/uielement~ViewUIElement view UI element}. The `data` object and
	* {@link module:engine/conversion/downcastdispatcher~DowncastConversionApi `conversionApi`} are passed from
	* {@link module:engine/conversion/downcastdispatcher~DowncastDispatcher#event:addMarker}. Additionally,
	* the `data.isOpening` parameter is passed, which is set to `true` for the marker start boundary element, and `false` for
	* the marker end boundary element.
	*
	* See {@link module:engine/conversion/conversion~Conversion#for `conversion.for()`} to learn how to add a converter
	* to the conversion process.
	*
	* @param config Conversion configuration.
	* @param config.model The name of the model marker (or model marker group) to convert.
	* @param config.view A view element definition or a function that takes the model marker data and
	* {@link module:engine/conversion/downcastdispatcher~DowncastConversionApi downcast conversion API} as a parameters
	* and returns a view UI element.
	* @param config.converterPriority Converter priority.
	*/
	markerToElement(config) {
		return this.add(downcastMarkerToElement(config));
	}
	/**
	* Model marker to highlight conversion helper.
	*
	* This conversion results in creating a highlight on view nodes. For this kind of conversion,
	* the {@link module:engine/conversion/downcasthelpers~DowncastHighlightDescriptor} should be provided.
	*
	* For text nodes, a `<span>` {@link module:engine/view/attributeelement~ViewAttributeElement} is created and it wraps all text nodes
	* in the converted marker range. For example, a model marker set like this: `<paragraph>F[oo b]ar</paragraph>` becomes
	* `<p>F<span class="comment">oo b</span>ar</p>` in the view.
	*
	* {@link module:engine/view/containerelement~ViewContainerElement} may provide a custom way of handling highlight. Most often,
	* the element itself is given classes and attributes described in the highlight descriptor (instead of being wrapped in `<span>`).
	* For example, a model marker set like this:
	* `[<imageInline src="foo.jpg"></imageInline>]` becomes `<img src="foo.jpg" class="comment"></img>` in the view.
	*
	* For container elements, the conversion is two-step. While the converter processes the highlight descriptor and passes it
	* to a container element, it is the container element instance itself that applies values from the highlight descriptor.
	* So, in a sense, the converter takes care of stating what should be applied on what, while the element decides how to apply that.
	*
	* ```ts
	* editor.conversion.for( 'downcast' ).markerToHighlight( { model: 'comment', view: { classes: 'comment' } } );
	*
	* editor.conversion.for( 'downcast' ).markerToHighlight( {
	* 	model: 'comment',
	* 	view: { classes: 'comment' },
	* 	converterPriority: 'high'
	* } );
	*
	* editor.conversion.for( 'downcast' ).markerToHighlight( {
	* 	model: 'comment',
	* 	view: ( data, conversionApi ) => {
	* 		// Assuming that the marker name is in a form of comment:commentType:commentId.
	* 		const [ , commentType, commentId ] = data.markerName.split( ':' );
	*
	* 		return {
	* 			classes: [ 'comment', 'comment-' + commentType ],
	* 			attributes: { 'data-comment-id': commentId }
	* 		};
	* 	}
	* } );
	* ```
	*
	* If a function is passed as the `config.view` parameter, it will be used to generate the highlight descriptor. The function
	* receives the `data` object and {@link module:engine/conversion/downcastdispatcher~DowncastConversionApi downcast conversion API}
	* as the parameters and should return a
	* {@link module:engine/conversion/downcasthelpers~DowncastHighlightDescriptor highlight descriptor}.
	* The `data` object properties are passed from {@link module:engine/conversion/downcastdispatcher~DowncastDispatcher#event:addMarker}.
	*
	* See {@link module:engine/conversion/conversion~Conversion#for `conversion.for()`} to learn how to add a converter
	* to the conversion process.
	*
	* @param config Conversion configuration.
	* @param config.model The name of the model marker (or model marker group) to convert.
	* @param config.view A highlight descriptor that will be used for highlighting or a function that takes the model marker data and
	* {@link module:engine/conversion/downcastdispatcher~DowncastConversionApi downcast conversion API} as a parameters
	* and returns a highlight descriptor.
	* @param config.converterPriority Converter priority.
	*/
	markerToHighlight(config) {
		return this.add(downcastMarkerToHighlight(config));
	}
	/**
	* Model marker converter for data downcast.
	*
	* This conversion creates a representation for model marker boundaries in the view:
	*
	* * If the marker boundary is before or after a model element, a view attribute is set on a corresponding view element.
	* * In other cases, a view element with the specified tag name is inserted at the corresponding view position.
	*
	* Typically, the marker names use the `group:uniqueId:otherData` convention. For example: `comment:e34zfk9k2n459df53sjl34:zx32c`.
	* The default configuration for this conversion is that the first part is the `group` part and the rest of
	* the marker name becomes the `name` part.
	*
	* Tag and attribute names and values are generated from the marker name:
	*
	* * The templates for attributes are `data-[group]-start-before="[name]"`, `data-[group]-start-after="[name]"`,
	* `data-[group]-end-before="[name]"` and `data-[group]-end-after="[name]"`.
	* * The templates for view elements are `<[group]-start name="[name]">` and `<[group]-end name="[name]">`.
	*
	* Attributes mark whether the given marker's start or end boundary is before or after the given element.
	* The `data-[group]-start-before` and `data-[group]-end-after` attributes are favored.
	* The other two are used when the former two cannot be used.
	*
	* The conversion configuration can take a function that will generate different group and name parts.
	* If such a function is set as the `config.view` parameter, it is passed a marker name and it is expected to return an object with two
	* properties: `group` and `name`. If the function returns a falsy value, the conversion will not take place.
	*
	* Basic usage:
	*
	* ```ts
	* // Using the default conversion.
	* // In this case, all markers with names starting with 'comment:' will be converted.
	* // The `group` parameter will be set to `comment`.
	* // The `name` parameter will be the rest of the marker name (without the `:`).
	* editor.conversion.for( 'dataDowncast' ).markerToData( {
	* 	model: 'comment'
	* } );
	* ```
	*
	* An example of a view that may be generated by this conversion (assuming a marker with the name `comment:commentId:uid` marked
	* by `[]`):
	*
	* ```
	* // Model:
	* <paragraph>Foo[bar</paragraph>
	* <imageBlock src="abc.jpg"></imageBlock>]
	*
	* // View:
	* <p>Foo<comment-start name="commentId:uid"></comment-start>bar</p>
	* <figure data-comment-end-after="commentId:uid" class="image"><img src="abc.jpg" /></figure>
	* ```
	*
	* In the example above, the comment starts before "bar" and ends after the image.
	*
	* If the `name` part is empty, the following view may be generated:
	*
	* ```html
	* <p>Foo <myMarker-start></myMarker-start>bar</p>
	* <figure data-myMarker-end-after="" class="image"><img src="abc.jpg" /></figure>
	* ```
	*
	* **Note:** A situation where some markers have the `name` part and some do not, is incorrect and should be avoided.
	*
	* Examples where `data-group-start-after` and `data-group-end-before` are used:
	*
	* ```
	* // Model:
	* <blockQuote>[]<paragraph>Foo</paragraph></blockQuote>
	*
	* // View:
	* <blockquote><p data-group-end-before="name" data-group-start-before="name">Foo</p></blockquote>
	* ```
	*
	* Similarly, when a marker is collapsed after the last element:
	*
	* ```
	* // Model:
	* <blockQuote><paragraph>Foo</paragraph>[]</blockQuote>
	*
	* // View:
	* <blockquote><p data-group-end-after="name" data-group-start-after="name">Foo</p></blockquote>
	* ```
	*
	* When there are multiple markers from the same group stored in the same attribute of the same element, their
	* name parts are put together in the attribute value, for example: `data-group-start-before="name1,name2,name3"`.
	*
	* Other examples of usage:
	*
	* ```ts
	* // Using a custom function which is the same as the default conversion:
	* editor.conversion.for( 'dataDowncast' ).markerToData( {
	* 	model: 'comment',
	* 	view: markerName => ( {
	* 		group: 'comment',
	* 		name: markerName.substr( 8 ) // Removes 'comment:' part.
	* 	} )
	* } );
	*
	* // Using the converter priority:
	* editor.conversion.for( 'dataDowncast' ).markerToData( {
	* 	model: 'comment',
	* 	view: markerName => ( {
	* 		group: 'comment',
	* 		name: markerName.substr( 8 ) // Removes 'comment:' part.
	* 	} ),
	* 	converterPriority: 'high'
	* } );
	* ```
	*
	* This kind of conversion is useful for saving data into the database, so it should be used in the data conversion pipeline.
	*
	* See the {@link module:engine/conversion/conversion~Conversion#for `conversion.for()`} API guide to learn how to
	* add a converter to the conversion process.
	*
	* @param config Conversion configuration.
	* @param config.model The name of the model marker (or the model marker group) to convert.
	* @param config.view A function that takes the model marker name and
	* {@link module:engine/conversion/downcastdispatcher~DowncastConversionApi downcast conversion API} as the parameters
	* and returns an object with the `group` and `name` properties.
	* @param config.converterPriority Converter priority.
	*/
	markerToData(config) {
		return this.add(downcastMarkerToData(config));
	}
};
/**
* Function factory that creates a default downcast converter for text insertion changes.
*
* The converter automatically consumes the corresponding value from the consumables list and stops the event (see
* {@link module:engine/conversion/downcastdispatcher~DowncastDispatcher}).
*
* ```ts
* modelDispatcher.on( 'insert:$text', insertText() );
* ```
*
* @returns Insert text event converter.
* @internal
*/
function insertText() {
	return (evt, data, conversionApi) => {
		if (!conversionApi.consumable.consume(data.item, evt.name)) return;
		const viewWriter = conversionApi.writer;
		const viewPosition = conversionApi.mapper.toViewPosition(data.range.start);
		const viewText = viewWriter.createText(data.item.data);
		viewWriter.insert(viewPosition, viewText);
	};
}
/**
* Function factory that creates a default downcast converter for triggering attributes and children conversion.
*
* @returns The converter.
* @internal
*/
function insertAttributesAndChildren() {
	return (evt, data, conversionApi) => {
		conversionApi.convertAttributes(data.item);
		if (!data.reconversion && data.item.is("element") && !data.item.isEmpty) conversionApi.convertChildren(data.item);
	};
}
/**
* Function factory that creates a default downcast converter for node remove changes.
*
* ```ts
* modelDispatcher.on( 'remove', remove() );
* ```
*
* @returns Remove event converter.
* @internal
*/
function remove() {
	return (evt, data, conversionApi) => {
		if (data.reconversion) return;
		const viewStart = conversionApi.mapper.toViewPosition(data.position);
		const modelEnd = data.position.getShiftedBy(data.length);
		const viewEnd = conversionApi.mapper.toViewPosition(modelEnd, { isPhantom: true });
		removeRangeAndUnbind(conversionApi.writer.createRange(viewStart, viewEnd).getTrimmed(), conversionApi);
	};
}
/**
* Creates a `<span>` {@link module:engine/view/attributeelement~ViewAttributeElement view attribute element} from the information
* provided by the {@link module:engine/conversion/downcasthelpers~DowncastHighlightDescriptor highlight descriptor} object. If the priority
* is not provided in the descriptor, the default priority will be used.
*
* @internal
*/
function createViewElementFromDowncastHighlightDescriptor(writer, descriptor) {
	const viewElement = writer.createAttributeElement("span", descriptor.attributes);
	if (descriptor.classes) viewElement._addClass(descriptor.classes);
	if (typeof descriptor.priority === "number") viewElement._priority = descriptor.priority;
	viewElement._id = descriptor.id;
	return viewElement;
}
/**
* Function factory that creates a converter which converts a non-collapsed
* {@link module:engine/model/selection~ModelSelection model selection}
* to a {@link module:engine/view/documentselection~ViewDocumentSelection view selection}. The converter consumes appropriate
* value from the `consumable` object and maps model positions from the selection to view positions.
*
* ```ts
* modelDispatcher.on( 'selection', convertRangeSelection() );
* ```
*
* @returns Selection converter.
* @internal
*/
function convertRangeSelection() {
	return (evt, data, conversionApi) => {
		const selection = data.selection;
		if (selection.isCollapsed) return;
		if (!conversionApi.consumable.consume(selection, "selection")) return;
		const viewRanges = [];
		for (const range of selection.getRanges()) viewRanges.push(conversionApi.mapper.toViewRange(range));
		conversionApi.writer.setSelection(viewRanges, { backward: selection.isBackward });
	};
}
/**
* Function factory that creates a converter which converts a collapsed
* {@link module:engine/model/selection~ModelSelection model selection} to
* a {@link module:engine/view/documentselection~ViewDocumentSelection view selection}. The converter consumes appropriate
* value from the `consumable` object, maps the model selection position to the view position and breaks
* {@link module:engine/view/attributeelement~ViewAttributeElement attribute elements} at the selection position.
*
* ```ts
* modelDispatcher.on( 'selection', convertCollapsedSelection() );
* ```
*
* An example of the view state before and after converting the collapsed selection:
*
* ```
*    <p><strong>f^oo<strong>bar</p>
* -> <p><strong>f</strong>^<strong>oo</strong>bar</p>
* ```
*
* By breaking attribute elements like `<strong>`, the selection is in a correct element. Then, when the selection attribute is
* converted, broken attributes might be merged again, or the position where the selection is may be wrapped
* with different, appropriate attribute elements.
*
* See also {@link module:engine/conversion/downcasthelpers~cleanSelection} which does a clean-up
* by merging attributes.
*
* @returns Selection converter.
* @internal
*/
function convertCollapsedSelection() {
	return (evt, data, conversionApi) => {
		const selection = data.selection;
		if (!selection.isCollapsed) return;
		if (!conversionApi.consumable.consume(selection, "selection")) return;
		const viewWriter = conversionApi.writer;
		const modelPosition = selection.getFirstPosition();
		const viewPosition = conversionApi.mapper.toViewPosition(modelPosition);
		const brokenPosition = viewWriter.breakAttributes(viewPosition);
		viewWriter.setSelection(brokenPosition);
	};
}
/**
* Function factory that creates a converter which cleans artifacts after the previous
* {@link module:engine/model/selection~ModelSelection model selection} conversion. It removes all empty
* {@link module:engine/view/attributeelement~ViewAttributeElement view attribute elements} and merges
* sibling attributes at all start and end positions of all ranges.
*
* ```
*    <p><strong>^</strong></p>
* -> <p>^</p>
*
*    <p><strong>foo</strong>^<strong>bar</strong>bar</p>
* -> <p><strong>foo^bar<strong>bar</p>
*
*    <p><strong>foo</strong><em>^</em><strong>bar</strong>bar</p>
* -> <p><strong>foo^bar<strong>bar</p>
* ```
*
* This listener should be assigned before any converter for the new selection:
*
* ```ts
* modelDispatcher.on( 'cleanSelection', cleanSelection() );
* ```
*
* See {@link module:engine/conversion/downcasthelpers~convertCollapsedSelection}
* which does the opposite by breaking attributes in the selection position.
*
* @returns Selection converter.
* @internal
*/
function cleanSelection() {
	return (evt, data, conversionApi) => {
		const viewWriter = conversionApi.writer;
		const viewSelection = viewWriter.document.selection;
		for (const range of viewSelection.getRanges()) if (range.isCollapsed) {
			if (range.end.parent.isAttached()) conversionApi.writer.mergeAttributes(range.start);
		}
		viewWriter.setSelection(null);
	};
}
/**
* Function factory that creates a converter which converts the set/change/remove attribute changes from the model to the view.
* It can also be used to convert selection attributes. In that case, an empty attribute element will be created and the
* selection will be put inside it.
*
* Attributes from the model are converted to a view element that will be wrapping these view nodes that are bound to
* model elements having the given attribute. This is useful for attributes like `bold` that may be set on text nodes in the model
* but are represented as an element in the view:
*
* ```
* [paragraph]              MODEL ====> VIEW        <p>
* 	|- a {bold: true}                             |- <b>
* 	|- b {bold: true}                             |   |- ab
* 	|- c                                          |- c
* 	```
*
* Passed `Function` will be provided with the attribute value and then all the parameters of the
* {@link module:engine/conversion/downcastdispatcher~DowncastDispatcher#event:attribute `attribute` event}.
* It is expected that the function returns an {@link module:engine/view/element~ViewElement}.
* The result of the function will be the wrapping element.
* When the provided `Function` does not return any element, no conversion will take place.
*
* The converter automatically consumes the corresponding value from the consumables list and stops the event (see
* {@link module:engine/conversion/downcastdispatcher~DowncastDispatcher}).
*
* ```ts
* modelDispatcher.on( 'attribute:bold', wrap( ( modelAttributeValue, { writer } ) => {
* 	return writer.createAttributeElement( 'strong' );
* } );
* ```
*
* @internal
* @param elementCreator Function returning a view element that will be used for wrapping.
* @returns Set/change attribute converter.
*/
function wrap(elementCreator) {
	return (evt, data, conversionApi) => {
		if (!conversionApi.consumable.test(data.item, evt.name)) return;
		const oldViewElement = elementCreator(data.attributeOldValue, conversionApi, data);
		const newViewElement = elementCreator(data.attributeNewValue, conversionApi, data);
		if (!oldViewElement && !newViewElement) return;
		conversionApi.consumable.consume(data.item, evt.name);
		const viewWriter = conversionApi.writer;
		const viewSelection = viewWriter.document.selection;
		if (data.item instanceof ModelSelection || data.item instanceof ModelDocumentSelection) viewWriter.wrap(viewSelection.getFirstRange(), newViewElement);
		else {
			let viewRange = conversionApi.mapper.toViewRange(data.range);
			if (data.attributeOldValue !== null && oldViewElement) viewRange = viewWriter.unwrap(viewRange, oldViewElement);
			if (data.attributeNewValue !== null && newViewElement) viewWriter.wrap(viewRange, newViewElement);
		}
	};
}
/**
* Function factory that creates a converter which converts node insertion changes from the model to the view.
* The function passed will be provided with all the parameters of the dispatcher's
* {@link module:engine/conversion/downcastdispatcher~DowncastDispatcher#event:insert `insert` event}.
* It is expected that the function returns an {@link module:engine/view/element~ViewElement}.
* The result of the function will be inserted into the view.
*
* The converter automatically consumes the corresponding value from the consumables list and binds the model and view elements.
*
* ```ts
* downcastDispatcher.on(
* 	'insert:myElem',
* 	insertElement( ( modelItem, { writer } ) => {
* 		const text = writer.createText( 'myText' );
* 		const myElem = writer.createElement( 'myElem', { myAttr: 'my-' + modelItem.getAttribute( 'myAttr' ) }, text );
*
* 		// Do something fancy with `myElem` using `modelItem` or other parameters.
*
* 		return myElem;
* 	}
* ) );
* ```
*
* @internal
* @param  elementCreator Function returning a view element, which will be inserted.
* @param consumer Function defining element consumption process.
* By default this function just consume passed item insertion.
* @returns Insert element event converter.
*/
function insertElement(elementCreator, consumer = defaultConsumer) {
	return (evt, data, conversionApi) => {
		if (!consumer(data.item, conversionApi.consumable, { preflight: true })) return;
		const viewElement = elementCreator(data.item, conversionApi, data);
		if (!viewElement) return;
		consumer(data.item, conversionApi.consumable);
		const viewPosition = data.reconversion && removeElementAndUnbind(data.item, conversionApi) || conversionApi.mapper.toViewPosition(data.range.start);
		conversionApi.mapper.bindElements(data.item, viewElement);
		conversionApi.writer.insert(viewPosition, viewElement);
		conversionApi.convertAttributes(data.item);
		reinsertOrConvertNodes(viewElement, data.item.getChildren(), conversionApi, { reconversion: data.reconversion });
	};
}
/**
* Function factory that creates a converter which converts a single model node insertion to a view structure.
*
* It is expected that the passed element creator function returns an {@link module:engine/view/element~ViewElement} with attached slots
* created with `writer.createSlot()` to indicate where child nodes should be converted.
*
* @see module:engine/conversion/downcasthelpers~DowncastHelpers#elementToStructure
*
* @internal
* @param elementCreator Function returning a view structure, which will be inserted.
* @param consumer A callback that is expected to consume all the consumables
* that were used by the element creator.
* @returns Insert element event converter.
*/
function insertStructure(elementCreator, consumer) {
	return (evt, data, conversionApi) => {
		if (!consumer(data.item, conversionApi.consumable, { preflight: true })) return;
		const slotsMap = /* @__PURE__ */ new Map();
		conversionApi.writer._registerSlotFactory(createSlotFactory(data.item, slotsMap, conversionApi));
		const viewElement = elementCreator(data.item, conversionApi, data);
		conversionApi.writer._clearSlotFactory();
		if (!viewElement) return;
		validateSlotsChildren(data.item, slotsMap, conversionApi);
		consumer(data.item, conversionApi.consumable);
		const viewPosition = data.reconversion && removeElementAndUnbind(data.item, conversionApi) || conversionApi.mapper.toViewPosition(data.range.start);
		conversionApi.mapper.bindElements(data.item, viewElement);
		conversionApi.writer.insert(viewPosition, viewElement);
		conversionApi.convertAttributes(data.item);
		fillSlots(viewElement, slotsMap, conversionApi, { reconversion: data.reconversion });
	};
}
/**
* Function factory that creates a converter which converts marker adding change to the
* {@link module:engine/view/uielement~ViewUIElement view UI element}.
*
* The view UI element that will be added to the view depends on the passed parameter. See {@link ~insertElement}.
* In case of a non-collapsed range, the UI element will not wrap nodes but separate elements will be placed at the beginning
* and at the end of the range.
*
* This converter binds created UI elements with the marker name using {@link module:engine/conversion/mapper~Mapper#bindElementToMarker}.
*
* @internal
* @param elementCreator A view UI element or a function returning the view element that will be inserted.
* @returns Insert element event converter.
*/
function insertUIElement(elementCreator) {
	return (evt, data, conversionApi) => {
		data.isOpening = true;
		const viewStartElement = elementCreator(data, conversionApi);
		data.isOpening = false;
		const viewEndElement = elementCreator(data, conversionApi);
		if (!viewStartElement || !viewEndElement) return;
		const { markerRange, markerName } = data;
		if (markerRange.isCollapsed && !conversionApi.consumable.consume(markerRange, evt.name)) return;
		for (const value of markerRange) if (!conversionApi.consumable.consume(value.item, evt.name)) return;
		const mapper = conversionApi.mapper;
		const viewWriter = conversionApi.writer;
		viewWriter.setCustomProperty("markerBoundaryType", "start", viewStartElement);
		viewWriter.setCustomProperty("markerBoundaryName", markerName, viewStartElement);
		viewWriter.setCustomProperty("markerBoundaryType", "end", viewEndElement);
		viewWriter.setCustomProperty("markerBoundaryName", markerName, viewEndElement);
		const getBoundaryModelPosition = (name, type) => {
			const elements = mapper.markerNameToElements(name);
			if (elements) {
				for (const el of elements) if (el.getCustomProperty("markerBoundaryType") === type) return mapper.toModelPosition(viewWriter.createPositionBefore(el));
			}
			return null;
		};
		if (!markerRange.isCollapsed) {
			const endViewPosition = mapper.toViewPosition(markerRange.end).getLastMatchingPosition(({ item }) => {
				if (!item.is("uiElement") || item.getCustomProperty("markerBoundaryType") !== "end") return false;
				const existingName = item.getCustomProperty("markerBoundaryName");
				const existingStartPos = getBoundaryModelPosition(existingName, "start");
				if (!existingStartPos) return false;
				const cmp = existingStartPos.compareWith(markerRange.start);
				if (cmp === "same") return existingName.localeCompare(markerName) > 0;
				return cmp === "after";
			});
			viewWriter.insert(endViewPosition, viewEndElement);
			mapper.bindElementToMarker(viewEndElement, markerName);
		}
		const startViewPosition = mapper.toViewPosition(markerRange.start).getLastMatchingPosition(({ item }) => {
			if (!item.is("uiElement")) return false;
			const boundaryType = item.getCustomProperty("markerBoundaryType");
			if (!boundaryType) return false;
			if (boundaryType === "end") return true;
			const existingName = item.getCustomProperty("markerBoundaryName");
			const existingEndPos = getBoundaryModelPosition(existingName, "end");
			if (!existingEndPos) return markerRange.isCollapsed;
			if (markerRange.isCollapsed) return true;
			const cmp = existingEndPos.compareWith(markerRange.end);
			if (cmp === "same") return existingName.localeCompare(markerName) < 0;
			return cmp === "after";
		});
		viewWriter.insert(startViewPosition, viewStartElement);
		mapper.bindElementToMarker(viewStartElement, markerName);
		evt.stop();
	};
}
/**
* Removes given view range content and unbinds removed elements.
*/
function removeRangeAndUnbind(viewRange, conversionApi) {
	const removed = conversionApi.writer.remove(viewRange);
	for (const child of conversionApi.writer.createRangeIn(removed).getItems()) conversionApi.mapper.unbindViewElement(child, { defer: true });
	return viewRange.start;
}
/**
* Removes view element for given model element and unbinds removed view elements.
*/
function removeElementAndUnbind(modelElement, conversionApi) {
	const viewElement = conversionApi.mapper.toViewElement(modelElement);
	return viewElement && removeRangeAndUnbind(conversionApi.writer.createRangeOn(viewElement), conversionApi);
}
/**
* Function factory that returns a default downcast converter for removing a {@link module:engine/view/uielement~ViewUIElement UI element}
* based on marker remove change.
*
* This converter unbinds elements from the marker name.
*
* @returns Removed UI element converter.
*/
function removeUIElement() {
	return (evt, data, conversionApi) => {
		const elements = conversionApi.mapper.markerNameToElements(data.markerName);
		if (!elements) return;
		for (const element of elements) {
			conversionApi.mapper.unbindElementFromMarkerName(element, data.markerName);
			conversionApi.writer.clear(conversionApi.writer.createRangeOn(element), element);
		}
		conversionApi.writer.clearClonedElementsGroup(data.markerName);
		evt.stop();
	};
}
/**
* Function factory that creates a default converter for model markers.
*
* See {@link DowncastHelpers#markerToData} for more information what type of view is generated.
*
* This converter binds created UI elements and affected view elements with the marker name
* using {@link module:engine/conversion/mapper~Mapper#bindElementToMarker}.
*
* @returns Add marker converter.
*/
function insertMarkerData(viewCreator) {
	return (evt, data, conversionApi) => {
		const viewMarkerData = viewCreator(data.markerName, conversionApi);
		if (!viewMarkerData) return;
		const markerRange = data.markerRange;
		if (!conversionApi.consumable.consume(markerRange, evt.name)) return;
		handleMarkerBoundary(markerRange, false, conversionApi, data, viewMarkerData);
		handleMarkerBoundary(markerRange, true, conversionApi, data, viewMarkerData);
		evt.stop();
	};
}
/**
* Helper function for `insertMarkerData()` that marks a marker boundary at the beginning or end of given `range`.
*/
function handleMarkerBoundary(range, isStart, conversionApi, data, viewMarkerData) {
	const modelPosition = isStart ? range.start : range.end;
	const elementAfter = modelPosition.nodeAfter && modelPosition.nodeAfter.is("element") ? modelPosition.nodeAfter : null;
	const elementBefore = modelPosition.nodeBefore && modelPosition.nodeBefore.is("element") ? modelPosition.nodeBefore : null;
	if (elementAfter || elementBefore) {
		let modelElement;
		let isBefore;
		if (isStart && elementAfter || !isStart && !elementBefore) {
			modelElement = elementAfter;
			isBefore = true;
		} else {
			modelElement = elementBefore;
			isBefore = false;
		}
		const viewElement = conversionApi.mapper.toViewElement(modelElement);
		if (viewElement) {
			insertMarkerAsAttribute(viewElement, isStart, isBefore, conversionApi, data, viewMarkerData);
			return;
		}
	}
	insertMarkerAsElement(conversionApi.mapper.toViewPosition(modelPosition), isStart, conversionApi, data, viewMarkerData);
}
/**
* Helper function for `insertMarkerData()` that marks a marker boundary in the view as an attribute on a view element.
*/
function insertMarkerAsAttribute(viewElement, isStart, isBefore, conversionApi, data, viewMarkerData) {
	const attributeName = `data-${viewMarkerData.group}-${isStart ? "start" : "end"}-${isBefore ? "before" : "after"}`;
	const markerNames = viewElement.hasAttribute(attributeName) ? viewElement.getAttribute(attributeName).split(",") : [];
	markerNames.unshift(viewMarkerData.name);
	conversionApi.writer.setAttribute(attributeName, markerNames.join(","), viewElement);
	conversionApi.mapper.bindElementToMarker(viewElement, data.markerName);
}
/**
* Helper function for `insertMarkerData()` that marks a marker boundary in the view as a separate view ui element.
*/
function insertMarkerAsElement(position, isStart, conversionApi, data, viewMarkerData) {
	const viewElementName = `${viewMarkerData.group}-${isStart ? "start" : "end"}`;
	const attrs = viewMarkerData.name ? { "name": viewMarkerData.name } : null;
	const viewElement = conversionApi.writer.createUIElement(viewElementName, attrs);
	conversionApi.writer.insert(position, viewElement);
	conversionApi.mapper.bindElementToMarker(viewElement, data.markerName);
}
/**
* Function factory that creates a converter for removing a model marker data added by the {@link #insertMarkerData} converter.
*
* @returns Remove marker converter.
*/
function removeMarkerData(viewCreator) {
	return (evt, data, conversionApi) => {
		const viewData = viewCreator(data.markerName, conversionApi);
		if (!viewData) return;
		const elements = conversionApi.mapper.markerNameToElements(data.markerName);
		if (!elements) return;
		for (const element of elements) {
			conversionApi.mapper.unbindElementFromMarkerName(element, data.markerName);
			if (element.is("containerElement")) {
				removeMarkerFromAttribute(`data-${viewData.group}-start-before`, element);
				removeMarkerFromAttribute(`data-${viewData.group}-start-after`, element);
				removeMarkerFromAttribute(`data-${viewData.group}-end-before`, element);
				removeMarkerFromAttribute(`data-${viewData.group}-end-after`, element);
			} else conversionApi.writer.clear(conversionApi.writer.createRangeOn(element), element);
		}
		conversionApi.writer.clearClonedElementsGroup(data.markerName);
		evt.stop();
		function removeMarkerFromAttribute(attributeName, element) {
			if (element.hasAttribute(attributeName)) {
				const markerNames = new Set(element.getAttribute(attributeName).split(","));
				markerNames.delete(viewData.name);
				if (markerNames.size == 0) conversionApi.writer.removeAttribute(attributeName, element);
				else conversionApi.writer.setAttribute(attributeName, Array.from(markerNames).join(","), element);
			}
		}
	};
}
/**
* Function factory that creates a converter which converts the set/change/remove attribute changes from the model to the view.
*
* Attributes from the model are converted to the view element attributes in the view. You may provide a custom function to generate
* a key-value attribute pair to add/change/remove. If not provided, model attributes will be converted to view element
* attributes on a one-to-one basis.
*
* *Note:** The provided attribute creator should always return the same `key` for a given attribute from the model.
*
* The converter automatically consumes the corresponding value from the consumables list and stops the event (see
* {@link module:engine/conversion/downcastdispatcher~DowncastDispatcher}).
*
* ```ts
* modelDispatcher.on( 'attribute:customAttr:myElem', changeAttribute( ( value, data ) => {
* 	// Change attribute key from `customAttr` to `class` in the view.
* 	const key = 'class';
* 	let value = data.attributeNewValue;
*
* 	// Force attribute value to 'empty' if the model element is empty.
* 	if ( data.item.childCount === 0 ) {
* 		value = 'empty';
* 	}
*
* 	// Return the key-value pair.
* 	return { key, value };
* } ) );
* ```
*
* @param attributeCreator Function returning an object with two properties: `key` and `value`, which
* represent the attribute key and attribute value to be set on a {@link module:engine/view/element~ViewElement view element}.
* The function is passed the model attribute value as the first parameter and additional data about the change as the second parameter.
* @returns Set/change attribute converter.
*/
function changeAttribute(attributeCreator) {
	return (evt, data, conversionApi) => {
		if (!conversionApi.consumable.test(data.item, evt.name)) return;
		const oldAttribute = attributeCreator(data.attributeOldValue, conversionApi, data);
		const newAttribute = attributeCreator(data.attributeNewValue, conversionApi, data);
		if (!oldAttribute && !newAttribute) return;
		conversionApi.consumable.consume(data.item, evt.name);
		const viewElement = conversionApi.mapper.toViewElement(data.item);
		const viewWriter = conversionApi.writer;
		if (!viewElement)
 /**
		* This error occurs when a {@link module:engine/model/textproxy~ModelTextProxy text node's} attribute is to be downcasted
		* by an {@link module:engine/conversion/conversion~Conversion#attributeToAttribute `Attribute to Attribute converter`}.
		* In most cases it is caused by converters misconfiguration when only "generic" converter is defined:
		*
		* ```ts
		* editor.conversion.for( 'downcast' ).attributeToAttribute( {
		* 	model: 'attribute-name',
		* 	view: 'attribute-name'
		* } ) );
		* ```
		*
		* and given attribute is used on text node, for example:
		*
		* ```ts
		* model.change( writer => {
		* 	writer.insertText( 'Foo', { 'attribute-name': 'bar' }, parent, 0 );
		* } );
		* ```
		*
		* In such cases, to convert the same attribute for both {@link module:engine/model/element~ModelElement}
		* and {@link module:engine/model/textproxy~ModelTextProxy `Text`} nodes, text specific
		* {@link module:engine/conversion/conversion~Conversion#attributeToElement `Attribute to Element converter`}
		* with higher {@link module:utils/priorities~PriorityString priority} must also be defined:
		*
		* ```ts
		* editor.conversion.for( 'downcast' ).attributeToElement( {
		* 	model: {
		* 		key: 'attribute-name',
		* 		name: '$text'
		* 	},
		* 	view: ( value, { writer } ) => {
		* 		return writer.createAttributeElement( 'span', { 'attribute-name': value } );
		* 	},
		* 	converterPriority: 'high'
		* } ) );
		* ```
		*
		* @error conversion-attribute-to-attribute-on-text
		* @param {object} data The conversion data.
		*/
		throw new CKEditorError("conversion-attribute-to-attribute-on-text", conversionApi.dispatcher, data);
		if (data.attributeOldValue !== null && oldAttribute) {
			let value = oldAttribute.value;
			if (oldAttribute.key == "style") if (typeof oldAttribute.value == "string") value = new StylesMap(viewWriter.document.stylesProcessor).setTo(oldAttribute.value).getStylesEntries().map(([key]) => key);
			else value = Object.keys(oldAttribute.value);
			viewWriter.removeAttribute(oldAttribute.key, value, viewElement);
		}
		if (data.attributeNewValue !== null && newAttribute) {
			let value = newAttribute.value;
			if (newAttribute.key == "style" && typeof newAttribute.value == "string") value = Object.fromEntries(new StylesMap(viewWriter.document.stylesProcessor).setTo(newAttribute.value).getStylesEntries());
			viewWriter.setAttribute(newAttribute.key, value, false, viewElement);
		}
	};
}
/**
* Function factory that creates a converter which converts the text inside marker's range. The converter wraps the text with
* {@link module:engine/view/attributeelement~ViewAttributeElement} created from the provided descriptor.
* See {link module:engine/conversion/downcasthelpers~createViewElementFromDowncastHighlightDescriptor}.
*
* It can also be used to convert the selection that is inside a marker. In that case, an empty attribute element will be
* created and the selection will be put inside it.
*
* If the highlight descriptor does not provide the `priority` property, `10` will be used.
*
* If the highlight descriptor does not provide the `id` property, the name of the marker will be used.
*
* This converter binds the created {@link module:engine/view/attributeelement~ViewAttributeElement attribute elemens} with the marker name
* using the {@link module:engine/conversion/mapper~Mapper#bindElementToMarker} method.
*/
function highlightText(highlightDescriptor) {
	return (evt, data, conversionApi) => {
		if (!data.item) return;
		if (!(data.item instanceof ModelSelection || data.item instanceof ModelDocumentSelection) && !data.item.is("$textProxy")) return;
		const descriptor = prepareDescriptor(highlightDescriptor, data, conversionApi);
		if (!descriptor) return;
		if (!conversionApi.consumable.consume(data.item, evt.name)) return;
		const viewWriter = conversionApi.writer;
		const viewElement = createViewElementFromDowncastHighlightDescriptor(viewWriter, descriptor);
		const viewSelection = viewWriter.document.selection;
		if (data.item instanceof ModelSelection || data.item instanceof ModelDocumentSelection) viewWriter.wrap(viewSelection.getFirstRange(), viewElement);
		else {
			const viewRange = conversionApi.mapper.toViewRange(data.range);
			const rangeAfterWrap = viewWriter.wrap(viewRange, viewElement);
			for (const element of rangeAfterWrap.getItems()) if (element.is("attributeElement") && element.isSimilar(viewElement)) {
				conversionApi.mapper.bindElementToMarker(element, data.markerName);
				break;
			}
		}
	};
}
/**
* Converter function factory. It creates a function which applies the marker's highlight to an element inside the marker's range.
*
* The converter checks if an element has the `addHighlight` function stored as a
* {@link module:engine/view/element~ViewElement#_setCustomProperty custom property} and, if so, uses it to apply the highlight.
* In such case the converter will consume all element's children, assuming that they were handled by the element itself.
*
* When the `addHighlight` custom property is not present, the element is not converted in any special way.
* This means that converters will proceed to convert the element's child nodes.
*
* If the highlight descriptor does not provide the `priority` property, `10` will be used.
*
* If the highlight descriptor does not provide the `id` property, the name of the marker will be used.
*
* This converter binds altered {@link module:engine/view/containerelement~ViewContainerElement container elements}
* with the marker name using the {@link module:engine/conversion/mapper~Mapper#bindElementToMarker} method.
*/
function highlightElement(highlightDescriptor) {
	return (evt, data, conversionApi) => {
		if (!data.item) return;
		if (!(data.item instanceof ModelElement)) return;
		const descriptor = prepareDescriptor(highlightDescriptor, data, conversionApi);
		if (!descriptor) return;
		if (!conversionApi.consumable.test(data.item, evt.name)) return;
		const viewElement = conversionApi.mapper.toViewElement(data.item);
		if (viewElement && viewElement.getCustomProperty("addHighlight")) {
			conversionApi.consumable.consume(data.item, evt.name);
			for (const value of ModelRange._createIn(data.item)) conversionApi.consumable.consume(value.item, evt.name);
			viewElement.getCustomProperty("addHighlight")(viewElement, descriptor, conversionApi.writer);
			conversionApi.mapper.bindElementToMarker(viewElement, data.markerName);
		}
	};
}
/**
* Function factory that creates a converter which converts the removing model marker to the view.
*
* Both text nodes and elements are handled by this converter but they are handled a bit differently.
*
* Text nodes are unwrapped using the {@link module:engine/view/attributeelement~ViewAttributeElement attribute element} created from the
* provided highlight descriptor. See {link module:engine/conversion/downcasthelpers~DowncastHighlightDescriptor}.
*
* For elements, the converter checks if an element has the `removeHighlight` function stored as a
* {@link module:engine/view/element~ViewElement#_setCustomProperty custom property}. If so, it uses it to remove the highlight.
* In such case, the children of that element will not be converted.
*
* When `removeHighlight` is not present, the element is not converted in any special way.
* The converter will proceed to convert the element's child nodes instead.
*
* If the highlight descriptor does not provide the `priority` property, `10` will be used.
*
* If the highlight descriptor does not provide the `id` property, the name of the marker will be used.
*
* This converter unbinds elements from the marker name.
*/
function removeHighlight(highlightDescriptor) {
	return (evt, data, conversionApi) => {
		if (data.markerRange.isCollapsed) return;
		const descriptor = prepareDescriptor(highlightDescriptor, data, conversionApi);
		if (!descriptor) return;
		const viewHighlightElement = createViewElementFromDowncastHighlightDescriptor(conversionApi.writer, descriptor);
		const elements = conversionApi.mapper.markerNameToElements(data.markerName);
		if (!elements) return;
		for (const element of elements) {
			conversionApi.mapper.unbindElementFromMarkerName(element, data.markerName);
			if (element.is("attributeElement")) conversionApi.writer.unwrap(conversionApi.writer.createRangeOn(element), viewHighlightElement);
			else element.getCustomProperty("removeHighlight")(element, descriptor.id, conversionApi.writer);
		}
		conversionApi.writer.clearClonedElementsGroup(data.markerName);
		evt.stop();
	};
}
/**
* Model element to view element conversion helper.
*
* See {@link ~DowncastHelpers#elementToElement `.elementToElement()` downcast helper} for examples and config params description.
*
* @param config Conversion configuration.
* @param config.model The description or a name of the model element to convert.
* @param config.model.attributes List of attributes triggering element reconversion.
* @param config.model.children Should reconvert element if the list of model child nodes changed.
* @returns Conversion helper.
*/
function downcastElementToElement(config) {
	const model = normalizeModelElementConfig(config.model);
	const view = normalizeToElementConfig(config.view, "container");
	if (model.attributes.length) model.children = true;
	return (dispatcher) => {
		dispatcher.on(`insert:${model.name}`, insertElement(view, createConsumer(model)), { priority: config.converterPriority || "normal" });
		if (model.children || model.attributes.length) dispatcher.on("reduceChanges", createChangeReducer(model), { priority: "low" });
	};
}
/**
* Model element to view structure conversion helper.
*
* See {@link ~DowncastHelpers#elementToStructure `.elementToStructure()` downcast helper} for examples and config params description.
*
* @param config Conversion configuration.
* @returns Conversion helper.
*/
function downcastElementToStructure(config) {
	const model = normalizeModelElementConfig(config.model);
	const view = normalizeToElementConfig(config.view, "container");
	model.children = true;
	return (dispatcher) => {
		if (dispatcher._conversionApi.schema.checkChild(model.name, "$text"))
 /**
		* This error occurs when a {@link module:engine/model/element~ModelElement model element} is downcasted
		* via {@link module:engine/conversion/downcasthelpers~DowncastHelpers#elementToStructure} helper but the element was
		* allowed to host `$text` by the {@link module:engine/model/schema~ModelSchema model schema}.
		*
		* For instance, this may be the result of `myElement` allowing the content of
		* {@glink framework/deep-dive/schema#generic-items `$block`} in its schema definition:
		*
		* ```ts
		* // Element definition in schema.
		* schema.register( 'myElement', {
		* 	allowContentOf: '$block',
		*
		* 	// ...
		* } );
		*
		* // ...
		*
		* // Conversion of myElement with the use of elementToStructure().
		* editor.conversion.for( 'downcast' ).elementToStructure( {
		* 	model: 'myElement',
		* 	view: ( modelElement, { writer } ) => {
		* 		// ...
		* 	}
		* } );
		* ```
		*
		* In such case, {@link module:engine/conversion/downcasthelpers~DowncastHelpers#elementToElement `elementToElement()`} helper
		* can be used instead to get around this problem:
		*
		* ```ts
		* editor.conversion.for( 'downcast' ).elementToElement( {
		* 	model: 'myElement',
		* 	view: ( modelElement, { writer } ) => {
		* 		// ...
		* 	}
		* } );
		* ```
		*
		* @error conversion-element-to-structure-disallowed-text
		* @param {string} elementName The name of the element the structure is to be created for.
		*/
		throw new CKEditorError("conversion-element-to-structure-disallowed-text", dispatcher, { elementName: model.name });
		dispatcher.on(`insert:${model.name}`, insertStructure(view, createConsumer(model)), { priority: config.converterPriority || "normal" });
		dispatcher.on("reduceChanges", createChangeReducer(model), { priority: "low" });
	};
}
/**
* Model attribute to view element conversion helper.
*
* See {@link ~DowncastHelpers#attributeToElement `.attributeToElement()` downcast helper} for examples.
*
* @param config Conversion configuration.
* @param config.model The key of the attribute to convert from or a `{ key, values }` object. `values` is an array
* of `String`s with possible values if the model attribute is an enumerable.
* @param config.view A view element definition or a function that takes the model attribute value and
* {@link module:engine/view/downcastwriter~ViewDowncastWriter view downcast writer} as parameters and returns a view attribute element.
* If `config.model.values` is given, `config.view` should be an object assigning values from `config.model.values` to view element
* definitions or functions.
* @param config.converterPriority Converter priority.
* @returns Conversion helper.
*/
function downcastAttributeToElement(config) {
	config = cloneDeep(config);
	let model = config.model;
	if (typeof model == "string") model = { key: model };
	let eventName = `attribute:${model.key}`;
	if (model.name) eventName += ":" + model.name;
	if (model.values) for (const modelValue of model.values) config.view[modelValue] = normalizeToElementConfig(config.view[modelValue], "attribute");
	else config.view = normalizeToElementConfig(config.view, "attribute");
	const elementCreator = getFromAttributeCreator(config);
	return (dispatcher) => {
		dispatcher.on(eventName, wrap(elementCreator), { priority: config.converterPriority || "normal" });
	};
}
/**
* Model attribute to view attribute conversion helper.
*
* See {@link ~DowncastHelpers#attributeToAttribute `.attributeToAttribute()` downcast helper} for examples.
*
* @param config Conversion configuration.
* @param config.model The key of the attribute to convert from or a `{ key, values, [ name ] }` object describing
* the attribute key, possible values and, optionally, an element name to convert from.
* @param config.view A view attribute key, or a `{ key, value }` object or a function that takes the model attribute value and returns
* a `{ key, value }` object.
* If `key` is `'class'`, `value` can be a `String` or an array of `String`s. If `key` is `'style'`, `value` is an object with
* key-value pairs. In other cases, `value` is a `String`.
* If `config.model.values` is set, `config.view` should be an object assigning values from `config.model.values` to
* `{ key, value }` objects or a functions.
* @param config.converterPriority Converter priority.
* @returns Conversion helper.
*/
function downcastAttributeToAttribute(config) {
	config = cloneDeep(config);
	let model = config.model;
	if (typeof model == "string") model = { key: model };
	let eventName = `attribute:${model.key}`;
	if (model.name) eventName += ":" + model.name;
	if (model.values) for (const modelValue of model.values) config.view[modelValue] = normalizeToAttributeConfig(config.view[modelValue]);
	else config.view = normalizeToAttributeConfig(config.view);
	const elementCreator = getFromAttributeCreator(config);
	return (dispatcher) => {
		dispatcher.on(eventName, changeAttribute(elementCreator), { priority: config.converterPriority || "normal" });
	};
}
/**
* Model marker to view element conversion helper.
*
* See {@link ~DowncastHelpers#markerToElement `.markerToElement()` downcast helper} for examples.
*
* @param config Conversion configuration.
* @param config.model The name of the model marker (or model marker group) to convert.
* @param config.view A view element definition or a function that takes the model marker data as a parameter and returns a view UI element.
* @param config.converterPriority Converter priority.
* @returns Conversion helper.
*/
function downcastMarkerToElement(config) {
	const view = normalizeToElementConfig(config.view, "ui");
	return (dispatcher) => {
		dispatcher.on(`addMarker:${config.model}`, insertUIElement(view), { priority: config.converterPriority || "normal" });
		dispatcher.on(`removeMarker:${config.model}`, removeUIElement(), { priority: config.converterPriority || "normal" });
	};
}
/**
* Model marker to view data conversion helper.
*
* See {@link ~DowncastHelpers#markerToData `markerToData()` downcast helper} to learn more.
*
* @returns Conversion helper.
*/
function downcastMarkerToData(config) {
	config = cloneDeep(config);
	const group = config.model;
	let view = config.view;
	if (!view) view = (markerName) => ({
		group,
		name: markerName.substr(config.model.length + 1)
	});
	return (dispatcher) => {
		dispatcher.on(`addMarker:${group}`, insertMarkerData(view), { priority: config.converterPriority || "normal" });
		dispatcher.on(`removeMarker:${group}`, removeMarkerData(view), { priority: config.converterPriority || "normal" });
	};
}
/**
* Model marker to highlight conversion helper.
*
* See {@link ~DowncastHelpers#markerToElement `.markerToElement()` downcast helper} for examples.
*
* @param config Conversion configuration.
* @param config.model The name of the model marker (or model marker group) to convert.
* @param config.view A highlight descriptor that will be used for highlighting or a function that takes
* the model marker data as a parameter and returns a highlight descriptor.
* @param config.converterPriority Converter priority.
* @returns Conversion helper.
*/
function downcastMarkerToHighlight(config) {
	return (dispatcher) => {
		dispatcher.on(`addMarker:${config.model}`, highlightText(config.view), { priority: config.converterPriority || "normal" });
		dispatcher.on(`addMarker:${config.model}`, highlightElement(config.view), { priority: config.converterPriority || "normal" });
		dispatcher.on(`removeMarker:${config.model}`, removeHighlight(config.view), { priority: config.converterPriority || "normal" });
	};
}
/**
* Takes `config.model`, and converts it to an object with normalized structure.
*
* @param model Model configuration or element name.
*/
function normalizeModelElementConfig(model) {
	if (typeof model == "string") model = { name: model };
	return {
		name: model.name,
		attributes: model.attributes ? toArray(model.attributes) : [],
		children: !!model.children
	};
}
/**
* Takes `config.view`, and if it is an {@link module:engine/view/elementdefinition~ViewElementDefinition}, converts it
* to a function (because lower level converters accept only element creator functions).
*
* @param view View configuration.
* @param viewElementType View element type to create.
* @returns Element creator function to use in lower level converters.
*/
function normalizeToElementConfig(view, viewElementType) {
	if (typeof view == "function") return view;
	return ((modelData, conversionApi) => createViewElementFromDefinition(view, conversionApi, viewElementType));
}
/**
* Creates a view element instance from the provided {@link module:engine/view/elementdefinition~ViewElementDefinition} and class.
*/
function createViewElementFromDefinition(viewElementDefinition, conversionApi, viewElementType) {
	if (typeof viewElementDefinition == "string") viewElementDefinition = { name: viewElementDefinition };
	let element;
	const viewWriter = conversionApi.writer;
	const attributes = Object.assign({}, viewElementDefinition.attributes);
	if (viewElementType == "container") element = viewWriter.createContainerElement(viewElementDefinition.name, attributes);
	else if (viewElementType == "attribute") {
		const options = { priority: viewElementDefinition.priority || ViewAttributeElement.DEFAULT_PRIORITY };
		element = viewWriter.createAttributeElement(viewElementDefinition.name, attributes, options);
	} else element = viewWriter.createUIElement(viewElementDefinition.name, attributes);
	if (viewElementDefinition.styles) {
		const keys = Object.keys(viewElementDefinition.styles);
		for (const key of keys) viewWriter.setStyle(key, viewElementDefinition.styles[key], element);
	}
	if (viewElementDefinition.classes) {
		const classes = viewElementDefinition.classes;
		if (typeof classes == "string") viewWriter.addClass(classes, element);
		else for (const className of classes) viewWriter.addClass(className, element);
	}
	return element;
}
function getFromAttributeCreator(config) {
	if (config.model.values) return ((modelAttributeValue, conversionApi, data) => {
		const view = config.view[modelAttributeValue];
		if (view) return view(modelAttributeValue, conversionApi, data);
		return null;
	});
	else return config.view;
}
/**
* Takes the configuration, adds default parameters if they do not exist and normalizes other parameters to be used in downcast converters
* for generating a view attribute.
*
* @param view View configuration.
*/
function normalizeToAttributeConfig(view) {
	if (typeof view == "string") return (modelAttributeValue) => ({
		key: view,
		value: modelAttributeValue
	});
	else if (typeof view == "object") if (view.value) return () => view;
	else return (modelAttributeValue) => ({
		key: view.key,
		value: modelAttributeValue
	});
	else return view;
}
/**
* Helper function for `highlight`. Prepares the actual descriptor object using value passed to the converter.
*/
function prepareDescriptor(highlightDescriptor, data, conversionApi) {
	const descriptor = typeof highlightDescriptor == "function" ? highlightDescriptor(data, conversionApi) : { ...highlightDescriptor };
	if (!descriptor) return null;
	if (!descriptor.priority) descriptor.priority = 10;
	if (!descriptor.id) descriptor.id = data.markerName;
	return descriptor;
}
/**
* Creates a function that checks a single differ diff item whether it should trigger reconversion.
*
* @param model A normalized `config.model` converter configuration.
* @param model.name The name of element.
* @param model.attributes The list of attribute names that should trigger reconversion.
* @param model.children Whether the child list change should trigger reconversion.
*/
function createChangeReducerCallback(model) {
	return (node, change) => {
		if (!node.is("element", model.name)) return false;
		if (change.type == "attribute") {
			if (model.attributes.includes(change.attributeKey)) return true;
		} else if (model.children) return true;
		return false;
	};
}
/**
* Creates a `reduceChanges` event handler for reconversion.
*
* @param model A normalized `config.model` converter configuration.
* @param model.name The name of element.
* @param model.attributes The list of attribute names that should trigger reconversion.
* @param model.children Whether the child list change should trigger reconversion.
*/
function createChangeReducer(model) {
	const shouldReplace = createChangeReducerCallback(model);
	return (evt, data) => {
		const reducedChanges = [];
		if (!data.reconvertedElements) data.reconvertedElements = /* @__PURE__ */ new Set();
		for (const change of data.changes) {
			const node = change.type == "attribute" ? change.range.start.nodeAfter : change.position.parent;
			if (!node || !shouldReplace(node, change) || change.type == "reinsert") {
				reducedChanges.push(change);
				continue;
			}
			if (change.type == "insert" && change.action == "rename") data.refreshedItems.add(change.position.nodeAfter);
			if (!data.reconvertedElements.has(node)) {
				data.reconvertedElements.add(node);
				const position = ModelPosition._createBefore(node);
				let changeIndex = reducedChanges.length;
				for (let i = reducedChanges.length - 1; i >= 0; i--) {
					const change = reducedChanges[i];
					const positionRelation = (change.type == "attribute" ? change.range.start : change.position).compareWith(position);
					if (positionRelation == "before" || change.type == "remove" && positionRelation == "same") break;
					changeIndex = i;
				}
				reducedChanges.splice(changeIndex, 0, {
					type: "reinsert",
					name: node.name,
					position,
					length: 1
				});
			}
		}
		data.changes = reducedChanges;
	};
}
/**
* Creates a function that checks if an element and its watched attributes can be consumed and consumes them.
*
* @param model A normalized `config.model` converter configuration.
* @param model.name The name of element.
* @param model.attributes The list of attribute names that should trigger reconversion.
* @param model.children Whether the child list change should trigger reconversion.
*/
function createConsumer(model) {
	return (node, consumable, options = {}) => {
		const events = ["insert"];
		for (const attributeName of model.attributes) if (node.hasAttribute(attributeName)) events.push(`attribute:${attributeName}`);
		if (!events.every((event) => consumable.test(node, event))) return false;
		if (!options.preflight) events.forEach((event) => consumable.consume(node, event));
		return true;
	};
}
/**
* Creates a function that creates view slots.
*
* @returns Function exposed by the writer as `createSlot()`.
*/
function createSlotFactory(element, slotsMap, conversionApi) {
	return (writer, modeOrFilter) => {
		const slot = writer.createContainerElement("$slot");
		let children = null;
		if (modeOrFilter === "children") children = Array.from(element.getChildren());
		else if (typeof modeOrFilter == "function") children = Array.from(element.getChildren()).filter((element) => modeOrFilter(element));
		else
 /**
		* Unknown slot mode was provided to `writer.createSlot()` in the downcast converter.
		*
		* @error conversion-slot-mode-unknown
		* @param {never} modeOrFilter The specified mode or filter.
		*/
		throw new CKEditorError("conversion-slot-mode-unknown", conversionApi.dispatcher, { modeOrFilter });
		slotsMap.set(slot, children);
		return slot;
	};
}
/**
* Checks if all children are covered by slots and there is no child that landed in multiple slots.
*/
function validateSlotsChildren(element, slotsMap, conversionApi) {
	const childrenInSlots = Array.from(slotsMap.values()).flat();
	const uniqueChildrenInSlots = new Set(childrenInSlots);
	if (uniqueChildrenInSlots.size != childrenInSlots.length)
 /**
	* Filters provided to `writer.createSlot()` overlap (at least two filters accept the same child element).
	*
	* @error conversion-slot-filter-overlap
	* @param {module:engine/model/element~ModelElement} element The element of which children would not be properly
	* allocated to multiple slots.
	*/
	throw new CKEditorError("conversion-slot-filter-overlap", conversionApi.dispatcher, { element });
	if (uniqueChildrenInSlots.size != element.childCount)
 /**
	* Filters provided to `writer.createSlot()` are incomplete and exclude at least one children element (one of
	* the children elements would not be assigned to any of the slots).
	*
	* @error conversion-slot-filter-incomplete
	* @param {module:engine/model/element~ModelElement} element The element of which children would not be properly
	* allocated to multiple slots.
	*/
	throw new CKEditorError("conversion-slot-filter-incomplete", conversionApi.dispatcher, { element });
}
/**
* Fill slots with appropriate view elements.
*/
function fillSlots(viewElement, slotsMap, conversionApi, options) {
	conversionApi.mapper.on("modelToViewPosition", toViewPositionMapping, { priority: "highest" });
	let currentSlot = null;
	let currentSlotNodes = null;
	for ([currentSlot, currentSlotNodes] of slotsMap) {
		reinsertOrConvertNodes(viewElement, currentSlotNodes, conversionApi, options);
		conversionApi.writer.setCustomProperty("$structureSlotParent", true, currentSlot.parent);
		conversionApi.writer.move(conversionApi.writer.createRangeIn(currentSlot), conversionApi.writer.createPositionBefore(currentSlot));
		conversionApi.writer.remove(currentSlot);
	}
	conversionApi.mapper.off("modelToViewPosition", toViewPositionMapping);
	function toViewPositionMapping(evt, data) {
		const element = data.modelPosition.nodeAfter;
		const index = currentSlotNodes.indexOf(element);
		if (index < 0) return;
		data.viewPosition = data.mapper.findPositionIn(currentSlot, index);
	}
}
/**
* Inserts view representation of `nodes` into the `viewElement` either by bringing back just removed view nodes
* or by triggering conversion for them.
*/
function reinsertOrConvertNodes(viewElement, modelNodes, conversionApi, options) {
	for (const modelChildNode of modelNodes) if (!reinsertNode(viewElement.root, modelChildNode, conversionApi, options)) conversionApi.convertItem(modelChildNode);
}
/**
* Checks if the view for the given model element could be reused and reinserts it to the view.
*
* @returns `false` if view element can't be reused.
*/
function reinsertNode(viewRoot, modelNode, conversionApi, options) {
	const { writer, mapper } = conversionApi;
	if (!options.reconversion) return false;
	const viewChildNode = mapper.toViewElement(modelNode);
	if (!viewChildNode || viewChildNode.root == viewRoot) return false;
	if (!conversionApi.canReuseView(viewChildNode)) return false;
	writer.move(writer.createRangeOn(viewChildNode), mapper.toViewPosition(ModelPosition._createBefore(modelNode)));
	return true;
}
/**
* The default consumer for insert events.
*
* @param item Model item.
* @param consumable The model consumable.
* @param options.preflight Whether should consume or just check if can be consumed.
*/
function defaultConsumer(item, consumable, { preflight } = {}) {
	if (preflight) return consumable.test(item, "insert");
	else return consumable.consume(item, "insert");
}

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* Fixes all empty roots.
*
* @internal
* @param writer The model writer.
* @returns `true` if any change has been applied, `false` otherwise.
*/
function autoParagraphEmptyRoots(writer) {
	const { schema, document } = writer.model;
	for (const root of document.getRoots()) if (root.isEmpty && !schema.checkChild(root, "$text")) {
		if (schema.checkChild(root, "paragraph")) {
			writer.insertElement("paragraph", root);
			return true;
		}
	}
	return false;
}
/**
* Checks if the given node wrapped with a paragraph would be accepted by the schema in the given position.
*
* @internal
* @param position The position at which to check.
* @param nodeOrType The child node or child type to check.
* @param schema A schema instance used for element validation.
*/
function isParagraphable(position, nodeOrType, schema) {
	const context = schema.createContext(position);
	if (!schema.checkChild(context, "paragraph")) return false;
	if (!schema.checkChild(context.push("paragraph"), nodeOrType)) return false;
	return true;
}
/**
* Inserts a new paragraph at the given position and returns a position inside that paragraph.
*
* @internal
* @param position The position where a paragraph should be inserted.
* @param writer The model writer.
* @returns  Position inside the created paragraph.
*/
function wrapInParagraph(position, writer) {
	const paragraph = writer.createElement("paragraph");
	writer.insert(paragraph, position);
	return writer.createPositionAt(paragraph, 0);
}

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* Contains the {@link module:engine/view/view view} to {@link module:engine/model/model model} converters for
* {@link module:engine/conversion/upcastdispatcher~UpcastDispatcher}.
*
* @module engine/conversion/upcasthelpers
*/
/**
* Upcast conversion helper functions.
*
* Learn more about {@glink framework/deep-dive/conversion/upcast upcast helpers}.
*
* @extends module:engine/conversion/conversionhelpers~ConversionHelpers
*/
var UpcastHelpers = class extends ConversionHelpers {
	/**
	* View element to model element conversion helper.
	*
	* This conversion results in creating a model element. For example,
	* view `<p>Foo</p>` becomes `<paragraph>Foo</paragraph>` in the model.
	*
	* Keep in mind that the element will be inserted only if it is allowed
	* by {@link module:engine/model/schema~ModelSchema schema} configuration.
	*
	* ```ts
	* editor.conversion.for( 'upcast' ).elementToElement( {
	* 	view: 'p',
	* 	model: 'paragraph'
	* } );
	*
	* editor.conversion.for( 'upcast' ).elementToElement( {
	* 	view: 'p',
	* 	model: 'paragraph',
	* 	converterPriority: 'high'
	* } );
	*
	* editor.conversion.for( 'upcast' ).elementToElement( {
	* 	view: {
	* 		name: 'p',
	* 		classes: 'fancy'
	* 	},
	* 	model: 'fancyParagraph'
	* } );
	*
	* editor.conversion.for( 'upcast' ).elementToElement( {
	* 	view: {
	* 		name: 'p',
	* 		classes: 'heading'
	* 	},
	* 	model: ( viewElement, conversionApi ) => {
	* 		const modelWriter = conversionApi.writer;
	*
	* 		return modelWriter.createElement( 'heading', { level: viewElement.getAttribute( 'data-level' ) } );
	* 	}
	* } );
	* ```
	*
	* See {@link module:engine/conversion/conversion~Conversion#for `conversion.for()`} to learn how to add a converter
	* to the conversion process.
	*
	* @param config Conversion configuration.
	* @param config.view Pattern matching all view elements which should be converted. If not set, the converter
	* will fire for every view element.
	* @param config.model Name of the model element, a model element instance or a function that takes a view element
	* and {@link module:engine/conversion/upcastdispatcher~UpcastConversionApi upcast conversion API}
	* and returns a model element. The model element will be inserted in the model.
	* @param config.converterPriority Converter priority.
	*/
	elementToElement(config) {
		return this.add(upcastElementToElement(config));
	}
	/**
	* View element to model attribute conversion helper.
	*
	* This conversion results in setting an attribute on a model node. For example, view `<strong>Foo</strong>` becomes
	* `Foo` {@link module:engine/model/text~ModelText model text node} with `bold` attribute set to `true`.
	*
	* This helper is meant to set a model attribute on all the elements that are inside the converted element:
	*
	* ```
	* <strong>Foo</strong>   -->   <strong><p>Foo</p></strong>   -->   <paragraph><$text bold="true">Foo</$text></paragraph>
	* ```
	*
	* Above is a sample of HTML code, that goes through autoparagraphing (first step) and then is converted (second step).
	* Even though `<strong>` is over `<p>` element, `bold="true"` was added to the text. See
	* {@link module:engine/conversion/upcasthelpers~UpcastHelpers#attributeToAttribute} for comparison.
	*
	* Keep in mind that the attribute will be set only if it is allowed by
	* {@link module:engine/model/schema~ModelSchema schema} configuration.
	*
	* ```ts
	* editor.conversion.for( 'upcast' ).elementToAttribute( {
	* 	view: 'strong',
	* 	model: 'bold'
	* } );
	*
	* editor.conversion.for( 'upcast' ).elementToAttribute( {
	* 	view: 'strong',
	* 	model: 'bold',
	* 	converterPriority: 'high'
	* } );
	*
	* editor.conversion.for( 'upcast' ).elementToAttribute( {
	* 	view: {
	* 		name: 'span',
	* 		classes: 'bold'
	* 	},
	* 	model: 'bold'
	* } );
	*
	* editor.conversion.for( 'upcast' ).elementToAttribute( {
	* 	view: {
	* 		name: 'span',
	* 		classes: [ 'styled', 'styled-dark' ]
	* 	},
	* 	model: {
	* 		key: 'styled',
	* 		value: 'dark'
	* 	}
	* } );
	*
	* editor.conversion.for( 'upcast' ).elementToAttribute( {
	* 	view: {
	* 		name: 'span',
	* 		styles: {
	* 			'font-size': /[\s\S]+/
	* 		}
	* 	},
	* 	model: {
	* 		key: 'fontSize',
	* 		value: ( viewElement, conversionApi ) => {
	* 			const fontSize = viewElement.getStyle( 'font-size' );
	* 			const value = fontSize.substr( 0, fontSize.length - 2 );
	*
	* 			if ( value <= 10 ) {
	* 				return 'small';
	* 			} else if ( value > 12 ) {
	* 				return 'big';
	* 			}
	*
	* 			return null;
	* 		}
	* 	}
	* } );
	* ```
	*
	* See {@link module:engine/conversion/conversion~Conversion#for `conversion.for()`} to learn how to add a converter
	* to the conversion process.
	*
	* @param config Conversion configuration.
	* @param config.view Pattern matching all view elements which should be converted.
	* @param config.model Model attribute key or an object with `key` and `value` properties, describing
	* the model attribute. `value` property may be set as a function that takes a view element and
	* {@link module:engine/conversion/upcastdispatcher~UpcastConversionApi upcast conversion API} and returns the value.
	* If `String` is given, the model attribute value will be set to `true`.
	* @param config.converterPriority Converter priority. Defaults to `low`.
	*/
	elementToAttribute(config) {
		return this.add(upcastElementToAttribute(config));
	}
	/**
	* View attribute to model attribute conversion helper.
	*
	* This conversion results in setting an attribute on a model node. For example, view `<img src="foo.jpg"></img>` becomes
	* `<imageBlock source="foo.jpg"></imageBlock>` in the model.
	*
	* This helper is meant to convert view attributes from view elements which got converted to the model, so the view attribute
	* is set only on the corresponding model node:
	*
	* ```
	* <div class="dark"><div>foo</div></div>    -->    <div dark="true"><div>foo</div></div>
	* ```
	*
	* Above, `class="dark"` attribute is added only to the `<div>` elements that has it. This is in contrast to
	* {@link module:engine/conversion/upcasthelpers~UpcastHelpers#elementToAttribute} which sets attributes for
	* all the children in the model:
	*
	* ```
	* <strong>Foo</strong>   -->   <strong><p>Foo</p></strong>   -->   <paragraph><$text bold="true">Foo</$text></paragraph>
	* ```
	*
	* Above is a sample of HTML code, that goes through autoparagraphing (first step) and then is converted (second step).
	* Even though `<strong>` is over `<p>` element, `bold="true"` was added to the text.
	*
	* Keep in mind that the attribute will be set only if it is allowed by
	* {@link module:engine/model/schema~ModelSchema schema} configuration.
	*
	* ```ts
	* editor.conversion.for( 'upcast' ).attributeToAttribute( {
	* 	view: 'src',
	* 	model: 'source'
	* } );
	*
	* editor.conversion.for( 'upcast' ).attributeToAttribute( {
	* 	view: { key: 'src' },
	* 	model: 'source'
	* } );
	*
	* editor.conversion.for( 'upcast' ).attributeToAttribute( {
	* 	view: { key: 'src' },
	* 	model: 'source',
	* 	converterPriority: 'normal'
	* } );
	*
	* editor.conversion.for( 'upcast' ).attributeToAttribute( {
	* 	view: {
	* 		key: 'data-style',
	* 		value: /[\s\S]+/
	* 	},
	* 	model: 'styled'
	* } );
	*
	* editor.conversion.for( 'upcast' ).attributeToAttribute( {
	* 	view: {
	* 		name: 'img',
	* 		key: 'class',
	* 		value: 'styled-dark'
	* 	},
	* 	model: {
	* 		key: 'styled',
	* 		value: 'dark'
	* 	}
	* } );
	*
	* editor.conversion.for( 'upcast' ).attributeToAttribute( {
	* 	view: {
	* 		key: 'class',
	* 		value: /styled-[\S]+/
	* 	},
	* 	model: {
	* 		key: 'styled'
	* 		value: ( viewElement, conversionApi ) => {
	* 			const regexp = /styled-([\S]+)/;
	* 			const match = viewElement.getAttribute( 'class' ).match( regexp );
	*
	* 			return match[ 1 ];
	* 		}
	* 	}
	* } );
	* ```
	*
	* Converting styles works a bit differently as it requires `view.styles` to be an object and by default
	* a model attribute will be set to `true` by such a converter. You can set the model attribute to any value by providing the `value`
	* callback that returns the desired value.
	*
	* ```ts
	* // Default conversion of font-weight style will result in setting bold attribute to true.
	* editor.conversion.for( 'upcast' ).attributeToAttribute( {
	* 	view: {
	* 		styles: {
	* 			'font-weight': 'bold'
	* 		}
	* 	},
	* 	model: 'bold'
	* } );
	*
	* // This converter will pass any style value to the `lineHeight` model attribute.
	* editor.conversion.for( 'upcast' ).attributeToAttribute( {
	* 	view: {
	* 		styles: {
	* 			'line-height': /[\s\S]+/
	* 		}
	* 	},
	* 	model: {
	* 		key: 'lineHeight',
	* 		value: ( viewElement, conversionApi ) => viewElement.getStyle( 'line-height' )
	* 	}
	* } );
	* ```
	*
	* See {@link module:engine/conversion/conversion~Conversion#for `conversion.for()`} to learn how to add a converter
	* to the conversion process.
	*
	* @param config Conversion configuration.
	* @param config.view Specifies which view attribute will be converted. If a `String` is passed,
	* attributes with given key will be converted. If an `Object` is passed, it must have a required `key` property,
	* specifying view attribute key, and may have an optional `value` property, specifying view attribute value and optional `name`
	* property specifying a view element name from/on which the attribute should be converted. `value` can be given as a `String`,
	* a `RegExp` or a function callback, that takes view attribute value as the only parameter and returns `Boolean`.
	* @param config.model Model attribute key or an object with `key` and `value` properties, describing
	* the model attribute. `value` property may be set as a function that takes a view element and
	* {@link module:engine/conversion/upcastdispatcher~UpcastConversionApi upcast conversion API} and returns the value.
	* If `String` is given, the model attribute value will be same as view attribute value.
	* @param config.converterPriority Converter priority. Defaults to `low`.
	*/
	attributeToAttribute(config) {
		return this.add(upcastAttributeToAttribute(config));
	}
	/**
	* View element to model marker conversion helper.
	*
	* This conversion results in creating a model marker. For example, if the marker was stored in a view as an element:
	* `<p>Fo<span data-marker="comment" data-comment-id="7"></span>o</p><p>B<span data-marker="comment" data-comment-id="7"></span>ar</p>`,
	* after the conversion is done, the marker will be available in
	* {@link module:engine/model/model~Model#markers model document markers}.
	*
	* **Note**: When this helper is used in the data upcast in combination with
	* {@link module:engine/conversion/downcasthelpers~DowncastHelpers#markerToData `#markerToData()`} in the data downcast,
	* then invalid HTML code (e.g. a span between table cells) may be produced by the latter converter.
	*
	* In most of the cases, the {@link #dataToMarker} should be used instead.
	*
	* ```ts
	* editor.conversion.for( 'upcast' ).elementToMarker( {
	* 	view: 'marker-search',
	* 	model: 'search'
	* } );
	*
	* editor.conversion.for( 'upcast' ).elementToMarker( {
	* 	view: 'marker-search',
	* 	model: 'search',
	* 	converterPriority: 'high'
	* } );
	*
	* editor.conversion.for( 'upcast' ).elementToMarker( {
	* 	view: 'marker-search',
	* 	model: ( viewElement, conversionApi ) => 'comment:' + viewElement.getAttribute( 'data-comment-id' )
	* } );
	*
	* editor.conversion.for( 'upcast' ).elementToMarker( {
	* 	view: {
	* 		name: 'span',
	* 		attributes: {
	* 			'data-marker': 'search'
	* 		}
	* 	},
	* 	model: 'search'
	* } );
	* ```
	*
	* See {@link module:engine/conversion/conversion~Conversion#for `conversion.for()`} to learn how to add a converter
	* to the conversion process.
	*
	* @param config Conversion configuration.
	* @param config.view Pattern matching all view elements which should be converted.
	* @param config.model Name of the model marker, or a function that takes a view element and returns
	* a model marker name.
	* @param config.converterPriority Converter priority.
	*/
	elementToMarker(config) {
		return this.add(upcastElementToMarker(config));
	}
	/**
	* View-to-model marker conversion helper.
	*
	* Converts view data created by {@link module:engine/conversion/downcasthelpers~DowncastHelpers#markerToData `#markerToData()`}
	* back to a model marker.
	*
	* This converter looks for specific view elements and view attributes that mark marker boundaries. See
	* {@link module:engine/conversion/downcasthelpers~DowncastHelpers#markerToData `#markerToData()`} to learn what view data
	* is expected by this converter.
	*
	* The `config.view` property is equal to the marker group name to convert.
	*
	* By default, this converter creates markers with the `group:name` name convention (to match the default `markerToData` conversion).
	*
	* The conversion configuration can take a function that will generate a marker name.
	* If such function is set as the `config.model` parameter, it is passed the `name` part from the view element or attribute and it is
	* expected to return a string with the marker name.
	*
	* Basic usage:
	*
	* ```ts
	* // Using the default conversion.
	* // In this case, all markers from the `comment` group will be converted.
	* // The conversion will look for `<comment-start>` and `<comment-end>` tags and
	* // `data-comment-start-before`, `data-comment-start-after`,
	* // `data-comment-end-before` and `data-comment-end-after` attributes.
	* editor.conversion.for( 'upcast' ).dataToMarker( {
	* 	view: 'comment'
	* } );
	* ```
	*
	* An example of a model that may be generated by this conversion:
	*
	* ```
	* // View:
	* <p>Foo<comment-start name="commentId:uid"></comment-start>bar</p>
	* <figure data-comment-end-after="commentId:uid" class="image"><img src="abc.jpg" /></figure>
	*
	* // Model:
	* <paragraph>Foo[bar</paragraph>
	* <imageBlock src="abc.jpg"></imageBlock>]
	* ```
	*
	* Where `[]` are boundaries of a marker that will receive the `comment:commentId:uid` name.
	*
	* Other examples of usage:
	*
	* ```ts
	* // Using a custom function which is the same as the default conversion:
	* editor.conversion.for( 'upcast' ).dataToMarker( {
	* 	view: 'comment',
	* 	model: ( name, conversionApi ) => 'comment:' + name,
	* } );
	*
	* // Using the converter priority:
	* editor.conversion.for( 'upcast' ).dataToMarker( {
	* 	view: 'comment',
	* 	model: ( name, conversionApi ) => 'comment:' + name,
	* 	converterPriority: 'high'
	* } );
	* ```
	*
	* See {@link module:engine/conversion/conversion~Conversion#for `conversion.for()`} to learn how to add a converter
	* to the conversion process.
	*
	* @param config Conversion configuration.
	* @param config.view The marker group name to convert.
	* @param config.model A function that takes the `name` part from the view element or attribute and
	* {@link module:engine/conversion/upcastdispatcher~UpcastConversionApi upcast conversion API} and returns the marker name.
	* @param config.converterPriority Converter priority.
	*/
	dataToMarker(config) {
		return this.add(upcastDataToMarker(config));
	}
};
/**
* Function factory, creates a converter that converts
* {@link module:engine/view/documentfragment~ViewDocumentFragment view document fragment}
* or all children of {@link module:engine/view/element~ViewElement} into
* {@link module:engine/model/documentfragment~ModelDocumentFragment model document fragment}.
* This is the "entry-point" converter for upcast (view to model conversion). This converter starts the conversion of all children
* of passed view document fragment. Those children {@link module:engine/view/node~ViewNode view nodes} are then
* handled by other converters.
*
* This also a "default", last resort converter for all view elements that has not been converted by other converters.
* When a view element is being converted to the model but it does not have converter specified, that view element
* will be converted to {@link module:engine/model/documentfragment~ModelDocumentFragment model document fragment} and returned.
*
* @returns Universal converter for view {@link module:engine/view/documentfragment~ViewDocumentFragment fragments} and
* {@link module:engine/view/element~ViewElement elements} that returns
* {@link module:engine/model/documentfragment~ModelDocumentFragment model fragment} with children of converted view item.
*
* @internal
*/
function convertToModelFragment() {
	return (evt, data, conversionApi) => {
		if (!data.modelRange && conversionApi.consumable.consume(data.viewItem, { name: true })) {
			const { modelRange, modelCursor } = conversionApi.convertChildren(data.viewItem, data.modelCursor);
			data.modelRange = modelRange;
			data.modelCursor = modelCursor;
		}
	};
}
/**
* Function factory, creates a converter that converts
* {@link module:engine/view/text~ViewText} to {@link module:engine/model/text~ModelText}.
*
* @returns {@link module:engine/view/text~ViewText View text} converter.
* @internal
*/
function convertText() {
	return (evt, data, { schema, consumable, writer }) => {
		let position = data.modelCursor;
		if (!consumable.test(data.viewItem)) return;
		if (!schema.checkChild(position, "$text")) {
			if (!isParagraphable(position, "$text", schema)) return;
			if (data.viewItem.data.trim().length == 0) return;
			position = wrapInParagraph(position, writer);
		}
		consumable.consume(data.viewItem);
		const text = writer.createText(data.viewItem.data);
		writer.insert(text, position);
		data.modelRange = writer.createRange(position, position.getShiftedBy(text.offsetSize));
		data.modelCursor = data.modelRange.end;
	};
}
/**
* Function factory, creates a callback function which converts a {@link module:engine/view/selection~ViewSelection
* view selection} taken from the {@link module:engine/view/document~ViewDocument#event:selectionChange} event
* and sets in on the {@link module:engine/model/document~ModelDocument#selection model}.
*
* **Note**: because there is no view selection change dispatcher nor any other advanced view selection to model
* conversion mechanism, the callback should be set directly on view document.
*
* ```ts
* view.document.on( 'selectionChange', convertSelectionChange( modelDocument, mapper ) );
* ```
*
* @param model Data model.
* @param mapper Conversion mapper.
* @returns {@link module:engine/view/document~ViewDocument#event:selectionChange} callback function.
* @internal
*/
function convertSelectionChange(model, mapper) {
	return (evt, data) => {
		const viewSelection = data.newSelection;
		const ranges = [];
		for (const viewRange of viewSelection.getRanges()) ranges.push(mapper.toModelRange(viewRange));
		const modelSelection = model.createSelection(ranges, { backward: viewSelection.isBackward });
		if (!modelSelection.isEqual(model.document.selection)) model.change((writer) => {
			writer.setSelection(modelSelection);
		});
	};
}
/**
* View element to model element conversion helper.
*
* See {@link ~UpcastHelpers#elementToElement `.elementToElement()` upcast helper} for examples.
*
* @param config Conversion configuration.
* @param config.view Pattern matching all view elements which should be converted. If not
* set, the converter will fire for every view element.
* @param config.model Name of the model element, a model element
* instance or a function that takes a view element and returns a model element. The model element will be inserted in the model.
* @param config.converterPriority Converter priority.
* @returns Conversion helper.
*/
function upcastElementToElement(config) {
	config = cloneDeep(config);
	const converter = prepareToElementConverter(config);
	const elementName = getViewElementNameFromConfig(config.view);
	const eventName = elementName ? `element:${elementName}` : "element";
	return (dispatcher) => {
		dispatcher.on(eventName, converter, { priority: config.converterPriority || "normal" });
	};
}
/**
* View element to model attribute conversion helper.
*
* See {@link ~UpcastHelpers#elementToAttribute `.elementToAttribute()` upcast helper} for examples.
*
* @param config Conversion configuration.
* @param config.view Pattern matching all view elements which should be converted.
* @param config.model Model attribute key or an object with `key` and `value` properties, describing
* the model attribute. `value` property may be set as a function that takes a view element and returns the value.
* If `String` is given, the model attribute value will be set to `true`.
* @param config.converterPriority Converter priority. Defaults to `low`.
* @returns Conversion helper.
*/
function upcastElementToAttribute(config) {
	config = cloneDeep(config);
	normalizeModelAttributeConfig(config);
	const converter = prepareToAttributeConverter(config, false);
	const elementName = getViewElementNameFromConfig(config.view);
	const eventName = elementName ? `element:${elementName}` : "element";
	return (dispatcher) => {
		dispatcher.on(eventName, converter, { priority: config.converterPriority || "low" });
	};
}
/**
* View attribute to model attribute conversion helper.
*
* See {@link ~UpcastHelpers#attributeToAttribute `.attributeToAttribute()` upcast helper} for examples.
*
* @param config Conversion configuration.
* @param config.view Specifies which view attribute will be converted. If a `String` is passed,
* attributes with given key will be converted. If an `Object` is passed, it must have a required `key` property,
* specifying view attribute key, and may have an optional `value` property, specifying view attribute value and optional `name`
* property specifying a view element name from/on which the attribute should be converted. `value` can be given as a `String`,
* a `RegExp` or a function callback, that takes view attribute value as the only parameter and returns `Boolean`.
* @param config.model Model attribute key or an object with `key` and `value` properties, describing
* the model attribute. `value` property may be set as a function that takes a view element and returns the value.
* If `String` is given, the model attribute value will be same as view attribute value.
* @param config.converterPriority Converter priority. Defaults to `low`.
* @returns Conversion helper.
*/
function upcastAttributeToAttribute(config) {
	config = cloneDeep(config);
	let viewKey = null;
	if (typeof config.view == "string" || config.view.key) viewKey = normalizeViewAttributeKeyValueConfig(config);
	normalizeModelAttributeConfig(config, viewKey);
	const converter = prepareToAttributeConverter(config, true);
	return (dispatcher) => {
		dispatcher.on("element", converter, { priority: config.converterPriority || "low" });
	};
}
/**
* View element to model marker conversion helper.
*
* See {@link ~UpcastHelpers#elementToMarker `.elementToMarker()` upcast helper} for examples.
*
* @param config Conversion configuration.
* @param config.view Pattern matching all view elements which should be converted.
* @param config.model Name of the model marker, or a function that takes a view element and returns
* a model marker name.
* @param config.converterPriority Converter priority.
* @returns Conversion helper.
*/
function upcastElementToMarker(config) {
	const model = normalizeElementToMarkerModelConfig(config.model);
	return upcastElementToElement({
		...config,
		model
	});
}
/**
* View data to model marker conversion helper.
*
* See {@link ~UpcastHelpers#dataToMarker} to learn more.
*
* @returns Conversion helper.
*/
function upcastDataToMarker(config) {
	config = cloneDeep(config);
	if (!config.model) config.model = (name) => {
		return name ? config.view + ":" + name : config.view;
	};
	const normalizedConfig = {
		view: config.view,
		model: config.model
	};
	const converterStart = prepareToElementConverter(normalizeDataToMarkerConfig(normalizedConfig, "start"));
	const converterEnd = prepareToElementConverter(normalizeDataToMarkerConfig(normalizedConfig, "end"));
	return (dispatcher) => {
		dispatcher.on(`element:${config.view}-start`, converterStart, { priority: config.converterPriority || "normal" });
		dispatcher.on(`element:${config.view}-end`, converterEnd, { priority: config.converterPriority || "normal" });
		const basePriority = priorities.low;
		const maxPriority = priorities.highest;
		const priorityFactor = priorities.get(config.converterPriority) / maxPriority;
		dispatcher.on("element", upcastAttributeToMarker(normalizedConfig), { priority: basePriority + priorityFactor });
	};
}
/**
* Function factory, returns a callback function which converts view attributes to a model marker.
*
* The converter looks for elements with `data-group-start-before`, `data-group-start-after`, `data-group-end-before`
* and `data-group-end-after` attributes and inserts `$marker` model elements before/after those elements.
* `group` part is specified in `config.view`.
*
* @returns Marker converter.
*/
function upcastAttributeToMarker(config) {
	return (evt, data, conversionApi) => {
		const attrName = `data-${config.view}`;
		if (!conversionApi.consumable.test(data.viewItem, { attributes: attrName + "-end-after" }) && !conversionApi.consumable.test(data.viewItem, { attributes: attrName + "-start-after" }) && !conversionApi.consumable.test(data.viewItem, { attributes: attrName + "-end-before" }) && !conversionApi.consumable.test(data.viewItem, { attributes: attrName + "-start-before" })) return;
		if (!data.modelRange) Object.assign(data, conversionApi.convertChildren(data.viewItem, data.modelCursor));
		if (conversionApi.consumable.consume(data.viewItem, { attributes: attrName + "-end-after" })) addMarkerElements(data.modelRange.end, data.viewItem.getAttribute(attrName + "-end-after").split(","));
		if (conversionApi.consumable.consume(data.viewItem, { attributes: attrName + "-start-after" })) addMarkerElements(data.modelRange.end, data.viewItem.getAttribute(attrName + "-start-after").split(","));
		if (conversionApi.consumable.consume(data.viewItem, { attributes: attrName + "-end-before" })) addMarkerElements(data.modelRange.start, data.viewItem.getAttribute(attrName + "-end-before").split(","));
		if (conversionApi.consumable.consume(data.viewItem, { attributes: attrName + "-start-before" })) addMarkerElements(data.modelRange.start, data.viewItem.getAttribute(attrName + "-start-before").split(","));
		function addMarkerElements(position, markerViewNames) {
			for (const markerViewName of markerViewNames) {
				const markerName = config.model(markerViewName, conversionApi);
				const element = conversionApi.writer.createElement("$marker", { "data-name": markerName });
				conversionApi.writer.insert(element, position);
				if (data.modelCursor.isEqual(position)) data.modelCursor = data.modelCursor.getShiftedBy(1);
				else data.modelCursor = data.modelCursor._getTransformedByInsertion(position, 1);
				data.modelRange = data.modelRange._getTransformedByInsertion(position, 1)[0];
			}
		}
	};
}
/**
* Helper function for from-view-element conversion. Checks if `config.view` directly specifies converted view element's name
* and if so, returns it.
*
* @param config Conversion view config.
* @returns View element name or `null` if name is not directly set.
*/
function getViewElementNameFromConfig(viewConfig) {
	if (typeof viewConfig == "string") return viewConfig;
	if (typeof viewConfig == "object" && typeof viewConfig.name == "string") return viewConfig.name;
	return null;
}
/**
* Helper for to-model-element conversion. Takes a config object and returns a proper converter function.
*
* @param config Conversion configuration.
* @returns View to model converter.
*/
function prepareToElementConverter(config) {
	const matcher = new Matcher(config.view);
	return (evt, data, conversionApi) => {
		const matcherResult = matcher.match(data.viewItem);
		if (!matcherResult) return;
		const match = matcherResult.match;
		match.name = true;
		if (!conversionApi.consumable.test(data.viewItem, match)) return;
		const modelElement = getModelElement(config.model, data.viewItem, conversionApi);
		if (!modelElement) return;
		if (!conversionApi.safeInsert(modelElement, data.modelCursor)) return;
		conversionApi.consumable.consume(data.viewItem, match);
		conversionApi.convertChildren(data.viewItem, modelElement);
		conversionApi.updateConversionResult(modelElement, data);
	};
}
/**
* Helper function for upcasting-to-element converter. Takes the model configuration, the converted view element
* and a writer instance and returns a model element instance to be inserted in the model.
*
* @param model Model conversion configuration.
* @param input The converted view node.
* @param conversionApi The upcast conversion API.
*/
function getModelElement(model, input, conversionApi) {
	if (model instanceof Function) return model(input, conversionApi);
	else return conversionApi.writer.createElement(model);
}
/**
* Helper function view-attribute-to-model-attribute helper. Normalizes `config.view` which was set as `String` or
* as an `Object` with `key`, `value` and `name` properties. Normalized `config.view` has is compatible with
* {@link module:engine/view/matcher~MatcherPattern}.
*
* @param config Conversion config.
* @returns Key of the converted view attribute.
*/
function normalizeViewAttributeKeyValueConfig(config) {
	if (typeof config.view == "string") config.view = { key: config.view };
	const key = config.view.key;
	const value = typeof config.view.value == "undefined" ? /[\s\S]*/ : config.view.value;
	let normalized;
	if (key == "class" || key == "style") normalized = { [key == "class" ? "classes" : "styles"]: value };
	else normalized = { attributes: { [key]: value } };
	if (config.view.name) normalized.name = config.view.name;
	config.view = normalized;
	return key;
}
/**
* Helper function that normalizes `config.model` in from-model-attribute conversion. `config.model` can be set
* as a `String`, an `Object` with only `key` property or an `Object` with `key` and `value` properties. Normalized
* `config.model` is an `Object` with `key` and `value` properties.
*
* @param config Conversion config.
* @param viewAttributeKeyToCopy Key of the converted view attribute. If it is set, model attribute value
* will be equal to view attribute value.
*/
function normalizeModelAttributeConfig(config, viewAttributeKeyToCopy = null) {
	const defaultModelValue = viewAttributeKeyToCopy === null ? true : (viewElement) => viewElement.getAttribute(viewAttributeKeyToCopy);
	config.model = {
		key: typeof config.model != "object" ? config.model : config.model.key,
		value: typeof config.model != "object" || typeof config.model.value == "undefined" ? defaultModelValue : config.model.value
	};
}
/**
* Helper for to-model-attribute conversion. Takes the model attribute name and conversion configuration and returns
* a proper converter function.
*
* @param config Conversion configuration. It is possible to provide multiple configurations in an array.
* @param shallow If set to `true` the attribute will be set only on top-level nodes. Otherwise, it will be set
* on all elements in the range.
*/
function prepareToAttributeConverter(config, shallow) {
	const matcher = new Matcher(config.view);
	return (evt, data, conversionApi) => {
		if (!data.modelRange && shallow) return;
		const match = matcher.match(data.viewItem);
		if (!match) return;
		if (onlyViewNameIsDefined(config.view, data.viewItem)) match.match.name = true;
		else delete match.match.name;
		if (!conversionApi.consumable.test(data.viewItem, match.match)) return;
		const modelKey = config.model.key;
		const modelValue = typeof config.model.value == "function" ? config.model.value(data.viewItem, conversionApi, data) : config.model.value;
		if (modelValue === null || modelValue === void 0) return;
		if (!data.modelRange) Object.assign(data, conversionApi.convertChildren(data.viewItem, data.modelCursor));
		if (setAttributeOn(data.modelRange, {
			key: modelKey,
			value: modelValue
		}, shallow, conversionApi)) {
			if (conversionApi.consumable.test(data.viewItem, { name: true })) match.match.name = true;
			conversionApi.consumable.consume(data.viewItem, match.match);
		}
	};
}
/**
* Helper function that checks if element name should be consumed in attribute converters.
*
* @param viewConfig Conversion view config.
*/
function onlyViewNameIsDefined(viewConfig, viewItem) {
	const configToTest = typeof viewConfig == "function" ? viewConfig(viewItem) : viewConfig;
	if (typeof configToTest == "object" && !getViewElementNameFromConfig(configToTest)) return false;
	return !configToTest.classes && !configToTest.attributes && !configToTest.styles;
}
/**
* Helper function for to-model-attribute converter. Sets model attribute on given range.
* Checks {@link module:engine/model/schema~ModelSchema} to ensure proper model structure.
*
* If any node on the given range has already defined an attribute with the same name, its value will not be updated.
*
* @param modelRange Model range on which attribute should be set.
* @param modelAttribute Model attribute to set.
* @param conversionApi Conversion API.
* @param shallow If set to `true` the attribute will be set only on top-level nodes. Otherwise, it will be set
* on all elements in the range.
* @returns `true` if attribute was set on at least one node from given `modelRange`.
*/
function setAttributeOn(modelRange, modelAttribute, shallow, conversionApi) {
	let result = false;
	for (const node of Array.from(modelRange.getItems({ shallow }))) {
		if (!conversionApi.schema.checkAttribute(node, modelAttribute.key)) continue;
		result = true;
		if (node.hasAttribute(modelAttribute.key)) continue;
		conversionApi.writer.setAttribute(modelAttribute.key, modelAttribute.value, node);
	}
	return result;
}
/**
* Helper function for upcasting-to-marker conversion. Takes the config in a format requested by `upcastElementToMarker()`
* function and converts it to a format that is supported by `upcastElementToElement()` function.
*/
function normalizeElementToMarkerModelConfig(model) {
	return (viewElement, conversionApi) => {
		const markerName = typeof model == "string" ? model : model(viewElement, conversionApi);
		return conversionApi.writer.createElement("$marker", { "data-name": markerName });
	};
}
/**
* Helper function for upcasting-to-marker conversion. Takes the config in a format requested by `upcastDataToMarker()`
* function and converts it to a format that is supported by `upcastElementToElement()` function.
*/
function normalizeDataToMarkerConfig(config, type) {
	const elementCreatorFunction = (viewElement, conversionApi) => {
		const viewName = viewElement.getAttribute("name");
		const markerName = config.model(viewName, conversionApi);
		return conversionApi.writer.createElement("$marker", { "data-name": markerName });
	};
	return {
		view: `${config.view}-${type}`,
		model: elementCreatorFunction
	};
}

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* @module engine/controller/editingcontroller
*/
const EditingControllerBase = /* #__PURE__ */ ObservableMixin();
/**
* A controller for the editing pipeline. The editing pipeline controls the {@link ~EditingController#model model} rendering,
* including selection handling. It also creates the {@link ~EditingController#view view} which builds a
* browser-independent virtualization over the DOM elements. The editing controller also attaches default converters.
*/
var EditingController = class extends EditingControllerBase {
	/**
	* Editor model.
	*/
	model;
	/**
	* Editing view controller.
	*/
	view;
	/**
	* A mapper that describes the model-view binding.
	*/
	mapper;
	/**
	* Downcast dispatcher that converts changes from the model to the {@link #view editing view}.
	*/
	downcastDispatcher;
	/**
	* Creates an editing controller instance.
	*
	* @param model Editing model.
	* @param stylesProcessor The styles processor instance.
	*/
	constructor(model, stylesProcessor) {
		super();
		this.model = model;
		this.view = new EditingView(stylesProcessor);
		this.mapper = new Mapper();
		this.downcastDispatcher = new DowncastDispatcher({
			mapper: this.mapper,
			schema: model.schema
		});
		const doc = this.model.document;
		const selection = doc.selection;
		const markers = this.model.markers;
		this.listenTo(this.model, "_beforeChanges", () => {
			this.view._disableRendering(true);
		}, { priority: "highest" });
		this.listenTo(this.model, "_afterChanges", () => {
			this.view._disableRendering(false);
		}, { priority: "lowest" });
		this.listenTo(doc, "change", () => {
			this.view.change((writer) => {
				this.downcastDispatcher.convertChanges(doc.differ, markers, writer);
				this.downcastDispatcher.convertSelection(selection, markers, writer);
			});
		}, { priority: "low" });
		this.listenTo(this.view.document, "selectionChange", convertSelectionChange(this.model, this.mapper));
		this.downcastDispatcher.on("insert:$text", insertText(), { priority: "lowest" });
		this.downcastDispatcher.on("insert", insertAttributesAndChildren(), { priority: "lowest" });
		this.downcastDispatcher.on("remove", remove(), { priority: "low" });
		this.downcastDispatcher.on("cleanSelection", cleanSelection());
		this.downcastDispatcher.on("selection", convertRangeSelection(), { priority: "low" });
		this.downcastDispatcher.on("selection", convertCollapsedSelection(), { priority: "low" });
		this.view.document.roots.bindTo(this.model.document.roots).using((root) => {
			if (root.rootName == "$graveyard") return null;
			const viewRoot = new ViewRootEditableElement(this.view.document, root.name);
			viewRoot.rootName = root.rootName;
			this.mapper.bindElements(root, viewRoot);
			return viewRoot;
		});
	}
	/**
	* Removes all event listeners attached to the `EditingController`. Destroys all objects created
	* by `EditingController` that need to be destroyed.
	*/
	destroy() {
		this.view.destroy();
		this.stopListening();
	}
	/**
	* Calling this method will refresh the marker by triggering the downcast conversion for it.
	*
	* Reconverting the marker is useful when you want to change its {@link module:engine/view/element~ViewElement view element}
	* without changing any marker data. For instance:
	*
	* ```ts
	* let isCommentActive = false;
	*
	* model.conversion.markerToHighlight( {
	* 	model: 'comment',
	* 	view: data => {
	* 		const classes = [ 'comment-marker' ];
	*
	* 		if ( isCommentActive ) {
	* 			classes.push( 'comment-marker--active' );
	* 		}
	*
	* 		return { classes };
	* 	}
	* } );
	*
	* // ...
	*
	* // Change the property that indicates if marker is displayed as active or not.
	* isCommentActive = true;
	*
	* // Reconverting will downcast and synchronize the marker with the new isCommentActive state value.
	* editor.editing.reconvertMarker( 'comment' );
	* ```
	*
	* **Note**: If you want to reconvert a model item, use {@link #reconvertItem} instead.
	*
	* @param markerOrName Name of a marker to update, or a marker instance.
	*/
	reconvertMarker(markerOrName) {
		const markerName = typeof markerOrName == "string" ? markerOrName : markerOrName.name;
		const currentMarker = this.model.markers.get(markerName);
		if (!currentMarker)
 /**
		* The marker with the provided name does not exist and cannot be reconverted.
		*
		* @error editingcontroller-reconvertmarker-marker-not-exist
		* @param {string} markerName The name of the reconverted marker.
		*/
		throw new CKEditorError("editingcontroller-reconvertmarker-marker-not-exist", this, { markerName });
		this.model.change(() => {
			this.model.markers._refresh(currentMarker);
		});
	}
	/**
	* Calling this method will downcast a model item on demand (by requesting a refresh in the {@link module:engine/model/differ~Differ}).
	*
	* You can use it if you want the view representation of a specific item updated as a response to external modifications. For instance,
	* when the view structure depends not only on the associated model data but also on some external state.
	*
	* **Note**: If you want to reconvert a model marker, use {@link #reconvertMarker} instead.
	*
	* @param item Item to refresh.
	*/
	reconvertItem(item) {
		this.model.change(() => {
			this.model.document.differ._refreshItem(item);
		});
	}
};

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* @module engine/model/schema
*/
const ModelSchemaBase = /* #__PURE__ */ ObservableMixin();
/**
* The model's schema. It defines the allowed and disallowed structures of nodes as well as nodes' attributes.
* The schema is usually defined by the features and based on them, the editing framework and features
* make decisions on how to change and process the model.
*
* The instance of schema is available in {@link module:engine/model/model~Model#schema `editor.model.schema`}.
*
* Read more about the schema in:
*
* * The {@glink framework/architecture/editing-engine#schema schema section} of the
* {@glink framework/architecture/editing-engine Introduction to the Editing engine architecture} guide.
* * The {@glink framework/deep-dive/schema Schema deep-dive} guide.
*/
var ModelSchema = class extends ModelSchemaBase {
	_sourceDefinitions = {};
	/**
	* A dictionary containing attribute properties.
	*/
	_attributeProperties = Object.create(null);
	/**
	* Stores additional callbacks registered for schema items, which are evaluated when {@link ~ModelSchema#checkChild} is called.
	*
	* Keys are schema item names for which the callbacks are registered. Values are arrays with the callbacks.
	*
	* Some checks are added under {@link ~ModelSchema#_genericCheckSymbol} key, these are
	* evaluated for every {@link ~ModelSchema#checkChild} call.
	*/
	_customChildChecks = /* @__PURE__ */ new Map();
	/**
	* Stores additional callbacks registered for attribute names, which are evaluated when {@link ~ModelSchema#checkAttribute} is called.
	*
	* Keys are schema attribute names for which the callbacks are registered. Values are arrays with the callbacks.
	*
	* Some checks are added under {@link ~ModelSchema#_genericCheckSymbol} key, these are evaluated for every
	* {@link ~ModelSchema#checkAttribute} call.
	*/
	_customAttributeChecks = /* @__PURE__ */ new Map();
	_genericCheckSymbol = Symbol("$generic");
	_compiledDefinitions;
	/**
	* Creates a schema instance.
	*/
	constructor() {
		super();
		this.decorate("checkChild");
		this.decorate("checkAttribute");
		this.on("checkAttribute", (evt, args) => {
			args[0] = new ModelSchemaContext(args[0]);
		}, { priority: "highest" });
		this.on("checkChild", (evt, args) => {
			args[0] = new ModelSchemaContext(args[0]);
			args[1] = this.getDefinition(args[1]);
		}, { priority: "highest" });
	}
	/**
	* Registers a schema item. Can only be called once for every item name.
	*
	* ```ts
	* schema.register( 'paragraph', {
	* 	inheritAllFrom: '$block'
	* } );
	* ```
	*/
	register(itemName, definition) {
		if (this._sourceDefinitions[itemName])
 /**
		* A single item cannot be registered twice in the schema.
		*
		* This situation may happen when:
		*
		* * Two or more plugins called {@link module:engine/model/schema~ModelSchema#register `register()`} with the same name.
		* This will usually mean that there is a collision between plugins which try to use the same element in the model.
		* Unfortunately, the only way to solve this is by modifying one of these plugins to use a unique model element name.
		* * A single plugin was loaded twice. This happens when it is installed by npm/yarn in two versions
		* and usually means one or more of the following issues:
		*     * a version mismatch (two of your dependencies require two different versions of this plugin),
		*     * incorrect imports (this plugin is somehow imported twice in a way which confuses webpack),
		*     * mess in `node_modules/` (`rm -rf node_modules/` may help).
		*
		* **Note:** Check the logged `itemName` to better understand which plugin was duplicated/conflicting.
		*
		* @param itemName The name of the model element that is being registered twice.
		* @error schema-cannot-register-item-twice
		*/
		throw new CKEditorError("schema-cannot-register-item-twice", this, { itemName });
		this._sourceDefinitions[itemName] = [Object.assign({}, definition)];
		this._clearCache();
	}
	/**
	* Extends a {@link #register registered} item's definition.
	*
	* Extending properties such as `allowIn` will add more items to the existing properties,
	* while redefining properties such as `isBlock` will override the previously defined ones.
	*
	* ```ts
	* schema.register( 'foo', {
	* 	allowIn: '$root',
	* 	isBlock: true;
	* } );
	* schema.extend( 'foo', {
	* 	allowIn: 'blockQuote',
	* 	isBlock: false
	* } );
	*
	* schema.getDefinition( 'foo' );
	* //	{
	* //		allowIn: [ '$root', 'blockQuote' ],
	* // 		isBlock: false
	* //	}
	* ```
	*/
	extend(itemName, definition) {
		if (!this._sourceDefinitions[itemName])
 /**
		* Cannot extend an item which was not registered yet.
		*
		* This error happens when a plugin tries to extend the schema definition of an item which was not
		* {@link module:engine/model/schema~ModelSchema#register registered} yet.
		*
		* @param itemName The name of the model element which is being extended.
		* @error schema-cannot-extend-missing-item
		*/
		throw new CKEditorError("schema-cannot-extend-missing-item", this, { itemName });
		this._sourceDefinitions[itemName].push(Object.assign({}, definition));
		this._clearCache();
	}
	/**
	* Returns data of all registered items.
	*
	* This method should normally be used for reflection purposes (e.g. defining a clone of a certain element,
	* checking a list of all block elements, etc).
	* Use specific methods (such as {@link #checkChild `checkChild()`} or {@link #isLimit `isLimit()`})
	* in other cases.
	*/
	getDefinitions() {
		if (!this._compiledDefinitions) this._compile();
		return this._compiledDefinitions;
	}
	/**
	* Returns a definition of the given item or `undefined` if an item is not registered.
	*
	* This method should normally be used for reflection purposes (e.g. defining a clone of a certain element,
	* checking a list of all block elements, etc).
	* Use specific methods (such as {@link #checkChild `checkChild()`} or {@link #isLimit `isLimit()`})
	* in other cases.
	*/
	getDefinition(item) {
		let itemName;
		if (typeof item == "string") itemName = item;
		else if ("is" in item && (item.is("$text") || item.is("$textProxy"))) itemName = "$text";
		else itemName = item.name;
		return this.getDefinitions()[itemName];
	}
	/**
	* Returns `true` if the given item is registered in the schema.
	*
	* ```ts
	* schema.isRegistered( 'paragraph' ); // -> true
	* schema.isRegistered( editor.model.document.getRoot() ); // -> true
	* schema.isRegistered( 'foo' ); // -> false
	* ```
	*/
	isRegistered(item) {
		return !!this.getDefinition(item);
	}
	/**
	* Returns `true` if the given item is defined to be
	* a block by the {@link module:engine/model/schema~ModelSchemaItemDefinition}'s `isBlock` property.
	*
	* ```ts
	* schema.isBlock( 'paragraph' ); // -> true
	* schema.isBlock( '$root' ); // -> false
	*
	* const paragraphElement = writer.createElement( 'paragraph' );
	* schema.isBlock( paragraphElement ); // -> true
	* ```
	*
	* See the {@glink framework/deep-dive/schema#block-elements Block elements} section of
	* the {@glink framework/deep-dive/schema Schema deep-dive} guide for more details.
	*/
	isBlock(item) {
		const def = this.getDefinition(item);
		return !!(def && def.isBlock);
	}
	/**
	* Returns `true` if the given item should be treated as a limit element.
	*
	* It considers an item to be a limit element if its
	* {@link module:engine/model/schema~ModelSchemaItemDefinition}'s
	* {@link module:engine/model/schema~ModelSchemaItemDefinition#isLimit `isLimit`} or
	* {@link module:engine/model/schema~ModelSchemaItemDefinition#isObject `isObject`} property
	* was set to `true`.
	*
	* ```ts
	* schema.isLimit( 'paragraph' ); // -> false
	* schema.isLimit( '$root' ); // -> true
	* schema.isLimit( editor.model.document.getRoot() ); // -> true
	* schema.isLimit( 'imageBlock' ); // -> true
	* ```
	*
	* See the {@glink framework/deep-dive/schema#limit-elements Limit elements} section of
	* the {@glink framework/deep-dive/schema Schema deep-dive} guide for more details.
	*/
	isLimit(item) {
		const def = this.getDefinition(item);
		if (!def) return false;
		return !!(def.isLimit || def.isObject);
	}
	/**
	* Returns `true` if the given item should be treated as an object element.
	*
	* It considers an item to be an object element if its
	* {@link module:engine/model/schema~ModelSchemaItemDefinition}'s
	* {@link module:engine/model/schema~ModelSchemaItemDefinition#isObject `isObject`} property
	* was set to `true`.
	*
	* ```ts
	* schema.isObject( 'paragraph' ); // -> false
	* schema.isObject( 'imageBlock' ); // -> true
	*
	* const imageElement = writer.createElement( 'imageBlock' );
	* schema.isObject( imageElement ); // -> true
	* ```
	*
	* See the {@glink framework/deep-dive/schema#object-elements Object elements} section of
	* the {@glink framework/deep-dive/schema Schema deep-dive} guide for more details.
	*/
	isObject(item) {
		const def = this.getDefinition(item);
		if (!def) return false;
		return !!(def.isObject || def.isLimit && def.isSelectable && def.isContent);
	}
	/**
	* Returns `true` if the given item is defined to be
	* an inline element by the {@link module:engine/model/schema~ModelSchemaItemDefinition}'s `isInline` property.
	*
	* ```ts
	* schema.isInline( 'paragraph' ); // -> false
	* schema.isInline( 'softBreak' ); // -> true
	*
	* const text = writer.createText( 'foo' );
	* schema.isInline( text ); // -> true
	* ```
	*
	* See the {@glink framework/deep-dive/schema#inline-elements Inline elements} section of
	* the {@glink framework/deep-dive/schema Schema deep-dive} guide for more details.
	*/
	isInline(item) {
		const def = this.getDefinition(item);
		return !!(def && def.isInline);
	}
	/**
	* Returns `true` if the given item is defined to be
	* a selectable element by the {@link module:engine/model/schema~ModelSchemaItemDefinition}'s `isSelectable` property.
	*
	* ```ts
	* schema.isSelectable( 'paragraph' ); // -> false
	* schema.isSelectable( 'heading1' ); // -> false
	* schema.isSelectable( 'imageBlock' ); // -> true
	* schema.isSelectable( 'tableCell' ); // -> true
	*
	* const text = writer.createText( 'foo' );
	* schema.isSelectable( text ); // -> false
	* ```
	*
	* See the {@glink framework/deep-dive/schema#selectable-elements Selectable elements section} of
	* the {@glink framework/deep-dive/schema Schema deep-dive} guide for more details.
	*/
	isSelectable(item) {
		const def = this.getDefinition(item);
		if (!def) return false;
		return !!(def.isSelectable || def.isObject);
	}
	/**
	* Returns `true` if the given item is defined to be
	* a content by the {@link module:engine/model/schema~ModelSchemaItemDefinition}'s `isContent` property.
	*
	* ```ts
	* schema.isContent( 'paragraph' ); // -> false
	* schema.isContent( 'heading1' ); // -> false
	* schema.isContent( 'imageBlock' ); // -> true
	* schema.isContent( 'horizontalLine' ); // -> true
	*
	* const text = writer.createText( 'foo' );
	* schema.isContent( text ); // -> true
	* ```
	*
	* See the {@glink framework/deep-dive/schema#content-elements Content elements section} of
	* the {@glink framework/deep-dive/schema Schema deep-dive} guide for more details.
	*/
	isContent(item) {
		const def = this.getDefinition(item);
		if (!def) return false;
		return !!(def.isContent || def.isObject);
	}
	/**
	* Checks whether the given node can be a child of the given context.
	*
	* ```ts
	* schema.checkChild( model.document.getRoot(), paragraph ); // -> false
	*
	* schema.register( 'paragraph', {
	* 	allowIn: '$root'
	* } );
	*
	* schema.checkChild( model.document.getRoot(), paragraph ); // -> true
	* ```
	*
	* Both {@link module:engine/model/schema~ModelSchema#addChildCheck callback checks} and declarative rules (added when
	* {@link module:engine/model/schema~ModelSchema#register registering} and
	* {@link module:engine/model/schema~ModelSchema#extend extending} items)
	* are evaluated when this method is called.
	*
	* Note that callback checks have bigger priority than declarative rules checks and may overwrite them.
	*
	* Note that when verifying whether the given node can be a child of the given context, the schema also verifies the entire
	* context &ndash; from its root to its last element. Therefore, it is possible for `checkChild()` to return `false` even though
	* the `context` last element can contain the checked child. It happens if one of the `context` elements does not allow its child.
	* When `context` is verified, {@link module:engine/model/schema~ModelSchema#addChildCheck custom checks} are considered as well.
	*
	* @fires checkChild
	* @param context The context in which the child will be checked.
	* @param def The child to check.
	*/
	checkChild(context, def) {
		if (!def) return false;
		return this._checkContextMatch(context, def);
	}
	/**
	* Checks whether the given attribute can be applied in the given context (on the last item of the context).
	*
	* ```ts
	* schema.checkAttribute( textNode, 'bold' ); // -> false
	*
	* schema.extend( '$text', {
	* 	allowAttributes: 'bold'
	* } );
	*
	* schema.checkAttribute( textNode, 'bold' ); // -> true
	* ```
	*
	* Both {@link module:engine/model/schema~ModelSchema#addAttributeCheck callback checks} and declarative rules (added when
	* {@link module:engine/model/schema~ModelSchema#register registering} and
	* {@link module:engine/model/schema~ModelSchema#extend extending} items)
	* are evaluated when this method is called.
	*
	* Note that callback checks have bigger priority than declarative rules checks and may overwrite them.
	*
	* @fires checkAttribute
	* @param context The context in which the attribute will be checked.
	* @param attributeName Name of attribute to check in the given context.
	*/
	checkAttribute(context, attributeName) {
		const def = this.getDefinition(context.last);
		if (!def) return false;
		const isAllowed = this._evaluateAttributeChecks(context, attributeName);
		return isAllowed !== void 0 ? isAllowed : def.allowAttributes.includes(attributeName);
	}
	/**
	* Checks whether the given element (`elementToMerge`) can be merged with the specified base element (`positionOrBaseElement`).
	*
	* In other words &ndash; both elements are not a limit elements and whether `elementToMerge`'s children
	* {@link #checkChild are allowed} in the `positionOrBaseElement`.
	*
	* This check ensures that elements merged with {@link module:engine/model/writer~ModelWriter#merge `Writer#merge()`}
	* will be valid.
	*
	* Instead of elements, you can pass the instance of the {@link module:engine/model/position~ModelPosition} class as the
	* `positionOrBaseElement`. It means that the elements before and after the position will be checked whether they can be merged.
	*
	* @param positionOrBaseElement The position or base element to which the `elementToMerge` will be merged.
	* @param elementToMerge The element to merge. Required if `positionOrBaseElement` is an element.
	*/
	checkMerge(positionOrBaseElement, elementToMerge) {
		if (positionOrBaseElement instanceof ModelPosition) {
			const nodeBefore = positionOrBaseElement.nodeBefore;
			const nodeAfter = positionOrBaseElement.nodeAfter;
			if (!(nodeBefore instanceof ModelElement))
 /**
			* The node before the merge position must be an element.
			*
			* @error schema-check-merge-no-element-before
			*/
			throw new CKEditorError("schema-check-merge-no-element-before", this);
			if (!(nodeAfter instanceof ModelElement))
 /**
			* The node after the merge position must be an element.
			*
			* @error schema-check-merge-no-element-after
			*/
			throw new CKEditorError("schema-check-merge-no-element-after", this);
			return this.checkMerge(nodeBefore, nodeAfter);
		}
		if (this.isLimit(positionOrBaseElement) || this.isLimit(elementToMerge)) return false;
		for (const child of elementToMerge.getChildren()) if (!this.checkChild(positionOrBaseElement, child)) return false;
		return true;
	}
	/**
	* Allows registering a callback to the {@link #checkChild} method calls.
	*
	* Callbacks allow you to implement rules which are not otherwise possible to achieve
	* by using the declarative API of {@link module:engine/model/schema~ModelSchemaItemDefinition}.
	*
	* Note that callback checks have bigger priority than declarative rules checks and may overwrite them.
	*
	* For example, by using this method you can disallow elements in specific contexts:
	*
	* ```ts
	* // Disallow `heading1` inside a `blockQuote` that is inside a table.
	* schema.addChildCheck( ( context, childDefinition ) => {
	* 	if ( context.endsWith( 'tableCell blockQuote' ) ) {
	* 		return false;
	* 	}
	* }, 'heading1' );
	* ```
	*
	* You can skip the optional `itemName` parameter to evaluate the callback for every `checkChild()` call.
	*
	* ```ts
	* // Inside specific custom element, allow only children, which allows for a specific attribute.
	* schema.addChildCheck( ( context, childDefinition ) => {
	* 	if ( context.endsWith( 'myElement' ) ) {
	* 		return childDefinition.allowAttributes.includes( 'myAttribute' );
	* 	}
	* } );
	* ```
	*
	* Please note that the generic callbacks may affect the editor performance and should be avoided if possible.
	*
	* When one of the callbacks makes a decision (returns `true` or `false`) the processing is finished and other callbacks are not fired.
	* Callbacks are fired in the order they were added, however generic callbacks are fired before callbacks added for a specified item.
	*
	* You can also use `checkChild` event, if you need even better control. The result from the example above could also be
	* achieved with following event callback:
	*
	* ```ts
	* schema.on( 'checkChild', ( evt, args ) => {
	* 	const context = args[ 0 ];
	* 	const childDefinition = args[ 1 ];
	*
	* 	if ( context.endsWith( 'myElement' ) ) {
	* 		// Prevent next listeners from being called.
	* 		evt.stop();
	* 		// Set the `checkChild()` return value.
	* 		evt.return = childDefinition.allowAttributes.includes( 'myAttribute' );
	* 	}
	* }, { priority: 'high' } );
	* ```
	*
	* Note that the callback checks and declarative rules checks are processed on `normal` priority.
	*
	* Adding callbacks this way can also negatively impact editor performance.
	*
	* @param callback The callback to be called. It is called with two parameters:
	* {@link module:engine/model/schema~ModelSchemaContext} (context) instance and
	* {@link module:engine/model/schema~ModelSchemaCompiledItemDefinition} (definition). The callback may return `true/false` to
	* override `checkChild()`'s return value. If it does not return a boolean value, the default algorithm (or other callbacks) will define
	* `checkChild()`'s return value.
	* @param itemName Name of the schema item for which the callback is registered. If specified, the callback will be run only for
	* `checkChild()` calls which `def` parameter matches the `itemName`. Otherwise, the callback will run for every `checkChild` call.
	*/
	addChildCheck(callback, itemName) {
		const key = itemName !== void 0 ? itemName : this._genericCheckSymbol;
		const checks = this._customChildChecks.get(key) || [];
		checks.push(callback);
		this._customChildChecks.set(key, checks);
	}
	/**
	* Allows registering a callback to the {@link #checkAttribute} method calls.
	*
	* Callbacks allow you to implement rules which are not otherwise possible to achieve
	* by using the declarative API of {@link module:engine/model/schema~ModelSchemaItemDefinition}.
	*
	* Note that callback checks have bigger priority than declarative rules checks and may overwrite them.
	*
	* For example, by using this method you can disallow setting attributes on nodes in specific contexts:
	*
	* ```ts
	* // Disallow setting `bold` on text inside `heading1` element:
	* schema.addAttributeCheck( context => {
	* 	if ( context.endsWith( 'heading1 $text' ) ) {
	* 		return false;
	* 	}
	* }, 'bold' );
	* ```
	*
	* You can skip the optional `attributeName` parameter to evaluate the callback for every `checkAttribute()` call.
	*
	* ```ts
	* // Disallow formatting attributes on text inside custom `myTitle` element:
	* schema.addAttributeCheck( ( context, attributeName ) => {
	* 	if ( context.endsWith( 'myTitle $text' ) && schema.getAttributeProperties( attributeName ).isFormatting ) {
	* 		return false;
	* 	}
	* } );
	* ```
	*
	* Please note that the generic callbacks may affect the editor performance and should be avoided if possible.
	*
	* When one of the callbacks makes a decision (returns `true` or `false`) the processing is finished and other callbacks are not fired.
	* Callbacks are fired in the order they were added, however generic callbacks are fired before callbacks added for a specified item.
	*
	* You can also use {@link #event:checkAttribute} event, if you need even better control. The result from the example above could also
	* be achieved with following event callback:
	*
	* ```ts
	* schema.on( 'checkAttribute', ( evt, args ) => {
	* 	const context = args[ 0 ];
	* 	const attributeName = args[ 1 ];
	*
	* 	if ( context.endsWith( 'myTitle $text' ) && schema.getAttributeProperties( attributeName ).isFormatting ) {
	* 		// Prevent next listeners from being called.
	* 		evt.stop();
	* 		// Set the `checkAttribute()` return value.
	* 		evt.return = false;
	* 	}
	* }, { priority: 'high' } );
	* ```
	*
	* Note that the callback checks and declarative rules checks are processed on `normal` priority.
	*
	* Adding callbacks this way can also negatively impact editor performance.
	*
	* @param callback The callback to be called. It is called with two parameters:
	* {@link module:engine/model/schema~ModelSchemaContext `context`} and attribute name. The callback may return `true` or `false`, to
	* override `checkAttribute()`'s return value. If it does not return a boolean value, the default algorithm (or other callbacks)
	* will define `checkAttribute()`'s return value.
	* @param attributeName Name of the attribute for which the callback is registered. If specified, the callback will be run only for
	* `checkAttribute()` calls with matching `attributeName`. Otherwise, the callback will run for every `checkAttribute()` call.
	*/
	addAttributeCheck(callback, attributeName) {
		const key = attributeName !== void 0 ? attributeName : this._genericCheckSymbol;
		const checks = this._customAttributeChecks.get(key) || [];
		checks.push(callback);
		this._customAttributeChecks.set(key, checks);
	}
	/**
	* This method allows assigning additional metadata to the model attributes. For example,
	* {@link module:engine/model/schema~ModelAttributeProperties `AttributeProperties#isFormatting` property} is
	* used to mark formatting attributes (like `bold` or `italic`).
	*
	* ```ts
	* // Mark bold as a formatting attribute.
	* schema.setAttributeProperties( 'bold', {
	* 	isFormatting: true
	* } );
	*
	* // Override code not to be considered a formatting markup.
	* schema.setAttributeProperties( 'code', {
	* 	isFormatting: false
	* } );
	* ```
	*
	* Properties are not limited to members defined in the
	* {@link module:engine/model/schema~ModelAttributeProperties `AttributeProperties` type} and you can also use custom properties:
	*
	* ```ts
	* schema.setAttributeProperties( 'blockQuote', {
	* 	customProperty: 'value'
	* } );
	* ```
	*
	* Subsequent calls with the same attribute will extend its custom properties:
	*
	* ```ts
	* schema.setAttributeProperties( 'blockQuote', {
	* 	one: 1
	* } );
	*
	* schema.setAttributeProperties( 'blockQuote', {
	* 	two: 2
	* } );
	*
	* console.log( schema.getAttributeProperties( 'blockQuote' ) );
	* // Logs: { one: 1, two: 2 }
	* ```
	*
	* @param attributeName A name of the attribute to receive the properties.
	* @param properties A dictionary of properties.
	*/
	setAttributeProperties(attributeName, properties) {
		this._attributeProperties[attributeName] = Object.assign(this.getAttributeProperties(attributeName), properties);
	}
	/**
	* Returns properties associated with a given model attribute. See {@link #setAttributeProperties `setAttributeProperties()`}.
	*
	* @param attributeName A name of the attribute.
	*/
	getAttributeProperties(attributeName) {
		return this._attributeProperties[attributeName] || Object.create(null);
	}
	/**
	* Returns the lowest {@link module:engine/model/schema~ModelSchema#isLimit limit element} containing the entire
	* selection/range/position or the root otherwise.
	*
	* @param selectionOrRangeOrPosition The selection/range/position to check.
	* @returns The lowest limit element containing the entire `selectionOrRangeOrPosition`.
	*/
	getLimitElement(selectionOrRangeOrPosition) {
		let element;
		if (selectionOrRangeOrPosition instanceof ModelPosition) element = selectionOrRangeOrPosition.parent;
		else element = (selectionOrRangeOrPosition instanceof ModelRange ? [selectionOrRangeOrPosition] : Array.from(selectionOrRangeOrPosition.getRanges())).reduce((element, range) => {
			const rangeCommonAncestor = range.getCommonAncestor();
			if (!element) return rangeCommonAncestor;
			return element.getCommonAncestor(rangeCommonAncestor, { includeSelf: true });
		}, null);
		while (!this.isLimit(element)) if (element.parent) element = element.parent;
		else break;
		return element;
	}
	/**
	* Checks whether the attribute is allowed in selection:
	*
	* * if the selection is not collapsed, then checks if the attribute is allowed on any of nodes in that range,
	* * if the selection is collapsed, then checks if on the selection position there's a text with the
	* specified attribute allowed.
	*
	* @param selection Selection which will be checked.
	* @param attribute The name of the attribute to check.
	*/
	checkAttributeInSelection(selection, attribute) {
		if (selection.isCollapsed) {
			const context = [...selection.getFirstPosition().getAncestors(), new ModelText("", selection.getAttributes())];
			return this.checkAttribute(context, attribute);
		} else {
			const ranges = selection.getRanges();
			for (const range of ranges) for (const value of range) if (this.checkAttribute(value.item, attribute)) return true;
		}
		return false;
	}
	/**
	* Transforms the given set of ranges into a set of ranges where the given attribute is allowed (and can be applied).
	*
	* @param ranges Ranges to be validated.
	* @param attribute The name of the attribute to check.
	* @param options Optional configuration.
	* @param options.includeEmptyRanges When `true`, empty ranges that allow the attribute are also included
	* in the returned ranges.
	* @returns Ranges in which the attribute is allowed.
	*/
	*getValidRanges(ranges, attribute, options = {}) {
		ranges = convertToMinimalFlatRanges(ranges);
		for (const range of ranges) yield* this._getValidRangesForRange(range, attribute, options);
	}
	/**
	* Basing on given `position`, finds and returns a {@link module:engine/model/range~ModelRange range} which is
	* nearest to that `position` and is a correct range for selection.
	*
	* The correct selection range might be collapsed when it is located in a position where the text node can be placed.
	* Non-collapsed range is returned when selection can be placed around element marked as an "object" in
	* the {@link module:engine/model/schema~ModelSchema schema}.
	*
	* Direction of searching for the nearest correct selection range can be specified as:
	*
	* * `both` - searching will be performed in both ways,
	* * `forward` - searching will be performed only forward,
	* * `backward` - searching will be performed only backward.
	*
	* When valid selection range cannot be found, `null` is returned.
	*
	* @param position Reference position where new selection range should be looked for.
	* @param direction Search direction.
	* @returns Nearest selection range or `null` if one cannot be found.
	*/
	getNearestSelectionRange(position, direction = "both") {
		if (position.root.rootName == "$graveyard") return null;
		if (this.checkChild(position, "$text")) return new ModelRange(position);
		let backwardWalker, forwardWalker;
		const limitElement = position.getAncestors().reverse().find((item) => this.isLimit(item)) || position.root;
		if (direction == "both" || direction == "backward") backwardWalker = new ModelTreeWalker({
			boundaries: ModelRange._createIn(limitElement),
			startPosition: position,
			direction: "backward"
		});
		if (direction == "both" || direction == "forward") forwardWalker = new ModelTreeWalker({
			boundaries: ModelRange._createIn(limitElement),
			startPosition: position
		});
		for (const data of combineWalkers(backwardWalker, forwardWalker)) {
			const type = data.walker == backwardWalker ? "elementEnd" : "elementStart";
			const value = data.value;
			if (value.type == type && this.isObject(value.item)) return ModelRange._createOn(value.item);
			if (this.checkChild(value.nextPosition, "$text")) return new ModelRange(value.nextPosition);
		}
		return null;
	}
	/**
	* Tries to find position ancestors that allow to insert a given node.
	* It starts searching from the given position and goes node by node to the top of the model tree
	* as long as a {@link module:engine/model/schema~ModelSchema#isLimit limit element}, an
	* {@link module:engine/model/schema~ModelSchema#isObject object element} or a topmost ancestor is not reached.
	*
	* @param position The position that the search will start from.
	* @param node The node for which an allowed parent should be found or its name.
	* @returns Allowed parent or null if nothing was found.
	*/
	findAllowedParent(position, node) {
		let parent = position.parent;
		while (parent) {
			if (this.checkChild(parent, node)) return parent;
			if (this.isLimit(parent)) return null;
			parent = parent.parent;
		}
		return null;
	}
	/**
	* Sets attributes allowed by the schema on a given node.
	*
	* @param node A node to set attributes on.
	* @param attributes Attributes keys and values.
	* @param writer An instance of the model writer.
	*/
	setAllowedAttributes(node, attributes, writer) {
		const model = writer.model;
		for (const [attributeName, attributeValue] of Object.entries(attributes)) if (model.schema.checkAttribute(node, attributeName)) writer.setAttribute(attributeName, attributeValue, node);
	}
	/**
	* Removes attributes disallowed by the schema.
	*
	* @param nodes Nodes that will be filtered.
	*/
	removeDisallowedAttributes(nodes, writer) {
		for (const node of nodes) if (node.is("$text")) removeDisallowedAttributeFromNode(this, node, writer);
		else {
			const positionsInRange = ModelRange._createIn(node).getPositions();
			for (const position of positionsInRange) {
				const item = position.nodeBefore || position.parent;
				removeDisallowedAttributeFromNode(this, item, writer);
			}
		}
	}
	/**
	* Gets attributes of a node that have a given property.
	*
	* @param node Node to get attributes from.
	* @param propertyName Name of the property that attribute must have to return it.
	* @param propertyValue Desired value of the property that we want to check.
	* When `undefined` attributes will be returned if they have set a given property no matter what the value is. If specified it will
	* return attributes which given property's value is equal to this parameter.
	* @returns Object with attributes' names as key and attributes' values as value.
	*/
	getAttributesWithProperty(node, propertyName, propertyValue) {
		const attributes = {};
		for (const [attributeName, attributeValue] of node.getAttributes()) {
			const attributeProperties = this.getAttributeProperties(attributeName);
			if (attributeProperties[propertyName] === void 0) continue;
			if (propertyValue === void 0 || propertyValue === attributeProperties[propertyName]) attributes[attributeName] = attributeValue;
		}
		return attributes;
	}
	/**
	* Creates an instance of the schema context.
	*/
	createContext(context) {
		return new ModelSchemaContext(context);
	}
	_clearCache() {
		this._compiledDefinitions = null;
	}
	_compile() {
		const definitions = {};
		const sourceRules = this._sourceDefinitions;
		const itemNames = Object.keys(sourceRules);
		for (const itemName of itemNames) definitions[itemName] = compileBaseItemRule(sourceRules[itemName], itemName);
		const items = Object.values(definitions);
		for (const item of items) {
			propagateAllowIn(definitions, item);
			propagateAllowChildren(definitions, item);
			propagateDisallowIn(definitions, item);
			propagateDisallowChildren(definitions, item);
		}
		for (const item of items) resolveDisallows(definitions, item);
		for (const item of items) compileAllowContentOf(definitions, item);
		for (const item of items) compileAllowWhere(definitions, item);
		for (const item of items) compileAllowAttributesOf(definitions, item);
		for (const item of items) compileInheritPropertiesFrom(definitions, item);
		this._compiledDefinitions = compileDefinitions(definitions);
	}
	_checkContextMatch(context, def) {
		const parentItem = context.last;
		let isAllowed = this._evaluateChildChecks(context, def);
		isAllowed = isAllowed !== void 0 ? isAllowed : def.allowIn.includes(parentItem.name);
		if (!isAllowed) return false;
		const parentItemDefinition = this.getDefinition(parentItem);
		const parentContext = context.trimLast();
		if (!parentItemDefinition) return false;
		if (parentContext.length == 0) return true;
		return this._checkContextMatch(parentContext, parentItemDefinition);
	}
	/**
	* Calls child check callbacks to decide whether `def` is allowed in `context`. It uses both generic and specific (defined for `def`
	* item) callbacks. If neither callback makes a decision, `undefined` is returned.
	*
	* Note that the first callback that makes a decision "wins", i.e., if any callback returns `true` or `false`, then the processing
	* is over and that result is returned.
	*/
	_evaluateChildChecks(context, def) {
		const genericChecks = this._customChildChecks.get(this._genericCheckSymbol) || [];
		const childChecks = this._customChildChecks.get(def.name) || [];
		for (const check of [...genericChecks, ...childChecks]) {
			const result = check(context, def);
			if (result !== void 0) return result;
		}
	}
	/**
	* Calls attribute check callbacks to decide whether `attributeName` can be set on the last element of `context`. It uses both
	* generic and specific (defined for `attributeName`) callbacks. If neither callback makes a decision, `undefined` is returned.
	*
	* Note that the first callback that makes a decision "wins", i.e., if any callback returns `true` or `false`, then the processing
	* is over and that result is returned.
	*/
	_evaluateAttributeChecks(context, attributeName) {
		const genericChecks = this._customAttributeChecks.get(this._genericCheckSymbol) || [];
		const childChecks = this._customAttributeChecks.get(attributeName) || [];
		for (const check of [...genericChecks, ...childChecks]) {
			const result = check(context, attributeName);
			if (result !== void 0) return result;
		}
	}
	/**
	* Takes a flat range and an attribute name. Traverses the range recursively and deeply to find and return all ranges
	* inside the given range on which the attribute can be applied.
	*
	* This is a helper function for {@link ~ModelSchema#getValidRanges}.
	*
	* @param range The range to process.
	* @param attribute The name of the attribute to check.
	* @returns Ranges in which the attribute is allowed.
	*/
	*_getValidRangesForRange(range, attribute, options) {
		let start = range.start;
		let end = range.start;
		for (const item of range.getItems({ shallow: true })) {
			if (item.is("element")) if (options.includeEmptyRanges && item.isEmpty) {
				const context = this.createContext(item);
				if (this.checkChild(context, "$text") && this.checkAttribute(context.push("$text"), attribute)) yield ModelRange._createIn(item);
			} else yield* this._getValidRangesForRange(ModelRange._createIn(item), attribute, options);
			if (!this.checkAttribute(item, attribute)) {
				if (!start.isEqual(end)) yield new ModelRange(start, end);
				start = ModelPosition._createAfter(item);
			}
			end = ModelPosition._createAfter(item);
		}
		if (!start.isEqual(end)) yield new ModelRange(start, end);
	}
	/**
	* Returns a model range which is optimal (in terms of UX) for inserting a widget block.
	*
	* For instance, if a selection is in the middle of a paragraph, the collapsed range before this paragraph
	* will be returned so that it is not split. If the selection is at the end of a paragraph,
	* the collapsed range after this paragraph will be returned.
	*
	* Note: If the selection is placed in an empty block, the range in that block will be returned. If that range
	* is then passed to {@link module:engine/model/model~Model#insertContent}, the block will be fully replaced
	* by the inserted widget block.
	*
	* @internal
	* @param selection The selection based on which the insertion position should be calculated.
	* @param place The place where to look for optimal insertion range.
	* The `auto` value will determine itself the best position for insertion.
	* The `before` value will try to find a position before selection.
	* The `after` value will try to find a position after selection.
	* @returns The optimal range.
	*/
	findOptimalInsertionRange(selection, place) {
		const selectedElement = selection.getSelectedElement();
		if (selectedElement && this.isObject(selectedElement) && !this.isInline(selectedElement)) {
			if (place == "before" || place == "after") return new ModelRange(ModelPosition._createAt(selectedElement, place));
			return ModelRange._createOn(selectedElement);
		}
		const firstBlock = first(selection.getSelectedBlocks());
		if (!firstBlock) return new ModelRange(selection.focus);
		if (firstBlock.isEmpty) return new ModelRange(ModelPosition._createAt(firstBlock, 0));
		const positionAfter = ModelPosition._createAfter(firstBlock);
		if (selection.focus.isTouching(positionAfter)) return new ModelRange(positionAfter);
		return new ModelRange(ModelPosition._createBefore(firstBlock));
	}
};
/**
* A schema context &ndash; a list of ancestors of a given position in the document.
*
* Considering such position:
*
* ```xml
* <$root>
* 	<blockQuote>
* 		<paragraph>
* 			^
* 		</paragraph>
* 	</blockQuote>
* </$root>
* ```
*
* The context of this position is its {@link module:engine/model/position~ModelPosition#getAncestors lists of ancestors}:
*
*		[ rootElement, blockQuoteElement, paragraphElement ]
*
* Contexts are used in the {@link module:engine/model/schema~ModelSchema#event:checkChild `Schema#checkChild`} and
* {@link module:engine/model/schema~ModelSchema#event:checkAttribute `Schema#checkAttribute`} events as a definition
* of a place in the document where the check occurs. The context instances are created based on the first arguments
* of the {@link module:engine/model/schema~ModelSchema#checkChild `Schema#checkChild()`} and
* {@link module:engine/model/schema~ModelSchema#checkAttribute `Schema#checkAttribute()`} methods so when
* using these methods you need to use {@link module:engine/model/schema~ModelSchemaContextDefinition}s.
*/
var ModelSchemaContext = class ModelSchemaContext {
	_items;
	/**
	* Creates an instance of the context.
	*/
	constructor(context) {
		if (context instanceof ModelSchemaContext) return context;
		let items;
		if (typeof context == "string") items = [context];
		else if (!Array.isArray(context)) items = context.getAncestors({ includeSelf: true });
		else items = context;
		this._items = items.map(mapContextItem);
	}
	/**
	* The number of items.
	*/
	get length() {
		return this._items.length;
	}
	/**
	* The last item (the lowest node).
	*/
	get last() {
		return this._items[this._items.length - 1];
	}
	/**
	* Iterable interface.
	*
	* Iterates over all context items.
	*/
	[Symbol.iterator]() {
		return this._items[Symbol.iterator]();
	}
	/**
	* Returns a new schema context instance with an additional item.
	*
	* Item can be added as:
	*
	* ```ts
	* const context = new ModelSchemaContext( [ '$root' ] );
	*
	* // An element.
	* const fooElement = writer.createElement( 'fooElement' );
	* const newContext = context.push( fooElement ); // [ '$root', 'fooElement' ]
	*
	* // A text node.
	* const text = writer.createText( 'foobar' );
	* const newContext = context.push( text ); // [ '$root', '$text' ]
	*
	* // A string (element name).
	* const newContext = context.push( 'barElement' ); // [ '$root', 'barElement' ]
	* ```
	*
	* **Note** {@link module:engine/model/node~ModelNode} that is already in the model tree will be added as the only item
	* (without ancestors).
	*
	* @param item An item that will be added to the current context.
	* @returns A new schema context instance with an additional item.
	*/
	push(item) {
		const ctx = new ModelSchemaContext([item]);
		ctx._items = [...this._items, ...ctx._items];
		return ctx;
	}
	/**
	* Returns a new schema context that is based on this context but has the last item removed.
	*
	* ```ts
	* const ctxParagraph = new ModelSchemaContext( [ '$root', 'blockQuote', 'paragraph' ] );
	* const ctxBlockQuote = ctxParagraph.trimLast(); // Items in `ctxBlockQuote` are: `$root` an `blockQuote`.
	* const ctxRoot = ctxBlockQuote.trimLast(); // Items in `ctxRoot` are: `$root`.
	* ```
	*
	* @returns A new reduced schema context instance.
	*/
	trimLast() {
		const ctx = new ModelSchemaContext([]);
		ctx._items = this._items.slice(0, -1);
		return ctx;
	}
	/**
	* Gets an item on the given index.
	*/
	getItem(index) {
		return this._items[index];
	}
	/**
	* Returns the names of items.
	*/
	*getNames() {
		yield* this._items.map((item) => item.name);
	}
	/**
	* Checks whether the context ends with the given nodes.
	*
	* ```ts
	* const ctx = new ModelSchemaContext( [ rootElement, paragraphElement, textNode ] );
	*
	* ctx.endsWith( '$text' ); // -> true
	* ctx.endsWith( 'paragraph $text' ); // -> true
	* ctx.endsWith( '$root' ); // -> false
	* ctx.endsWith( 'paragraph' ); // -> false
	* ```
	*/
	endsWith(query) {
		return Array.from(this.getNames()).join(" ").endsWith(query);
	}
	/**
	* Checks whether the context starts with the given nodes.
	*
	* ```ts
	* const ctx = new ModelSchemaContext( [ rootElement, paragraphElement, textNode ] );
	*
	* ctx.endsWith( '$root' ); // -> true
	* ctx.endsWith( '$root paragraph' ); // -> true
	* ctx.endsWith( '$text' ); // -> false
	* ctx.endsWith( 'paragraph' ); // -> false
	* ```
	*/
	startsWith(query) {
		return Array.from(this.getNames()).join(" ").startsWith(query);
	}
};
function compileBaseItemRule(sourceItemRules, itemName) {
	const itemRule = {
		name: itemName,
		allowIn: /* @__PURE__ */ new Set(),
		allowChildren: /* @__PURE__ */ new Set(),
		disallowIn: /* @__PURE__ */ new Set(),
		disallowChildren: /* @__PURE__ */ new Set(),
		allowContentOf: /* @__PURE__ */ new Set(),
		allowWhere: /* @__PURE__ */ new Set(),
		allowAttributes: /* @__PURE__ */ new Set(),
		disallowAttributes: /* @__PURE__ */ new Set(),
		allowAttributesOf: /* @__PURE__ */ new Set(),
		inheritTypesFrom: /* @__PURE__ */ new Set()
	};
	copyTypes(sourceItemRules, itemRule);
	copyProperty(sourceItemRules, itemRule, "allowIn");
	copyProperty(sourceItemRules, itemRule, "allowChildren");
	copyProperty(sourceItemRules, itemRule, "disallowIn");
	copyProperty(sourceItemRules, itemRule, "disallowChildren");
	copyProperty(sourceItemRules, itemRule, "allowContentOf");
	copyProperty(sourceItemRules, itemRule, "allowWhere");
	copyProperty(sourceItemRules, itemRule, "allowAttributes");
	copyProperty(sourceItemRules, itemRule, "disallowAttributes");
	copyProperty(sourceItemRules, itemRule, "allowAttributesOf");
	copyProperty(sourceItemRules, itemRule, "inheritTypesFrom");
	resolveInheritAll(sourceItemRules, itemRule);
	return itemRule;
}
function propagateAllowIn(definitions, item) {
	for (const parentName of item.allowIn) {
		const parentItem = definitions[parentName];
		if (parentItem) parentItem.allowChildren.add(item.name);
		else item.allowIn.delete(parentName);
	}
}
function propagateAllowChildren(definitions, item) {
	for (const childName of item.allowChildren) {
		const childItem = definitions[childName];
		if (childItem) childItem.allowIn.add(item.name);
		else item.allowChildren.delete(childName);
	}
}
function propagateDisallowIn(definitions, item) {
	for (const parentName of item.disallowIn) {
		const parentItem = definitions[parentName];
		if (parentItem) parentItem.disallowChildren.add(item.name);
		else item.disallowIn.delete(parentName);
	}
}
function propagateDisallowChildren(definitions, item) {
	for (const childName of item.disallowChildren) {
		const childItem = definitions[childName];
		if (childItem) childItem.disallowIn.add(item.name);
		else item.disallowChildren.delete(childName);
	}
}
function resolveDisallows(definitions, item) {
	for (const childName of item.disallowChildren) item.allowChildren.delete(childName);
	for (const parentName of item.disallowIn) item.allowIn.delete(parentName);
	for (const attributeName of item.disallowAttributes) item.allowAttributes.delete(attributeName);
}
function compileAllowContentOf(definitions, item) {
	for (const allowContentOfItemName of item.allowContentOf) {
		const baseItem = definitions[allowContentOfItemName];
		if (!baseItem) continue;
		baseItem.disallowChildren.forEach((childName) => {
			if (item.allowChildren.has(childName)) return;
			item.disallowChildren.add(childName);
			definitions[childName].disallowIn.add(item.name);
		});
		baseItem.allowChildren.forEach((childName) => {
			if (item.disallowChildren.has(childName)) return;
			item.allowChildren.add(childName);
			definitions[childName].allowIn.add(item.name);
		});
	}
}
function compileAllowWhere(definitions, item) {
	for (const allowWhereItemName of item.allowWhere) {
		const baseItem = definitions[allowWhereItemName];
		if (!baseItem) continue;
		baseItem.disallowIn.forEach((parentName) => {
			if (item.allowIn.has(parentName)) return;
			item.disallowIn.add(parentName);
			definitions[parentName].disallowChildren.add(item.name);
		});
		baseItem.allowIn.forEach((parentName) => {
			if (item.disallowIn.has(parentName)) return;
			item.allowIn.add(parentName);
			definitions[parentName].allowChildren.add(item.name);
		});
	}
}
function compileDefinitions(definitions) {
	const finalDefinitions = {};
	for (const item of Object.values(definitions)) finalDefinitions[item.name] = {
		name: item.name,
		isBlock: !!item.isBlock,
		isContent: !!item.isContent,
		isInline: !!item.isInline,
		isLimit: !!item.isLimit,
		isObject: !!item.isObject,
		isSelectable: !!item.isSelectable,
		allowIn: Array.from(item.allowIn).filter((name) => !!definitions[name]),
		allowChildren: Array.from(item.allowChildren).filter((name) => !!definitions[name]),
		allowAttributes: Array.from(item.allowAttributes)
	};
	return finalDefinitions;
}
function compileAllowAttributesOf(definitions, item) {
	for (const allowAttributeOfItemName of item.allowAttributesOf) {
		const baseItem = definitions[allowAttributeOfItemName];
		if (!baseItem) return;
		baseItem.allowAttributes.forEach((attributeName) => {
			if (item.disallowAttributes.has(attributeName)) return;
			item.allowAttributes.add(attributeName);
		});
	}
}
function compileInheritPropertiesFrom(definitions, item) {
	for (const inheritPropertiesOfItemName of item.inheritTypesFrom) {
		const inheritFrom = definitions[inheritPropertiesOfItemName];
		if (inheritFrom) {
			const typeNames = Object.keys(inheritFrom).filter((name) => name.startsWith("is"));
			for (const name of typeNames) if (!(name in item)) item[name] = inheritFrom[name];
		}
	}
}
function copyTypes(sourceItemRules, itemRule) {
	for (const sourceItemRule of sourceItemRules) {
		const typeNames = Object.keys(sourceItemRule).filter((name) => name.startsWith("is"));
		for (const name of typeNames) itemRule[name] = !!sourceItemRule[name];
	}
}
function copyProperty(sourceItemRules, itemRule, propertyName) {
	for (const sourceItemRule of sourceItemRules) {
		let value = sourceItemRule[propertyName];
		if (typeof value == "string") value = [value];
		if (Array.isArray(value)) value.forEach((singleValue) => itemRule[propertyName].add(singleValue));
	}
}
function resolveInheritAll(sourceItemRules, itemRule) {
	for (const sourceItemRule of sourceItemRules) {
		const inheritFrom = sourceItemRule.inheritAllFrom;
		if (inheritFrom) {
			itemRule.allowContentOf.add(inheritFrom);
			itemRule.allowWhere.add(inheritFrom);
			itemRule.allowAttributesOf.add(inheritFrom);
			itemRule.inheritTypesFrom.add(inheritFrom);
		}
	}
}
function mapContextItem(ctxItem) {
	if (typeof ctxItem == "string" || ctxItem.is("documentFragment")) return {
		name: typeof ctxItem == "string" ? ctxItem : "$documentFragment",
		*getAttributeKeys() {},
		getAttribute() {}
	};
	else return {
		name: ctxItem.is("element") ? ctxItem.name : "$text",
		*getAttributeKeys() {
			yield* ctxItem.getAttributeKeys();
		},
		getAttribute(key) {
			return ctxItem.getAttribute(key);
		}
	};
}
/**
* Generator function returning values from provided walkers, switching between them at each iteration. If only one walker
* is provided it will return data only from that walker.
*
* @param backward Walker iterating in backward direction.
* @param forward Walker iterating in forward direction.
* @returns Object returned at each iteration contains `value` and `walker` (informing which walker returned
* given value) fields.
*/
function* combineWalkers(backward, forward) {
	let done = false;
	while (!done) {
		done = true;
		if (backward) {
			const step = backward.next();
			if (!step.done) {
				done = false;
				yield {
					walker: backward,
					value: step.value
				};
			}
		}
		if (forward) {
			const step = forward.next();
			if (!step.done) {
				done = false;
				yield {
					walker: forward,
					value: step.value
				};
			}
		}
	}
}
/**
* Takes an array of non-intersecting ranges. For each of them gets minimal flat ranges covering that range and returns
* all those minimal flat ranges.
*
* @param ranges Ranges to process.
* @returns Minimal flat ranges of given `ranges`.
*/
function* convertToMinimalFlatRanges(ranges) {
	for (const range of ranges) yield* range.getMinimalFlatRanges();
}
function removeDisallowedAttributeFromNode(schema, node, writer) {
	for (const attribute of node.getAttributeKeys()) if (!schema.checkAttribute(node, attribute)) writer.removeAttribute(attribute, node);
}

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* @module engine/conversion/upcastdispatcher
*/
const UpcastDispatcherBase = /* #__PURE__ */ EmitterMixin();
/**
* Upcast dispatcher is a central point of the view-to-model conversion, which is a process of
* converting a given {@link module:engine/view/documentfragment~ViewDocumentFragment view document fragment} or
* {@link module:engine/view/element~ViewElement view element} into a correct model structure.
*
* During the conversion process, the dispatcher fires events for all {@link module:engine/view/node~ViewNode view nodes}
* from the converted view document fragment.
* Special callbacks called "converters" should listen to these events in order to convert the view nodes.
*
* The second parameter of the callback is the `data` object with the following properties:
*
* * `data.viewItem` contains a {@link module:engine/view/node~ViewNode view node} or a
* {@link module:engine/view/documentfragment~ViewDocumentFragment view document fragment}
* that is converted at the moment and might be handled by the callback.
* * `data.modelRange` is used to point to the result
* of the current conversion (e.g. the element that is being inserted)
* and is always a {@link module:engine/model/range~ModelRange} when the conversion succeeds.
* * `data.modelCursor` is a {@link module:engine/model/position~ModelPosition position} on which the converter should insert
* the newly created items.
*
* The third parameter of the callback is an instance of {@link module:engine/conversion/upcastdispatcher~UpcastConversionApi}
* which provides additional tools for converters.
*
* You can read more about conversion in the {@glink framework/deep-dive/conversion/upcast Upcast conversion} guide.
*
* Examples of event-based converters:
*
* ```ts
* // A converter for links (<a>).
* editor.data.upcastDispatcher.on( 'element:a', ( evt, data, conversionApi ) => {
* 	if ( conversionApi.consumable.consume( data.viewItem, { name: true, attributes: [ 'href' ] } ) ) {
* 		// The <a> element is inline and is represented by an attribute in the model.
* 		// This is why you need to convert only children.
* 		const { modelRange } = conversionApi.convertChildren( data.viewItem, data.modelCursor );
*
* 		for ( let item of modelRange.getItems() ) {
* 			if ( conversionApi.schema.checkAttribute( item, 'linkHref' ) ) {
* 				conversionApi.writer.setAttribute( 'linkHref', data.viewItem.getAttribute( 'href' ), item );
* 			}
* 		}
* 	}
* } );
*
* // Convert <p> element's font-size style.
* // Note: You should use a low-priority observer in order to ensure that
* // it is executed after the element-to-element converter.
* editor.data.upcastDispatcher.on( 'element:p', ( evt, data, conversionApi ) => {
* 	const { consumable, schema, writer } = conversionApi;
*
* 	if ( !consumable.consume( data.viewItem, { style: 'font-size' } ) ) {
* 		return;
* 	}
*
* 	const fontSize = data.viewItem.getStyle( 'font-size' );
*
* 	// Do not go for the model element after data.modelCursor because it might happen
* 	// that a single view element was converted to multiple model elements. Get all of them.
* 	for ( const item of data.modelRange.getItems( { shallow: true } ) ) {
* 		if ( schema.checkAttribute( item, 'fontSize' ) ) {
* 			writer.setAttribute( 'fontSize', fontSize, item );
* 		}
* 	}
* }, { priority: 'low' } );
*
* // Convert all elements which have no custom converter into a paragraph (autoparagraphing).
* editor.data.upcastDispatcher.on( 'element', ( evt, data, conversionApi ) => {
* 	// Check if an element can be converted.
* 	if ( !conversionApi.consumable.test( data.viewItem, { name: data.viewItem.name } ) ) {
* 		// When an element is already consumed by higher priority converters, do nothing.
* 		return;
* 	}
*
* 	const paragraph = conversionApi.writer.createElement( 'paragraph' );
*
* 	// Try to safely insert a paragraph at the model cursor - it will find an allowed parent for the current element.
* 	if ( !conversionApi.safeInsert( paragraph, data.modelCursor ) ) {
* 		// When an element was not inserted, it means that you cannot insert a paragraph at this position.
* 		return;
* 	}
*
* 	// Consume the inserted element.
* 	conversionApi.consumable.consume( data.viewItem, { name: data.viewItem.name } ) );
*
* 	// Convert the children to a paragraph.
* 	const { modelRange } = conversionApi.convertChildren( data.viewItem,  paragraph ) );
*
* 	// Update `modelRange` and `modelCursor` in the `data` as a conversion result.
* 	conversionApi.updateConversionResult( paragraph, data );
* }, { priority: 'low' } );
* ```
*
* @fires viewCleanup
* @fires element
* @fires text
* @fires documentFragment
*/
var UpcastDispatcher = class extends UpcastDispatcherBase {
	/**
	* An interface passed by the dispatcher to the event callbacks.
	*/
	conversionApi;
	/**
	* The list of elements that were created during splitting.
	*
	* After the conversion process, the list is cleared.
	*/
	_splitParts = /* @__PURE__ */ new Map();
	/**
	* The list of cursor parent elements that were created during splitting.
	*
	* After the conversion process the list is cleared.
	*/
	_cursorParents = /* @__PURE__ */ new Map();
	/**
	* The position in the temporary structure where the converted content is inserted. The structure reflects the context of
	* the target position where the content will be inserted. This property is built based on the context parameter of the
	* convert method.
	*/
	_modelCursor = null;
	/**
	* The list of elements that were created during the splitting but should not get removed on conversion end even if they are empty.
	*
	* The list is cleared after the conversion process.
	*/
	_emptyElementsToKeep = /* @__PURE__ */ new Set();
	/**
	* Creates an upcast dispatcher that operates using the passed API.
	*
	* @see module:engine/conversion/upcastdispatcher~UpcastConversionApi
	* @param conversionApi Additional properties for an interface that will be passed to events fired
	* by the upcast dispatcher.
	*/
	constructor(conversionApi) {
		super();
		this.conversionApi = {
			...conversionApi,
			consumable: null,
			writer: null,
			store: null,
			convertItem: (viewItem, modelCursor) => this._convertItem(viewItem, modelCursor),
			convertChildren: (viewElement, positionOrElement) => this._convertChildren(viewElement, positionOrElement),
			safeInsert: (modelNode, position) => this._safeInsert(modelNode, position),
			updateConversionResult: (modelElement, data) => this._updateConversionResult(modelElement, data),
			splitToAllowedParent: (modelNode, modelCursor) => this._splitToAllowedParent(modelNode, modelCursor),
			getSplitParts: (modelElement) => this._getSplitParts(modelElement),
			keepEmptyElement: (modelElement) => this._keepEmptyElement(modelElement)
		};
	}
	/**
	* Starts the conversion process. The entry point for the conversion.
	*
	* **Note:** The default `context` value is `[ '$root' ]`, which only matches the generic root. When the editor uses
	* a custom root {@link module:core/editor/editorconfig~RootConfig#modelElement `modelElement`}, pass the target
	* {@link module:engine/model/rootelement~ModelRootElement root element} (or its configured model element name)
	* explicitly, otherwise the conversion result may be wrong.
	* See the {@glink framework/deep-dive/schema#custom-root-elements Custom root elements} section of the
	* {@glink framework/deep-dive/schema Schema deep-dive} guide for more details.
	*
	* @fires element
	* @fires text
	* @fires documentFragment
	* @param viewElement The part of the view to be converted.
	* @param writer An instance of the model writer.
	* @param context Elements will be converted according to this context.
	* @returns Model data that is the result of the conversion process
	* wrapped in `DocumentFragment`. Converted marker elements will be set as the document fragment's
	* {@link module:engine/model/documentfragment~ModelDocumentFragment#markers static markers map}.
	*/
	convert(viewElement, writer, context = ["$root"]) {
		this.fire("viewCleanup", viewElement);
		this._modelCursor = createContextTree(context, writer);
		this.conversionApi.writer = writer;
		this.conversionApi.consumable = ViewConsumable.createFrom(viewElement);
		this.conversionApi.store = {};
		const { modelRange } = this._convertItem(viewElement, this._modelCursor);
		const documentFragment = writer.createDocumentFragment();
		if (modelRange) {
			this._removeEmptyElements();
			const parent = this._modelCursor.parent;
			const children = parent._removeChildren(0, parent.childCount);
			documentFragment._insertChild(0, children);
			documentFragment.markers = extractMarkersFromModelFragment(documentFragment, writer);
		}
		this._modelCursor = null;
		this._splitParts.clear();
		this._cursorParents.clear();
		this._emptyElementsToKeep.clear();
		this.conversionApi.writer = null;
		this.conversionApi.store = null;
		return documentFragment;
	}
	/**
	* @see module:engine/conversion/upcastdispatcher~UpcastConversionApi#convertItem
	*/
	_convertItem(viewItem, modelCursor) {
		const data = {
			viewItem,
			modelCursor,
			modelRange: null
		};
		if (viewItem.is("element")) this.fire(`element:${viewItem.name}`, data, this.conversionApi);
		else if (viewItem.is("$text")) this.fire("text", data, this.conversionApi);
		else this.fire("documentFragment", data, this.conversionApi);
		if (data.modelRange && !(data.modelRange instanceof ModelRange))
 /**
		* Incorrect conversion result was dropped.
		*
		* {@link module:engine/model/range~ModelRange Model range} should be a conversion result.
		*
		* @error view-conversion-dispatcher-incorrect-result
		*/
		throw new CKEditorError("view-conversion-dispatcher-incorrect-result", this);
		return {
			modelRange: data.modelRange,
			modelCursor: data.modelCursor
		};
	}
	/**
	* @see module:engine/conversion/upcastdispatcher~UpcastConversionApi#convertChildren
	*/
	_convertChildren(viewItem, elementOrModelCursor) {
		let nextModelCursor = elementOrModelCursor.is("position") ? elementOrModelCursor : ModelPosition._createAt(elementOrModelCursor, 0);
		const modelRange = new ModelRange(nextModelCursor);
		for (const viewChild of Array.from(viewItem.getChildren())) {
			const result = this._convertItem(viewChild, nextModelCursor);
			if (result.modelRange instanceof ModelRange) {
				modelRange.end = result.modelRange.end;
				nextModelCursor = result.modelCursor;
			}
		}
		return {
			modelRange,
			modelCursor: nextModelCursor
		};
	}
	/**
	* @see module:engine/conversion/upcastdispatcher~UpcastConversionApi#safeInsert
	*/
	_safeInsert(modelNode, position) {
		const splitResult = this._splitToAllowedParent(modelNode, position);
		if (!splitResult) return false;
		this.conversionApi.writer.insert(modelNode, splitResult.position);
		return true;
	}
	/**
	* @see module:engine/conversion/upcastdispatcher~UpcastConversionApi#updateConversionResult
	*/
	_updateConversionResult(modelElement, data) {
		const parts = this._getSplitParts(modelElement);
		const writer = this.conversionApi.writer;
		if (!data.modelRange) data.modelRange = writer.createRange(writer.createPositionBefore(modelElement), writer.createPositionAfter(parts[parts.length - 1]));
		const savedCursorParent = this._cursorParents.get(modelElement);
		if (savedCursorParent) data.modelCursor = writer.createPositionAt(savedCursorParent, 0);
		else data.modelCursor = data.modelRange.end;
	}
	/**
	* @see module:engine/conversion/upcastdispatcher~UpcastConversionApi#splitToAllowedParent
	*/
	_splitToAllowedParent(node, modelCursor) {
		const { schema, writer } = this.conversionApi;
		let allowedParent = schema.findAllowedParent(modelCursor, node);
		if (allowedParent) {
			if (allowedParent === modelCursor.parent) return { position: modelCursor };
			if (this._modelCursor.parent.getAncestors().includes(allowedParent)) allowedParent = null;
		}
		if (!allowedParent) {
			if (!isParagraphable(modelCursor, node, schema)) return null;
			return { position: wrapInParagraph(modelCursor, writer) };
		}
		const splitResult = this.conversionApi.writer.split(modelCursor, allowedParent);
		const stack = [];
		for (const treeWalkerValue of splitResult.range.getWalker()) if (treeWalkerValue.type == "elementEnd") stack.push(treeWalkerValue.item);
		else {
			const originalPart = stack.pop();
			const splitPart = treeWalkerValue.item;
			this._registerSplitPair(originalPart, splitPart);
		}
		const cursorParent = splitResult.range.end.parent;
		this._cursorParents.set(node, cursorParent);
		return {
			position: splitResult.position,
			cursorParent
		};
	}
	/**
	* Registers that a `splitPart` element is a split part of the `originalPart` element.
	*
	* The data set by this method is used by {@link #_getSplitParts} and {@link #_removeEmptyElements}.
	*/
	_registerSplitPair(originalPart, splitPart) {
		if (!this._splitParts.has(originalPart)) this._splitParts.set(originalPart, [originalPart]);
		const list = this._splitParts.get(originalPart);
		this._splitParts.set(splitPart, list);
		list.push(splitPart);
	}
	/**
	* @see module:engine/conversion/upcastdispatcher~UpcastConversionApi#getSplitParts
	*/
	_getSplitParts(element) {
		let parts;
		if (!this._splitParts.has(element)) parts = [element];
		else parts = this._splitParts.get(element);
		return parts;
	}
	/**
	* Mark an element that were created during the splitting to not get removed on conversion end even if it is empty.
	*/
	_keepEmptyElement(element) {
		this._emptyElementsToKeep.add(element);
	}
	/**
	* Checks if there are any empty elements created while splitting and removes them.
	*
	* This method works recursively to re-check empty elements again after at least one element was removed in the initial call,
	* as some elements might have become empty after other empty elements were removed from them.
	*/
	_removeEmptyElements() {
		const toRemove = /* @__PURE__ */ new Map();
		for (const element of this._splitParts.keys()) if (element.isEmpty && !this._emptyElementsToKeep.has(element)) {
			const children = toRemove.get(element.parent) || [];
			children.push(element);
			this._splitParts.delete(element);
			toRemove.set(element.parent, children);
		}
		for (const [parent, children] of toRemove) parent._removeChildrenArray(children);
		if (toRemove.size) this._removeEmptyElements();
	}
};
/**
* Traverses given model item and searches elements which marks marker range. Found element is removed from
* ModelDocumentFragment but path of this element is stored in a Map which is then returned.
*
* @param modelItem Fragment of model.
* @returns List of static markers.
*/
function extractMarkersFromModelFragment(modelItem, writer) {
	const markerElements = /* @__PURE__ */ new Set();
	const markers = /* @__PURE__ */ new Map();
	const range = ModelRange._createIn(modelItem).getItems();
	for (const item of range) if (item.is("element", "$marker")) markerElements.add(item);
	for (const markerElement of markerElements) {
		const markerName = markerElement.getAttribute("data-name");
		const currentPosition = writer.createPositionBefore(markerElement);
		if (!markers.has(markerName)) markers.set(markerName, new ModelRange(currentPosition.clone()));
		else markers.get(markerName).end = currentPosition.clone();
		writer.remove(markerElement);
	}
	return markers;
}
/**
* Creates model fragment according to given context and returns position in the bottom (the deepest) element.
*/
function createContextTree(contextDefinition, writer) {
	let position;
	for (const item of new ModelSchemaContext(contextDefinition)) {
		const attributes = {};
		for (const key of item.getAttributeKeys()) attributes[key] = item.getAttribute(key);
		const current = writer.createElement(item.name, attributes);
		if (position) writer.insert(current, position);
		position = ModelPosition._createAt(current, 0);
	}
	return position;
}

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* Basic HTML writer. It uses the native `innerHTML` property for basic conversion
* from a document fragment to an HTML string.
*
* @internal
*/
var BasicHtmlWriter = class {
	/**
	* Returns an HTML string created from the document fragment.
	*/
	getHtml(fragment) {
		const container = global.document.implementation.createHTMLDocument("").createElement("div");
		container.appendChild(fragment);
		return container.innerHTML;
	}
};

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* @module engine/dataprocessor/htmldataprocessor
*/
/**
* The HTML data processor class.
* This data processor implementation uses HTML as input and output data.
*/
var HtmlDataProcessor = class {
	/**
	* A DOM parser instance used to parse an HTML string to an HTML document.
	*/
	domParser;
	/**
	* A DOM converter used to convert DOM elements to view elements.
	*/
	domConverter;
	/**
	* A basic HTML writer instance used to convert DOM elements to an HTML string.
	*/
	htmlWriter;
	skipComments = true;
	/**
	* Creates a new instance of the HTML data processor class.
	*
	* @param document The view document instance.
	*/
	constructor(document) {
		this.domParser = new DOMParser();
		this.domConverter = new ViewDomConverter(document, { renderingMode: "data" });
		this.htmlWriter = new BasicHtmlWriter();
	}
	/**
	* Converts a provided {@link module:engine/view/documentfragment~ViewDocumentFragment document fragment}
	* to data format &ndash; in this case to an HTML string.
	*
	* @returns HTML string.
	*/
	toData(viewFragment) {
		const domFragment = this.domConverter.viewToDom(viewFragment);
		return this.htmlWriter.getHtml(domFragment);
	}
	/**
	* Converts the provided HTML string to a view tree.
	*
	* @param data An HTML string.
	* @returns A converted view element.
	*/
	toView(data) {
		const domFragment = this._toDom(data);
		return this.domConverter.domToView(domFragment, { skipComments: this.skipComments });
	}
	/**
	* Registers a {@link module:engine/view/matcher~MatcherPattern} for view elements whose content should be treated as raw data
	* and not processed during the conversion from the DOM to the view elements.
	*
	* The raw data can be later accessed by a
	* {@link module:engine/view/element~ViewElement#getCustomProperty custom property of a view element} called `"$rawContent"`.
	*
	* @param pattern Pattern matching all view elements whose content should be treated as raw data.
	*/
	registerRawContentMatcher(pattern) {
		this.domConverter.registerRawContentMatcher(pattern);
	}
	/**
	* If the processor is set to use marked fillers, it will insert `&nbsp;` fillers wrapped in `<span>` elements
	* (`<span data-cke-filler="true">&nbsp;</span>`) instead of regular `&nbsp;` characters.
	*
	* This mode allows for a more precise handling of the block fillers (so they do not leak into the editor content) but
	* bloats the editor data with additional markup.
	*
	* This mode may be required by some features and will be turned on by them automatically.
	*
	* @param type Whether to use the default or the marked `&nbsp;` block fillers.
	*/
	useFillerType(type) {
		this.domConverter.blockFillerMode = type == "marked" ? "markedNbsp" : "nbsp";
	}
	/**
	* Converts an HTML string to its DOM representation. Returns a document fragment containing nodes parsed from
	* the provided data.
	*/
	_toDom(data) {
		if (!/<(?:html|body|head|meta)(?:\s[^>]*)?>/i.test(data.trim().slice(0, 1e4))) data = `<body>${data}</body>`;
		const document = this.domParser.parseFromString(data, "text/html");
		const fragment = document.createDocumentFragment();
		const bodyChildNodes = document.body.childNodes;
		while (bodyChildNodes.length > 0) fragment.appendChild(bodyChildNodes[0]);
		return fragment;
	}
};

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* @module engine/controller/datacontroller
*/
const DataControllerBase = /* #__PURE__ */ EmitterMixin();
/**
* Controller for the data pipeline. The data pipeline controls how data is retrieved from the document
* and set inside it. Hence, the controller features two methods which allow to {@link ~DataController#get get}
* and {@link ~DataController#set set} data of the {@link ~DataController#model model}
* using the given:
*
* * {@link module:engine/dataprocessor/dataprocessor~DataProcessor data processor},
* * downcast converters,
* * upcast converters.
*
* An instance of the data controller is always available in the {@link module:core/editor/editor~Editor#data `editor.data`}
* property:
*
* ```ts
* editor.data.get( { rootName: 'customRoot' } ); // -> '<p>Hello!</p>'
* ```
*/
var DataController = class extends DataControllerBase {
	/**
	* Data model.
	*/
	model;
	/**
	* Mapper used for the conversion. It has no permanent bindings, because these are created while getting data and
	* are cleared directly after the data are converted. However, the mapper is defined as a class property, because
	* it needs to be passed to the `DowncastDispatcher` as a conversion API.
	*/
	mapper;
	/**
	* Downcast dispatcher used by the {@link #get get method}. Downcast converters should be attached to it.
	*/
	downcastDispatcher;
	/**
	* Upcast dispatcher used by the {@link #set set method}. Upcast converters should be attached to it.
	*/
	upcastDispatcher;
	/**
	* The view document used by the data controller.
	*/
	viewDocument;
	/**
	* Styles processor used during the conversion.
	*/
	stylesProcessor;
	/**
	* Data processor used specifically for HTML conversion.
	*/
	htmlProcessor;
	/**
	* Data processor used during the conversion.
	* Same instance as {@link #htmlProcessor} by default. Can be replaced at run time to handle different format, e.g. XML or Markdown.
	*/
	processor;
	/**
	* The view downcast writer just for data conversion purposes, i.e. to modify
	* the {@link #viewDocument}.
	*/
	_viewWriter;
	/**
	* Creates a data controller instance.
	*
	* @param model Data model.
	* @param stylesProcessor The styles processor instance.
	*/
	constructor(model, stylesProcessor) {
		super();
		this.model = model;
		this.mapper = new Mapper();
		this.downcastDispatcher = new DowncastDispatcher({
			mapper: this.mapper,
			schema: model.schema
		});
		this.downcastDispatcher.on("insert:$text", insertText(), { priority: "lowest" });
		this.downcastDispatcher.on("insert", insertAttributesAndChildren(), { priority: "lowest" });
		this.upcastDispatcher = new UpcastDispatcher({ schema: model.schema });
		this.viewDocument = new ViewDocument(stylesProcessor);
		this.stylesProcessor = stylesProcessor;
		this.htmlProcessor = new HtmlDataProcessor(this.viewDocument);
		this.processor = this.htmlProcessor;
		this._viewWriter = new ViewDowncastWriter(this.viewDocument);
		this.upcastDispatcher.on("text", convertText(), { priority: "lowest" });
		this.upcastDispatcher.on("element", convertToModelFragment(), { priority: "lowest" });
		this.upcastDispatcher.on("documentFragment", convertToModelFragment(), { priority: "lowest" });
		ObservableMixin().prototype.decorate.call(this, "init");
		ObservableMixin().prototype.decorate.call(this, "set");
		ObservableMixin().prototype.decorate.call(this, "get");
		ObservableMixin().prototype.decorate.call(this, "toView");
		ObservableMixin().prototype.decorate.call(this, "toModel");
		this.on("init", () => {
			this.fire("ready");
		}, { priority: "lowest" });
		this.on("ready", () => {
			this.model.enqueueChange({ isUndoable: false }, autoParagraphEmptyRoots);
		}, { priority: "lowest" });
	}
	/**
	* Returns the model's data converted by downcast dispatchers attached to {@link #downcastDispatcher} and
	* formatted by the {@link #processor data processor}.
	*
	* A warning is logged when you try to retrieve data for a detached root, as most probably this is a mistake. A detached root should
	* be treated like it is removed, and you should not save its data. Note, that the detached root data is always an empty string.
	*
	* @fires get
	* @param options Additional configuration for the retrieved data. `DataController` provides two optional
	* properties: `rootName` and `trim`. Other properties of this object are specified by various editor features.
	* @param options.rootName Root name. Default 'main'.
	* @param options.trim Whether returned data should be trimmed. This option is set to `empty` by default,
	* which means whenever editor content is considered empty, an empty string will be returned. To turn off trimming completely
	* use `'none'`. In such cases the exact content will be returned (for example a `<p>&nbsp;</p>` for an empty editor).
	* @returns Output data.
	*/
	get(options = {}) {
		const { rootName = "main", trim = "empty" } = options;
		if (!this._checkIfRootsExists([rootName]))
 /**
		* Cannot get data from a non-existing root. This error is thrown when
		* {@link module:engine/controller/datacontroller~DataController#get `DataController#get()` method}
		* is called with a non-existent root name. For example, if there is an editor instance with only `main` root,
		* calling {@link module:engine/controller/datacontroller~DataController#get} like:
		*
		* ```ts
		* data.get( { rootName: 'root2' } );
		* ```
		*
		* will throw this error.
		*
		* @error datacontroller-get-non-existent-root
		*/
		throw new CKEditorError("datacontroller-get-non-existent-root", this);
		const root = this.model.document.getRoot(rootName);
		if (!root.isAttached())
 /**
		* Retrieving document data for a detached root.
		*
		* This usually indicates an error as a detached root should be considered "removed" and should not be included in the
		* document data.
		*
		* @error datacontroller-get-detached-root
		*/
		logWarning("datacontroller-get-detached-root", this);
		if (trim === "empty" && !this.model.hasContent(root, { ignoreWhitespaces: true })) return "";
		return this.stringify(root, options);
	}
	/**
	* Returns the content of the given {@link module:engine/model/element~ModelElement model's element} or
	* {@link module:engine/model/documentfragment~ModelDocumentFragment model document fragment} converted by the downcast converters
	* attached to the {@link #downcastDispatcher} and formatted by the {@link #processor data processor}.
	*
	* @param modelElementOrFragment The element whose content will be stringified.
	* @param options Additional configuration passed to the conversion process.
	* @returns Output data.
	*/
	stringify(modelElementOrFragment, options = {}) {
		const viewDocumentFragment = this.toView(modelElementOrFragment, options);
		return this.processor.toData(viewDocumentFragment);
	}
	/**
	* Returns the content of the given {@link module:engine/model/element~ModelElement model element} or
	* {@link module:engine/model/documentfragment~ModelDocumentFragment model document fragment} converted by the downcast
	* converters attached to {@link #downcastDispatcher} into a
	* {@link module:engine/view/documentfragment~ViewDocumentFragment view document fragment}.
	*
	* @fires toView
	* @param modelElementOrFragment Element or document fragment whose content will be converted.
	* @param options Additional configuration that will be available through the
	* {@link module:engine/conversion/downcastdispatcher~DowncastConversionApi#options} during the conversion process.
	* @returns Output view ModelDocumentFragment.
	*/
	toView(modelElementOrFragment, options = {}) {
		const viewDocument = this.viewDocument;
		const viewWriter = this._viewWriter;
		this.mapper.clearBindings();
		const modelRange = ModelRange._createIn(modelElementOrFragment);
		const viewDocumentFragment = new ViewDocumentFragment(viewDocument);
		this.mapper.bindElements(modelElementOrFragment, viewDocumentFragment);
		const markers = modelElementOrFragment.is("documentFragment") ? modelElementOrFragment.markers : _getMarkersRelativeToElement(modelElementOrFragment);
		this.downcastDispatcher.convert(modelRange, markers, viewWriter, options);
		return viewDocumentFragment;
	}
	/**
	* Sets the initial input data parsed by the {@link #processor data processor} and
	* converted by the {@link #upcastDispatcher view-to-model converters}.
	* Initial data can be only set to a document whose {@link module:engine/model/document~ModelDocument#version} is equal 0.
	*
	* **Note** This method is {@link module:utils/observablemixin~Observable#decorate decorated} which is
	* used by e.g. collaborative editing plugin that syncs remote data on init.
	*
	* When data is passed as a string, it is initialized on the default `main` root:
	*
	* ```ts
	* dataController.init( '<p>Foo</p>' ); // Initializes data on the `main` root only, as no other is specified.
	* ```
	*
	* To initialize data on a different root or multiple roots at once, an object containing `rootName` - `data` pairs should be passed:
	*
	* ```ts
	* dataController.init( { main: '<p>Foo</p>', title: '<h1>Bar</h1>' } ); // Initializes data on both the `main` and `title` roots.
	* ```
	*
	* @fires init
	* @param data Input data as a string or an object containing the `rootName` - `data`
	* pairs to initialize data on multiple roots at once.
	* @returns Promise that is resolved after the data is set on the editor.
	*/
	init(data) {
		if (this.model.document.version)
 /**
		* Cannot set initial data to a non-empty {@link module:engine/model/document~ModelDocument}.
		* Initial data should be set once, during the {@link module:core/editor/editor~Editor} initialization,
		* when the {@link module:engine/model/document~ModelDocument#version} is equal 0.
		*
		* @error datacontroller-init-document-not-empty
		*/
		throw new CKEditorError("datacontroller-init-document-not-empty", this);
		let initialData = {};
		if (typeof data === "string") initialData.main = data;
		else initialData = data;
		if (!this._checkIfRootsExists(Object.keys(initialData)))
 /**
		* Cannot init data on a non-existent root. This error is thrown when
		* {@link module:engine/controller/datacontroller~DataController#init DataController#init() method}
		* is called with non-existent root name. For example, if there is an editor instance with only `main` root,
		* calling {@link module:engine/controller/datacontroller~DataController#init} like:
		*
		* ```ts
		* data.init( { main: '<p>Foo</p>', root2: '<p>Bar</p>' } );
		* ```
		*
		* will throw this error.
		*
		* @error datacontroller-init-non-existent-root
		*/
		throw new CKEditorError("datacontroller-init-non-existent-root", this);
		this.model.enqueueChange({ isUndoable: false }, (writer) => {
			for (const rootName of Object.keys(initialData)) {
				const modelRoot = this.model.document.getRoot(rootName);
				writer.insert(this.parse(initialData[rootName], modelRoot), modelRoot, 0);
			}
		});
		return Promise.resolve();
	}
	/**
	* Sets the input data parsed by the {@link #processor data processor} and
	* converted by the {@link #upcastDispatcher view-to-model converters}.
	* This method can be used any time to replace existing editor data with the new one without clearing the
	* {@link module:engine/model/document~ModelDocument#history document history}.
	*
	* This method also creates a batch with all the changes applied. If all you need is to parse data, use
	* the {@link #parse} method.
	*
	* When data is passed as a string it is set on the default `main` root:
	*
	* ```ts
	* dataController.set( '<p>Foo</p>' ); // Sets data on the `main` root, as no other is specified.
	* ```
	*
	* To set data on a different root or multiple roots at once, an object containing `rootName` - `data` pairs should be passed:
	*
	* ```ts
	* dataController.set( { main: '<p>Foo</p>', title: '<h1>Bar</h1>' } ); // Sets data on the `main` and `title` roots as specified.
	* ```
	*
	* To set the data with a preserved undo stack and add the change to the undo stack, set `{ isUndoable: true }` as a `batchType` option.
	*
	* ```ts
	* dataController.set( '<p>Foo</p>', { batchType: { isUndoable: true } } );
	* ```
	*
	* @fires set
	* @param data Input data as a string or an object containing the `rootName` - `data`
	* pairs to set data on multiple roots at once.
	* @param options Options for setting data.
	* @param options.batchType The batch type that will be used to create a batch for the changes applied by this method.
	* By default, the batch will be set as {@link module:engine/model/batch~Batch#isUndoable not undoable} and the undo stack will be
	* cleared after the new data is applied (all undo steps will be removed). If the batch type `isUndoable` flag is be set to `true`,
	* the undo stack will be preserved instead and not cleared when new data is applied.
	*/
	set(data, options = {}) {
		let newData = {};
		if (typeof data === "string") newData.main = data;
		else newData = data;
		if (!this._checkIfRootsExists(Object.keys(newData)))
 /**
		* Cannot set data on a non-existent root. This error is thrown when the
		* {@link module:engine/controller/datacontroller~DataController#set DataController#set() method}
		* is called with non-existent root name. For example, if there is an editor instance with only the default `main` root,
		* calling {@link module:engine/controller/datacontroller~DataController#set} like:
		*
		* ```ts
		* data.set( { main: '<p>Foo</p>', root2: '<p>Bar</p>' } );
		* ```
		*
		* will throw this error.
		*
		* @error datacontroller-set-non-existent-root
		*/
		throw new CKEditorError("datacontroller-set-non-existent-root", this);
		this.model.enqueueChange(options.batchType || {}, (writer) => {
			writer.setSelection(null);
			writer.removeSelectionAttribute(this.model.document.selection.getAttributeKeys());
			for (const rootName of Object.keys(newData)) {
				const modelRoot = this.model.document.getRoot(rootName);
				writer.remove(writer.createRangeIn(modelRoot));
				writer.insert(this.parse(newData[rootName], modelRoot), modelRoot, 0);
			}
		});
	}
	/**
	* Returns the data parsed by the {@link #processor data processor} and then converted by upcast converters
	* attached to the {@link #upcastDispatcher}.
	*
	* **Note:** The default `context` value is `'$root'`, which only matches the generic root. When the editor uses a
	* custom root {@link module:core/editor/editorconfig~RootConfig#modelElement `modelElement`}, pass the target
	* {@link module:engine/model/rootelement~ModelRootElement root element} (or its configured model element name)
	* explicitly, otherwise the conversion result may be wrong.
	* See the {@glink framework/deep-dive/schema#custom-root-elements Custom root elements} section of the
	* {@glink framework/deep-dive/schema Schema deep-dive} guide for more details.
	*
	* @see #set
	* @param data Data to parse.
	* @param context Base context in which the view will be converted to the model.
	* See: {@link module:engine/conversion/upcastdispatcher~UpcastDispatcher#convert}.
	* @returns Parsed data.
	*/
	parse(data, context = "$root") {
		const viewDocumentFragment = this.processor.toView(data);
		return this.toModel(viewDocumentFragment, context);
	}
	/**
	* Returns the result of the given {@link module:engine/view/element~ViewElement view element} or
	* {@link module:engine/view/documentfragment~ViewDocumentFragment view document fragment} converted by the
	* {@link #upcastDispatcher view-to-model converters}, wrapped by {@link module:engine/model/documentfragment~ModelDocumentFragment}.
	*
	* When marker elements were converted during the conversion process, it will be set as a document fragment's
	* {@link module:engine/model/documentfragment~ModelDocumentFragment#markers static markers map}.
	*
	* **Note:** The default `context` value is `'$root'`, which only matches the generic root. When the editor uses a
	* custom root {@link module:core/editor/editorconfig~RootConfig#modelElement `modelElement`}, pass the target
	* {@link module:engine/model/rootelement~ModelRootElement root element} (or its configured model element name)
	* explicitly, otherwise the conversion result may be wrong.
	* See the {@glink framework/deep-dive/schema#custom-root-elements Custom root elements} section of the
	* {@glink framework/deep-dive/schema Schema deep-dive} guide for more details.
	*
	* @fires toModel
	* @param viewElementOrFragment The element or document fragment whose content will be converted.
	* @param context Base context in which the view will be converted to the model.
	* See: {@link module:engine/conversion/upcastdispatcher~UpcastDispatcher#convert}.
	* @returns Output document fragment.
	*/
	toModel(viewElementOrFragment, context = "$root") {
		return this.model.change((writer) => {
			return this.upcastDispatcher.convert(viewElementOrFragment, writer, context);
		});
	}
	/**
	* Adds the style processor normalization rules.
	*
	* You can implement your own rules as well as use one of the available processor rules:
	*
	* * background: {@link module:engine/view/styles/background~addBackgroundStylesRules}
	* * border: {@link module:engine/view/styles/border~addBorderStylesRules}
	* * margin: {@link module:engine/view/styles/margin~addMarginStylesRules}
	* * padding: {@link module:engine/view/styles/padding~addPaddingStylesRules}
	*/
	addStyleProcessorRules(callback) {
		callback(this.stylesProcessor);
	}
	/**
	* Registers a {@link module:engine/view/matcher~MatcherPattern} on an {@link #htmlProcessor htmlProcessor}
	* and a {@link #processor processor} for view elements whose content should be treated as raw data
	* and not processed during the conversion from DOM to view elements.
	*
	* The raw data can be later accessed by the
	* {@link module:engine/view/element~ViewElement#getCustomProperty view element custom property}
	* `"$rawContent"`.
	*
	* @param pattern Pattern matching all view elements whose content should be treated as a raw data.
	*/
	registerRawContentMatcher(pattern) {
		if (this.processor && this.processor !== this.htmlProcessor) this.processor.registerRawContentMatcher(pattern);
		this.htmlProcessor.registerRawContentMatcher(pattern);
	}
	/**
	* Removes all event listeners set by the DataController.
	*/
	destroy() {
		this.stopListening();
	}
	/**
	* Checks whether all provided root names are actually existing editor roots.
	*
	* @param rootNames Root names to check.
	* @returns Whether all provided root names are existing editor roots.
	*/
	_checkIfRootsExists(rootNames) {
		for (const rootName of rootNames) if (!this.model.document.getRoot(rootName)) return false;
		return true;
	}
};
/**
* Helper function for downcast conversion.
*
* Takes a document element (element that is added to a model document) and checks which markers are inside it. If the marker is collapsed
* at element boundary, it is considered as contained inside the element and marker range is returned. Otherwise, if the marker is
* intersecting with the element, the intersection is returned.
*/
function _getMarkersRelativeToElement(element) {
	const result = [];
	const doc = element.root.document;
	if (!doc) return /* @__PURE__ */ new Map();
	const elementRange = ModelRange._createIn(element);
	for (const marker of doc.model.markers) {
		const markerRange = marker.getRange();
		const isMarkerCollapsed = markerRange.isCollapsed;
		const isMarkerAtElementBoundary = markerRange.start.isEqual(elementRange.start) || markerRange.end.isEqual(elementRange.end);
		if (isMarkerCollapsed && isMarkerAtElementBoundary) result.push([marker.name, markerRange]);
		else {
			const updatedMarkerRange = elementRange.getIntersection(markerRange);
			if (updatedMarkerRange) result.push([marker.name, updatedMarkerRange]);
		}
	}
	return new Map(result);
}

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* @module engine/conversion/conversion
*/
/**
* A utility class that helps add converters to upcast and downcast dispatchers.
*
* We recommend reading the {@glink framework/deep-dive/conversion/intro editor conversion} guide first to
* understand the core concepts of the conversion mechanisms.
*
* An instance of the conversion manager is available in the
* {@link module:core/editor/editor~Editor#conversion `editor.conversion`} property
* and by default has the following groups of dispatchers (i.e. directions of conversion):
*
* * `downcast` (editing and data downcasts)
* * `editingDowncast`
* * `dataDowncast`
* * `upcast`
*
* # One-way converters
*
* To add a converter to a specific group, use the {@link module:engine/conversion/conversion~Conversion#for `for()`}
* method:
*
* ```ts
* // Add a converter to editing downcast and data downcast.
* editor.conversion.for( 'downcast' ).elementToElement( config ) );
*
* // Add a converter to the data pipepline only:
* editor.conversion.for( 'dataDowncast' ).elementToElement( dataConversionConfig ) );
*
* // And a slightly different one for the editing pipeline:
* editor.conversion.for( 'editingDowncast' ).elementToElement( editingConversionConfig ) );
* ```
*
* See {@link module:engine/conversion/conversion~Conversion#for `for()`} method documentation to learn more about
* available conversion helpers and how to use your custom ones.
*
* # Two-way converters
*
* Besides using one-way converters via the `for()` method, you can also use other methods available in this
* class to add two-way converters (upcast and downcast):
*
* * {@link module:engine/conversion/conversion~Conversion#elementToElement `elementToElement()`} &ndash;
* Model element to view element and vice versa.
* * {@link module:engine/conversion/conversion~Conversion#attributeToElement `attributeToElement()`} &ndash;
* Model attribute to view element and vice versa.
* * {@link module:engine/conversion/conversion~Conversion#attributeToAttribute `attributeToAttribute()`} &ndash;
* Model attribute to view attribute and vice versa.
*/
var Conversion = class {
	/**
	* Maps dispatchers group name to ConversionHelpers instances.
	*/
	_helpers = /* @__PURE__ */ new Map();
	_downcast;
	_upcast;
	/**
	* Creates a new conversion instance.
	*/
	constructor(downcastDispatchers, upcastDispatchers) {
		this._downcast = toArray(downcastDispatchers);
		this._createConversionHelpers({
			name: "downcast",
			dispatchers: this._downcast,
			isDowncast: true
		});
		this._upcast = toArray(upcastDispatchers);
		this._createConversionHelpers({
			name: "upcast",
			dispatchers: this._upcast,
			isDowncast: false
		});
	}
	/**
	* Define an alias for registered dispatcher.
	*
	* ```ts
	* const conversion = new Conversion(
	* 	[ dataDowncastDispatcher, editingDowncastDispatcher ],
	* 	upcastDispatcher
	* );
	*
	* conversion.addAlias( 'dataDowncast', dataDowncastDispatcher );
	* ```
	*
	* @param alias An alias of a dispatcher.
	* @param dispatcher Dispatcher which should have an alias.
	*/
	addAlias(alias, dispatcher) {
		const isDowncast = this._downcast.includes(dispatcher);
		if (!this._upcast.includes(dispatcher) && !isDowncast)
 /**
		* Trying to register an alias for a dispatcher that nas not been registered.
		*
		* @error conversion-add-alias-dispatcher-not-registered
		*/
		throw new CKEditorError("conversion-add-alias-dispatcher-not-registered", this);
		this._createConversionHelpers({
			name: alias,
			dispatchers: [dispatcher],
			isDowncast
		});
	}
	/**
	* Provides a chainable API to assign converters to a conversion dispatchers group.
	*
	* If the given group name has not been registered, the
	* {@link module:utils/ckeditorerror~CKEditorError `conversion-for-unknown-group` error} is thrown.
	*
	* You can use conversion helpers available directly in the `for()` chain or your custom ones via
	* the {@link module:engine/conversion/conversionhelpers~ConversionHelpers#add `add()`} method.
	*
	* # Using built-in conversion helpers
	*
	* The `for()` chain comes with a set of conversion helpers which you can use like this:
	*
	* ```ts
	* editor.conversion.for( 'downcast' )
	* 	.elementToElement( config1 )        // Adds an element-to-element downcast converter.
	* 	.attributeToElement( config2 );     // Adds an attribute-to-element downcast converter.
	*
	* editor.conversion.for( 'upcast' )
	* 	.elementToAttribute( config3 );     // Adds an element-to-attribute upcast converter.
	* ```
	*
	* Refer to the documentation of built-in conversion helpers to learn about their configuration options.
	*
	* * downcast (model-to-view) conversion helpers:
	*
	*	* {@link module:engine/conversion/downcasthelpers~DowncastHelpers#elementToElement `elementToElement()`},
	*	* {@link module:engine/conversion/downcasthelpers~DowncastHelpers#attributeToElement `attributeToElement()`},
	*	* {@link module:engine/conversion/downcasthelpers~DowncastHelpers#attributeToAttribute `attributeToAttribute()`}.
	*	* {@link module:engine/conversion/downcasthelpers~DowncastHelpers#markerToElement `markerToElement()`}.
	*	* {@link module:engine/conversion/downcasthelpers~DowncastHelpers#markerToHighlight `markerToHighlight()`}.
	*
	* * upcast (view-to-model) conversion helpers:
	*
	*	* {@link module:engine/conversion/upcasthelpers~UpcastHelpers#elementToElement `elementToElement()`},
	*	* {@link module:engine/conversion/upcasthelpers~UpcastHelpers#elementToAttribute `elementToAttribute()`},
	*	* {@link module:engine/conversion/upcasthelpers~UpcastHelpers#attributeToAttribute `attributeToAttribute()`}.
	*	* {@link module:engine/conversion/upcasthelpers~UpcastHelpers#elementToMarker `elementToMarker()`}.
	*
	* # Using custom conversion helpers
	*
	* If you need to implement an atypical converter, you can do so by calling:
	*
	* ```ts
	* editor.conversion.for( direction ).add( customHelper );
	* ```
	*
	* The `.add()` method takes exactly one parameter, which is a function. This function should accept one parameter that
	* is a dispatcher instance. The function should add an actual converter to the passed dispatcher instance.
	*
	* Example:
	*
	* ```ts
	* editor.conversion.for( 'upcast' ).add( dispatcher => {
	* 	dispatcher.on( 'element:a',  ( evt, data, conversionApi ) => {
	* 		// Do something with a view <a> element.
	* 	} );
	* } );
	* ```
	*
	* Refer to the documentation of {@link module:engine/conversion/upcastdispatcher~UpcastDispatcher}
	* and {@link module:engine/conversion/downcastdispatcher~DowncastDispatcher} to learn how to write
	* custom converters.
	*
	* @param groupName The name of dispatchers group to add the converters to.
	*/
	for(groupName) {
		if (!this._helpers.has(groupName))
 /**
		* Trying to add a converter to an unknown dispatchers group.
		*
		* @error conversion-for-unknown-group
		*/
		throw new CKEditorError("conversion-for-unknown-group", this);
		return this._helpers.get(groupName);
	}
	/**
	* Sets up converters between the model and the view that convert a model element to a view element (and vice versa).
	* For example, the model `<paragraph>Foo</paragraph>` is turned into `<p>Foo</p>` in the view.
	*
	* ```ts
	* // A simple conversion from the `paragraph` model element to the `<p>` view element (and vice versa).
	* editor.conversion.elementToElement( { model: 'paragraph', view: 'p' } );
	*
	* // Override other converters by specifying a converter definition with a higher priority.
	* editor.conversion.elementToElement( { model: 'paragraph', view: 'div', converterPriority: 'high' } );
	*
	* // View specified as an object instead of a string.
	* editor.conversion.elementToElement( {
	* 	model: 'fancyParagraph',
	* 	view: {
	* 		name: 'p',
	* 		classes: 'fancy'
	* 	}
	* } );
	*
	* // Use `upcastAlso` to define other view elements that should also be converted to a `paragraph` element.
	* editor.conversion.elementToElement( {
	* 	model: 'paragraph',
	* 	view: 'p',
	* 	upcastAlso: [
	* 		'div',
	* 		{
	* 			// Any element with the `display: block` style.
	* 			styles: {
	* 				display: 'block'
	* 			}
	* 		}
	* 	]
	* } );
	*
	* // `upcastAlso` set as callback enables a conversion of a wide range of different view elements.
	* editor.conversion.elementToElement( {
	* 	model: 'heading',
	* 	view: 'h2',
	* 	// Convert "heading-like" paragraphs to headings.
	* 	upcastAlso: viewElement => {
	* 		const fontSize = viewElement.getStyle( 'font-size' );
	*
	* 		if ( !fontSize ) {
	* 			return null;
	* 		}
	*
	* 		const match = fontSize.match( /(\d+)\s*px/ );
	*
	* 		if ( !match ) {
	* 			return null;
	* 		}
	*
	* 		const size = Number( match[ 1 ] );
	*
	* 		if ( size > 26 ) {
	* 			// Returned value can be an object with the matched properties.
	* 			// These properties will be "consumed" during the conversion.
	* 			// See `engine.view.Matcher~MatcherPattern` and `engine.view.Matcher#match` for more details.
	*
	* 			return { name: true, styles: [ 'font-size' ] };
	* 		}
	*
	* 		return null;
	* 	}
	* } );
	* ```
	*
	* `definition.model` is a `String` with a model element name to convert from or to.
	*
	* @param definition The converter definition.
	*/
	elementToElement(definition) {
		this.for("downcast").elementToElement(definition);
		for (const { model, view } of _getAllUpcastDefinitions(definition)) this.for("upcast").elementToElement({
			model,
			view,
			converterPriority: definition.converterPriority
		});
	}
	/**
	* Sets up converters between the model and the view that convert a model attribute to a view element (and vice versa).
	* For example, a model text node with `"Foo"` as data and the `bold` attribute will be turned to `<strong>Foo</strong>` in the view.
	*
	* ```ts
	* // A simple conversion from the `bold=true` attribute to the `<strong>` view element (and vice versa).
	* editor.conversion.attributeToElement( { model: 'bold', view: 'strong' } );
	*
	* // Override other converters by specifying a converter definition with a higher priority.
	* editor.conversion.attributeToElement( { model: 'bold', view: 'b', converterPriority: 'high' } );
	*
	* // View specified as an object instead of a string.
	* editor.conversion.attributeToElement( {
	* 	model: 'bold',
	* 	view: {
	* 		name: 'span',
	* 		classes: 'bold'
	* 	}
	* } );
	*
	* // Use `config.model.name` to define the conversion only from a given node type, `$text` in this case.
	* // The same attribute on different elements may then be handled by a different converter.
	* editor.conversion.attributeToElement( {
	* 	model: {
	* 		key: 'textDecoration',
	* 		values: [ 'underline', 'lineThrough' ],
	* 		name: '$text'
	* 	},
	* 	view: {
	* 		underline: {
	* 			name: 'span',
	* 			styles: {
	* 				'text-decoration': 'underline'
	* 			}
	* 		},
	* 		lineThrough: {
	* 			name: 'span',
	* 			styles: {
	* 				'text-decoration': 'line-through'
	* 			}
	* 		}
	* 	}
	* } );
	*
	* // Use `upcastAlso` to define other view elements that should also be converted to the `bold` attribute.
	* editor.conversion.attributeToElement( {
	* 	model: 'bold',
	* 	view: 'strong',
	* 	upcastAlso: [
	* 		'b',
	* 		{
	* 			name: 'span',
	* 			classes: 'bold'
	* 		},
	* 		{
	* 			name: 'span',
	* 			styles: {
	* 				'font-weight': 'bold'
	* 			}
	* 		},
	* 		viewElement => {
	* 			const fontWeight = viewElement.getStyle( 'font-weight' );
	*
	* 			if ( viewElement.is( 'element', 'span' ) && fontWeight && /\d+/.test() && Number( fontWeight ) > 500 ) {
	* 				// Returned value can be an object with the matched properties.
	* 				// These properties will be "consumed" during the conversion.
	* 				// See `engine.view.Matcher~MatcherPattern` and `engine.view.Matcher#match` for more details.
	*
	* 				return {
	* 					name: true,
	* 					styles: [ 'font-weight' ]
	* 				};
	* 			}
	* 		}
	* 	]
	* } );
	*
	* // Conversion from and to a model attribute key whose value is an enum (`fontSize=big|small`).
	* // `upcastAlso` set as callback enables a conversion of a wide range of different view elements.
	* editor.conversion.attributeToElement( {
	* 	model: {
	* 		key: 'fontSize',
	* 		values: [ 'big', 'small' ]
	* 	},
	* 	view: {
	* 		big: {
	* 			name: 'span',
	* 			styles: {
	* 				'font-size': '1.2em'
	* 			}
	* 		},
	* 		small: {
	* 			name: 'span',
	* 			styles: {
	* 				'font-size': '0.8em'
	* 			}
	* 		}
	* 	},
	* 	upcastAlso: {
	* 		big: viewElement => {
	* 			const fontSize = viewElement.getStyle( 'font-size' );
	*
	* 			if ( !fontSize ) {
	* 				return null;
	* 			}
	*
	* 			const match = fontSize.match( /(\d+)\s*px/ );
	*
	* 			if ( !match ) {
	* 				return null;
	* 			}
	*
	* 			const size = Number( match[ 1 ] );
	*
	* 			if ( viewElement.is( 'element', 'span' ) && size > 10 ) {
	* 				// Returned value can be an object with the matched properties.
	* 				// These properties will be "consumed" during the conversion.
	* 				// See `engine.view.Matcher~MatcherPattern` and `engine.view.Matcher#match` for more details.
	*
	* 				return { name: true, styles: [ 'font-size' ] };
	* 			}
	*
	* 			return null;
	* 		},
	* 		small: viewElement => {
	* 			const fontSize = viewElement.getStyle( 'font-size' );
	*
	* 			if ( !fontSize ) {
	* 				return null;
	* 			}
	*
	* 			const match = fontSize.match( /(\d+)\s*px/ );
	*
	* 			if ( !match ) {
	* 				return null;
	* 			}
	*
	* 			const size = Number( match[ 1 ] );
	*
	* 			if ( viewElement.is( 'element', 'span' ) && size < 10 ) {
	* 				// Returned value can be an object with the matched properties.
	* 				// These properties will be "consumed" during the conversion.
	* 				// See `engine.view.Matcher~MatcherPattern` and `engine.view.Matcher#match` for more details.
	*
	* 				return { name: true, styles: [ 'font-size' ] };
	* 			}
	*
	* 			return null;
	* 		}
	* 	}
	* } );
	* ```
	*
	* The `definition.model` parameter specifies which model attribute should be converted from or to. It can be a `{ key, value }` object
	* describing the attribute key and value to convert or a `String` specifying just the attribute key (in such a case
	* `value` is set to `true`).
	*
	* @param definition The converter definition.
	*/
	attributeToElement(definition) {
		this.for("downcast").attributeToElement(definition);
		for (const { model, view } of _getAllUpcastDefinitions(definition)) this.for("upcast").elementToAttribute({
			view,
			model,
			converterPriority: definition.converterPriority
		});
	}
	/**
	* Sets up converters between the model and the view that convert a model attribute to a view attribute (and vice versa). For example,
	* `<imageBlock src='foo.jpg'></imageBlock>` is converted to `<img src='foo.jpg'></img>` (the same attribute key and value).
	* This type of converters is intended to be used with {@link module:engine/model/element~ModelElement model element} nodes.
	* To convert the text attributes,
	* the {@link module:engine/conversion/conversion~Conversion#attributeToElement `attributeToElement converter`}should be set up.
	*
	* ```ts
	* // A simple conversion from the `source` model attribute to the `src` view attribute (and vice versa).
	* editor.conversion.attributeToAttribute( { model: 'source', view: 'src' } );
	*
	* // Attribute values are strictly specified.
	* editor.conversion.attributeToAttribute( {
	* 	model: {
	* 		name: 'imageInline',
	* 		key: 'aside',
	* 		values: [ 'aside' ]
	* 	},
	* 	view: {
	* 		aside: {
	* 			name: 'img',
	* 			key: 'class',
	* 			value: [ 'aside', 'half-size' ]
	* 		}
	* 	}
	* } );
	*
	* // Set the style attribute.
	* editor.conversion.attributeToAttribute( {
	* 	model: {
	* 		name: 'imageInline',
	* 		key: 'aside',
	* 		values: [ 'aside' ]
	* 	},
	* 	view: {
	* 		aside: {
	* 			name: 'img',
	* 			key: 'style',
	* 			value: {
	* 				float: 'right',
	* 				width: '50%',
	* 				margin: '5px'
	* 			}
	* 		}
	* 	}
	* } );
	*
	* // Conversion from and to a model attribute key whose value is an enum (`align=right|center`).
	* // Use `upcastAlso` to define other view elements that should also be converted to the `align=right` attribute.
	* editor.conversion.attributeToAttribute( {
	* 	model: {
	* 		key: 'align',
	* 		values: [ 'right', 'center' ]
	* 	},
	* 	view: {
	* 		right: {
	* 			key: 'class',
	* 			value: 'align-right'
	* 		},
	* 		center: {
	* 			key: 'class',
	* 			value: 'align-center'
	* 		}
	* 	},
	* 	upcastAlso: {
	* 		right: {
	* 			styles: {
	* 				'text-align': 'right'
	* 			}
	* 		},
	* 		center: {
	* 			styles: {
	* 				'text-align': 'center'
	* 			}
	* 		}
	* 	}
	* } );
	* ```
	*
	* The `definition.model` parameter specifies which model attribute should be converted from and to.
	* It can be a `{ key, [ values ], [ name ] }` object or a `String`, which will be treated like `{ key: definition.model }`.
	* The `key` property is the model attribute key to convert from and to.
	* The `values` are the possible model attribute values. If the `values` parameter is not set, the model attribute value
	* will be the same as the view attribute value.
	* If `name` is set, the conversion will be set up only for model elements with the given name.
	*
	* The `definition.view` parameter specifies which view attribute should be converted from and to.
	* It can be a `{ key, value, [ name ] }` object or a `String`, which will be treated like `{ key: definition.view }`.
	* The `key` property is the view attribute key to convert from and to.
	* The `value` is the view attribute value to convert from and to. If `definition.value` is not set, the view attribute value will be
	* the same as the model attribute value.
	* If `key` is `'class'`, `value` can be a `String` or an array of `String`s.
	* If `key` is `'style'`, `value` is an object with key-value pairs.
	* In other cases, `value` is a `String`.
	* If `name` is set, the conversion will be set up only for model elements with the given name.
	* If `definition.model.values` is set, `definition.view` is an object that assigns values from `definition.model.values`
	* to `{ key, value, [ name ] }` objects.
	*
	* `definition.upcastAlso` specifies which other matching view elements should also be upcast to the given model configuration.
	* If `definition.model.values` is set, `definition.upcastAlso` should be an object assigning values from `definition.model.values`
	* to {@link module:engine/view/matcher~MatcherPattern}s or arrays of {@link module:engine/view/matcher~MatcherPattern}s.
	*
	* **Note:** `definition.model` and `definition.view` form should be mirrored, so the same types of parameters should
	* be given in both parameters.
	*
	* @param definition The converter definition.
	* @param definition.model The model attribute to convert from and to.
	* @param definition.view The view attribute to convert from and to.
	* @param definition.upcastAlso Any view element matching `definition.upcastAlso` will also be converted to the given model attribute.
	* `definition.upcastAlso` is used only if `config.model.values` is specified.
	*/
	attributeToAttribute(definition) {
		this.for("downcast").attributeToAttribute(definition);
		for (const { model, view } of _getAllUpcastDefinitions(definition)) this.for("upcast").attributeToAttribute({
			view,
			model
		});
	}
	/**
	* Creates and caches conversion helpers for given dispatchers group.
	*
	* @param options Group name.
	* @param options.name Group name.
	* @param options.dispatchers Dispatchers to register.
	* @param options.isDowncast Whether downcast group.
	*/
	_createConversionHelpers({ name, dispatchers, isDowncast }) {
		if (this._helpers.has(name))
 /**
		* Trying to register a group name that has already been registered.
		*
		* @error conversion-group-exists
		*/
		throw new CKEditorError("conversion-group-exists", this);
		const helpers = isDowncast ? new DowncastHelpers(dispatchers) : new UpcastHelpers(dispatchers);
		this._helpers.set(name, helpers);
	}
};
/**
* Helper function that creates a joint array out of an item passed in `definition.view` and items passed in
* `definition.upcastAlso`.
*/
function* _getAllUpcastDefinitions(definition) {
	if (definition.model.values) for (const value of definition.model.values) {
		const model = {
			key: definition.model.key,
			value
		};
		const view = definition.view[value];
		yield* _getUpcastDefinition(model, view, definition.upcastAlso ? definition.upcastAlso[value] : void 0);
	}
	else yield* _getUpcastDefinition(definition.model, definition.view, definition.upcastAlso);
}
function* _getUpcastDefinition(model, view, upcastAlso) {
	yield {
		model,
		view
	};
	if (upcastAlso) for (const upcastAlsoItem of toArray(upcastAlso)) yield {
		model,
		view: upcastAlsoItem
	};
}

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* @module engine/dataprocessor/xmldataprocessor
*/
/**
* The XML data processor class.
* This data processor implementation uses XML as input and output data.
* This class is needed because unlike HTML, XML allows to use any tag with any value.
* For example, `<link>Text</link>` is a valid XML but invalid HTML.
*/
var XmlDataProcessor = class {
	/**
	* A list of namespaces allowed to use in the XML input.
	*
	* For example, registering namespaces [ 'attribute', 'container' ] allows to use `<attirbute:tagName></attribute:tagName>`
	* and `<container:tagName></container:tagName>` input. It is mainly for debugging.
	*/
	namespaces;
	/**
	* DOM parser instance used to parse an XML string to an XML document.
	*/
	domParser;
	/**
	* DOM converter used to convert DOM elements to view elements.
	*/
	domConverter;
	/**
	* A basic HTML writer instance used to convert DOM elements to an XML string.
	* There is no need to use a dedicated XML writer because the basic HTML writer works well in this case.
	*/
	htmlWriter;
	skipComments = true;
	/**
	* Creates a new instance of the XML data processor class.
	*
	* @param document The view document instance.
	* @param options Configuration options.
	* @param options.namespaces A list of namespaces allowed to use in the XML input.
	*/
	constructor(document, options = {}) {
		this.namespaces = options.namespaces || [];
		this.domParser = new DOMParser();
		this.domConverter = new ViewDomConverter(document, { renderingMode: "data" });
		this.htmlWriter = new BasicHtmlWriter();
	}
	/**
	* Converts the provided {@link module:engine/view/documentfragment~ViewDocumentFragment document fragment}
	* to data format &ndash; in this case an XML string.
	*
	* @returns An XML string.
	*/
	toData(viewFragment) {
		const domFragment = this.domConverter.viewToDom(viewFragment);
		return this.htmlWriter.getHtml(domFragment);
	}
	/**
	* Converts the provided XML string to a view tree.
	*
	* @param data An XML string.
	* @returns A converted view element.
	*/
	toView(data) {
		const domFragment = this._toDom(data);
		return this.domConverter.domToView(domFragment, {
			keepOriginalCase: true,
			skipComments: this.skipComments
		});
	}
	/**
	* Registers a {@link module:engine/view/matcher~MatcherPattern} for view elements whose content should be treated as raw data
	* and not processed during the conversion from XML to view elements.
	*
	* The raw data can be later accessed by a
	* {@link module:engine/view/element~ViewElement#getCustomProperty custom property of a view element} called `"$rawContent"`.
	*
	* @param pattern Pattern matching all view elements whose content should be treated as raw data.
	*/
	registerRawContentMatcher(pattern) {
		this.domConverter.registerRawContentMatcher(pattern);
	}
	/**
	* If the processor is set to use marked fillers, it will insert `&nbsp;` fillers wrapped in `<span>` elements
	* (`<span data-cke-filler="true">&nbsp;</span>`) instead of regular `&nbsp;` characters.
	*
	* This mode allows for a more precise handling of block fillers (so they do not leak into editor content) but
	* bloats the editor data with additional markup.
	*
	* This mode may be required by some features and will be turned on by them automatically.
	*
	* @param type Whether to use the default or the marked `&nbsp;` block fillers.
	*/
	useFillerType(type) {
		this.domConverter.blockFillerMode = type == "marked" ? "markedNbsp" : "nbsp";
	}
	/**
	* Converts an XML string to its DOM representation. Returns a document fragment containing nodes parsed from
	* the provided data.
	*/
	_toDom(data) {
		data = `<xml ${this.namespaces.map((nsp) => `xmlns:${nsp}="nsp"`).join(" ")}>${data}</xml>`;
		const parsedDocument = this.domParser.parseFromString(data, "text/xml");
		const parserError = parsedDocument.querySelector("parsererror");
		if (parserError) throw new Error("Parse error - " + parserError.textContent);
		const fragment = parsedDocument.createDocumentFragment();
		const nodes = parsedDocument.documentElement.childNodes;
		while (nodes.length > 0) fragment.appendChild(nodes[0]);
		return fragment;
	}
};

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* Abstract base operation class.
*/
var Operation = class {
	/**
	* {@link module:engine/model/document~ModelDocument#version} on which operation can be applied. If you try to
	* {@link module:engine/model/model~Model#applyOperation apply} operation with different base version than the
	* {@link module:engine/model/document~ModelDocument#version document version} the
	* {@link module:utils/ckeditorerror~CKEditorError model-document-applyOperation-wrong-version} error is thrown.
	*/
	baseVersion;
	/**
	* Defines whether operation is executed on attached or detached {@link module:engine/model/item~ModelItem items}.
	*/
	isDocumentOperation;
	/**
	* {@link module:engine/model/batch~Batch Batch} to which the operation is added or `null` if the operation is not
	* added to any batch yet.
	*
	* Note, that a {@link #isDocumentOperation non-document operation} has this property always set to `null`, and is never added
	* to any batch.
	*/
	batch;
	/**
	* Base operation constructor.
	*
	* @param baseVersion Document {@link module:engine/model/document~ModelDocument#version} on which operation
	* can be applied or `null` if the operation operates on detached (non-document) tree.
	*/
	constructor(baseVersion) {
		this.baseVersion = baseVersion;
		this.isDocumentOperation = this.baseVersion !== null;
		this.batch = null;
	}
	/**
	* Checks whether the operation's parameters are correct and the operation can be correctly executed. Throws
	* an error if operation is not valid.
	*
	* @internal
	*/
	_validate() {}
	/**
	* Custom toJSON method to solve child-parent circular dependencies.
	*
	* @returns Clone of this object with the operation property replaced with string.
	*/
	toJSON() {
		const json = Object.assign({}, this);
		json.__className = this.constructor.className;
		delete json.batch;
		delete json.isDocumentOperation;
		return json;
	}
	/**
	* Name of the operation class used for serialization.
	*/
	static get className() {
		return "Operation";
	}
	/**
	* Creates `Operation` object from deserialized object, i.e. from parsed JSON string.
	*
	* @param json Deserialized JSON object.
	* @param document Document on which this operation will be applied.
	*/
	static fromJSON(json, document) {
		return new this(json.baseVersion);
	}
};

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* @module engine/model/operation/utils
*/
/**
* Inserts given nodes at given position.
*
* @internal
* @param position Position at which nodes should be inserted.
* @param nodes Nodes to insert.
* @returns Range spanning over inserted elements.
*/
function _insert(position, nodes) {
	const normalizedNodes = _normalizeNodes(nodes);
	const offset = normalizedNodes.reduce((sum, node) => sum + node.offsetSize, 0);
	const parent = position.parent;
	_splitNodeAtPosition(position);
	const index = position.index;
	parent._insertChild(index, normalizedNodes);
	_mergeNodesAtIndex(parent, index + normalizedNodes.length);
	_mergeNodesAtIndex(parent, index);
	return new ModelRange(position, position.getShiftedBy(offset));
}
/**
* Removed nodes in given range. Only {@link module:engine/model/range~ModelRange#isFlat flat} ranges are accepted.
*
* @internal
* @param range Range containing nodes to remove.
*/
function _remove(range) {
	if (!range.isFlat)
 /**
	* Trying to remove a range which starts and ends in different element.
	*
	* @error operation-utils-remove-range-not-flat
	*/
	throw new CKEditorError("operation-utils-remove-range-not-flat", this);
	const parent = range.start.parent;
	_splitNodeAtPosition(range.start);
	_splitNodeAtPosition(range.end);
	const removed = parent._removeChildren(range.start.index, range.end.index - range.start.index);
	_mergeNodesAtIndex(parent, range.start.index);
	return removed;
}
/**
* Moves nodes in given range to given target position. Only {@link module:engine/model/range~ModelRange#isFlat flat} ranges are accepted.
*
* @internal
* @param sourceRange Range containing nodes to move.
* @param targetPosition Position to which nodes should be moved.
* @returns Range containing moved nodes.
*/
function _move(sourceRange, targetPosition) {
	if (!sourceRange.isFlat)
 /**
	* Trying to move a range which starts and ends in different element.
	*
	* @error operation-utils-move-range-not-flat
	*/
	throw new CKEditorError("operation-utils-move-range-not-flat", this);
	const nodes = _remove(sourceRange);
	targetPosition = targetPosition._getTransformedByDeletion(sourceRange.start, sourceRange.end.offset - sourceRange.start.offset);
	return _insert(targetPosition, nodes);
}
/**
* Sets given attribute on nodes in given range. The attributes are only set on top-level nodes of the range, not on its children.
*
* @internal
* @param range Range containing nodes that should have the attribute set. Must be a flat range.
* @param key Key of attribute to set.
* @param value Attribute value.
*/
function _setAttribute(range, key, value) {
	_splitNodeAtPosition(range.start);
	_splitNodeAtPosition(range.end);
	for (const item of range.getItems({ shallow: true })) {
		const node = item.is("$textProxy") ? item.textNode : item;
		if (value !== null) node._setAttribute(key, value);
		else node._removeAttribute(key);
		_mergeNodesAtIndex(node.parent, node.index);
	}
	_mergeNodesAtIndex(range.end.parent, range.end.index);
}
/**
* Normalizes given object or an array of objects to an array of {@link module:engine/model/node~ModelNode nodes}. See
* {@link ~ModelNodeSet NodeSet} for details on how normalization is performed.
*
* @internal
* @param nodes Objects to normalize.
* @returns Normalized nodes.
*/
function _normalizeNodes(nodes) {
	const normalized = [];
	function convert(nodes) {
		if (typeof nodes == "string") normalized.push(new ModelText(nodes));
		else if (nodes instanceof ModelTextProxy) normalized.push(new ModelText(nodes.data, nodes.getAttributes()));
		else if (nodes instanceof ModelNode) normalized.push(nodes);
		else if (isIterable(nodes)) for (const node of nodes) convert(node);
	}
	convert(nodes);
	for (let i = 1; i < normalized.length; i++) {
		const node = normalized[i];
		const prev = normalized[i - 1];
		if (node instanceof ModelText && prev instanceof ModelText && _haveSameAttributes(node, prev)) {
			normalized.splice(i - 1, 2, new ModelText(prev.data + node.data, prev.getAttributes()));
			i--;
		}
	}
	return normalized;
}
/**
* Checks if nodes before and after given index in given element are {@link module:engine/model/text~ModelText text nodes} and
* merges them into one node if they have same attributes.
*
* Merging is done by removing two text nodes and inserting a new text node containing data from both merged text nodes.
*
* @param element Parent element of nodes to merge.
* @param index Index between nodes to merge.
*/
function _mergeNodesAtIndex(element, index) {
	const nodeBefore = element.getChild(index - 1);
	const nodeAfter = element.getChild(index);
	if (nodeBefore && nodeAfter && nodeBefore.is("$text") && nodeAfter.is("$text") && _haveSameAttributes(nodeBefore, nodeAfter)) {
		const mergedNode = new ModelText(nodeBefore.data + nodeAfter.data, nodeBefore.getAttributes());
		element._removeChildren(index - 1, 2);
		element._insertChild(index - 1, mergedNode);
	}
}
/**
* Checks if given position is in a text node, and if so, splits the text node in two text nodes, each of them
* containing a part of original text node.
*
* @param position Position at which node should be split.
*/
function _splitNodeAtPosition(position) {
	const textNode = position.textNode;
	const element = position.parent;
	if (textNode) {
		const offsetDiff = position.offset - textNode.startOffset;
		const index = textNode.index;
		element._removeChildren(index, 1);
		const firstPart = new ModelText(textNode.data.substr(0, offsetDiff), textNode.getAttributes());
		const secondPart = new ModelText(textNode.data.substr(offsetDiff), textNode.getAttributes());
		element._insertChild(index, [firstPart, secondPart]);
	}
}
/**
* Checks whether two given nodes have same attributes.
*
* @param nodeA Node to check.
* @param nodeB Node to check.
* @returns `true` if nodes have same attributes, `false` otherwise.
*/
function _haveSameAttributes(nodeA, nodeB) {
	const iteratorA = nodeA.getAttributes();
	const iteratorB = nodeB.getAttributes();
	for (const attr of iteratorA) {
		if (attr[1] !== nodeB.getAttribute(attr[0])) return false;
		iteratorB.next();
	}
	return iteratorB.next().done;
}

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* @module engine/model/operation/moveoperation
*/
/**
* Operation to move a range of {@link module:engine/model/item~ModelItem model items}
* to given {@link module:engine/model/position~ModelPosition target position}.
*/
var MoveOperation = class MoveOperation extends Operation {
	/**
	* Position before the first {@link module:engine/model/item~ModelItem model item} to move.
	*/
	sourcePosition;
	/**
	* Offset size of moved range.
	*/
	howMany;
	/**
	* Position at which moved nodes will be inserted.
	*/
	targetPosition;
	/**
	* Creates a move operation.
	*
	* @param sourcePosition Position before the first {@link module:engine/model/item~ModelItem model item} to move.
	* @param howMany Offset size of moved range. Moved range will start from `sourcePosition` and end at
	* `sourcePosition` with offset shifted by `howMany`.
	* @param targetPosition Position at which moved nodes will be inserted.
	* @param baseVersion Document {@link module:engine/model/document~ModelDocument#version} on which operation
	* can be applied or `null` if the operation operates on detached (non-document) tree.
	*/
	constructor(sourcePosition, howMany, targetPosition, baseVersion) {
		super(baseVersion);
		this.sourcePosition = sourcePosition.clone();
		this.sourcePosition.stickiness = "toNext";
		this.howMany = howMany;
		this.targetPosition = targetPosition.clone();
		this.targetPosition.stickiness = "toNone";
	}
	/**
	* @inheritDoc
	*/
	get type() {
		if (this.targetPosition.root.rootName == this.sourcePosition.root.rootName) return "move";
		else if (this.targetPosition.root.rootName == "$graveyard") return "remove";
		else if (this.sourcePosition.root.rootName == "$graveyard") return "reinsert";
		return "move";
	}
	/**
	* @inheritDoc
	*/
	get affectedSelectable() {
		return [this.sourceRange, ModelRange._createFromPositionAndShift(this.targetPosition, 0)];
	}
	/**
	* Returns the range that is moved by this operation. This is equal to a range starting at {@link #sourcePosition} spanning over
	* {@link #howMany} nodes.
	*/
	get sourceRange() {
		return ModelRange._createFromPositionAndShift(this.sourcePosition, this.howMany);
	}
	/**
	* Creates and returns an operation that has the same parameters as this operation.
	*/
	clone() {
		return new MoveOperation(this.sourcePosition, this.howMany, this.targetPosition, this.baseVersion);
	}
	/**
	* Returns the start position of the moved range after it got moved. This may be different than
	* {@link module:engine/model/operation/moveoperation~MoveOperation#targetPosition} in some cases, i.e. when a range is moved
	* inside the same parent but {@link module:engine/model/operation/moveoperation~MoveOperation#targetPosition targetPosition}
	* is after {@link module:engine/model/operation/moveoperation~MoveOperation#sourcePosition sourcePosition}.
	*
	* ```
	*  vv              vv
	* abcdefg ===> adefbcg
	*      ^          ^
	*      targetPos  movedRangeStart
	*      offset 6   offset 4
	*```
	*/
	getMovedRangeStart() {
		return this.targetPosition._getTransformedByDeletion(this.sourcePosition, this.howMany);
	}
	/**
	* See {@link module:engine/model/operation/operation~Operation#getReversed `Operation#getReversed()`}.
	*/
	getReversed() {
		const newTargetPosition = this.sourcePosition._getTransformedByInsertion(this.targetPosition, this.howMany);
		return new MoveOperation(this.getMovedRangeStart(), this.howMany, newTargetPosition, this.baseVersion + 1);
	}
	/**
	* @inheritDoc
	* @internal
	*/
	_validate() {
		const sourceElement = this.sourcePosition.parent;
		const targetElement = this.targetPosition.parent;
		const sourceOffset = this.sourcePosition.offset;
		const targetOffset = this.targetPosition.offset;
		if (sourceOffset + this.howMany > sourceElement.maxOffset)
 /**
		* The nodes which should be moved do not exist.
		*
		* @error move-operation-nodes-do-not-exist
		*/
		throw new CKEditorError("move-operation-nodes-do-not-exist", this);
		else if (sourceElement === targetElement && sourceOffset < targetOffset && targetOffset < sourceOffset + this.howMany)
 /**
		* Trying to move a range of nodes into the middle of that range.
		*
		* @error move-operation-range-into-itself
		*/
		throw new CKEditorError("move-operation-range-into-itself", this);
		else if (this.sourcePosition.root == this.targetPosition.root) {
			if (compareArrays(this.sourcePosition.getParentPath(), this.targetPosition.getParentPath()) == "prefix") {
				const i = this.sourcePosition.path.length - 1;
				if (this.targetPosition.path[i] >= sourceOffset && this.targetPosition.path[i] < sourceOffset + this.howMany)
 /**
				* Trying to move a range of nodes into one of nodes from that range.
				*
				* @error move-operation-node-into-itself
				*/
				throw new CKEditorError("move-operation-node-into-itself", this);
			}
		}
	}
	/**
	* @inheritDoc
	* @internal
	*/
	_execute() {
		_move(this.sourceRange, this.targetPosition);
	}
	/**
	* @inheritDoc
	*/
	toJSON() {
		const json = super.toJSON();
		json.sourcePosition = this.sourcePosition.toJSON();
		json.targetPosition = this.targetPosition.toJSON();
		return json;
	}
	/**
	* @inheritDoc
	*/
	static get className() {
		return "MoveOperation";
	}
	/**
	* Creates `MoveOperation` object from deserialized object, i.e. from parsed JSON string.
	*
	* @param json Deserialized JSON object.
	* @param document Document on which this operation will be applied.
	*/
	static fromJSON(json, document) {
		const sourcePosition = ModelPosition.fromJSON(json.sourcePosition, document);
		const targetPosition = ModelPosition.fromJSON(json.targetPosition, document);
		return new this(sourcePosition, json.howMany, targetPosition, json.baseVersion);
	}
};

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* @module engine/model/operation/insertoperation
*/
/**
* Operation to insert one or more nodes at given position in the model.
*/
var InsertOperation = class InsertOperation extends Operation {
	/**
	* Position of insertion.
	*
	* @readonly
	*/
	position;
	/**
	* List of nodes to insert.
	*
	* @readonly
	*/
	nodes;
	/**
	* Flag deciding how the operation should be transformed. If set to `true`, nodes might get additional attributes
	* during operational transformation. This happens when the operation insertion position is inside of a range
	* where attributes have changed.
	*/
	shouldReceiveAttributes;
	/**
	* Creates an insert operation.
	*
	* @param position Position of insertion.
	* @param nodes The list of nodes to be inserted.
	* @param baseVersion Document {@link module:engine/model/document~ModelDocument#version} on which operation
	* can be applied or `null` if the operation operates on detached (non-document) tree.
	*/
	constructor(position, nodes, baseVersion) {
		super(baseVersion);
		this.position = position.clone();
		this.position.stickiness = "toNone";
		this.nodes = new ModelNodeList(_normalizeNodes(nodes));
		this.shouldReceiveAttributes = false;
	}
	/**
	* @inheritDoc
	*/
	get type() {
		return "insert";
	}
	/**
	* Total offset size of inserted nodes.
	*/
	get howMany() {
		return this.nodes.maxOffset;
	}
	/**
	* @inheritDoc
	*/
	get affectedSelectable() {
		return this.position.clone();
	}
	/**
	* Creates and returns an operation that has the same parameters as this operation.
	*/
	clone() {
		const nodes = new ModelNodeList([...this.nodes].map((node) => node._clone(true)));
		const insert = new InsertOperation(this.position, nodes, this.baseVersion);
		insert.shouldReceiveAttributes = this.shouldReceiveAttributes;
		return insert;
	}
	/**
	* See {@link module:engine/model/operation/operation~Operation#getReversed `Operation#getReversed()`}.
	*/
	getReversed() {
		const graveyard = this.position.root.document.graveyard;
		const gyPosition = new ModelPosition(graveyard, [0]);
		return new MoveOperation(this.position, this.nodes.maxOffset, gyPosition, this.baseVersion + 1);
	}
	/**
	* @inheritDoc
	* @internal
	*/
	_validate() {
		const targetElement = this.position.parent;
		if (!targetElement || targetElement.maxOffset < this.position.offset)
 /**
		* Insertion position is invalid.
		*
		* @error insert-operation-position-invalid
		*/
		throw new CKEditorError("insert-operation-position-invalid", this);
	}
	/**
	* @inheritDoc
	* @internal
	*/
	_execute() {
		const originalNodes = this.nodes;
		this.nodes = new ModelNodeList([...originalNodes].map((node) => node._clone(true)));
		_insert(this.position, originalNodes);
	}
	/**
	* @inheritDoc
	*/
	toJSON() {
		const json = super.toJSON();
		json.position = this.position.toJSON();
		json.nodes = this.nodes.toJSON();
		return json;
	}
	/**
	* @inheritDoc
	*/
	static get className() {
		return "InsertOperation";
	}
	/**
	* Creates `InsertOperation` object from deserialized object, i.e. from parsed JSON string.
	*
	* @param json Deserialized JSON object.
	* @param document Document on which this operation will be applied.
	*/
	static fromJSON(json, document) {
		const children = [];
		for (const child of json.nodes) if (child.name) children.push(ModelElement.fromJSON(child));
		else children.push(ModelText.fromJSON(child));
		const insert = new InsertOperation(ModelPosition.fromJSON(json.position, document), children, json.baseVersion);
		insert.shouldReceiveAttributes = json.shouldReceiveAttributes;
		return insert;
	}
};

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* @module engine/model/operation/splitoperation
*/
/**
* Operation to split {@link module:engine/model/element~ModelElement an element} at given
* {@link module:engine/model/operation/splitoperation~SplitOperation#splitPosition split position} into two elements,
* both containing a part of the element's original content.
*/
var SplitOperation = class SplitOperation extends Operation {
	/**
	* Position at which an element should be split.
	*/
	splitPosition;
	/**
	* Total offset size of elements that are in the split element after `position`.
	*/
	howMany;
	/**
	* Position at which the clone of split element (or element from graveyard) will be inserted.
	*/
	insertionPosition;
	/**
	* Position in the graveyard root before the element which should be used as a parent of the nodes after `position`.
	* If it is not set, a copy of the the `position` parent will be used.
	*
	* The default behavior is to clone the split element. Element from graveyard is used during undo.
	*/
	graveyardPosition;
	/**
	* Creates a split operation.
	*
	* @param splitPosition Position at which an element should be split.
	* @param howMany Total offset size of elements that are in the split element after `position`.
	* @param insertionPosition Position at which the clone of split element (or element from graveyard) will be inserted.
	* @param graveyardPosition Position in the graveyard root before the element which
	* should be used as a parent of the nodes after `position`. If it is not set, a copy of the the `position` parent will be used.
	* @param baseVersion Document {@link module:engine/model/document~ModelDocument#version} on which operation
	* can be applied or `null` if the operation operates on detached (non-document) tree.
	*/
	constructor(splitPosition, howMany, insertionPosition, graveyardPosition, baseVersion) {
		super(baseVersion);
		this.splitPosition = splitPosition.clone();
		this.splitPosition.stickiness = "toNext";
		this.howMany = howMany;
		this.insertionPosition = insertionPosition;
		this.graveyardPosition = graveyardPosition ? graveyardPosition.clone() : null;
		if (this.graveyardPosition) this.graveyardPosition.stickiness = "toNext";
	}
	/**
	* @inheritDoc
	*/
	get type() {
		return "split";
	}
	/**
	* Position inside the new clone of a split element.
	*
	* This is a position where nodes that are after the split position will be moved to.
	*/
	get moveTargetPosition() {
		const path = this.insertionPosition.path.slice();
		path.push(0);
		return new ModelPosition(this.insertionPosition.root, path);
	}
	/**
	* Artificial range that contains all the nodes from the split element that will be moved to the new element.
	* The range starts at {@link #splitPosition} and ends in the same parent, at `POSITIVE_INFINITY` offset.
	*/
	get movedRange() {
		const end = this.splitPosition.getShiftedBy(Number.POSITIVE_INFINITY);
		return new ModelRange(this.splitPosition, end);
	}
	/**
	* @inheritDoc
	*/
	get affectedSelectable() {
		const ranges = [ModelRange._createFromPositionAndShift(this.splitPosition, 0), ModelRange._createFromPositionAndShift(this.insertionPosition, 0)];
		if (this.graveyardPosition) ranges.push(ModelRange._createFromPositionAndShift(this.graveyardPosition, 0));
		return ranges;
	}
	/**
	* Creates and returns an operation that has the same parameters as this operation.
	*
	* @returns Clone of this operation.
	*/
	clone() {
		return new SplitOperation(this.splitPosition, this.howMany, this.insertionPosition, this.graveyardPosition, this.baseVersion);
	}
	/**
	* See {@link module:engine/model/operation/operation~Operation#getReversed `Operation#getReversed()`}.
	*/
	getReversed() {
		const graveyard = this.splitPosition.root.document.graveyard;
		const graveyardPosition = new ModelPosition(graveyard, [0]);
		return new MergeOperation(this.moveTargetPosition, this.howMany, this.splitPosition, graveyardPosition, this.baseVersion + 1);
	}
	/**
	* @inheritDoc
	* @internal
	*/
	_validate() {
		const element = this.splitPosition.parent;
		const offset = this.splitPosition.offset;
		if (!element || element.maxOffset < offset)
 /**
		* Split position is invalid.
		*
		* @error split-operation-position-invalid
		*/
		throw new CKEditorError("split-operation-position-invalid", this);
		else if (!element.parent)
 /**
		* Cannot split root element.
		*
		* @error split-operation-split-in-root
		*/
		throw new CKEditorError("split-operation-split-in-root", this);
		else if (this.howMany !== Number.NEGATIVE_INFINITY && this.howMany != element.maxOffset - this.splitPosition.offset)
 /**
		* Split operation specifies wrong number of nodes to move.
		*
		* @error split-operation-how-many-invalid
		*/
		throw new CKEditorError("split-operation-how-many-invalid", this);
		else if (this.graveyardPosition && !this.graveyardPosition.nodeAfter)
 /**
		* Graveyard position invalid.
		*
		* @error split-operation-graveyard-position-invalid
		*/
		throw new CKEditorError("split-operation-graveyard-position-invalid", this);
		this.howMany = this.splitPosition.parent.maxOffset - this.splitPosition.offset;
	}
	/**
	* @inheritDoc
	* @internal
	*/
	_execute() {
		const splitElement = this.splitPosition.parent;
		if (this.graveyardPosition) _move(ModelRange._createFromPositionAndShift(this.graveyardPosition, 1), this.insertionPosition);
		else {
			const newElement = splitElement._clone();
			_insert(this.insertionPosition, newElement);
		}
		_move(new ModelRange(ModelPosition._createAt(splitElement, this.splitPosition.offset), ModelPosition._createAt(splitElement, splitElement.maxOffset)), this.moveTargetPosition);
	}
	/**
	* @inheritDoc
	*/
	toJSON() {
		const json = super.toJSON();
		json.splitPosition = this.splitPosition.toJSON();
		json.insertionPosition = this.insertionPosition.toJSON();
		if (this.graveyardPosition) json.graveyardPosition = this.graveyardPosition.toJSON();
		return json;
	}
	/**
	* @inheritDoc
	*/
	static get className() {
		return "SplitOperation";
	}
	/**
	* Helper function that returns a default insertion position basing on given `splitPosition`. The default insertion
	* position is after the split element.
	*/
	static getInsertionPosition(splitPosition) {
		const path = splitPosition.path.slice(0, -1);
		path[path.length - 1]++;
		return new ModelPosition(splitPosition.root, path, "toPrevious");
	}
	/**
	* Creates `SplitOperation` object from deserialized object, i.e. from parsed JSON string.
	*
	* @param json Deserialized JSON object.
	* @param document Document on which this operation will be applied.
	*/
	static fromJSON(json, document) {
		const splitPosition = ModelPosition.fromJSON(json.splitPosition, document);
		const insertionPosition = ModelPosition.fromJSON(json.insertionPosition, document);
		const graveyardPosition = json.graveyardPosition ? ModelPosition.fromJSON(json.graveyardPosition, document) : null;
		return new this(splitPosition, json.howMany, insertionPosition, graveyardPosition, json.baseVersion);
	}
};

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* @module engine/model/operation/mergeoperation
*/
/**
* Operation to merge two {@link module:engine/model/element~ModelElement elements}.
*
* The merged element is the parent of {@link ~MergeOperation#sourcePosition} and it is merged into the parent of
* {@link ~MergeOperation#targetPosition}. All nodes from the merged element are moved to {@link ~MergeOperation#targetPosition}.
*
* The merged element is moved to the graveyard at {@link ~MergeOperation#graveyardPosition}.
*/
var MergeOperation = class MergeOperation extends Operation {
	/**
	* Position inside the merged element. All nodes from that element after that position will be moved to {@link #targetPosition}.
	*/
	sourcePosition;
	/**
	* Summary offset size of nodes which will be moved from the merged element to the new parent.
	*/
	howMany;
	/**
	* Position which the nodes from the merged elements will be moved to.
	*/
	targetPosition;
	/**
	* Position in graveyard to which the merged element will be moved.
	*/
	graveyardPosition;
	/**
	* Creates a merge operation.
	*
	* @param sourcePosition Position inside the merged element. All nodes from that
	* element after that position will be moved to {@link #targetPosition}.
	* @param howMany Summary offset size of nodes which will be moved from the merged element to the new parent.
	* @param targetPosition Position which the nodes from the merged elements will be moved to.
	* @param graveyardPosition Position in graveyard to which the merged element will be moved.
	* @param baseVersion Document {@link module:engine/model/document~ModelDocument#version} on which operation
	* can be applied or `null` if the operation operates on detached (non-document) tree.
	*/
	constructor(sourcePosition, howMany, targetPosition, graveyardPosition, baseVersion) {
		super(baseVersion);
		this.sourcePosition = sourcePosition.clone();
		this.sourcePosition.stickiness = "toPrevious";
		this.howMany = howMany;
		this.targetPosition = targetPosition.clone();
		this.targetPosition.stickiness = "toNext";
		this.graveyardPosition = graveyardPosition.clone();
	}
	/**
	* @inheritDoc
	*/
	get type() {
		return "merge";
	}
	/**
	* Position before the merged element (which will be deleted).
	*/
	get deletionPosition() {
		return new ModelPosition(this.sourcePosition.root, this.sourcePosition.path.slice(0, -1));
	}
	/**
	* Artificial range that contains all the nodes from the merged element that will be moved to {@link ~MergeOperation#sourcePosition}.
	* The range starts at {@link ~MergeOperation#sourcePosition} and ends in the same parent, at `POSITIVE_INFINITY` offset.
	*/
	get movedRange() {
		const end = this.sourcePosition.getShiftedBy(Number.POSITIVE_INFINITY);
		return new ModelRange(this.sourcePosition, end);
	}
	/**
	* @inheritDoc
	*/
	get affectedSelectable() {
		const mergedElement = this.sourcePosition.parent;
		return [
			ModelRange._createOn(mergedElement),
			ModelRange._createFromPositionAndShift(this.targetPosition, 0),
			ModelRange._createFromPositionAndShift(this.graveyardPosition, 0)
		];
	}
	/**
	* Creates and returns an operation that has the same parameters as this operation.
	*/
	clone() {
		return new MergeOperation(this.sourcePosition, this.howMany, this.targetPosition, this.graveyardPosition, this.baseVersion);
	}
	/**
	* See {@link module:engine/model/operation/operation~Operation#getReversed `Operation#getReversed()`}.
	*/
	getReversed() {
		const targetPosition = this.targetPosition._getTransformedByMergeOperation(this);
		const path = this.sourcePosition.path.slice(0, -1);
		const insertionPosition = new ModelPosition(this.sourcePosition.root, path)._getTransformedByMergeOperation(this);
		return new SplitOperation(targetPosition, this.howMany, insertionPosition, this.graveyardPosition, this.baseVersion + 1);
	}
	/**
	* @inheritDoc
	* @internal
	*/
	_validate() {
		const sourceElement = this.sourcePosition.parent;
		const targetElement = this.targetPosition.parent;
		if (!sourceElement.parent)
 /**
		* Merge source position is invalid. The element to be merged must have a parent node.
		*
		* @error merge-operation-source-position-invalid
		*/
		throw new CKEditorError("merge-operation-source-position-invalid", this);
		else if (!targetElement.parent)
 /**
		* Merge target position is invalid. The element to be merged must have a parent node.
		*
		* @error merge-operation-target-position-invalid
		*/
		throw new CKEditorError("merge-operation-target-position-invalid", this);
		else if (this.howMany !== Number.NEGATIVE_INFINITY && this.howMany != sourceElement.maxOffset)
 /**
		* Merge operation specifies wrong number of nodes to move.
		*
		* @error merge-operation-how-many-invalid
		*/
		throw new CKEditorError("merge-operation-how-many-invalid", this);
		this.howMany = this.sourcePosition.parent.maxOffset;
	}
	/**
	* @inheritDoc
	* @internal
	*/
	_execute() {
		const mergedElement = this.sourcePosition.parent;
		_move(ModelRange._createIn(mergedElement), this.targetPosition);
		_move(ModelRange._createOn(mergedElement), this.graveyardPosition);
	}
	/**
	* @inheritDoc
	*/
	toJSON() {
		const json = super.toJSON();
		json.sourcePosition = json.sourcePosition.toJSON();
		json.targetPosition = json.targetPosition.toJSON();
		json.graveyardPosition = json.graveyardPosition.toJSON();
		return json;
	}
	/**
	* @inheritDoc
	*/
	static get className() {
		return "MergeOperation";
	}
	/**
	* Creates `MergeOperation` object from deserialized object, i.e. from parsed JSON string.
	*
	* @param json Deserialized JSON object.
	* @param document Document on which this operation will be applied.
	*/
	static fromJSON(json, document) {
		const sourcePosition = ModelPosition.fromJSON(json.sourcePosition, document);
		const targetPosition = ModelPosition.fromJSON(json.targetPosition, document);
		const graveyardPosition = ModelPosition.fromJSON(json.graveyardPosition, document);
		return new this(sourcePosition, json.howMany, targetPosition, graveyardPosition, json.baseVersion);
	}
};

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* @module engine/model/operation/markeroperation
*/
var MarkerOperation = class MarkerOperation extends Operation {
	/**
	* Marker name.
	*
	* @readonly
	*/
	name;
	/**
	* Marker range before the change.
	*
	* @readonly
	*/
	oldRange;
	/**
	* Marker range after the change.
	*
	* @readonly
	*/
	newRange;
	/**
	* Specifies whether the marker operation affects the data produced by the data pipeline
	* (is persisted in the editor's data).
	*
	* @readonly
	*/
	affectsData;
	/**
	* Marker collection on which change should be executed.
	*/
	_markers;
	/**
	* @param name Marker name.
	* @param oldRange Marker range before the change.
	* @param newRange Marker range after the change.
	* @param markers Marker collection on which change should be executed.
	* @param affectsData Specifies whether the marker operation affects the data produced by the data pipeline
	* (is persisted in the editor's data).
	* @param baseVersion Document {@link module:engine/model/document~ModelDocument#version} on which operation
	* can be applied or `null` if the operation operates on detached (non-document) tree.
	*/
	constructor(name, oldRange, newRange, markers, affectsData, baseVersion) {
		super(baseVersion);
		this.name = name;
		this.oldRange = oldRange ? oldRange.clone() : null;
		this.newRange = newRange ? newRange.clone() : null;
		this.affectsData = affectsData;
		this._markers = markers;
	}
	/**
	* @inheritDoc
	*/
	get type() {
		return "marker";
	}
	/**
	* @inheritDoc
	*/
	get affectedSelectable() {
		const ranges = [];
		if (this.oldRange) ranges.push(this.oldRange.clone());
		if (this.newRange) if (this.oldRange) ranges.push(...this.newRange.getDifference(this.oldRange));
		else ranges.push(this.newRange.clone());
		return ranges;
	}
	/**
	* Creates and returns an operation that has the same parameters as this operation.
	*/
	clone() {
		return new MarkerOperation(this.name, this.oldRange, this.newRange, this._markers, this.affectsData, this.baseVersion);
	}
	/**
	* See {@link module:engine/model/operation/operation~Operation#getReversed `Operation#getReversed()`}.
	*/
	getReversed() {
		return new MarkerOperation(this.name, this.newRange, this.oldRange, this._markers, this.affectsData, this.baseVersion + 1);
	}
	/**
	* @inheritDoc
	* @internal
	*/
	_execute() {
		if (this.newRange) this._markers._set(this.name, this.newRange, true, this.affectsData);
		else this._markers._remove(this.name);
	}
	/**
	* @inheritDoc
	* @internal
	*/
	toJSON() {
		const json = super.toJSON();
		if (this.oldRange) json.oldRange = this.oldRange.toJSON();
		if (this.newRange) json.newRange = this.newRange.toJSON();
		delete json._markers;
		return json;
	}
	/**
	* @inheritDoc
	*/
	static get className() {
		return "MarkerOperation";
	}
	/**
	* Creates `MarkerOperation` object from deserialized object, i.e. from parsed JSON string.
	*
	* @param json Deserialized JSON object.
	* @param document Document on which this operation will be applied.
	*/
	static fromJSON(json, document) {
		return new MarkerOperation(json.name, json.oldRange ? ModelRange.fromJSON(json.oldRange, document) : null, json.newRange ? ModelRange.fromJSON(json.newRange, document) : null, document.model.markers, json.affectsData, json.baseVersion);
	}
};

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* @module engine/model/operation/attributeoperation
*/
/**
* Operation to change nodes' attribute.
*
* Using this class you can add, remove or change value of the attribute.
*/
var AttributeOperation = class AttributeOperation extends Operation {
	/**
	* Range on which operation should be applied.
	*
	* @readonly
	*/
	range;
	/**
	* Key of an attribute to change or remove.
	*
	* @readonly
	*/
	key;
	/**
	* Old value of the attribute with given key or `null`, if attribute was not set before.
	*
	* @readonly
	*/
	oldValue;
	/**
	* New value of the attribute with given key or `null`, if operation should remove attribute.
	*
	* @readonly
	*/
	newValue;
	/**
	* Creates an operation that changes, removes or adds attributes.
	*
	* If only `newValue` is set, attribute will be added on a node. Note that all nodes in operation's range must not
	* have an attribute with the same key as the added attribute.
	*
	* If only `oldValue` is set, then attribute with given key will be removed. Note that all nodes in operation's range
	* must have an attribute with that key added.
	*
	* If both `newValue` and `oldValue` are set, then the operation will change the attribute value. Note that all nodes in
	* operation's ranges must already have an attribute with given key and `oldValue` as value
	*
	* @param range Range on which the operation should be applied. Must be a flat range.
	* @param key Key of an attribute to change or remove.
	* @param oldValue Old value of the attribute with given key or `null`, if attribute was not set before.
	* @param newValue New value of the attribute with given key or `null`, if operation should remove attribute.
	* @param baseVersion Document {@link module:engine/model/document~ModelDocument#version} on which operation
	* can be applied or `null` if the operation operates on detached (non-document) tree.
	*/
	constructor(range, key, oldValue, newValue, baseVersion) {
		super(baseVersion);
		this.range = range.clone();
		this.key = key;
		this.oldValue = oldValue === void 0 ? null : oldValue;
		this.newValue = newValue === void 0 ? null : newValue;
	}
	/**
	* @inheritDoc
	*/
	get type() {
		if (this.oldValue === null) return "addAttribute";
		else if (this.newValue === null) return "removeAttribute";
		else return "changeAttribute";
	}
	/**
	* @inheritDoc
	*/
	get affectedSelectable() {
		return this.range.clone();
	}
	/**
	* Creates and returns an operation that has the same parameters as this operation.
	*/
	clone() {
		return new AttributeOperation(this.range, this.key, this.oldValue, this.newValue, this.baseVersion);
	}
	/**
	* See {@link module:engine/model/operation/operation~Operation#getReversed `Operation#getReversed()`}.
	*/
	getReversed() {
		return new AttributeOperation(this.range, this.key, this.newValue, this.oldValue, this.baseVersion + 1);
	}
	/**
	* @inheritDoc
	*/
	toJSON() {
		const json = super.toJSON();
		json.range = this.range.toJSON();
		return json;
	}
	/**
	* @inheritDoc
	* @internal
	*/
	_validate() {
		if (!this.range.isFlat)
 /**
		* The range to change is not flat.
		*
		* @error attribute-operation-range-not-flat
		*/
		throw new CKEditorError("attribute-operation-range-not-flat", this);
		for (const item of this.range.getItems({ shallow: true })) {
			if (this.oldValue !== null && !isEqual(item.getAttribute(this.key), this.oldValue))
 /**
			* Changed node has different attribute value than operation's old attribute value.
			*
			* @error attribute-operation-wrong-old-value
			* @param {module:engine/model/item~ModelItem} root The item element.
			* @param {string} key The key of the attribute.
			* @param {never} value The value.
			*/
			throw new CKEditorError("attribute-operation-wrong-old-value", this, {
				item,
				key: this.key,
				value: this.oldValue
			});
			if (this.oldValue === null && this.newValue !== null && item.hasAttribute(this.key))
 /**
			* The attribute with given key already exists for the given node.
			*
			* @error attribute-operation-attribute-exists
			* @param {module:engine/model/item~ModelItem} root The item element.
			* @param {string} key The key of the attribute.
			*/
			throw new CKEditorError("attribute-operation-attribute-exists", this, {
				node: item,
				key: this.key
			});
		}
	}
	/**
	* @inheritDoc
	* @internal
	*/
	_execute() {
		if (!isEqual(this.oldValue, this.newValue)) _setAttribute(this.range, this.key, this.newValue);
	}
	/**
	* @inheritDoc
	*/
	static get className() {
		return "AttributeOperation";
	}
	/**
	* Creates `AttributeOperation` object from deserialized object, i.e. from parsed JSON string.
	*
	* @param json Deserialized JSON object.
	* @param document Document on which this operation will be applied.
	*/
	static fromJSON(json, document) {
		return new AttributeOperation(ModelRange.fromJSON(json.range, document), json.key, json.oldValue, json.newValue, json.baseVersion);
	}
};

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* @module engine/model/operation/nooperation
*/
/**
* Operation which is doing nothing ("empty operation", "do-nothing operation", "noop"). This is an operation,
* which when executed does not change the tree model. It still has some parameters defined for transformation purposes.
*
* In most cases this operation is a result of transforming operations. When transformation returns
* {@link module:engine/model/operation/nooperation~NoOperation} it means that changes done by the transformed operation
* have already been applied.
*/
var NoOperation = class NoOperation extends Operation {
	get type() {
		return "noop";
	}
	/**
	* @inheritDoc
	*/
	get affectedSelectable() {
		return null;
	}
	/**
	* Creates and returns an operation that has the same parameters as this operation.
	*/
	clone() {
		return new NoOperation(this.baseVersion);
	}
	/**
	* See {@link module:engine/model/operation/operation~Operation#getReversed `Operation#getReversed()`}.
	*/
	getReversed() {
		return new NoOperation(this.baseVersion + 1);
	}
	/** @internal */
	_execute() {}
	/**
	* @inheritDoc
	*/
	static get className() {
		return "NoOperation";
	}
};

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* @module engine/model/operation/renameoperation
*/
/**
* Operation to change element's name.
*
* Using this class you can change element's name.
*/
var RenameOperation = class RenameOperation extends Operation {
	/**
	* Position before an element to change.
	*/
	position;
	/**
	* Current name of the element.
	*/
	oldName;
	/**
	* New name for the element.
	*/
	newName;
	/**
	* Creates an operation that changes element's name.
	*
	* @param position Position before an element to change.
	* @param oldName Current name of the element.
	* @param newName New name for the element.
	* @param baseVersion Document {@link module:engine/model/document~ModelDocument#version} on which operation
	* can be applied or `null` if the operation operates on detached (non-document) tree.
	*/
	constructor(position, oldName, newName, baseVersion) {
		super(baseVersion);
		this.position = position;
		this.position.stickiness = "toNext";
		this.oldName = oldName;
		this.newName = newName;
	}
	/**
	* @inheritDoc
	*/
	get type() {
		return "rename";
	}
	/**
	* @inheritDoc
	*/
	get affectedSelectable() {
		return this.position.nodeAfter;
	}
	/**
	* Creates and returns an operation that has the same parameters as this operation.
	*
	* @returns Clone of this operation.
	*/
	clone() {
		return new RenameOperation(this.position.clone(), this.oldName, this.newName, this.baseVersion);
	}
	/**
	* See {@link module:engine/model/operation/operation~Operation#getReversed `Operation#getReversed()`}.
	*/
	getReversed() {
		return new RenameOperation(this.position.clone(), this.newName, this.oldName, this.baseVersion + 1);
	}
	/**
	* @inheritDoc
	* @internal
	*/
	_validate() {
		const element = this.position.nodeAfter;
		if (!(element instanceof ModelElement))
 /**
		* Given position is invalid or node after it is not instance of Element.
		*
		* @error rename-operation-wrong-position
		*/
		throw new CKEditorError("rename-operation-wrong-position", this);
		else if (this.oldName !== "" && element.name !== this.oldName)
 /**
		* Element to change has different name than operation's old name.
		*
		* @error rename-operation-wrong-name
		*/
		throw new CKEditorError("rename-operation-wrong-name", this);
		this.oldName = element.name;
	}
	/**
	* @inheritDoc
	* @internal
	*/
	_execute() {
		const element = this.position.nodeAfter;
		element.name = this.newName;
	}
	/**
	* @inheritDoc
	*/
	toJSON() {
		const json = super.toJSON();
		json.position = this.position.toJSON();
		return json;
	}
	/**
	* @inheritDoc
	*/
	static get className() {
		return "RenameOperation";
	}
	/**
	* Creates `RenameOperation` object from deserialized object, i.e. from parsed JSON string.
	*
	* @param json Deserialized JSON object.
	* @param document Document on which this operation will be applied.
	*/
	static fromJSON(json, document) {
		return new RenameOperation(ModelPosition.fromJSON(json.position, document), json.oldName, json.newName, json.baseVersion);
	}
};

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* @module engine/model/operation/rootattributeoperation
*/
/**
* Operation to change root element's attribute. Using this class you can add, remove or change value of the attribute.
*
* This operation is needed, because root elements can't be changed through
* {@link module:engine/model/operation/attributeoperation~AttributeOperation}.
* It is because {@link module:engine/model/operation/attributeoperation~AttributeOperation}
* requires a range to change and root element can't
* be a part of range because every {@link module:engine/model/position~ModelPosition} has to be inside a root.
* {@link module:engine/model/position~ModelPosition} can't be created before a root element.
*/
var RootAttributeOperation = class RootAttributeOperation extends Operation {
	/**
	* Root element to change.
	*/
	root;
	/**
	* Key of an attribute to change or remove.
	*/
	key;
	/**
	* Old value of the attribute with given key or `null`, if attribute was not set before.
	*
	* @readonly
	*/
	oldValue;
	/**
	* New value of the attribute with given key or `null`, if operation should remove attribute.
	*
	* @readonly
	*/
	newValue;
	/**
	* Creates an operation that changes, removes or adds attributes on root element.
	*
	* @see module:engine/model/operation/attributeoperation~AttributeOperation
	* @param root Root element to change.
	* @param key Key of an attribute to change or remove.
	* @param oldValue Old value of the attribute with given key or `null`, if attribute was not set before.
	* @param newValue New value of the attribute with given key or `null`, if operation should remove attribute.
	* @param baseVersion Document {@link module:engine/model/document~ModelDocument#version} on which operation
	* can be applied or `null` if the operation operates on detached (non-document) tree.
	*/
	constructor(root, key, oldValue, newValue, baseVersion) {
		super(baseVersion);
		this.root = root;
		this.key = key;
		this.oldValue = oldValue === void 0 ? null : oldValue;
		this.newValue = newValue === void 0 ? null : newValue;
	}
	/**
	* @inheritDoc
	*/
	get type() {
		if (this.oldValue === null) return "addRootAttribute";
		else if (this.newValue === null) return "removeRootAttribute";
		else return "changeRootAttribute";
	}
	/**
	* @inheritDoc
	*/
	get affectedSelectable() {
		return this.root;
	}
	/**
	* Creates and returns an operation that has the same parameters as this operation.
	*
	* @returns Clone of this operation.
	*/
	clone() {
		return new RootAttributeOperation(this.root, this.key, this.oldValue, this.newValue, this.baseVersion);
	}
	/**
	* See {@link module:engine/model/operation/operation~Operation#getReversed `Operation#getReversed()`}.
	*/
	getReversed() {
		return new RootAttributeOperation(this.root, this.key, this.newValue, this.oldValue, this.baseVersion + 1);
	}
	/**
	* @inheritDoc
	* @internal
	*/
	_validate() {
		if (this.root != this.root.root || this.root.is("documentFragment"))
 /**
		* The element to change is not a root element.
		*
		* @error rootattribute-operation-not-a-root
		* @param {module:engine/model/rootelement~ModelRootElement} root The root element.
		* @param {string} key The key of the attribute.
		*/
		throw new CKEditorError("rootattribute-operation-not-a-root", this, {
			root: this.root,
			key: this.key
		});
		if (this.oldValue !== null && !isEqual(this.root.getAttribute(this.key), this.oldValue))
 /**
		* The attribute which should be removed does not exist for the given node.
		*
		* @error rootattribute-operation-wrong-old-value
		* @param {module:engine/model/rootelement~ModelRootElement} root The root element.
		* @param {string} key The key of the attribute.
		*/
		throw new CKEditorError("rootattribute-operation-wrong-old-value", this, {
			root: this.root,
			key: this.key
		});
		if (this.oldValue === null && this.newValue !== null && this.root.hasAttribute(this.key))
 /**
		* The attribute with given key already exists for the given node.
		*
		* @error rootattribute-operation-attribute-exists
		* @param {module:engine/model/rootelement~ModelRootElement} root The root element.
		* @param {string} key The key of the attribute.
		*/
		throw new CKEditorError("rootattribute-operation-attribute-exists", this, {
			root: this.root,
			key: this.key
		});
	}
	/**
	* @inheritDoc
	* @internal
	*/
	_execute() {
		if (this.newValue !== null) this.root._setAttribute(this.key, this.newValue);
		else this.root._removeAttribute(this.key);
	}
	/**
	* @inheritDoc
	*/
	toJSON() {
		const json = super.toJSON();
		json.root = this.root.toJSON();
		return json;
	}
	/**
	* @inheritDoc
	*/
	static get className() {
		return "RootAttributeOperation";
	}
	/**
	* Creates `RootAttributeOperation` object from deserialized object, i.e. from parsed JSON string.
	*
	* @param json Deserialized JSON object.
	* @param document Document on which this operation will be applied.
	*/
	static fromJSON(json, document) {
		if (!document.getRoot(json.root))
 /**
		* Cannot create RootAttributeOperation for document. Root with the specified name does not exist.
		*
		* @error rootattribute-operation-fromjson-no-root
		* @param {string} rootName The root name.
		*/
		throw new CKEditorError("rootattribute-operation-fromjson-no-root", this, { rootName: json.root });
		return new RootAttributeOperation(document.getRoot(json.root), json.key, json.oldValue, json.newValue, json.baseVersion);
	}
};

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* @module engine/model/operation/rootoperation
*/
/**
* Operation that creates (or attaches) or detaches a root element.
*/
var RootOperation = class RootOperation extends Operation {
	/**
	* Root name to create or detach.
	*/
	rootName;
	/**
	* Root element name.
	*/
	elementName;
	/**
	* Specifies whether the operation adds (`true`) or detaches the root (`false`).
	*/
	isAdd;
	/**
	* Document which owns the root.
	*/
	_document;
	/**
	* Creates an operation that creates or removes a root element.
	*
	* @param rootName Root name to create or detach.
	* @param elementName Root element name.
	* @param isAdd Specifies whether the operation adds (`true`) or detaches the root (`false`).
	* @param document Document which owns the root.
	* @param baseVersion Document {@link module:engine/model/document~ModelDocument#version} on which operation can be applied.
	*/
	constructor(rootName, elementName, isAdd, document, baseVersion) {
		super(baseVersion);
		this.rootName = rootName;
		this.elementName = elementName;
		this.isAdd = isAdd;
		this._document = document;
		if (!this._document.getRoot(this.rootName)) {
			const root = this._document.createRoot(this.elementName, this.rootName);
			root._isAttached = false;
		}
	}
	/**
	* @inheritDoc
	*/
	get type() {
		return this.isAdd ? "addRoot" : "detachRoot";
	}
	/**
	* @inheritDoc
	*/
	get affectedSelectable() {
		return this._document.getRoot(this.rootName);
	}
	/**
	* @inheritDoc
	*/
	clone() {
		return new RootOperation(this.rootName, this.elementName, this.isAdd, this._document, this.baseVersion);
	}
	/**
	* @inheritDoc
	*/
	getReversed() {
		return new RootOperation(this.rootName, this.elementName, !this.isAdd, this._document, this.baseVersion + 1);
	}
	/**
	* @inheritDoc
	*/
	_execute() {
		this._document.getRoot(this.rootName)._isAttached = this.isAdd;
	}
	/**
	* @inheritDoc
	*/
	toJSON() {
		const json = super.toJSON();
		delete json._document;
		return json;
	}
	/**
	* @inheritDoc
	*/
	static get className() {
		return "RootOperation";
	}
	/**
	* Creates `RootOperation` object from deserialized object, i.e. from parsed JSON string.
	*
	* @param json Deserialized JSON object.
	* @param document Document on which this operation will be applied.
	*/
	static fromJSON(json, document) {
		return new RootOperation(json.rootName, json.elementName, json.isAdd, document, json.baseVersion);
	}
};

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* @module engine/model/operation/operationfactory
*/
const operations = {};
operations[AttributeOperation.className] = AttributeOperation;
operations[InsertOperation.className] = InsertOperation;
operations[MarkerOperation.className] = MarkerOperation;
operations[MoveOperation.className] = MoveOperation;
operations[NoOperation.className] = NoOperation;
operations[Operation.className] = Operation;
operations[RenameOperation.className] = RenameOperation;
operations[RootAttributeOperation.className] = RootAttributeOperation;
operations[RootOperation.className] = RootOperation;
operations[SplitOperation.className] = SplitOperation;
operations[MergeOperation.className] = MergeOperation;
/**
* A factory class for creating operations.
*/
var OperationFactory = class {
	/**
	* Creates an operation instance from a JSON object (parsed JSON string).
	*
	* @param json Deserialized JSON object.
	* @param document Document on which this operation will be applied.
	*/
	static fromJSON(json, document) {
		return operations[json.__className].fromJSON(json, document);
	}
};

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* @module engine/model/operation/transform
*/
const transformations = /* @__PURE__ */ new Map();
/**
* Sets a transformation function to be be used to transform instances of class `OperationA` by instances of class `OperationB`.
*
* The `transformationFunction` is passed three parameters:
*
* * `a` - operation to be transformed, an instance of `OperationA`,
* * `b` - operation to be transformed by, an instance of `OperationB`,
* * {@link module:engine/model/operation/transform~TransformationContext `context`} - object with additional information about
* transformation context.
*
* The `transformationFunction` should return transformation result, which is an array with one or multiple
* {@link module:engine/model/operation/operation~Operation operation} instances.
*
* @param transformationFunction Function to use for transforming.
*/
function setTransformation(OperationA, OperationB, transformationFunction) {
	let aGroup = transformations.get(OperationA);
	if (!aGroup) {
		aGroup = /* @__PURE__ */ new Map();
		transformations.set(OperationA, aGroup);
	}
	aGroup.set(OperationB, transformationFunction);
}
/**
* Returns a previously set transformation function for transforming an instance of `OperationA` by an instance of `OperationB`.
*
* If no transformation was set for given pair of operations, {@link module:engine/model/operation/transform~noUpdateTransformation}
* is returned. This means that if no transformation was set, the `OperationA` instance will not change when transformed
* by the `OperationB` instance.
*
* @returns Function set to transform an instance of `OperationA` by an instance of `OperationB`.
*/
function getTransformation(OperationA, OperationB) {
	const aGroup = transformations.get(OperationA);
	if (aGroup && aGroup.has(OperationB)) return aGroup.get(OperationB);
	return noUpdateTransformation;
}
/**
* A transformation function that only clones operation to transform, without changing it.
*/
function noUpdateTransformation(a) {
	return [a];
}
/**
* Transforms operation `a` by operation `b`.
*
* @param a Operation to be transformed.
* @param b Operation to transform by.
* @param context Transformation context for this transformation.
* @returns Transformation result.
* @internal
*/
function transform(a, b, context = {}) {
	const transformationFunction = getTransformation(a.constructor, b.constructor);
	try {
		a = a.clone();
		return transformationFunction(a, b, context);
	} catch (e) {
		throw e;
	}
}
/**
* Performs a transformation of two sets of operations - `operationsA` and `operationsB`. The transformation is two-way -
* both transformed `operationsA` and transformed `operationsB` are returned.
*
* Note, that the first operation in each set should base on the same document state (
* {@link module:engine/model/document~ModelDocument#version document version}).
*
* It is assumed that `operationsA` are "more important" during conflict resolution between two operations.
*
* New copies of both passed arrays and operations inside them are returned. Passed arguments are not altered.
*
* Base versions of the transformed operations sets are updated accordingly. For example, assume that base versions are `4`
* and there are `3` operations in `operationsA` and `5` operations in `operationsB`. Then:
*
* * transformed `operationsA` will start from base version `9` (`4` base version + `5` operations B),
* * transformed `operationsB` will start from base version `7` (`4` base version + `3` operations A).
*
* If no operation was broken into two during transformation, then both sets will end up with an operation that bases on version `11`:
*
* * transformed `operationsA` start from `9` and there are `3` of them, so the last will have `baseVersion` equal to `11`,
* * transformed `operationsB` start from `7` and there are `5` of them, so the last will have `baseVersion` equal to `11`.
*
* @param operationsA
* @param operationsB
* @param options Additional transformation options.
* @param options.document Document which the operations change.
* @param options.useRelations Whether during transformation relations should be used (used during undo for better conflict resolution).
* @param options.padWithNoOps Whether additional {@link module:engine/model/operation/nooperation~NoOperation}s
* should be added to the transformation results to force the same last base version for both transformed sets (in case
* if some operations got broken into multiple operations during transformation).
* @param options.forceWeakRemove If set to `false`, remove operation will be always stronger than move operation,
* so the removed nodes won't end up back in the document root. When set to `true`, context data will be used.
* @returns Transformation result.
*/
function transformOperationSets(operationsA, operationsB, options) {
	operationsA = operationsA.slice();
	operationsB = operationsB.slice();
	const contextFactory = new ContextFactory(options.document, options.useRelations, options.forceWeakRemove);
	contextFactory.setOriginalOperations(operationsA);
	contextFactory.setOriginalOperations(operationsB);
	const originalOperations = contextFactory.originalOperations;
	if (operationsA.length == 0 || operationsB.length == 0) return {
		operationsA,
		operationsB,
		originalOperations
	};
	const nextTransformIndex = /* @__PURE__ */ new WeakMap();
	for (const op of operationsA) nextTransformIndex.set(op, 0);
	const data = {
		nextBaseVersionA: operationsA[operationsA.length - 1].baseVersion + 1,
		nextBaseVersionB: operationsB[operationsB.length - 1].baseVersion + 1,
		originalOperationsACount: operationsA.length,
		originalOperationsBCount: operationsB.length
	};
	let i = 0;
	while (i < operationsA.length) {
		const opA = operationsA[i];
		const indexB = nextTransformIndex.get(opA);
		if (indexB == operationsB.length) {
			i++;
			continue;
		}
		const opB = operationsB[indexB];
		const newOpsA = transform(opA, opB, contextFactory.getContext(opA, opB, true));
		const newOpsB = transform(opB, opA, contextFactory.getContext(opB, opA, false));
		contextFactory.updateRelation(opA, opB);
		contextFactory.setOriginalOperations(newOpsA, opA);
		contextFactory.setOriginalOperations(newOpsB, opB);
		for (const newOpA of newOpsA) nextTransformIndex.set(newOpA, indexB + newOpsB.length);
		operationsA.splice(i, 1, ...newOpsA);
		operationsB.splice(indexB, 1, ...newOpsB);
	}
	handlePartialMarkerOperations(operationsA);
	handlePartialMarkerOperations(operationsB);
	if (options.padWithNoOps) {
		const brokenOperationsACount = operationsA.length - data.originalOperationsACount;
		const brokenOperationsBCount = operationsB.length - data.originalOperationsBCount;
		padWithNoOps(operationsA, brokenOperationsBCount - brokenOperationsACount);
		padWithNoOps(operationsB, brokenOperationsACount - brokenOperationsBCount);
	}
	updateBaseVersions(operationsA, data.nextBaseVersionB);
	updateBaseVersions(operationsB, data.nextBaseVersionA);
	return {
		operationsA,
		operationsB,
		originalOperations
	};
}
/**
* Gathers additional data about operations processed during transformation. Can be used to obtain contextual information
* about two operations that are about to be transformed. This contextual information can be used for better conflict resolution.
*/
var ContextFactory = class {
	originalOperations;
	_history;
	_useRelations;
	_forceWeakRemove;
	_relations;
	/**
	* Creates `ContextFactory` instance.
	*
	* @param document Document which the operations change.
	* @param useRelations Whether during transformation relations should be used (used during undo for
	* better conflict resolution).
	* @param forceWeakRemove If set to `false`, remove operation will be always stronger than move operation,
	* so the removed nodes won't end up back in the document root. When set to `true`, context data will be used.
	*/
	constructor(document, useRelations, forceWeakRemove = false) {
		this.originalOperations = /* @__PURE__ */ new Map();
		this._history = document.history;
		this._useRelations = useRelations;
		this._forceWeakRemove = !!forceWeakRemove;
		this._relations = /* @__PURE__ */ new Map();
	}
	/**
	* Sets "original operation" for given operations.
	*
	* During transformation process, operations are cloned, then changed, then processed again, sometimes broken into two
	* or multiple operations. When gathering additional data it is important that all operations can be somehow linked
	* so a cloned and transformed "version" still kept track of the data assigned earlier to it.
	*
	* The original operation object will be used as such an universal linking id. Throughout the transformation process
	* all cloned operations will refer to "the original operation" when storing and reading additional data.
	*
	* If `takeFrom` is not set, each operation from `operations` array will be assigned itself as "the original operation".
	* This should be used as an initialization step.
	*
	* If `takeFrom` is set, each operation from `operations` will be assigned the same original operation as assigned
	* for `takeFrom` operation. This should be used to update original operations. It should be used in a way that
	* `operations` are the result of `takeFrom` transformation to ensure proper "original operation propagation".
	*/
	setOriginalOperations(operations, takeFrom = null) {
		const originalOperation = takeFrom ? this.originalOperations.get(takeFrom) : null;
		for (const operation of operations) this.originalOperations.set(operation, originalOperation || operation);
	}
	/**
	* Saves a relation between operations `opA` and `opB`.
	*
	* Relations are then later used to help solve conflicts when operations are transformed.
	*/
	updateRelation(opA, opB) {
		if (opA instanceof MoveOperation) {
			if (opB instanceof MergeOperation) {
				if (opA.targetPosition.isEqual(opB.sourcePosition) || opB.movedRange.containsPosition(opA.targetPosition)) this._setRelation(opA, opB, "insertAtSource");
				else if (opA.targetPosition.isEqual(opB.deletionPosition)) this._setRelation(opA, opB, "insertBetween");
				else if (opA.targetPosition.isAfter(opB.sourcePosition)) this._setRelation(opA, opB, "moveTargetAfter");
				else if (opA.howMany > 1 && opA.sourcePosition.isEqual(opB.deletionPosition)) this._setRelation(opA, opB, "firstToMoveMerged");
				else if (opA.howMany > 1 && opA.sourcePosition.getShiftedBy(opA.howMany - 1).isEqual(opB.deletionPosition)) this._setRelation(opA, opB, "lastToMoveMerged");
			} else if (opB instanceof MoveOperation) if (opA.targetPosition.isEqual(opB.sourcePosition) || opA.targetPosition.isBefore(opB.sourcePosition)) this._setRelation(opA, opB, "insertBefore");
			else this._setRelation(opA, opB, "insertAfter");
		} else if (opA instanceof SplitOperation) {
			if (opB instanceof MergeOperation) {
				if (opA.splitPosition.isBefore(opB.sourcePosition)) this._setRelation(opA, opB, "splitBefore");
			} else if (opB instanceof MoveOperation) if (opA.splitPosition.isEqual(opB.sourcePosition) || opA.splitPosition.isBefore(opB.sourcePosition)) this._setRelation(opA, opB, "splitBefore");
			else {
				const range = ModelRange._createFromPositionAndShift(opB.sourcePosition, opB.howMany);
				if (opA.splitPosition.hasSameParentAs(opB.sourcePosition) && range.containsPosition(opA.splitPosition)) {
					const howMany = range.end.offset - opA.splitPosition.offset;
					const offset = opA.splitPosition.offset - range.start.offset;
					this._setRelation(opA, opB, {
						howMany,
						offset
					});
				}
			}
		} else if (opA instanceof MergeOperation) {
			if (opB instanceof MergeOperation) {
				if (!opA.targetPosition.isEqual(opB.sourcePosition)) this._setRelation(opA, opB, "mergeTargetNotMoved");
				if (opA.sourcePosition.isEqual(opB.targetPosition)) this._setRelation(opA, opB, "mergeSourceNotMoved");
				if (opA.sourcePosition.isEqual(opB.sourcePosition)) this._setRelation(opA, opB, "mergeSameElement");
			} else if (opB instanceof SplitOperation) {
				if (opA.sourcePosition.isEqual(opB.splitPosition)) this._setRelation(opA, opB, "splitAtSource");
			} else if (opB instanceof MoveOperation && opB.howMany > 0) {
				if (opA.sourcePosition.isEqual(opB.sourcePosition.getShiftedBy(opB.howMany))) this._setRelation(opA, opB, "mergeSourceAffected");
				if (opA.targetPosition.isEqual(opB.sourcePosition)) this._setRelation(opA, opB, "mergeTargetWasBefore");
			}
		} else if (opA instanceof MarkerOperation) {
			const markerRange = opA.newRange;
			if (!markerRange) return;
			if (opB instanceof MergeOperation) {
				const wasInLeftElement = markerRange.start.isEqual(opB.targetPosition);
				const wasStartBeforeMergedElement = markerRange.start.isEqual(opB.deletionPosition);
				const wasEndBeforeMergedElement = markerRange.end.isEqual(opB.deletionPosition);
				const wasInRightElement = markerRange.end.isEqual(opB.sourcePosition);
				if (wasInLeftElement || wasStartBeforeMergedElement || wasEndBeforeMergedElement || wasInRightElement) this._setRelation(opA, opB, {
					wasInLeftElement,
					wasStartBeforeMergedElement,
					wasEndBeforeMergedElement,
					wasInRightElement
				});
			}
		}
	}
	/**
	* Evaluates and returns contextual information about two given operations `opA` and `opB` which are about to be transformed.
	*/
	getContext(opA, opB, aIsStrong) {
		return {
			aIsStrong,
			aWasUndone: this._wasUndone(opA),
			bWasUndone: this._wasUndone(opB),
			abRelation: this._useRelations ? this._getRelation(opA, opB) : null,
			baRelation: this._useRelations ? this._getRelation(opB, opA) : null,
			forceWeakRemove: this._forceWeakRemove
		};
	}
	/**
	* Returns whether given operation `op` has already been undone.
	*
	* Information whether an operation was undone gives more context when making a decision when two operations are in conflict.
	*/
	_wasUndone(op) {
		const originalOp = this.originalOperations.get(op);
		return originalOp.wasUndone || this._history.isUndoneOperation(originalOp);
	}
	/**
	* Returns a relation between `opA` and an operation which is undone by `opB`. This can be `String` value if a relation
	* was set earlier or `null` if there was no relation between those operations.
	*
	* This is a little tricky to understand, so let's compare it to `ContextFactory#_wasUndone`.
	*
	* When `wasUndone( opB )` is used, we check if the `opB` has already been undone. It is obvious, that the
	* undoing operation must happen after the undone operation. So, essentially, we have `opB`, we take document history,
	* we look forward in the future and ask if in that future `opB` was undone.
	*
	* Relations is a backward process to `wasUndone()`.
	*
	* Long story short - using relations is asking what happened in the past. Looking back. This time we have an undoing
	* operation `opB` which has undone some other operation. When there is a transformation `opA` x `opB` and there is
	* a conflict to solve and `opB` is an undoing operation, we can look back in the history and see what was a relation
	* between `opA` and the operation which `opB` undone. Basing on that relation from the past, we can now make
	* a better decision when resolving a conflict between two operations, because we know more about the context of
	* those two operations.
	*
	* This is why this function does not return a relation directly between `opA` and `opB` because we need to look
	* back to search for a meaningful contextual information.
	*/
	_getRelation(opA, opB) {
		const origB = this.originalOperations.get(opB);
		const undoneB = this._history.getUndoneOperation(origB);
		if (!undoneB) return null;
		const origA = this.originalOperations.get(opA);
		const relationsA = this._relations.get(origA);
		if (relationsA) return relationsA.get(undoneB) || null;
		return null;
	}
	/**
	* Helper function for `ContextFactory#updateRelations`.
	*/
	_setRelation(opA, opB, relation) {
		const origA = this.originalOperations.get(opA);
		const origB = this.originalOperations.get(opB);
		let relationsA = this._relations.get(origA);
		if (!relationsA) {
			relationsA = /* @__PURE__ */ new Map();
			this._relations.set(origA, relationsA);
		}
		relationsA.set(origB, relation);
	}
};
/**
* An utility function that updates {@link module:engine/model/operation/operation~Operation#baseVersion base versions}
* of passed operations.
*
* The function simply sets `baseVersion` as a base version of the first passed operation and then increments it for
* each following operation in `operations`.
*
* @param operations Operations to update.
* @param baseVersion Base version to set for the first operation in `operations`.
*/
function updateBaseVersions(operations, baseVersion) {
	for (const operation of operations) operation.baseVersion = baseVersion++;
}
/**
* Adds `howMany` instances of {@link module:engine/model/operation/nooperation~NoOperation} to `operations` set.
*/
function padWithNoOps(operations, howMany) {
	for (let i = 0; i < howMany; i++) operations.push(new NoOperation(0));
}
/**
* Transformed operations set may include marker operations which were broken into multiple marker operations during transformation.
* It represents marker range being broken into multiple pieces as the transformation was processed. Each partial marker operation is
* a piece of the original marker range.
*
* These partial marker operations ("marker range pieces") should be "glued" together if, after transformations, the ranges ended up
* next to each other.
*
* If the ranges did not end up next to each other, then partial marker operations should be discarded, as the marker range cannot
* be broken into two pieces.
*
* There is always one "reference" marker operation (the original operation) and there may be some partial marker operations. Partial
* marker operations have base version set to `-1`. If the `operations` set includes partial marker operations, then they are always
* after the original marker operation.
*
* See also `MarkerOperation` x `MoveOperation` transformation.
* See also https://github.com/ckeditor/ckeditor5/pull/17071.
*/
function handlePartialMarkerOperations(operations) {
	const markerOps = /* @__PURE__ */ new Map();
	for (let i = 0; i < operations.length; i++) {
		const op = operations[i];
		if (!(op instanceof MarkerOperation)) continue;
		if (op.baseVersion !== -1) markerOps.set(op.name, {
			op,
			ranges: op.newRange ? [op.newRange] : []
		});
		else {
			/* v8 ignore next -- @preserve */
			if (op.newRange) {
				const partialRanges = markerOps.get(op.name).ranges;
				if (!partialRanges[0].containsRange(op.newRange, true)) partialRanges.push(op.newRange);
			}
			operations.splice(i, 1);
			i--;
		}
	}
	for (const { op, ranges } of markerOps.values()) if (ranges.length) op.newRange = ModelRange._createFromRanges(ranges);
	else op.newRange = null;
}
setTransformation(AttributeOperation, AttributeOperation, (a, b, context) => {
	if (a.key === b.key && a.range.start.hasSameParentAs(b.range.start)) {
		const operations = a.range.getDifference(b.range).map((range) => {
			return new AttributeOperation(range, a.key, a.oldValue, a.newValue, 0);
		});
		const common = a.range.getIntersection(b.range);
		if (common) {
			if (context.aIsStrong) operations.push(new AttributeOperation(common, b.key, b.newValue, a.newValue, 0));
		}
		if (operations.length == 0) return [new NoOperation(0)];
		return operations;
	} else return [a];
});
setTransformation(AttributeOperation, InsertOperation, (a, b) => {
	if (a.range.start.hasSameParentAs(b.position) && a.range.containsPosition(b.position)) {
		const result = a.range._getTransformedByInsertion(b.position, b.howMany, !b.shouldReceiveAttributes).map((r) => {
			return new AttributeOperation(r, a.key, a.oldValue, a.newValue, a.baseVersion);
		});
		if (b.shouldReceiveAttributes) {
			const op = _getComplementaryAttributeOperations(b, a.key, a.oldValue);
			if (op) result.unshift(op);
		}
		return result;
	}
	a.range = a.range._getTransformedByInsertion(b.position, b.howMany, false)[0];
	return [a];
});
/**
* Helper function for `AttributeOperation` x `InsertOperation` (and reverse) transformation.
*
* For given `insertOperation` it checks the inserted node if it has an attribute `key` set to a value different
* than `newValue`. If so, it generates an `AttributeOperation` which changes the value of `key` attribute to `newValue`.
*/
function _getComplementaryAttributeOperations(insertOperation, key, newValue) {
	const insertValue = insertOperation.nodes.getNode(0).getAttribute(key);
	if (insertValue == newValue) return null;
	return new AttributeOperation(new ModelRange(insertOperation.position, insertOperation.position.getShiftedBy(insertOperation.howMany)), key, insertValue, newValue, 0);
}
setTransformation(AttributeOperation, MergeOperation, (a, b) => {
	const ranges = [];
	if (a.range.start.hasSameParentAs(b.deletionPosition)) {
		if (a.range.containsPosition(b.deletionPosition) || a.range.start.isEqual(b.deletionPosition)) ranges.push(ModelRange._createFromPositionAndShift(b.graveyardPosition, 1));
	}
	const range = a.range._getTransformedByMergeOperation(b);
	if (!range.isCollapsed) ranges.push(range);
	return ranges.map((range) => {
		return new AttributeOperation(range, a.key, a.oldValue, a.newValue, a.baseVersion);
	});
});
setTransformation(AttributeOperation, MoveOperation, (a, b) => {
	return _breakRangeByMoveOperation(a.range, b).map((range) => new AttributeOperation(range, a.key, a.oldValue, a.newValue, a.baseVersion));
});
/**
* Helper function for `AttributeOperation` x `MoveOperation` transformation.
*
* Takes the passed `range` and transforms it by move operation `moveOp` in a specific way. Only top-level nodes of `range`
* are considered to be in the range. If move operation moves nodes deep from inside of the range, those nodes won't
* be included in the result. In other words, top-level nodes of the ranges from the result are exactly the same as
* top-level nodes of the original `range`.
*
* This is important for `AttributeOperation` because, for its range, it changes only the top-level nodes. So we need to
* track only how those nodes have been affected by `MoveOperation`.
*/
function _breakRangeByMoveOperation(range, moveOp) {
	const moveRange = ModelRange._createFromPositionAndShift(moveOp.sourcePosition, moveOp.howMany);
	let common = null;
	let difference = [];
	if (moveRange.containsRange(range, true)) common = range;
	else if (range.start.hasSameParentAs(moveRange.start)) {
		difference = range.getDifference(moveRange);
		common = range.getIntersection(moveRange);
	} else difference = [range];
	const result = [];
	for (let diff of difference) {
		diff = diff._getTransformedByDeletion(moveOp.sourcePosition, moveOp.howMany);
		const targetPosition = moveOp.getMovedRangeStart();
		const spread = diff.start.hasSameParentAs(targetPosition);
		const diffs = diff._getTransformedByInsertion(targetPosition, moveOp.howMany, spread);
		result.push(...diffs);
	}
	if (common) result.push(common._getTransformedByMove(moveOp.sourcePosition, moveOp.targetPosition, moveOp.howMany, false)[0]);
	return result;
}
setTransformation(AttributeOperation, SplitOperation, (a, b) => {
	if (a.range.end.isEqual(b.insertionPosition)) {
		if (!b.graveyardPosition) a.range.end.offset++;
		return [a];
	}
	if (a.range.start.hasSameParentAs(b.splitPosition) && a.range.containsPosition(b.splitPosition)) {
		const secondPart = a.clone();
		secondPart.range = new ModelRange(b.moveTargetPosition.clone(), a.range.end._getCombined(b.splitPosition, b.moveTargetPosition));
		a.range.end = b.splitPosition.clone();
		a.range.end.stickiness = "toPrevious";
		return [a, secondPart];
	}
	a.range = a.range._getTransformedBySplitOperation(b);
	return [a];
});
setTransformation(InsertOperation, AttributeOperation, (a, b) => {
	const result = [a];
	if (a.shouldReceiveAttributes && a.position.hasSameParentAs(b.range.start) && b.range.containsPosition(a.position)) {
		const op = _getComplementaryAttributeOperations(a, b.key, b.newValue);
		if (op) result.push(op);
	}
	return result;
});
setTransformation(InsertOperation, InsertOperation, (a, b, context) => {
	if (a.position.isEqual(b.position) && context.aIsStrong) return [a];
	a.position = a.position._getTransformedByInsertOperation(b);
	return [a];
});
setTransformation(InsertOperation, MoveOperation, (a, b) => {
	a.position = a.position._getTransformedByMoveOperation(b);
	return [a];
});
setTransformation(InsertOperation, SplitOperation, (a, b) => {
	a.position = a.position._getTransformedBySplitOperation(b);
	return [a];
});
setTransformation(InsertOperation, MergeOperation, (a, b) => {
	a.position = a.position._getTransformedByMergeOperation(b);
	return [a];
});
setTransformation(MarkerOperation, InsertOperation, (a, b) => {
	if (a.oldRange) a.oldRange = a.oldRange._getTransformedByInsertOperation(b)[0];
	if (a.newRange) a.newRange = a.newRange._getTransformedByInsertOperation(b)[0];
	return [a];
});
setTransformation(MarkerOperation, MarkerOperation, (a, b, context) => {
	if (a.name == b.name) if (context.aIsStrong) a.oldRange = b.newRange ? b.newRange.clone() : null;
	else return [new NoOperation(0)];
	return [a];
});
setTransformation(MarkerOperation, MergeOperation, (a, b) => {
	if (a.oldRange) a.oldRange = a.oldRange._getTransformedByMergeOperation(b);
	if (a.newRange) a.newRange = a.newRange._getTransformedByMergeOperation(b);
	return [a];
});
setTransformation(MarkerOperation, MoveOperation, (a, b) => {
	const result = [a];
	if (a.oldRange) a.oldRange = ModelRange._createFromRanges(a.oldRange._getTransformedByMoveOperation(b));
	if (a.newRange) {
		const ranges = a.newRange._getTransformedByMoveOperation(b);
		a.newRange = ranges[0];
		for (let i = 1; i < ranges.length; i++) {
			const op = a.clone();
			op.oldRange = null;
			op.newRange = ranges[i];
			op.baseVersion = -1;
			result.push(op);
		}
	}
	return result;
});
setTransformation(MarkerOperation, SplitOperation, (a, b, context) => {
	if (a.oldRange) a.oldRange = a.oldRange._getTransformedBySplitOperation(b);
	if (a.newRange) {
		if (context.abRelation) {
			const aNewRange = a.newRange._getTransformedBySplitOperation(b);
			if (a.newRange.start.isEqual(b.splitPosition)) if (context.abRelation.wasStartBeforeMergedElement) a.newRange.start = ModelPosition._createAt(b.insertionPosition);
			else if (context.abRelation.wasInLeftElement) a.newRange.start = ModelPosition._createAt(a.newRange.start);
			else a.newRange.start = ModelPosition._createAt(b.moveTargetPosition);
			else a.newRange.start = aNewRange.start;
			if (a.newRange.end.isEqual(b.splitPosition)) if (a.newRange.end.isEqual(b.splitPosition) && context.abRelation.wasEndBeforeMergedElement) a.newRange.end = ModelPosition._createAt(b.insertionPosition);
			else if (context.abRelation.wasInRightElement) a.newRange.end = ModelPosition._createAt(b.moveTargetPosition);
			else a.newRange.end = ModelPosition._createAt(a.newRange.end);
			else a.newRange.end = aNewRange.end;
			return [a];
		}
		a.newRange = a.newRange._getTransformedBySplitOperation(b);
	}
	return [a];
});
setTransformation(MergeOperation, InsertOperation, (a, b) => {
	if (a.sourcePosition.hasSameParentAs(b.position)) a.howMany += b.howMany;
	a.sourcePosition = a.sourcePosition._getTransformedByInsertOperation(b);
	a.targetPosition = a.targetPosition._getTransformedByInsertOperation(b);
	return [a];
});
setTransformation(MergeOperation, MergeOperation, (a, b, context) => {
	if (a.sourcePosition.isEqual(b.sourcePosition) && a.targetPosition.isEqual(b.targetPosition)) if (!context.bWasUndone) return [new NoOperation(0)];
	else {
		const path = b.graveyardPosition.path.slice();
		path.push(0);
		a.sourcePosition = new ModelPosition(b.graveyardPosition.root, path);
		a.howMany = 0;
		return [a];
	}
	if (a.sourcePosition.isEqual(b.sourcePosition) && !a.targetPosition.isEqual(b.targetPosition) && !context.bWasUndone && context.abRelation != "splitAtSource") {
		const aToGraveyard = a.targetPosition.root.rootName == "$graveyard";
		const bToGraveyard = b.targetPosition.root.rootName == "$graveyard";
		if (bToGraveyard && !aToGraveyard || !(aToGraveyard && !bToGraveyard) && context.aIsStrong) {
			const sourcePosition = b.targetPosition._getTransformedByMergeOperation(b);
			const targetPosition = a.targetPosition._getTransformedByMergeOperation(b);
			return [new MoveOperation(sourcePosition, a.howMany, targetPosition, 0)];
		} else return [new NoOperation(0)];
	}
	if (a.sourcePosition.hasSameParentAs(b.targetPosition)) a.howMany += b.howMany;
	a.sourcePosition = a.sourcePosition._getTransformedByMergeOperation(b);
	a.targetPosition = a.targetPosition._getTransformedByMergeOperation(b);
	if (!a.graveyardPosition.isEqual(b.graveyardPosition) || !context.aIsStrong) a.graveyardPosition = a.graveyardPosition._getTransformedByMergeOperation(b);
	return [a];
});
setTransformation(MergeOperation, MoveOperation, (a, b, context) => {
	const removedRange = ModelRange._createFromPositionAndShift(b.sourcePosition, b.howMany);
	if (b.type == "remove" && !context.bWasUndone) {
		if (a.deletionPosition.hasSameParentAs(b.sourcePosition) && removedRange.containsPosition(a.sourcePosition)) return [new NoOperation(0)];
	}
	if (b.sourcePosition.getShiftedBy(b.howMany).isEqual(a.sourcePosition)) a.sourcePosition.stickiness = "toNone";
	else if (b.targetPosition.isEqual(a.sourcePosition) && context.abRelation == "mergeSourceAffected") a.sourcePosition.stickiness = "toNext";
	else if (b.sourcePosition.isEqual(a.targetPosition)) {
		a.targetPosition.stickiness = "toNone";
		a.howMany -= b.howMany;
	} else if (b.targetPosition.isEqual(a.targetPosition) && context.abRelation == "mergeTargetWasBefore") {
		a.targetPosition.stickiness = "toPrevious";
		a.howMany += b.howMany;
	} else {
		if (a.sourcePosition.hasSameParentAs(b.targetPosition)) a.howMany += b.howMany;
		if (a.sourcePosition.hasSameParentAs(b.sourcePosition)) a.howMany -= b.howMany;
	}
	a.sourcePosition = a.sourcePosition._getTransformedByMoveOperation(b);
	a.targetPosition = a.targetPosition._getTransformedByMoveOperation(b);
	a.sourcePosition.stickiness = "toPrevious";
	a.targetPosition.stickiness = "toNext";
	if (!a.graveyardPosition.isEqual(b.targetPosition)) a.graveyardPosition = a.graveyardPosition._getTransformedByMoveOperation(b);
	return [a];
});
setTransformation(MergeOperation, SplitOperation, (a, b, context) => {
	if (b.graveyardPosition) {
		a.graveyardPosition = a.graveyardPosition._getTransformedByDeletion(b.graveyardPosition, 1);
		if (a.deletionPosition.isEqual(b.graveyardPosition)) a.howMany = b.howMany;
	}
	if (a.targetPosition.isEqual(b.splitPosition)) {
		if (b.graveyardPosition && a.deletionPosition.isEqual(b.graveyardPosition) || context.abRelation == "mergeTargetNotMoved") {
			a.sourcePosition = a.sourcePosition._getTransformedBySplitOperation(b);
			return [a];
		}
	}
	if (a.sourcePosition.isEqual(b.splitPosition)) {
		if (context.abRelation == "mergeSourceNotMoved") {
			a.howMany = 0;
			a.targetPosition = a.targetPosition._getTransformedBySplitOperation(b);
			return [a];
		}
		if (context.abRelation == "mergeSameElement" || a.sourcePosition.offset > 0) {
			a.sourcePosition = b.moveTargetPosition.clone();
			a.targetPosition = a.targetPosition._getTransformedBySplitOperation(b);
			return [a];
		}
	}
	if (a.sourcePosition.hasSameParentAs(b.splitPosition)) a.howMany = b.splitPosition.offset;
	a.sourcePosition = a.sourcePosition._getTransformedBySplitOperation(b);
	a.targetPosition = a.targetPosition._getTransformedBySplitOperation(b);
	return [a];
});
setTransformation(MoveOperation, InsertOperation, (a, b) => {
	const transformed = ModelRange._createFromPositionAndShift(a.sourcePosition, a.howMany)._getTransformedByInsertOperation(b, false)[0];
	a.sourcePosition = transformed.start;
	a.howMany = transformed.end.offset - transformed.start.offset;
	if (!a.targetPosition.isEqual(b.position)) a.targetPosition = a.targetPosition._getTransformedByInsertOperation(b);
	return [a];
});
setTransformation(MoveOperation, MoveOperation, (a, b, context) => {
	const rangeA = ModelRange._createFromPositionAndShift(a.sourcePosition, a.howMany);
	const rangeB = ModelRange._createFromPositionAndShift(b.sourcePosition, b.howMany);
	let aIsStrong = context.aIsStrong;
	let insertBefore = !context.aIsStrong;
	if (context.abRelation == "insertBefore" || context.baRelation == "insertAfter") insertBefore = true;
	else if (context.abRelation == "insertAfter" || context.baRelation == "insertBefore") insertBefore = false;
	let newTargetPosition;
	if (a.targetPosition.isEqual(b.targetPosition) && insertBefore) newTargetPosition = a.targetPosition._getTransformedByDeletion(b.sourcePosition, b.howMany);
	else newTargetPosition = a.targetPosition._getTransformedByMove(b.sourcePosition, b.targetPosition, b.howMany);
	if (_moveTargetIntoMovedRange(a, b) && _moveTargetIntoMovedRange(b, a)) return [b.getReversed()];
	if (rangeA.containsPosition(b.targetPosition) && rangeA.containsRange(rangeB, true)) {
		rangeA.start = rangeA.start._getTransformedByMove(b.sourcePosition, b.targetPosition, b.howMany);
		rangeA.end = rangeA.end._getTransformedByMove(b.sourcePosition, b.targetPosition, b.howMany);
		return _makeMoveOperationsFromRanges([rangeA], newTargetPosition);
	}
	if (rangeB.containsPosition(a.targetPosition) && rangeB.containsRange(rangeA, true)) {
		rangeA.start = rangeA.start._getCombined(b.sourcePosition, b.getMovedRangeStart());
		rangeA.end = rangeA.end._getCombined(b.sourcePosition, b.getMovedRangeStart());
		return _makeMoveOperationsFromRanges([rangeA], newTargetPosition);
	}
	const aCompB = compareArrays(a.sourcePosition.getParentPath(), b.sourcePosition.getParentPath());
	if (aCompB == "prefix" || aCompB == "extension") {
		rangeA.start = rangeA.start._getTransformedByMove(b.sourcePosition, b.targetPosition, b.howMany);
		rangeA.end = rangeA.end._getTransformedByMove(b.sourcePosition, b.targetPosition, b.howMany);
		return _makeMoveOperationsFromRanges([rangeA], newTargetPosition);
	}
	if (a.type == "remove" && b.type != "remove" && !context.aWasUndone && !context.forceWeakRemove) aIsStrong = true;
	else if (a.type != "remove" && b.type == "remove" && !context.bWasUndone && !context.forceWeakRemove) aIsStrong = false;
	const ranges = [];
	const difference = rangeA.getDifference(rangeB);
	for (const range of difference) {
		range.start = range.start._getTransformedByDeletion(b.sourcePosition, b.howMany);
		range.end = range.end._getTransformedByDeletion(b.sourcePosition, b.howMany);
		const shouldSpread = compareArrays(range.start.getParentPath(), b.getMovedRangeStart().getParentPath()) == "same";
		const newRanges = range._getTransformedByInsertion(b.getMovedRangeStart(), b.howMany, shouldSpread);
		ranges.push(...newRanges);
	}
	const common = rangeA.getIntersection(rangeB);
	if (common !== null && aIsStrong) {
		common.start = common.start._getCombined(b.sourcePosition, b.getMovedRangeStart());
		common.end = common.end._getCombined(b.sourcePosition, b.getMovedRangeStart());
		if (ranges.length === 0) ranges.push(common);
		else if (ranges.length == 1) if (rangeB.start.isBefore(rangeA.start) || rangeB.start.isEqual(rangeA.start)) ranges.unshift(common);
		else ranges.push(common);
		else ranges.splice(1, 0, common);
	}
	if (ranges.length === 0) return [new NoOperation(a.baseVersion)];
	return _makeMoveOperationsFromRanges(ranges, newTargetPosition);
});
setTransformation(MoveOperation, SplitOperation, (a, b, context) => {
	let newTargetPosition = a.targetPosition.clone();
	if (!a.targetPosition.isEqual(b.insertionPosition) || !b.graveyardPosition || context.abRelation == "moveTargetAfter") newTargetPosition = a.targetPosition._getTransformedBySplitOperation(b);
	if (a.sourcePosition.isEqual(b.insertionPosition) && context.abRelation == "firstToMoveMerged") {
		a.howMany++;
		a.targetPosition = newTargetPosition;
		return [a];
	}
	const moveRange = ModelRange._createFromPositionAndShift(a.sourcePosition, a.howMany);
	if (moveRange.end.isEqual(b.insertionPosition)) {
		if (!b.graveyardPosition || context.abRelation == "lastToMoveMerged") a.howMany++;
		a.targetPosition = newTargetPosition;
		return [a];
	}
	if (moveRange.start.hasSameParentAs(b.splitPosition) && moveRange.containsPosition(b.splitPosition)) {
		let rightRange = new ModelRange(b.splitPosition, moveRange.end);
		rightRange = rightRange._getTransformedBySplitOperation(b);
		return _makeMoveOperationsFromRanges([new ModelRange(moveRange.start, b.splitPosition), rightRange], newTargetPosition);
	}
	if (a.targetPosition.isEqual(b.splitPosition) && context.abRelation == "insertAtSource") newTargetPosition = b.moveTargetPosition;
	if (a.targetPosition.isEqual(b.insertionPosition) && context.abRelation == "insertBetween") newTargetPosition = a.targetPosition;
	const ranges = [moveRange._getTransformedBySplitOperation(b)];
	if (b.graveyardPosition) {
		const movesGraveyardElement = moveRange.start.isEqual(b.graveyardPosition) || moveRange.containsPosition(b.graveyardPosition);
		if (a.howMany > 1 && movesGraveyardElement && !context.aWasUndone) ranges.push(ModelRange._createFromPositionAndShift(b.insertionPosition, 1));
	}
	return _makeMoveOperationsFromRanges(ranges, newTargetPosition);
});
setTransformation(MoveOperation, MergeOperation, (a, b, context) => {
	const movedRange = ModelRange._createFromPositionAndShift(a.sourcePosition, a.howMany);
	if (b.deletionPosition.hasSameParentAs(a.sourcePosition) && movedRange.containsPosition(b.sourcePosition)) {
		if (a.type == "remove" && !context.forceWeakRemove) {
			if (!context.aWasUndone) {
				const results = [];
				let gyMoveSource = b.graveyardPosition.clone();
				let splitNodesMoveSource = b.targetPosition._getTransformedByMergeOperation(b);
				const aTarget = a.targetPosition.getTransformedByOperation(b);
				if (a.howMany > 1) {
					results.push(new MoveOperation(a.sourcePosition, a.howMany - 1, aTarget, 0));
					gyMoveSource = gyMoveSource._getTransformedByMove(a.sourcePosition, aTarget, a.howMany - 1);
					splitNodesMoveSource = splitNodesMoveSource._getTransformedByMove(a.sourcePosition, aTarget, a.howMany - 1);
				}
				const gyMoveTarget = b.deletionPosition._getCombined(a.sourcePosition, aTarget);
				const gyMove = new MoveOperation(gyMoveSource, 1, gyMoveTarget, 0);
				const splitNodesMoveTargetPath = gyMove.getMovedRangeStart().path.slice();
				splitNodesMoveTargetPath.push(0);
				const splitNodesMoveTarget = new ModelPosition(gyMove.targetPosition.root, splitNodesMoveTargetPath);
				splitNodesMoveSource = splitNodesMoveSource._getTransformedByMove(gyMoveSource, gyMoveTarget, 1);
				const splitNodesMove = new MoveOperation(splitNodesMoveSource, b.howMany, splitNodesMoveTarget, 0);
				results.push(gyMove);
				results.push(splitNodesMove);
				return results;
			}
		} else if (a.howMany == 1) if (!context.bWasUndone) return [new NoOperation(0)];
		else {
			a.sourcePosition = b.graveyardPosition.clone();
			a.targetPosition = a.targetPosition._getTransformedByMergeOperation(b);
			return [a];
		}
	}
	const transformed = ModelRange._createFromPositionAndShift(a.sourcePosition, a.howMany)._getTransformedByMergeOperation(b);
	a.sourcePosition = transformed.start;
	a.howMany = transformed.end.offset - transformed.start.offset;
	a.targetPosition = a.targetPosition._getTransformedByMergeOperation(b);
	return [a];
});
setTransformation(RenameOperation, InsertOperation, (a, b) => {
	a.position = a.position._getTransformedByInsertOperation(b);
	return [a];
});
setTransformation(RenameOperation, MergeOperation, (a, b) => {
	if (a.position.isEqual(b.deletionPosition)) {
		a.position = b.graveyardPosition.clone();
		a.position.stickiness = "toNext";
		return [a];
	}
	a.position = a.position._getTransformedByMergeOperation(b);
	return [a];
});
setTransformation(RenameOperation, MoveOperation, (a, b) => {
	a.position = a.position._getTransformedByMoveOperation(b);
	return [a];
});
setTransformation(RenameOperation, RenameOperation, (a, b, context) => {
	if (a.position.isEqual(b.position)) if (context.aIsStrong) a.oldName = b.newName;
	else return [new NoOperation(0)];
	return [a];
});
setTransformation(RenameOperation, SplitOperation, (a, b) => {
	const renamePath = a.position.path;
	if (compareArrays(renamePath, b.splitPosition.getParentPath()) == "same" && !b.graveyardPosition) return [a, new RenameOperation(a.position.getShiftedBy(1), a.oldName, a.newName, 0)];
	a.position = a.position._getTransformedBySplitOperation(b);
	return [a];
});
setTransformation(RootAttributeOperation, RootAttributeOperation, (a, b, context) => {
	if (a.root === b.root && a.key === b.key) if (!context.aIsStrong) return [new NoOperation(0)];
	else a.oldValue = b.newValue;
	return [a];
});
setTransformation(RootOperation, RootOperation, (a, b) => {
	if (a.rootName === b.rootName && a.isAdd === b.isAdd) return [new NoOperation(0)];
	return [a];
});
setTransformation(SplitOperation, InsertOperation, (a, b) => {
	if (a.splitPosition.hasSameParentAs(b.position) && a.splitPosition.offset < b.position.offset) a.howMany += b.howMany;
	a.splitPosition = a.splitPosition._getTransformedByInsertOperation(b);
	a.insertionPosition = a.insertionPosition._getTransformedByInsertOperation(b);
	return [a];
});
setTransformation(SplitOperation, MergeOperation, (a, b, context) => {
	if (!a.graveyardPosition && !context.bWasUndone && a.splitPosition.hasSameParentAs(b.sourcePosition)) {
		const splitPath = b.graveyardPosition.path.slice();
		splitPath.push(0);
		const additionalSplit = new SplitOperation(new ModelPosition(b.graveyardPosition.root, splitPath), 0, SplitOperation.getInsertionPosition(new ModelPosition(b.graveyardPosition.root, splitPath)), null, 0);
		a.splitPosition = a.splitPosition._getTransformedByMergeOperation(b);
		a.insertionPosition = SplitOperation.getInsertionPosition(a.splitPosition);
		a.graveyardPosition = additionalSplit.insertionPosition.clone();
		a.graveyardPosition.stickiness = "toNext";
		return [additionalSplit, a];
	}
	if (a.splitPosition.hasSameParentAs(b.deletionPosition) && !a.splitPosition.isAfter(b.deletionPosition)) a.howMany--;
	if (a.splitPosition.hasSameParentAs(b.targetPosition)) a.howMany += b.howMany;
	a.splitPosition = a.splitPosition._getTransformedByMergeOperation(b);
	a.insertionPosition = SplitOperation.getInsertionPosition(a.splitPosition);
	if (a.graveyardPosition) a.graveyardPosition = a.graveyardPosition._getTransformedByMergeOperation(b);
	return [a];
});
setTransformation(SplitOperation, MoveOperation, (a, b, context) => {
	const rangeToMove = ModelRange._createFromPositionAndShift(b.sourcePosition, b.howMany);
	if (a.graveyardPosition) {
		const gyElementMoved = rangeToMove.start.isEqual(a.graveyardPosition) || rangeToMove.containsPosition(a.graveyardPosition);
		if (!context.bWasUndone && gyElementMoved) {
			const sourcePosition = a.splitPosition._getTransformedByMoveOperation(b);
			const newParentPosition = a.graveyardPosition._getTransformedByMoveOperation(b);
			const newTargetPath = newParentPosition.path.slice();
			newTargetPath.push(0);
			const newTargetPosition = new ModelPosition(newParentPosition.root, newTargetPath);
			return [new MoveOperation(sourcePosition, a.howMany, newTargetPosition, 0)];
		}
		a.graveyardPosition = a.graveyardPosition._getTransformedByMoveOperation(b);
	}
	const splitAtTarget = a.splitPosition.isEqual(b.targetPosition);
	if (splitAtTarget && (context.baRelation == "insertAtSource" || context.abRelation == "splitBefore")) {
		a.howMany += b.howMany;
		a.splitPosition = a.splitPosition._getTransformedByDeletion(b.sourcePosition, b.howMany);
		a.insertionPosition = SplitOperation.getInsertionPosition(a.splitPosition);
		return [a];
	}
	if (splitAtTarget && context.abRelation && context.abRelation.howMany) {
		const { howMany, offset } = context.abRelation;
		a.howMany += howMany;
		a.splitPosition = a.splitPosition.getShiftedBy(offset);
		return [a];
	}
	if (a.splitPosition.hasSameParentAs(b.sourcePosition) && rangeToMove.containsPosition(a.splitPosition)) {
		const howManyRemoved = b.howMany - (a.splitPosition.offset - b.sourcePosition.offset);
		a.howMany -= howManyRemoved;
		if (a.splitPosition.hasSameParentAs(b.targetPosition) && a.splitPosition.offset < b.targetPosition.offset) a.howMany += b.howMany;
		a.splitPosition = b.sourcePosition.clone();
		a.insertionPosition = SplitOperation.getInsertionPosition(a.splitPosition);
		return [a];
	}
	if (!b.sourcePosition.isEqual(b.targetPosition)) {
		if (a.splitPosition.hasSameParentAs(b.sourcePosition) && a.splitPosition.offset <= b.sourcePosition.offset) a.howMany -= b.howMany;
		if (a.splitPosition.hasSameParentAs(b.targetPosition) && a.splitPosition.offset < b.targetPosition.offset) a.howMany += b.howMany;
	}
	a.splitPosition.stickiness = "toNone";
	a.splitPosition = a.splitPosition._getTransformedByMoveOperation(b);
	a.splitPosition.stickiness = "toNext";
	if (a.graveyardPosition) a.insertionPosition = a.insertionPosition._getTransformedByMoveOperation(b);
	else a.insertionPosition = SplitOperation.getInsertionPosition(a.splitPosition);
	return [a];
});
setTransformation(SplitOperation, SplitOperation, (a, b, context) => {
	if (a.splitPosition.isEqual(b.splitPosition)) {
		if (!a.graveyardPosition && !b.graveyardPosition) return [new NoOperation(0)];
		if (a.graveyardPosition && b.graveyardPosition && a.graveyardPosition.isEqual(b.graveyardPosition)) return [new NoOperation(0)];
		if (context.abRelation == "splitBefore") {
			a.howMany = 0;
			a.graveyardPosition = a.graveyardPosition._getTransformedBySplitOperation(b);
			return [a];
		}
	}
	if (a.graveyardPosition && b.graveyardPosition && a.graveyardPosition.isEqual(b.graveyardPosition)) {
		const aInGraveyard = a.splitPosition.root.rootName == "$graveyard";
		const bInGraveyard = b.splitPosition.root.rootName == "$graveyard";
		if (bInGraveyard && !aInGraveyard || !(aInGraveyard && !bInGraveyard) && context.aIsStrong) {
			const result = [];
			if (b.howMany) result.push(new MoveOperation(b.moveTargetPosition, b.howMany, b.splitPosition, 0));
			if (a.howMany) result.push(new MoveOperation(a.splitPosition, a.howMany, a.moveTargetPosition, 0));
			return result;
		} else return [new NoOperation(0)];
	}
	if (a.graveyardPosition) a.graveyardPosition = a.graveyardPosition._getTransformedBySplitOperation(b);
	if (a.splitPosition.isEqual(b.insertionPosition) && context.abRelation == "splitBefore") {
		a.howMany++;
		return [a];
	}
	if (b.splitPosition.isEqual(a.insertionPosition) && context.baRelation == "splitBefore") {
		const newPositionPath = b.insertionPosition.path.slice();
		newPositionPath.push(0);
		const newPosition = new ModelPosition(b.insertionPosition.root, newPositionPath);
		return [a, new MoveOperation(a.insertionPosition, 1, newPosition, 0)];
	}
	if (a.splitPosition.hasSameParentAs(b.splitPosition) && a.splitPosition.offset < b.splitPosition.offset) a.howMany -= b.howMany;
	a.splitPosition = a.splitPosition._getTransformedBySplitOperation(b);
	a.insertionPosition = SplitOperation.getInsertionPosition(a.splitPosition);
	return [a];
});
/**
* Checks whether `MoveOperation` `targetPosition` is inside a node from the moved range of the other `MoveOperation`.
*/
function _moveTargetIntoMovedRange(a, b) {
	return a.targetPosition._getTransformedByDeletion(b.sourcePosition, b.howMany) === null;
}
/**
* Helper function for `MoveOperation` x `MoveOperation` transformation. Converts given ranges and target position to
* move operations and returns them.
*
* Ranges and target position will be transformed on-the-fly when generating operations.
*
* Given `ranges` should be in the order of how they were in the original transformed operation.
*
* Given `targetPosition` is the target position of the first range from `ranges`.
*/
function _makeMoveOperationsFromRanges(ranges, targetPosition) {
	const operations = [];
	for (let i = 0; i < ranges.length; i++) {
		const range = ranges[i];
		const op = new MoveOperation(range.start, range.end.offset - range.start.offset, targetPosition, 0);
		operations.push(op);
		for (let j = i + 1; j < ranges.length; j++) ranges[j] = ranges[j]._getTransformedByMove(op.sourcePosition, op.targetPosition, op.howMany)[0];
		targetPosition = targetPosition._getTransformedByMove(op.sourcePosition, op.targetPosition, op.howMany);
	}
	return operations;
}

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* @module engine/model/liveposition
*/
const ModelLivePositionBase = /* #__PURE__ */ EmitterMixin(ModelPosition);
/**
* `ModelLivePosition` is a type of {@link module:engine/model/position~ModelPosition Position}
* that updates itself as {@link module:engine/model/document~ModelDocument document}
* is changed through operations. It may be used as a bookmark.
*
* **Note:** Contrary to {@link module:engine/model/position~ModelPosition}, `ModelLivePosition` works only in roots that are
* {@link module:engine/model/rootelement~ModelRootElement}.
* If {@link module:engine/model/documentfragment~ModelDocumentFragment} is passed, error will be thrown.
*
* **Note:** Be very careful when dealing with `ModelLivePosition`. Each `ModelLivePosition` instance bind events that might
* have to be unbound.
* Use {@link module:engine/model/liveposition~ModelLivePosition#detach} whenever you don't need `ModelLivePosition` anymore.
*/
var ModelLivePosition = class extends ModelLivePositionBase {
	/**
	* Creates a live position.
	*
	* @see module:engine/model/position~ModelPosition
	*/
	constructor(root, path, stickiness = "toNone") {
		super(root, path, stickiness);
		if (!this.root.is("rootElement"))
 /**
		* LivePosition's root has to be an instance of ModelRootElement.
		*
		* @error model-liveposition-root-not-rootelement
		*/
		throw new CKEditorError("model-liveposition-root-not-rootelement", root);
		bindWithDocument.call(this);
	}
	/**
	* Unbinds all events previously bound by `ModelLivePosition`. Use it whenever you don't need `ModelLivePosition` instance
	* anymore (i.e. when leaving scope in which it was declared or before re-assigning variable that was
	* referring to it).
	*/
	detach() {
		this.stopListening();
	}
	/**
	* Creates a {@link module:engine/model/position~ModelPosition position instance}, which is equal to this live position.
	*/
	toPosition() {
		return new ModelPosition(this.root, this.path.slice(), this.stickiness);
	}
	/**
	* Creates a `ModelLivePosition` instance that is equal to position.
	*/
	static fromPosition(position, stickiness) {
		return new this(position.root, position.path.slice(), stickiness ? stickiness : position.stickiness);
	}
};
ModelLivePosition.prototype.is = function(type) {
	return type === "livePosition" || type === "model:livePosition" || type == "position" || type === "model:position";
};
/**
* Binds this `ModelLivePosition` to the {@link module:engine/model/document~ModelDocument document} that owns
* this position's {@link module:engine/model/position~ModelPosition#root root}.
*/
function bindWithDocument() {
	this.listenTo(this.root.document.model, "applyOperation", (event, args) => {
		const operation = args[0];
		if (!operation.isDocumentOperation) return;
		transform$1.call(this, operation);
	}, { priority: "low" });
}
/**
* Updates this position accordingly to the updates applied to the model. Bases on change events.
*/
function transform$1(operation) {
	const result = this.getTransformedByOperation(operation);
	if (!this.isEqual(result)) {
		const oldPosition = this.toPosition();
		this.path = result.path;
		this.root = result.root;
		this.fire("change", oldPosition);
	}
}

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* @module engine/model/batch
*/
/**
* A batch instance groups model changes ({@link module:engine/model/operation/operation~Operation operations}). All operations
* grouped in a single batch can be reverted together, so you can also think about a batch as of a single undo step. If you want
* to extend a given undo step, you can add more changes to the batch using {@link module:engine/model/model~Model#enqueueChange}:
*
* ```ts
* model.enqueueChange( batch, writer => {
* 	writer.insertText( 'foo', paragraph, 'end' );
* } );
* ```
*
* @see module:engine/model/model~Model#enqueueChange
* @see module:engine/model/model~Model#change
*/
var Batch = class {
	/**
	* An array of operations that compose this batch.
	*/
	operations;
	/**
	* Whether the batch can be undone through the undo feature.
	*/
	isUndoable;
	/**
	* Whether the batch includes operations created locally (`true`) or operations created on other, remote editors (`false`).
	*/
	isLocal;
	/**
	* Whether the batch was created by the undo feature and undoes other operations.
	*/
	isUndo;
	/**
	* Whether the batch includes operations connected with typing.
	*/
	isTyping;
	/**
	* Creates a batch instance.
	*
	* @see module:engine/model/model~Model#enqueueChange
	* @see module:engine/model/model~Model#change
	* @param type A set of flags that specify the type of the batch. Batch type can alter how some of the features work
	* when encountering a given `Batch` instance (for example, when a feature listens to applied operations).
	*/
	constructor(type = {}) {
		if (typeof type === "string") {
			type = type === "transparent" ? { isUndoable: false } : {};
			/**
			* The string value for a `type` property of the `Batch` constructor has been deprecated and will be removed in the near future.
			* Please refer to the {@link module:engine/model/batch~Batch#constructor `Batch` constructor API documentation} for more
			* information.
			*
			* @error batch-constructor-deprecated-string-type
			*/
			logWarning("batch-constructor-deprecated-string-type");
		}
		const { isUndoable = true, isLocal = true, isUndo = false, isTyping = false } = type;
		this.operations = [];
		this.isUndoable = isUndoable;
		this.isLocal = isLocal;
		this.isUndo = isUndo;
		this.isTyping = isTyping;
	}
	/**
	* Returns the base version of this batch, which is equal to the base version of the first operation in the batch.
	* If there are no operations in the batch or neither operation has the base version set, it returns `null`.
	*/
	get baseVersion() {
		for (const op of this.operations) if (op.baseVersion !== null) return op.baseVersion;
		return null;
	}
	/**
	* Adds an operation to the batch instance.
	*
	* @param operation An operation to add.
	* @returns The added operation.
	*/
	addOperation(operation) {
		if (operation.isDocumentOperation) {
			operation.batch = this;
			this.operations.push(operation);
		}
		return operation;
	}
};

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* @module engine/model/differ
*/
/**
* Calculates the difference between two model states.
*
* Receives operations that are to be applied on the model document. Marks parts of the model document tree which
* are changed and saves the state of these elements before the change. Then, it compares saved elements with the
* changed elements, after all changes are applied on the model document. Calculates the diff between saved
* elements and new ones and returns a change set.
*/
var Differ = class Differ {
	/**
	* Priority of the {@link ~Differ#_elementState element states}. States on higher indexes of the array can overwrite states on the lower
	* indexes.
	*/
	static _statesPriority = [
		void 0,
		"refresh",
		"rename",
		"move"
	];
	/**
	* Reference to the model's marker collection.
	*/
	_markerCollection;
	/**
	* A map that stores changes that happened in a given element.
	*
	* The keys of the map are references to the model elements.
	* The values of the map are arrays with changes that were done on this element.
	*/
	_changesInElement = /* @__PURE__ */ new Map();
	/**
	* Stores a snapshot for these model nodes that might have changed.
	*
	* This complements {@link ~Differ#_elementChildrenSnapshots `_elementChildrenSnapshots`}.
	*
	* See also {@link ~DifferSnapshot}.
	*/
	_elementsSnapshots = /* @__PURE__ */ new Map();
	/**
	* For each element or document fragment inside which there was a change, it stores a snapshot of the child nodes list (an array
	* of children snapshots that represent the state in the element / fragment before any change has happened).
	*
	* This complements {@link ~Differ#_elementsSnapshots `_elementsSnapshots`}.
	*
	* See also {@link ~DifferSnapshot}.
	*/
	_elementChildrenSnapshots = /* @__PURE__ */ new Map();
	/**
	* Keeps the state for a given element, describing how the element was changed so far. It is used to evaluate the `action` property
	* of diff items returned by {@link ~Differ#getChanges}.
	*
	* Possible values, in the order from the lowest priority to the highest priority:
	*
	* * `'refresh'` - element was refreshed,
	* * `'rename'` - element was renamed,
	* * `'move'` - element was moved (or, usually, removed, that is moved to the graveyard).
	*
	* Element that was refreshed, may change its state to `'rename'` if it was later renamed, or to `'move'` if it was removed.
	* But the element cannot change its state from `'move'` to `'rename'`, or from `'rename'` to `'refresh'`.
	*
	* Only already existing elements are registered in `_elementState`. If a new element was inserted as a result of a buffered operation,
	* it is not be registered in `_elementState`.
	*/
	_elementState = /* @__PURE__ */ new Map();
	/**
	* A map that stores all changed markers.
	*
	* The keys of the map are marker names.
	*
	* The values of the map are objects with the following properties:
	*
	* * `oldMarkerData`,
	* * `newMarkerData`.
	*/
	_changedMarkers = /* @__PURE__ */ new Map();
	/**
	* A map that stores all roots that have been changed.
	*
	* The keys are the names of the roots while value represents the changes.
	*/
	_changedRoots = /* @__PURE__ */ new Map();
	/**
	* Stores the number of changes that were processed. Used to order the changes chronologically. It is important
	* when changes are sorted.
	*/
	_changeCount = 0;
	/**
	* For efficiency purposes, `Differ` stores the change set returned by the differ after {@link #getChanges} call.
	* Cache is reset each time a new operation is buffered. If the cache has not been reset, {@link #getChanges} will
	* return the cached value instead of calculating it again.
	*
	* This property stores those changes that did not take place in graveyard root.
	*/
	_cachedChanges = null;
	/**
	* For efficiency purposes, `Differ` stores the change set returned by the differ after the {@link #getChanges} call.
	* The cache is reset each time a new operation is buffered. If the cache has not been reset, {@link #getChanges} will
	* return the cached value instead of calculating it again.
	*
	* This property stores all changes evaluated by `Differ`, including those that took place in the graveyard.
	*/
	_cachedChangesWithGraveyard = null;
	/**
	* Set of model items that were marked to get refreshed in {@link #_refreshItem}.
	*/
	_refreshedItems = /* @__PURE__ */ new Set();
	/**
	* Creates a `Differ` instance.
	*
	* @param markerCollection Model's marker collection.
	*/
	constructor(markerCollection) {
		this._markerCollection = markerCollection;
	}
	/**
	* Informs whether there are any changes buffered in `Differ`.
	*/
	get isEmpty() {
		return this._changesInElement.size == 0 && this._changedMarkers.size == 0 && this._changedRoots.size == 0;
	}
	/**
	* Buffers the given operation. **An operation has to be buffered before it is executed.**
	*
	* @param operationToBuffer An operation to buffer.
	*/
	bufferOperation(operationToBuffer) {
		const operation = operationToBuffer;
		switch (operation.type) {
			case "insert":
				if (this._isInInsertedElement(operation.position.parent)) return;
				this._markInsert(operation.position.parent, operation.position.offset, operation.nodes.maxOffset);
				break;
			case "addAttribute":
			case "removeAttribute":
			case "changeAttribute":
				for (const item of operation.range.getItems({ shallow: true })) {
					if (this._isInInsertedElement(item.parent)) continue;
					this._markAttribute(item);
				}
				break;
			case "remove":
			case "move":
			case "reinsert": {
				if (operation.sourcePosition.isEqual(operation.targetPosition) || operation.sourcePosition.getShiftedBy(operation.howMany).isEqual(operation.targetPosition)) return;
				const sourceParentInserted = this._isInInsertedElement(operation.sourcePosition.parent);
				const targetParentInserted = this._isInInsertedElement(operation.targetPosition.parent);
				if (!sourceParentInserted) this._markRemove(operation.sourcePosition.parent, operation.sourcePosition.offset, operation.howMany);
				if (!targetParentInserted) this._markInsert(operation.targetPosition.parent, operation.getMovedRangeStart().offset, operation.howMany);
				const range = ModelRange._createFromPositionAndShift(operation.sourcePosition, operation.howMany);
				for (const node of range.getItems({ shallow: true })) this._setElementState(node, "move");
				break;
			}
			case "rename": {
				if (this._isInInsertedElement(operation.position.parent)) return;
				this._markRemove(operation.position.parent, operation.position.offset, 1);
				this._markInsert(operation.position.parent, operation.position.offset, 1);
				const range = ModelRange._createFromPositionAndShift(operation.position, 1);
				for (const marker of this._markerCollection.getMarkersIntersectingRange(range)) {
					const markerData = marker.getData();
					this.bufferMarkerChange(marker.name, markerData, markerData);
				}
				this._setElementState(operation.position.nodeAfter, "rename");
				break;
			}
			case "split": {
				const splitElement = operation.splitPosition.parent;
				if (!this._isInInsertedElement(splitElement)) {
					this._markRemove(splitElement, operation.splitPosition.offset, operation.howMany);
					const range = ModelRange._createFromPositionAndShift(operation.splitPosition, operation.howMany);
					for (const node of range.getItems({ shallow: true })) this._setElementState(node, "move");
				}
				if (!this._isInInsertedElement(operation.insertionPosition.parent)) this._markInsert(operation.insertionPosition.parent, operation.insertionPosition.offset, 1);
				if (operation.graveyardPosition) {
					this._markRemove(operation.graveyardPosition.parent, operation.graveyardPosition.offset, 1);
					this._setElementState(operation.graveyardPosition.nodeAfter, "move");
				}
				break;
			}
			case "merge": {
				const mergedElement = operation.sourcePosition.parent;
				if (!this._isInInsertedElement(mergedElement.parent)) this._markRemove(mergedElement.parent, mergedElement.startOffset, 1);
				const graveyardParent = operation.graveyardPosition.parent;
				this._markInsert(graveyardParent, operation.graveyardPosition.offset, 1);
				this._setElementState(mergedElement, "move");
				const mergedIntoElement = operation.targetPosition.parent;
				if (!this._isInInsertedElement(mergedIntoElement)) {
					this._markInsert(mergedIntoElement, operation.targetPosition.offset, mergedElement.maxOffset);
					const range = ModelRange._createFromPositionAndShift(operation.sourcePosition, operation.howMany);
					for (const node of range.getItems({ shallow: true })) this._setElementState(node, "move");
				}
				break;
			}
			case "detachRoot":
			case "addRoot": {
				const root = operation.affectedSelectable;
				if (!root._isLoaded) return;
				if (root.isAttached() == operation.isAdd) return;
				this._bufferRootStateChange(operation.rootName, operation.isAdd);
				break;
			}
			case "addRootAttribute":
			case "removeRootAttribute":
			case "changeRootAttribute": {
				if (!operation.root._isLoaded) return;
				const rootName = operation.root.rootName;
				this._bufferRootAttributeChange(rootName, operation.key, operation.oldValue, operation.newValue);
				break;
			}
		}
		this._cachedChanges = null;
	}
	/**
	* Buffers a marker change.
	*
	* @param markerName The name of the marker that changed.
	* @param oldMarkerData Marker data before the change.
	* @param newMarkerData Marker data after the change.
	*/
	bufferMarkerChange(markerName, oldMarkerData, newMarkerData) {
		if (oldMarkerData.range && oldMarkerData.range.root.is("rootElement") && !oldMarkerData.range.root._isLoaded) oldMarkerData.range = null;
		if (newMarkerData.range && newMarkerData.range.root.is("rootElement") && !newMarkerData.range.root._isLoaded) newMarkerData.range = null;
		let buffered = this._changedMarkers.get(markerName);
		if (!buffered) {
			buffered = {
				newMarkerData,
				oldMarkerData
			};
			this._changedMarkers.set(markerName, buffered);
		} else buffered.newMarkerData = newMarkerData;
		if (buffered.oldMarkerData.range == null && newMarkerData.range == null) this._changedMarkers.delete(markerName);
	}
	/**
	* Returns all markers that should be removed as a result of buffered changes.
	*
	* @returns Markers to remove. Each array item is an object containing the `name` and `range` properties.
	*/
	getMarkersToRemove() {
		const result = [];
		for (const [name, change] of this._changedMarkers) if (change.oldMarkerData.range != null) result.push({
			name,
			range: change.oldMarkerData.range
		});
		return result;
	}
	/**
	* Returns all markers which should be added as a result of buffered changes.
	*
	* @returns Markers to add. Each array item is an object containing the `name` and `range` properties.
	*/
	getMarkersToAdd() {
		const result = [];
		for (const [name, change] of this._changedMarkers) if (change.newMarkerData.range != null) result.push({
			name,
			range: change.newMarkerData.range
		});
		return result;
	}
	/**
	* Returns all markers which changed.
	*/
	getChangedMarkers() {
		return Array.from(this._changedMarkers).map(([name, change]) => ({
			name,
			data: {
				oldRange: change.oldMarkerData.range,
				newRange: change.newMarkerData.range
			}
		}));
	}
	/**
	* Checks whether some of the buffered changes affect the editor data.
	*
	* Types of changes which affect the editor data:
	*
	* * model structure changes,
	* * attribute changes,
	* * a root is added or detached,
	* * changes of markers which were defined as `affectsData`,
	* * changes of markers' `affectsData` property.
	*/
	hasDataChanges() {
		if (this.getChanges().length) return true;
		if (this._changedRoots.size > 0) return true;
		for (const { newMarkerData, oldMarkerData } of this._changedMarkers.values()) {
			if (newMarkerData.affectsData !== oldMarkerData.affectsData) return true;
			if (newMarkerData.affectsData) {
				const markerAdded = newMarkerData.range && !oldMarkerData.range;
				const markerRemoved = !newMarkerData.range && oldMarkerData.range;
				const markerChanged = newMarkerData.range && oldMarkerData.range && !newMarkerData.range.isEqual(oldMarkerData.range);
				if (markerAdded || markerRemoved || markerChanged) return true;
			}
		}
		return false;
	}
	/**
	* Calculates the diff between the old model tree state (the state before the first buffered operations since the last {@link #reset}
	* call) and the new model tree state (actual one). It should be called after all buffered operations are executed.
	*
	* The diff set is returned as an array of {@link module:engine/model/differ~DifferItem diff items}, each describing a change done
	* on the model. The items are sorted by the position on which the change happened. If a position
	* {@link module:engine/model/position~ModelPosition#isBefore is before} another one, it will be on an earlier index in the diff set.
	*
	* **Note**: Elements inside inserted element will not have a separate diff item, only the top most element change will be reported.
	*
	* Because calculating the diff is a costly operation, the result is cached. If no new operation was buffered since the
	* previous {@link #getChanges} call, the next call will return the cached value.
	*
	* @param options Additional options.
	* @param options.includeChangesInGraveyard If set to `true`, also changes that happened
	* in the graveyard root will be returned. By default, changes in the graveyard root are not returned.
	* @returns Diff between the old and the new model tree state.
	*/
	getChanges(options = {}) {
		if (this._cachedChanges) if (options.includeChangesInGraveyard) return this._cachedChangesWithGraveyard.slice();
		else return this._cachedChanges.slice();
		let diffSet = [];
		for (const element of this._changesInElement.keys()) {
			const changes = this._changesInElement.get(element).sort((a, b) => {
				if (a.offset === b.offset) {
					if (a.type != b.type) return a.type == "remove" ? -1 : 1;
					return 0;
				}
				return a.offset < b.offset ? -1 : 1;
			});
			const childrenBefore = this._elementChildrenSnapshots.get(element);
			const childrenAfter = _getChildrenSnapshots(element.getChildren());
			const diffInstructions = _generateDiffInstructionsFromChanges(childrenBefore.length, changes);
			let i = 0;
			let j = 0;
			for (const instruction of diffInstructions) if (instruction === "i") {
				const action = this._getDiffActionForNode(childrenAfter[i].node, "insert");
				const childSnapshotBefore = this._elementsSnapshots.get(childrenAfter[i].node);
				const diffItem = this._getInsertDiff(element, i, action, childrenAfter[i], childSnapshotBefore);
				diffSet.push(diffItem);
				i++;
			} else if (instruction === "r") {
				const action = this._getDiffActionForNode(childrenBefore[j].node, "remove");
				const diffItem = this._getRemoveDiff(element, i, action, childrenBefore[j]);
				diffSet.push(diffItem);
				j++;
			} else if (instruction === "a") {
				const beforeAttributes = childrenBefore[j].attributes;
				const afterAttributes = childrenAfter[i].attributes;
				let range;
				if (childrenAfter[i].name == "$text") range = new ModelRange(ModelPosition._createAt(element, i), ModelPosition._createAt(element, i + 1));
				else {
					const index = element.offsetToIndex(i);
					range = new ModelRange(ModelPosition._createAt(element, i), ModelPosition._createAt(element.getChild(index), 0));
				}
				const diffItems = this._getAttributesDiff(range, beforeAttributes, afterAttributes);
				diffSet.push(...diffItems);
				i++;
				j++;
			} else {
				i++;
				j++;
			}
		}
		diffSet.sort((a, b) => {
			if (a.position.root != b.position.root) return a.position.root.rootName < b.position.root.rootName ? -1 : 1;
			if (a.position.isEqual(b.position)) return a.changeCount - b.changeCount;
			return a.position.isBefore(b.position) ? -1 : 1;
		});
		for (let i = 1, prevIndex = 0; i < diffSet.length; i++) {
			const prevDiff = diffSet[prevIndex];
			const thisDiff = diffSet[i];
			const isConsecutiveTextRemove = prevDiff.type == "remove" && thisDiff.type == "remove" && prevDiff.name == "$text" && thisDiff.name == "$text" && prevDiff.position.isEqual(thisDiff.position);
			const isConsecutiveTextAdd = prevDiff.type == "insert" && thisDiff.type == "insert" && prevDiff.name == "$text" && thisDiff.name == "$text" && prevDiff.position.parent == thisDiff.position.parent && prevDiff.position.offset + prevDiff.length == thisDiff.position.offset;
			const isConsecutiveAttributeChange = prevDiff.type == "attribute" && thisDiff.type == "attribute" && prevDiff.position.parent == thisDiff.position.parent && prevDiff.range.isFlat && thisDiff.range.isFlat && prevDiff.position.offset + prevDiff.length == thisDiff.position.offset && prevDiff.attributeKey == thisDiff.attributeKey && prevDiff.attributeOldValue == thisDiff.attributeOldValue && prevDiff.attributeNewValue == thisDiff.attributeNewValue;
			if (isConsecutiveTextRemove || isConsecutiveTextAdd || isConsecutiveAttributeChange) {
				prevDiff.length++;
				if (isConsecutiveAttributeChange) prevDiff.range.end = prevDiff.range.end.getShiftedBy(1);
				diffSet[i] = null;
			} else prevIndex = i;
		}
		diffSet = diffSet.filter((v) => v);
		for (const item of diffSet) {
			delete item.changeCount;
			if (item.type == "attribute") {
				delete item.position;
				delete item.length;
			}
		}
		this._changeCount = 0;
		this._cachedChangesWithGraveyard = diffSet;
		this._cachedChanges = diffSet.filter(_changesInGraveyardFilter);
		if (options.includeChangesInGraveyard) return this._cachedChangesWithGraveyard.slice();
		else return this._cachedChanges.slice();
	}
	/**
	* Returns all roots that have changed (either were attached, or detached, or their attributes changed).
	*
	* @returns Diff between the old and the new roots state.
	*/
	getChangedRoots() {
		return Array.from(this._changedRoots.values()).map((diffItem) => {
			const entry = { ...diffItem };
			if (entry.state !== void 0) delete entry.attributes;
			return entry;
		});
	}
	/**
	* Returns a set of model items that were marked to get refreshed.
	*/
	getRefreshedItems() {
		return new Set(this._refreshedItems);
	}
	/**
	* Resets `Differ`. Removes all buffered changes.
	*/
	reset() {
		this._changesInElement.clear();
		this._elementChildrenSnapshots.clear();
		this._elementsSnapshots.clear();
		this._elementState.clear();
		this._changedMarkers.clear();
		this._changedRoots.clear();
		this._refreshedItems.clear();
		this._cachedChanges = null;
	}
	/**
	* Marks the given `item` in differ to be "refreshed". It means that the item will be marked as removed and inserted
	* in the differ changes set, so it will be effectively re-converted when the differ changes are handled by a dispatcher.
	*
	* @internal
	* @param item Item to refresh.
	*/
	_refreshItem(item) {
		if (this._isInInsertedElement(item.parent)) return;
		this._markRemove(item.parent, item.startOffset, item.offsetSize);
		this._markInsert(item.parent, item.startOffset, item.offsetSize);
		this._refreshedItems.add(item);
		this._setElementState(item, "refresh");
		const range = ModelRange._createOn(item);
		for (const marker of this._markerCollection.getMarkersIntersectingRange(range)) {
			const markerData = marker.getData();
			this.bufferMarkerChange(marker.name, markerData, markerData);
		}
		this._cachedChanges = null;
	}
	/**
	* Buffers all the data related to given root like it was all just added to the editor.
	*
	* Following changes are buffered:
	*
	* * root is attached,
	* * all root content is inserted,
	* * all root attributes are added,
	* * all markers inside the root are added.
	*
	* @internal
	*/
	_bufferRootLoad(root) {
		if (!root.isAttached()) return;
		this._bufferRootStateChange(root.rootName, true);
		this._markInsert(root, 0, root.maxOffset);
		for (const key of root.getAttributeKeys()) this._bufferRootAttributeChange(root.rootName, key, null, root.getAttribute(key));
		for (const marker of this._markerCollection) if (marker.getRange().root == root) {
			const markerData = marker.getData();
			this.bufferMarkerChange(marker.name, {
				...markerData,
				range: null
			}, markerData);
		}
	}
	/**
	* Buffers the root state change after the root was attached or detached
	*/
	_bufferRootStateChange(rootName, isAttached) {
		if (!this._changedRoots.has(rootName)) {
			this._changedRoots.set(rootName, {
				name: rootName,
				state: isAttached ? "attached" : "detached"
			});
			return;
		}
		const diffItem = this._changedRoots.get(rootName);
		if (diffItem.state !== void 0) {
			delete diffItem.state;
			if (diffItem.attributes === void 0) this._changedRoots.delete(rootName);
		} else diffItem.state = isAttached ? "attached" : "detached";
	}
	/**
	* Buffers a root attribute change.
	*/
	_bufferRootAttributeChange(rootName, key, oldValue, newValue) {
		const diffItem = this._changedRoots.get(rootName) || { name: rootName };
		const attrs = diffItem.attributes || {};
		if (attrs[key]) {
			const attrEntry = attrs[key];
			if (newValue === attrEntry.oldValue) delete attrs[key];
			else attrEntry.newValue = newValue;
		} else attrs[key] = {
			oldValue,
			newValue
		};
		if (Object.entries(attrs).length === 0) {
			delete diffItem.attributes;
			if (diffItem.state === void 0) this._changedRoots.delete(rootName);
		} else {
			diffItem.attributes = attrs;
			this._changedRoots.set(rootName, diffItem);
		}
	}
	/**
	* Saves and handles an insert change.
	*/
	_markInsert(parent, offset, howMany) {
		if (parent.root.is("rootElement") && !parent.root._isLoaded) return;
		const changeItem = {
			type: "insert",
			offset,
			howMany,
			count: this._changeCount++
		};
		this._markChange(parent, changeItem);
	}
	/**
	* Saves and handles a remove change.
	*/
	_markRemove(parent, offset, howMany) {
		if (parent.root.is("rootElement") && !parent.root._isLoaded) return;
		const changeItem = {
			type: "remove",
			offset,
			howMany,
			count: this._changeCount++
		};
		this._markChange(parent, changeItem);
		this._removeAllNestedChanges(parent, offset, howMany);
	}
	/**
	* Saves and handles an attribute change.
	*/
	_markAttribute(item) {
		if (item.root.is("rootElement") && !item.root._isLoaded) return;
		const changeItem = {
			type: "attribute",
			offset: item.startOffset,
			howMany: item.offsetSize,
			count: this._changeCount++
		};
		this._markChange(item.parent, changeItem);
	}
	/**
	* Saves and handles a model change.
	*/
	_markChange(parent, changeItem) {
		this._makeSnapshots(parent);
		const changes = this._getChangesForElement(parent);
		this._handleChange(changeItem, changes);
		changes.push(changeItem);
		for (let i = 0; i < changes.length; i++) if (changes[i].howMany < 1) {
			changes.splice(i, 1);
			i--;
		}
	}
	/**
	* Tries to set given state for given item.
	*
	* This method does simple validation (it sets the state only for model elements, not for text proxy nodes). It also follows state
	* setting rules, that is, `'refresh'` cannot overwrite `'rename'`, and `'rename'` cannot overwrite `'move'`.
	*/
	_setElementState(node, state) {
		if (!node.is("element")) return;
		const currentStatePriority = Differ._statesPriority.indexOf(this._elementState.get(node));
		if (Differ._statesPriority.indexOf(state) > currentStatePriority) this._elementState.set(node, state);
	}
	/**
	* Returns a value for {@link ~DifferItemAction `action`} property for diff items returned by {@link ~Differ#getChanges}.
	* This method aims to return `'rename'` or `'refresh'` when it should, and `diffItemType` ("default action") in all other cases.
	*
	* It bases on a few factors:
	*
	* * for text nodes, the method always returns `diffItemType`,
	* * for newly inserted element, the method returns `diffItemType`,
	* * if {@link ~Differ#_elementState element state} was not recorded, the method returns `diffItemType`,
	* * if state was recorded, and it was `'move'` (default action), the method returns `diffItemType`,
	* * finally, if state was `'refresh'` or `'rename'`, the method returns the state value.
	*/
	_getDiffActionForNode(node, diffItemType) {
		if (!node.is("element")) return diffItemType;
		if (!this._elementsSnapshots.has(node)) return diffItemType;
		const state = this._elementState.get(node);
		if (!state || state == "move") return diffItemType;
		return state;
	}
	/**
	* Gets an array of changes that have already been saved for a given element.
	*/
	_getChangesForElement(element) {
		let changes;
		if (this._changesInElement.has(element)) changes = this._changesInElement.get(element);
		else {
			changes = [];
			this._changesInElement.set(element, changes);
		}
		return changes;
	}
	/**
	* Creates and saves a snapshot for all children of the given element.
	*/
	_makeSnapshots(element) {
		if (this._elementChildrenSnapshots.has(element)) return;
		const childrenSnapshots = _getChildrenSnapshots(element.getChildren());
		this._elementChildrenSnapshots.set(element, childrenSnapshots);
		for (const snapshot of childrenSnapshots) this._elementsSnapshots.set(snapshot.node, snapshot);
	}
	/**
	* For a given newly saved change, compares it with a change already done on the element and modifies the incoming
	* change and/or the old change.
	*
	* @param inc Incoming (new) change.
	* @param changes An array containing all the changes done on that element.
	*/
	_handleChange(inc, changes) {
		inc.nodesToHandle = inc.howMany;
		for (const old of changes) {
			const incEnd = inc.offset + inc.howMany;
			const oldEnd = old.offset + old.howMany;
			if (inc.type == "insert") {
				if (old.type == "insert") {
					if (inc.offset <= old.offset) old.offset += inc.howMany;
					else if (inc.offset < oldEnd) {
						old.howMany += inc.nodesToHandle;
						inc.nodesToHandle = 0;
					}
				}
				if (old.type == "remove") {
					if (inc.offset < old.offset) old.offset += inc.howMany;
				}
				if (old.type == "attribute") {
					if (inc.offset <= old.offset) old.offset += inc.howMany;
					else if (inc.offset < oldEnd) {
						const howMany = old.howMany;
						old.howMany = inc.offset - old.offset;
						changes.unshift({
							type: "attribute",
							offset: incEnd,
							howMany: howMany - old.howMany,
							count: this._changeCount++
						});
					}
				}
			}
			if (inc.type == "remove") {
				if (old.type == "insert") {
					if (incEnd <= old.offset) old.offset -= inc.howMany;
					else if (incEnd <= oldEnd) if (inc.offset < old.offset) {
						const intersectionLength = incEnd - old.offset;
						old.offset = inc.offset;
						old.howMany -= intersectionLength;
						inc.nodesToHandle -= intersectionLength;
					} else {
						old.howMany -= inc.nodesToHandle;
						inc.nodesToHandle = 0;
					}
					else if (inc.offset <= old.offset) {
						inc.nodesToHandle -= old.howMany;
						old.howMany = 0;
					} else if (inc.offset < oldEnd) {
						const intersectionLength = oldEnd - inc.offset;
						old.howMany -= intersectionLength;
						inc.nodesToHandle -= intersectionLength;
					}
				}
				if (old.type == "remove") {
					if (incEnd <= old.offset) old.offset -= inc.howMany;
					else if (inc.offset < old.offset) {
						inc.nodesToHandle += old.howMany;
						old.howMany = 0;
					}
				}
				if (old.type == "attribute") {
					if (incEnd <= old.offset) old.offset -= inc.howMany;
					else if (inc.offset < old.offset) {
						const intersectionLength = incEnd - old.offset;
						old.offset = inc.offset;
						old.howMany -= intersectionLength;
					} else if (inc.offset < oldEnd) if (incEnd <= oldEnd) {
						const howMany = old.howMany;
						old.howMany = inc.offset - old.offset;
						const howManyAfter = howMany - old.howMany - inc.nodesToHandle;
						changes.unshift({
							type: "attribute",
							offset: inc.offset,
							howMany: howManyAfter,
							count: this._changeCount++
						});
					} else old.howMany -= oldEnd - inc.offset;
				}
			}
			if (inc.type == "attribute") {
				if (old.type == "insert") {
					if (inc.offset < old.offset && incEnd > old.offset) {
						if (incEnd > oldEnd) {
							const attributePart = {
								type: "attribute",
								offset: oldEnd,
								howMany: incEnd - oldEnd,
								count: this._changeCount++
							};
							this._handleChange(attributePart, changes);
							changes.push(attributePart);
						}
						inc.nodesToHandle = old.offset - inc.offset;
						inc.howMany = inc.nodesToHandle;
					} else if (inc.offset >= old.offset && inc.offset < oldEnd) if (incEnd > oldEnd) {
						inc.nodesToHandle = incEnd - oldEnd;
						inc.offset = oldEnd;
					} else inc.nodesToHandle = 0;
				}
				if (old.type == "remove") {
					if (inc.offset < old.offset && incEnd > old.offset) {
						const attributePart = {
							type: "attribute",
							offset: old.offset,
							howMany: incEnd - old.offset,
							count: this._changeCount++
						};
						this._handleChange(attributePart, changes);
						changes.push(attributePart);
						inc.nodesToHandle = old.offset - inc.offset;
						inc.howMany = inc.nodesToHandle;
					}
				}
				if (old.type == "attribute") {
					if (inc.offset >= old.offset && incEnd <= oldEnd) {
						inc.nodesToHandle = 0;
						inc.howMany = 0;
						inc.offset = 0;
					} else if (inc.offset <= old.offset && incEnd >= oldEnd) old.howMany = 0;
				}
			}
		}
		inc.howMany = inc.nodesToHandle;
		delete inc.nodesToHandle;
	}
	/**
	* Returns an object with a single insert change description.
	*
	* @param parent The element in which the change happened.
	* @param offset The offset at which change happened.
	* @param action Further specifies what kind of action led to generating this change.
	* @param elementSnapshot Snapshot of the inserted node after changes.
	* @param elementSnapshotBefore Snapshot of the inserted node before changes.
	* @returns The diff item.
	*/
	_getInsertDiff(parent, offset, action, elementSnapshot, elementSnapshotBefore) {
		const diffItem = {
			type: "insert",
			position: ModelPosition._createAt(parent, offset),
			name: elementSnapshot.name,
			attributes: new Map(elementSnapshot.attributes),
			length: 1,
			changeCount: this._changeCount++,
			action
		};
		if (action != "insert" && elementSnapshotBefore) diffItem.before = {
			name: elementSnapshotBefore.name,
			attributes: new Map(elementSnapshotBefore.attributes)
		};
		return diffItem;
	}
	/**
	* Returns an object with a single remove change description.
	*
	* @param parent The element in which change happened.
	* @param offset The offset at which change happened.
	* @param action Further specifies what kind of action led to generating this change.
	* @param elementSnapshot The snapshot of the removed node before changes.
	* @returns The diff item.
	*/
	_getRemoveDiff(parent, offset, action, elementSnapshot) {
		return {
			type: "remove",
			action,
			position: ModelPosition._createAt(parent, offset),
			name: elementSnapshot.name,
			attributes: new Map(elementSnapshot.attributes),
			length: 1,
			changeCount: this._changeCount++
		};
	}
	/**
	* Returns an array of objects where each one is a single attribute change description.
	*
	* @param range The range where the change happened.
	* @param oldAttributes A map, map iterator or compatible object that contains attributes before the change.
	* @param newAttributes A map, map iterator or compatible object that contains attributes after the change.
	* @returns An array containing one or more diff items.
	*/
	_getAttributesDiff(range, oldAttributes, newAttributes) {
		const diffs = [];
		newAttributes = new Map(newAttributes);
		for (const [key, oldValue] of oldAttributes) {
			const newValue = newAttributes.has(key) ? newAttributes.get(key) : null;
			if (newValue !== oldValue) diffs.push({
				type: "attribute",
				position: range.start,
				range: range.clone(),
				length: 1,
				attributeKey: key,
				attributeOldValue: oldValue,
				attributeNewValue: newValue,
				changeCount: this._changeCount++
			});
			newAttributes.delete(key);
		}
		for (const [key, newValue] of newAttributes) diffs.push({
			type: "attribute",
			position: range.start,
			range: range.clone(),
			length: 1,
			attributeKey: key,
			attributeOldValue: null,
			attributeNewValue: newValue,
			changeCount: this._changeCount++
		});
		return diffs;
	}
	/**
	* Checks whether given element or any of its parents is an element that is buffered as an inserted element.
	*/
	_isInInsertedElement(element) {
		const parent = element.parent;
		if (!parent) return false;
		const changes = this._changesInElement.get(parent);
		const offset = element.startOffset;
		if (changes) {
			for (const change of changes) if (change.type == "insert" && offset >= change.offset && offset < change.offset + change.howMany) return true;
		}
		return this._isInInsertedElement(parent);
	}
	/**
	* Removes deeply all buffered changes that are registered in elements from range specified by `parent`, `offset`
	* and `howMany`.
	*/
	_removeAllNestedChanges(parent, offset, howMany) {
		const range = new ModelRange(ModelPosition._createAt(parent, offset), ModelPosition._createAt(parent, offset + howMany));
		for (const item of range.getItems({ shallow: true })) if (item.is("element")) {
			this._changesInElement.delete(item);
			this._removeAllNestedChanges(item, 0, item.maxOffset);
		}
	}
};
/**
* Returns a snapshot for the specified child node. Text node snapshots have the `name` property set to `$text`.
*/
function _getSingleNodeSnapshot(node) {
	return {
		node,
		name: node.is("$text") ? "$text" : node.name,
		attributes: new Map(node.getAttributes())
	};
}
/**
* Returns an array that is a copy of passed child list with the exception that text nodes are split to one or more
* objects, each representing one character and attributes set on that character.
*/
function _getChildrenSnapshots(children) {
	const snapshots = [];
	for (const child of children) if (child.is("$text")) for (let i = 0; i < child.data.length; ++i) snapshots.push(_getSingleNodeSnapshot(child));
	else snapshots.push(_getSingleNodeSnapshot(child));
	return snapshots;
}
/**
* Generates array of diff instructions for given changes set.
*
* Generated actions are:
*
* - 'e' for 'equal' - when item at that position did not change,
* - 'i' for 'insert' - when item at that position was inserted,
* - 'r' for 'remove' - when item at that position was removed,
* - 'a' for 'attribute' - when item at that position has it attributes changed.
*
* Example (assume that uppercase letters have bold attribute, compare with function code):
*
* children before:	fooBAR
* children after:	foxybAR
*
* changes: type: remove, offset: 1, howMany: 1
*			type: insert, offset: 2, howMany: 2
*			type: attribute, offset: 4, howMany: 1
*
* Expected actions: equal (f), remove (o), equal (o), insert (x), insert (y), attribute (b), equal (A), equal (R)
*
* Steps taken by the script:
*
* 1. change = "type: remove, offset: 1, howMany: 1"; offset = 0; oldChildrenHandled = 0
*    1.1 between this change and the beginning is one not-changed node, fill with one equal action, one old child has been handled
*    1.2 this change removes one node, add one remove action
*    1.3 change last visited `offset` to 1
*    1.4 since an old child has been removed, one more old child has been handled
*    1.5 actions at this point are: equal, remove
*
* 2. change = "type: insert, offset: 2, howMany: 2"; offset = 1; oldChildrenHandled = 2
*    2.1 between this change and previous change is one not-changed node, add equal action, another one old children has been handled
*    2.2 this change inserts two nodes, add two insert actions
*    2.3 change last visited offset to the end of the inserted range, that is 4
*    2.4 actions at this point are: equal, remove, equal, insert, insert
*
* 3. change = "type: attribute, offset: 4, howMany: 1"; offset = 4, oldChildrenHandled = 3
*    3.1 between this change and previous change are no not-changed nodes
*    3.2 this change changes one node, add one attribute action
*    3.3 change last visited `offset` to the end of change range, that is 5
*    3.4 since an old child has been changed, one more old child has been handled
*    3.5 actions at this point are: equal, remove, equal, insert, insert, attribute
*
* 4. after loop oldChildrenHandled = 4, oldChildrenLength = 6 (fooBAR is 6 characters)
*    4.1 fill up with two equal actions
*
* The result actions are: equal, remove, equal, insert, insert, attribute, equal, equal.
*/
function _generateDiffInstructionsFromChanges(oldChildrenLength, changes) {
	const diff = [];
	let offset = 0;
	let oldChildrenHandled = 0;
	for (const change of changes) {
		if (change.offset > offset) {
			for (let i = 0; i < change.offset - offset; i++) diff.push("e");
			oldChildrenHandled += change.offset - offset;
		}
		if (change.type == "insert") {
			for (let i = 0; i < change.howMany; i++) diff.push("i");
			offset = change.offset + change.howMany;
		} else if (change.type == "remove") {
			for (let i = 0; i < change.howMany; i++) diff.push("r");
			offset = change.offset;
			oldChildrenHandled += change.howMany;
		} else {
			if (change.howMany > 1500) for (let i = 0; i < change.howMany; i++) diff.push("a");
			else diff.push(..."a".repeat(change.howMany).split(""));
			offset = change.offset + change.howMany;
			oldChildrenHandled += change.howMany;
		}
	}
	if (oldChildrenHandled < oldChildrenLength) for (let i = 0; i < oldChildrenLength - oldChildrenHandled - offset; i++) diff.push("e");
	return diff;
}
/**
* Filter callback for `Array.filter` that filters out change entries that are in graveyard.
*/
function _changesInGraveyardFilter(entry) {
	const posInGy = "position" in entry && entry.position.root.rootName == "$graveyard";
	const rangeInGy = "range" in entry && entry.range.root.rootName == "$graveyard";
	return !posInGy && !rangeInGy;
}

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* `History` keeps the track of all the operations applied to the {@link module:engine/model/document~ModelDocument document}.
*/
var History = class {
	/**
	* Operations added to the history.
	*/
	_operations = [];
	/**
	* Holds an information which {@link module:engine/model/operation/operation~Operation operation} undoes which
	* {@link module:engine/model/operation/operation~Operation operation}.
	*
	* Keys of the map are "undoing operations", that is operations that undone some other operations. For each key, the
	* value is an operation that has been undone by the "undoing operation".
	*/
	_undoPairs = /* @__PURE__ */ new Map();
	/**
	* Holds all undone operations.
	*/
	_undoneOperations = /* @__PURE__ */ new Set();
	/**
	* A map that allows retrieving the operations fast based on the given base version.
	*/
	_baseVersionToOperationIndex = /* @__PURE__ */ new Map();
	/**
	* The history version.
	*/
	_version = 0;
	/**
	* The gap pairs kept in the <from,to> format.
	*
	* Anytime the `history.version` is set to a version larger than `history.version + 1`,
	* a new <lastHistoryVersion, newHistoryVersion> entry is added to the map.
	*/
	_gaps = /* @__PURE__ */ new Map();
	/**
	* The version of the last operation in the history.
	*
	* The history version is incremented automatically when a new operation is added to the history.
	* Setting the version manually should be done only in rare circumstances when a gap is planned
	* between history versions. When doing so, a gap will be created and the history will accept adding
	* an operation with base version equal to the new history version.
	*/
	get version() {
		return this._version;
	}
	set version(version) {
		if (this._operations.length && version > this._version + 1) this._gaps.set(this._version, version);
		this._version = version;
	}
	/**
	* The last history operation.
	*/
	get lastOperation() {
		return this._operations[this._operations.length - 1];
	}
	/**
	* Adds an operation to the history and increments the history version.
	*
	* The operation's base version should be equal to the history version. Otherwise an error is thrown.
	*/
	addOperation(operation) {
		if (operation.baseVersion !== this.version)
 /**
		* Only operations with matching versions can be added to the history.
		*
		* @error model-document-history-addoperation-incorrect-version
		* @param {module:engine/model/operation/operation~Operation} operation The current operation.
		* @param {number} historyVersion The current document history version.
		*/
		throw new CKEditorError("model-document-history-addoperation-incorrect-version", this, {
			operation,
			historyVersion: this.version
		});
		this._operations.push(operation);
		this._version++;
		this._baseVersionToOperationIndex.set(operation.baseVersion, this._operations.length - 1);
	}
	/**
	* Returns operations from the given range of operation base versions that were added to the history.
	*
	* Note that there may be gaps in operations base versions.
	*
	* @param fromBaseVersion Base version from which operations should be returned (inclusive).
	* @param toBaseVersion Base version up to which operations should be returned (exclusive).
	* @returns History operations for the given range, in chronological order.
	*/
	getOperations(fromBaseVersion, toBaseVersion = this.version) {
		if (!this._operations.length) return [];
		const firstOperation = this._operations[0];
		if (fromBaseVersion === void 0) fromBaseVersion = firstOperation.baseVersion;
		let inclusiveTo = toBaseVersion - 1;
		for (const [gapFrom, gapTo] of this._gaps) {
			if (fromBaseVersion > gapFrom && fromBaseVersion < gapTo) fromBaseVersion = gapTo;
			if (inclusiveTo > gapFrom && inclusiveTo < gapTo) inclusiveTo = gapFrom - 1;
		}
		if (inclusiveTo < firstOperation.baseVersion || fromBaseVersion > this.lastOperation.baseVersion) return [];
		let fromIndex = this._baseVersionToOperationIndex.get(fromBaseVersion);
		if (fromIndex === void 0) fromIndex = 0;
		let toIndex = this._baseVersionToOperationIndex.get(inclusiveTo);
		if (toIndex === void 0) toIndex = this._operations.length - 1;
		return this._operations.slice(fromIndex, toIndex + 1);
	}
	/**
	* Returns operation from the history that bases on given `baseVersion`.
	*
	* @param baseVersion Base version of the operation to get.
	* @returns Operation with given base version or `undefined` if there is no such operation in history.
	*/
	getOperation(baseVersion) {
		const operationIndex = this._baseVersionToOperationIndex.get(baseVersion);
		if (operationIndex === void 0) return;
		return this._operations[operationIndex];
	}
	/**
	* Marks in history that one operation is an operation that is undoing the other operation. By marking operation this way,
	* history is keeping more context information about operations, which helps in operational transformation.
	*
	* @param undoneOperation Operation which is undone by `undoingOperation`.
	* @param undoingOperation Operation which undoes `undoneOperation`.
	*/
	setOperationAsUndone(undoneOperation, undoingOperation) {
		this._undoPairs.set(undoingOperation, undoneOperation);
		this._undoneOperations.add(undoneOperation);
	}
	/**
	* Checks whether given `operation` is undoing any other operation.
	*
	* @param operation Operation to check.
	* @returns `true` if given `operation` is undoing any other operation, `false` otherwise.
	*/
	isUndoingOperation(operation) {
		return this._undoPairs.has(operation);
	}
	/**
	* Checks whether given `operation` has been undone by any other operation.
	*
	* @param operation Operation to check.
	* @returns `true` if given `operation` has been undone any other operation, `false` otherwise.
	*/
	isUndoneOperation(operation) {
		return this._undoneOperations.has(operation);
	}
	/**
	* For given `undoingOperation`, returns the operation which has been undone by it.
	*
	* @returns Operation that has been undone by given `undoingOperation` or `undefined`
	* if given `undoingOperation` is not undoing any other operation.
	*/
	getUndoneOperation(undoingOperation) {
		return this._undoPairs.get(undoingOperation);
	}
	/**
	* Resets the history of operations.
	*/
	reset() {
		this._version = 0;
		this._undoPairs = /* @__PURE__ */ new Map();
		this._operations = [];
		this._undoneOperations = /* @__PURE__ */ new Set();
		this._gaps = /* @__PURE__ */ new Map();
		this._baseVersionToOperationIndex = /* @__PURE__ */ new Map();
	}
};

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* @module engine/model/rootelement
*/
/**
* Type of {@link module:engine/model/element~ModelElement} that is a root of a model tree.
*/
var ModelRootElement = class extends ModelElement {
	/**
	* Unique root name used to identify this root element by {@link module:engine/model/document~ModelDocument}.
	*/
	rootName;
	/**
	* Document that is an owner of this root.
	*/
	_document;
	/**
	* @internal
	*/
	_isAttached = true;
	/**
	* Informs if the root element is loaded (default).
	*
	* @internal
	*/
	_isLoaded = true;
	/**
	* Creates root element.
	*
	* @param document Document that is an owner of this root.
	* @param name Node name.
	* @param rootName Unique root name used to identify this root element by {@link module:engine/model/document~ModelDocument}.
	*/
	constructor(document, name, rootName = "main") {
		super(name);
		this._document = document;
		this.rootName = rootName;
	}
	/**
	* {@link module:engine/model/document~ModelDocument Document} that owns this root element.
	*/
	get document() {
		return this._document;
	}
	/**
	* Informs if the root element is currently attached to the document, or not.
	*
	* A detached root is equivalent to being removed and cannot contain any children or markers.
	*
	* By default, a newly added root is attached. It can be detached using
	* {@link module:engine/model/writer~ModelWriter#detachRoot `Writer#detachRoot`}. A detached root can be re-attached again using
	* {@link module:engine/model/writer~ModelWriter#addRoot `Writer#addRoot`}.
	*/
	isAttached() {
		return this._isAttached;
	}
	/**
	* Converts `RootElement` instance to `string` containing its name.
	*
	* @returns `RootElement` instance converted to `string`.
	*/
	toJSON() {
		return this.rootName;
	}
};
ModelRootElement.prototype.is = function(type, name) {
	if (!name) return type === "rootElement" || type === "model:rootElement" || type === "element" || type === "model:element" || type === "node" || type === "model:node";
	return name === this.name && (type === "rootElement" || type === "model:rootElement" || type === "element" || type === "model:element");
};

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* @module engine/model/document
*/
const graveyardName = "$graveyard";
const ModelDocumentBase = /* #__PURE__ */ EmitterMixin();
/**
* Data model's document. It contains the model's structure, its selection and the history of changes.
*
* Read more about working with the model in
* {@glink framework/architecture/editing-engine#model introduction to the the editing engine's architecture}.
*
* Usually, the document contains just one {@link module:engine/model/document~ModelDocument#roots root element}, so
* you can retrieve it by just calling {@link module:engine/model/document~ModelDocument#getRoot} without specifying its name:
*
* ```ts
* model.document.getRoot(); // -> returns the main root
* ```
*
* However, the document may contain multiple roots – e.g. when the editor has multiple editable areas
* (e.g. a title and a body of a message).
*/
var ModelDocument = class extends ModelDocumentBase {
	/**
	* The {@link module:engine/model/model~Model model} that the document is a part of.
	*/
	model;
	/**
	* The document's history.
	*/
	history;
	/**
	* The selection in this document.
	*/
	selection;
	/**
	* A list of roots that are owned and managed by this document. Use {@link #createRoot}, {@link #getRoot} and
	* {@link #getRootNames} to manipulate it.
	*/
	roots;
	/**
	* The model differ object. Its role is to buffer changes done on the model document and then calculate a diff of those changes.
	*/
	differ;
	/**
	* Defines whether the document is in a read-only mode.
	*
	* The user should not be able to change the data of a document that is read-only.
	*
	* @readonly
	*/
	isReadOnly;
	/**
	* Post-fixer callbacks registered to the model document.
	*/
	_postFixers;
	/**
	* A flag that indicates whether the selection has changed since last change block.
	*/
	_hasSelectionChangedFromTheLastChangeBlock;
	/**
	* Creates an empty document instance with no {@link #roots} (other than
	* the {@link #graveyard graveyard root}).
	*/
	constructor(model) {
		super();
		this.model = model;
		this.history = new History();
		this.selection = new ModelDocumentSelection(this);
		this.roots = new Collection({ idProperty: "rootName" });
		this.differ = new Differ(model.markers);
		this.isReadOnly = false;
		this._postFixers = /* @__PURE__ */ new Set();
		this._hasSelectionChangedFromTheLastChangeBlock = false;
		this.createRoot("$root", graveyardName);
		this.listenTo(model, "applyOperation", (evt, args) => {
			const operation = args[0];
			if (operation.isDocumentOperation) this.differ.bufferOperation(operation);
		}, { priority: "high" });
		this.listenTo(model, "applyOperation", (evt, args) => {
			const operation = args[0];
			if (operation.isDocumentOperation) this.history.addOperation(operation);
		}, { priority: "low" });
		this.listenTo(this.selection, "change", () => {
			this._hasSelectionChangedFromTheLastChangeBlock = true;
		});
		this.listenTo(model.markers, "update", (evt, marker, oldRange, newRange, oldMarkerData) => {
			const newMarkerData = {
				...marker.getData(),
				range: newRange
			};
			this.differ.bufferMarkerChange(marker.name, oldMarkerData, newMarkerData);
			if (oldRange === null) marker.on("change", (evt, oldRange) => {
				const markerData = marker.getData();
				this.differ.bufferMarkerChange(marker.name, {
					...markerData,
					range: oldRange
				}, markerData);
			});
		});
		this.registerPostFixer((writer) => {
			let result = false;
			for (const root of this.roots) if (!root.isAttached() && !root.isEmpty) {
				writer.remove(writer.createRangeIn(root));
				result = true;
			}
			for (const marker of this.model.markers) if (!marker.getRange().root.isAttached()) {
				writer.removeMarker(marker);
				result = true;
			}
			return result;
		});
	}
	/**
	* The document version. Every applied operation increases the version number. It is used to
	* ensure that operations are applied on a proper document version.
	*
	* This property is equal to {@link module:engine/model/history~History#version `model.Document#history#version`}.
	*
	* If the {@link module:engine/model/operation/operation~Operation#baseVersion base version} does not match the document version,
	* a {@link module:utils/ckeditorerror~CKEditorError model-document-applyoperation-wrong-version} error is thrown.
	*/
	get version() {
		return this.history.version;
	}
	set version(version) {
		this.history.version = version;
	}
	/**
	* The graveyard tree root. A document always has a graveyard root that stores removed nodes.
	*/
	get graveyard() {
		return this.getRoot(graveyardName);
	}
	/**
	* Creates a new root.
	*
	* **Note:** do not use this method after the editor has been initialized! If you want to dynamically add a root, use
	* {@link module:engine/model/writer~ModelWriter#addRoot `model.Writer#addRoot`} instead.
	*
	* **Note:** The default `elementName` value is `'$root'`. When the editor uses a custom root
	* {@link module:core/editor/editorconfig~RootConfig#modelElement `modelElement`}, pass the configured model element
	* name explicitly so the created root matches the schema the features expect.
	* See the {@glink framework/deep-dive/schema#custom-root-elements Custom root elements} section of the
	* {@glink framework/deep-dive/schema Schema deep-dive} guide for more details.
	*
	* @param elementName The element name. Defaults to `'$root'` which also has some basic schema defined
	* (e.g. `$block` elements are allowed inside the `$root`). Make sure to define a proper schema if you use a different name.
	* @param rootName A unique root name.
	* @returns The created root.
	*/
	createRoot(elementName = "$root", rootName = "main") {
		if (this.roots.get(rootName))
 /**
		* A root with the specified name already exists.
		*
		* @error model-document-createroot-name-exists
		*/
		throw new CKEditorError("model-document-createroot-name-exists", this, { name: rootName });
		const root = new ModelRootElement(this, elementName, rootName);
		this.roots.add(root);
		return root;
	}
	/**
	* Removes all event listeners set by the document instance.
	*/
	destroy() {
		this.selection.destroy();
		this.stopListening();
	}
	/**
	* Returns a root by its name.
	*
	* Detached roots are returned by this method. This is to be able to operate on the detached root (for example, to be able to create
	* a position inside such a root for undo feature purposes).
	*
	* @param name The root name of the root to return.
	* @returns The root registered under a given name or `null` when there is no root with the given name.
	*/
	getRoot(name = "main") {
		return this.roots.get(name);
	}
	/**
	* Returns an array with names of all roots added to the document (except the {@link #graveyard graveyard root}).
	*
	* Detached roots **are not** returned by this method by default. This is to make sure that all features or algorithms that operate
	* on the document data know which roots are still a part of the document and should be processed.
	*
	* @param includeDetached Specified whether detached roots should be returned as well.
	*/
	getRootNames(includeDetached = false) {
		return this.getRoots(includeDetached).map((root) => root.rootName);
	}
	/**
	* Returns an array with all roots added to the document (except the {@link #graveyard graveyard root}).
	*
	* Detached roots **are not** returned by this method by default. This is to make sure that all features or algorithms that operate
	* on the document data know which roots are still a part of the document and should be processed.
	*
	* @param includeDetached Specified whether detached roots should be returned as well.
	*/
	getRoots(includeDetached = false) {
		return this.roots.filter((root) => root != this.graveyard && (includeDetached || root.isAttached()) && root._isLoaded);
	}
	/**
	* Used to register a post-fixer callback. A post-fixer mechanism guarantees that the features
	* will operate on a correct model state.
	*
	* An execution of a feature may lead to an incorrect document tree state. The callbacks are used to fix the document tree after
	* it has changed. Post-fixers are fired just after all changes from the outermost change block were applied but
	* before the {@link module:engine/model/document~ModelDocument#event:change change event} is fired. If a post-fixer callback made
	* a change, it should return `true`. When this happens, all post-fixers are fired again to check if something else should
	* not be fixed in the new document tree state.
	*
	* As a parameter, a post-fixer callback receives a {@link module:engine/model/writer~ModelWriter writer} instance connected with the
	* executed changes block. Thanks to that, all changes done by the callback will be added to the same
	* {@link module:engine/model/batch~Batch batch} (and undo step) as the original changes. This makes post-fixer changes transparent
	* for the user.
	*
	* An example of a post-fixer is a callback that checks if all the data were removed from the editor. If so, the
	* callback should add an empty paragraph so that the editor is never empty:
	*
	* ```ts
	* document.registerPostFixer( writer => {
	* 	const changes = document.differ.getChanges();
	*
	* 	// Check if the changes lead to an empty root in the editor.
	* 	for ( const entry of changes ) {
	* 		if ( entry.type == 'remove' && entry.position.root.isEmpty ) {
	* 			writer.insertElement( 'paragraph', entry.position.root, 0 );
	*
	* 			// It is fine to return early, even if multiple roots would need to be fixed.
	* 			// All post-fixers will be fired again, so if there are more empty roots, those will be fixed, too.
	* 			return true;
	* 		}
	* 	}
	*
	* 	return false;
	* } );
	* ```
	*/
	registerPostFixer(postFixer) {
		this._postFixers.add(postFixer);
	}
	/**
	* A custom `toJSON()` method to solve child-parent circular dependencies.
	*
	* @returns A clone of this object with the document property changed to a string.
	*/
	toJSON() {
		const json = clone(this);
		json.selection = "[engine.model.DocumentSelection]";
		json.model = "[engine.model.Model]";
		return json;
	}
	/**
	* Check if there were any changes done on document, and if so, call post-fixers,
	* fire `change` event for features and conversion and then reset the differ.
	* Fire `change:data` event when at least one operation or buffered marker changes the data.
	*
	* @internal
	* @fires change
	* @fires change:data
	* @param writer The writer on which post-fixers will be called.
	*/
	_handleChangeBlock(writer) {
		if (this._hasDocumentChangedFromTheLastChangeBlock()) {
			this._callPostFixers(writer);
			this.selection.refresh();
			if (this.differ.hasDataChanges()) this.fire("change:data", writer.batch);
			else this.fire("change", writer.batch);
			this.selection.refresh();
			this.differ.reset();
		}
		this._hasSelectionChangedFromTheLastChangeBlock = false;
	}
	/**
	* Returns whether there is a buffered change or if the selection has changed from the last
	* {@link module:engine/model/model~Model#enqueueChange `enqueueChange()` block}
	* or {@link module:engine/model/model~Model#change `change()` block}.
	*
	* @returns Returns `true` if document has changed from the last `change()` or `enqueueChange()` block.
	*/
	_hasDocumentChangedFromTheLastChangeBlock() {
		return !this.differ.isEmpty || this._hasSelectionChangedFromTheLastChangeBlock;
	}
	/**
	* Returns the default root for this document which is either the first root that was added to the document using
	* {@link #createRoot} or the {@link #graveyard graveyard root} if no other roots were created.
	*
	* @returns The default root for this document.
	*/
	_getDefaultRoot() {
		const roots = this.getRoots();
		return roots.length ? roots[0] : this.graveyard;
	}
	/**
	* Returns the default range for this selection. The default range is a collapsed range that starts and ends
	* at the beginning of this selection's document {@link #_getDefaultRoot default root}.
	*
	* @internal
	*/
	_getDefaultRange() {
		const defaultRoot = this._getDefaultRoot();
		const model = this.model;
		const schema = model.schema;
		const position = model.createPositionFromPath(defaultRoot, [0]);
		return schema.getNearestSelectionRange(position) || model.createRange(position);
	}
	/**
	* Checks whether a given {@link module:engine/model/range~ModelRange range} is a valid range for
	* the {@link #selection document's selection}.
	*
	* @internal
	* @param range A range to check.
	* @returns `true` if `range` is valid, `false` otherwise.
	*/
	_validateSelectionRange(range) {
		return range.start.isValid() && range.end.isValid() && validateTextNodePosition(range.start) && validateTextNodePosition(range.end);
	}
	/**
	* Performs post-fixer loops. Executes post-fixer callbacks as long as none of them has done any changes to the model.
	*
	* @param writer The writer on which post-fixer callbacks will be called.
	*/
	_callPostFixers(writer) {
		let wasFixed = false;
		do
			for (const callback of this._postFixers) {
				this.selection.refresh();
				wasFixed = callback(writer);
				if (wasFixed) break;
			}
		while (wasFixed);
	}
};
/**
* Checks whether given range boundary position is valid for document selection, meaning that is not between
* unicode surrogate pairs or base character and combining marks.
*/
function validateTextNodePosition(rangeBoundary) {
	const textNode = rangeBoundary.textNode;
	if (textNode) {
		const data = textNode.data;
		const offset = rangeBoundary.offset - textNode.startOffset;
		return !isInsideSurrogatePair(data, offset) && !isInsideCombinedSymbol(data, offset);
	}
	return true;
}

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* @module engine/model/markercollection
*/
const MarkerCollectionBase = /* #__PURE__ */ EmitterMixin();
const MarkerBase = /* #__PURE__ */ EmitterMixin(ModelTypeCheckable);
/**
* The collection of all {@link module:engine/model/markercollection~Marker markers} attached to the document.
* It lets you {@link module:engine/model/markercollection~MarkerCollection#get get} markers or track them using
* {@link module:engine/model/markercollection~MarkerCollection#event:update} event.
*
* To create, change or remove makers use {@link module:engine/model/writer~ModelWriter model writers'} methods:
* {@link module:engine/model/writer~ModelWriter#addMarker} or {@link module:engine/model/writer~ModelWriter#removeMarker}. Since
* the writer is the only proper way to change the data model it is not possible to change markers directly using this
* collection. All markers created by the writer will be automatically added to this collection.
*
* By default there is one marker collection available as {@link module:engine/model/model~Model#markers model property}.
*
* @see module:engine/model/markercollection~Marker
*/
var MarkerCollection = class extends MarkerCollectionBase {
	/**
	* Stores {@link ~Marker markers} added to the collection.
	*/
	_markers = /* @__PURE__ */ new Map();
	/**
	* Iterable interface.
	*
	* Iterates over all {@link ~Marker markers} added to the collection.
	*/
	[Symbol.iterator]() {
		return this._markers.values();
	}
	/**
	* Checks if given {@link ~Marker marker} or marker name is in the collection.
	*
	* @param markerOrName Name of marker or marker instance to check.
	* @returns `true` if marker is in the collection, `false` otherwise.
	*/
	has(markerOrName) {
		const markerName = markerOrName instanceof Marker ? markerOrName.name : markerOrName;
		return this._markers.has(markerName);
	}
	/**
	* Returns {@link ~Marker marker} with given `markerName`.
	*
	* @param markerName Name of marker to get.
	* @returns Marker with given name or `null` if such marker was
	* not added to the collection.
	*/
	get(markerName) {
		return this._markers.get(markerName) || null;
	}
	/**
	* Creates and adds a {@link ~Marker marker} to the `MarkerCollection` with given name on given
	* {@link module:engine/model/range~ModelRange range}.
	*
	* If `MarkerCollection` already had a marker with given name (or {@link ~Marker marker} was passed), the marker in
	* collection is updated and {@link module:engine/model/markercollection~MarkerCollection#event:update} event is fired
	* but only if there was a change (marker range or {@link module:engine/model/markercollection~Marker#managedUsingOperations}
	* flag has changed.
	*
	* @internal
	* @fires update
	* @param markerOrName Name of marker to set or marker instance to update.
	* @param range Marker range.
	* @param managedUsingOperations Specifies whether the marker is managed using operations.
	* @param affectsData Specifies whether the marker affects the data produced by the data pipeline
	* (is persisted in the editor's data).
	* @returns `Marker` instance which was added or updated.
	*/
	_set(markerOrName, range, managedUsingOperations = false, affectsData = false) {
		const markerName = markerOrName instanceof Marker ? markerOrName.name : markerOrName;
		if (markerName.includes(","))
 /**
		* Marker name cannot contain the "," character.
		*
		* @error markercollection-incorrect-marker-name
		*/
		throw new CKEditorError("markercollection-incorrect-marker-name", this);
		const oldMarker = this._markers.get(markerName);
		if (oldMarker) {
			const oldMarkerData = oldMarker.getData();
			const oldRange = oldMarker.getRange();
			let hasChanged = false;
			if (!oldRange.isEqual(range)) {
				oldMarker._attachLiveRange(ModelLiveRange.fromRange(range));
				hasChanged = true;
			}
			if (managedUsingOperations != oldMarker.managedUsingOperations) {
				oldMarker._managedUsingOperations = managedUsingOperations;
				hasChanged = true;
			}
			if (typeof affectsData === "boolean" && affectsData != oldMarker.affectsData) {
				oldMarker._affectsData = affectsData;
				hasChanged = true;
			}
			if (hasChanged) this.fire(`update:${markerName}`, oldMarker, oldRange, range, oldMarkerData);
			return oldMarker;
		}
		const marker = new Marker(markerName, ModelLiveRange.fromRange(range), managedUsingOperations, affectsData);
		this._markers.set(markerName, marker);
		this.fire(`update:${markerName}`, marker, null, range, {
			...marker.getData(),
			range: null
		});
		return marker;
	}
	/**
	* Removes given {@link ~Marker marker} or a marker with given name from the `MarkerCollection`.
	*
	* @internal
	* @fires update
	* @param markerOrName Marker or name of a marker to remove.
	* @returns `true` if marker was found and removed, `false` otherwise.
	*/
	_remove(markerOrName) {
		const markerName = markerOrName instanceof Marker ? markerOrName.name : markerOrName;
		const oldMarker = this._markers.get(markerName);
		if (oldMarker) {
			this._markers.delete(markerName);
			this.fire(`update:${markerName}`, oldMarker, oldMarker.getRange(), null, oldMarker.getData());
			this._destroyMarker(oldMarker);
			return true;
		}
		return false;
	}
	/**
	* Fires an {@link module:engine/model/markercollection~MarkerCollection#event:update} event for the given {@link ~Marker marker}
	* but does not change the marker. Useful to force {@link module:engine/conversion/downcastdispatcher~DowncastDispatcher downcast
	* conversion} for the marker.
	*
	* @internal
	* @fires update
	* @param markerOrName Marker or name of a marker to refresh.
	*/
	_refresh(markerOrName) {
		const markerName = markerOrName instanceof Marker ? markerOrName.name : markerOrName;
		const marker = this._markers.get(markerName);
		if (!marker)
 /**
		* Marker with provided name does not exists.
		*
		* @error markercollection-refresh-marker-not-exists
		*/
		throw new CKEditorError("markercollection-refresh-marker-not-exists", this);
		const range = marker.getRange();
		this.fire(`update:${markerName}`, marker, range, range, marker.getData());
	}
	/**
	* Returns iterator that iterates over all markers, which ranges
	* contain given {@link module:engine/model/position~ModelPosition position}.
	*/
	*getMarkersAtPosition(position) {
		for (const marker of this) if (marker.getRange().containsPosition(position)) yield marker;
	}
	/**
	* Returns iterator that iterates over all markers, which intersects with given {@link module:engine/model/range~ModelRange range}.
	*/
	*getMarkersIntersectingRange(range) {
		for (const marker of this) if (marker.getRange().getIntersection(range) !== null) yield marker;
	}
	/**
	* Destroys marker collection and all markers inside it.
	*/
	destroy() {
		for (const marker of this._markers.values()) this._destroyMarker(marker);
		this._markers = null;
		this.stopListening();
	}
	/**
	* Iterates over all markers that starts with given `prefix`.
	*
	* ```ts
	* const markerFooA = markersCollection._set( 'foo:a', rangeFooA );
	* const markerFooB = markersCollection._set( 'foo:b', rangeFooB );
	* const markerBarA = markersCollection._set( 'bar:a', rangeBarA );
	* const markerFooBarA = markersCollection._set( 'foobar:a', rangeFooBarA );
	* Array.from( markersCollection.getMarkersGroup( 'foo' ) ); // [ markerFooA, markerFooB ]
	* Array.from( markersCollection.getMarkersGroup( 'a' ) ); // []
	* ```
	*/
	*getMarkersGroup(prefix) {
		for (const marker of this._markers.values()) if (marker.name.startsWith(prefix + ":")) yield marker;
	}
	/**
	* Destroys the marker.
	*/
	_destroyMarker(marker) {
		marker.stopListening();
		marker._detachLiveRange();
	}
};
/**
* `Marker` is a continuous part of the model (like a range), is named and represents some kind of information about the
* marked part of the model document. In contrary to {@link module:engine/model/node~ModelNode nodes}, which are building blocks of
* the model document tree, markers are not stored directly in the document tree but in the
* {@link module:engine/model/model~Model#markers model markers' collection}. Still, they are document data, by giving
* additional meaning to the part of a model document between marker start and marker end.
*
* In this sense, markers are similar to adding and converting attributes on nodes. The difference is that attribute is
* connected with a given node (e.g. a character is bold no matter if it gets moved or content around it changes).
* Markers on the other hand are continuous ranges and are characterized by their start and end position. This means that
* any character in the marker is marked by the marker. For example, if a character is moved outside of marker it stops being
* "special" and the marker is shrunk. Similarly, when a character is moved into the marker from other place in document
* model, it starts being "special" and the marker is enlarged.
*
* Another upside of markers is that finding marked part of document is fast and easy. Using attributes to mark some nodes
* and then trying to find that part of document would require traversing whole document tree. Marker gives instant access
* to the range which it is marking at the moment.
*
* Markers are built from a name and a range.
*
* Range of the marker is updated automatically when document changes, using
* {@link module:engine/model/liverange~ModelLiveRange live range} mechanism.
*
* Name is used to group and identify markers. Names have to be unique, but markers can be grouped by
* using common prefixes, separated with `:`, for example: `user:john` or `search:3`. That's useful in term of creating
* namespaces for custom elements (e.g. comments, highlights). You can use this prefixes in
* {@link module:engine/model/markercollection~MarkerCollection#event:update} listeners to listen on changes in a group of markers.
* For instance: `model.markers.on( 'update:user', callback );` will be called whenever any `user:*` markers changes.
*
* There are two types of markers.
*
* 1. Markers managed directly, without using operations. They are added directly by {@link module:engine/model/writer~ModelWriter}
* to the {@link module:engine/model/markercollection~MarkerCollection} without any additional mechanism. They can be used
* as bookmarks or visual markers. They are great for showing results of the find, or select link when the focus is in the input.
*
* 1. Markers managed using operations. These markers are also stored in {@link module:engine/model/markercollection~MarkerCollection}
* but changes in these markers is managed the same way all other changes in the model structure - using operations.
* Therefore, they are handled in the undo stack and synchronized between clients if the collaboration plugin is enabled.
* This type of markers is useful for solutions like spell checking or comments.
*
* Both type of them should be added / updated by {@link module:engine/model/writer~ModelWriter#addMarker}
* and removed by {@link module:engine/model/writer~ModelWriter#removeMarker} methods.
*
* ```ts
* model.change( ( writer ) => {
* 	const marker = writer.addMarker( name, { range, usingOperation: true } );
*
* 	// ...
*
* 	writer.removeMarker( marker );
* } );
* ```
*
* See {@link module:engine/model/writer~ModelWriter} to find more examples.
*
* Since markers need to track change in the document, for efficiency reasons, it is best to create and keep as little
* markers as possible and remove them as soon as they are not needed anymore.
*
* Markers can be downcasted and upcasted.
*
* Markers downcast happens on {@link module:engine/conversion/downcastdispatcher~DowncastDispatcher#event:addMarker} and
* {@link module:engine/conversion/downcastdispatcher~DowncastDispatcher#event:removeMarker} events.
* Use {@link module:engine/conversion/downcasthelpers downcast converters} or attach a custom converter to mentioned events.
* For {@link module:engine/controller/datacontroller~DataController data pipeline}, marker should be downcasted to an element.
* Then, it can be upcasted back to a marker. Again, use {@link module:engine/conversion/upcasthelpers upcast converters} or
* attach a custom converter to {@link module:engine/conversion/upcastdispatcher~UpcastDispatcher#event:element}.
*
* `Marker` instances are created and destroyed only by {@link ~MarkerCollection MarkerCollection}.
*/
var Marker = class extends MarkerBase {
	/**
	* Marker's name.
	*/
	name;
	/**
	* Flag indicates if the marker is managed using operations or not.
	*
	* @internal
	*/
	_managedUsingOperations;
	/**
	* Specifies whether the marker affects the data produced by the data pipeline
	* (is persisted in the editor's data).
	*
	* @internal
	*/
	_affectsData;
	/**
	* Range marked by the marker.
	*/
	_liveRange;
	/**
	* Creates a marker instance.
	*
	* @param name Marker name.
	* @param liveRange Range marked by the marker.
	* @param managedUsingOperations Specifies whether the marker is managed using operations.
	* @param affectsData Specifies whether the marker affects the data produced by the data pipeline (is persisted in the editor's data).
	*/
	constructor(name, liveRange, managedUsingOperations, affectsData) {
		super();
		this.name = name;
		this._liveRange = this._attachLiveRange(liveRange);
		this._managedUsingOperations = managedUsingOperations;
		this._affectsData = affectsData;
	}
	/**
	* A value indicating if the marker is managed using operations.
	* See {@link ~Marker marker class description} to learn more about marker types.
	* See {@link module:engine/model/writer~ModelWriter#addMarker}.
	*/
	get managedUsingOperations() {
		if (!this._liveRange) throw new CKEditorError("marker-destroyed", this);
		return this._managedUsingOperations;
	}
	/**
	* A value indicating if the marker changes the data.
	*/
	get affectsData() {
		if (!this._liveRange) throw new CKEditorError("marker-destroyed", this);
		return this._affectsData;
	}
	/**
	* Returns the marker data (properties defining the marker).
	*/
	getData() {
		return {
			range: this.getRange(),
			affectsData: this.affectsData,
			managedUsingOperations: this.managedUsingOperations
		};
	}
	/**
	* Returns current marker start position.
	*/
	getStart() {
		if (!this._liveRange) throw new CKEditorError("marker-destroyed", this);
		return this._liveRange.start.clone();
	}
	/**
	* Returns current marker end position.
	*/
	getEnd() {
		if (!this._liveRange) throw new CKEditorError("marker-destroyed", this);
		return this._liveRange.end.clone();
	}
	/**
	* Returns a range that represents the current state of the marker.
	*
	* Keep in mind that returned value is a {@link module:engine/model/range~ModelRange Range}, not a
	* {@link module:engine/model/liverange~ModelLiveRange ModelLiveRange}. This means that it is up-to-date and relevant only
	* until next model document change. Do not store values returned by this method. Instead, store {@link ~Marker#name}
	* and get `Marker` instance from {@link module:engine/model/markercollection~MarkerCollection MarkerCollection} every
	* time there is a need to read marker properties. This will guarantee that the marker has not been removed and
	* that it's data is up-to-date.
	*/
	getRange() {
		if (!this._liveRange) throw new CKEditorError("marker-destroyed", this);
		return this._liveRange.toRange();
	}
	/**
	* Converts `Marker` to plain object and returns it.
	*
	* @returns `Marker` converted to plain object.
	*/
	toJSON() {
		return {
			name: this.name,
			range: this._liveRange?.toJSON(),
			usingOperations: this._managedUsingOperations,
			affectsData: this._affectsData
		};
	}
	/**
	* Binds new live range to the marker and detach the old one if is attached.
	*
	* @internal
	* @param liveRange Live range to attach
	* @returns Attached live range.
	*/
	_attachLiveRange(liveRange) {
		if (this._liveRange) this._detachLiveRange();
		liveRange.delegate("change:range").to(this);
		liveRange.delegate("change:content").to(this);
		this._liveRange = liveRange;
		return liveRange;
	}
	/**
	* Unbinds and destroys currently attached live range.
	*
	* @internal
	*/
	_detachLiveRange() {
		this._liveRange.stopDelegating("change:range", this);
		this._liveRange.stopDelegating("change:content", this);
		this._liveRange.detach();
		this._liveRange = null;
	}
};
Marker.prototype.is = function(type) {
	return type === "marker" || type === "model:marker";
};
/**
* Cannot use a {@link module:engine/model/markercollection~MarkerCollection#destroy destroyed marker} instance.
*
* @error marker-destroyed
*/

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* @module engine/model/operation/detachoperation
*/
/**
* Operation to permanently remove node from detached root.
* Note this operation is only a local operation and won't be send to the other clients.
*
* @internal
*/
var DetachOperation = class extends Operation {
	/**
	* Position before the first {@link module:engine/model/item~ModelItem model item} to detach.
	*/
	sourcePosition;
	/**
	* Offset size of moved range.
	*/
	howMany;
	/**
	* Creates an insert operation.
	*
	* @param sourcePosition Position before the first {@link module:engine/model/item~ModelItem model item} to move.
	* @param howMany Offset size of moved range. Moved range will start from `sourcePosition` and end at
	* `sourcePosition` with offset shifted by `howMany`.
	*/
	constructor(sourcePosition, howMany) {
		super(null);
		this.sourcePosition = sourcePosition.clone();
		this.howMany = howMany;
	}
	/**
	* @inheritDoc
	*/
	get type() {
		return "detach";
	}
	/**
	* @inheritDoc
	*/
	get affectedSelectable() {
		return null;
	}
	/**
	* @inheritDoc
	*/
	toJSON() {
		const json = super.toJSON();
		json.sourcePosition = this.sourcePosition.toJSON();
		return json;
	}
	/**
	* @inheritDoc
	* @internal
	*/
	_validate() {
		if (this.sourcePosition.root.document)
 /**
		* Cannot detach document node.
		*
		* @error detach-operation-on-document-node
		*/
		throw new CKEditorError("detach-operation-on-document-node", this);
	}
	/**
	* @inheritDoc
	* @internal
	*/
	_execute() {
		_remove(ModelRange._createFromPositionAndShift(this.sourcePosition, this.howMany));
	}
	/**
	* @inheritDoc
	*/
	static get className() {
		return "DetachOperation";
	}
};

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* @module engine/model/documentfragment
*/
/**
* ModelDocumentFragment represents a part of model which does not have a common root but its top-level nodes
* can be seen as siblings. In other words, it is a detached part of model tree, without a root.
*
* ModelDocumentFragment has own {@link module:engine/model/markercollection~MarkerCollection}. Markers from this collection
* will be set to the {@link module:engine/model/model~Model#markers model markers} by a
* {@link module:engine/model/writer~ModelWriter#insert} function.
*/
var ModelDocumentFragment = class ModelDocumentFragment extends ModelTypeCheckable {
	/**
	* ModelDocumentFragment static markers map. This is a list of names and {@link module:engine/model/range~ModelRange ranges}
	* which will be set as Markers to {@link module:engine/model/model~Model#markers model markers collection}
	* when ModelDocumentFragment will be inserted to the document.
	*/
	markers = /* @__PURE__ */ new Map();
	/**
	* List of nodes contained inside the document fragment.
	*/
	_children = new ModelNodeList();
	/**
	* Creates an empty `ModelDocumentFragment`.
	*
	* **Note:** Constructor of this class shouldn't be used directly in the code.
	* Use the {@link module:engine/model/writer~ModelWriter#createDocumentFragment} method instead.
	*
	* @internal
	* @param children Nodes to be contained inside the `ModelDocumentFragment`.
	*/
	constructor(children) {
		super();
		if (children) this._insertChild(0, children);
	}
	/**
	* Returns an iterator that iterates over all nodes contained inside this document fragment.
	*/
	[Symbol.iterator]() {
		return this.getChildren();
	}
	/**
	* Number of this document fragment's children.
	*/
	get childCount() {
		return this._children.length;
	}
	/**
	* Sum of {@link module:engine/model/node~ModelNode#offsetSize offset sizes} of all of this document fragment's children.
	*/
	get maxOffset() {
		return this._children.maxOffset;
	}
	/**
	* Is `true` if there are no nodes inside this document fragment, `false` otherwise.
	*/
	get isEmpty() {
		return this.childCount === 0;
	}
	/**
	* Artificial next sibling. Returns `null`. Added for compatibility reasons.
	*/
	get nextSibling() {
		return null;
	}
	/**
	* Artificial previous sibling. Returns `null`. Added for compatibility reasons.
	*/
	get previousSibling() {
		return null;
	}
	/**
	* Artificial root of `ModelDocumentFragment`. Returns itself. Added for compatibility reasons.
	*/
	get root() {
		return this;
	}
	/**
	* Artificial parent of `ModelDocumentFragment`. Returns `null`. Added for compatibility reasons.
	*/
	get parent() {
		return null;
	}
	/**
	* Artificial owner of `ModelDocumentFragment`. Returns `null`. Added for compatibility reasons.
	*/
	get document() {
		return null;
	}
	/**
	* Returns `false` as `ModelDocumentFragment` by definition is not attached to a document. Added for compatibility reasons.
	*/
	isAttached() {
		return false;
	}
	/**
	* Returns empty array. Added for compatibility reasons.
	*/
	getAncestors() {
		return [];
	}
	/**
	* Gets the child at the given index. Returns `null` if incorrect index was passed.
	*
	* @param index Index in this document fragment.
	* @returns Child node.
	*/
	getChild(index) {
		return this._children.getNode(index);
	}
	/**
	* Gets the child at the given offset. Returns `null` if incorrect index was passed.
	*
	* @param offset Offset in this document fragment.
	* @returns Child node.
	*/
	getChildAtOffset(offset) {
		return this._children.getNodeAtOffset(offset);
	}
	/**
	* Returns an iterator that iterates over all of this document fragment's children.
	*/
	getChildren() {
		return this._children[Symbol.iterator]();
	}
	/**
	* Returns an index of the given child node. Returns `null` if given node is not a child of this document fragment.
	*
	* @param node Child node to look for.
	* @returns Child node's index.
	*/
	getChildIndex(node) {
		return this._children.getNodeIndex(node);
	}
	/**
	* Returns the starting offset of given child. Starting offset is equal to the sum of
	* {@link module:engine/model/node~ModelNode#offsetSize offset sizes} of all node's siblings that are before it. Returns `null` if
	* given node is not a child of this document fragment.
	*
	* @param node Child node to look for.
	* @returns Child node's starting offset.
	*/
	getChildStartOffset(node) {
		return this._children.getNodeStartOffset(node);
	}
	/**
	* Returns path to a `ModelDocumentFragment`, which is an empty array. Added for compatibility reasons.
	*/
	getPath() {
		return [];
	}
	/**
	* Returns a descendant node by its path relative to this element.
	*
	* ```ts
	* // <this>a<b>c</b></this>
	* this.getNodeByPath( [ 0 ] );     // -> "a"
	* this.getNodeByPath( [ 1 ] );     // -> <b>
	* this.getNodeByPath( [ 1, 0 ] );  // -> "c"
	* ```
	*
	* @param relativePath Path of the node to find, relative to this element.
	*/
	getNodeByPath(relativePath) {
		let node = this;
		for (const offset of relativePath) node = node.getChildAtOffset(offset);
		return node;
	}
	/**
	* Converts offset "position" to index "position".
	*
	* Returns index of a node that occupies given offset. If given offset is too low, returns `0`. If given offset is
	* too high, returns index after last child.
	*
	* ```ts
	* const textNode = new Text( 'foo' );
	* const pElement = new Element( 'p' );
	* const docFrag = new ModelDocumentFragment( [ textNode, pElement ] );
	* docFrag.offsetToIndex( -1 ); // Returns 0, because offset is too low.
	* docFrag.offsetToIndex( 0 ); // Returns 0, because offset 0 is taken by `textNode` which is at index 0.
	* docFrag.offsetToIndex( 1 ); // Returns 0, because `textNode` has `offsetSize` equal to 3, so it occupies offset 1 too.
	* docFrag.offsetToIndex( 2 ); // Returns 0.
	* docFrag.offsetToIndex( 3 ); // Returns 1.
	* docFrag.offsetToIndex( 4 ); // Returns 2. There are no nodes at offset 4, so last available index is returned.
	* ```
	*
	* @param offset Offset to look for.
	* @returns Index of a node that occupies given offset.
	*/
	offsetToIndex(offset) {
		return this._children.offsetToIndex(offset);
	}
	/**
	* Converts `ModelDocumentFragment` instance to plain object and returns it.
	* Takes care of converting all of this document fragment's children.
	*
	* @returns `ModelDocumentFragment` instance converted to plain object.
	*/
	toJSON() {
		const json = [];
		for (const node of this._children) json.push(node.toJSON());
		return json;
	}
	/**
	* Creates a `ModelDocumentFragment` instance from given plain object (i.e. parsed JSON string).
	* Converts `ModelDocumentFragment` children to proper nodes.
	*
	* @param json Plain object to be converted to `ModelDocumentFragment`.
	* @returns `ModelDocumentFragment` instance created using given plain object.
	*/
	static fromJSON(json) {
		const children = [];
		for (const child of json) if (child.name) children.push(ModelElement.fromJSON(child));
		else children.push(ModelText.fromJSON(child));
		return new ModelDocumentFragment(children);
	}
	/**
	* {@link #_insertChild Inserts} one or more nodes at the end of this document fragment.
	*
	* @internal
	* @param items Items to be inserted.
	*/
	_appendChild(items) {
		this._insertChild(this.childCount, items);
	}
	/**
	* Inserts one or more nodes at the given index and sets {@link module:engine/model/node~ModelNode#parent parent} of these nodes
	* to this document fragment.
	*
	* @internal
	* @param index Index at which nodes should be inserted.
	* @param items Items to be inserted.
	*/
	_insertChild(index, items) {
		const nodes = normalize(items);
		for (const node of nodes) {
			if (node.parent !== null) node._remove();
			node.parent = this;
		}
		this._children._insertNodes(index, nodes);
	}
	/**
	* Removes one or more nodes starting at the given index
	* and sets {@link module:engine/model/node~ModelNode#parent parent} of these nodes to `null`.
	*
	* @internal
	* @param index Index of the first node to remove.
	* @param howMany Number of nodes to remove.
	* @returns Array containing removed nodes.
	*/
	_removeChildren(index, howMany = 1) {
		const nodes = this._children._removeNodes(index, howMany);
		for (const node of nodes) node.parent = null;
		return nodes;
	}
	/**
	* Removes children nodes provided as an array and sets
	* the {@link module:engine/model/node~ModelNode#parent parent} of these nodes to `null`.
	*
	* These nodes do not need to be direct siblings.
	*
	* This method is faster than removing nodes one by one, as it recalculates offsets only once.
	*
	* @internal
	* @param nodes Array of nodes.
	*/
	_removeChildrenArray(nodes) {
		this._children._removeNodesArray(nodes);
		for (const node of nodes) node.parent = null;
	}
};
ModelDocumentFragment.prototype.is = function(type) {
	return type === "documentFragment" || type === "model:documentFragment";
};
/**
* Converts strings to Text and non-iterables to arrays.
*/
function normalize(nodes) {
	if (typeof nodes == "string") return [new ModelText(nodes)];
	if (!isIterable(nodes)) nodes = [nodes];
	return Array.from(nodes).map((node) => {
		if (typeof node == "string") return new ModelText(node);
		if (node instanceof ModelTextProxy) return new ModelText(node.data, node.getAttributes());
		return node;
	});
}

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* @module engine/model/writer
*/
/**
* The model can only be modified by using the writer. It should be used whenever you want to create a node, modify
* child nodes, attributes or text, set the selection's position and its attributes.
*
* The instance of the writer is only available in the {@link module:engine/model/model~Model#change `change()`} or
* {@link module:engine/model/model~Model#enqueueChange `enqueueChange()`}.
*
* ```ts
* model.change( writer => {
* 	writer.insertText( 'foo', paragraph, 'end' );
* } );
* ```
*
* Note that the writer should never be stored and used outside of the `change()` and
* `enqueueChange()` blocks.
*
* Note that writer's methods do not check the {@link module:engine/model/schema~ModelSchema}. It is possible
* to create incorrect model structures by using the writer. Read more about in
* {@glink framework/deep-dive/schema#who-checks-the-schema "Who checks the schema?"}.
*
* @see module:engine/model/model~Model#change
* @see module:engine/model/model~Model#enqueueChange
*/
var ModelWriter = class {
	/**
	* Instance of the model on which this writer operates.
	*/
	model;
	/**
	* The batch to which this writer will add changes.
	*/
	batch;
	/**
	* Creates a writer instance.
	*
	* **Note:** It is not recommended to use it directly. Use {@link module:engine/model/model~Model#change `Model#change()`} or
	* {@link module:engine/model/model~Model#enqueueChange `Model#enqueueChange()`} instead.
	*
	* @internal
	*/
	constructor(model, batch) {
		this.model = model;
		this.batch = batch;
	}
	/**
	* Creates a new {@link module:engine/model/text~ModelText text node}.
	*
	* ```ts
	* writer.createText( 'foo' );
	* writer.createText( 'foo', { bold: true } );
	* ```
	*
	* @param data Text data.
	* @param attributes Text attributes.
	* @returns {module:engine/model/text~ModelText} Created text node.
	*/
	createText(data, attributes) {
		return new ModelText(data, attributes);
	}
	/**
	* Creates a new {@link module:engine/model/element~ModelElement element}.
	*
	* ```ts
	* writer.createElement( 'paragraph' );
	* writer.createElement( 'paragraph', { alignment: 'center' } );
	* ```
	*
	* @param name Name of the element.
	* @param attributes Elements attributes.
	* @returns Created element.
	*/
	createElement(name, attributes) {
		return new ModelElement(name, attributes);
	}
	/**
	* Creates a new {@link module:engine/model/documentfragment~ModelDocumentFragment document fragment}.
	*
	* @returns Created document fragment.
	*/
	createDocumentFragment() {
		return new ModelDocumentFragment();
	}
	/**
	* Creates a copy of the element and returns it. Created element has the same name and attributes as the original element.
	* If clone is deep, the original element's children are also cloned. If not, then empty element is returned.
	*
	* @param element The element to clone.
	* @param deep If set to `true` clones element and all its children recursively. When set to `false`,
	* element will be cloned without any child.
	*/
	cloneElement(element, deep = true) {
		return element._clone(deep);
	}
	/**
	* Inserts item on given position.
	*
	* ```ts
	* const paragraph = writer.createElement( 'paragraph' );
	* writer.insert( paragraph, position );
	* ```
	*
	* Instead of using position you can use parent and offset:
	*
	* ```ts
	* const text = writer.createText( 'foo' );
	* writer.insert( text, paragraph, 5 );
	* ```
	*
	* You can also use `end` instead of the offset to insert at the end:
	*
	* ```ts
	* const text = writer.createText( 'foo' );
	* writer.insert( text, paragraph, 'end' );
	* ```
	*
	* Or insert before or after another element:
	*
	* ```ts
	* const paragraph = writer.createElement( 'paragraph' );
	* writer.insert( paragraph, anotherParagraph, 'after' );
	* ```
	*
	* These parameters works the same way as {@link #createPositionAt `writer.createPositionAt()`}.
	*
	* Note that if the item already has parent it will be removed from the previous parent.
	*
	* Note that you cannot re-insert a node from a document to a different document or a document fragment. In this case,
	* `model-writer-insert-forbidden-move` is thrown.
	*
	* If you want to move {@link module:engine/model/range~ModelRange range} instead of an
	* {@link module:engine/model/item~ModelItem item} use {@link module:engine/model/writer~ModelWriter#move `Writer#move()`}.
	*
	* **Note:** For a paste-like content insertion mechanism see
	* {@link module:engine/model/model~Model#insertContent `model.insertContent()`}.
	*
	* @param item Item or document fragment to insert.
	* @param offset Offset or one of the flags. Used only when second parameter is a {@link module:engine/model/item~ModelItem model item}.
	*/
	insert(item, itemOrPosition, offset = 0) {
		this._assertWriterUsedCorrectly();
		if (item instanceof ModelText && item.data == "") return;
		const position = ModelPosition._createAt(itemOrPosition, offset);
		if (item.parent) if (isSameTree(item.root, position.root)) {
			this.move(ModelRange._createOn(item), position);
			return;
		} else if (item.root.document)
 /**
		* Cannot move a node from a document to a different tree.
		* It is forbidden to move a node that was already in a document outside of it.
		*
		* @error model-writer-insert-forbidden-move
		*/
		throw new CKEditorError("model-writer-insert-forbidden-move", this);
		else this.remove(item);
		const version = position.root.document ? position.root.document.version : null;
		const insert = new InsertOperation(position, item instanceof ModelDocumentFragment ? item._removeChildren(0, item.childCount) : item, version);
		if (item instanceof ModelText) insert.shouldReceiveAttributes = true;
		this.batch.addOperation(insert);
		this.model.applyOperation(insert);
		if (item instanceof ModelDocumentFragment) for (const [markerName, markerRange] of item.markers) {
			const rangeRootPosition = ModelPosition._createAt(markerRange.root, 0);
			const options = {
				range: new ModelRange(markerRange.start._getCombined(rangeRootPosition, position), markerRange.end._getCombined(rangeRootPosition, position)),
				usingOperation: true,
				affectsData: true
			};
			if (this.model.markers.has(markerName)) this.updateMarker(markerName, options);
			else this.addMarker(markerName, options);
		}
	}
	insertText(text, attributes, itemOrPosition, offset) {
		if (attributes instanceof ModelDocumentFragment || attributes instanceof ModelElement || attributes instanceof ModelPosition) this.insert(this.createText(text), attributes, itemOrPosition);
		else this.insert(this.createText(text, attributes), itemOrPosition, offset);
	}
	insertElement(name, attributes, itemOrPositionOrOffset, offset) {
		if (attributes instanceof ModelDocumentFragment || attributes instanceof ModelElement || attributes instanceof ModelPosition) this.insert(this.createElement(name), attributes, itemOrPositionOrOffset);
		else this.insert(this.createElement(name, attributes), itemOrPositionOrOffset, offset);
	}
	/**
	* Inserts item at the end of the given parent.
	*
	* ```ts
	* const paragraph = writer.createElement( 'paragraph' );
	* writer.append( paragraph, root );
	* ```
	*
	* Note that if the item already has parent it will be removed from the previous parent.
	*
	* If you want to move {@link module:engine/model/range~ModelRange range} instead of an
	* {@link module:engine/model/item~ModelItem item} use {@link module:engine/model/writer~ModelWriter#move `Writer#move()`}.
	*
	* @param item Item or document fragment to insert.
	*/
	append(item, parent) {
		this.insert(item, parent, "end");
	}
	appendText(text, attributes, parent) {
		if (attributes instanceof ModelDocumentFragment || attributes instanceof ModelElement) this.insert(this.createText(text), attributes, "end");
		else this.insert(this.createText(text, attributes), parent, "end");
	}
	appendElement(name, attributes, parent) {
		if (attributes instanceof ModelDocumentFragment || attributes instanceof ModelElement) this.insert(this.createElement(name), attributes, "end");
		else this.insert(this.createElement(name, attributes), parent, "end");
	}
	/**
	* Sets value of the attribute with given key on a {@link module:engine/model/item~ModelItem model item}
	* or on a {@link module:engine/model/range~ModelRange range}.
	*
	* @param key Attribute key.
	* @param value Attribute new value.
	* @param itemOrRange Model item or range on which the attribute will be set.
	*/
	setAttribute(key, value, itemOrRange) {
		this._assertWriterUsedCorrectly();
		if (itemOrRange instanceof ModelRange) {
			const ranges = itemOrRange.getMinimalFlatRanges();
			for (const range of ranges) setAttributeOnRange(this, key, value, range);
		} else setAttributeOnItem(this, key, value, itemOrRange);
	}
	/**
	* Sets values of attributes on a {@link module:engine/model/item~ModelItem model item}
	* or on a {@link module:engine/model/range~ModelRange range}.
	*
	* ```ts
	* writer.setAttributes( {
	* 	bold: true,
	* 	italic: true
	* }, range );
	* ```
	*
	* @param attributes Attributes keys and values.
	* @param itemOrRange Model item or range on which the attributes will be set.
	*/
	setAttributes(attributes, itemOrRange) {
		for (const [key, val] of toMap(attributes)) this.setAttribute(key, val, itemOrRange);
	}
	/**
	* Removes an attribute with given key from a {@link module:engine/model/item~ModelItem model item}
	* or from a {@link module:engine/model/range~ModelRange range}.
	*
	* @param key Attribute key.
	* @param itemOrRange Model item or range from which the attribute will be removed.
	*/
	removeAttribute(key, itemOrRange) {
		this._assertWriterUsedCorrectly();
		if (itemOrRange instanceof ModelRange) {
			const ranges = itemOrRange.getMinimalFlatRanges();
			for (const range of ranges) setAttributeOnRange(this, key, null, range);
		} else setAttributeOnItem(this, key, null, itemOrRange);
	}
	/**
	* Removes all attributes from all elements in the range or from the given item.
	*
	* @param itemOrRange Model item or range from which all attributes will be removed.
	*/
	clearAttributes(itemOrRange) {
		this._assertWriterUsedCorrectly();
		const removeAttributesFromItem = (item) => {
			for (const attribute of item.getAttributeKeys()) this.removeAttribute(attribute, item);
		};
		if (!(itemOrRange instanceof ModelRange)) removeAttributesFromItem(itemOrRange);
		else for (const item of itemOrRange.getItems()) removeAttributesFromItem(item);
	}
	/**
	* Moves all items in the source range to the target position.
	*
	* ```ts
	* writer.move( sourceRange, targetPosition );
	* ```
	*
	* Instead of the target position you can use parent and offset or define that range should be moved to the end
	* or before or after chosen item:
	*
	* ```ts
	* // Moves all items in the range to the paragraph at offset 5:
	* writer.move( sourceRange, paragraph, 5 );
	* // Moves all items in the range to the end of a blockquote:
	* writer.move( sourceRange, blockquote, 'end' );
	* // Moves all items in the range to a position after an image:
	* writer.move( sourceRange, image, 'after' );
	* ```
	*
	* These parameters work the same way as {@link #createPositionAt `writer.createPositionAt()`}.
	*
	* Note that items can be moved only within the same tree. It means that you can move items within the same root
	* (element or document fragment) or between {@link module:engine/model/document~ModelDocument#roots documents roots},
	* but you cannot move items from document fragment to the document or from one detached element to another. Use
	* {@link module:engine/model/writer~ModelWriter#insert} in such cases.
	*
	* @param range Source range.
	* @param offset Offset or one of the flags. Used only when second parameter is a {@link module:engine/model/item~ModelItem model item}.
	*/
	move(range, itemOrPosition, offset) {
		this._assertWriterUsedCorrectly();
		if (!(range instanceof ModelRange))
 /**
		* Invalid range to move.
		*
		* @error writer-move-invalid-range
		*/
		throw new CKEditorError("writer-move-invalid-range", this);
		if (!range.isFlat)
 /**
		* Range to move is not flat.
		*
		* @error writer-move-range-not-flat
		*/
		throw new CKEditorError("writer-move-range-not-flat", this);
		const position = ModelPosition._createAt(itemOrPosition, offset);
		if (position.isEqual(range.start)) return;
		this._addOperationForAffectedMarkers("move", range);
		if (!isSameTree(range.root, position.root))
 /**
		* Range is going to be moved within not the same document. Please use
		* {@link module:engine/model/writer~ModelWriter#insert insert} instead.
		*
		* @error writer-move-different-document
		*/
		throw new CKEditorError("writer-move-different-document", this);
		const version = range.root.document ? range.root.document.version : null;
		const operation = new MoveOperation(range.start, range.end.offset - range.start.offset, position, version);
		this.batch.addOperation(operation);
		this.model.applyOperation(operation);
	}
	/**
	* Removes given model {@link module:engine/model/item~ModelItem item} or {@link module:engine/model/range~ModelRange range}.
	*
	* @param itemOrRange Model item or range to remove.
	*/
	remove(itemOrRange) {
		this._assertWriterUsedCorrectly();
		const ranges = (itemOrRange instanceof ModelRange ? itemOrRange : ModelRange._createOn(itemOrRange)).getMinimalFlatRanges().reverse();
		for (const flat of ranges) {
			this._addOperationForAffectedMarkers("move", flat);
			applyRemoveOperation(flat.start, flat.end.offset - flat.start.offset, this.batch, this.model);
		}
	}
	/**
	* Merges two siblings at the given position.
	*
	* Node before and after the position have to be an element. Otherwise `writer-merge-no-element-before` or
	* `writer-merge-no-element-after` error will be thrown.
	*
	* @param position Position between merged elements.
	*/
	merge(position) {
		this._assertWriterUsedCorrectly();
		const nodeBefore = position.nodeBefore;
		const nodeAfter = position.nodeAfter;
		this._addOperationForAffectedMarkers("merge", position);
		if (!(nodeBefore instanceof ModelElement))
 /**
		* Node before merge position must be an element.
		*
		* @error writer-merge-no-element-before
		*/
		throw new CKEditorError("writer-merge-no-element-before", this);
		if (!(nodeAfter instanceof ModelElement))
 /**
		* Node after merge position must be an element.
		*
		* @error writer-merge-no-element-after
		*/
		throw new CKEditorError("writer-merge-no-element-after", this);
		if (!position.root.document) this._mergeDetached(position);
		else this._merge(position);
	}
	/**
	* Shortcut for {@link module:engine/model/model~Model#createPositionFromPath `Model#createPositionFromPath()`}.
	*
	* @param root Root of the position.
	* @param path Position path. See {@link module:engine/model/position~ModelPosition#path}.
	* @param stickiness Position stickiness. See {@link module:engine/model/position~ModelPositionStickiness}.
	*/
	createPositionFromPath(root, path, stickiness) {
		return this.model.createPositionFromPath(root, path, stickiness);
	}
	/**
	* Shortcut for {@link module:engine/model/model~Model#createPositionAt `Model#createPositionAt()`}.
	*
	* @param offset Offset or one of the flags. Used only when first parameter is a {@link module:engine/model/item~ModelItem model item}.
	*/
	createPositionAt(itemOrPosition, offset) {
		return this.model.createPositionAt(itemOrPosition, offset);
	}
	/**
	* Shortcut for {@link module:engine/model/model~Model#createPositionAfter `Model#createPositionAfter()`}.
	*
	* @param item Item after which the position should be placed.
	*/
	createPositionAfter(item) {
		return this.model.createPositionAfter(item);
	}
	/**
	* Shortcut for {@link module:engine/model/model~Model#createPositionBefore `Model#createPositionBefore()`}.
	*
	* @param item Item after which the position should be placed.
	*/
	createPositionBefore(item) {
		return this.model.createPositionBefore(item);
	}
	/**
	* Shortcut for {@link module:engine/model/model~Model#createRange `Model#createRange()`}.
	*
	* @param start Start position.
	* @param end End position. If not set, range will be collapsed at `start` position.
	*/
	createRange(start, end) {
		return this.model.createRange(start, end);
	}
	/**
	* Shortcut for {@link module:engine/model/model~Model#createRangeIn `Model#createRangeIn()`}.
	*
	* @param element Element which is a parent for the range.
	*/
	createRangeIn(element) {
		return this.model.createRangeIn(element);
	}
	/**
	* Shortcut for {@link module:engine/model/model~Model#createRangeOn `Model#createRangeOn()`}.
	*
	* @param element Element which is a parent for the range.
	*/
	createRangeOn(element) {
		return this.model.createRangeOn(element);
	}
	createSelection(...args) {
		return this.model.createSelection(...args);
	}
	/**
	* Performs merge action in a detached tree.
	*
	* @param position Position between merged elements.
	*/
	_mergeDetached(position) {
		const nodeBefore = position.nodeBefore;
		const nodeAfter = position.nodeAfter;
		this.move(ModelRange._createIn(nodeAfter), ModelPosition._createAt(nodeBefore, "end"));
		this.remove(nodeAfter);
	}
	/**
	* Performs merge action in a non-detached tree.
	*
	* @param position Position between merged elements.
	*/
	_merge(position) {
		const targetPosition = ModelPosition._createAt(position.nodeBefore, "end");
		const sourcePosition = ModelPosition._createAt(position.nodeAfter, 0);
		const graveyard = position.root.document.graveyard;
		const graveyardPosition = new ModelPosition(graveyard, [0]);
		const version = position.root.document.version;
		const merge = new MergeOperation(sourcePosition, position.nodeAfter.maxOffset, targetPosition, graveyardPosition, version);
		this.batch.addOperation(merge);
		this.model.applyOperation(merge);
	}
	/**
	* Renames the given element.
	*
	* @param element The element to rename.
	* @param newName New element name.
	*/
	rename(element, newName) {
		this._assertWriterUsedCorrectly();
		if (!(element instanceof ModelElement))
 /**
		* Trying to rename an object which is not an instance of Element.
		*
		* @error writer-rename-not-element-instance
		*/
		throw new CKEditorError("writer-rename-not-element-instance", this);
		const version = element.root.document ? element.root.document.version : null;
		const renameOperation = new RenameOperation(ModelPosition._createBefore(element), element.name, newName, version);
		this.batch.addOperation(renameOperation);
		this.model.applyOperation(renameOperation);
	}
	/**
	* Splits elements starting from the given position and going to the top of the model tree as long as given
	* `limitElement` is reached. When `limitElement` is not defined then only the parent of the given position will be split.
	*
	* The element needs to have a parent. It cannot be a root element nor a document fragment.
	* The `writer-split-element-no-parent` error will be thrown if you try to split an element with no parent.
	*
	* @param position Position of split.
	* @param limitElement Stop splitting when this element will be reached.
	* @returns Split result with properties:
	* * `position` - Position between split elements.
	* * `range` - Range that stars from the end of the first split element and ends at the beginning of the first copy element.
	*/
	split(position, limitElement) {
		this._assertWriterUsedCorrectly();
		let splitElement = position.parent;
		if (!splitElement.parent)
 /**
		* Element with no parent cannot be split.
		*
		* @error writer-split-element-no-parent
		*/
		throw new CKEditorError("writer-split-element-no-parent", this);
		if (!limitElement) limitElement = splitElement.parent;
		if (!position.parent.getAncestors({ includeSelf: true }).includes(limitElement))
 /**
		* Limit element is not a position ancestor.
		*
		* @error writer-split-invalid-limit-element
		*/
		throw new CKEditorError("writer-split-invalid-limit-element", this);
		let firstSplitElement;
		let firstCopyElement;
		do {
			const version = splitElement.root.document ? splitElement.root.document.version : null;
			const howMany = splitElement.maxOffset - position.offset;
			const insertionPosition = SplitOperation.getInsertionPosition(position);
			const split = new SplitOperation(position, howMany, insertionPosition, null, version);
			this.batch.addOperation(split);
			this.model.applyOperation(split);
			if (!firstSplitElement && !firstCopyElement) {
				firstSplitElement = splitElement;
				firstCopyElement = position.parent.nextSibling;
			}
			position = this.createPositionAfter(position.parent);
			splitElement = position.parent;
		} while (splitElement !== limitElement);
		return {
			position,
			range: new ModelRange(ModelPosition._createAt(firstSplitElement, "end"), ModelPosition._createAt(firstCopyElement, 0))
		};
	}
	/**
	* Wraps the given range with the given element or with a new element (if a string was passed).
	*
	* **Note:** range to wrap should be a "flat range" (see {@link module:engine/model/range~ModelRange#isFlat `Range#isFlat`}).
	* If not, an error will be thrown.
	*
	* @param range Range to wrap.
	* @param elementOrString Element or name of element to wrap the range with.
	*/
	wrap(range, elementOrString) {
		this._assertWriterUsedCorrectly();
		if (!range.isFlat)
 /**
		* Range to wrap is not flat.
		*
		* @error writer-wrap-range-not-flat
		*/
		throw new CKEditorError("writer-wrap-range-not-flat", this);
		const element = elementOrString instanceof ModelElement ? elementOrString : new ModelElement(elementOrString);
		if (element.childCount > 0)
 /**
		* Element to wrap with is not empty.
		*
		* @error writer-wrap-element-not-empty
		*/
		throw new CKEditorError("writer-wrap-element-not-empty", this);
		if (element.parent !== null)
 /**
		* Element to wrap with is already attached to a tree model.
		*
		* @error writer-wrap-element-attached
		*/
		throw new CKEditorError("writer-wrap-element-attached", this);
		this.insert(element, range.start);
		const shiftedRange = new ModelRange(range.start.getShiftedBy(1), range.end.getShiftedBy(1));
		this.move(shiftedRange, ModelPosition._createAt(element, 0));
	}
	/**
	* Unwraps children of the given element – all its children are moved before it and then the element is removed.
	* Throws error if you try to unwrap an element which does not have a parent.
	*
	* @param element Element to unwrap.
	*/
	unwrap(element) {
		this._assertWriterUsedCorrectly();
		if (element.parent === null)
 /**
		* Trying to unwrap an element which has no parent.
		*
		* @error writer-unwrap-element-no-parent
		*/
		throw new CKEditorError("writer-unwrap-element-no-parent", this);
		this.move(ModelRange._createIn(element), this.createPositionAfter(element));
		this.remove(element);
	}
	/**
	* Adds a {@link module:engine/model/markercollection~Marker marker}. Marker is a named range, which tracks
	* changes in the document and updates its range automatically, when model tree changes.
	*
	* As the first parameter you can set marker name.
	*
	* The required `options.usingOperation` parameter lets you decide if the marker should be managed by operations or not. See
	* {@link module:engine/model/markercollection~Marker marker class description} to learn about the difference between
	* markers managed by operations and not-managed by operations.
	*
	* The `options.affectsData` parameter, which defaults to `false`, allows you to define if a marker affects the data. It should be
	* `true` when the marker change changes the data returned by the
	* {@link module:core/editor/editor~Editor#getData `editor.getData()`} method.
	* When set to `true` it fires the {@link module:engine/model/document~ModelDocument#event:change:data `change:data`} event.
	* When set to `false` it fires the {@link module:engine/model/document~ModelDocument#event:change `change`} event.
	*
	* Create marker directly base on marker's name:
	*
	* ```ts
	* addMarker( markerName, { range, usingOperation: false } );
	* ```
	*
	* Create marker using operation:
	*
	* ```ts
	* addMarker( markerName, { range, usingOperation: true } );
	* ```
	*
	* Create marker that affects the editor data:
	*
	* ```ts
	* addMarker( markerName, { range, usingOperation: false, affectsData: true } );
	* ```
	*
	* Note: For efficiency reasons, it's best to create and keep as little markers as possible.
	*
	* @see module:engine/model/markercollection~Marker
	* @param name Name of a marker to create - must be unique.
	* @param options.usingOperation Flag indicating that the marker should be added by MarkerOperation.
	* See {@link module:engine/model/markercollection~Marker#managedUsingOperations}.
	* @param options.range Marker range.
	* @param options.affectsData Flag indicating that the marker changes the editor data.
	* @returns Marker that was set.
	*/
	addMarker(name, options) {
		this._assertWriterUsedCorrectly();
		if (!options || typeof options.usingOperation != "boolean")
 /**
		* The `options.usingOperation` parameter is required when adding a new marker.
		*
		* @error writer-addmarker-no-usingoperation
		*/
		throw new CKEditorError("writer-addmarker-no-usingoperation", this);
		const usingOperation = options.usingOperation;
		const range = options.range;
		const affectsData = options.affectsData === void 0 ? false : options.affectsData;
		if (this.model.markers.has(name))
 /**
		* Marker with provided name already exists.
		*
		* @error writer-addmarker-marker-exists
		*/
		throw new CKEditorError("writer-addmarker-marker-exists", this);
		if (!range)
 /**
		* Range parameter is required when adding a new marker.
		*
		* @error writer-addmarker-no-range
		*/
		throw new CKEditorError("writer-addmarker-no-range", this);
		if (!usingOperation) return this.model.markers._set(name, range, usingOperation, affectsData);
		applyMarkerOperation(this, name, null, range, affectsData);
		return this.model.markers.get(name);
	}
	/**
	* Adds, updates or refreshes a {@link module:engine/model/markercollection~Marker marker}. Marker is a named range, which tracks
	* changes in the document and updates its range automatically, when model tree changes. Still, it is possible to change the
	* marker's range directly using this method.
	*
	* As the first parameter you can set marker name or instance. If none of them is provided, new marker, with a unique
	* name is created and returned.
	*
	* **Note**: If you want to change the {@link module:engine/view/element~ViewElement view element}
	* of the marker while its data in the model
	* remains the same, use the dedicated {@link module:engine/controller/editingcontroller~EditingController#reconvertMarker} method.
	*
	* The `options.usingOperation` parameter lets you change if the marker should be managed by operations or not. See
	* {@link module:engine/model/markercollection~Marker marker class description} to learn about the difference between
	* markers managed by operations and not-managed by operations. It is possible to change this option for an existing marker.
	*
	* The `options.affectsData` parameter, which defaults to `false`, allows you to define if a marker affects the data. It should be
	* `true` when the marker change changes the data returned by
	* the {@link module:core/editor/editor~Editor#getData `editor.getData()`} method.
	* When set to `true` it fires the {@link module:engine/model/document~ModelDocument#event:change:data `change:data`} event.
	* When set to `false` it fires the {@link module:engine/model/document~ModelDocument#event:change `change`} event.
	*
	* Update marker directly base on marker's name:
	*
	* ```ts
	* updateMarker( markerName, { range } );
	* ```
	*
	* Update marker using operation:
	*
	* ```ts
	* updateMarker( marker, { range, usingOperation: true } );
	* updateMarker( markerName, { range, usingOperation: true } );
	* ```
	*
	* Change marker's option (start using operations to manage it):
	*
	* ```ts
	* updateMarker( marker, { usingOperation: true } );
	* ```
	*
	* Change marker's option (inform the engine, that the marker does not affect the data anymore):
	*
	* ```ts
	* updateMarker( markerName, { affectsData: false } );
	* ```
	*
	* @see module:engine/model/markercollection~Marker
	* @param markerOrName Name of a marker to update, or a marker instance.
	* @param options If options object is not defined then marker will be refreshed by triggering
	* downcast conversion for this marker with the same data.
	* @param options.range Marker range to update.
	* @param options.usingOperation Flag indicated whether the marker should be added by MarkerOperation.
	* See {@link module:engine/model/markercollection~Marker#managedUsingOperations}.
	* @param options.affectsData Flag indicating that the marker changes the editor data.
	*/
	updateMarker(markerOrName, options) {
		this._assertWriterUsedCorrectly();
		const markerName = typeof markerOrName == "string" ? markerOrName : markerOrName.name;
		const currentMarker = this.model.markers.get(markerName);
		if (!currentMarker)
 /**
		* Marker with provided name does not exist and will not be updated.
		*
		* @error writer-updatemarker-marker-not-exists
		*/
		throw new CKEditorError("writer-updatemarker-marker-not-exists", this);
		if (!options) {
			/**
			* The usage of `writer.updateMarker()` only to reconvert (refresh) a
			* {@link module:engine/model/markercollection~Marker model marker} was deprecated and may not work in the future.
			* Please update your code to use
			* {@link module:engine/controller/editingcontroller~EditingController#reconvertMarker `editor.editing.reconvertMarker()`}
			* instead.
			*
			* @error writer-updatemarker-reconvert-using-editingcontroller
			* @param markerName The name of the updated marker.
			*/
			logWarning("writer-updatemarker-reconvert-using-editingcontroller", { markerName });
			this.model.markers._refresh(currentMarker);
			return;
		}
		const hasUsingOperationDefined = typeof options.usingOperation == "boolean";
		const affectsDataDefined = typeof options.affectsData == "boolean";
		const affectsData = affectsDataDefined ? options.affectsData : currentMarker.affectsData;
		if (!hasUsingOperationDefined && !options.range && !affectsDataDefined)
 /**
		* One of the options is required - provide range, usingOperations or affectsData.
		*
		* @error writer-updatemarker-wrong-options
		*/
		throw new CKEditorError("writer-updatemarker-wrong-options", this);
		const currentRange = currentMarker.getRange();
		const updatedRange = options.range ? options.range : currentRange;
		if (hasUsingOperationDefined && options.usingOperation !== currentMarker.managedUsingOperations) {
			if (options.usingOperation) applyMarkerOperation(this, markerName, null, updatedRange, affectsData);
			else {
				applyMarkerOperation(this, markerName, currentRange, null, affectsData);
				this.model.markers._set(markerName, updatedRange, void 0, affectsData);
			}
			return;
		}
		if (currentMarker.managedUsingOperations) applyMarkerOperation(this, markerName, currentRange, updatedRange, affectsData);
		else this.model.markers._set(markerName, updatedRange, void 0, affectsData);
	}
	/**
	* Removes given {@link module:engine/model/markercollection~Marker marker} or marker with given name.
	* The marker is removed accordingly to how it has been created, so if the marker was created using operation,
	* it will be destroyed using operation.
	*
	* @param markerOrName Marker or marker name to remove.
	*/
	removeMarker(markerOrName) {
		this._assertWriterUsedCorrectly();
		const name = typeof markerOrName == "string" ? markerOrName : markerOrName.name;
		if (!this.model.markers.has(name))
 /**
		* Trying to remove marker which does not exist.
		*
		* @error writer-removemarker-no-marker
		*/
		throw new CKEditorError("writer-removemarker-no-marker", this);
		const marker = this.model.markers.get(name);
		if (!marker.managedUsingOperations) {
			this.model.markers._remove(name);
			return;
		}
		const oldRange = marker.getRange();
		applyMarkerOperation(this, name, oldRange, null, marker.affectsData);
	}
	/**
	* Adds a new root to the document (or re-attaches a {@link #detachRoot detached root}).
	*
	* Throws an error, if trying to add a root that is already added and attached.
	*
	* **Note:** The default `elementName` value is `'$root'`. When the editor uses a custom root
	* {@link module:core/editor/editorconfig~RootConfig#modelElement `modelElement`}, pass the configured model element
	* name explicitly so the added root matches the schema the features expect.
	* See the {@glink framework/deep-dive/schema#custom-root-elements Custom root elements} section of the
	* {@glink framework/deep-dive/schema Schema deep-dive} guide for more details.
	*
	* @param rootName Name of the added root.
	* @param elementName The element name. Defaults to `'$root'` which also has some basic schema defined
	* (e.g. `$block` elements are allowed inside the `$root`). Make sure to define a proper schema if you use a different name.
	* @returns The added root element.
	*/
	addRoot(rootName, elementName = "$root") {
		this._assertWriterUsedCorrectly();
		const root = this.model.document.getRoot(rootName);
		if (root && root.isAttached())
 /**
		* Root with provided name already exists and is attached.
		*
		* @error writer-addroot-root-exists
		*/
		throw new CKEditorError("writer-addroot-root-exists", this);
		const document = this.model.document;
		const operation = new RootOperation(rootName, elementName, true, document, document.version);
		this.batch.addOperation(operation);
		this.model.applyOperation(operation);
		return this.model.document.getRoot(rootName);
	}
	/**
	* Detaches the root from the document.
	*
	* All content and markers are removed from the root upon detaching. New content and new markers cannot be added to the root, as long
	* as it is detached.
	*
	* A root cannot be fully removed from the document, it can be only detached. A root is permanently removed only after you
	* re-initialize the editor and do not specify the root in the initial data.
	*
	* A detached root can be re-attached using {@link #addRoot}.
	*
	* Throws an error if the root does not exist or the root is already detached.
	*
	* @param rootOrName Name of the detached root.
	*/
	detachRoot(rootOrName) {
		this._assertWriterUsedCorrectly();
		const root = typeof rootOrName == "string" ? this.model.document.getRoot(rootOrName) : rootOrName;
		if (!root || !root.isAttached())
 /**
		* Root with provided name does not exist or is already detached.
		*
		* @error writer-detachroot-no-root
		*/
		throw new CKEditorError("writer-detachroot-no-root", this);
		for (const marker of this.model.markers) if (marker.getRange().root === root) this.removeMarker(marker);
		for (const key of root.getAttributeKeys()) this.removeAttribute(key, root);
		this.remove(this.createRangeIn(root));
		const document = this.model.document;
		const operation = new RootOperation(root.rootName, root.name, false, document, document.version);
		this.batch.addOperation(operation);
		this.model.applyOperation(operation);
	}
	setSelection(...args) {
		this._assertWriterUsedCorrectly();
		this.model.document.selection._setTo(...args);
	}
	/**
	* Moves {@link module:engine/model/documentselection~ModelDocumentSelection#focus} to the specified location.
	*
	* The location can be specified in the same form as
	* {@link #createPositionAt `writer.createPositionAt()`} parameters.
	*
	* @param itemOrPosition
	* @param offset Offset or one of the flags. Used only when first parameter is a {@link module:engine/model/item~ModelItem model item}.
	*/
	setSelectionFocus(itemOrPosition, offset) {
		this._assertWriterUsedCorrectly();
		this.model.document.selection._setFocus(itemOrPosition, offset);
	}
	setSelectionAttribute(keyOrObjectOrIterable, value) {
		this._assertWriterUsedCorrectly();
		if (typeof keyOrObjectOrIterable === "string") this._setSelectionAttribute(keyOrObjectOrIterable, value);
		else for (const [key, value] of toMap(keyOrObjectOrIterable)) this._setSelectionAttribute(key, value);
	}
	/**
	* Removes attribute(s) with given key(s) from the selection.
	*
	* Remove one attribute:
	*
	* ```ts
	* writer.removeSelectionAttribute( 'italic' );
	* ```
	*
	* Remove multiple attributes:
	*
	* ```ts
	* writer.removeSelectionAttribute( [ 'italic', 'bold' ] );
	* ```
	*
	* @param keyOrIterableOfKeys Key of the attribute to remove or an iterable of attribute keys to remove.
	*/
	removeSelectionAttribute(keyOrIterableOfKeys) {
		this._assertWriterUsedCorrectly();
		if (typeof keyOrIterableOfKeys === "string") this._removeSelectionAttribute(keyOrIterableOfKeys);
		else for (const key of keyOrIterableOfKeys) this._removeSelectionAttribute(key);
	}
	/**
	* Temporarily changes the {@link module:engine/model/documentselection~ModelDocumentSelection#isGravityOverridden gravity}
	* of the selection from left to right.
	*
	* The gravity defines from which direction the selection inherits its attributes. If it's the default left gravity,
	* then the selection (after being moved by the user) inherits attributes from its left-hand side.
	* This method allows to temporarily override this behavior by forcing the gravity to the right.
	*
	* For the following model fragment:
	*
	* ```xml
	* <$text bold="true" linkHref="url">bar[]</$text><$text bold="true">biz</$text>
	* ```
	*
	* * Default gravity: selection will have the `bold` and `linkHref` attributes.
	* * Overridden gravity: selection will have `bold` attribute.
	*
	* **Note**: It returns an unique identifier which is required to restore the gravity. It guarantees the symmetry
	* of the process.
	*
	* @returns The unique id which allows restoring the gravity.
	*/
	overrideSelectionGravity() {
		return this.model.document.selection._overrideGravity();
	}
	/**
	* Restores {@link ~ModelWriter#overrideSelectionGravity} gravity to default.
	*
	* Restoring the gravity is only possible using the unique identifier returned by
	* {@link ~ModelWriter#overrideSelectionGravity}. Note that the gravity remains overridden as long as won't be restored
	* the same number of times it was overridden.
	*
	* @param uid The unique id returned by {@link ~ModelWriter#overrideSelectionGravity}.
	*/
	restoreSelectionGravity(uid) {
		this.model.document.selection._restoreGravity(uid);
	}
	/**
	* @param key Key of the attribute to remove.
	* @param value Attribute value.
	*/
	_setSelectionAttribute(key, value) {
		const selection = this.model.document.selection;
		if (selection.isCollapsed && selection.anchor.parent.isEmpty) {
			const storeKey = ModelDocumentSelection._getStoreAttributeKey(key);
			this.setAttribute(storeKey, value, selection.anchor.parent);
		}
		selection._setAttribute(key, value);
	}
	/**
	* @param key Key of the attribute to remove.
	*/
	_removeSelectionAttribute(key) {
		const selection = this.model.document.selection;
		if (selection.isCollapsed && selection.anchor.parent.isEmpty) {
			const storeKey = ModelDocumentSelection._getStoreAttributeKey(key);
			this.removeAttribute(storeKey, selection.anchor.parent);
		}
		selection._removeAttribute(key);
	}
	/**
	* Throws `writer-detached-writer-tries-to-modify-model` error when the writer is used outside of the `change()` block.
	*/
	_assertWriterUsedCorrectly() {
		/**
		* Trying to use a writer outside a {@link module:engine/model/model~Model#change `change()`} or
		* {@link module:engine/model/model~Model#enqueueChange `enqueueChange()`} blocks.
		*
		* The writer can only be used inside these blocks which ensures that the model
		* can only be changed during such "sessions".
		*
		* @error writer-incorrect-use
		*/
		if (this.model._currentWriter !== this) throw new CKEditorError("writer-incorrect-use", this);
	}
	/**
	* For given action `type` and `positionOrRange` where the action happens, this function finds all affected markers
	* and applies a marker operation with the new marker range equal to the current range. Thanks to this, the marker range
	* can be later correctly processed during undo.
	*
	* Exposed so that code applying operations directly to the model can reproduce the same marker bookkeeping.
	*
	* @internal
	* @param type Writer action type.
	* @param positionOrRange Position or range where the writer action happens.
	*/
	_addOperationForAffectedMarkers(type, positionOrRange) {
		for (const marker of this.model.markers) {
			if (!marker.managedUsingOperations) continue;
			const markerRange = marker.getRange();
			let isAffected = false;
			if (type === "move") {
				const range = positionOrRange;
				isAffected = range.containsPosition(markerRange.start) || range.start.isEqual(markerRange.start) || range.containsPosition(markerRange.end) || range.end.isEqual(markerRange.end);
			} else {
				const position = positionOrRange;
				const elementBefore = position.nodeBefore;
				const elementAfter = position.nodeAfter;
				const affectedInLeftElement = markerRange.start.parent == elementBefore && markerRange.start.isAtEnd;
				const affectedInRightElement = markerRange.end.parent == elementAfter && markerRange.end.offset == 0;
				const affectedAfterLeftElement = markerRange.end.nodeAfter == elementAfter;
				const affectedBeforeRightElement = markerRange.start.nodeAfter == elementAfter;
				isAffected = affectedInLeftElement || affectedInRightElement || affectedAfterLeftElement || affectedBeforeRightElement;
			}
			if (isAffected) this.updateMarker(marker.name, { range: markerRange });
		}
	}
};
/**
* Sets given attribute to each node in given range. When attribute value is null then attribute will be removed.
*
* Because attribute operation needs to have the same attribute value on the whole range, this function splits
* the range into smaller parts.
*
* Given `range` must be flat.
*/
function setAttributeOnRange(writer, key, value, range) {
	const model = writer.model;
	const doc = model.document;
	let lastSplitPosition = range.start;
	let position;
	let valueBefore;
	let valueAfter;
	for (const val of range.getWalker({ shallow: true })) {
		valueAfter = val.item.getAttribute(key);
		if (position && valueBefore != valueAfter) {
			if (valueBefore != value) addOperation();
			lastSplitPosition = position;
		}
		position = val.nextPosition;
		valueBefore = valueAfter;
	}
	if (position instanceof ModelPosition && position != lastSplitPosition && valueBefore != value) addOperation();
	function addOperation() {
		const range = new ModelRange(lastSplitPosition, position);
		const version = range.root.document ? doc.version : null;
		const operation = new AttributeOperation(range, key, valueBefore, value, version);
		writer.batch.addOperation(operation);
		model.applyOperation(operation);
	}
}
/**
* Sets given attribute to the given node. When attribute value is null then attribute will be removed.
*/
function setAttributeOnItem(writer, key, value, item) {
	const model = writer.model;
	const doc = model.document;
	const previousValue = item.getAttribute(key);
	let range, operation;
	if (previousValue != value) {
		if (item.root === item) operation = new RootAttributeOperation(item, key, previousValue, value, item.document ? doc.version : null);
		else {
			range = new ModelRange(ModelPosition._createBefore(item), writer.createPositionAfter(item));
			const version = range.root.document ? doc.version : null;
			operation = new AttributeOperation(range, key, previousValue, value, version);
		}
		writer.batch.addOperation(operation);
		model.applyOperation(operation);
	}
}
/**
* Creates and applies marker operation to {@link module:engine/model/operation/operation~Operation operation}.
*/
function applyMarkerOperation(writer, name, oldRange, newRange, affectsData) {
	const model = writer.model;
	const doc = model.document;
	const operation = new MarkerOperation(name, oldRange, newRange, model.markers, !!affectsData, doc.version);
	writer.batch.addOperation(operation);
	model.applyOperation(operation);
}
/**
* Creates `MoveOperation` or `DetachOperation` that removes `howMany` nodes starting from `position`.
* The operation will be applied on given model instance and added to given operation instance.
*
* @param position Position from which nodes are removed.
* @param howMany Number of nodes to remove.
* @param batch Batch to which the operation will be added.
* @param model Model instance on which operation will be applied.
*/
function applyRemoveOperation(position, howMany, batch, model) {
	let operation;
	if (position.root.document) {
		const doc = model.document;
		operation = new MoveOperation(position, howMany, new ModelPosition(doc.graveyard, [0]), doc.version);
	} else operation = new DetachOperation(position, howMany);
	batch.addOperation(operation);
	model.applyOperation(operation);
}
/**
* Returns `true` if both root elements are the same element or both are documents root elements.
*
* Elements in the same tree can be moved (for instance you can move element form one documents root to another, or
* within the same document fragment), but when element supposed to be moved from document fragment to the document, or
* to another document it should be removed and inserted to avoid problems with OT. This is because features like undo or
* collaboration may track changes on the document but ignore changes on detached fragments and should not get
* unexpected `move` operation.
*/
function isSameTree(rootA, rootB) {
	if (rootA === rootB) return true;
	if (rootA instanceof ModelRootElement && rootB instanceof ModelRootElement) return true;
	return false;
}

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* @module engine/model/utils/selection-post-fixer
*/
/**
* Injects selection post-fixer to the model.
*
* The role of the selection post-fixer is to ensure that the selection is in a correct place
* after a {@link module:engine/model/model~Model#change `change()`} block was executed.
*
* The correct position means that:
*
* * All collapsed selection ranges are in a place where the {@link module:engine/model/schema~ModelSchema}
* allows a `$text`.
* * None of the selection's non-collapsed ranges crosses a {@link module:engine/model/schema~ModelSchema#isLimit limit element}
* boundary (a range must be rooted within one limit element).
* * Only {@link module:engine/model/schema~ModelSchema#isSelectable selectable elements} can be selected from the outside
* (e.g. `[<paragraph>foo</paragraph>]` is invalid). This rule applies independently to both selection ends, so this
* selection is correct: `<paragraph>f[oo</paragraph><imageBlock></imageBlock>]`.
*
* If the position is not correct, the post-fixer will automatically correct it.
*
* ## Fixing a non-collapsed selection
*
* See as an example a selection that starts in a P1 element and ends inside the text of a TD element
* (`[` and `]` are range boundaries and `(l)` denotes an element defined as `isLimit=true`):
*
* ```
* root
*  |- element P1
*  |   |- "foo"                                      root
*  |- element TABLE (l)                   P1         TABLE             P2
*  |   |- element TR (l)                 f o[o     TR      TR         b a r
*  |   |   |- element TD (l)                       TD      TD
*  |   |       |- "aaa"                          a]a a    b b b
*  |   |- element TR (l)
*  |   |   |- element TD (l)                           ||
*  |   |       |- "bbb"                                ||
*  |- element P2                                       VV
*  |   |- "bar"
*                                                    root
*                                         P1         TABLE]            P2
*                                        f o[o     TR      TR         b a r
*                                                  TD      TD
*                                                a a a    b b b
* ```
*
* In the example above, the TABLE, TR and TD are defined as `isLimit=true` in the schema. The range which is not contained within
* a single limit element must be expanded to select the outermost limit element. The range end is inside the text node of the TD element.
* As the TD element is a child of the TR and TABLE elements, where both are defined as `isLimit=true` in the schema, the range must be
* expanded to select the whole TABLE element.
*
* **Note** If the selection contains multiple ranges, the method returns a minimal set of ranges that are not intersecting after expanding
* them to select `isLimit=true` elements.
*
* @internal
*/
function injectSelectionPostFixer(model) {
	model.document.registerPostFixer((writer) => selectionPostFixer(writer, model));
}
/**
* The selection post-fixer.
*/
function selectionPostFixer(writer, model) {
	const selection = model.document.selection;
	const schema = model.schema;
	const ranges = [];
	let wasFixed = false;
	for (const modelRange of selection.getRanges()) {
		const correctedRange = tryFixingRange(modelRange, schema);
		if (correctedRange && !correctedRange.isEqual(modelRange)) {
			ranges.push(correctedRange);
			wasFixed = true;
		} else ranges.push(modelRange);
	}
	if (wasFixed) writer.setSelection(mergeIntersectingRanges(ranges), { backward: selection.isBackward });
	return false;
}
/**
* Tries fixing a range if it's incorrect.
*
* **Note:** This helper is used by the selection post-fixer and to fix the `beforeinput` target ranges.
*
* @returns Returns fixed range or null if range is valid.
* @internal
*/
function tryFixingRange(range, schema) {
	if (range.isCollapsed) return tryFixingCollapsedRange(range, schema);
	return tryFixingNonCollapsedRage(range, schema);
}
/**
* Tries to fix collapsed ranges.
*
* * Fixes situation when a range is in a place where $text is not allowed
*
* @param range Collapsed range to fix.
* @returns Returns fixed range or null if range is valid.
*/
function tryFixingCollapsedRange(range, schema) {
	const originalPosition = range.start;
	const nearestSelectionRange = schema.getNearestSelectionRange(originalPosition);
	if (!nearestSelectionRange) {
		const ancestorObject = originalPosition.getAncestors().reverse().find((item) => schema.isObject(item));
		if (ancestorObject) return ModelRange._createOn(ancestorObject);
		return null;
	}
	if (!nearestSelectionRange.isCollapsed) return nearestSelectionRange;
	const fixedPosition = nearestSelectionRange.start;
	if (originalPosition.isEqual(fixedPosition)) return null;
	return new ModelRange(fixedPosition);
}
/**
* Tries to fix an expanded range.
*
* @param range Expanded range to fix.
* @returns Returns fixed range or null if range is valid.
*/
function tryFixingNonCollapsedRage(range, schema) {
	const { start, end } = range;
	const isTextAllowedOnStart = schema.checkChild(start, "$text");
	const isTextAllowedOnEnd = schema.checkChild(end, "$text");
	const startLimitElement = schema.getLimitElement(start);
	const endLimitElement = schema.getLimitElement(end);
	if (startLimitElement === endLimitElement) {
		if (isTextAllowedOnStart && isTextAllowedOnEnd) return null;
		if (checkSelectionOnNonLimitElements(start, end, schema)) {
			const fixedStart = start.nodeAfter && schema.isSelectable(start.nodeAfter) ? null : schema.getNearestSelectionRange(start, "forward");
			const fixedEnd = end.nodeBefore && schema.isSelectable(end.nodeBefore) ? null : schema.getNearestSelectionRange(end, "backward");
			return new ModelRange(fixedStart ? fixedStart.start : start, fixedEnd ? fixedEnd.end : end);
		}
	}
	const isStartInLimit = startLimitElement && !startLimitElement.is("rootElement");
	const isEndInLimit = endLimitElement && !endLimitElement.is("rootElement");
	if (isStartInLimit || isEndInLimit) {
		const bothInSameParent = start.nodeAfter && end.nodeBefore && start.nodeAfter.parent === end.nodeBefore.parent;
		const expandStart = isStartInLimit && (!bothInSameParent || !isSelectable(start.nodeAfter, schema));
		const expandEnd = isEndInLimit && (!bothInSameParent || !isSelectable(end.nodeBefore, schema));
		let fixedStart = start;
		let fixedEnd = end;
		if (expandStart) fixedStart = ModelPosition._createBefore(findOutermostLimitAncestor(startLimitElement, schema));
		if (expandEnd) fixedEnd = ModelPosition._createAfter(findOutermostLimitAncestor(endLimitElement, schema));
		return new ModelRange(fixedStart, fixedEnd);
	}
	return null;
}
/**
* Finds the outer-most ancestor.
*/
function findOutermostLimitAncestor(startingNode, schema) {
	let isLimitNode = startingNode;
	let parent = isLimitNode;
	while (schema.isLimit(parent) && parent.parent) {
		isLimitNode = parent;
		parent = parent.parent;
	}
	return isLimitNode;
}
/**
* Checks whether any of range boundaries is placed around non-limit elements.
*/
function checkSelectionOnNonLimitElements(start, end, schema) {
	const startIsOnBlock = start.nodeAfter && !schema.isLimit(start.nodeAfter) || schema.checkChild(start, "$text");
	const endIsOnBlock = end.nodeBefore && !schema.isLimit(end.nodeBefore) || schema.checkChild(end, "$text");
	return startIsOnBlock || endIsOnBlock;
}
/**
* Returns a minimal non-intersecting array of ranges without duplicates.
*
* @param ranges Ranges to merge.
* @returns Array of unique and non-intersecting ranges.
* @internal
*/
function mergeIntersectingRanges(ranges) {
	const rangesToMerge = [...ranges];
	const rangeIndexesToRemove = /* @__PURE__ */ new Set();
	let currentRangeIndex = 1;
	while (currentRangeIndex < rangesToMerge.length) {
		const currentRange = rangesToMerge[currentRangeIndex];
		const previousRanges = rangesToMerge.slice(0, currentRangeIndex);
		for (const [previousRangeIndex, previousRange] of previousRanges.entries()) {
			if (rangeIndexesToRemove.has(previousRangeIndex)) continue;
			if (currentRange.isEqual(previousRange)) rangeIndexesToRemove.add(previousRangeIndex);
			else if (currentRange.isIntersecting(previousRange)) {
				rangeIndexesToRemove.add(previousRangeIndex);
				rangeIndexesToRemove.add(currentRangeIndex);
				const mergedRange = currentRange.getJoined(previousRange);
				rangesToMerge.push(mergedRange);
			}
		}
		currentRangeIndex++;
	}
	return rangesToMerge.filter((_, index) => !rangeIndexesToRemove.has(index));
}
/**
* Checks if node exists and if it's a selectable.
*/
function isSelectable(node, schema) {
	return node && schema.isSelectable(node);
}

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* @module engine/model/utils/deletecontent
*/
/**
* Deletes content of the selection and merge siblings. The resulting selection is always collapsed.
*
* **Note:** Use {@link module:engine/model/model~Model#deleteContent} instead of this function.
* This function is only exposed to be reusable in algorithms
* which change the {@link module:engine/model/model~Model#deleteContent}
* method's behavior.
*
* @param model The model in context of which the insertion should be performed.
* @param selection Selection of which the content should be deleted.
* @param options.leaveUnmerged Whether to merge elements after removing the content of the selection.
*
* For example `<heading>x[x</heading><paragraph>y]y</paragraph>` will become:
*
* * `<heading>x^y</heading>` with the option disabled (`leaveUnmerged == false`)
* * `<heading>x^</heading><paragraph>y</paragraph>` with enabled (`leaveUnmerged == true`).
*
* Note: {@link module:engine/model/schema~ModelSchema#isObject object} and {@link module:engine/model/schema~ModelSchema#isLimit limit}
* elements will not be merged.
*
* @param options.doNotResetEntireContent Whether to skip replacing the entire content with a
* paragraph when the entire content was selected.
*
* For example `<heading>[x</heading><paragraph>y]</paragraph>` will become:
*
* * `<paragraph>^</paragraph>` with the option disabled (`doNotResetEntireContent == false`)
* * `<heading>^</heading>` with enabled (`doNotResetEntireContent == true`).
*
* @param options.doNotAutoparagraph Whether to create a paragraph if after content deletion selection is moved
* to a place where text cannot be inserted.
*
* For example `<paragraph>x</paragraph>[<imageBlock src="foo.jpg"></imageBlock>]` will become:
*
* * `<paragraph>x</paragraph><paragraph>[]</paragraph>` with the option disabled (`doNotAutoparagraph == false`)
* * `<paragraph>x</paragraph>[]` with the option enabled (`doNotAutoparagraph == true`).
*
* If you use this option you need to make sure to handle invalid selections yourself or leave
* them to the selection post-fixer (may not always work).
*
* **Note:** If there is no valid position for the selection, the paragraph will always be created:
*
* `[<imageBlock src="foo.jpg"></imageBlock>]` -> `<paragraph>[]</paragraph>`.
*
* @param options.doNotFixSelection Whether given selection-to-remove should be fixed if it ends at the beginning of an element.
*
* By default, `deleteContent()` will fix selection before performing a deletion, so that the selection does not end at the beginning of
* an element. For example, selection `<heading>[Heading</heading><paragraph>]Some text.</paragraph>` will be treated as it was
* `<heading>[Heading]</heading><paragraph>Some text.</paragraph>`. As a result, the elements will not get merged.
*
* If selection is as in example, visually, the next element (paragraph) is not selected and it may be confusing for the user that
* the elements got merged. Selection is set up like this by browsers when a user triple-clicks on some text.
*
* However, in some cases, it is expected to remove content exactly as selected in the selection, without any fixing. In these cases,
* this flag can be set to `true`, which will prevent fixing the selection.
*
* @internal
*/
function deleteContent(model, selection, options = {}) {
	if (selection.isCollapsed) return;
	const selRange = selection.getFirstRange();
	if (selRange.root.rootName == "$graveyard") return;
	const schema = model.schema;
	const documentSelection = model.document.selection;
	const selectionIsDocumentSelection = isRelatedToDocumentSelection(selection, documentSelection, selRange);
	const selectionAttributes = Array.from(documentSelection.getAttributes());
	const selectionParentWasEmpty = !!documentSelection.getFirstRange()?.start.parent.isEmpty;
	model.change((writer) => {
		if (!options.doNotResetEntireContent && shouldEntireContentBeReplacedWithParagraph(schema, selection)) {
			replaceEntireContentWithParagraph(writer, selection);
			return;
		}
		const attributesForAutoparagraph = {};
		if (!options.doNotAutoparagraph) {
			const selectedElement = selection.getSelectedElement();
			if (selectedElement) Object.assign(attributesForAutoparagraph, schema.getAttributesWithProperty(selectedElement, "copyOnReplace", true));
		}
		let startPosition, endPosition;
		if (!options.doNotFixSelection) [startPosition, endPosition] = getLivePositionsForSelectedBlocks(selRange);
		else {
			startPosition = ModelLivePosition.fromPosition(selRange.start, "toPrevious");
			endPosition = ModelLivePosition.fromPosition(selRange.end, "toNext");
		}
		if (!startPosition.isTouching(endPosition)) writer.remove(writer.createRange(startPosition, endPosition));
		if (!options.leaveUnmerged) {
			mergeBranches(writer, startPosition, endPosition);
			schema.removeDisallowedAttributes(startPosition.parent.getChildren(), writer);
		}
		collapseSelectionAt(writer, selection, startPosition);
		if (!options.doNotAutoparagraph && shouldAutoparagraph(schema, startPosition)) insertParagraph(writer, startPosition, selection, attributesForAutoparagraph);
		if (selectionIsDocumentSelection) restoreSelectionAttributesOnEmptyParent(writer, selectionAttributes, selectionParentWasEmpty);
		startPosition.detach();
		endPosition.detach();
	});
}
/**
* Returns the live positions for the range adjusted to span only blocks selected from the user perspective. Example:
*
* ```
* <heading1>[foo</heading1>
* <paragraph>bar</paragraph>
* <heading1>]abc</heading1>  <-- this block is not considered as selected
* ```
*
* This is the same behavior as in Selection#getSelectedBlocks() "special case".
*/
function getLivePositionsForSelectedBlocks(range) {
	const model = range.root.document.model;
	const startPosition = range.start;
	let endPosition = range.end;
	if (model.hasContent(range, { ignoreMarkers: true })) {
		const endBlock = getParentBlock(endPosition);
		if (endBlock && endPosition.isTouching(model.createPositionAt(endBlock, 0))) {
			const selection = model.createSelection(range);
			model.modifySelection(selection, { direction: "backward" });
			const newEndPosition = selection.getLastPosition();
			const skippedRange = model.createRange(newEndPosition, endPosition);
			if (!model.hasContent(skippedRange, { ignoreMarkers: true })) endPosition = newEndPosition;
		}
	}
	return [ModelLivePosition.fromPosition(startPosition, "toPrevious"), ModelLivePosition.fromPosition(endPosition, "toNext")];
}
/**
* Finds the lowest element in position's ancestors which is a block.
* Returns null if a limit element is encountered before reaching a block element.
*/
function getParentBlock(position) {
	const element = position.parent;
	const schema = element.root.document.model.schema;
	const ancestors = element.getAncestors({
		parentFirst: true,
		includeSelf: true
	});
	for (const element of ancestors) {
		if (schema.isLimit(element)) return null;
		if (schema.isBlock(element)) return element;
	}
}
/**
* This function is a result of reaching the Ballmer's peak for just the right amount of time.
* Even I had troubles documenting it after a while and after reading it again I couldn't believe that it really works.
*/
function mergeBranches(writer, startPosition, endPosition) {
	const model = writer.model;
	if (!checkShouldMerge(writer.model.schema, startPosition, endPosition)) return;
	const [startAncestor, endAncestor] = getAncestorsJustBelowCommonAncestor(startPosition, endPosition);
	if (!startAncestor || !endAncestor) return;
	if (!model.hasContent(startAncestor, { ignoreMarkers: true }) && model.hasContent(endAncestor, { ignoreMarkers: true })) mergeBranchesRight(writer, startPosition, endPosition, startAncestor.parent);
	else mergeBranchesLeft(writer, startPosition, endPosition, startAncestor.parent);
}
/**
* Merging blocks to the left (properties of the left block are preserved).
* Simple example:
*
* ```
* <heading1>foo[</heading1>    ->  <heading1>foo[bar</heading1>]
* <paragraph>]bar</paragraph>  ->              --^
* ```
*
* Nested example:
*
* ```
* <blockQuote>                     ->  <blockQuote>
*     <heading1>foo[</heading1>    ->      <heading1>foo[bar</heading1>
* </blockQuote>                    ->  </blockQuote>]    ^
* <blockBlock>                     ->                    |
*     <paragraph>]bar</paragraph>  ->                 ---
* </blockBlock>                    ->
* ```
*/
function mergeBranchesLeft(writer, startPosition, endPosition, commonAncestor) {
	const startElement = startPosition.parent;
	const endElement = endPosition.parent;
	if (startElement == commonAncestor || endElement == commonAncestor) return;
	startPosition = writer.createPositionAfter(startElement);
	endPosition = writer.createPositionBefore(endElement);
	if (!endPosition.isEqual(startPosition)) writer.insert(endElement, startPosition);
	writer.merge(startPosition);
	while (endPosition.parent.isEmpty) {
		const parentToRemove = endPosition.parent;
		endPosition = writer.createPositionBefore(parentToRemove);
		writer.remove(parentToRemove);
	}
	if (!checkShouldMerge(writer.model.schema, startPosition, endPosition)) return;
	mergeBranchesLeft(writer, startPosition, endPosition, commonAncestor);
}
/**
* Merging blocks to the right (properties of the right block are preserved).
* Simple example:
*
* ```
* <heading1>foo[</heading1>    ->            --v
* <paragraph>]bar</paragraph>  ->  [<paragraph>foo]bar</paragraph>
* ```
*
* Nested example:
*
* ```
* <blockQuote>                     ->
*     <heading1>foo[</heading1>    ->              ---
* </blockQuote>                    ->                 |
* <blockBlock>                     ->  [<blockBlock>  v
*     <paragraph>]bar</paragraph>  ->      <paragraph>foo]bar</paragraph>
* </blockBlock>                    ->  </blockBlock>
* ```
*/
function mergeBranchesRight(writer, startPosition, endPosition, commonAncestor) {
	const startElement = startPosition.parent;
	const endElement = endPosition.parent;
	if (startElement == commonAncestor || endElement == commonAncestor) return;
	startPosition = writer.createPositionAfter(startElement);
	endPosition = writer.createPositionBefore(endElement);
	if (!endPosition.isEqual(startPosition)) writer.insert(startElement, endPosition);
	while (startPosition.parent.isEmpty) {
		const parentToRemove = startPosition.parent;
		startPosition = writer.createPositionBefore(parentToRemove);
		writer.remove(parentToRemove);
	}
	endPosition = writer.createPositionBefore(endElement);
	mergeRight(writer, endPosition);
	if (!checkShouldMerge(writer.model.schema, startPosition, endPosition)) return;
	mergeBranchesRight(writer, startPosition, endPosition, commonAncestor);
}
/**
* There is no right merge operation so we need to simulate it.
*/
function mergeRight(writer, position) {
	const startElement = position.nodeBefore;
	const endElement = position.nodeAfter;
	if (startElement.name != endElement.name) writer.rename(startElement, endElement.name);
	writer.clearAttributes(startElement);
	writer.setAttributes(Object.fromEntries(endElement.getAttributes()), startElement);
	writer.merge(position);
}
/**
* Verifies if merging is needed and possible. It's not needed if both positions are in the same element
* and it's not possible if some element is a limit or the range crosses a limit element.
*/
function checkShouldMerge(schema, startPosition, endPosition) {
	const startElement = startPosition.parent;
	const endElement = endPosition.parent;
	if (startElement == endElement) return false;
	if (schema.isLimit(startElement) || schema.isLimit(endElement)) return false;
	return isCrossingLimitElement(startPosition, endPosition, schema);
}
/**
* Returns the elements that are the ancestors of the provided positions that are direct children of the common ancestor.
*/
function getAncestorsJustBelowCommonAncestor(positionA, positionB) {
	const ancestorsA = positionA.getAncestors();
	const ancestorsB = positionB.getAncestors();
	let i = 0;
	while (ancestorsA[i] && ancestorsA[i] == ancestorsB[i]) i++;
	return [ancestorsA[i], ancestorsB[i]];
}
function shouldAutoparagraph(schema, position) {
	const isTextAllowed = schema.checkChild(position, "$text");
	const isParagraphAllowed = schema.checkChild(position, "paragraph");
	return !isTextAllowed && isParagraphAllowed;
}
/**
* Check if parents of two positions can be merged by checking if there are no limit/object
* boundaries between those two positions.
*
* E.g. in <bQ><p>x[]</p></bQ><widget><caption>{}</caption></widget>
* we'll check <p>, <bQ>, <widget> and <caption>.
* Usually, widget and caption are marked as objects/limits in the schema, so in this case merging will be blocked.
*/
function isCrossingLimitElement(leftPos, rightPos, schema) {
	const rangeToCheck = new ModelRange(leftPos, rightPos);
	for (const value of rangeToCheck.getWalker()) if (schema.isLimit(value.item)) return false;
	return true;
}
function insertParagraph(writer, position, selection, attributes = {}) {
	const paragraph = writer.createElement("paragraph");
	writer.model.schema.setAllowedAttributes(paragraph, attributes, writer);
	writer.insert(paragraph, position);
	collapseSelectionAt(writer, selection, writer.createPositionAt(paragraph, 0));
}
function replaceEntireContentWithParagraph(writer, selection) {
	const limitElement = writer.model.schema.getLimitElement(selection);
	writer.remove(writer.createRangeIn(limitElement));
	insertParagraph(writer, writer.createPositionAt(limitElement, 0), selection);
}
/**
* We want to replace the entire content with a paragraph when:
* * the entire content is selected,
* * selection contains at least two elements,
* * whether the paragraph is allowed in schema in the common ancestor.
*/
function shouldEntireContentBeReplacedWithParagraph(schema, selection) {
	const limitElement = schema.getLimitElement(selection);
	if (!selection.containsEntireContent(limitElement)) return false;
	const range = selection.getFirstRange();
	if (range.start.parent == range.end.parent) return false;
	return schema.checkChild(limitElement, "paragraph");
}
/**
* Helper function that sets the selection. Depending whether given `selection` is a document selection or not,
* uses a different method to set it.
*/
function collapseSelectionAt(writer, selection, positionOrRange) {
	if (selection instanceof ModelDocumentSelection) writer.setSelection(positionOrRange);
	else selection.setTo(positionOrRange);
}
/**
* Restores the document selection attributes after a deletion that leaves the selection in an empty parent block.
* This preserves the pre-delete formatting (e.g. bold, italic) so that subsequent typing continues in the same style.
*
* Attributes are only restored when:
* - There were attributes on the selection before the deletion.
* - The deletion left the document selection's parent block empty.
* - The parent block was **not** already empty before the deletion — this ensures that attributes are not
*   re-applied when `deleteContent()` was called on a completely unrelated block.
*/
function restoreSelectionAttributesOnEmptyParent(writer, selectionAttributes, selectionParentWasEmpty) {
	if (!selectionAttributes.length) return;
	const documentSelection = writer.model.document.selection;
	if (!documentSelection.anchor.parent.isEmpty) return;
	if (selectionParentWasEmpty) return;
	for (const [key, value] of selectionAttributes) if (writer.model.schema.getAttributeProperties(key).isFormatting && writer.model.schema.checkAttributeInSelection(documentSelection, key)) writer.setSelectionAttribute(key, value);
}
/**
* Checks whether the provided selection is related to the document selection.
*
* Returns `true` when:
* - the selection is a `DocumentSelection` instance, or
* - the document selection is collapsed and sits at the start or end of the given range, or
* - the document selection is not collapsed and its range intersects the given range.
*/
function isRelatedToDocumentSelection(selection, documentSelection, selRange) {
	if (selection instanceof ModelDocumentSelection) return true;
	if (documentSelection.isCollapsed) {
		const position = documentSelection.getFirstPosition();
		return position.isEqual(selRange.start) || position.isEqual(selRange.end);
	}
	return !!documentSelection.getFirstRange()?.isIntersecting(selRange);
}

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* Gets a clone of the selected content.
*
* For example, for the following selection:
*
* ```html
* <p>x</p><quote><p>y</p><h>fir[st</h></quote><p>se]cond</p><p>z</p>
* ```
*
* It will return a document fragment with such a content:
*
* ```html
* <quote><h>st</h></quote><p>se</p>
* ```
*
* @param model The model in context of which the selection modification should be performed.
* @param selection The selection of which content will be returned.
* @internal
*/
function getSelectedContent(model, selection) {
	return model.change((writer) => {
		const frag = writer.createDocumentFragment();
		const range = selection.getFirstRange();
		if (!range || range.isCollapsed) return frag;
		const root = range.start.root;
		const commonPath = range.start.getCommonPath(range.end);
		const commonParent = root.getNodeByPath(commonPath);
		let flatSubtreeRange;
		if (range.start.parent == range.end.parent) flatSubtreeRange = range;
		else flatSubtreeRange = writer.createRange(writer.createPositionAt(commonParent, range.start.path[commonPath.length]), writer.createPositionAt(commonParent, range.end.path[commonPath.length] + 1));
		const howMany = flatSubtreeRange.end.offset - flatSubtreeRange.start.offset;
		for (const item of flatSubtreeRange.getItems({ shallow: true })) if (item.is("$textProxy")) writer.appendText(item.data, item.getAttributes(), frag);
		else writer.append(writer.cloneElement(item, true), frag);
		if (flatSubtreeRange != range) {
			const newRange = range._getTransformedByMove(flatSubtreeRange.start, writer.createPositionAt(frag, 0), howMany)[0];
			const leftExcessRange = writer.createRange(writer.createPositionAt(frag, 0), newRange.start);
			removeRangeContent(writer.createRange(newRange.end, writer.createPositionAt(frag, "end")), writer);
			removeRangeContent(leftExcessRange, writer);
		}
		return frag;
	});
}
function removeRangeContent(range, writer) {
	const parentsToCheck = [];
	Array.from(range.getItems({ direction: "backward" })).map((item) => writer.createRangeOn(item)).filter((itemRange) => {
		return (itemRange.start.isAfter(range.start) || itemRange.start.isEqual(range.start)) && (itemRange.end.isBefore(range.end) || itemRange.end.isEqual(range.end));
	}).forEach((itemRange) => {
		parentsToCheck.push(itemRange.start.parent);
		writer.remove(itemRange);
	});
	parentsToCheck.forEach((parentToCheck) => {
		let parent = parentToCheck;
		while (parent.parent && parent.isEmpty) {
			const removeRange = writer.createRangeOn(parent);
			parent = parent.parent;
			writer.remove(removeRange);
		}
	});
}

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* @module engine/model/utils/insertcontent
*/
/**
* Inserts content into the editor (specified selection) as one would expect the paste functionality to work.
*
* It takes care of removing the selected content, splitting elements (if needed), inserting elements and merging elements appropriately.
*
* Some examples:
*
* ```html
* <p>x^</p> + <p>y</p> => <p>x</p><p>y</p> => <p>xy[]</p>
* <p>x^y</p> + <p>z</p> => <p>x</p>^<p>y</p> + <p>z</p> => <p>x</p><p>z</p><p>y</p> => <p>xz[]y</p>
* <p>x^y</p> + <img /> => <p>x</p>^<p>y</p> + <img /> => <p>x</p><img /><p>y</p>
* <p>x</p><p>^</p><p>z</p> + <p>y</p> => <p>x</p><p>y[]</p><p>z</p> (no merging)
* <p>x</p>[<img />]<p>z</p> + <p>y</p> => <p>x</p>^<p>z</p> + <p>y</p> => <p>x</p><p>y[]</p><p>z</p>
* ```
*
* If an instance of {@link module:engine/model/selection~ModelSelection} is passed as `selectable` it will be modified
* to the insertion selection (equal to a range to be selected after insertion).
*
* If `selectable` is not passed, the content will be inserted using the current selection of the model document.
*
* **Note:** Use {@link module:engine/model/model~Model#insertContent} instead of this function.
* This function is only exposed to be reusable in algorithms which change the {@link module:engine/model/model~Model#insertContent}
* method's behavior.
*
* @param model The model in context of which the insertion should be performed.
* @param content The content to insert.
* @param selectable Selection into which the content should be inserted.
* @returns Range which contains all the performed changes. This is a range that, if removed,
* would return the model to the state before the insertion. If no changes were preformed by `insertContent`, returns a range collapsed
* at the insertion position.
* @internal
*/
function insertContent(model, content, selectable) {
	return model.change((writer) => {
		const selection = selectable ? selectable : model.document.selection;
		if (!selection.isCollapsed) model.deleteContent(selection, { doNotAutoparagraph: true });
		const insertion = new Insertion(model, writer, selection.anchor);
		const fakeMarkerElements = [];
		let nodesToInsert;
		if (content.is("documentFragment")) {
			if (content.markers.size) {
				const markersPosition = [];
				for (const [name, range] of content.markers) {
					const { start, end } = range;
					const isCollapsed = start.isEqual(end);
					markersPosition.push({
						position: start,
						name,
						isCollapsed
					}, {
						position: end,
						name,
						isCollapsed
					});
				}
				markersPosition.sort(({ position: posA }, { position: posB }) => posA.isBefore(posB) ? 1 : -1);
				for (const { position, name, isCollapsed } of markersPosition) {
					let fakeElement = null;
					let collapsed = null;
					const isAtBeginning = position.parent === content && position.isAtStart;
					const isAtEnd = position.parent === content && position.isAtEnd;
					if (!isAtBeginning && !isAtEnd) {
						fakeElement = writer.createElement("$marker");
						writer.insert(fakeElement, position);
					} else if (isCollapsed) collapsed = isAtBeginning ? "start" : "end";
					fakeMarkerElements.push({
						name,
						element: fakeElement,
						collapsed
					});
				}
			}
			nodesToInsert = content.getChildren();
		} else nodesToInsert = [content];
		insertion.handleNodes(nodesToInsert);
		let newRange = insertion.getSelectionRange();
		if (content.is("documentFragment") && fakeMarkerElements.length) {
			const selectionLiveRange = newRange ? ModelLiveRange.fromRange(newRange) : null;
			const markersData = {};
			for (let i = fakeMarkerElements.length - 1; i >= 0; i--) {
				const { name, element, collapsed } = fakeMarkerElements[i];
				const isStartBoundary = !markersData[name];
				if (isStartBoundary) markersData[name] = [];
				if (element) {
					const elementPosition = writer.createPositionAt(element, "before");
					markersData[name].push(elementPosition);
					writer.remove(element);
				} else {
					const rangeOnInsertion = insertion.getAffectedRange();
					if (!rangeOnInsertion) {
						if (collapsed) markersData[name].push(insertion.position);
						continue;
					}
					if (collapsed) markersData[name].push(rangeOnInsertion[collapsed]);
					else markersData[name].push(isStartBoundary ? rangeOnInsertion.start : rangeOnInsertion.end);
				}
			}
			for (const [name, [start, end]] of Object.entries(markersData)) if (start && end && start.root === end.root && start.root.document && !writer.model.markers.has(name)) writer.addMarker(name, {
				usingOperation: true,
				affectsData: true,
				range: new ModelRange(start, end)
			});
			if (selectionLiveRange) {
				newRange = selectionLiveRange.toRange();
				selectionLiveRange.detach();
			}
		}
		/* istanbul ignore else -- @preserve */
		if (newRange) if (selection instanceof ModelDocumentSelection) writer.setSelection(newRange);
		else selection.setTo(newRange);
		const affectedRange = insertion.getAffectedRange() || model.createRange(selection.anchor);
		insertion.destroy();
		return affectedRange;
	});
}
/**
* Utility class for performing content insertion.
*/
var Insertion = class {
	/**
	* The model in context of which the insertion should be performed.
	*/
	model;
	/**
	* Batch to which operations will be added.
	*/
	writer;
	/**
	* The position at which (or near which) the next node will be inserted.
	*/
	position;
	/**
	* Elements with which the inserted elements can be merged.
	*
	* ```html
	* <p>x^</p><p>y</p> + <p>z</p> (can merge to <p>x</p>)
	* <p>x</p><p>^y</p> + <p>z</p> (can merge to <p>y</p>)
	* <p>x^y</p> + <p>z</p> (can merge to <p>xy</p> which will be split during the action,
	* 						so both its pieces will be added to this set)
	* ```
	*/
	canMergeWith;
	/**
	* Schema of the model.
	*/
	schema;
	/**
	* The temporary ModelDocumentFragment used for grouping multiple nodes for single insert operation.
	*/
	_documentFragment;
	/**
	* The current position in the temporary ModelDocumentFragment.
	*/
	_documentFragmentPosition;
	/**
	* The reference to the first inserted node.
	*/
	_firstNode = null;
	/**
	* The reference to the last inserted node.
	*/
	_lastNode = null;
	/**
	* The reference to the last auto paragraph node.
	*/
	_lastAutoParagraph = null;
	/**
	* The array of nodes that should be cleaned of not allowed attributes and sub-nodes.
	*/
	_filterAttributesAndChildrenOf = [];
	/**
	* Beginning of the affected range. See {@link module:engine/model/utils/insertcontent~Insertion#getAffectedRange}.
	*/
	_affectedStart = null;
	/**
	* End of the affected range. See {@link module:engine/model/utils/insertcontent~Insertion#getAffectedRange}.
	*/
	_affectedEnd = null;
	_nodeToSelect = null;
	constructor(model, writer, position) {
		this.model = model;
		this.writer = writer;
		this.position = position;
		this.canMergeWith = new Set([this.position.parent]);
		this.schema = model.schema;
		this._documentFragment = writer.createDocumentFragment();
		this._documentFragmentPosition = writer.createPositionAt(this._documentFragment, 0);
	}
	/**
	* Handles insertion of a set of nodes.
	*
	* @param nodes Nodes to insert.
	*/
	handleNodes(nodes) {
		for (const node of Array.from(nodes)) if (node.offsetSize > 0) this._handleNode(node);
		this._insertPartialFragment();
		if (this._lastAutoParagraph) this._updateLastNodeFromAutoParagraph(this._lastAutoParagraph);
		this._mergeOnRight();
		this.schema.removeDisallowedAttributes(this._filterAttributesAndChildrenOf, this.writer);
		if (this.model._config?.get("experimentalFlags.modelInsertContentDeepSchemaVerification") !== false) this._removeDisallowedChildren(this._filterAttributesAndChildrenOf);
		this._filterAttributesAndChildrenOf = [];
	}
	/**
	* Removes disallowed children from nodes that were inserted or modified during insertion.
	*/
	_removeDisallowedChildren(nodes) {
		const nodesArray = Array.from(nodes);
		for (const node of nodesArray) {
			if (!node.is("element")) continue;
			const remove = [];
			const unwrap = [];
			const walker = this.writer.createRangeIn(node).getWalker({ ignoreElementEnd: true });
			for (const { item } of walker) {
				const itemParent = item.parent;
				if (!this.schema.checkChild(itemParent, item)) {
					if (item.is("element") && !this.schema.isObject(item)) {
						unwrap.push(item);
						nodesArray.push(itemParent);
					} else remove.push(item);
					walker.jumpTo(this.writer.createPositionAfter(item));
				}
			}
			for (const item of unwrap) this.writer.unwrap(item);
			for (const item of remove) this.writer.remove(item);
		}
	}
	/**
	* Updates the last node after the auto paragraphing.
	*
	* @param node The last auto paragraphing node.
	*/
	_updateLastNodeFromAutoParagraph(node) {
		const positionAfterLastNode = this.writer.createPositionAfter(this._lastNode);
		const positionAfterNode = this.writer.createPositionAfter(node);
		if (positionAfterNode.isAfter(positionAfterLastNode)) {
			this._lastNode = node;
			/* istanbul ignore if -- @preserve */
			if (this.position.parent != node || !this.position.isAtEnd) throw new CKEditorError("insertcontent-invalid-insertion-position", this);
			this.position = positionAfterNode;
			this._setAffectedBoundaries(this.position);
		}
	}
	/**
	* Returns range to be selected after insertion.
	* Returns `null` if there is no valid range to select after insertion.
	*/
	getSelectionRange() {
		if (this._nodeToSelect) return ModelRange._createOn(this._nodeToSelect);
		return this.model.schema.getNearestSelectionRange(this.position);
	}
	/**
	* Returns a range which contains all the performed changes. This is a range that, if removed, would return the model to the state
	* before the insertion. Returns `null` if no changes were done.
	*/
	getAffectedRange() {
		if (!this._affectedStart) return null;
		return new ModelRange(this._affectedStart, this._affectedEnd);
	}
	/**
	* Destroys `Insertion` instance.
	*/
	destroy() {
		if (this._affectedStart) this._affectedStart.detach();
		if (this._affectedEnd) this._affectedEnd.detach();
	}
	/**
	* Handles insertion of a single node.
	*/
	_handleNode(node) {
		if (!this._checkAndSplitToAllowedPosition(node)) {
			if (!this.schema.isObject(node)) this._handleDisallowedNode(node);
			return;
		}
		node = this._appendToFragment(node);
		if (!this._firstNode) this._firstNode = node;
		this._lastNode = node;
	}
	/**
	* Inserts the temporary ModelDocumentFragment into the model.
	*/
	_insertPartialFragment() {
		if (this._documentFragment.isEmpty) return;
		const livePosition = ModelLivePosition.fromPosition(this.position, "toNext");
		this._setAffectedBoundaries(this.position);
		if (this._documentFragment.getChild(0) == this._firstNode) {
			this.writer.insert(this._firstNode, this.position);
			this._mergeOnLeft();
			this.position = livePosition.toPosition();
		}
		if (!this._documentFragment.isEmpty) this.writer.insert(this._documentFragment, this.position);
		this._documentFragmentPosition = this.writer.createPositionAt(this._documentFragment, 0);
		this.position = livePosition.toPosition();
		livePosition.detach();
	}
	/**
	* @param node The disallowed node which needs to be handled.
	*/
	_handleDisallowedNode(node) {
		if (node.is("element")) this.handleNodes(node.getChildren());
	}
	/**
	* Append a node to the temporary ModelDocumentFragment.
	*
	* @param node The node to insert.
	*/
	_appendToFragment(node) {
		/* istanbul ignore if -- @preserve */
		if (!this.schema.checkChild(this.position, node))
 /**
		* Given node cannot be inserted on the given position.
		*
		* @error insertcontent-wrong-position
		* @param {module:engine/model/node~ModelNode} node Node to insert.
		* @param {module:engine/model/position~ModelPosition} position Position to insert the node at.
		*/
		throw new CKEditorError("insertcontent-wrong-position", this, {
			node,
			position: this.position
		});
		this.writer.insert(node, this._documentFragmentPosition);
		this._documentFragmentPosition = this._documentFragmentPosition.getShiftedBy(node.offsetSize);
		if (!node.parent) node = this._documentFragmentPosition.nodeBefore;
		if (this.schema.isObject(node) && !this.schema.checkChild(this.position, "$text")) this._nodeToSelect = node;
		else this._nodeToSelect = null;
		this._filterAttributesAndChildrenOf.push(node);
		return node;
	}
	/**
	* Sets `_affectedStart` and `_affectedEnd` to the given `position`. Should be used before a change is done during insertion process to
	* mark the affected range.
	*
	* This method is used before inserting a node or splitting a parent node. `_affectedStart` and `_affectedEnd` are also changed
	* during merging, but the logic there is more complicated so it is left out of this function.
	*/
	_setAffectedBoundaries(position) {
		if (!this._affectedStart) this._affectedStart = ModelLivePosition.fromPosition(position, "toPrevious");
		if (!this._affectedEnd || this._affectedEnd.isBefore(position)) {
			if (this._affectedEnd) this._affectedEnd.detach();
			this._affectedEnd = ModelLivePosition.fromPosition(position, "toNext");
		}
	}
	/**
	* Merges the previous sibling of the first node if it should be merged.
	*
	* After the content was inserted we may try to merge it with its siblings.
	* This should happen only if the selection was in those elements initially.
	*/
	_mergeOnLeft() {
		const node = this._firstNode;
		if (!(node instanceof ModelElement)) return;
		if (!this._canMergeLeft(node)) return;
		const mergePosLeft = ModelLivePosition._createBefore(node);
		mergePosLeft.stickiness = "toNext";
		const livePosition = ModelLivePosition.fromPosition(this.position, "toNext");
		if (this._affectedStart.isEqual(mergePosLeft)) {
			this._affectedStart.detach();
			this._affectedStart = ModelLivePosition._createAt(mergePosLeft.nodeBefore, "end", "toPrevious");
		}
		if (this._firstNode === this._lastNode) {
			this._firstNode = mergePosLeft.nodeBefore;
			this._lastNode = mergePosLeft.nodeBefore;
		}
		this.writer.merge(mergePosLeft);
		if (mergePosLeft.isEqual(this._affectedEnd) && this._firstNode === this._lastNode) {
			this._affectedEnd.detach();
			this._affectedEnd = ModelLivePosition._createAt(mergePosLeft.nodeBefore, "end", "toNext");
		}
		this.position = livePosition.toPosition();
		livePosition.detach();
		this._filterAttributesAndChildrenOf.push(this.position.parent);
		mergePosLeft.detach();
	}
	/**
	* Merges the next sibling of the last node if it should be merged.
	*
	* After the content was inserted we may try to merge it with its siblings.
	* This should happen only if the selection was in those elements initially.
	*/
	_mergeOnRight() {
		const node = this._lastNode;
		if (!(node instanceof ModelElement)) return;
		if (!this._canMergeRight(node)) return;
		const mergePosRight = ModelLivePosition._createAfter(node);
		mergePosRight.stickiness = "toNext";
		/* istanbul ignore if -- @preserve */
		if (!this.position.isEqual(mergePosRight))
 /**
		* An internal error occurred when merging inserted content with its siblings.
		* The insertion position should equal the merge position.
		*
		* If you encountered this error, report it back to the CKEditor 5 team
		* with as many details as possible regarding the content being inserted and the insertion position.
		*
		* @error insertcontent-invalid-insertion-position
		*/
		throw new CKEditorError("insertcontent-invalid-insertion-position", this);
		this.position = ModelPosition._createAt(mergePosRight.nodeBefore, "end");
		const livePosition = ModelLivePosition.fromPosition(this.position, "toPrevious");
		if (this._affectedEnd.isEqual(mergePosRight)) {
			this._affectedEnd.detach();
			this._affectedEnd = ModelLivePosition._createAt(mergePosRight.nodeBefore, "end", "toNext");
		}
		if (this._firstNode === this._lastNode) {
			this._firstNode = mergePosRight.nodeBefore;
			this._lastNode = mergePosRight.nodeBefore;
		}
		this.writer.merge(mergePosRight);
		if (mergePosRight.getShiftedBy(-1).isEqual(this._affectedStart) && this._firstNode === this._lastNode) {
			this._affectedStart.detach();
			this._affectedStart = ModelLivePosition._createAt(mergePosRight.nodeBefore, 0, "toPrevious");
		}
		this.position = livePosition.toPosition();
		livePosition.detach();
		this._filterAttributesAndChildrenOf.push(this.position.parent);
		mergePosRight.detach();
	}
	/**
	* Checks whether specified node can be merged with previous sibling element.
	*
	* @param node The node which could potentially be merged.
	*/
	_canMergeLeft(node) {
		const previousSibling = node.previousSibling;
		return previousSibling instanceof ModelElement && this.canMergeWith.has(previousSibling) && this.model.schema.checkMerge(previousSibling, node);
	}
	/**
	* Checks whether specified node can be merged with next sibling element.
	*
	* @param node The node which could potentially be merged.
	*/
	_canMergeRight(node) {
		const nextSibling = node.nextSibling;
		return nextSibling instanceof ModelElement && this.canMergeWith.has(nextSibling) && this.model.schema.checkMerge(node, nextSibling);
	}
	/**
	* Inserts a paragraph and moves the insertion position into it.
	*/
	_insertAutoParagraph() {
		this._insertPartialFragment();
		const paragraph = this.writer.createElement("paragraph");
		this.writer.insert(paragraph, this.position);
		this._setAffectedBoundaries(this.position);
		this._lastAutoParagraph = paragraph;
		this.position = this.writer.createPositionAt(paragraph, 0);
	}
	/**
	* @returns Whether an allowed position was found.
	* `false` is returned if the node isn't allowed at any position up in the tree, `true` if was.
	*/
	_checkAndSplitToAllowedPosition(node) {
		const allowedIn = this._getAllowedIn(this.position.parent, node);
		if (!allowedIn) return false;
		if (allowedIn != this.position.parent) this._insertPartialFragment();
		while (allowedIn != this.position.parent) if (this.position.isAtStart) {
			const parent = this.position.parent;
			this.position = this.writer.createPositionBefore(parent);
			if (parent.isEmpty && parent.parent === allowedIn) this.writer.remove(parent);
		} else if (this.position.isAtEnd) this.position = this.writer.createPositionAfter(this.position.parent);
		else {
			const tempPos = this.writer.createPositionAfter(this.position.parent);
			this._setAffectedBoundaries(this.position);
			this.writer.split(this.position);
			this.position = tempPos;
			this.canMergeWith.add(this.position.nodeAfter);
		}
		if (!this.schema.checkChild(this.position.parent, node)) this._insertAutoParagraph();
		return true;
	}
	/**
	* Gets the element in which the given node is allowed. It checks the passed element and all its ancestors.
	*
	* It also verifies if auto-paragraphing could help.
	*
	* @param contextElement The element in which context the node should be checked.
	* @param childNode The node to check.
	*/
	_getAllowedIn(contextElement, childNode) {
		const context = this.schema.createContext(contextElement);
		if (this.schema.checkChild(context, childNode)) return contextElement;
		if (this.schema.checkChild(context, "paragraph") && this.schema.checkChild(context.push("paragraph"), childNode)) return contextElement;
		if (this.schema.isLimit(contextElement)) return null;
		return this._getAllowedIn(contextElement.parent, childNode);
	}
};

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* Inserts an {@glink framework/deep-dive/schema#object-elements object element} at a specific position in the editor content.
*
* **Note:** Use {@link module:engine/model/model~Model#insertObject} instead of this function.
* This function is only exposed to be reusable in algorithms which change the {@link module:engine/model/model~Model#insertObject}
* method's behavior.
*
* **Note**: For more documentation and examples, see {@link module:engine/model/model~Model#insertObject}.
*
* @param model The model in context of which the insertion should be performed.
* @param object An object to be inserted into the model document.
* @param selectable A selectable where the content should be inserted. If not specified, the current
* {@link module:engine/model/document~ModelDocument#selection document selection} will be used instead.
* @param options Additional options.
* @param options.findOptimalPosition An option that, when set, adjusts the insertion position (relative to
* `selectable` and `placeOrOffset`) so that the content of `selectable` is not split upon insertion (a.k.a. non-destructive insertion).
* * When `'auto'`, the algorithm will decide whether to insert the object before or after `selectable` to avoid content splitting.
* * When `'before'`, the closest position before `selectable` will be used that will not result in content splitting.
* * When `'after'`, the closest position after `selectable` will be used that will not result in content splitting.
*
* Note that this option works only for block objects. Inline objects are inserted into text and do not split blocks.
* @param options.setSelection An option that, when set, moves the
* {@link module:engine/model/document~ModelDocument#selection document selection} after inserting the object.
* * When `'on'`, the document selection will be set on the inserted object.
* * When `'after'`, the document selection will move to the closest text node after the inserted object. If there is no
* such text node, a paragraph will be created and the document selection will be moved inside it.
* @returns A range which contains all the performed changes. This is a range that, if removed,
* would return the model to the state before the insertion. If no changes were preformed by `insertObject()`, returns a range collapsed
* at the insertion position.
*/
function insertObject(model, object, selectable, options = {}) {
	if (!model.schema.isObject(object))
 /**
	* Tried to insert an element with {@link module:engine/model/utils/insertobject insertObject()} function
	* that is not defined as an object in schema.
	* See {@link module:engine/model/schema~ModelSchemaItemDefinition#isObject `SchemaItemDefinition`}.
	* If you want to insert content that is not an object you might want to use
	* {@link module:engine/model/utils/insertcontent insertContent()} function.
	* @error insertobject-element-not-an-object
	*/
	throw new CKEditorError("insertobject-element-not-an-object", model, { object });
	const originalSelection = selectable ? selectable : model.document.selection;
	let insertionSelection = originalSelection;
	if (options.findOptimalPosition && model.schema.isBlock(object)) insertionSelection = model.createSelection(model.schema.findOptimalInsertionRange(originalSelection, options.findOptimalPosition));
	const firstSelectedBlock = first(originalSelection.getSelectedBlocks());
	const attributesToCopy = {};
	if (firstSelectedBlock) Object.assign(attributesToCopy, model.schema.getAttributesWithProperty(firstSelectedBlock, "copyOnReplace", true));
	return model.change((writer) => {
		if (!insertionSelection.isCollapsed) model.deleteContent(insertionSelection, { doNotAutoparagraph: true });
		let elementToInsert = object;
		const insertionPositionParent = insertionSelection.anchor.parent;
		const context = model.schema.createContext(insertionPositionParent);
		if (!model.schema.checkChild(context, object) && model.schema.checkChild(context, "paragraph") && model.schema.checkChild(context.push("paragraph"), object)) {
			elementToInsert = writer.createElement("paragraph");
			writer.insert(object, elementToInsert);
		}
		model.schema.setAllowedAttributes(elementToInsert, attributesToCopy, writer);
		const affectedRange = model.insertContent(elementToInsert, insertionSelection);
		if (affectedRange.isCollapsed) return affectedRange;
		if (options.setSelection) updateSelection(writer, object, options.setSelection, attributesToCopy);
		return affectedRange;
	});
}
/**
* Updates document selection based on given `place` parameter in relation to `contextElement` element.
*
* @param writer An instance of the model writer.
* @param contextElement An element to set the attributes on.
* @param place The place where selection should be set in relation to the `contextElement` element.
* Value `on` will set selection on the passed `contextElement`. Value `after` will set selection after `contextElement`.
* @param attributes Attributes keys and values to set on a paragraph that this function can create when
* `place` parameter is equal to `after` but there is no element with `$text` node to set selection in.
*/
function updateSelection(writer, contextElement, place, paragraphAttributes) {
	const model = writer.model;
	if (place == "on") {
		writer.setSelection(contextElement, "on");
		return;
	}
	if (place != "after")
 /**
	* The unsupported `options.setSelection` parameter was passed
	* to the {@link module:engine/model/utils/insertobject insertObject()} function.
	* Check the {@link module:engine/model/utils/insertobject insertObject()} API documentation for allowed
	* `options.setSelection` parameter values.
	*
	* @error insertobject-invalid-place-parameter-value
	*/
	throw new CKEditorError("insertobject-invalid-place-parameter-value", model);
	let nextElement = contextElement.nextSibling;
	if (model.schema.isInline(contextElement)) {
		writer.setSelection(contextElement, "after");
		return;
	}
	if (!(nextElement && model.schema.checkChild(nextElement, "$text")) && model.schema.checkChild(contextElement.parent, "paragraph")) {
		nextElement = writer.createElement("paragraph");
		model.schema.setAllowedAttributes(nextElement, paragraphAttributes, writer);
		model.insertContent(nextElement, writer.createPositionAfter(contextElement));
	}
	if (nextElement) writer.setSelection(nextElement, 0);
}

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* @module engine/model/utils/modifyselection
*/
const wordBoundaryCharacters = " ,.?!:;\"-()";
/**
* Modifies the selection. Currently, the supported modifications are:
*
* * Extending. The selection focus is moved in the specified `options.direction` with a step specified in `options.unit`.
* Possible values for `unit` are:
*  * `'character'` (default) - moves selection by one user-perceived character. In most cases this means moving by one
*  character in `String` sense. However, unicode also defines "combing marks". These are special symbols, that combines
*  with a symbol before it ("base character") to create one user-perceived character. For example, `q̣̇` is a normal
*  letter `q` with two "combining marks": upper dot (`Ux0307`) and lower dot (`Ux0323`). For most actions, i.e. extending
*  selection by one position, it is correct to include both "base character" and all of it's "combining marks". That is
*  why `'character'` value is most natural and common method of modifying selection.
*  * `'codePoint'` - moves selection by one unicode code point. In contrary to, `'character'` unit, this will insert
*  selection between "base character" and "combining mark", because "combining marks" have their own unicode code points.
*  However, for technical reasons, unicode code points with values above `UxFFFF` are represented in native `String` by
*  two characters, called "surrogate pairs". Halves of "surrogate pairs" have a meaning only when placed next to each other.
*  For example `𨭎` is represented in `String` by `\uD862\uDF4E`. Both `\uD862` and `\uDF4E` do not have any meaning
*  outside the pair (are rendered as ? when alone). Position between them would be incorrect. In this case, selection
*  extension will include whole "surrogate pair".
*  * `'word'` - moves selection by a whole word.
*
* **Note:** if you extend a forward selection in a backward direction you will in fact shrink it.
*
* **Note:** Use {@link module:engine/model/model~Model#modifySelection} instead of this function.
* This function is only exposed to be reusable in algorithms
* which change the {@link module:engine/model/model~Model#modifySelection}
* method's behavior.
*
* @param model The model in context of which the selection modification should be performed.
* @param selection The selection to modify.
* @param options.direction The direction in which the selection should be modified. Default 'forward'.
* @param options.unit The unit by which selection should be modified. Default 'character'.
* @param options.treatEmojiAsSingleUnit Whether multi-characer emoji sequences should be handled as single unit.
* @internal
*/
function modifySelection(model, selection, options = {}) {
	const schema = model.schema;
	const isForward = options.direction != "backward";
	const unit = options.unit ? options.unit : "character";
	const treatEmojiAsSingleUnit = !!options.treatEmojiAsSingleUnit;
	const focus = selection.focus;
	const walker = new ModelTreeWalker({
		boundaries: getSearchRange(focus, isForward),
		singleCharacters: true,
		direction: isForward ? "forward" : "backward"
	});
	const data = {
		walker,
		schema,
		isForward,
		unit,
		treatEmojiAsSingleUnit
	};
	let next;
	while (next = walker.next()) {
		if (next.done) return;
		const position = tryExtendingTo(data, next.value);
		if (position) {
			if (selection instanceof ModelDocumentSelection) model.change((writer) => {
				writer.setSelectionFocus(position);
			});
			else selection.setFocus(position);
			return;
		}
	}
}
/**
* Checks whether the selection can be extended to the the walker's next value (next position).
*/
function tryExtendingTo(data, value) {
	const { isForward, walker, unit, schema, treatEmojiAsSingleUnit } = data;
	const { type, item, nextPosition } = value;
	if (type == "text") {
		if (data.unit === "word") return getCorrectWordBreakPosition(walker, isForward);
		return getCorrectPosition(walker, unit, treatEmojiAsSingleUnit);
	}
	if (type == (isForward ? "elementStart" : "elementEnd")) {
		if (schema.isSelectable(item)) return ModelPosition._createAt(item, isForward ? "after" : "before");
		if (schema.checkChild(nextPosition, "$text")) return nextPosition;
	} else {
		if (schema.isLimit(item)) {
			walker.skip(() => true);
			return;
		}
		if (schema.checkChild(nextPosition, "$text")) return nextPosition;
	}
}
/**
* Finds a correct position by walking in a text node and checking whether selection can be extended to given position
* or should be extended further.
*/
function getCorrectPosition(walker, unit, treatEmojiAsSingleUnit) {
	const textNode = walker.position.textNode;
	if (textNode) {
		const data = textNode.data;
		let offset = walker.position.offset - textNode.startOffset;
		while (isInsideSurrogatePair(data, offset) || unit == "character" && isInsideCombinedSymbol(data, offset) || treatEmojiAsSingleUnit && isInsideEmojiSequence(data, offset)) {
			walker.next();
			offset = walker.position.offset - textNode.startOffset;
		}
	}
	return walker.position;
}
/**
* Finds a correct position of a word break by walking in a text node and checking whether selection can be extended to given position
* or should be extended further.
*/
function getCorrectWordBreakPosition(walker, isForward) {
	let textNode = walker.position.textNode;
	if (!textNode) textNode = isForward ? walker.position.nodeAfter : walker.position.nodeBefore;
	while (textNode && textNode.is("$text")) {
		const offset = walker.position.offset - textNode.startOffset;
		if (isAtNodeBoundary(textNode, offset, isForward)) textNode = isForward ? walker.position.nodeAfter : walker.position.nodeBefore;
		else if (isAtWordBoundary(textNode.data, offset, isForward)) break;
		else walker.next();
	}
	return walker.position;
}
function getSearchRange(start, isForward) {
	const root = start.root;
	const searchEnd = ModelPosition._createAt(root, isForward ? "end" : 0);
	if (isForward) return new ModelRange(start, searchEnd);
	else return new ModelRange(searchEnd, start);
}
/**
* Checks if selection is on word boundary.
*/
function isAtWordBoundary(data, offset, isForward) {
	const offsetToCheck = offset + (isForward ? 0 : -1);
	return wordBoundaryCharacters.includes(data.charAt(offsetToCheck));
}
/**
* Checks if selection is on node boundary.
*/
function isAtNodeBoundary(textNode, offset, isForward) {
	return offset === (isForward ? textNode.offsetSize : 0);
}

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* @module engine/model/model
*/
const ModelBase = /* #__PURE__ */ ObservableMixin();
/**
* Editor's data model. Read about the model in the
* {@glink framework/architecture/editing-engine engine architecture} guide.
*/
var Model = class extends ModelBase {
	/**
	* Model's marker collection.
	*/
	markers;
	/**
	* Model's document.
	*/
	document;
	/**
	* Model's schema.
	*/
	schema;
	/**
	* Stores all configurations specific to editor instance.
	*
	* @internal
	*/
	_config;
	/**
	* All callbacks added by {@link module:engine/model/model~Model#change} or
	* {@link module:engine/model/model~Model#enqueueChange} methods waiting to be executed.
	*/
	_pendingChanges;
	/**
	* The last created and currently used writer instance.
	*/
	_currentWriter;
	constructor(config) {
		super();
		this.markers = new MarkerCollection();
		this.document = new ModelDocument(this);
		this.schema = new ModelSchema();
		this._config = config;
		this._pendingChanges = [];
		this._currentWriter = null;
		[
			"deleteContent",
			"modifySelection",
			"getSelectedContent",
			"applyOperation"
		].forEach((methodName) => this.decorate(methodName));
		this.on("applyOperation", (evt, args) => {
			args[0]._validate();
		}, { priority: "highest" });
		this.schema.register("$root", { isLimit: true });
		this.schema.register("$inlineRoot", {
			allowContentOf: "$block",
			allowAttributesOf: "$root",
			isLimit: true
		});
		this.schema.register("$container", { allowIn: ["$root", "$container"] });
		this.schema.register("$block", {
			allowIn: ["$root", "$container"],
			isBlock: true
		});
		this.schema.register("$blockObject", {
			allowWhere: "$block",
			isBlock: true,
			isObject: true
		});
		this.schema.register("$inlineObject", {
			allowWhere: "$text",
			allowAttributesOf: "$text",
			isInline: true,
			isObject: true
		});
		this.schema.register("$text", {
			allowIn: "$block",
			isInline: true,
			isContent: true
		});
		this.schema.register("$clipboardHolder", {
			allowContentOf: ["$root", "$inlineRoot"],
			allowChildren: "$text",
			isLimit: true
		});
		this.schema.register("$documentFragment", {
			allowContentOf: ["$root", "$inlineRoot"],
			allowChildren: "$text",
			isLimit: true
		});
		this.schema.register("$marker");
		this.schema.addChildCheck(() => true, "$marker");
		injectSelectionPostFixer(this);
		this.document.registerPostFixer(autoParagraphEmptyRoots);
		this.on("insertContent", (evt, [content, selectable]) => {
			evt.return = insertContent(this, content, selectable);
		});
		this.on("insertObject", (evt, [element, selection, options]) => {
			evt.return = insertObject(this, element, selection, options);
		});
		this.on("canEditAt", (evt) => {
			const canEditAt = !this.document.isReadOnly;
			evt.return = canEditAt;
			if (!canEditAt) evt.stop();
		});
	}
	/**
	* The `change()` method is the primary way of changing the model. You should use it to modify all document nodes
	* (including detached nodes – i.e. nodes not added to the {@link module:engine/model/model~Model#document model document}),
	* the {@link module:engine/model/document~ModelDocument#selection document's selection}, and
	* {@link module:engine/model/model~Model#markers model markers}.
	*
	* ```ts
	* model.change( writer => {
	* 	writer.insertText( 'foo', paragraph, 'end' );
	* } );
	* ```
	*
	* All changes inside the change block use the same {@link module:engine/model/batch~Batch} so they are combined
	* into a single undo step.
	*
	* ```ts
	* model.change( writer => {
	* 	writer.insertText( 'foo', paragraph, 'end' ); // foo.
	*
	* 	model.change( writer => {
	* 		writer.insertText( 'bar', paragraph, 'end' ); // foobar.
	* 	} );
	*
	* 	writer.insertText( 'bom', paragraph, 'end' ); // foobarbom.
	* } );
	* ```
	*
	* The callback of the `change()` block is executed synchronously.
	*
	* You can also return a value from the change block.
	*
	* ```ts
	* const img = model.change( writer => {
	* 	return writer.createElement( 'img' );
	* } );
	* ```
	*
	* @see #enqueueChange
	* @typeParam TReturn The return type of the provided callback.
	* @param callback Callback function which may modify the model.
	*/
	change(callback) {
		try {
			if (this._pendingChanges.length === 0) {
				this._pendingChanges.push({
					batch: new Batch(),
					callback
				});
				return this._runPendingChanges()[0];
			} else return callback(this._currentWriter);
		} catch (err) {
			CKEditorError.rethrowUnexpectedError(err, this);
		}
	}
	enqueueChange(batchOrType, callback) {
		try {
			if (!batchOrType) batchOrType = new Batch();
			else if (typeof batchOrType === "function") {
				callback = batchOrType;
				batchOrType = new Batch();
			} else if (!(batchOrType instanceof Batch)) batchOrType = new Batch(batchOrType);
			this._pendingChanges.push({
				batch: batchOrType,
				callback
			});
			if (this._pendingChanges.length == 1) this._runPendingChanges();
		} catch (err) {
			CKEditorError.rethrowUnexpectedError(err, this);
		}
	}
	/**
	* {@link module:utils/observablemixin~Observable#decorate Decorated} function for applying
	* {@link module:engine/model/operation/operation~Operation operations} to the model.
	*
	* This is a low-level way of changing the model. It is exposed for very specific use cases (like the undo feature).
	* Normally, to modify the model, you will want to use {@link module:engine/model/writer~ModelWriter `Writer`}.
	* See also {@glink framework/architecture/editing-engine#changing-the-model Changing the model} section
	* of the {@glink framework/architecture/editing-engine Editing architecture} guide.
	*
	* @param operation The operation to apply.
	*/
	applyOperation(operation) {
		operation._execute();
	}
	/**
	* Inserts content at the position in the editor specified by the selection, as one would expect the paste
	* functionality to work.
	*
	* **Note**: If you want to insert an {@glink framework/deep-dive/schema#object-elements object element}
	* (e.g. a {@link module:widget/utils~toWidget widget}), see {@link #insertObject} instead.
	*
	* This is a high-level method. It takes the {@link #schema schema} into consideration when inserting
	* the content, clears the given selection's content before inserting nodes and moves the selection
	* to its target position at the end of the process.
	* It can split elements, merge them, wrap bare text nodes with paragraphs, etc. &ndash; just like the
	* pasting feature should do.
	*
	* For lower-level methods see {@link module:engine/model/writer~ModelWriter `Writer`}.
	*
	* This method, unlike {@link module:engine/model/writer~ModelWriter `Writer`}'s methods, does not have to be used
	* inside a {@link #change `change()` block}.
	*
	* # Conversion and schema
	*
	* Inserting elements and text nodes into the model is not enough to make CKEditor 5 render that content
	* to the user. CKEditor 5 implements a model-view-controller architecture and what `model.insertContent()` does
	* is only adding nodes to the model. Additionally, you need to define
	* {@glink framework/architecture/editing-engine#conversion converters} between the model and view
	* and define those nodes in the {@glink framework/architecture/editing-engine#schema schema}.
	*
	* So, while this method may seem similar to CKEditor 4 `editor.insertHtml()` (in fact, both methods
	* are used for paste-like content insertion), the CKEditor 5 method cannot be use to insert arbitrary HTML
	* unless converters are defined for all elements and attributes in that HTML.
	*
	* # Examples
	*
	* Using `insertContent()` with a manually created model structure:
	*
	* ```ts
	* // Let's create a document fragment containing such content as:
	* //
	* // <paragraph>foo</paragraph>
	* // <blockQuote>
	* //    <paragraph>bar</paragraph>
	* // </blockQuote>
	* const docFrag = editor.model.change( writer => {
	* 	const p1 = writer.createElement( 'paragraph' );
	* 	const p2 = writer.createElement( 'paragraph' );
	* 	const blockQuote = writer.createElement( 'blockQuote' );
	* 	const docFrag = writer.createDocumentFragment();
	*
	* 	writer.append( p1, docFrag );
	* 	writer.append( blockQuote, docFrag );
	* 	writer.append( p2, blockQuote );
	* 	writer.insertText( 'foo', p1 );
	* 	writer.insertText( 'bar', p2 );
	*
	* 	return docFrag;
	* } );
	*
	* // insertContent() does not have to be used in a change() block. It can, though,
	* // so this code could be moved to the callback defined above.
	* editor.model.insertContent( docFrag );
	* ```
	*
	* Using `insertContent()` with an HTML string converted to a model document fragment (similar to the pasting mechanism):
	*
	* ```ts
	* // You can create your own HtmlDataProcessor instance or use editor.data.processor
	* // if you have not overridden the default one (which is the HtmlDataProcessor instance).
	* const htmlDP = new HtmlDataProcessor( viewDocument );
	*
	* // Convert an HTML string to a view document fragment:
	* const viewFragment = htmlDP.toView( htmlString );
	*
	* // Convert the view document fragment to a model document fragment
	* // in the context of $root. This conversion takes the schema into
	* // account so if, for example, the view document fragment contained a bare text node,
	* // this text node cannot be a child of $root, so it will be automatically
	* // wrapped with a <paragraph>. You can define the context yourself (in the second parameter),
	* // and e.g. convert the content like it would happen in a <paragraph>.
	* // Note: The clipboard feature uses a custom context called $clipboardHolder
	* // which has a loosened schema.
	* const modelFragment = editor.data.toModel( viewFragment );
	*
	* editor.model.insertContent( modelFragment );
	* ```
	*
	* By default this method will use the document selection but it can also be used with a position, range or selection instance.
	*
	* ```ts
	* // Insert text at the current document selection position.
	* editor.model.change( writer => {
	* 	editor.model.insertContent( writer.createText( 'x' ) );
	* } );
	*
	* // Insert text at a given position - the document selection will not be modified.
	* editor.model.change( writer => {
	* 	editor.model.insertContent( writer.createText( 'x' ), doc.getRoot(), 2 );
	*
	* 	// Which is a shorthand for:
	* 	editor.model.insertContent( writer.createText( 'x' ), writer.createPositionAt( doc.getRoot(), 2 ) );
	* } );
	* ```
	*
	* If you want the document selection to be moved to the inserted content, use the
	* {@link module:engine/model/writer~ModelWriter#setSelection `setSelection()`} method of the writer after inserting
	* the content:
	*
	* ```ts
	* editor.model.change( writer => {
	* 	const paragraph = writer.createElement( 'paragraph' );
	*
	* 	// Insert an empty paragraph at the beginning of the root.
	* 	editor.model.insertContent( paragraph, writer.createPositionAt( editor.model.document.getRoot(), 0 ) );
	*
	* 	// Move the document selection to the inserted paragraph.
	* 	writer.setSelection( paragraph, 'in' );
	* } );
	* ```
	*
	* If an instance of the {@link module:engine/model/selection~ModelSelection model selection} is passed as `selectable`,
	* the new content will be inserted at the passed selection (instead of document selection):
	*
	* ```ts
	* editor.model.change( writer => {
	* 	// Create a selection in a paragraph that will be used as a place of insertion.
	* 	const selection = writer.createSelection( paragraph, 'in' );
	*
	* 	// Insert the new text at the created selection.
	* 	editor.model.insertContent( writer.createText( 'x' ), selection );
	*
	* 	// insertContent() modifies the passed selection instance so it can be used to set the document selection.
	* 	// Note: This is not necessary when you passed the document selection to insertContent().
	* 	writer.setSelection( selection );
	* } );
	* ```
	*
	* @fires insertContent
	* @param content The content to insert.
	* @param selectable The selection into which the content should be inserted.
	* If not provided the current model document selection will be used.
	* @param placeOrOffset To be used when a model item was passed as `selectable`.
	* This param defines a position in relation to that item.
	* at the insertion position.
	*/
	insertContent(content, selectable, placeOrOffset, ...rest) {
		const selection = normalizeSelectable(selectable, placeOrOffset);
		return this.fire("insertContent", [
			content,
			selection,
			placeOrOffset,
			...rest
		]);
	}
	/**
	* Inserts an {@glink framework/deep-dive/schema#object-elements object element} at a specific position in the editor content.
	*
	* This is a high-level API:
	* * It takes the {@link #schema schema} into consideration,
	* * It clears the content of passed `selectable` before inserting,
	* * It can move the selection at the end of the process,
	* * It will copy the selected block's attributes to preserve them upon insertion,
	* * It can split elements or wrap inline objects with paragraphs if they are not allowed in target position,
	* * etc.
	*
	* # Notes
	*
	* * If you want to insert a non-object content, see {@link #insertContent} instead.
	* * For lower-level API, see {@link module:engine/model/writer~ModelWriter `Writer`}.
	* * Unlike {@link module:engine/model/writer~ModelWriter `Writer`}, this method does not have to be used inside
	* a {@link #change `change()` block}.
	* * Inserting object into the model is not enough to make CKEditor 5 render that content to the user.
	* CKEditor 5 implements a model-view-controller architecture and what `model.insertObject()` does
	* is only adding nodes to the model. Additionally, you need to define
	* {@glink framework/architecture/editing-engine#conversion converters} between the model and view
	* and define those nodes in the {@glink framework/architecture/editing-engine#schema schema}.
	*
	* # Examples
	*
	* Use the following code to insert an object at the current selection and keep the selection on the inserted element:
	*
	* ```ts
	* const rawHtmlEmbedElement = writer.createElement( 'rawHtml' );
	*
	* model.insertObject( rawHtmlEmbedElement, null, null, {
	* 	setSelection: 'on'
	* } );
	* ```
	*
	* Use the following code to insert an object at the current selection and nudge the selection after the inserted object:
	*
	* ```ts
	* const pageBreakElement = writer.createElement( 'pageBreak' );
	*
	* model.insertObject( pageBreakElement, null, null, {
	* 	setSelection: 'after'
	* } );
	* ```
	*
	* Use the following code to insert an object at the current selection and avoid splitting the content (non-destructive insertion):
	*
	* ```ts
	* const tableElement = writer.createElement( 'table' );
	*
	* model.insertObject( tableElement, null, null, {
	* 	findOptimalPosition: 'auto'
	* } );
	* ```
	*
	* Use the following code to insert an object at the specific range (also: replace the content of the range):
	*
	* ```ts
	* const tableElement = writer.createElement( 'table' );
	* const range = model.createRangeOn( model.document.getRoot().getChild( 1 ) );
	*
	* model.insertObject( tableElement, range );
	* ```
	*
	* @param element An object to be inserted into the model document.
	* @param selectable A selectable where the content should be inserted. If not specified, the current
	* {@link module:engine/model/document~ModelDocument#selection document selection} will be used instead.
	* @param placeOrOffset Specifies the exact place or offset for the insertion to take place, relative to `selectable`.
	* @param options Additional options.
	* @param options.findOptimalPosition An option that, when set, adjusts the insertion position (relative to
	* `selectable` and `placeOrOffset`) so that the content of `selectable` is not split upon insertion (a.k.a. non-destructive insertion).
	* * When `'auto'`, the algorithm will decide whether to insert the object before or after `selectable` to avoid content splitting.
	* * When `'before'`, the closest position before `selectable` will be used that will not result in content splitting.
	* * When `'after'`, the closest position after `selectable` will be used that will not result in content splitting.
	*
	* Note that this option only works for block objects. Inline objects are inserted into text and do not split blocks.
	* @param options.setSelection An option that, when set, moves the
	* {@link module:engine/model/document~ModelDocument#selection document selection} after inserting the object.
	* * When `'on'`, the document selection will be set on the inserted object.
	* * When `'after'`, the document selection will move to the closest text node after the inserted object. If there is no
	* such text node, a paragraph will be created and the document selection will be moved inside it.
	* at the insertion position.
	*/
	insertObject(element, selectable, placeOrOffset, options, ...rest) {
		const selection = normalizeSelectable(selectable, placeOrOffset);
		return this.fire("insertObject", [
			element,
			selection,
			options,
			options,
			...rest
		]);
	}
	/**
	* Deletes content of the selection and merge siblings. The resulting selection is always collapsed.
	*
	* **Note:** For the sake of predictability, the resulting selection should always be collapsed.
	* In cases where a feature wants to modify deleting behavior so selection isn't collapsed
	* (e.g. a table feature may want to keep row selection after pressing <kbd>Backspace</kbd>),
	* then that behavior should be implemented in the view's listener. At the same time, the table feature
	* will need to modify this method's behavior too, e.g. to "delete contents and then collapse
	* the selection inside the last selected cell" or "delete the row and collapse selection somewhere near".
	* That needs to be done in order to ensure that other features which use `deleteContent()` will work well with tables.
	*
	* @fires deleteContent
	* @param selection Selection of which the content should be deleted.
	* @param options.leaveUnmerged Whether to merge elements after removing the content of the selection.
	*
	* For example `<heading1>x[x</heading1><paragraph>y]y</paragraph>` will become:
	*
	* * `<heading1>x^y</heading1>` with the option disabled (`leaveUnmerged == false`)
	* * `<heading1>x^</heading1><paragraph>y</paragraph>` with enabled (`leaveUnmerged == true`).
	*
	* Note: {@link module:engine/model/schema~ModelSchema#isObject object} and {@link module:engine/model/schema~ModelSchema#isLimit limit}
	* elements will not be merged.
	*
	* @param options.doNotResetEntireContent Whether to skip replacing the entire content with a
	* paragraph when the entire content was selected.
	*
	* For example `<heading1>[x</heading1><paragraph>y]</paragraph>` will become:
	*
	* * `<paragraph>^</paragraph>` with the option disabled (`doNotResetEntireContent == false`)
	* * `<heading1>^</heading1>` with enabled (`doNotResetEntireContent == true`)
	*
	* @param options.doNotAutoparagraph Whether to create a paragraph if after content deletion selection is moved
	* to a place where text cannot be inserted.
	*
	* For example `<paragraph>x</paragraph>[<imageBlock src="foo.jpg"></imageBlock>]` will become:
	*
	* * `<paragraph>x</paragraph><paragraph>[]</paragraph>` with the option disabled (`doNotAutoparagraph == false`)
	* * `<paragraph>x[]</paragraph>` with the option enabled (`doNotAutoparagraph == true`).
	*
	* **Note:** if there is no valid position for the selection, the paragraph will always be created:
	*
	* `[<imageBlock src="foo.jpg"></imageBlock>]` -> `<paragraph>[]</paragraph>`.
	*
	* @param options.direction The direction in which the content is being consumed.
	* Deleting backward corresponds to using the <kbd>Backspace</kbd> key, while deleting content forward corresponds to
	* the <kbd>Shift</kbd>+<kbd>Backspace</kbd> keystroke.
	*/
	deleteContent(selection, options) {
		deleteContent(this, selection, options);
	}
	/**
	* Modifies the selection. Currently, the supported modifications are:
	*
	* * Extending. The selection focus is moved in the specified `options.direction` with a step specified in `options.unit`.
	* Possible values for `unit` are:
	*  * `'character'` (default) - moves selection by one user-perceived character. In most cases this means moving by one
	*  character in `String` sense. However, unicode also defines "combing marks". These are special symbols, that combines
	*  with a symbol before it ("base character") to create one user-perceived character. For example, `q̣̇` is a normal
	*  letter `q` with two "combining marks": upper dot (`Ux0307`) and lower dot (`Ux0323`). For most actions, i.e. extending
	*  selection by one position, it is correct to include both "base character" and all of it's "combining marks". That is
	*  why `'character'` value is most natural and common method of modifying selection.
	*  * `'codePoint'` - moves selection by one unicode code point. In contrary to, `'character'` unit, this will insert
	*  selection between "base character" and "combining mark", because "combining marks" have their own unicode code points.
	*  However, for technical reasons, unicode code points with values above `UxFFFF` are represented in native `String` by
	*  two characters, called "surrogate pairs". Halves of "surrogate pairs" have a meaning only when placed next to each other.
	*  For example `𨭎` is represented in `String` by `\uD862\uDF4E`. Both `\uD862` and `\uDF4E` do not have any meaning
	*  outside the pair (are rendered as ? when alone). Position between them would be incorrect. In this case, selection
	*  extension will include whole "surrogate pair".
	*  * `'word'` - moves selection by a whole word.
	*
	* **Note:** if you extend a forward selection in a backward direction you will in fact shrink it.
	*
	* @fires modifySelection
	* @param selection The selection to modify.
	* @param options.direction The direction in which the selection should be modified.
	* @param options.unit The unit by which selection should be modified.
	* @param options.treatEmojiAsSingleUnit Whether multi-characer emoji sequences should be handled as single unit.
	*/
	modifySelection(selection, options) {
		modifySelection(this, selection, options);
	}
	/**
	* Gets a clone of the selected content.
	*
	* For example, for the following selection:
	*
	* ```html
	* <paragraph>x</paragraph>
	* <blockQuote>
	* 	<paragraph>y</paragraph>
	* 	<heading1>fir[st</heading1>
	* </blockQuote>
	* <paragraph>se]cond</paragraph>
	* <paragraph>z</paragraph>
	* ```
	*
	* It will return a document fragment with such a content:
	*
	* ```html
	* <blockQuote>
	* 	<heading1>st</heading1>
	* </blockQuote>
	* <paragraph>se</paragraph>
	* ```
	*
	* @fires getSelectedContent
	* @param selection The selection of which content will be returned.
	*/
	getSelectedContent(selection) {
		return getSelectedContent(this, selection);
	}
	/**
	* Checks whether given `subject` has any meaningful content.
	*
	* Meaningful content is:
	*
	* * any text node (`options.ignoreWhitespaces` allows controlling whether this text node must also contain
	* any non-whitespace characters),
	* * or any {@link module:engine/model/schema~ModelSchema#isContent content element},
	* * or any {@link module:engine/model/markercollection~Marker marker} which
	* {@link module:engine/model/markercollection~Marker#_affectsData affects data}.
	*
	* This means that a range containing an empty `<paragraph></paragraph>` is not considered to have a meaningful content.
	* However, a range containing an `<imageBlock></imageBlock>` (which would normally be marked in the schema as an object element)
	* is considered non-empty.
	*
	* @param subject Subject to check if includes meaningful content. Could be a model range, an element, a document fragment, or a
	* selection.
	* @param options.ignoreWhitespaces Whether text node with whitespaces only should be considered empty.
	* @param options.ignoreMarkers Whether markers should be ignored.
	*/
	hasContent(subject, options = {}) {
		let ranges;
		if (subject.is("selection")) ranges = Array.from(subject.getRanges());
		else if (subject.is("range")) ranges = [subject];
		else ranges = [ModelRange._createIn(subject)];
		for (const range of ranges) if (this._rangeHasContent(range, options)) return true;
		return false;
	}
	/**
	* Checks whether given range has any meaningful content.
	*
	* Helper method for {@link #hasContent}.
	*/
	_rangeHasContent(range, options) {
		if (range.isCollapsed) return false;
		const { ignoreWhitespaces = false, ignoreMarkers = false } = options;
		if (!ignoreMarkers) {
			for (const intersectingMarker of this.markers.getMarkersIntersectingRange(range)) if (intersectingMarker.affectsData) return true;
		}
		for (const item of range.getItems()) if (this.schema.isContent(item)) if (item.is("$textProxy")) {
			if (!ignoreWhitespaces) return true;
			else if (item.data.search(/\S/) !== -1) return true;
		} else return true;
		return false;
	}
	/**
	* Check whether given selectable is at a place in the model where it can be edited (returns `true`) or not (returns `false`).
	*
	* Should be used instead of {@link module:core/editor/editor~Editor#isReadOnly} to check whether a user action can happen at
	* given selectable. It may be decorated and used differently in different environment (e.g. multi-root editor can disable
	* a particular root).
	*
	* This method is decorated. Although this method accepts any parameter of `Selectable` type, the
	* {@link ~Model#event:canEditAt `canEditAt` event} is fired with `selectable` normalized to an instance of
	* {@link module:engine/model/selection~ModelSelection} or {@link module:engine/model/documentselection~ModelDocumentSelection}
	*
	* @fires canEditAt
	*/
	canEditAt(selectable) {
		const selection = normalizeSelectable(selectable);
		return this.fire("canEditAt", [selection]);
	}
	/**
	* Creates a position from the given root and path in that root.
	*
	* Note: This method is also available as
	* {@link module:engine/model/writer~ModelWriter#createPositionFromPath `Writer#createPositionFromPath()`}.
	*
	* @param root Root of the position.
	* @param path Position path. See {@link module:engine/model/position~ModelPosition#path}.
	* @param stickiness Position stickiness. See {@link module:engine/model/position~ModelPositionStickiness}.
	*/
	createPositionFromPath(root, path, stickiness) {
		return new ModelPosition(root, path, stickiness);
	}
	/**
	* Creates position at the given location. The location can be specified as:
	*
	* * a {@link module:engine/model/position~ModelPosition position},
	* * a parent element and offset in that element,
	* * a parent element and `'end'` (the position will be set at the end of that element),
	* * a {@link module:engine/model/item~ModelItem model item} and `'before'` or `'after'`
	* (the position will be set before or after the given model item).
	*
	* This method is a shortcut to other factory methods such as:
	*
	* * {@link module:engine/model/model~Model#createPositionBefore `createPositionBefore()`},
	* * {@link module:engine/model/model~Model#createPositionAfter `createPositionAfter()`}.
	*
	* Note: This method is also available as
	* {@link module:engine/model/writer~ModelWriter#createPositionAt `Writer#createPositionAt()`},
	*
	* @param itemOrPosition
	* @param offset Offset or one of the flags. Used only when first parameter is a {@link module:engine/model/item~ModelItem model item}.
	*/
	createPositionAt(itemOrPosition, offset) {
		return ModelPosition._createAt(itemOrPosition, offset);
	}
	/**
	* Creates a new position after the given {@link module:engine/model/item~ModelItem model item}.
	*
	* Note: This method is also available as
	* {@link module:engine/model/writer~ModelWriter#createPositionAfter `Writer#createPositionAfter()`}.
	*
	* @param item Item after which the position should be placed.
	*/
	createPositionAfter(item) {
		return ModelPosition._createAfter(item);
	}
	/**
	* Creates a new position before the given {@link module:engine/model/item~ModelItem model item}.
	*
	* Note: This method is also available as
	* {@link module:engine/model/writer~ModelWriter#createPositionBefore `Writer#createPositionBefore()`}.
	*
	* @param item Item before which the position should be placed.
	*/
	createPositionBefore(item) {
		return ModelPosition._createBefore(item);
	}
	/**
	* Creates a range spanning from the `start` position to the `end` position.
	*
	* Note: This method is also available as
	* {@link module:engine/model/writer~ModelWriter#createRange `Writer#createRange()`}:
	*
	* ```ts
	* model.change( writer => {
	* 	const range = writer.createRange( start, end );
	* } );
	* ```
	*
	* @param start Start position.
	* @param end End position. If not set, the range will be collapsed to the `start` position.
	*/
	createRange(start, end) {
		return new ModelRange(start, end);
	}
	/**
	* Creates a range inside the given element which starts before the first child of
	* that element and ends after the last child of that element.
	*
	* Note: This method is also available as
	* {@link module:engine/model/writer~ModelWriter#createRangeIn `Writer#createRangeIn()`}:
	*
	* ```ts
	* model.change( writer => {
	* 	const range = writer.createRangeIn( paragraph );
	* } );
	* ```
	*
	* @param element Element which is a parent for the range.
	*/
	createRangeIn(element) {
		return ModelRange._createIn(element);
	}
	/**
	* Creates a range that starts before the given {@link module:engine/model/item~ModelItem model item} and ends after it.
	*
	* Note: This method is also available on `writer` instance as
	* {@link module:engine/model/writer~ModelWriter#createRangeOn `Writer.createRangeOn()`}:
	*
	* ```ts
	* model.change( writer => {
	* 	const range = writer.createRangeOn( paragraph );
	* } );
	* ```
	*
	* @param item
	*/
	createRangeOn(item) {
		return ModelRange._createOn(item);
	}
	createSelection(...args) {
		return new ModelSelection(...args);
	}
	/**
	* Creates a {@link module:engine/model/batch~Batch} instance.
	*
	* **Note:** In most cases creating a batch instance is not necessary as they are created when using:
	*
	* * {@link #change `change()`},
	* * {@link #enqueueChange `enqueueChange()`}.
	*
	* @param type {@link module:engine/model/batch~Batch#constructor The type} of the batch.
	*/
	createBatch(type) {
		return new Batch(type);
	}
	/**
	* Creates an operation instance from a JSON object (parsed JSON string).
	*
	* This is an alias for {@link module:engine/model/operation/operationfactory~OperationFactory.fromJSON `OperationFactory.fromJSON()`}.
	*
	* @param json Deserialized JSON object.
	*/
	createOperationFromJSON(json) {
		return OperationFactory.fromJSON(json, this.document);
	}
	/**
	* Removes all events listeners set by model instance and destroys {@link module:engine/model/document~ModelDocument}.
	*/
	destroy() {
		this.document.destroy();
		this.stopListening();
	}
	/**
	* Common part of {@link module:engine/model/model~Model#change} and {@link module:engine/model/model~Model#enqueueChange}
	* which calls callbacks and returns array of values returned by these callbacks.
	*/
	_runPendingChanges() {
		const ret = [];
		this.fire("_beforeChanges");
		try {
			while (this._pendingChanges.length) {
				const currentBatch = this._pendingChanges[0].batch;
				this._currentWriter = new ModelWriter(this, currentBatch);
				const callbackReturnValue = this._pendingChanges[0].callback(this._currentWriter);
				ret.push(callbackReturnValue);
				this.document._handleChangeBlock(this._currentWriter);
				this._pendingChanges.shift();
				this._currentWriter = null;
			}
		} finally {
			this._pendingChanges.length = 0;
			this._currentWriter = null;
			this.fire("_afterChanges");
		}
		return ret;
	}
};
/**
* Normalizes a selectable to a Selection or ModelDocumentSelection.
*/
function normalizeSelectable(selectable, placeOrOffset) {
	if (!selectable) return;
	if (selectable instanceof ModelSelection || selectable instanceof ModelDocumentSelection) return selectable;
	if (selectable instanceof ModelNode) if (placeOrOffset || placeOrOffset === 0) return new ModelSelection(selectable, placeOrOffset);
	else if (selectable.is("rootElement")) return new ModelSelection(selectable, "in");
	else return new ModelSelection(selectable, "on");
	return new ModelSelection(selectable);
}

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* @module engine/view/observer/clickobserver
*/
/**
* {@link module:engine/view/document~ViewDocument#event:click Click} event observer.
*
* Note that this observer is not available by default. To make it available it needs to be added to
* {@link module:engine/view/view~EditingView view controller} by a {@link module:engine/view/view~EditingView#addObserver} method.
*/
var ClickObserver = class extends DomEventObserver {
	/**
	* @inheritDoc
	*/
	domEventType = "click";
	/**
	* @inheritDoc
	*/
	onDomEvent(domEvent) {
		this.fire(domEvent.type, domEvent);
	}
};

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* @module engine/view/observer/mouseobserver
*/
/**
* Mouse events observer.
*
* Note that this observer is not available by default. To make it available it needs to be added to
* {@link module:engine/view/view~EditingView} by {@link module:engine/view/view~EditingView#addObserver} method.
*/
var MouseObserver = class extends DomEventObserver {
	/**
	* @inheritDoc
	*/
	domEventType = [
		"mousedown",
		"mouseup",
		"mouseover",
		"mouseout"
	];
	/**
	* @inheritDoc
	*/
	onDomEvent(domEvent) {
		this.fire(domEvent.type, domEvent);
	}
};

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* @module engine/view/observer/touchobserver
*/
/**
* Touch events observer.
*
* Note that this observer is not available by default. To make it available it needs to be added to
* {@link module:engine/view/view~EditingView} by {@link module:engine/view/view~EditingView#addObserver} method.
*/
var TouchObserver = class extends DomEventObserver {
	/**
	* @inheritDoc
	*/
	domEventType = [
		"touchstart",
		"touchend",
		"touchmove"
	];
	/**
	* @inheritDoc
	*/
	onDomEvent(domEvent) {
		this.fire(domEvent.type, domEvent);
	}
};

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* @module engine/view/observer/pointerobserver
*/
/**
* Pointer events observer.
*
* Note that this observer is not available by default. To make it available it needs to be added to
* {@link module:engine/view/view~EditingView} by {@link module:engine/view/view~EditingView#addObserver} method.
*/
var PointerObserver = class extends DomEventObserver {
	/**
	* @inheritDoc
	*/
	domEventType = [
		"pointerdown",
		"pointerup",
		"pointermove"
	];
	/**
	* @inheritDoc
	*/
	onDomEvent(domEvent) {
		this.fire(domEvent.type, domEvent);
	}
};

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* @module engine/view/upcastwriter
*/
/**
* View upcast writer. It provides a set of methods used to manipulate non-semantic view trees.
*
* It should be used only while working on a non-semantic view
* (e.g. a view created from HTML string on paste).
* To manipulate a view which was or is being downcasted from the the model use the
* {@link module:engine/view/downcastwriter~ViewDowncastWriter downcast writer}.
*
* Read more about changing the view in the {@glink framework/architecture/editing-engine#changing-the-view Changing the view}
* section of the {@glink framework/architecture/editing-engine Editing engine architecture} guide.
*
* Unlike `ViewDowncastWriter`, which is available in the {@link module:engine/view/view~EditingView#change `View#change()`} block,
* `ViewUpcastWriter` can be created wherever you need it:
*
* ```ts
* const writer = new ViewUpcastWriter( viewDocument );
* const text = writer.createText( 'foo!' );
*
* writer.appendChild( text, someViewElement );
* ```
*/
var ViewUpcastWriter = class {
	/**
	* The view document instance in which this upcast writer operates.
	*/
	document;
	/**
	* @param document The view document instance in which this upcast writer operates.
	*/
	constructor(document) {
		this.document = document;
	}
	/**
	* Creates a new {@link module:engine/view/documentfragment~ViewDocumentFragment} instance.
	*
	* @param children A list of nodes to be inserted into the created document fragment.
	* @returns The created document fragment.
	*/
	createDocumentFragment(children) {
		return new ViewDocumentFragment(this.document, children);
	}
	/**
	* Creates a new {@link module:engine/view/element~ViewElement} instance.
	*
	* Attributes can be passed in various formats:
	*
	* ```ts
	* upcastWriter.createElement( 'div', { class: 'editor', contentEditable: 'true' } ); // object
	* upcastWriter.createElement( 'div', [ [ 'class', 'editor' ], [ 'contentEditable', 'true' ] ] ); // map-like iterator
	* upcastWriter.createElement( 'div', mapOfAttributes ); // map
	* ```
	*
	* @param name Node name.
	* @param attrs Collection of attributes.
	* @param children A list of nodes to be inserted into created element.
	* @returns Created element.
	*/
	createElement(name, attrs, children) {
		return new ViewElement(this.document, name, attrs, children);
	}
	/**
	* Creates a new {@link module:engine/view/text~ViewText} instance.
	*
	* @param data The text's data.
	* @returns The created text node.
	*/
	createText(data) {
		return new ViewText(this.document, data);
	}
	/**
	* Clones the provided element.
	*
	* @see module:engine/view/element~ViewElement#_clone
	* @param element Element to be cloned.
	* @param deep If set to `true` clones element and all its children recursively. When set to `false`,
	* element will be cloned without any children.
	* @returns Clone of this element.
	*/
	clone(element, deep = false) {
		return element._clone(deep);
	}
	/**
	* Appends a child node or a list of child nodes at the end of this node
	* and sets the parent of these nodes to this element.
	*
	* @see module:engine/view/element~ViewElement#_appendChild
	* @param items Items to be inserted.
	* @param element Element to which items will be appended.
	* @returns Number of appended nodes.
	*/
	appendChild(items, element) {
		return element._appendChild(items);
	}
	/**
	* Inserts a child node or a list of child nodes on the given index and sets the parent of these nodes to
	* this element.
	*
	* @see module:engine/view/element~ViewElement#_insertChild
	* @param index Offset at which nodes should be inserted.
	* @param items Items to be inserted.
	* @param element Element to which items will be inserted.
	* @returns Number of inserted nodes.
	*/
	insertChild(index, items, element) {
		return element._insertChild(index, items);
	}
	/**
	* Removes the given number of child nodes starting at the given index and set the parent of these nodes to `null`.
	*
	* @see module:engine/view/element~ViewElement#_removeChildren
	* @param index Offset from which nodes will be removed.
	* @param howMany Number of nodes to remove.
	* @param element Element which children will be removed.
	* @returns The array containing removed nodes.
	*/
	removeChildren(index, howMany, element) {
		return element._removeChildren(index, howMany);
	}
	/**
	* Removes given element from the view structure. Will not have effect on detached elements.
	*
	* @param element Element which will be removed.
	* @returns The array containing removed nodes.
	*/
	remove(element) {
		const parent = element.parent;
		if (parent) return this.removeChildren(parent.getChildIndex(element), 1, parent);
		return [];
	}
	/**
	* Replaces given element with the new one in the view structure. Will not have effect on detached elements.
	*
	* @param oldElement Element which will be replaced.
	* @param newElement Element which will be inserted in the place of the old element.
	* @returns Whether old element was successfully replaced.
	*/
	replace(oldElement, newElement) {
		const parent = oldElement.parent;
		if (parent) {
			const index = parent.getChildIndex(oldElement);
			this.removeChildren(index, 1, parent);
			this.insertChild(index, newElement, parent);
			return true;
		}
		return false;
	}
	/**
	* Removes given element from view structure and places its children in its position.
	* It does nothing if element has no parent.
	*
	* @param element Element to unwrap.
	*/
	unwrapElement(element) {
		const parent = element.parent;
		if (parent) {
			const index = parent.getChildIndex(element);
			this.remove(element);
			this.insertChild(index, element.getChildren(), parent);
		}
	}
	/**
	* Renames element by creating a copy of a given element but with its name changed and then moving contents of the
	* old element to the new one.
	*
	* Since this function creates a new element and removes the given one, the new element is returned to keep reference.
	*
	* @param newName New element name.
	* @param  element Element to be renamed.
	* @returns New element or null if the old element was not replaced (happens for detached elements).
	*/
	rename(newName, element) {
		const newElement = new ViewElement(this.document, newName, element.getAttributes(), element.getChildren());
		return this.replace(element, newElement) ? newElement : null;
	}
	/**
	* Adds or overwrites element's attribute with a specified key and value.
	*
	* ```ts
	* writer.setAttribute( 'href', 'http://ckeditor.com', linkElement );
	* ```
	*
	* @see module:engine/view/element~ViewElement#_setAttribute
	* @param key Attribute key.
	* @param value Attribute value.
	* @param element Element for which attribute will be set.
	*/
	setAttribute(key, value, element) {
		element._setAttribute(key, value);
	}
	/**
	* Removes attribute from the element.
	*
	* ```ts
	* writer.removeAttribute( 'href', linkElement );
	* ```
	*
	* @see module:engine/view/element~ViewElement#_removeAttribute
	* @param key Attribute key.
	* @param element Element from which attribute will be removed.
	*/
	removeAttribute(key, element) {
		element._removeAttribute(key);
	}
	/**
	* Adds specified class to the element.
	*
	* ```ts
	* writer.addClass( 'foo', linkElement );
	* writer.addClass( [ 'foo', 'bar' ], linkElement );
	* ```
	*
	* @see module:engine/view/element~ViewElement#_addClass
	* @param className Single class name or array of class names which will be added.
	* @param element Element for which class will be added.
	*/
	addClass(className, element) {
		element._addClass(className);
	}
	/**
	* Removes specified class from the element.
	*
	* ```ts
	* writer.removeClass( 'foo', linkElement );
	* writer.removeClass( [ 'foo', 'bar' ], linkElement );
	* ```
	*
	* @see module:engine/view/element~ViewElement#_removeClass
	* @param className Single class name or array of class names which will be removed.
	* @param element Element from which class will be removed.
	*/
	removeClass(className, element) {
		element._removeClass(className);
	}
	setStyle(property, valueOrElement, element) {
		if (isPlainObject(property) && element === void 0) valueOrElement._setStyle(property);
		else element._setStyle(property, valueOrElement);
	}
	/**
	* Removes specified style from the element.
	*
	* ```ts
	* writer.removeStyle( 'color', element );  // Removes 'color' style.
	* writer.removeStyle( [ 'color', 'border-top' ], element ); // Removes both 'color' and 'border-top' styles.
	* ```
	*
	* **Note**: This method can work with normalized style names if
	* {@link module:engine/controller/datacontroller~DataController#addStyleProcessorRules a particular style processor rule is enabled}.
	* See {@link module:engine/view/stylesmap~StylesMap#remove `StylesMap#remove()`} for details.
	*
	* @see module:engine/view/element~ViewElement#_removeStyle
	* @param property Style property name or names to be removed.
	* @param element Element from which style will be removed.
	*/
	removeStyle(property, element) {
		element._removeStyle(property);
	}
	/**
	* Sets a custom property on element. Unlike attributes, custom properties are not rendered to the DOM,
	* so they can be used to add special data to elements.
	*
	* @see module:engine/view/element~ViewElement#_setCustomProperty
	* @param key Custom property name/key.
	* @param value Custom property value to be stored.
	* @param element Element for which custom property will be set.
	*/
	setCustomProperty(key, value, element) {
		element._setCustomProperty(key, value);
	}
	/**
	* Removes a custom property stored under the given key.
	*
	* @see module:engine/view/element~ViewElement#_removeCustomProperty
	* @param key Name/key of the custom property to be removed.
	* @param element Element from which the custom property will be removed.
	* @returns Returns true if property was removed.
	*/
	removeCustomProperty(key, element) {
		return element._removeCustomProperty(key);
	}
	/**
	* Creates position at the given location. The location can be specified as:
	*
	* * a {@link module:engine/view/position~ViewPosition position},
	* * parent element and offset (offset defaults to `0`),
	* * parent element and `'end'` (sets position at the end of that element),
	* * {@link module:engine/view/item~ViewItem view item} and `'before'` or `'after'` (sets position before or after given view item).
	*
	* This method is a shortcut to other constructors such as:
	*
	* * {@link #createPositionBefore},
	* * {@link #createPositionAfter},
	*
	* @param offset Offset or one of the flags. Used only when first parameter is a {@link module:engine/view/item~ViewItem view item}.
	*/
	createPositionAt(itemOrPosition, offset) {
		return ViewPosition._createAt(itemOrPosition, offset);
	}
	/**
	* Creates a new position after given view item.
	*
	* @param item View item after which the position should be located.
	*/
	createPositionAfter(item) {
		return ViewPosition._createAfter(item);
	}
	/**
	* Creates a new position before given view item.
	*
	* @param item View item before which the position should be located.
	*/
	createPositionBefore(item) {
		return ViewPosition._createBefore(item);
	}
	/**
	* Creates a range spanning from `start` position to `end` position.
	*
	* **Note:** This factory method creates it's own {@link module:engine/view/position~ViewPosition} instances basing on passed values.
	*
	* @param start Start position.
	* @param end End position. If not set, range will be collapsed at `start` position.
	*/
	createRange(start, end) {
		return new ViewRange(start, end);
	}
	/**
	* Creates a range that starts before given {@link module:engine/view/item~ViewItem view item} and ends after it.
	*/
	createRangeOn(item) {
		return ViewRange._createOn(item);
	}
	/**
	* Creates a range inside an {@link module:engine/view/element~ViewElement element} which starts before the first child of
	* that element and ends after the last child of that element.
	*
	* @param element Element which is a parent for the range.
	*/
	createRangeIn(element) {
		return ViewRange._createIn(element);
	}
	createSelection(...args) {
		return new ViewSelection(...args);
	}
};

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
const HEX_COLOR_REGEXP = /^#([0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;
const RGB_COLOR_REGEXP = /^rgb\([ ]?([0-9]{1,3}[ %]?,[ ]?){2,3}[0-9]{1,3}[ %]?\)$/i;
const RGBA_COLOR_REGEXP = /^rgba\([ ]?([0-9]{1,3}[ %]?,[ ]?){3}(1|[0-9]+%|[0]?\.?[0-9]+)\)$/i;
const HSL_COLOR_REGEXP = /^hsl\([ ]?([0-9]{1,3}[ %]?[,]?[ ]*){3}(1|[0-9]+%|[0]?\.?[0-9]+)?\)$/i;
const HSLA_COLOR_REGEXP = /^hsla\([ ]?([0-9]{1,3}[ %]?,[ ]?){2,3}(1|[0-9]+%|[0]?\.?[0-9]+)\)$/i;
const CSS_SHORTHAND_VALUE_REGEXP = /\w+\((?:[^()]|\([^()]*\))*\)|\S+/gi;
const COLOR_NAMES = new Set([
	"black",
	"silver",
	"gray",
	"white",
	"maroon",
	"red",
	"purple",
	"fuchsia",
	"green",
	"lime",
	"olive",
	"yellow",
	"navy",
	"blue",
	"teal",
	"aqua",
	"orange",
	"aliceblue",
	"antiquewhite",
	"aquamarine",
	"azure",
	"beige",
	"bisque",
	"blanchedalmond",
	"blueviolet",
	"brown",
	"burlywood",
	"cadetblue",
	"chartreuse",
	"chocolate",
	"coral",
	"cornflowerblue",
	"cornsilk",
	"crimson",
	"cyan",
	"darkblue",
	"darkcyan",
	"darkgoldenrod",
	"darkgray",
	"darkgreen",
	"darkgrey",
	"darkkhaki",
	"darkmagenta",
	"darkolivegreen",
	"darkorange",
	"darkorchid",
	"darkred",
	"darksalmon",
	"darkseagreen",
	"darkslateblue",
	"darkslategray",
	"darkslategrey",
	"darkturquoise",
	"darkviolet",
	"deeppink",
	"deepskyblue",
	"dimgray",
	"dimgrey",
	"dodgerblue",
	"firebrick",
	"floralwhite",
	"forestgreen",
	"gainsboro",
	"ghostwhite",
	"gold",
	"goldenrod",
	"greenyellow",
	"grey",
	"honeydew",
	"hotpink",
	"indianred",
	"indigo",
	"ivory",
	"khaki",
	"lavender",
	"lavenderblush",
	"lawngreen",
	"lemonchiffon",
	"lightblue",
	"lightcoral",
	"lightcyan",
	"lightgoldenrodyellow",
	"lightgray",
	"lightgreen",
	"lightgrey",
	"lightpink",
	"lightsalmon",
	"lightseagreen",
	"lightskyblue",
	"lightslategray",
	"lightslategrey",
	"lightsteelblue",
	"lightyellow",
	"limegreen",
	"linen",
	"magenta",
	"mediumaquamarine",
	"mediumblue",
	"mediumorchid",
	"mediumpurple",
	"mediumseagreen",
	"mediumslateblue",
	"mediumspringgreen",
	"mediumturquoise",
	"mediumvioletred",
	"midnightblue",
	"mintcream",
	"mistyrose",
	"moccasin",
	"navajowhite",
	"oldlace",
	"olivedrab",
	"orangered",
	"orchid",
	"palegoldenrod",
	"palegreen",
	"paleturquoise",
	"palevioletred",
	"papayawhip",
	"peachpuff",
	"peru",
	"pink",
	"plum",
	"powderblue",
	"rosybrown",
	"royalblue",
	"saddlebrown",
	"salmon",
	"sandybrown",
	"seagreen",
	"seashell",
	"sienna",
	"skyblue",
	"slateblue",
	"slategray",
	"slategrey",
	"snow",
	"springgreen",
	"steelblue",
	"tan",
	"thistle",
	"tomato",
	"turquoise",
	"violet",
	"wheat",
	"whitesmoke",
	"yellowgreen",
	"activeborder",
	"activecaption",
	"appworkspace",
	"background",
	"buttonface",
	"buttonhighlight",
	"buttonshadow",
	"buttontext",
	"captiontext",
	"graytext",
	"highlight",
	"highlighttext",
	"inactiveborder",
	"inactivecaption",
	"inactivecaptiontext",
	"infobackground",
	"infotext",
	"menu",
	"menutext",
	"scrollbar",
	"threeddarkshadow",
	"threedface",
	"threedhighlight",
	"threedlightshadow",
	"threedshadow",
	"window",
	"windowframe",
	"windowtext",
	"rebeccapurple",
	"currentcolor",
	"transparent"
]);
/**
* Checks if string contains [color](https://developer.mozilla.org/en-US/docs/Web/CSS/color) CSS value.
*
* ```ts
* isColorStyleValue( '#f00' );						// true
* isColorStyleValue( '#AA00BB33' );				// true
* isColorStyleValue( 'rgb(0, 0, 250)' );			// true
* isColorStyleValue( 'hsla(240, 100%, 50%, .7)' );	// true
* isColorStyleValue( 'deepskyblue' );				// true
* ```
*
* **Note**: It does not support CSS Level 4 whitespace syntax, system colors and radius values for HSL colors.
*/
function isColorStyleValue(string) {
	if (string.startsWith("#")) return HEX_COLOR_REGEXP.test(string);
	if (string.startsWith("rgb")) return RGB_COLOR_REGEXP.test(string) || RGBA_COLOR_REGEXP.test(string);
	if (string.startsWith("hsl")) return HSL_COLOR_REGEXP.test(string) || HSLA_COLOR_REGEXP.test(string);
	return COLOR_NAMES.has(string.toLowerCase());
}
const lineStyleValues = [
	"none",
	"hidden",
	"dotted",
	"dashed",
	"solid",
	"double",
	"groove",
	"ridge",
	"inset",
	"outset"
];
/**
* Checks if string contains [line style](https://developer.mozilla.org/en-US/docs/Web/CSS/border-style) CSS value.
*/
function isLineStyleValue(string) {
	return lineStyleValues.includes(string);
}
const lengthRegExp = /^([+-]?[0-9]*([.][0-9]+)?(px|cm|mm|in|pc|pt|ch|em|ex|rem|vh|vw|vmin|vmax)|0)$/;
/**
* Checks if string contains [length](https://developer.mozilla.org/en-US/docs/Web/CSS/length) CSS value.
*/
function isLengthStyleValue(string) {
	return lengthRegExp.test(string);
}
const PERCENTAGE_VALUE_REGEXP = /^[+-]?[0-9]*([.][0-9]+)?%$/;
/**
* Checks if string contains [percentage](https://developer.mozilla.org/en-US/docs/Web/CSS/percentage) CSS value.
*/
function isPercentageStyleValue(string) {
	return PERCENTAGE_VALUE_REGEXP.test(string);
}
const sizeValues = [
	"auto",
	"cover",
	"contain"
];
/**
* Checks if a string is a valid CSS `background-size` value token.
*/
function isSizeStyleValue(value) {
	return sizeValues.includes(value) || isLengthStyleValue(value) || isPercentageStyleValue(value);
}
const repeatValues = [
	"repeat-x",
	"repeat-y",
	"repeat",
	"space",
	"round",
	"no-repeat"
];
/**
* Checks if string contains [background repeat](https://developer.mozilla.org/en-US/docs/Web/CSS/background-repeat) CSS value.
*/
function isRepeatStyleValue(string) {
	return repeatValues.includes(string);
}
const positionValues = [
	"center",
	"top",
	"bottom",
	"left",
	"right"
];
/**
* Checks if string contains [background position](https://developer.mozilla.org/en-US/docs/Web/CSS/background-position) CSS value.
*/
function isPositionStyleValue(string) {
	return positionValues.includes(string);
}
const attachmentValues = [
	"fixed",
	"scroll",
	"local"
];
/**
* Checks if string contains [background attachment](https://developer.mozilla.org/en-US/docs/Web/CSS/background-attachment) CSS value.
*/
function isAttachmentStyleValue(string) {
	return attachmentValues.includes(string);
}
const originValues = [
	"border-box",
	"padding-box",
	"content-box"
];
/**
* Checks if string contains [background origin](https://developer.mozilla.org/en-US/docs/Web/CSS/background-origin) CSS value.
*/
function isOriginStyleValue(string) {
	return originValues.includes(string);
}
const clipValues = [
	"border-box",
	"padding-box",
	"content-box",
	"text"
];
/**
* Checks if string contains [background clip](https://developer.mozilla.org/en-US/docs/Web/CSS/background-clip) CSS value.
*/
function isClipStyleValue(string) {
	return clipValues.includes(string);
}
const urlRegExp = /^url\(/;
/**
* Checks if string contains [URL](https://developer.mozilla.org/en-US/docs/Web/CSS/url) CSS value.
*/
function isURLStyleValue(string) {
	return urlRegExp.test(string);
}
/**
* Parses box sides as individual values.
*/
function getBoxSidesStyleValues(value = "") {
	if (value === "") return {
		top: void 0,
		right: void 0,
		bottom: void 0,
		left: void 0
	};
	const values = getShorthandStylesValues(value);
	const top = values[0];
	const bottom = values[2] || top;
	const right = values[1] || top;
	return {
		top,
		bottom,
		right,
		left: values[3] || right
	};
}
/**
* Default reducer for CSS properties that concerns edges of a box
* [shorthand](https://developer.mozilla.org/en-US/docs/Web/CSS/Shorthand_properties) notations:
*
* ```ts
* stylesProcessor.setReducer( 'padding', getBoxSidesStyleValueReducer( 'padding' ) );
* ```
*/
function getBoxSidesStyleValueReducer(styleShorthand) {
	return (value) => {
		const { top, right, bottom, left } = value;
		const reduced = [];
		if (![
			top,
			right,
			left,
			bottom
		].every((value) => !!value)) {
			if (top) reduced.push([styleShorthand + "-top", top]);
			if (right) reduced.push([styleShorthand + "-right", right]);
			if (bottom) reduced.push([styleShorthand + "-bottom", bottom]);
			if (left) reduced.push([styleShorthand + "-left", left]);
		} else reduced.push([styleShorthand, getBoxSidesStyleShorthandValue(value)]);
		return reduced;
	};
}
/**
* Returns a [shorthand](https://developer.mozilla.org/en-US/docs/Web/CSS/Shorthand_properties) notation
* of a CSS property value.
*
* ```ts
* getBoxSidesStyleShorthandValue( { top: '1px', right: '1px', bottom: '2px', left: '1px' } );
* // will return '1px 1px 2px'
* ```
*/
function getBoxSidesStyleShorthandValue({ top, right, bottom, left }) {
	const out = [];
	if (left !== right) out.push(top, right, bottom, left);
	else if (bottom !== top) out.push(top, right, bottom);
	else if (right !== top) out.push(top, right);
	else out.push(top);
	return out.join(" ");
}
/**
* Creates a normalizer for a [shorthand](https://developer.mozilla.org/en-US/docs/Web/CSS/Shorthand_properties) 1-to-4 value.
*
* ```ts
* stylesProcessor.setNormalizer( 'margin', getPositionStyleShorthandNormalizer( 'margin' ) );
* ```
*/
function getPositionStyleShorthandNormalizer(shorthand) {
	return (value) => {
		return {
			path: shorthand,
			value: getBoxSidesStyleValues(value)
		};
	};
}
/**
* Parses parts of a 1-to-4 value notation - handles some CSS values with spaces (like RGB()).
*
* ```ts
* getShorthandStylesValues( 'red blue RGB(0, 0, 0)');
* // will return [ 'red', 'blue', 'RGB(0, 0, 0)' ]
* ```
*/
function getShorthandStylesValues(string) {
	const matches = string.trim().slice(0, 1500).matchAll(CSS_SHORTHAND_VALUE_REGEXP);
	return Array.from(matches).map((i) => i[0]);
}

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* @module engine/view/styles/background
*/
/**
* CSS initial values for background longhand properties.
*/
const BACKGROUND_INITIAL_ARRAY_VALUES = {
	image: "none",
	repeat: "repeat",
	position: "0% 0%",
	size: "auto",
	origin: "padding-box",
	clip: "border-box",
	attachment: "scroll"
};
/**
* CSS function names that represent image values in the `background` shorthand.
*/
const IMAGE_FUNCTIONS = [
	"linear-gradient",
	"repeating-linear-gradient",
	"radial-gradient",
	"repeating-radial-gradient",
	"conic-gradient",
	"repeating-conic-gradient",
	"url"
];
/**
* Adds background CSS styles processing rules to the given `StylesProcessor`.
*
* Registers normalizers for the `background` shorthand and its longhand properties,
* reducers that serialize them back to CSS strings, and the relation mapping between them.
*
* ```ts
* editor.data.addStyleProcessorRules( addBackgroundStylesRules );
* ```
*
* @param stylesProcessor The styles processor instance to register rules on.
*/
function addBackgroundStylesRules(stylesProcessor) {
	stylesProcessor.setNormalizer("background-color", getBackgroundColorNormalizer());
	for (const property in BACKGROUND_INITIAL_ARRAY_VALUES) {
		stylesProcessor.setNormalizer(`background-${property}`, getBackgroundArrayPropertyNormalizer(`background.${property}`));
		stylesProcessor.setReducer(`background-${property}`, getBackgroundArrayPropertyReducer(`background-${property}`));
	}
	stylesProcessor.setNormalizer("background", getBackgroundNormalizer());
	stylesProcessor.setReducer("background", getBackgroundReducer());
	stylesProcessor.setStyleRelation("background", [
		"background-color",
		"background-image",
		"background-repeat",
		"background-position",
		"background-size",
		"background-attachment",
		"background-origin",
		"background-clip"
	]);
}
/**
* Returns a normalizer for the `background` shorthand property.
*
* Parses the raw CSS value into comma-separated layers and merges them into
* a structured `Background` object stored under the `background` path.
*/
function getBackgroundNormalizer() {
	return (value) => {
		const layers = parseBackgroundIntoLayers(value);
		if (!layers.length) layers.push({
			...getDefaultBackgroundLayer(),
			color: value
		});
		return {
			path: "background",
			value: normalizeBackgroundLayers(layers)
		};
	};
}
/**
* Returns a normalizer for the `background-color` longhand property.
*
* Stores the raw color value directly under the `background.color` path.
*/
function getBackgroundColorNormalizer() {
	return (value) => ({
		path: "background.color",
		value
	});
}
/**
* Returns a normalizer for a comma-separated background array longhand property
* (e.g. `background-image`, `background-repeat`, `background-position`, `background-attachment`).
*
* Splits the raw value by top-level commas and stores the resulting array under the given path.
*
* @param path The target style path, e.g. `'background.image'`.
*/
function getBackgroundArrayPropertyNormalizer(path) {
	return (value) => ({
		path,
		value: splitByTopLevelCommas(value)
	});
}
/**
* Returns a reducer for a comma-separated background array longhand property
* (e.g. `background-image`, `background-repeat`, `background-position`, `background-attachment`).
*
* Serializes an array of per-layer values back into a comma-separated CSS string.
*
* @param property The CSS property name to output, e.g. `'background-image'`.
*/
function getBackgroundArrayPropertyReducer(property) {
	return (value) => {
		return [[property, value.join(", ")]];
	};
}
/**
* Returns a reducer for the `background` property. Depending on which longhand properties are present, it either:
*
* * Serializes into a concise `background` shorthand if all required longhand properties are present and non-empty, or
* * Serializes into individual longhand properties for those that have defined values.
*
* This prevents overwriting external stylesheet rules with default values that were not explicitly set in the model.
* For example, if only `background-color` is defined in the model, we emit just `background-color` rather than
* the full `background` shorthand, which would reset all other sub-properties to their initial values.
*/
function getBackgroundReducer() {
	return (value) => {
		const background = value;
		if (Object.keys(BACKGROUND_INITIAL_ARRAY_VALUES).every((key) => {
			const value = background[key];
			return value !== void 0 && (!Array.isArray(value) || value.length > 0);
		})) return serializeToShorthandBackground(background);
		return serializeToLonghandBackground(background);
	};
}
/**
* Serializes a structured `Background` object into a concise CSS `background` shorthand string.
*/
function serializeToShorthandBackground(background) {
	const result = [];
	const shorthand = extractBackgroundLayers(background).map((layer) => serializeBackgroundLayer(layer).trim()).filter(Boolean).join(", ");
	if (shorthand) result.push(["background", shorthand]);
	return result;
}
/**
* Serializes a structured `Background` object into individual CSS longhand properties for those that have defined values.
* Each longhand property is serialized into a comma-separated string of per-layer values.
*
* This is used when not all required longhand properties are present, to avoid emitting default values for missing properties
* and potentially overwriting external stylesheet rules.
*/
function serializeToLonghandBackground(background) {
	const result = [];
	for (const key in BACKGROUND_INITIAL_ARRAY_VALUES) {
		const value = background[key];
		if (Array.isArray(value)) result.push([`background-${key}`, value.join(", ")]);
	}
	if (background.color !== void 0) result.push(["background-color", background.color]);
	return result;
}
/**
* Serializes a single `BackgroundLayer` into a CSS background layer string.
* Properties equal to their CSS initial values are omitted.
*
* @param layer A single background layer to serialize.
* @returns A space-separated CSS string for the layer, or an empty string if no parts are present.
*/
function serializeBackgroundLayer(layer) {
	const parts = [];
	if (layer.image && layer.image !== BACKGROUND_INITIAL_ARRAY_VALUES.image) parts.push(layer.image);
	const positionStr = layer.position.join(" ");
	const isPositionDefault = !positionStr || positionStr === BACKGROUND_INITIAL_ARRAY_VALUES.position;
	if (!isPositionDefault) parts.push(positionStr);
	const sizeStr = layer.size.join(" ");
	if (!(!sizeStr || sizeStr === BACKGROUND_INITIAL_ARRAY_VALUES.size)) {
		if (isPositionDefault) parts.push("0% 0%");
		parts.push("/", sizeStr);
	}
	const repeatStr = layer.repeat.join(" ");
	if (repeatStr && repeatStr !== BACKGROUND_INITIAL_ARRAY_VALUES.repeat) parts.push(repeatStr);
	if (layer.attachment && layer.attachment !== BACKGROUND_INITIAL_ARRAY_VALUES.attachment) parts.push(layer.attachment);
	const isOriginDefault = layer.origin === BACKGROUND_INITIAL_ARRAY_VALUES.origin;
	const isClipDefault = layer.clip === BACKGROUND_INITIAL_ARRAY_VALUES.clip;
	if (!isOriginDefault || !isClipDefault) {
		parts.push(layer.origin);
		if (layer.clip !== layer.origin) parts.push(layer.clip);
	}
	if (layer.color) parts.push(layer.color);
	return parts.join(" ");
}
/**
* Splits a structured `Background` object into an array of individual `BackgroundLayer` objects.
*
* The number of layers is determined by the longest array among the longhand properties.
* The `color` value, if present, is always assigned to the last layer (per CSS spec).
*
* @param background The structured background object to split.
* @returns An array of per-layer background objects.
*
* @example
* // Input: { image: [ 'url(a.png)', 'url(b.png)' ], repeat: [ 'no-repeat', 'repeat' ], color: 'blue' }
* // Output: [
* //   { image: 'url(a.png)', repeat: [ 'no-repeat' ] },
* //   { image: 'url(b.png)', repeat: [ 'repeat' ], color: 'blue' }
* // ]
*/
function extractBackgroundLayers(background) {
	const layerCount = getTotalBackgroundLayers(background);
	const layers = Array.from({ length: layerCount }, (_, i) => {
		const layer = getDefaultBackgroundLayer();
		if (background.image?.[i]) layer.image = background.image[i];
		if (background.position?.[i]) layer.position = background.position[i].split(" ");
		if (background.size?.[i]) layer.size = background.size[i].split(" ");
		if (background.repeat?.[i]) layer.repeat = background.repeat[i].split(" ");
		if (background.attachment?.[i]) layer.attachment = background.attachment[i];
		if (background.origin?.[i]) layer.origin = background.origin[i];
		if (background.clip?.[i]) layer.clip = background.clip[i];
		return layer;
	});
	if (background.color) layers.at(-1).color = background.color;
	return layers;
}
/**
* Determines the total number of background layers based on the longest array among the longhand properties in the `Background` object.
*
* @param background The structured background object to analyze.
* @returns The total number of layers, which is the maximum length among the longhand property arrays.
*/
function getTotalBackgroundLayers(background) {
	let layersCount = 0;
	for (const value of Object.values(background)) if (Array.isArray(value) && value.length > layersCount) layersCount = value.length;
	return layersCount;
}
/**
* Merges an array of `BackgroundLayer` objects into a single structured `Background` object,
* storing each longhand property as an array of per-layer values.
*
* Each input layer always has all properties defined (via `getDefaultBackgroundLayer`),
* so the resulting arrays are fully populated for every layer.
*
* @param layers An array of parsed background layers.
* @returns A merged `Background` object.
*
* @example
* // Input: [ { image: 'url(a.png)', repeat: [ 'no-repeat' ], position: [ '0%', '0%' ], ... }, { image: 'none', color: 'red', ... } ]
* // Output: {
* //   image: [ 'url(a.png)', 'none' ],
* //   position: [ '0% 0%', '0% 0%' ],
* //   repeat: [ 'no-repeat', 'repeat' ],
* //   ...
* //   color: 'red'
* // }
*/
function normalizeBackgroundLayers(layers) {
	const background = {
		image: [],
		position: [],
		size: [],
		repeat: [],
		attachment: [],
		origin: [],
		clip: []
	};
	for (const layer of layers) {
		background.position.push(layer.position.join(" "));
		background.size.push(layer.size.join(" "));
		background.repeat.push(layer.repeat.join(" "));
		background.image.push(layer.image);
		background.attachment.push(layer.attachment);
		background.origin.push(layer.origin);
		background.clip.push(layer.clip);
	}
	if (layers.at(-1)?.color) background.color = layers.at(-1).color;
	return background;
}
/**
* Parses a raw CSS `background` value and splits it into an array of `BackgroundLayer` objects,
* one per comma-separated layer.
*
* The CSS `background` shorthand syntax is:
* `background: [ <bg-layer> , ]* <final-bg-layer>`
*
* Commas inside function calls (e.g. `rgba()`, `linear-gradient()`) are correctly ignored
* during splitting.
*
* @param value The raw CSS background value string.
* @returns An array of parsed background layers.
*
* @example
* splitBackgroundIntoLayers( 'url(a.png) no-repeat, red' );
* // → [ { image: 'url(a.png)', repeat: [ 'no-repeat' ] }, { color: 'red' } ]
*/
function parseBackgroundIntoLayers(value) {
	return splitByTopLevelCommas(value).map(parseBackgroundLayer).filter((obj) => obj !== null);
}
/**
* Parses a single CSS background layer string into a structured `BackgroundLayer` object.
*
* First extracts any image function (gradient or `url()`), then classifies the remaining
* space-separated tokens as repeat, position, attachment, or color values.
*
* @param layer A single (non-comma-separated) background layer string.
* @returns A structured background layer object. If no valid properties were found, returns `null`.
*
* @example
* parseBackgroundLayer( 'url(bg.png) top left no-repeat fixed' );
* // → { image: 'url(bg.png)', position: [ 'top', 'left' ], repeat: [ 'no-repeat' ], attachment: 'fixed' }
*/
function parseBackgroundLayer(layer) {
	const background = {};
	const { value: valueWithoutImage, image } = extractBackgroundImage(layer);
	const parts = getShorthandStylesValues(valueWithoutImage);
	if (image) background.image = image;
	for (let i = 0; i < parts.length; i++) {
		const part = parts[i];
		if (part === "/") {
			background.size = [];
			while (i + 1 < parts.length && isSizeStyleValue(parts[i + 1])) background.size.push(parts[++i]);
		} else if (isRepeatStyleValue(part)) {
			background.repeat ||= [];
			background.repeat.push(part);
		} else if (isPositionStyleValue(part) || isPercentageStyleValue(part)) {
			background.position ||= [];
			background.position.push(part);
			if (i + 1 < parts.length && isPercentageStyleValue(part) && isPercentageStyleValue(parts[i + 1])) background.position.push(parts[++i]);
		} else if (isAttachmentStyleValue(part)) background.attachment = part;
		else if (isClipStyleValue(part)) if (isOriginStyleValue(part) && !background.origin) {
			background.origin = part;
			background.clip = part;
		} else background.clip = part;
		else if (isColorStyleValue(part)) background.color = part;
	}
	if (isEmpty(background)) return null;
	return {
		...getDefaultBackgroundLayer(),
		...background
	};
}
/**
* Returns a `BackgroundLayer` object with all properties set to their CSS initial values.
*
* Used as the base when parsing the `background` shorthand so that every property of a parsed
* layer always has a defined value.
*
* @returns A background layer with default values.
*/
function getDefaultBackgroundLayer() {
	return {
		attachment: BACKGROUND_INITIAL_ARRAY_VALUES.attachment,
		image: BACKGROUND_INITIAL_ARRAY_VALUES.image,
		origin: BACKGROUND_INITIAL_ARRAY_VALUES.origin,
		clip: BACKGROUND_INITIAL_ARRAY_VALUES.clip,
		position: BACKGROUND_INITIAL_ARRAY_VALUES.position.split(" "),
		repeat: BACKGROUND_INITIAL_ARRAY_VALUES.repeat.split(" "),
		size: BACKGROUND_INITIAL_ARRAY_VALUES.size.split(" ")
	};
}
/**
* Extracts the first CSS image function (`linear-gradient`, `url`, etc.) from a raw CSS value string
* and returns it separately along with the remaining string with the image function removed.
*
* Handles arbitrarily nested parentheses correctly (e.g. `rgba()` inside a gradient).
* Only one image function is extracted per call — the caller is responsible for looping
* if multiple image functions may be present.
*
* @param value The raw CSS value string to process.
* @returns An object with `value` (the string with the image function removed) and
*          `image` (the extracted function string, or `null` if none was found).
*
* @example
* extractBackgroundImage( 'linear-gradient(red, blue) no-repeat' );
* // → { value: ' no-repeat', image: 'linear-gradient(red, blue)' }
*/
function extractBackgroundImage(value) {
	for (const imageFunction of IMAGE_FUNCTIONS) {
		const prefix = imageFunction + "(";
		const firstIndex = value.indexOf(prefix);
		if (firstIndex < 0) continue;
		let acc = imageFunction;
		let nesting = 0;
		for (let i = firstIndex + imageFunction.length; i < value.length; i++) {
			const char = value[i];
			if (char === "(") nesting++;
			else if (char === ")") nesting--;
			acc += char;
			if (nesting === 0) return {
				value: value.slice(0, firstIndex) + value.slice(i + 1),
				image: acc
			};
		}
	}
	if (value.includes("none")) return {
		value: value.replace("none", ""),
		image: "none"
	};
	return {
		value,
		image: null
	};
}
/**
* Splits a CSS value string by commas that are at the top level (i.e. not inside parentheses).
*
* This is necessary because commas appear both as layer separators in the `background` shorthand
* and as argument separators inside functions like `rgba()` or `linear-gradient()`.
*
* @param value The CSS value string to split.
* @returns An array of trimmed top-level comma-separated segments.
*
* @example
* splitByTopLevelCommas( 'linear-gradient(red, blue), center' );
* // → [ 'linear-gradient(red, blue)', 'center' ]
*/
function splitByTopLevelCommas(value) {
	const parts = [];
	let current = "";
	let nesting = 0;
	for (const char of value) {
		if (char === "(") nesting++;
		else if (char === ")") nesting--;
		else if (char === "," && nesting === 0) {
			parts.push(current.trim());
			current = "";
			continue;
		}
		current += char;
	}
	if (current.trim()) parts.push(current.trim());
	return parts;
}

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* Adds a border CSS styles processing rules.
*
* ```ts
* editor.data.addStyleProcessorRules( addBorderStylesRules );
* ```
*
* This rules merges all [border](https://developer.mozilla.org/en-US/docs/Web/CSS/border) styles notation shorthands:
*
* - border
* - border-top
* - border-right
* - border-bottom
* - border-left
* - border-color
* - border-style
* - border-width
*
* and all corresponding longhand forms (like `border-top-color`, `border-top-style`, etc).
*
* It does not handle other shorthands (like `border-radius` or `border-image`).
*/
function addBorderStylesRules(stylesProcessor) {
	stylesProcessor.setNormalizer("border", getBorderNormalizer());
	stylesProcessor.setNormalizer("border-top", getBorderPositionNormalizer("top"));
	stylesProcessor.setNormalizer("border-right", getBorderPositionNormalizer("right"));
	stylesProcessor.setNormalizer("border-bottom", getBorderPositionNormalizer("bottom"));
	stylesProcessor.setNormalizer("border-left", getBorderPositionNormalizer("left"));
	stylesProcessor.setNormalizer("border-color", getBorderPropertyNormalizer("color"));
	stylesProcessor.setNormalizer("border-width", getBorderPropertyNormalizer("width"));
	stylesProcessor.setNormalizer("border-style", getBorderPropertyNormalizer("style"));
	stylesProcessor.setNormalizer("border-top-color", getBorderPropertyPositionNormalizer("color", "top"));
	stylesProcessor.setNormalizer("border-top-style", getBorderPropertyPositionNormalizer("style", "top"));
	stylesProcessor.setNormalizer("border-top-width", getBorderPropertyPositionNormalizer("width", "top"));
	stylesProcessor.setNormalizer("border-right-color", getBorderPropertyPositionNormalizer("color", "right"));
	stylesProcessor.setNormalizer("border-right-style", getBorderPropertyPositionNormalizer("style", "right"));
	stylesProcessor.setNormalizer("border-right-width", getBorderPropertyPositionNormalizer("width", "right"));
	stylesProcessor.setNormalizer("border-bottom-color", getBorderPropertyPositionNormalizer("color", "bottom"));
	stylesProcessor.setNormalizer("border-bottom-style", getBorderPropertyPositionNormalizer("style", "bottom"));
	stylesProcessor.setNormalizer("border-bottom-width", getBorderPropertyPositionNormalizer("width", "bottom"));
	stylesProcessor.setNormalizer("border-left-color", getBorderPropertyPositionNormalizer("color", "left"));
	stylesProcessor.setNormalizer("border-left-style", getBorderPropertyPositionNormalizer("style", "left"));
	stylesProcessor.setNormalizer("border-left-width", getBorderPropertyPositionNormalizer("width", "left"));
	stylesProcessor.setExtractor("border-top", getBorderPositionExtractor("top"));
	stylesProcessor.setExtractor("border-right", getBorderPositionExtractor("right"));
	stylesProcessor.setExtractor("border-bottom", getBorderPositionExtractor("bottom"));
	stylesProcessor.setExtractor("border-left", getBorderPositionExtractor("left"));
	stylesProcessor.setExtractor("border-top-color", "border.color.top");
	stylesProcessor.setExtractor("border-right-color", "border.color.right");
	stylesProcessor.setExtractor("border-bottom-color", "border.color.bottom");
	stylesProcessor.setExtractor("border-left-color", "border.color.left");
	stylesProcessor.setExtractor("border-top-width", "border.width.top");
	stylesProcessor.setExtractor("border-right-width", "border.width.right");
	stylesProcessor.setExtractor("border-bottom-width", "border.width.bottom");
	stylesProcessor.setExtractor("border-left-width", "border.width.left");
	stylesProcessor.setExtractor("border-top-style", "border.style.top");
	stylesProcessor.setExtractor("border-right-style", "border.style.right");
	stylesProcessor.setExtractor("border-bottom-style", "border.style.bottom");
	stylesProcessor.setExtractor("border-left-style", "border.style.left");
	stylesProcessor.setReducer("border-color", getBoxSidesStyleValueReducer("border-color"));
	stylesProcessor.setReducer("border-style", getBoxSidesStyleValueReducer("border-style"));
	stylesProcessor.setReducer("border-width", getBoxSidesStyleValueReducer("border-width"));
	stylesProcessor.setReducer("border-top", getBorderPositionReducer("top"));
	stylesProcessor.setReducer("border-right", getBorderPositionReducer("right"));
	stylesProcessor.setReducer("border-bottom", getBorderPositionReducer("bottom"));
	stylesProcessor.setReducer("border-left", getBorderPositionReducer("left"));
	stylesProcessor.setReducer("border", getBorderReducer());
	stylesProcessor.setStyleRelation("border", [
		"border-color",
		"border-style",
		"border-width",
		"border-top",
		"border-right",
		"border-bottom",
		"border-left",
		"border-top-color",
		"border-right-color",
		"border-bottom-color",
		"border-left-color",
		"border-top-style",
		"border-right-style",
		"border-bottom-style",
		"border-left-style",
		"border-top-width",
		"border-right-width",
		"border-bottom-width",
		"border-left-width"
	]);
	stylesProcessor.setStyleRelation("border-color", [
		"border-top-color",
		"border-right-color",
		"border-bottom-color",
		"border-left-color"
	]);
	stylesProcessor.setStyleRelation("border-style", [
		"border-top-style",
		"border-right-style",
		"border-bottom-style",
		"border-left-style"
	]);
	stylesProcessor.setStyleRelation("border-width", [
		"border-top-width",
		"border-right-width",
		"border-bottom-width",
		"border-left-width"
	]);
	stylesProcessor.setStyleRelation("border-top", [
		"border-top-color",
		"border-top-style",
		"border-top-width"
	]);
	stylesProcessor.setStyleRelation("border-right", [
		"border-right-color",
		"border-right-style",
		"border-right-width"
	]);
	stylesProcessor.setStyleRelation("border-bottom", [
		"border-bottom-color",
		"border-bottom-style",
		"border-bottom-width"
	]);
	stylesProcessor.setStyleRelation("border-left", [
		"border-left-color",
		"border-left-style",
		"border-left-width"
	]);
}
function getBorderNormalizer() {
	return (value) => {
		const { color, style, width } = normalizeBorderShorthand(value);
		return {
			path: "border",
			value: {
				color: getBoxSidesStyleValues(color),
				style: getBoxSidesStyleValues(style),
				width: getBoxSidesStyleValues(width)
			}
		};
	};
}
function getBorderPositionNormalizer(side) {
	return (value) => {
		const { color, style, width } = normalizeBorderShorthand(value);
		const border = {};
		if (color !== void 0) border.color = { [side]: color };
		if (style !== void 0) border.style = { [side]: style };
		if (width !== void 0) border.width = { [side]: width };
		return {
			path: "border",
			value: border
		};
	};
}
function getBorderPropertyNormalizer(propertyName) {
	return (value) => {
		return {
			path: "border",
			value: toBorderPropertyShorthand(value, propertyName)
		};
	};
}
function toBorderPropertyShorthand(value, property) {
	return { [property]: getBoxSidesStyleValues(value) };
}
function getBorderPropertyPositionNormalizer(property, side) {
	return (value) => {
		return {
			path: "border",
			value: { [property]: { [side]: value } }
		};
	};
}
function getBorderPositionExtractor(which) {
	return (name, styles) => {
		if (styles.border) return extractBorderPosition(styles.border, which);
	};
}
function extractBorderPosition(border, which) {
	const value = {};
	if (border.width && border.width[which]) value.width = border.width[which];
	if (border.style && border.style[which]) value.style = border.style[which];
	if (border.color && border.color[which]) value.color = border.color[which];
	return value;
}
function normalizeBorderShorthand(string) {
	const result = {};
	const parts = getShorthandStylesValues(string);
	for (const part of parts) if (isLengthStyleValue(part) || /thin|medium|thick/.test(part)) result.width = part;
	else if (isLineStyleValue(part)) result.style = part;
	else result.color = part;
	return result;
}
/**
* The border reducer factory.
*
* It tries to produce the most optimal output for the specified styles.
*
* For a border style:
*
* ```css
* style: {top: "solid", bottom: "solid", right: "solid", left: "solid"}
* ```
*
* It will produce: `border-style: solid`.
* For a border style and color:
*
* ```css
* color: {top: "#ff0", bottom: "#ff0", right: "#ff0", left: "#ff0"}
* style: {top: "solid", bottom: "solid", right: "solid", left: "solid"}
* ```
*
* It will produce: `border-color: #ff0; border-style: solid`.
* If all border parameters are specified:
*
* ```css
* color: {top: "#ff0", bottom: "#ff0", right: "#ff0", left: "#ff0"}
* style: {top: "solid", bottom: "solid", right: "solid", left: "solid"}
* width: {top: "2px", bottom: "2px", right: "2px", left: "2px"}
* ```
*
* It will combine everything into a single property: `border: 2px solid #ff0`.
*
* The definitions are merged only if all border selectors have the same values.
*/
function getBorderReducer() {
	return (value) => {
		const topStyles = extractBorderPosition(value, "top");
		const rightStyles = extractBorderPosition(value, "right");
		const bottomStyles = extractBorderPosition(value, "bottom");
		const leftStyles = extractBorderPosition(value, "left");
		const borderStyles = [
			topStyles,
			rightStyles,
			bottomStyles,
			leftStyles
		];
		const borderStylesByType = {
			width: getReducedStyleValueForType(borderStyles, "width"),
			style: getReducedStyleValueForType(borderStyles, "style"),
			color: getReducedStyleValueForType(borderStyles, "color")
		};
		const reducedBorderStyle = reduceBorderPosition(borderStylesByType, "all");
		if (reducedBorderStyle.length) return reducedBorderStyle;
		return [
			...Object.entries(borderStylesByType).reduce((reducedStyleTypes, [type, value]) => {
				if (value) {
					reducedStyleTypes.push([`border-${type}`, value]);
					borderStyles.forEach((style) => delete style[type]);
				}
				return reducedStyleTypes;
			}, []),
			...reduceBorderPosition(topStyles, "top"),
			...reduceBorderPosition(rightStyles, "right"),
			...reduceBorderPosition(bottomStyles, "bottom"),
			...reduceBorderPosition(leftStyles, "left")
		];
	};
	/**
	* @param styles The array of objects with `style`, `color`, `width` properties.
	*/
	function getReducedStyleValueForType(styles, type) {
		return styles.map((style) => style[type]).reduce((result, style) => result == style ? result : null);
	}
}
function getBorderPositionReducer(which) {
	return (value) => reduceBorderPosition(value, which);
}
/**
* Returns an array with reduced border styles depending on the specified values.
*
* If all border properties (width, style, color) are specified, the returned selector will be
* merged into a group: `border-*: [width] [style] [color]`.
*
* Otherwise, the specific definitions will be returned: `border-(width|style|color)-*: [value]`.
*
* @param value Styles if defined.
* @param which The border position.
*/
function reduceBorderPosition(value, which) {
	const borderTypes = [];
	if (value && value.width) borderTypes.push("width");
	if (value && value.style) borderTypes.push("style");
	if (value && value.color) borderTypes.push("color");
	if (borderTypes.length == 3) {
		const borderValue = borderTypes.map((item) => value[item]).join(" ");
		return [which == "all" ? ["border", borderValue] : [`border-${which}`, borderValue]];
	}
	if (which == "all") return [];
	return borderTypes.map((type) => {
		return [`border-${which}-${type}`, value[type]];
	});
}

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* Adds a margin CSS styles processing rules.
*
* ```ts
* editor.data.addStyleProcessorRules( addMarginStylesRules );
* ```
*/
function addMarginStylesRules(stylesProcessor) {
	stylesProcessor.setNormalizer("margin", getPositionStyleShorthandNormalizer("margin"));
	stylesProcessor.setNormalizer("margin-top", (value) => ({
		path: "margin.top",
		value
	}));
	stylesProcessor.setNormalizer("margin-right", (value) => ({
		path: "margin.right",
		value
	}));
	stylesProcessor.setNormalizer("margin-bottom", (value) => ({
		path: "margin.bottom",
		value
	}));
	stylesProcessor.setNormalizer("margin-left", (value) => ({
		path: "margin.left",
		value
	}));
	stylesProcessor.setReducer("margin", getBoxSidesStyleValueReducer("margin"));
	stylesProcessor.setStyleRelation("margin", [
		"margin-top",
		"margin-right",
		"margin-bottom",
		"margin-left"
	]);
}

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* Adds a padding CSS styles processing rules.
*
* ```ts
* editor.data.addStyleProcessorRules( addPaddingStylesRules );
* ```
*/
function addPaddingStylesRules(stylesProcessor) {
	stylesProcessor.setNormalizer("padding", getPositionStyleShorthandNormalizer("padding"));
	stylesProcessor.setNormalizer("padding-top", (value) => ({
		path: "padding.top",
		value
	}));
	stylesProcessor.setNormalizer("padding-right", (value) => ({
		path: "padding.right",
		value
	}));
	stylesProcessor.setNormalizer("padding-bottom", (value) => ({
		path: "padding.bottom",
		value
	}));
	stylesProcessor.setNormalizer("padding-left", (value) => ({
		path: "padding.left",
		value
	}));
	stylesProcessor.setReducer("padding", getBoxSidesStyleValueReducer("padding"));
	stylesProcessor.setStyleRelation("padding", [
		"padding-top",
		"padding-right",
		"padding-bottom",
		"padding-left"
	]);
}

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* @module engine/dev-utils/view
*/
/**
* Collection of methods for manipulating the {@link module:engine/view/view view} for testing purposes.
*/
const ELEMENT_RANGE_START_TOKEN = "[";
const ELEMENT_RANGE_END_TOKEN = "]";
const TEXT_RANGE_START_TOKEN = "{";
const TEXT_RANGE_END_TOKEN = "}";
const allowedTypes = {
	"container": ViewContainerElement,
	"attribute": ViewAttributeElement,
	"empty": ViewEmptyElement,
	"ui": ViewUIElement,
	"raw": ViewRawElement
};
const domConverterStub = { setContentOf: (node, html) => {
	node.innerHTML = html;
} };
/**
* Writes the content of the {@link module:engine/view/document~ViewDocument document} to an HTML-like string.
*
* @param view The view to stringify.
* @param options.withoutSelection Whether to write the selection. When set to `true`, the selection will
* not be included in the returned string.
* @param options.rootName The name of the root from which the data should be stringified. If not provided,
* the default `main` name will be used.
* @param options.showType When set to `true`, the type of elements will be printed (`<container:p>`
* instead of `<p>`, `<attribute:b>` instead of `<b>` and `<empty:img>` instead of `<img>`).
* @param options.showPriority When set to `true`, the attribute element's priority will be printed
* (`<span view-priority="12">`, `<b view-priority="10">`).
* @param options.renderUIElements When set to `true`, the inner content of each
* {@link module:engine/view/uielement~ViewUIElement} will be printed.
* @param options.renderRawElements When set to `true`, the inner content of each
* {@link module:engine/view/rawelement~ViewRawElement} will be printed.
* @param options.domConverter When set to an actual {@link module:engine/view/domconverter~ViewDomConverter ViewDomConverter}
* instance, it lets the conversion go through exactly the same flow the editing view is going through,
* i.e. with view data filtering. Otherwise the simple stub is used.
* @returns The stringified data.
*/
const _getViewData = /* #__PURE__ */ (() => {
	const getViewData = function(view, options = {}) {
		if (!(view instanceof EditingView)) throw new TypeError("View needs to be an instance of module:engine/view/view~EditingView.");
		const document = view.document;
		const withoutSelection = !!options.withoutSelection;
		const rootName = options.rootName || "main";
		const root = document.getRoot(rootName);
		const stringifyOptions = {
			showType: options.showType,
			showPriority: options.showPriority,
			renderUIElements: options.renderUIElements,
			renderRawElements: options.renderRawElements,
			ignoreRoot: true,
			domConverter: options.domConverter,
			skipListItemIds: options.skipListItemIds
		};
		return withoutSelection ? getViewData._stringify(root, null, stringifyOptions) : getViewData._stringify(root, document.selection, stringifyOptions);
	};
	getViewData._stringify = _stringifyView;
	return getViewData;
})();
/**
* Sets the content of a view {@link module:engine/view/document~ViewDocument document} provided as an HTML-like string.
*
* @param data An HTML-like string to write into the document.
* @param options.rootName The root name where _parseViewd data will be stored. If not provided,
* the default `main` name will be used.
*/
const _setViewData = /* #__PURE__ */ (() => {
	const setViewData = function(view, data, options = {}) {
		if (!(view instanceof EditingView)) throw new TypeError("View needs to be an instance of module:engine/view/view~EditingView.");
		const document = view.document;
		const rootName = options.rootName || "main";
		const root = document.getRoot(rootName);
		view.change((writer) => {
			const result = setViewData._parse(data, { rootElement: root });
			if (result.view && result.selection) writer.setSelection(result.selection);
		});
	};
	setViewData._parse = _parseView;
	return setViewData;
})();
/**
* Converts view elements to HTML-like string representation.
*
* A root element can be provided as {@link module:engine/view/text~ViewText text}:
*
* ```ts
* const text = downcastWriter.createText( 'foobar' );
* stringify( text ); // 'foobar'
* ```
*
* or as an {@link module:engine/view/element~ViewElement element}:
*
* ```ts
* const element = downcastWriter.createElement( 'p', null, downcastWriter.createText( 'foobar' ) );
* stringify( element ); // '<p>foobar</p>'
* ```
*
* or as a {@link module:engine/view/documentfragment~ViewDocumentFragment document fragment}:
*
* ```ts
* const text = downcastWriter.createText( 'foobar' );
* const b = downcastWriter.createElement( 'b', { name: 'test' }, text );
* const p = downcastWriter.createElement( 'p', { style: 'color:red;' } );
* const fragment = downcastWriter.createDocumentFragment( [ p, b ] );
*
* stringify( fragment ); // '<p style="color:red;"></p><b name="test">foobar</b>'
* ```
*
* Additionally, a {@link module:engine/view/documentselection~ViewDocumentSelection selection} instance can be provided.
* Ranges from the selection will then be included in the output data.
* If a range position is placed inside the element node, it will be represented with `[` and `]`:
*
* ```ts
* const text = downcastWriter.createText( 'foobar' );
* const b = downcastWriter.createElement( 'b', null, text );
* const p = downcastWriter.createElement( 'p', null, b );
* const selection = downcastWriter.createSelection(
* 	downcastWriter.createRangeIn( p )
* );
*
* stringify( p, selection ); // '<p>[<b>foobar</b>]</p>'
* ```
*
* If a range is placed inside the text node, it will be represented with `{` and `}`:
*
* ```ts
* const text = downcastWriter.createText( 'foobar' );
* const b = downcastWriter.createElement( 'b', null, text );
* const p = downcastWriter.createElement( 'p', null, b );
* const selection = downcastWriter.createSelection(
* 	downcastWriter.createRange( downcastWriter.createPositionAt( text, 1 ), downcastWriter.createPositionAt( text, 5 ) )
* );
*
* stringify( p, selection ); // '<p><b>f{ooba}r</b></p>'
* ```
*
* ** Note: **
* It is possible to unify selection markers to `[` and `]` for both (inside and outside text)
* by setting the `sameSelectionCharacters=true` option. It is mainly used when the view stringify option is used by
* model utilities.
*
* Multiple ranges are supported:
*
* ```ts
* const text = downcastWriter.createText( 'foobar' );
* const selection = downcastWriter.createSelection( [
* 	downcastWriter.createRange( downcastWriter.createPositionAt( text, 0 ), downcastWriter.createPositionAt( text, 1 ) ),
* 	downcastWriter.createRange( downcastWriter.createPositionAt( text, 3 ), downcastWriter.createPositionAt( text, 5 ) )
* ] );
*
* stringify( text, selection ); // '{f}oo{ba}r'
* ```
*
* A {@link module:engine/view/range~ViewRange range} or {@link module:engine/view/position~ViewPosition position} instance can be provided
* instead of the {@link module:engine/view/documentselection~ViewDocumentSelection selection} instance. If a range instance
* is provided, it will be converted to a selection containing this range. If a position instance is provided, it will
* be converted to a selection containing one range collapsed at this position.
*
* ```ts
* const text = downcastWriter.createText( 'foobar' );
* const range = downcastWriter.createRange( downcastWriter.createPositionAt( text, 0 ), downcastWriter.createPositionAt( text, 1 ) );
* const position = downcastWriter.createPositionAt( text, 3 );
*
* stringify( text, range ); // '{f}oobar'
* stringify( text, position ); // 'foo{}bar'
* ```
*
* An additional `options` object can be provided.
* If `options.showType` is set to `true`, element's types will be
* presented for {@link module:engine/view/attributeelement~ViewAttributeElement attribute elements},
* {@link module:engine/view/containerelement~ViewContainerElement container elements}
* {@link module:engine/view/emptyelement~ViewEmptyElement empty elements}
* and {@link module:engine/view/uielement~ViewUIElement UI elements}:
*
* ```ts
* const attribute = downcastWriter.createAttributeElement( 'b' );
* const container = downcastWriter.createContainerElement( 'p' );
* const empty = downcastWriter.createEmptyElement( 'img' );
* const ui = downcastWriter.createUIElement( 'span' );
* getData( attribute, null, { showType: true } ); // '<attribute:b></attribute:b>'
* getData( container, null, { showType: true } ); // '<container:p></container:p>'
* getData( empty, null, { showType: true } ); // '<empty:img></empty:img>'
* getData( ui, null, { showType: true } ); // '<ui:span></ui:span>'
* ```
*
* If `options.showPriority` is set to `true`, a priority will be displayed for all
* {@link module:engine/view/attributeelement~ViewAttributeElement attribute elements}.
*
* ```ts
* const attribute = downcastWriter.createAttributeElement( 'b' );
* attribute._priority = 20;
* getData( attribute, null, { showPriority: true } ); // <b view-priority="20"></b>
* ```
*
* If `options.showAttributeElementId` is set to `true`, the attribute element's id will be displayed for all
* {@link module:engine/view/attributeelement~ViewAttributeElement attribute elements} that have it set.
*
* ```ts
* const attribute = downcastWriter.createAttributeElement( 'span' );
* attribute._id = 'marker:foo';
* getData( attribute, null, { showAttributeElementId: true } ); // <span view-id="marker:foo"></span>
* ```
*
* @param node The node to stringify.
* @param selectionOrPositionOrRange A selection instance whose ranges will be included in the returned string data.
* If a range instance is provided, it will be converted to a selection containing this range. If a position instance
* is provided, it will be converted to a selection containing one range collapsed at this position.
* @param options An object with additional options.
* @param options.showType When set to `true`, the type of elements will be printed (`<container:p>`
* instead of `<p>`, `<attribute:b>` instead of `<b>` and `<empty:img>` instead of `<img>`).
* @param options.showPriority When set to `true`,  the attribute element's priority will be printed
* (`<span view-priority="12">`, `<b view-priority="10">`).
* @param options.showAttributeElementId When set to `true`, attribute element's id will be printed
* (`<span id="marker:foo">`).
* @param options.ignoreRoot When set to `true`, the root's element opening and closing will not be printed.
* Mainly used by the `getData` function to ignore the {@link module:engine/view/document~ViewDocument document's} root element.
* @param options.sameSelectionCharacters When set to `true`, the selection inside the text will be marked as
*  `{` and `}` and the selection outside the text as `[` and `]`. When set to `false`, both will be marked as `[` and `]` only.
* @param options.renderUIElements When set to `true`, the inner content of each
* {@link module:engine/view/uielement~ViewUIElement} will be printed.
* @param options.renderRawElements When set to `true`, the inner content of each
* {@link module:engine/view/rawelement~ViewRawElement} will be printed.
* @param options.domConverter When set to an actual {@link module:engine/view/domconverter~ViewDomConverter ViewDomConverter}
* instance, it lets the conversion go through exactly the same flow the editing view is going through,
* i.e. with view data filtering. Otherwise the simple stub is used.
* @returns An HTML-like string representing the view.
*/
function _stringifyView(node, selectionOrPositionOrRange = null, options = {}) {
	let selection;
	if (selectionOrPositionOrRange instanceof ViewPosition || selectionOrPositionOrRange instanceof ViewRange) selection = new ViewDocumentSelection(selectionOrPositionOrRange);
	else selection = selectionOrPositionOrRange;
	return new ViewStringify(node, selection, options).stringify();
}
/**
* Parses an HTML-like string and returns a view tree.
* A simple string will be converted to a {@link module:engine/view/text~ViewText text} node:
*
* ```ts
* _parseView( 'foobar' ); // Returns an instance of text.
* ```
*
* {@link module:engine/view/element~ViewElement Elements} will be _parseViewd with attributes as children:
*
* ```ts
* _parseView( '<b name="baz">foobar</b>' ); // Returns an instance of element with the `baz` attribute and a text child node.
* ```
*
* Multiple nodes provided on root level will be converted to a
* {@link module:engine/view/documentfragment~ViewDocumentFragment document fragment}:
*
* ```ts
* _parseView( '<b>foo</b><i>bar</i>' ); // Returns a document fragment with two child elements.
* ```
*
* The method can _parseView multiple {@link module:engine/view/range~ViewRange ranges} provided in string data and return a
* {@link module:engine/view/documentselection~ViewDocumentSelection selection} instance containing these ranges. Ranges placed inside
* {@link module:engine/view/text~ViewText text} nodes should be marked using `{` and `}` brackets:
*
* ```ts
* const { text, selection } = _parseView( 'f{ooba}r' );
* ```
*
* Ranges placed outside text nodes should be marked using `[` and `]` brackets:
*
* ```ts
* const { root, selection } = _parseView( '<p>[<b>foobar</b>]</p>' );
* ```
*
* ** Note: **
* It is possible to unify selection markers to `[` and `]` for both (inside and outside text)
* by setting `sameSelectionCharacters=true` option. It is mainly used when the view _parseView option is used by model utilities.
*
* Sometimes there is a need for defining the order of ranges inside the created selection. This can be achieved by providing
* the range order array as an additional parameter:
*
* ```ts
* const { root, selection } = _parseView( '{fo}ob{ar}{ba}z', { order: [ 2, 3, 1 ] } );
* ```
*
* In the example above, the first range (`{fo}`) will be added to the selection as the second one, the second range (`{ar}`) will be
* added as the third and the third range (`{ba}`) will be added as the first one.
*
* If the selection's last range should be added as a backward one
* (so the {@link module:engine/view/documentselection~ViewDocumentSelection#anchor selection anchor} is represented
* by the `end` position and {@link module:engine/view/documentselection~ViewDocumentSelection#focus selection focus} is
* represented by the `start` position), use the `lastRangeBackward` flag:
*
* ```ts
* const { root, selection } = _parseView( `{foo}bar{baz}`, { lastRangeBackward: true } );
* ```
*
* Some more examples and edge cases:
*
* ```ts
* // Returns an empty document fragment.
* _parseView( '' );
*
* // Returns an empty document fragment and a collapsed selection.
* const { root, selection } = _parseView( '[]' );
*
* // Returns an element and a selection that is placed inside the document fragment containing that element.
* const { root, selection } = _parseView( '[<a></a>]' );
* ```
*
* @param data An HTML-like string to be parsed.
* @param options.order An array with the order of parsed ranges added to the returned
* {@link module:engine/view/documentselection~ViewDocumentSelection Selection} instance. Each element should represent the
* desired position of each range in the selection instance. For example: `[2, 3, 1]` means that the first range will be
* placed as the second, the second as the third and the third as the first.
* @param options.lastRangeBackward If set to `true`, the last range will be added as backward to the returned
* {@link module:engine/view/documentselection~ViewDocumentSelection selection} instance.
* @param options.rootElement The default root to use when parsing elements.
* When set to `null`, the root element will be created automatically. If set to
* {@link module:engine/view/element~ViewElement Element} or
* {@link module:engine/view/documentfragment~ViewDocumentFragment DocumentFragment},
* this node will be used as the root for all parsed nodes.
* @param options.sameSelectionCharacters When set to `false`, the selection inside the text should be marked using
* `{` and `}` and the selection outside the ext using `[` and `]`. When set to `true`, both should be marked with `[` and `]` only.
* @returns Returns the parsed view node or an object with two fields: `view` and `selection` when selection ranges were included in the
* data to parse.
*/
function _parseView(data, options = {}) {
	const viewDocument = new ViewDocument(new StylesProcessor());
	options.order = options.order || [];
	const rangeParser = new RangeParser({ sameSelectionCharacters: options.sameSelectionCharacters });
	const processor = new XmlDataProcessor(viewDocument, { namespaces: Object.keys(allowedTypes) });
	if (options.inlineObjectElements) processor.domConverter.inlineObjectElements.push(...options.inlineObjectElements);
	let view = processor.toView(data);
	view = _convertViewElements(view);
	if (options.rootElement) {
		const root = options.rootElement;
		const nodes = view._removeChildren(0, view.childCount);
		root._removeChildren(0, root.childCount);
		root._appendChild(nodes);
		view = root;
	}
	const ranges = rangeParser._parseView(view, options.order);
	if (view.is("documentFragment") && view.childCount === 1) view = view.getChild(0);
	if (ranges.length) {
		const selection = new ViewDocumentSelection(ranges, { backward: !!options.lastRangeBackward });
		return {
			view,
			selection
		};
	}
	if (view.parent) view._remove();
	return view;
}
/**
* Private helper class used for converting ranges represented as text inside view {@link module:engine/view/text~ViewText text nodes}.
*/
var RangeParser = class {
	sameSelectionCharacters;
	_positions;
	/**
	* Creates a range parser instance.
	*
	* @param options The range parser configuration.
	* @param options.sameSelectionCharacters When set to `true`, the selection inside the text is marked as
	* `{` and `}` and the selection outside the text as `[` and `]`. When set to `false`, both are marked as `[` and `]`.
	*/
	constructor(options) {
		this.sameSelectionCharacters = !!options.sameSelectionCharacters;
	}
	/**
	* Parses the view and returns ranges represented inside {@link module:engine/view/text~ViewText text nodes}.
	* The method will remove all occurrences of `{`, `}`, `[` and `]` from found text nodes. If a text node is empty after
	* the process, it will be removed, too.
	*
	* @param node The starting node.
	* @param order The order of ranges. Each element should represent the desired position of the range after
	* sorting. For example: `[2, 3, 1]` means that the first range will be placed as the second, the second as the third and the third
	* as the first.
	* @returns An array with ranges found.
	*/
	_parseView(node, order) {
		this._positions = [];
		this._getPositions(node);
		let ranges = this._createRanges();
		if (order.length) {
			if (order.length != ranges.length) throw new Error(`Parse error - there are ${ranges.length} ranges found, but ranges order array contains ${order.length} elements.`);
			ranges = this._sortRanges(ranges, order);
		}
		return ranges;
	}
	/**
	* Gathers positions of brackets inside the view tree starting from the provided node. The method will remove all occurrences of
	* `{`, `}`, `[` and `]` from found text nodes. If a text node is empty after the process, it will be removed, too.
	*
	* @param node Staring node.
	*/
	_getPositions(node) {
		if (node.is("documentFragment") || node.is("element")) {
			const children = [...node.getChildren()];
			for (const child of children) this._getPositions(child);
		}
		if (node.is("$text")) {
			const regexp = new RegExp(`[${TEXT_RANGE_START_TOKEN}${TEXT_RANGE_END_TOKEN}\\${ELEMENT_RANGE_END_TOKEN}\\${ELEMENT_RANGE_START_TOKEN}]`, "g");
			let text = node.data;
			let match;
			let offset = 0;
			const brackets = [];
			while (match = regexp.exec(text)) {
				const index = match.index;
				const bracket = match[0];
				brackets.push({
					bracket,
					textOffset: index - offset
				});
				offset++;
			}
			text = text.replace(regexp, "");
			node._data = text;
			const index = node.index;
			const parent = node.parent;
			if (!text) node._remove();
			for (const item of brackets) if (text) if (this.sameSelectionCharacters || !this.sameSelectionCharacters && (item.bracket == TEXT_RANGE_START_TOKEN || item.bracket == TEXT_RANGE_END_TOKEN)) this._positions.push({
				bracket: item.bracket,
				position: new ViewPosition(node, item.textOffset)
			});
			else {
				if (!this.sameSelectionCharacters && item.textOffset !== 0 && item.textOffset !== text.length) throw new Error(`Parse error - range delimiter '${item.bracket}' is placed inside text node.`);
				const offset = item.textOffset === 0 ? index : index + 1;
				this._positions.push({
					bracket: item.bracket,
					position: new ViewPosition(parent, offset)
				});
			}
			else {
				if (!this.sameSelectionCharacters && item.bracket == TEXT_RANGE_START_TOKEN || item.bracket == TEXT_RANGE_END_TOKEN) throw new Error(`Parse error - text range delimiter '${item.bracket}' is placed inside empty text node. `);
				this._positions.push({
					bracket: item.bracket,
					position: new ViewPosition(parent, index)
				});
			}
		}
	}
	/**
	* Sorts ranges in a given order. Range order should be an array and each element should represent the desired position
	* of the range after sorting.
	* For example: `[2, 3, 1]` means that the first range will be placed as the second, the second as the third and the third
	* as the first.
	*
	* @param ranges Ranges to sort.
	* @param rangesOrder An array with new range order.
	* @returns Sorted ranges array.
	*/
	_sortRanges(ranges, rangesOrder) {
		const sortedRanges = [];
		let index = 0;
		for (const newPosition of rangesOrder) {
			if (ranges[newPosition - 1] === void 0) throw new Error("Parse error - provided ranges order is invalid.");
			sortedRanges[newPosition - 1] = ranges[index];
			index++;
		}
		return sortedRanges;
	}
	/**
	* Uses all found bracket positions to create ranges from them.
	*/
	_createRanges() {
		const ranges = [];
		let range = null;
		for (const item of this._positions) {
			if (!range && (item.bracket == ELEMENT_RANGE_END_TOKEN || item.bracket == TEXT_RANGE_END_TOKEN)) throw new Error(`Parse error - end of range was found '${item.bracket}' but range was not started before.`);
			if (range && (item.bracket == ELEMENT_RANGE_START_TOKEN || item.bracket == TEXT_RANGE_START_TOKEN)) throw new Error(`Parse error - start of range was found '${item.bracket}' but one range is already started.`);
			if (item.bracket == ELEMENT_RANGE_START_TOKEN || item.bracket == TEXT_RANGE_START_TOKEN) range = new ViewRange(item.position, item.position);
			else {
				range.end = item.position;
				ranges.push(range);
				range = null;
			}
		}
		if (range !== null) throw new Error("Parse error - range was started but no end delimiter was found.");
		return ranges;
	}
};
/**
* Private helper class used for converting the view tree to a string.
*/
var ViewStringify = class {
	root;
	selection;
	ranges;
	showType;
	showPriority;
	showAttributeElementId;
	ignoreRoot;
	sameSelectionCharacters;
	renderUIElements;
	renderRawElements;
	domConverter;
	skipListItemIds;
	/**
	* Creates a view stringify instance.
	*
	* @param selection A selection whose ranges should also be converted to a string.
	* @param options An options object.
	* @param options.showType When set to `true`, the type of elements will be printed (`<container:p>`
	* instead of `<p>`, `<attribute:b>` instead of `<b>` and `<empty:img>` instead of `<img>`).
	* @param options.showPriority When set to `true`, the attribute element's priority will be printed.
	* @param options.ignoreRoot When set to `true`, the root's element opening and closing tag will not
	* be outputted.
	* @param options.sameSelectionCharacters When set to `true`, the selection inside the text is marked as
	* `{` and `}` and the selection outside the text as `[` and `]`. When set to `false`, both are marked as `[` and `]`.
	* @param options.renderUIElements When set to `true`, the inner content of each
	* {@link module:engine/view/uielement~ViewUIElement} will be printed.
	* @param options.renderRawElements When set to `true`, the inner content of each
	* @param options.domConverter When set to an actual {@link module:engine/view/domconverter~ViewDomConverter ViewDomConverter}
	* instance, it lets the conversion go through exactly the same flow the editing view is going through,
	* i.e. with view data filtering. Otherwise the simple stub is used.
	* {@link module:engine/view/rawelement~ViewRawElement} will be printed.
	* @param options.skipListItemIds When set to `true`, `<li>` elements will not have `listItemId` attribute. By default it's hidden
	* because it's randomly generated and hard to verify properly, while bringing little value.
	*/
	constructor(root, selection, options) {
		this.root = root;
		this.selection = selection;
		this.ranges = [];
		if (selection) this.ranges = [...selection.getRanges()];
		this.showType = !!options.showType;
		this.showPriority = !!options.showPriority;
		this.showAttributeElementId = !!options.showAttributeElementId;
		this.ignoreRoot = !!options.ignoreRoot;
		this.sameSelectionCharacters = !!options.sameSelectionCharacters;
		this.renderUIElements = !!options.renderUIElements;
		this.renderRawElements = !!options.renderRawElements;
		this.domConverter = options.domConverter || domConverterStub;
		this.skipListItemIds = options.skipListItemIds !== void 0 ? !!options.skipListItemIds : true;
	}
	/**
	* Converts the view to a string.
	*
	* @returns String representation of the view elements.
	*/
	stringify() {
		let result = "";
		this._walkView(this.root, (chunk) => {
			result += chunk;
		});
		if (this.skipListItemIds) result = result.replaceAll(/ data-list-item-id="[^"]+"/g, "");
		return result;
	}
	/**
	* Executes a simple walker that iterates over all elements in the view tree starting from the root element.
	* Calls the `callback` with parsed chunks of string data.
	*/
	_walkView(root, callback) {
		const ignore = this.ignoreRoot && this.root === root;
		if (root.is("element") || root.is("documentFragment")) {
			if (root.is("element") && !ignore) callback(this._stringifyElementOpen(root));
			if (this.renderUIElements && root.is("uiElement")) callback(root.render(document, this.domConverter).innerHTML);
			else if (this.renderRawElements && root.is("rawElement")) {
				const rawContentContainer = document.createElement("div");
				root.render(rawContentContainer, this.domConverter);
				callback(rawContentContainer.innerHTML);
			} else {
				let offset = 0;
				callback(this._stringifyElementRanges(root, offset));
				for (const child of root.getChildren()) {
					this._walkView(child, callback);
					offset++;
					callback(this._stringifyElementRanges(root, offset));
				}
			}
			if (root.is("element") && !ignore) callback(this._stringifyElementClose(root));
		}
		if (root.is("$text")) callback(this._stringifyTextRanges(root));
	}
	/**
	* Checks if a given {@link module:engine/view/element~ViewElement element} has
	* a {@link module:engine/view/range~ViewRange#start range start}
	* or a {@link module:engine/view/range~ViewRange#start range end} placed at a given offset and returns its string representation.
	*/
	_stringifyElementRanges(element, offset) {
		let start = "";
		let end = "";
		let collapsed = "";
		for (const range of this.ranges) {
			if (range.start.parent == element && range.start.offset === offset) if (range.isCollapsed) collapsed += "[]";
			else start += ELEMENT_RANGE_START_TOKEN;
			if (range.end.parent === element && range.end.offset === offset && !range.isCollapsed) end += ELEMENT_RANGE_END_TOKEN;
		}
		return end + collapsed + start;
	}
	/**
	* Checks if a given {@link module:engine/view/element~ViewElement Text node} has a
	* {@link module:engine/view/range~ViewRange#start range start} or a
	* {@link module:engine/view/range~ViewRange#start range end} placed somewhere inside. Returns a string representation of text
	* with range delimiters placed inside.
	*/
	_stringifyTextRanges(node) {
		const length = node.data.length;
		const data = node.data.split("");
		let rangeStartToken, rangeEndToken;
		if (this.sameSelectionCharacters) {
			rangeStartToken = ELEMENT_RANGE_START_TOKEN;
			rangeEndToken = ELEMENT_RANGE_END_TOKEN;
		} else {
			rangeStartToken = TEXT_RANGE_START_TOKEN;
			rangeEndToken = TEXT_RANGE_END_TOKEN;
		}
		data[length] = "";
		const result = data.map((letter) => {
			return {
				letter,
				start: "",
				end: "",
				collapsed: ""
			};
		});
		for (const range of this.ranges) {
			const start = range.start;
			const end = range.end;
			if (start.parent == node && start.offset >= 0 && start.offset <= length) if (range.isCollapsed) result[end.offset].collapsed += rangeStartToken + rangeEndToken;
			else result[start.offset].start += rangeStartToken;
			if (end.parent == node && end.offset >= 0 && end.offset <= length && !range.isCollapsed) result[end.offset].end += rangeEndToken;
		}
		return result.map((item) => item.end + item.collapsed + item.start + item.letter).join("");
	}
	/**
	* Converts the passed {@link module:engine/view/element~ViewElement element} to an opening tag.
	*
	* Depending on the current configuration, the opening tag can be simple (`<a>`), contain a type prefix (`<container:p>`,
	* `<attribute:a>` or `<empty:img>`), contain priority information ( `<attribute:a view-priority="20">` ),
	* or contain element id ( `<attribute:span view-id="foo">` ). Element attributes will also be included
	* (`<a href="https://ckeditor.com" name="foobar">`).
	*/
	_stringifyElementOpen(element) {
		const priority = this._stringifyElementPriority(element);
		const id = this._stringifyElementId(element);
		return `<${[
			[this._stringifyElementType(element), element.name].filter((i) => i !== "").join(":"),
			priority,
			id,
			this._stringifyElementAttributes(element)
		].filter((i) => i !== "").join(" ")}>`;
	}
	/**
	* Converts the passed {@link module:engine/view/element~ViewElement element} to a closing tag.
	* Depending on the current configuration, the closing tag can be simple (`</a>`) or contain a type prefix (`</container:p>`,
	* `</attribute:a>` or `</empty:img>`).
	*/
	_stringifyElementClose(element) {
		return `</${[this._stringifyElementType(element), element.name].filter((i) => i !== "").join(":")}>`;
	}
	/**
	* Converts the passed {@link module:engine/view/element~ViewElement element's} type to its string representation
	*
	* Returns:
	* * 'attribute' for {@link module:engine/view/attributeelement~ViewAttributeElement attribute elements},
	* * 'container' for {@link module:engine/view/containerelement~ViewContainerElement container elements},
	* * 'empty' for {@link module:engine/view/emptyelement~ViewEmptyElement empty elements},
	* * 'ui' for {@link module:engine/view/uielement~ViewUIElement UI elements},
	* * 'raw' for {@link module:engine/view/rawelement~ViewRawElement raw elements},
	* * an empty string when the current configuration is preventing showing elements' types.
	*/
	_stringifyElementType(element) {
		if (this.showType) {
			for (const type in allowedTypes) if (element instanceof allowedTypes[type]) return type;
		}
		return "";
	}
	/**
	* Converts the passed {@link module:engine/view/element~ViewElement element} to its priority representation.
	*
	* The priority string representation will be returned when the passed element is an instance of
	* {@link module:engine/view/attributeelement~ViewAttributeElement attribute element} and the current configuration allows to show the
	* priority. Otherwise returns an empty string.
	*/
	_stringifyElementPriority(element) {
		if (this.showPriority && element.is("attributeElement")) return `view-priority="${element.priority}"`;
		return "";
	}
	/**
	* Converts the passed {@link module:engine/view/element~ViewElement element} to its id representation.
	*
	* The id string representation will be returned when the passed element is an instance of
	* {@link module:engine/view/attributeelement~ViewAttributeElement attribute element}, the element has an id
	* and the current configuration allows to show the id. Otherwise returns an empty string.
	*/
	_stringifyElementId(element) {
		if (this.showAttributeElementId && element.is("attributeElement") && element.id) return `view-id="${element.id}"`;
		return "";
	}
	/**
	* Converts the passed {@link module:engine/view/element~ViewElement element} attributes to their string representation.
	* If an element has no attributes, an empty string is returned.
	*/
	_stringifyElementAttributes(element) {
		const attributes = [];
		const keys = [...element.getAttributeKeys()].sort();
		for (const attribute of keys) {
			let attributeValue;
			if (attribute === "class") attributeValue = [...element.getClassNames()].sort().join(" ");
			else if (attribute === "style") attributeValue = [...element.getStyleNames()].sort().map((style) => `${style}:${element.getStyle(style).replace(/"/g, "&quot;")}`).join(";");
			else attributeValue = element.getAttribute(attribute);
			attributes.push(`${attribute}="${attributeValue}"`);
		}
		return attributes.join(" ");
	}
};
/**
* Converts {@link module:engine/view/element~ViewElement elements} to
* {@link module:engine/view/attributeelement~ViewAttributeElement attribute elements},
* {@link module:engine/view/containerelement~ViewContainerElement container elements},
* {@link module:engine/view/emptyelement~ViewEmptyElement empty elements} or
* {@link module:engine/view/uielement~ViewUIElement UI elements}.
* It converts the whole tree starting from the `rootNode`. The conversion is based on element names.
* See the `_convertElement` method for more details.
*
* @param rootNode The root node to convert.
* @returns The root node of converted elements.
*/
function _convertViewElements(rootNode) {
	if (rootNode.is("element") || rootNode.is("documentFragment")) {
		const convertedElement = rootNode.is("documentFragment") ? new ViewDocumentFragment(rootNode.document) : _convertElement(rootNode.document, rootNode);
		for (const child of [...rootNode.getChildren()]) {
			if (convertedElement.is("emptyElement")) throw new Error("Parse error - cannot parse inside ViewEmptyElement.");
			else if (convertedElement.is("uiElement")) throw new Error("Parse error - cannot parse inside UIElement.");
			else if (convertedElement.is("rawElement")) throw new Error("Parse error - cannot parse inside RawElement.");
			convertedElement._appendChild(_convertViewElements(child));
		}
		return convertedElement;
	}
	return rootNode;
}
/**
* Converts an {@link module:engine/view/element~ViewElement element} to
* {@link module:engine/view/attributeelement~ViewAttributeElement attribute element},
* {@link module:engine/view/containerelement~ViewContainerElement container element},
* {@link module:engine/view/emptyelement~ViewEmptyElement empty element} or
* {@link module:engine/view/uielement~ViewUIElement UI element}.
* If the element's name is in the format of `attribute:b`, it will be converted to
* an {@link module:engine/view/attributeelement~ViewAttributeElement attribute element} with a priority of 11.
* Additionally, attribute elements may have specified priority (for example `view-priority="11"`) and/or
* id (for example `view-id="foo"`).
* If the element's name is in the format of `container:p`, it will be converted to
* a {@link module:engine/view/containerelement~ViewContainerElement container element}.
* If the element's name is in the format of `empty:img`, it will be converted to
* an {@link module:engine/view/emptyelement~ViewEmptyElement empty element}.
* If the element's name is in the format of `ui:span`, it will be converted to
* a {@link module:engine/view/uielement~ViewUIElement UI element}.
* If the element's name does not contain any additional information, a {@link module:engine/view/element~ViewElement view Element} will be
* returned.
*
* @param viewElement A view element to convert.
* @returns A tree view element converted according to its name.
*/
function _convertElement(viewDocument, viewElement) {
	const info = _convertElementNameAndInfo(viewElement);
	const ElementConstructor = allowedTypes[info.type];
	const newElement = ElementConstructor ? new ElementConstructor(viewDocument, info.name) : new ViewElement(viewDocument, info.name);
	if (newElement.is("attributeElement")) {
		if (info.priority !== null) newElement._priority = info.priority;
		if (info.id !== null) newElement._id = info.id;
	}
	for (const attributeKey of viewElement.getAttributeKeys()) newElement._setAttribute(attributeKey, viewElement.getAttribute(attributeKey));
	return newElement;
}
/**
* Converts the `view-priority` attribute and the {@link module:engine/view/element~ViewElement#name element's name} information needed for
* creating {@link module:engine/view/attributeelement~ViewAttributeElement attribute element},
* {@link module:engine/view/containerelement~ViewContainerElement container element},
* {@link module:engine/view/emptyelement~ViewEmptyElement empty element} or
* {@link module:engine/view/uielement~ViewUIElement UI element}.
* The name can be provided in two formats: as a simple element's name (`div`), or as a type and name (`container:div`,
* `attribute:span`, `empty:img`, `ui:span`);
*
* @param viewElement The element whose name should be converted.
* @returns An object with parsed information:
* * `name` The parsed name of the element.
* * `type` The parsed type of the element. It can be `attribute`, `container` or `empty`.
* * `priority` The parsed priority of the element.
*/
function _convertElementNameAndInfo(viewElement) {
	const parts = viewElement.name.split(":");
	const priority = _convertPriority(viewElement.getAttribute("view-priority"));
	const id = viewElement.hasAttribute("view-id") ? viewElement.getAttribute("view-id") : null;
	viewElement._removeAttribute("view-priority");
	viewElement._removeAttribute("view-id");
	if (parts.length == 1) return {
		name: parts[0],
		type: priority !== null ? "attribute" : null,
		priority,
		id
	};
	const type = _convertType(parts[0]);
	if (type) return {
		name: parts[1],
		type,
		priority,
		id
	};
	throw new Error(`Parse error - cannot parse element's name: ${viewElement.name}.`);
}
/**
* Checks if the element's type is allowed. Returns `attribute`, `container`, `empty` or `null`.
*/
function _convertType(type) {
	return type in allowedTypes ? type : null;
}
/**
* Checks if a given priority is allowed. Returns null if the priority cannot be converted.
*/
function _convertPriority(priorityString) {
	const priority = parseInt(priorityString, 10);
	if (!isNaN(priority)) return priority;
	return null;
}

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* @module engine/dev-utils/model
*/
/**
* Collection of methods for manipulating the {@link module:engine/model/model model} for testing purposes.
*/
/**
* Writes the content of a model {@link module:engine/model/document~ModelDocument document} to an HTML-like string.
*
* ```ts
* getData( editor.model ); // -> '<paragraph>Foo![]</paragraph>'
* ```
*
* **Note:** A {@link module:engine/model/text~ModelText text} node that contains attributes will be represented as:
*
* ```xml
* <$text attribute="value">Text data</$text>
* ```
*
* **Note:** Using this tool in production-grade code is not recommended. It was designed for development, prototyping,
* debugging and testing.
*
* @param options.withoutSelection Whether to write the selection. When set to `true`, the selection will
* not be included in the returned string.
* @param options.rootName The name of the root from which the data should be stringified. If not provided,
* the default `main` name will be used.
* @param options.convertMarkers Whether to include markers in the returned string.
* @returns The stringified data.
*/
const _getModelData = /* #__PURE__ */ (() => {
	const getModelData = function(model, options = {}) {
		if (!(model instanceof Model)) throw new TypeError("Model needs to be an instance of module:engine/model/model~Model.");
		const rootName = options.rootName || "main";
		const root = model.document.getRoot(rootName);
		return getModelData._stringify(root, options.withoutSelection ? null : model.document.selection, options.convertMarkers ? model.markers : null);
	};
	getModelData._stringify = _stringifyModel;
	return getModelData;
})();
/**
* Sets the content of a model {@link module:engine/model/document~ModelDocument document} provided as an HTML-like string.
*
* ```ts
* setData( editor.model, '<paragraph>Foo![]</paragraph>' );
* ```
*
* **Note:** Remember to register elements in the {@link module:engine/model/model~Model#schema model's schema} before
* trying to use them.
*
* **Note:** To create a {@link module:engine/model/text~ModelText text} node that contains attributes use:
*
* ```xml
* <$text attribute="value">Text data</$text>
* ```
*
* **Note:** Using this tool in production-grade code is not recommended. It was designed for development, prototyping,
* debugging and testing.
*
* @param data HTML-like string to write into the document.
* @param options.rootName Root name where parsed data will be stored. If not provided, the default `main`
* name will be used.
* @param options.selectionAttributes A list of attributes which will be passed to the selection.
* @param options.lastRangeBackward If set to `true`, the last range will be added as backward.
* @param options.batchType Batch type used for inserting elements. See {@link module:engine/model/batch~Batch#constructor}.
*/
const _setModelData = /* #__PURE__ */ (() => {
	const setModelData = function(model, data, options = {}) {
		if (!(model instanceof Model)) throw new TypeError("Model needs to be an instance of module:engine/model/model~Model.");
		let modelDocumentFragment;
		let selection = null;
		const modelRoot = model.document.getRoot(options.rootName || "main");
		const parsedResult = setModelData._parse(data, model.schema, {
			lastRangeBackward: options.lastRangeBackward,
			selectionAttributes: options.selectionAttributes,
			context: [modelRoot.name],
			inlineObjectElements: options.inlineObjectElements
		});
		if ("model" in parsedResult) {
			modelDocumentFragment = parsedResult.model;
			selection = parsedResult.selection;
		} else modelDocumentFragment = parsedResult;
		if (options.batchType !== void 0) model.enqueueChange(options.batchType, writeToModel);
		else model.change(writeToModel);
		function writeToModel(writer) {
			writer.remove(writer.createRangeIn(modelRoot));
			writer.insert(modelDocumentFragment, modelRoot);
			writer.setSelection(null);
			writer.removeSelectionAttribute(model.document.selection.getAttributeKeys());
			if (selection) {
				const ranges = [];
				for (const range of selection.getRanges()) {
					const start = new ModelPosition(modelRoot, range.start.path);
					const end = new ModelPosition(modelRoot, range.end.path);
					ranges.push(new ModelRange(start, end));
				}
				writer.setSelection(ranges, { backward: selection.isBackward });
				if (options.selectionAttributes) writer.setSelectionAttribute(selection.getAttributes());
			}
		}
	};
	setModelData._parse = _parseModel;
	return setModelData;
})();
/**
* Converts model nodes to HTML-like string representation.
*
* **Note:** A {@link module:engine/model/text~ModelText text} node that contains attributes will be represented as:
*
* ```xml
* <$text attribute="value">Text data</$text>
* ```
*
* @param node A node to stringify.
* @param selectionOrPositionOrRange A selection instance whose ranges will be included in the returned string data.
* If a range instance is provided, it will be converted to a selection containing this range. If a position instance
* is provided, it will be converted to a selection containing one range collapsed at this position.
* @param markers Markers to include.
* @returns An HTML-like string representing the model.
*/
function _stringifyModel(node, selectionOrPositionOrRange = null, markers = null) {
	const model = new Model();
	const mapper = new Mapper();
	let selection = null;
	let range;
	if (node instanceof ModelRootElement || node instanceof ModelDocumentFragment) range = model.createRangeIn(node);
	else if (!node.parent) {
		const fragment = new ModelDocumentFragment(node);
		range = model.createRangeIn(fragment);
	} else range = new ModelRange(model.createPositionBefore(node), model.createPositionAfter(node));
	if (selectionOrPositionOrRange instanceof ModelSelection) selection = selectionOrPositionOrRange;
	else if (selectionOrPositionOrRange instanceof ModelDocumentSelection) selection = selectionOrPositionOrRange;
	else if (selectionOrPositionOrRange instanceof ModelRange) selection = new ModelSelection(selectionOrPositionOrRange);
	else if (selectionOrPositionOrRange instanceof ModelPosition) selection = new ModelSelection(selectionOrPositionOrRange);
	const view = new EditingView(new StylesProcessor());
	const viewDocument = view.document;
	const viewRoot = new ViewRootEditableElement(viewDocument, "div");
	viewRoot.rootName = "main";
	viewDocument.roots.add(viewRoot);
	const downcastDispatcher = new DowncastDispatcher({
		mapper,
		schema: model.schema
	});
	mapper.bindElements(node.root, viewRoot);
	downcastDispatcher.on("insert:$text", insertText());
	downcastDispatcher.on("insert", insertAttributesAndChildren(), { priority: "lowest" });
	downcastDispatcher.on("attribute", (evt, data, conversionApi) => {
		if (data.item instanceof ModelSelection || data.item instanceof ModelDocumentSelection || data.item.is("$textProxy")) wrap((modelAttributeValue, { writer }) => {
			return writer.createAttributeElement("model-text-with-attributes", { [data.attributeKey]: stringifyAttributeValue(modelAttributeValue) });
		})(evt, data, conversionApi);
	});
	downcastDispatcher.on("insert", insertElement((modelItem) => {
		const attributes = convertAttributes(modelItem.getAttributes(), stringifyAttributeValue);
		return new ViewContainerElement(viewDocument, modelItem.name, attributes);
	}));
	downcastDispatcher.on("selection", convertRangeSelection());
	downcastDispatcher.on("selection", convertCollapsedSelection());
	downcastDispatcher.on("addMarker", insertUIElement((data, { writer }) => {
		const name = data.markerName + ":" + (data.isOpening ? "start" : "end");
		return writer.createUIElement(name);
	}));
	const markersMap = /* @__PURE__ */ new Map();
	if (markers) for (const marker of Array.from(markers).sort((a, b) => a.name < b.name ? 1 : -1)) markersMap.set(marker.name, marker.getRange());
	const writer = view._writer;
	downcastDispatcher.convert(range, markersMap, writer);
	if (selection) downcastDispatcher.convertSelection(selection, markers || model.markers, writer);
	let data = _stringifyView(viewRoot, viewDocument.selection, { sameSelectionCharacters: true });
	data = data.substr(5, data.length - 11);
	view.destroy();
	return data.replace(/* @__PURE__ */ new RegExp("model-text-with-attributes", "g"), "$text");
}
/**
* Parses an HTML-like string and returns the model {@link module:engine/model/rootelement~ModelRootElement rootElement}.
*
* **Note:** To create a {@link module:engine/model/text~ModelText text} node that contains attributes use:
*
* ```xml
* <$text attribute="value">Text data</$text>
* ```
*
* **Note:** The default `options.context` value is `'$root'`, which only matches the generic root. When the editor
* uses a custom root {@link module:core/editor/editorconfig~RootConfig#modelElement `modelElement`}, pass the target
* {@link module:engine/model/rootelement~ModelRootElement root element} (or its configured model element name)
* explicitly, otherwise the conversion result may be wrong.
* See the {@glink framework/deep-dive/schema#custom-root-elements Custom root elements} section of the
* {@glink framework/deep-dive/schema Schema deep-dive} guide for more details.
*
* @param data HTML-like string to be parsed.
* @param schema A schema instance used by converters for element validation.
* @param options Additional configuration.
* @param options.selectionAttributes A list of attributes which will be passed to the selection.
* @param options.lastRangeBackward If set to `true`, the last range will be added as backward.
* @param options.context The conversion context. If not provided, the default `'$root'` will be used.
* @returns Returns the parsed model node or an object with two fields: `model` and `selection`,
* when selection ranges were included in the data to parse.
*/
function _parseModel(data, schema, options = {}) {
	const mapper = new Mapper();
	data = data.replace(/* @__PURE__ */ new RegExp("\\$text", "g"), "model-text-with-attributes");
	const parsedResult = _parseView(data, {
		sameSelectionCharacters: true,
		lastRangeBackward: !!options.lastRangeBackward,
		inlineObjectElements: options.inlineObjectElements
	});
	let viewDocumentFragment;
	let viewSelection = null;
	let selection = null;
	if ("view" in parsedResult && "selection" in parsedResult) {
		viewDocumentFragment = parsedResult.view;
		viewSelection = parsedResult.selection;
	} else viewDocumentFragment = parsedResult;
	const modelController = new Model();
	const upcastDispatcher = new UpcastDispatcher({ schema });
	upcastDispatcher.on("documentFragment", convertToModelFragment$1(mapper));
	upcastDispatcher.on("element:model-text-with-attributes", convertToModelText());
	upcastDispatcher.on("element", convertToModelElement(mapper));
	upcastDispatcher.on("text", convertToModelText());
	let model = modelController.change((writer) => upcastDispatcher.convert(viewDocumentFragment.root, writer, options.context || "$root"));
	mapper.bindElements(model, viewDocumentFragment.root);
	if (model.childCount == 1) model = model.getChild(0);
	if (viewSelection) {
		const ranges = [];
		for (const viewRange of viewSelection.getRanges()) ranges.push(mapper.toModelRange(viewRange));
		selection = new ModelSelection(ranges, { backward: viewSelection.isBackward });
		for (const [key, value] of toMap(options.selectionAttributes || [])) selection.setAttribute(key, value);
	}
	if (selection) return {
		model,
		selection
	};
	return model;
}
function convertToModelFragment$1(mapper) {
	return (evt, data, conversionApi) => {
		const childrenResult = conversionApi.convertChildren(data.viewItem, data.modelCursor);
		mapper.bindElements(data.modelCursor.parent, data.viewItem);
		data = Object.assign(data, childrenResult);
		evt.stop();
	};
}
function convertToModelElement(mapper) {
	return (evt, data, conversionApi) => {
		const elementName = data.viewItem.name;
		if (!conversionApi.schema.checkChild(data.modelCursor, elementName)) throw new Error(`Element '${elementName}' was not allowed in given position.`);
		const attributes = convertAttributes(data.viewItem.getAttributes(), parseAttributeValue);
		const element = conversionApi.writer.createElement(data.viewItem.name, attributes);
		conversionApi.writer.insert(element, data.modelCursor);
		mapper.bindElements(element, data.viewItem);
		conversionApi.convertChildren(data.viewItem, element);
		data.modelRange = ModelRange._createOn(element);
		data.modelCursor = data.modelRange.end;
		evt.stop();
	};
}
function convertToModelText() {
	return (evt, data, conversionApi) => {
		if (!conversionApi.schema.checkChild(data.modelCursor, "$text")) throw new Error("Text was not allowed in given position.");
		let node;
		if (data.viewItem.is("element")) {
			const attributes = convertAttributes(data.viewItem.getAttributes(), parseAttributeValue);
			const viewText = data.viewItem.getChild(0);
			node = conversionApi.writer.createText(viewText.data, attributes);
		} else node = conversionApi.writer.createText(data.viewItem.data);
		conversionApi.writer.insert(node, data.modelCursor);
		data.modelRange = ModelRange._createFromPositionAndShift(data.modelCursor, node.offsetSize);
		data.modelCursor = data.modelRange.end;
		evt.stop();
	};
}
function parseAttributeValue(attribute) {
	try {
		return JSON.parse(attribute);
	} catch {
		return attribute;
	}
}
function stringifyAttributeValue(data) {
	if (isPlainObject(data)) return JSON.stringify(data);
	return data;
}
function* convertAttributes(attributes, converter) {
	for (const [key, value] of attributes) yield [key, converter(value)];
}

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* Note: This package is used only internally for debugging purposes and should not be used
* in other environments. It uses a few special methods not existing in the default
* building process. That is also why there are no tests for this file.
*
* @module engine/dev-utils/utils
*/
/**
* Helper function, converts a map to the 'key1="value1" key2="value1"' format.
*
* @param map Map to convert.
* @returns Converted map.
* @internal
*/
function convertMapToTags(map) {
	let string = "";
	for (const entry of map) string += ` ${entry[0]}=${JSON.stringify(entry[1])}`;
	return string;
}
/**
* Helper function, converts a map to the `{"key1":"value1","key2":"value2"}` format.
*
* @param map Map to convert.
* @returns Converted map.
* @internal
*/
function convertMapToStringifiedObject(map) {
	const obj = {};
	for (const entry of map) obj[entry[0]] = entry[1];
	return JSON.stringify(obj);
}
const treeDump = Symbol("_treeDump");
const maxTreeDumpLength = 20;
/**
* Helper function that stores the `document` state for a given `version`.
*
* @internal
*/
function dumpTrees(document, version) {
	console.log(document, version);
	let string = "";
	for (const root of document.roots) string += root.printTree() + "\n";
	document[treeDump][version] = string.substr(0, string.length - 1);
	const overflow = document[treeDump].length - maxTreeDumpLength;
	if (overflow > 0) document[treeDump][overflow - 1] = null;
}
/**
* Helper function that initializes document dumping.
*
* @internal
*/
function initDocumentDumping(document) {
	document[treeDump] = [];
}
/**
* Helper function that logs document for the given version.
*
* @internal
*/
function logDocument(document, version) {
	console.log("--------------------");
	if (document[treeDump][version]) console.log(document[treeDump][version]);
	else console.log("Tree log unavailable for given version: " + version);
}

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/
/**
* @module engine/dev-utils/operationreplayer
*/
/**
* Operation replayer is a development tool created for easy replaying of operations on the document from stringified operations.
*
* @internal
*/
var OperationReplayer = class {
	_model;
	_logSeparator;
	_operationsToReplay;
	/**
	* @param model Data model.
	* @param logSeparator Separator between operations.
	* @param stringifiedOperations Operations to replay.
	*/
	constructor(model, logSeparator, stringifiedOperations) {
		this._model = model;
		this._logSeparator = logSeparator;
		this.setStringifiedOperations(stringifiedOperations);
	}
	/**
	* Parses the given string containing stringified operations and sets parsed operations as operations to replay.
	*
	* @param stringifiedOperations Stringified operations to replay.
	*/
	setStringifiedOperations(stringifiedOperations) {
		if (stringifiedOperations === "") {
			this._operationsToReplay = [];
			return;
		}
		this._operationsToReplay = stringifiedOperations.split(this._logSeparator).map((stringifiedOperation) => JSON.parse(stringifiedOperation));
	}
	/**
	* Returns operations to replay.
	*/
	getOperationsToReplay() {
		return this._operationsToReplay;
	}
	/**
	* Applies all operations with a delay between actions.
	*
	* @param timeInterval Time between applying operations.
	*/
	play(timeInterval = 1e3) {
		const operationReplayer = this;
		return new Promise((res, rej) => {
			play();
			function play() {
				operationReplayer.applyNextOperation().then((isFinished) => {
					if (isFinished) return res();
					setTimeout(play, timeInterval);
				}).catch((err) => {
					rej(err);
				});
			}
		});
	}
	/**
	* Applies `numberOfOperations` operations, beginning after the last applied operation (or first, if no operations were applied).
	*
	* @param numberOfOperations The number of operations to apply.
	*/
	applyOperations(numberOfOperations) {
		if (numberOfOperations <= 0) return;
		return this.applyNextOperation().then((isFinished) => {
			if (!isFinished) return this.applyOperations(numberOfOperations - 1);
		});
	}
	/**
	* Applies all operations to replay at once.
	*/
	applyAllOperations() {
		return this.applyNextOperation().then((isFinished) => {
			if (!isFinished) return this.applyAllOperations();
		});
	}
	/**
	* Applies the next operation to replay. Returns a promise with the `isFinished` parameter that is `true` if the last
	* operation in the replayer has been applied, `false` otherwise.
	*/
	applyNextOperation() {
		const model = this._model;
		return new Promise((res) => {
			model.enqueueChange((writer) => {
				const operationJson = this._operationsToReplay.shift();
				if (!operationJson) return res(true);
				const operation = OperationFactory.fromJSON(operationJson, model.document);
				writer.batch.addOperation(operation);
				model.applyOperation(operation);
				res(false);
			});
		});
	}
};

/**
* @license Copyright (c) 2003-2026, CKSource Holding sp. z o.o. All rights reserved.
* For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
*/

export { AttributeOperation, Batch, BubblingEmitterMixin, BubblingEventInfo, ClickObserver, CompositionObserver, Conversion, ConversionHelpers, DataController, Differ, DomEventObserver, DowncastDispatcher, DowncastHelpers, EditingController, EditingView, FakeSelectionObserver, FocusObserver, History, HtmlDataProcessor, InputObserver, InsertOperation, KeyObserver, Mapper, Marker, MarkerCollection, MarkerOperation, Matcher, MergeOperation, Model, ModelConsumable, ModelDocument, ModelDocumentFragment, ModelDocumentSelection, ModelElement, ModelLivePosition, ModelLiveRange, ModelNode, ModelNodeList, ModelPosition, ModelRange, ModelRootElement, ModelSchema, ModelSchemaContext, ModelSelection, ModelText, ModelTextProxy, ModelTreeWalker, ModelTypeCheckable, ModelWriter, MouseObserver, MoveOperation, MutationObserver, NoOperation, Observer, Operation, OperationFactory, PointerObserver, RenameOperation, RootAttributeOperation, RootOperation, SelectionObserver, SplitOperation, StylesMap, StylesProcessor, TabObserver, TouchObserver, UpcastDispatcher, UpcastHelpers, ViewUpcastWriter as UpcastWriter, ViewUpcastWriter, ViewAttributeElement, ViewConsumable, ViewContainerElement, ViewDataTransfer, ViewDocument, ViewDocumentDomEventData, ViewDocumentFragment, ViewDocumentSelection, ViewDomConverter, ViewDowncastWriter, ViewEditableElement, ViewElement, ViewEmptyElement, ViewNode, ViewPosition, ViewRange, ViewRawElement, ViewRenderer, ViewRootEditableElement, ViewSelection, ViewText, ViewTextProxy, ViewTokenList, ViewTreeWalker, ViewTypeCheckable, ViewUIElement, XmlDataProcessor, BasicHtmlWriter as _DataProcessorBasicHtmlWriter, DetachOperation as _DetachOperation, MapperCache as _MapperCache, OperationReplayer as _OperationReplayer, BR_FILLER as _VIEW_BR_FILLER, INLINE_FILLER as _VIEW_INLINE_FILLER, INLINE_FILLER_LENGTH as _VIEW_INLINE_FILLER_LENGTH, MARKED_NBSP_FILLER as _VIEW_MARKED_NBSP_FILLER, NBSP_FILLER as _VIEW_NBSP_FILLER, ViewElementConsumables as _ViewElementConversionConsumables, autoParagraphEmptyRoots as _autoParagraphEmptyModelRoots, convertMapToStringifiedObject as _convertMapToStringifiedObject, convertMapToTags as _convertMapToTags, deleteContent as _deleteModelContent, cleanSelection as _downcastCleanSelection, convertCollapsedSelection as _downcastConvertCollapsedSelection, convertRangeSelection as _downcastConvertRangeSelection, createViewElementFromDowncastHighlightDescriptor as _downcastCreateViewElementFromDowncastHighlightDescriptor, insertAttributesAndChildren as _downcastInsertAttributesAndChildren, insertElement as _downcastInsertElement, insertStructure as _downcastInsertStructure, insertText as _downcastInsertText, insertUIElement as _downcastInsertUIElement, remove as _downcastRemove, wrap as _downcastWrap, dumpTrees as _dumpTrees, getDataWithoutFiller as _getDataWithoutViewFiller, _getModelData, getNodeAfterPosition as _getModelNodeAfterPosition, getNodeBeforePosition as _getModelNodeBeforePosition, getTextNodeAtPosition as _getModelTextNodeAtPosition, getSelectedContent as _getSelectedModelContent, _getViewData, initDocumentDumping as _initDocumentDumping, injectSelectionPostFixer as _injectModelSelectionPostFixer, injectQuirksHandling as _injectViewQuirksHandling, injectUiElementHandling as _injectViewUIElementHandling, _insert as _insertIntoModelNodeList, insertContent as _insertModelContent, insertObject as _insertModelObject, isInlineFiller as _isInlineViewFiller, isParagraphable as _isParagraphableModelNode, isPatternMatched as _isViewPatternMatched, logDocument as _logDocument, mergeIntersectingRanges as _mergeIntersectingModelRanges, modifySelection as _modifyModelSelection, _move as _moveInModelNodeList, normalizeConsumables as _normalizeConversionConsumables, _normalizeNodes as _normalizeInModelNodeList, transform as _operationTransform, _parseModel, _parseView, _remove as _removeFromModelNodeList, _setAttribute as _setAttributeInModelNodeList, _setModelData, _setViewData, startsWithFiller as _startsWithViewFiller, _stringifyModel, _stringifyView, tryFixingRange as _tryFixingModelRange, convertSelectionChange as _upcastConvertSelectionChange, convertText as _upcastConvertText, convertToModelFragment as _upcastConvertToModelFragment, wrapInParagraph as _wrapInModelParagraph, addBackgroundStylesRules, addBorderStylesRules, addMarginStylesRules, addPaddingStylesRules, disableViewPlaceholder, enableViewPlaceholder, getBoxSidesStyleShorthandValue, getBoxSidesStyleValueReducer, getBoxSidesStyleValues, getPositionStyleShorthandNormalizer, getShorthandStylesValues, getViewFillerOffset, hideViewPlaceholder, isAttachmentStyleValue, isColorStyleValue, isLengthStyleValue, isLineStyleValue, isPercentageStyleValue, isPositionStyleValue, isRepeatStyleValue, isURLStyleValue, needsViewPlaceholder, showViewPlaceholder, transformOperationSets };
//# sourceMappingURL=index.js.map